import store from '@/store'
import * as turf from '@turf/turf'
import { nextTick, toRef, reactive, ref, computed, watch } from 'vue'
import { escapeHtml } from '@/js/utils/sanitize'

const MAPILLARY_CLIENT_ID = process.env.VUE_APP_MAPILLARY_CLIENT_ID

// ====== 等高線（maplibre-contour） ======
// 等高線ソースのURL生成（DemSourceがwindowに設定された後に呼び出される）
function getContourSourceUrl() {
  if (window.contourDemSource) {
    return window.contourDemSource.contourProtocolUrl({
      // thresholds: ズームレベル → 等高線間隔の配列
      // 配列内で後の値（大きい間隔）がlevel=1（計曲線）になる
      thresholds: {
        // ズーム8-10: 100m間隔のみ（すべてlevel=1）
        8: [100],
        // ズーム11-12: 50m, 100m（100mがlevel=1）
        11: [50, 100],
        // ズーム13-14: 20m, 100m（100mがlevel=1）
        13: [20, 100],
        // ズーム15以上: 10m, 50m（50mがlevel=1）
        15: [10, 50],
      },
      contourLayer: 'contour',
      elevationKey: 'ele',
      levelKey: 'level',
      extent: 4096,
      buffer: 1
    })
  }
  // フォールバック（DemSourceがまだ準備できていない場合）
  return 'about:blank'
}

// 等高線ソース（動的生成）
const contourSource = {
  id: 'contour-source',
  obj: {
    type: 'vector',
    // tilesは動的に設定されるため、空配列で初期化
    tiles: [],
    maxzoom: 20  // ベクタータイルは高ズームまで対応（DEMのoverzoomで生成）
  },
  // カスタムプロパティ: 動的URL生成関数
  getTiles: () => [getContourSourceUrl()]
}

// 主曲線レイヤー（10m間隔、細い線）
const contourLayerMinor = {
  id: 'oh-contour-lines-minor',
  type: 'line',
  source: 'contour-source',
  'source-layer': 'contour',
  filter: ['!=', ['get', 'level'], 1],
  paint: {
    'line-color': 'rgba(0, 0, 0, 0.35)',
    'line-width': ['interpolate', ['linear'], ['zoom'],
      10, 0.3,
      14, 0.8,
      17, 1.2
    ]
  },
  minzoom: 12
}

// 計曲線レイヤー（50m間隔、太い線）
const contourLayerMajor = {
  id: 'oh-contour-lines-major',
  type: 'line',
  source: 'contour-source',
  'source-layer': 'contour',
  filter: ['==', ['get', 'level'], 1],
  paint: {
    'line-color': 'rgba(0, 0, 0, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'],
      8, 0.5,
      12, 1.5,
      17, 2.5
    ]
  },
  minzoom: 8
}

// 標高ラベル（計曲線のみ）
const contourLayerLabel = {
  id: 'oh-contour-labels',
  type: 'symbol',
  source: 'contour-source',
  'source-layer': 'contour',
  filter: ['==', ['get', 'level'], 1],
  layout: {
    'symbol-placement': 'line',
    'text-field': ['concat', ['to-string', ['get', 'ele']], 'm'],
    'text-size': ['interpolate', ['linear'], ['zoom'],
      12, 10,
      17, 14
    ],
    'text-font': ['Noto Sans CJK JP Regular'],
    'text-max-angle': 25,
    'text-padding': 5
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 0.8)',
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1.5
  },
  minzoom: 13
}

/**
 * マピラリメインレイヤー
 * @type {{obj: {tiles: string[], maxzoom: number, type: string, minzoom: number}, id: string}}
 */
const mapillarySource = {
  id: 'mapillary-source',
  obj: {
    type: 'vector',
    tiles: [`https://tiles.mapillary.com/maps/vtp/mly1_public/2/{z}/{x}/{y}?access_token=${MAPILLARY_CLIENT_ID}`],
    minzoom: 0,
    maxzoom: 14,
  },
}
// シーケンス（走行軌跡）をラインで表示
const mapillarySequences = {
  id: 'oh-mapillary-sequences',
  type: 'line',
  source: 'mapillary-source',
  'source-layer': 'sequence',
  layout: {
    'line-cap': 'round',
    'line-join': 'round',
  },
  paint: {
    'line-opacity': 0.6,
    'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1, 12, 2, 16, 4.0],
    'line-color': '#35AF6D',
  },
}
// 画像位置（ポイント）を円で表示（ズーム 13+ で）
const mapillaryImages = {
  id: 'oh-mapillary-images',
  type: 'circle',
  source: 'mapillary-source',
  'source-layer': 'image',
  minzoom: 13,
  paint: {
    'circle-opacity': 0.7,
    'circle-stroke-color': '#1b5e3a',
    'circle-stroke-width': 1,
    'circle-color': '#35AF6D',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 13, 4, 16, 8, 19, 12],
  },
}
/**
 * マピラリレイヤー2
 */
const mapillarySource2 = {
  id: 'mapillary-source-2',
  obj: {
    type: 'vector',
    tiles: [
      `https://tiles.mapillary.com/maps/vtp/mly_map_feature_point/2/{z}/{x}/{y}?access_token=${MAPILLARY_CLIENT_ID}`,
    ],
    minzoom: 14,
    maxzoom: 14, // これで z>14 はオーバーズーム表示
  },
}

const mapillaryImages2 = {
  id: 'oh-mapillary-images-2',
  type: 'circle',
  source: 'mapillary-source-2',
  'source-layer': 'point',
  minzoom: 14,
  paint: {
    'circle-color': 'red', // dummy
  },
}
// --- ラベルレイヤー定義（レイヤーオブジェクトだけ用意） ---
const mapillaryLabels2 = {
  id: 'oh-mapillary-images-2-label',
  type: 'symbol',
  source: 'mapillary-source-2',
  'source-layer': 'point',
  minzoom: 18,
  layout: {
    'text-field': 'dummy',
    'text-size': ['interpolate', ['linear'], ['zoom'], 18, 10, 20, 18],
    'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
    // 'text-radial-offset': 0.6,
    'text-offset': [0, 1],
    'text-allow-overlap': true,
    'text-optional': true,
  },
  paint: {
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.0,
    'text-color': '#111111',
  },
}
const mapillaryIcon2 = {
  id: 'oh-mapillary-images-2-icon',
  type: 'symbol',
  source: 'mapillary-source-2',
  'source-layer': 'point',
  // filter: ['has', 'class'],     // プロパティ名は実データに合わせる
  layout: {
    // 'icon-image': ['get', 'value'],
    'icon-image': [
      'let',
      'v',
      ['coalesce', ['to-string', ['get', 'value']], ''],
      [
        'case',
        // v に "object--traffic-light--general-horizontal" が含まれるか？
        ['>=', ['index-of', 'object--traffic-light--general-horizontal', ['var', 'v']], 0],
        'object--traffic-light--general-horizontal', // ← 使うアイコン名
        ['>=', ['index-of', 'object--traffic-light--general-upright', ['var', 'v']], 0],
        'object--traffic-light--general-upright', // ← 使うアイコン名
        ['>=', ['index-of', 'object--traffic-light--pedestrians', ['var', 'v']], 0],
        'object--traffic-light--pedestrians',
        ['var', 'v'], // それ以外は元の value をそのまま
      ],
    ],
    'icon-size': ['interpolate', ['linear'], ['zoom'], 14, 0.6, 16, 1.1],
    'icon-allow-overlap': true,
  },
}
/**
 * マピラリレイヤー3
 */
const mapillarySource3 = {
  id: 'mapillary-source-3',
  obj: {
    type: 'vector',
    tiles: [
      `https://tiles.mapillary.com/maps/vtp/mly_map_feature_traffic_sign/2/{z}/{x}/{y}?access_token=${MAPILLARY_CLIENT_ID}`,
    ],
    minzoom: 0,
    maxzoom: 14,
  },
}

const mapillaryImages3 = {
  id: 'oh-mapillary-images-3',
  type: 'circle',
  source: 'mapillary-source-3',
  'source-layer': 'traffic_sign',
  minzoom: 14,
  paint: {
    'circle-color': 'rgba(0,0,0,0)', // dummy
  },
}

const mapillaryLabels3 = {
  id: 'oh-mapillary-images-3-label',
  type: 'symbol',
  source: 'mapillary-source-3',
  'source-layer': 'traffic_sign',
  minzoom: 16,
  layout: {
    'text-field': ['get', 'value'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 16, 14, 18, 18],
    'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
    'text-radial-offset': 0.6,
    'text-allow-overlap': false,
    'text-optional': true,
  },
  paint: {
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.0,
    'text-color': '#111111',
  },
}
const mapillaryIcon3 = {
  id: 'oh-mapillary-images-3-icon',
  type: 'symbol',
  source: 'mapillary-source-3',
  'source-layer': 'traffic_sign',
  layout: {
    'icon-image': ['get', 'value'],
    'icon-size': ['interpolate', ['linear'], ['zoom'], 14, 0.6, 16, 1.1],
    'icon-allow-overlap': true,
  },
}

export const DETECTION_COLORS = [
  {
    value: 'marking--discrete--give-way-single',
    en: 'Lane Marking - Give Way (Single)',
    ja: 'ゆずれマーク（単独）',
    color: '#F9A825',
  },
  { value: 'marking--discrete--other-marking', en: 'Lane Marking - Other', ja: 'その他の路面標示', color: '#BDBDBD' },
  { value: 'marking--discrete--stop-line', en: 'Lane Marking - Stop Line', ja: '停止線', color: '#FF7043' },
  {
    value: 'marking--discrete--symbol--bicycle',
    en: 'Lane Marking - Symbol (Bicycle)',
    ja: '自転車シンボル',
    color: '#26A69A',
  },
  { value: 'marking--discrete--text', en: 'Lane Marking - Text', ja: '文字標示', color: '#8D6E63' },

  // Objects
  { value: 'object--banner', en: 'Banner', ja: 'バナー', color: '#AB47BC' },
  { value: 'object--bench', en: 'Bench', ja: 'ベンチ', color: '#00BCD4' },
  { value: 'object--bike-rack', en: 'Bike Rack', ja: '駐輪ラック', color: '#4DB6AC' },
  { value: 'object--catch-basin', en: 'Catch Basin', ja: '集水桝（雨水ます）', color: '#6D4C41' },
  { value: 'object--cctv-camera', en: 'CCTV Camera', ja: '防犯カメラ', color: '#7E57C2' },
  { value: 'object--fire-hydrant', en: 'Fire Hydrant', ja: '消火栓', color: '#E53935' },
  { value: 'object--junction-box', en: 'Junction Box', ja: '制御盤（ボックス）', color: '#795548' },
  { value: 'object--mailbox', en: 'Mailbox', ja: '郵便ポスト', color: '#9C27B0' },
  { value: 'object--manhole', en: 'Manhole', ja: 'マンホール', color: '#5D4037' },
  { value: 'object--parking-meter', en: 'Parking Meter', ja: 'パーキングメーター', color: '#00838F' },
  { value: 'object--phone-booth', en: 'Phone Booth', ja: '公衆電話ボックス', color: '#43A047' },
  { value: 'object--sign--advertisement', en: 'Signage - Advertisement', ja: '看板（広告）', color: '#EC407A' },
  { value: 'object--sign--information', en: 'Signage - Information', ja: '看板（案内）', color: '#26C6DA' },
  { value: 'object--sign--store', en: 'Signage - Store', ja: '看板（店舗）', color: '#FF8A65' },
  { value: 'object--street-light', en: 'Street Light', ja: '街路灯', color: '#FFD400' },
  { value: 'object--support--pole', en: 'Pole', ja: '支柱（ポール）', color: '#9E9E9E' },
  { value: 'object--support--traffic-sign-frame', en: 'Traffic Sign Frame', ja: '標識支柱フレーム', color: '#757575' },
  { value: 'object--support--utility-pole', en: 'Utility Pole', ja: '電柱', color: '#616161' },
  { value: 'object--traffic-cone', en: 'Traffic Cone', ja: 'カラーコーン', color: '#FF6D00' },
  {
    value: 'object--traffic-light--general-front',
    en: 'Traffic Light - General (Front)',
    ja: '信号機（一般／正面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-back',
    en: 'Traffic Light - General (Back)',
    ja: '信号機（一般／背面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-side',
    en: 'Traffic Light - General (Side)',
    ja: '信号機（一般／側面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--pedestrians-front',
    en: 'Traffic Light - Pedestrians (Front)',
    ja: '信号機（歩行者／正面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-back',
    en: 'Traffic Light - Pedestrians (Back)',
    ja: '信号機（歩行者／背面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-side',
    en: 'Traffic Light - Pedestrians (Side)',
    ja: '信号機（歩行者／側面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--cyclists-front',
    en: 'Traffic Light - Cyclists (Front)',
    ja: '信号機（自転車／正面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-back',
    en: 'Traffic Light - Cyclists (Back)',
    ja: '信号機（自転車／背面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-side',
    en: 'Traffic Light - Cyclists (Side)',
    ja: '信号機（自転車／側面）',
    color: '#2E7D32',
  },

  {
    value: 'object--traffic-light--general-horizontal-front',
    en: 'Traffic Light - General (Horizontal / Front)',
    ja: '信号機（一般／水平／正面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-horizontal-back',
    en: 'Traffic Light - General (Horizontal / Back)',
    ja: '信号機（一般／水平／背面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-horizontal-side',
    en: 'Traffic Light - General (Horizontal / Side)',
    ja: '信号機（一般／水平／側面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-upright-front',
    en: 'Traffic Light - General (Upright / Front)',
    ja: '信号機（一般／縦／正面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-upright-back',
    en: 'Traffic Light - General (Upright / Back)',
    ja: '信号機（一般／縦／背面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-upright-side',
    en: 'Traffic Light - General (Upright / Side)',
    ja: '信号機（一般／縦／側面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-single-front',
    en: 'Traffic Light - General (Single / Front)',
    ja: '信号機（一般／単灯／正面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-single-back',
    en: 'Traffic Light - General (Single / Back)',
    ja: '信号機（一般／単灯／背面）',
    color: '#D32F2F',
  },
  {
    value: 'object--traffic-light--general-single-side',
    en: 'Traffic Light - General (Single / Side)',
    ja: '信号機（一般／単灯／側面）',
    color: '#D32F2F',
  },

  {
    value: 'object--traffic-light--pedestrians-horizontal-front',
    en: 'Traffic Light - Pedestrians (Horizontal / Front)',
    ja: '信号機（歩行者／水平／正面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-horizontal-back',
    en: 'Traffic Light - Pedestrians (Horizontal / Back)',
    ja: '信号機（歩行者／水平／背面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-horizontal-side',
    en: 'Traffic Light - Pedestrians (Horizontal / Side)',
    ja: '信号機（歩行者／水平／側面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-upright-front',
    en: 'Traffic Light - Pedestrians (Upright / Front)',
    ja: '信号機（歩行者／縦／正面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-upright-back',
    en: 'Traffic Light - Pedestrians (Upright / Back)',
    ja: '信号機（歩行者／縦／背面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-upright-side',
    en: 'Traffic Light - Pedestrians (Upright / Side)',
    ja: '信号機（歩行者／縦／側面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-single-front',
    en: 'Traffic Light - Pedestrians (Single / Front)',
    ja: '信号機（歩行者／単灯／正面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-single-back',
    en: 'Traffic Light - Pedestrians (Single / Back)',
    ja: '信号機（歩行者／単灯／背面）',
    color: '#66BB6A',
  },
  {
    value: 'object--traffic-light--pedestrians-single-side',
    en: 'Traffic Light - Pedestrians (Single / Side)',
    ja: '信号機（歩行者／単灯／側面）',
    color: '#66BB6A',
  },

  {
    value: 'object--traffic-light--cyclists-horizontal-front',
    en: 'Traffic Light - Cyclists (Horizontal / Front)',
    ja: '信号機（自転車／水平／正面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-horizontal-back',
    en: 'Traffic Light - Cyclists (Horizontal / Back)',
    ja: '信号機（自転車／水平／背面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-horizontal-side',
    en: 'Traffic Light - Cyclists (Horizontal / Side)',
    ja: '信号機（自転車／水平／側面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-upright-front',
    en: 'Traffic Light - Cyclists (Upright / Front)',
    ja: '信号機（自転車／縦／正面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-upright-back',
    en: 'Traffic Light - Cyclists (Upright / Back)',
    ja: '信号機（自転車／縦／背面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-upright-side',
    en: 'Traffic Light - Cyclists (Upright / Side)',
    ja: '信号機（自転車／縦／側面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-single-front',
    en: 'Traffic Light - Cyclists (Single / Front)',
    ja: '信号機（自転車／単灯／正面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-single-back',
    en: 'Traffic Light - Cyclists (Single / Back)',
    ja: '信号機（自転車／単灯／背面）',
    color: '#2E7D32',
  },
  {
    value: 'object--traffic-light--cyclists-single-side',
    en: 'Traffic Light - Cyclists (Single / Side)',
    ja: '信号機（自転車／単灯／側面）',
    color: '#2E7D32',
  },

  { value: 'object--traffic-light--other', en: 'Traffic Light - Other', ja: '信号機（その他）', color: '#B71C1C' },
  { value: 'object--trash-can', en: 'Trash Can', ja: 'ごみ箱', color: '#4CAF50' },
  { value: 'object--water-valve', en: 'Water Valve', ja: '水道バルブ', color: '#29B6F6' },

  { value: 'marking--discrete--crosswalk-zebra', en: 'Crosswalk Zebra', ja: '横断歩道', color: '#29B6F6' },
  {
    value: 'marking--discrete--text--other',
    en: 'Discrete Text Other',
    ja: '路面標示（文字・その他）',
    color: '#29B6F6',
  },
]

// value で match 式を作る
function makeMatchByValue(defaultColor = '#9E9E9E') {
  const expr = ['match', ['get', 'value']]
  DETECTION_COLORS.forEach(({ value, color }) => expr.push(value, color))
  expr.push(defaultColor)
  return expr
}
function makeLabelMatch(lang = 'ja') {
  const key = lang === 'en' ? 'en' : 'ja'
  // まず通常の value→ラベルの match 式を作る
  const base = ['match', ['get', 'value']]
  DETECTION_COLORS.forEach((f) => {
    base.push(f.value, f[key] || f.en || f.value)
  })
  // デフォルトは value から "object--" を外して表示
  base.push(['coalesce', ['slice', ['get', 'value'], 8], ['get', 'value']])

  // "object--traffic-sign" を含むときは強制的に「」
  return ['case', ['>=', ['index-of', 'object--traffic-sign', ['get', 'value']], 0], '', base]
  // return base
}

// 既存のレイヤー定義に色を追加（半透明の縁取りつき）
mapillaryImages2.paint['circle-color'] = makeMatchByValue()
mapillaryImages2.paint['circle-stroke-color'] = '#000000'
mapillaryImages2.paint['circle-stroke-width'] = 0.5
mapillaryImages2.paint['circle-opacity'] = 0.9
mapillaryLabels2.layout['text-field'] = makeLabelMatch('ja')
// ついでにズームで半径を変えるなら
mapillaryImages2.paint['circle-radius'] = ['interpolate', ['linear'], ['zoom'], 14, 3, 18, 6]
// マピラリここまで-------------------------------------------------------------------------------------------------------

store.state.isOffline = !window.navigator.onLine
let convertTileJson = []
let isGsiNew = false
let tochibunruiNodes = []
if (!store.state.isOffline) {
  const [tileJson, tochibunruiEntries] = await Promise.all([
    fetchGsiTile(),
    fetchTochibunruiLayers(),
  ])
  const convertTileJsonObject = convertGsiTileJson(tileJson)
  convertTileJson = convertTileJsonObject.result
  isGsiNew = convertTileJsonObject.isNew
  tochibunruiNodes = convertTochibunruiJson(tochibunruiEntries)
}

// ---------------------------------------------------------------------------------------------------------------------
// cs https://rinya-ehime.geospatial.jp/tile/rinya/2024/csmap_Ehime/14/14251/9830.png
export const csUrls = [
  {
    selected: 1,
    citycode: '04000',
    name: '宮城県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://forestgeo.info/opendata/4_miyagi/topography_2023/{z}/{x}/{y}.webp',
    page: "<a href='https://miyagi.dataeye.jp/resources/1521' target='_blank'>【宮城県】3次元点群データ_データ一覧_2023</a>",
  },
  {
    selected: 1,
    citycode: '07201',
    name: '福島県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://www2.ffpri.go.jp/soilmap/tile/cs_fukushima/{z}/{x}/{y}.png',
    page: "<a href='https://www2.ffpri.go.jp/soilmap/data-src.html' target='_blank'>森林総合研究所 CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '09200',
    name: '栃木県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya-tochigi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/csmap_tochigi' target='_blank'>栃木県微地形図（CS立体図）</a>",
  },
  {
    selected: 1,
    citycode: '13000',
    name: '東京都23区CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/tokyo-digitaltwin/tokyopc-23ku-2024-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='' target='_blank'>東京都(区部)CS立体図(東京都デジタルツイン実現プロジェクト 区部点群データを加工して作成)</a>",
  },
  {
    selected: 1,
    citycode: '13000',
    name: '東京都多摩地域CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/tokyo-digitaltwin/tokyopc-tama-2023-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/tokyopc-tama-2023' target='_blank'>東京都(多摩地域)CS立体図(東京都デジタルツイン実現プロジェクト 多摩地域点群データを加工して作成)</a>",
  },
  {
    selected: 1,
    citycode: '14100',
    name: '神奈川県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/pref-kanagawa/kanagawapc-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset?q=%E7%A5%9E%E5%A5%88%E5%B7%9D%E7%9C%8C+3%E6%AC%A1%E5%85%83%E7%82%B9%E7%BE%A4&sort=metadata_modified+desc' target='_blank'>神奈川県CS立体図(データ提供:(神奈川県環境農政局緑政部森林再生課))</a>",
  },
  {
    selected: 1,
    citycode: '15202',
    name: '長岡市CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://forestgeo.info/opendata/15_niigata/nagaoka/csmap_2024/{z}/{x}/{y}.webp',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/rinya-csmap-nagaoka2024' target='_blank'>林野庁長岡地域CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '16201',
    name: '富山県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks2.xsrv.jp/raster-tiles/pref-toyama/toyama-csmap-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/dem' target='_blank'>富山県CS立体図(富山県 数値標高モデル（DEM）を加工して作成)</a>",
  },
  {
    selected: 0,
    citycode: '17463',
    name: '能登CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya.geospatial.jp/tile/csmaptile_noto/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/2024noto_rinya' target='_blank'>林野庁能登地域CS立体図(発災後)</a>",
  },
  {
    selected: 1,
    citycode: '17463',
    name: '能登CS2',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/rinya/noto-2024-csmap-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/2024noto_dem' target='_blank'>林野庁能登地域CS立体図(能登地域0.5mDEM(発災後)を加工して作成)</a>",
  },
  {
    selected: 1,
    citycode: '19201',
    name: '山梨県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks2.xsrv.jp/raster-tiles/pref-yamanashi/yamanashi-csmap-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/yamanashi-pointcloud-2024' target='_blank'>山梨県CS立体図(山梨県グリッドデータDEMを加工して作成)</a>",
  },
  {
    selected: 0,
    citycode: '20200',
    name: '長野県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://tile.geospatial.jp/CS/VER2/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/nagano-csmap' target='_blank'>長野県CS立体図（長野県林業総合センター）</a>",
  },
  {
    selected: 1,
    citycode: '20200',
    name: '長野県CS0.5m',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks2.xsrv.jp/raster-tiles/pref-nagano/nagano-csmap-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/r3-4-50cmdem' target='_blank'>この長野県CS立体図(0.5m)は長野県林務部長の承認を得て森林計画図を使用して作成したものである。承認番号 7森政第51-3号</a>",
  },
  {
    selected: 1,
    tms: 1,
    citycode: '21200',
    name: '岐阜県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://kenzkenz2.xsrv.jp/gihucs/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/cs-2019-geotiff' target='_blank'>岐阜県CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '22100',
    name: '静岡県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/pref-shizuoka/shizuoka-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/shizuoka-2023-csmap' target='_blank'>静岡県CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '26100',
    name: '京都府CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks2.xsrv.jp/raster-tiles/pref-kyoto/kyoto-csmap-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/dem05_kyoto' target='_blank'>京都府CS立体図(京都府「数値標高モデル（DEM）」を加工して作成)</a>",
  },
  {
    selected: 1,
    citycode: '27100',
    name: '大阪府CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://xs489works.xsrv.jp/raster-tiles/pref-osaka/osaka-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/cs' target='_blank'>大阪府CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '28100',
    name: '兵庫県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya-hyogo.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/csmap_hyogo' target='_blank'>兵庫県微地形図（CS立体図）</a>",
  },
  {
    selected: 1,
    citycode: '30201',
    name: '和歌山県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://xs489works.xsrv.jp/raster-tiles/pref-wakayama/wakayamapc-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://wakayamaken.geocloud.jp/mp/22' target='_blank'>和歌山県CS立体図(和歌山県3次元点群データを加工して作成)</a>",
  },
  {
    selected: 1,
    citycode: '31201',
    name: '鳥取県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya-tottori.geospatial.jp/tile/rinya/2024/csmap_tottori/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/csmap_tottori' target='_blank'>鳥取県CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '33100',
    name: '岡山県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://www2.ffpri.go.jp/soilmap/tile/cs_okayama/{z}/{x}/{y}.png',
    page: 'https://www2.ffpri.go.jp/soilmap/tile/cs_okayama/{z}/{x}/{y}.png',
  },
  {
    selected: 0,
    citycode: '34100',
    name: '広島県CS(林野庁0.5m)',
    maxzoom: 24,
    bbox: [],
    url: 'https://www2.ffpri.go.jp/soilmap/tile/cs_hiroshima/{z}/{x}/{y}.png',
    page: "<a href='https://www2.ffpri.go.jp/soilmap/data-src.html' target='_blank'>森林総合研究所CS立体図</a>",
  },
  {
    selected: 0,
    citycode: '34100',
    name: '広島県CS(広島県1m)',
    maxzoom: 24,
    bbox: [],
    url: 'https://xs489works.xsrv.jp/raster-tiles/pref-hiroshima/hiroshimapc-cs-tiles/{z}/{x}/{y}.png',
    page: "<a href='https://hiroshima-dobox.jp/index2' target='_blank'>広島県CS立体図(広島県3次元点群データを加工して作成)</a>\"",
  },
  {
    selected: 1,
    citycode: '34100',
    name: '広島県CS(広島県0.5m)',
    maxzoom: 24,
    bbox: [],
    url: 'https://shiworks.xsrv.jp/raster-tiles/pref-hiroshima/hiroshimapc-2022-cs-tiles/{z}/{x}/{y}.png',
    page: 'https://shiworks.xsrv.jp/raster-tiles/pref-hiroshima/hiroshimapc-2022-cs-tiles/{z}/{x}/{y}.png',
  },
  {
    selected: 1,
    tms: 1,
    citycode: '38200',
    name: '愛媛CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya-ehime.geospatial.jp/tile/rinya/2024/csmap_Ehime/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/csmap_ehime' target='_blank'>愛媛県CS立体図</a>",
  },
  {
    selected: 1,
    citycode: '39200',
    name: '高知県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://rinya-kochi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png',
    page: "<a href='https://www.geospatial.jp/ckan/dataset/csmap_kochi' target='_blank'>高知県微地形図（CS立体図）</a>",
  },
  {
    selected: 1,
    citycode: '43100',
    name: '熊本県大分県CS',
    maxzoom: 24,
    bbox: [],
    url: 'https://www2.ffpri.go.jp/soilmap/tile/cs_kumamoto_oita/{z}/{x}/{y}.png',
    page: "<a href='https://www2.ffpri.go.jp/soilmap/data-src.html' target='_blank'>森林総合研究所 CS立体図</a>",
  },
]
function csLayersCreate(url) {
  const csSource = {
    id: 'oh-cs-' + url.name + '-source',
    obj: {
      type: 'raster',
      tiles: [url.url],
      ...(url.tms !== undefined && { scheme: 'tms' }),
      tileSize: 256,
    },
  }
  const csLayer = {
    id: 'oh-cs-' + url.name,
    type: 'raster',
    source: 'oh-cs-' + url.name + '-source',
    name: url.name,
    minzoom: 0,
    ...(url.maxzoom !== undefined && { maxzoom: url.maxzoom }),
  }

  return { csSource, csLayer }
}
export const csSources = []
export const csLayers = []
export const csLayersSelected = []
csUrls.forEach((url) => {
  const layers = csLayersCreate(url)
  csSources.push(layers.csSource)
  csLayers.push(layers.csLayer)
  if (url.selected) csLayersSelected.push(layers.csLayer)
})
const csLayers2 = csLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.name,
    source: csSources[i],
    layers: [layer],
    attribution: '<a href="' + layer.page + '" target="_blank">' + name + '</a>',
    credit: name,
  }
})
// --------------------------------------------------------------------------------------------------------------------
/**
 * 全国地番図アッドレイヤー
 * @param map
 * @param zoom
 */
export function zenkokuChibanzuAddLayer(map) {
  const zoom = map.getZoom()
  const targetLayer = findLayerById(map, 'oh-chibanzu-all2')
  if (targetLayer) {
    if (zoom >= 11) {
      const bounds = map.getBounds()
      const maplibreBbox = [
        bounds.getWest(), // 最小経度（left）
        bounds.getSouth(), // 最小緯度（bottom）
        bounds.getEast(), // 最大経度（right）
        bounds.getNorth(), // 最大緯度（top）
      ]
      if (pablicDatas.length > 0) {
        pablicDatas.forEach((v) => {
          if (v.bbox) {
            const dataBbox = JSON.parse(v.bbox) // [west, south, east, north]
            const isIntersecting =
              maplibreBbox[2] > dataBbox[0] && // east > west
              maplibreBbox[0] < dataBbox[2] && // west < east
              maplibreBbox[3] > dataBbox[1] && // north > south
              maplibreBbox[1] < dataBbox[3] // south < north
            if (isIntersecting) {
              if (!v.url) {
                return
              }
              const layers = publiLayersCreate(v)
              const allLayers = map.getStyle().layers
              const result = allLayers.find((l) => {
                return l.id === layers.publicPolygonLayer.id
              })
              if (!result) {
                targetLayer.layers.push(layers.publicPolygonLayer)
                targetLayer.layers.push(layers.publicLineLayer)
                targetLayer.layers.push(layers.publicLabelLayer)
              }
            }
          }
        })
      }
      sicyosonChibanzuUrls.forEach((v) => {
        if (v.bbox) {
          const dataBbox = v.bbox // [west, south, east, north]
          const isIntersecting =
            Number(maplibreBbox[2]) > Number(dataBbox[0]) && // east > west
            Number(maplibreBbox[0]) < Number(dataBbox[2]) && // west < east
            Number(maplibreBbox[3]) > Number(dataBbox[1]) && // north > south
            Number(maplibreBbox[1]) < Number(dataBbox[3]) // south < north
          if (isIntersecting) {
            const layers = chibanzuLayersCreate(v)
            const allLayers = map.getStyle().layers
            const result = allLayers.find((l) => {
              return l.id === layers.chibanzuLayer.id
            })
            if (!result) {
              targetLayer.layers.push(layers.chibanzuLayer)
              targetLayer.layers.push(layers.chibanzuLayerLineGreen)
              targetLayer.layers.push(layers.chibanzuLayerLabelGreen)
            }
          }
        }
      })
    }
  }
  // return targetLayer?.layers
}

export async function loadColorData() {
  if (store.state.isOffline) {
    return { colorMap: {}, isNew: {} }
  }
  try {
    const response = await fetch('/oh3/php/userChibanzumapSelect2.php')
    if (!response.ok) {
      throw new Error(`HTTPエラー: ${response.status}`)
    }
    const data = await response.json()
    if (data.error) throw new Error(data.error)

    const colorMap = {}
    const isNew = {}
    const now = new Date()

    data.forEach((item) => {
      // public値を色に変換
      const code = parseInt(item.public, 10)
      const color =
        code === -1
          ? 'rgba(0,128,0,0.8)'
          : code === 0
            ? 'rgba(0,0,0,0)'
            : code === 1
              ? 'rgba(0,0,255,0.8)'
              : code === 3
                ? 'rgba(128,128,128,0.8)'
                : code === 4
                  ? 'rgba(255,0,0,0.8)'
                  : code === 5
                    ? 'rgba(255,165,0,0.8)'
                    : '#CCCCCC'

      colorMap[item.citycode] = color

      // 新着判定
      if (item.last_modified_date) {
        const last = new Date(item.last_modified_date.replace(' ', 'T')) // Safari対策
        const diffMs = now - last
        const diffDays = diffMs / (1000 * 60 * 60 * 24)
        if (diffDays <= 3) {
          // 3日間をNEWとする
          isNew[item.citycode] = 1
        }
      }
    })

    return { colorMap, isNew }
  } catch (error) {
    console.error('色データの取得に失敗:', error)
    return { colorMap: {}, isNew: {} }
  }
}
const abc = await loadColorData()
const chibanzuColors = abc.colorMap
const isNew = abc.isNew
// alert(JSON.stringify(isNew));
/**
 *
 * @param getter
 * @param checkFn
 * @param interval
 * @returns {Promise<unknown>}
 */
function waitForStateValue(getter, checkFn = (val) => val !== undefined && val !== null, interval = 50) {
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      const val = getter()
      if (checkFn(val)) {
        clearInterval(timer)
        resolve(val)
      }
    }, interval)
  })
}

function boundsSort(bounds) {
  // boundsが配列でない場合はnullを返す
  if (!Array.isArray(bounds)) {
    return null
  }
  function compareFunc(a, b) {
    return a - b
  }
  bounds.sort(compareFunc)
  return [bounds[2], bounds[0], bounds[3], bounds[1]]
}

async function publicData() {
  if (store.state.isOffline) {
    return null
  }
  try {
    const response = await axios.get('/oh3/php/userPmtileSelectPublicNew.php', {
      params: {},
    })

    if (response.data.error) {
      console.error('公開データの取得に失敗:', response.data.error)
      if (response.data.error === 'DB接続失敗') {
        store.state.dbConnectionFailed = true
      }
      return []
    } else {
      // vm.jsonDataPmtilePubilc = response.data
      return response.data
    }
  } catch (error) {
    console.error('公開データの取得に失敗:', error)
    return []
  }
}
const pablicDatas = await publicData()
function publiLayersCreate(v) {
  const publicSource = {
    id: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    obj: {
      type: 'vector',
      url: 'pmtiles://' + v.url,
    },
    // minZoom: 11,
    minzoom: 13,
    bounds: boundsSort(JSON.parse(v.bbox)),
  }
  const publicPolygonLayer = {
    name: v.name,
    id: 'oh-chiban-' + v.id + '-' + v.name + '-layer',
    type: 'fill',
    source: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    'source-layer': 'oh3',
    paint: {
      'fill-color': 'rgba(0,0,0,0)',
      // 'fill-color': [
      //     'case',
      //     ['has', 'AzaName'],
      //     'rgba(0,0,255,0.5)',
      //     'rgba(0,0,0,0)'
      // ],
    },
    // minzoom: 11
    minzoom: 13,
  }
  let lineColor
  switch (v.public) {
    case '-1':
      lineColor = 'green'
      break
    case '1':
      lineColor = 'blue'
      break
  }
  const publicLineLayer = {
    id: 'oh-chibanL-' + v.id + '-' + v.name + '-line-layer',
    source: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    type: 'line',
    'source-layer': 'oh3',
    paint: {
      'line-color': lineColor,
      'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.1, 16, 2],
    },
    // minzoom: 11
    minzoom: 13,
  }
  let minZoom
  if (!v.length) {
    minZoom = 17
  } else if (v.length < 10000) {
    minZoom = 0
  } else {
    minZoom = 17
  }
  const publicLabelLayer = {
    id: 'oh-chibanL-' + v.id + '-' + v.name + '-label-layer',
    type: 'symbol',
    source: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    'source-layer': 'oh3',
    layout: {
      'text-field': ['get', v.chiban],
    },
    paint: {
      'text-color': 'navy',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: minZoom,
  }
  // ポイントレイヤー（現在は非表示）
  const publicPointLayer = {
    id: 'oh-chibanL-' + v.id + '-' + v.name + '-point-layer',
    type: 'circle',
    source: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    filter: ['==', '$type', 'Point'],
    'source-layer': 'oh3',
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-color': 'rgba(0,0,255,1)', // 赤色で中心点を強調
      'circle-radius': 5, // 固定サイズの点
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#fff',
    },
    // minzoom: 11
    minzoom: 13,
  }
  // 頂点レイヤー（現在は非表示）
  const publicVertexLayer = {
    id: 'oh-chibanL-' + v.id + '-' + v.name + '-vertex-layer',
    type: 'circle',
    source: 'oh-chiban-' + v.id + '-' + v.name + '-source',
    filter: ['==', '$type', 'Polygon'],
    'source-layer': 'oh3',
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
      'circle-color': 'blue', // 公開データは青
    },
    // minzoom: 11
    minzoom: 13,
  }
  return { publicSource, publicPolygonLayer, publicLineLayer, publicLabelLayer, publicPointLayer, publicVertexLayer }
}
export const publicSources = []
const publicPolygonLayers = []
const publicLineLayers = []
const publicLabelLayers = []
const publicPointLayers = []
const publicVertexLayers = []
if (pablicDatas && pablicDatas.length > 0) {
  pablicDatas.forEach((v) => {
    if (!v.url) {
      return
    }
    const layers = publiLayersCreate(v)
    publicSources.push(layers.publicSource)
    publicPolygonLayers.push(layers.publicPolygonLayer)
    publicLineLayers.push(layers.publicLineLayer)
    publicLabelLayers.push(layers.publicLabelLayer)
    publicPointLayers.push(layers.publicPointLayer)
    publicVertexLayers.push(layers.publicVertexLayer)
  })
}
const publicLayers = publicPolygonLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.name,
    source: publicSources[i],
    layers: [layer, publicLineLayers[i], publicLabelLayers[i]],
    // attribution: '<a href="' + layer.page + '" target="_blank">' + name + '</a>',
    ext: { name: 'ext-chibanzu' },
  }
})
const publicLayers0 = [
  ...publicPolygonLayers,
  ...publicLineLayers,
  ...publicLabelLayers,
]

export const cityPmtilesSource = {
  id: 'city-pmtiles-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/original-data/city.pmtiles?nocache=' + Date.now(),
    // url: "pmtiles://https://kenzkenz.net/original-data/city.pmtiles",
  },
}

const cityPmtilesPolygonLayer = {
  id: 'oh-city-pmtiles-poligon-layer',
  type: 'fill',
  source: 'city-pmtiles-source',
  'source-layer': 'oh3',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'public'], 1],
      'rgba(0,0,255,0.8)', // public が 1 のとき
      ['==', ['get', 'public'], 2],
      'rgba(0,128,0,0.8)', // public が 2 のとき
      ['==', ['get', 'public'], 3],
      'rgba(128,128,128,0.8)', // public が 3 のとき
      'rgba(255,255,255,0.1)',
    ],
  },
  maxzoom: 11.5,
}

const cityPmtilesLineLayer = {
  id: 'oh-city-pmtiles-line-layer',
  source: 'city-pmtiles-source',
  'source-layer': 'oh3',
  type: 'line',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0, 9, 1, 16, 2],
  },
}
const cityPmtilesLabelLayer = {
  id: 'oh-city-pmtiles-label-layer',
  type: 'symbol',
  source: 'city-pmtiles-source',
  'source-layer': 'oh3',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  // paint: {
  //     'text-color': 'rgba(255, 255, 255, 1)',
  //     'text-halo-color': 'rgba(0,0,0,1)',
  //     'text-halo-width': 1.0,
  // },
  paint: {
    'text-color': 'navy',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 7,
}

export const cityGeojsonSource = {
  id: 'city-geojson-source',
  obj: {
    type: 'geojson',
    // 'data': 'https://kenzkenz.net/original-data/chibanzumap.geojson?nocache=' + Date.now()
    data: 'https://kenzkenz.net/original-data/chibanzumap.geojson?v=3',
  },
}

export const cityGeojsonPolygonLayer = {
  id: 'oh-city-geojson-poligon-layer',
  type: 'fill',
  source: 'city-geojson-source',
  paint: {
    'fill-color': [
      'case',
      ...Object.entries(chibanzuColors).flatMap(([key, color]) => [
        ['==', ['to-number', ['get', 'N03_007']], Number(key)],
        color,
      ]),
      'rgba(0,0,0,0)', // デフォルト色
    ],
  },
  maxzoom: 14,
}

export const cityGeojsonLineLayer = {
  id: 'oh-city-geojson-line-layer',
  source: 'city-geojson-source',
  // "source-layer": 'polygon',
  type: 'line',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0, 9, 1, 16, 2],
  },
}
// export const cityGeojsonLabelLayer = {
//     id: 'oh-city-geojson-label-layer',
//     type: 'symbol',
//     source: 'city-geojson-source',
//     layout: {
//         'text-field': ['get', 'N03_004'],
//         'text-offset': [0, 1],
//     },
//     paint: {
//         'text-color': '#000',
//         'text-halo-color': '#fff',
//         'text-halo-width': 1
//     },
//     minzoom: 7
// }
// NEWな市町村コード一覧
const newCodes = Object.keys(isNew).filter((k) => isNew[k] === 1)
// alert(JSON.stringify(newCodes));

// 1. 白字＋黒ハロー
// const newLabelColorOnRed = '#fff';
// const newHaloColorOnRed  = '#000';

// 2. 黒字＋白ハロー
// const newLabelColorOnRed = '#000';
// const newHaloColorOnRed  = '#fff';

// 3. 黄字＋黒ハロー
// const newLabelColorOnRed = '#fff600';
// const newHaloColorOnRed  = '#000';

// 4. 蛍光グリーン＋黒ハロー
const newLabelColorOnRed = '#39ff14'
const newHaloColorOnRed = '#000'

const newLabelColor = '#fff600' // 普段のNEW（蛍光イエロー）
const newHaloColor = '#ff00cc' // 普段のNEW（蛍光ピンク）

let caseTextColor = [
  'case',
  ...Object.entries(chibanzuColors).flatMap(([code, color]) => {
    if (newCodes.includes(code)) {
      if (color === 'rgba(255,0,0,0.8)') {
        // NEW かつ 赤背景
        return [['==', ['get', 'N03_007'], code], newLabelColorOnRed]
      } else {
        // NEW かつ 非赤背景
        return [['==', ['get', 'N03_007'], code], newLabelColor]
      }
    }
    return []
  }),
  '#000', // デフォルト
]
// alert(JSON.stringify(newCodes, null, 2));
// alert(JSON.stringify(chibanzuColors, null, 2));
// alert(JSON.stringify(caseTextColor, null, 2));
let caseTextHaloColor = [
  'case',
  ...Object.entries(chibanzuColors).flatMap(([code, color]) => {
    if (newCodes.includes(code)) {
      if (color === 'rgba(255,0,0,0.8)') {
        // NEW かつ 赤背景
        return [['==', ['get', 'N03_007'], code], newHaloColorOnRed]
      } else {
        // NEW かつ 非赤背景
        return [['==', ['get', 'N03_007'], code], newHaloColor]
      }
    }
    return []
  }),
  '#fff', // デフォルト
]

// 蛍光色・NEW表示のcase式を生成
// const caseTextField = [
//     'case',
//     ...newCodes.flatMap(code => [
//         ['==', ['get', 'N03_007'], code], ['concat', 'NEW\n', ['get', 'N03_004']]
//     ]),
//     ['get', 'N03_004'] // デフォルト: 市区町村名
// ];
let caseTextField = [
  'case',

  // ① newCodes に含まれるキーだけを抽出
  ...Object.entries(chibanzuColors)
    .filter(([key]) => newCodes.map(Number).includes(Number(key)))
    // ② その上で flatMap して条件＋テキストを作成
    .flatMap(([key, color]) => {
      const codeNum = Number(key)
      // MapLibre の「このコードか？」を判定する条件
      const condition = ['==', ['to-number', ['get', 'N03_007']], codeNum]
      // 色によって prefix を切り替え（特定色なら★付き）
      const prefix = color === 'rgba(0,0,255,0.8)' ? '★NEW★\n' : 'NEW\n'
      return [condition, ['concat', prefix, ['get', 'N03_004']]]
    }),

  // ③ どの条件にも当てはまらなかったときのデフォルト
  ['get', 'N03_004'],
]

// alert(JSON.stringify(caseTextField))
if (newCodes.length === 0) {
  caseTextField = ['get', 'N03_004']
  caseTextColor = 'black'
  caseTextHaloColor = 'white'
}
export const cityGeojsonLabelLayer = {
  id: 'oh-city-geojson-label-layer',
  type: 'symbol',
  source: 'city-geojson-source',
  layout: {
    'text-field': caseTextField,
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': caseTextColor,
    'text-halo-color': caseTextHaloColor,
    'text-halo-width': 2,
    'text-halo-blur': 0.5,
  },
  minzoom: 7,
  maxzoom: 14,
}
export const cityGeojsonLabelLayer2 = {
  id: 'oh-city-geojson-label-layer2',
  type: 'symbol',
  source: 'city-geojson-source',
  layout: {
    'text-field': caseTextField,
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': caseTextColor,
    'text-halo-color': caseTextHaloColor,
    'text-halo-width': 2,
    'text-halo-blur': 0.5,
  },
  minzoom: 0,
  maxzoom: 14,
  filter: ['any', ...newCodes.map((code) => ['==', ['get', 'N03_007'], code])],
}

// 塗り絵マップ ===================================================================
// 都道府県版
const prefColoringSource = {
  id: 'pref-coloring-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/prefectures.geojson',
  },
}
const prefColoringFillLayer = {
  id: 'oh-pref-coloring',
  type: 'fill',
  source: 'pref-coloring-source',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0.6,
  },
}
const prefColoringLineLayer = {
  id: 'oh-pref-coloring-line',
  type: 'line',
  source: 'pref-coloring-source',
  paint: {
    'line-color': '#333333',
    'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.5, 8, 1.5, 12, 2],
  },
}
const prefColoringLabelLayer = {
  id: 'oh-pref-coloring-label',
  type: 'symbol',
  source: 'pref-coloring-source',
  layout: {
    'text-field': ['get', 'pref_name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 8, 14],
    'visibility': 'none',
  },
  paint: {
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.5,
  },
  minzoom: 4,
}

// 市区町村版（既存cityGeojsonSourceを再利用）
const cityColoringFillLayer = {
  id: 'oh-city-coloring',
  type: 'fill',
  source: 'city-geojson-source',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0.6,
  },
}
const cityColoringLineLayer = {
  id: 'oh-city-coloring-line',
  type: 'line',
  source: 'city-geojson-source',
  paint: {
    'line-color': '#666666',
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0, 9, 0.5, 14, 1.5],
  },
}
const cityColoringLabelLayer = {
  id: 'oh-city-coloring-label',
  type: 'symbol',
  source: 'city-geojson-source',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 14, 13],
    'visibility': 'none',
  },
  paint: {
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.0,
  },
  minzoom: 8,
}

// 所得コロプレスマップ（city-geojson-source再利用）
const incomeChoroplethFillLayer = {
  id: 'oh-income-choropleth',
  type: 'fill',
  source: 'city-geojson-source',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0.7,
  },
}
const incomeChoroplethLineLayer = {
  id: 'oh-income-choropleth-line',
  type: 'line',
  source: 'city-geojson-source',
  paint: {
    'line-color': '#999999',
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0, 9, 0.3, 14, 1],
  },
}
const incomeChoroplethLabelLayer = {
  id: 'oh-income-choropleth-label',
  type: 'symbol',
  source: 'city-geojson-source',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 14, 13],
    'visibility': 'visible',
  },
  paint: {
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.0,
  },
  minzoom: 8,
}

// 汎用統計コロプレスマップ（city-geojson-source再利用）
const choroplethFillLayer = {
  id: 'oh-choropleth',
  type: 'fill',
  source: 'city-geojson-source',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0.7,
  },
}
const choroplethLineLayer = {
  id: 'oh-choropleth-line',
  type: 'line',
  source: 'city-geojson-source',
  paint: {
    'line-color': '#999999',
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0, 9, 0.3, 14, 1],
  },
}
const choroplethLabelLayer = {
  id: 'oh-choropleth-label',
  type: 'symbol',
  source: 'city-geojson-source',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 14, 13],
    'visibility': 'visible',
  },
  paint: {
    'text-color': '#333333',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.0,
  },
  minzoom: 8,
}

// 授業用・都道府県主題図（prefectures.geojson: pref_code / pref_name）
const schoolChoroplethSource = {
  id: 'school-choropleth-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/prefectures.geojson',
  },
}
const schoolChoroplethFillLayer = {
  id: 'oh-school-choropleth',
  type: 'fill',
  source: 'school-choropleth-source',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0.75,
  },
}
const schoolChoroplethLineLayer = {
  id: 'oh-school-choropleth-line',
  type: 'line',
  source: 'school-choropleth-source',
  paint: {
    'line-color': '#333333',
    'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.5, 8, 1.5, 12, 2],
  },
}
const schoolChoroplethLabelLayer = {
  id: 'oh-school-choropleth-label',
  type: 'symbol',
  source: 'school-choropleth-source',
  layout: {
    'text-field': ['get', 'pref_name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 4, 11, 8, 16],
    'visibility': 'visible',
  },
  paint: {
    'text-color': '#222222',
    'text-halo-color': 'rgba(255,255,255,0.95)',
    'text-halo-width': 1.5,
  },
}
// プリズムマップ（立体柱状図）用。既定は非表示。
const schoolChoroplethExtrusionLayer = {
  id: 'oh-school-choropleth-extrusion',
  type: 'fill-extrusion',
  source: 'school-choropleth-source',
  paint: {
    'fill-extrusion-color': 'rgba(0,0,0,0)',
    'fill-extrusion-height': 0,
    'fill-extrusion-base': 0,
    'fill-extrusion-opacity': 0.85,
  },
  layout: {
    'visibility': 'none',
  },
}

// --------------------------------------------------------------------------------
// クリックで追加する円のGeoJSONソースを作成
export const clickCircleSource = {
  iD: 'click-circle-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
// ドローレイヤーを追加--------------------------------------------------
export const clickCircleLayer = {
  id: 'click-circle-layer',
  type: 'fill',
  source: 'click-circle-source',
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'fill-color': ['get', 'color'],
  },
}
export const clickCircleLayerEx = {
  id: 'click-circle-layer-ex',
  type: 'fill-extrusion',
  source: 'click-circle-source',
  filter: ['all', ['has', 'height'], ['>', ['to-number', ['get', 'height']], 0]],
  paint: {
    'fill-extrusion-base': 0,
    'fill-extrusion-height': ['get', 'height'],
    'fill-extrusion-color': ['get', 'color'],
    'fill-extrusion-opacity': 0.7,
    'fill-extrusion-vertical-gradient': true,
  },
}
export const clickCirclePolygonLineLayer = {
  id: 'click-circle-polygon-line-layer',
  type: 'line',
  source: 'click-circle-source',
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'line-color': [
      'case',
      // lineColor プロパティがあればその値を使用
      ['has', 'lineColor'],
      ['get', 'lineColor'],
      // デフォルトは黒
      'black',
    ],
    'line-width': [
      'case',
      ['has', 'line-width'],
      ['get', 'line-width'],
      1, // デフォルト値（nullエラー回避）
    ],
  },
}
/**
 * ミゾッターさんの指摘により改修。他機能との検証が必要。しばらく様子見
 * @type {{filter: (string|string[])[], layout: {"text-justify": string[], "text-field": (string|(string|string[]|(string|(string|string[])[]|number)[])[]|string[])[], "text-offset": number[], "text-size": number, "text-anchor": string, "text-allow-overlap": boolean}, maxzoom: number, paint: {"text-halo-color": string, "text-color": string, "text-halo-width": number}, id: string, source: string, type: string, minzoom: number}}
 */
export const clickCircPolygonSymbolLayer = {
  // ポリゴンのラベルに使っている。面積ではない（円は除外）
  id: 'click-circle-polygon-symbol-layer',
  type: 'symbol',
  source: 'click-circle-source',
  // labelTypeを持たない、bearingを持たない、radiusを持たない、canterLngを持たない（円除外）
  filter: ['all', ['!has', 'labelType'], ['!has', 'bearing'], ['!has', 'radius'], ['!has', 'canterLng']],
  layout: {
    // 'text-field': ['get', 'label'],
    'text-field': [
      'case',
      // label プロパティがあり、かつ文字数が 0 より大きいときは label
      ['all', ['has', 'label'], ['>', ['length', ['get', 'label']], 0]],
      ['get', 'label'],
      // それ以外は地番
      ['get', '地番'],
    ],
    'text-size': 18, // サイズ調整
    'text-anchor': 'center',
    'text-allow-overlap': false, // 重なったら省略
    'text-offset': [0, -1],
    'text-justify': ['get', 'textJustify'], // 追加: 複数行のテキストを左寄せにする
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 15, // zoom15以上で表示
}
export const clickCircPolygonSymbolAreaLayer = {
  // ポリゴンの面積、周長に使っている。
  id: 'click-circle-polygon-symbol-area-layer',
  type: 'symbol',
  source: 'click-circle-source',
  filter: ['all', ['has', 'isArea'], ['!=', ['get', 'isArea'], false]],
  layout: {
    'text-field': [
      'concat',
      '面積:',
      ['to-string', ['get', 'area']],
      '\n',
      '周長:',
      ['to-string', ['get', 'perimeter']],
    ],
    'text-size': 16, // サイズ調整
    'text-anchor': 'center',
    'text-allow-overlap': true,
    'text-offset': [0, 1.2],
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': '#fff',
    'text-halo-width': 1,
  },
}
export const clickCircPolygonPopulationLayer = {
  // ポリゴン・円の人口表示レイヤー
  id: 'click-circle-polygon-population-layer',
  type: 'symbol',
  source: 'click-circle-source',
  // populationプロパティを持つPolygonのみ表示（古いフィルター構文）
  filter: ['all',
    ['==', '$type', 'Polygon'],
    ['has', 'population'],
    ['>', 'population', 0]
  ],
  layout: {
    'text-field': ['concat', '約', ['number-format', ['get', 'population'], {}], '人'],
    'text-size': 24,
    'text-anchor': 'center',
    'text-allow-overlap': true,
    // 円とポリゴン両方とも中心より上
    'text-offset': [
      'case',
      ['==', ['get', 'isCircle'], true], ['literal', [0, -1]],
      ['literal', [0, -2.5]]
    ],
  },
  paint: {
    'text-color': '#00796b', // teal
    'text-halo-color': '#ffffff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 0,
}
// 実線レイヤー（デフォルト）
export const clickCircleLineLayer = {
  id: 'click-circle-line-layer',
  type: 'line',
  source: 'click-circle-source',
  filter: [
    'all',
    ['any', ['==', ['get', 'keiko'], 0], ['!', ['has', 'keiko']]],
    ['any', ['==', ['get', 'line-style'], 'solid'], ['!', ['has', 'line-style']]]
  ],
  paint: {
    'line-color': ['get', 'color'],
    'line-width': ['get', 'line-width'],
  },
}

// 破線レイヤー
export const clickCircleLineDashedLayer = {
  id: 'click-circle-line-dashed-layer',
  type: 'line',
  source: 'click-circle-source',
  filter: [
    'all',
    ['any', ['==', ['get', 'keiko'], 0], ['!', ['has', 'keiko']]],
    ['==', ['get', 'line-style'], 'dashed']
  ],
  layout: {
    'line-cap': 'butt',
  },
  paint: {
    'line-color': ['get', 'color'],
    'line-width': ['get', 'line-width'],
    'line-dasharray': [4, 3],
  },
}

// 点線レイヤー
export const clickCircleLineDottedLayer = {
  id: 'click-circle-line-dotted-layer',
  type: 'line',
  source: 'click-circle-source',
  filter: [
    'all',
    ['any', ['==', ['get', 'keiko'], 0], ['!', ['has', 'keiko']]],
    ['==', ['get', 'line-style'], 'dotted']
  ],
  layout: {
    'line-cap': 'round',
  },
  paint: {
    'line-color': ['get', 'color'],
    'line-width': ['get', 'line-width'],
    'line-dasharray': [0.5, 2],
  },
}
export const clickCircleKeikoLineLayer = {
  id: 'click-circle-keiko-line-layer',
  type: 'line',
  source: 'click-circle-source',
  filter: ['all', ['==', ['get', 'keiko'], 1]],
  paint: {
    'line-color': ['get', 'keiko-color'],
    'line-width': ['*', ['get', 'line-width'], 3],
    // 'line-opacity': 0.3, // なぜか効かない
    'line-blur': 2, // にじみ（ぼかし）
  },
}
export const clickCircleMemoMarkLayer = {
  id: 'click-circle-memo-mark-layer',
  type: 'symbol',
  source: 'click-circle-source',
  filter: ['==', '$type', 'Point'],
  layout: {
    'text-field': [
      'case',
      [
        'any',
        ['==', ['get', 'isFieldNoteMemo'], true],
        ['==', ['get', 'isFieldNoteMemo'], 'true'],
        ['==', ['get', 'isFieldNoteMemo'], 1],
        ['==', ['get', 'isFieldNoteMemo'], '1'],
        ['==', ['get', 'hasMemo'], true],
        ['==', ['get', 'hasMemo'], 'true'],
        ['==', ['get', 'hasMemo'], 1],
        ['==', ['get', 'hasMemo'], '1'],
      ],
      'm',
      '',
    ],
    'text-size': 16,
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    'text-anchor': 'center',
    'text-offset': [0, 0],
    'text-allow-overlap': true,
    'text-ignore-placement': true,
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0,0,0,0.4)',
    'text-halo-width': 1,
    'text-opacity': 1,
  },
  maxzoom: 24,
  minzoom: 0,
}
export const clickCircleLabelLayer = {
  id: 'click-circle-label-layer',
  type: 'symbol',
  source: 'click-circle-source',
  // filter: ['==', '$type', 'Point'],
  // labelType が文字列'0'でも数値0でも一致するようにする
  filter: ['any', ['==', ['get', 'labelType'], '0'], ['==', ['get', 'labelType'], 0]],
  layout: {
    'text-field': [
      'case',
      ['any', ['!', ['has', 'isRadius']], ['==', ['get', 'isRadius'], false]],
      ['case', ['has', 'isRadius'], ['get', 'label2'], ['get', 'label']],
      ['concat', ['get', 'label2'], '\n', ['get', 'label']],
    ],
    'text-size': ['get', 'text-size'],
    'text-offset': ['get', 'offsetValue'],
    'text-anchor': ['coalesce', ['get', 'textAnchor'], 'left'],
    'text-justify': ['coalesce', ['get', 'textJustify'], 'left'],
    'text-max-width': 100,
  },
  paint: {
    'text-color': ['get', 'color'],
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 0,
}
export const clickCircleCenterLabelLayer = {
  // 円の中心ポイント専用ラベルレイヤー（通常ラベル表示時のみ）
  id: 'click-circle-center-label-layer',
  type: 'symbol',
  source: 'click-circle-source',
  filter: ['all', ['any', ['==', ['get', 'labelType'], '0'], ['==', ['get', 'labelType'], 0]], ['has', 'isCircleCenter']],
  layout: {
    'text-field': [
      'case',
      // showRadius が false の場合は label2 のみ表示（半径は表示しない）
      ['==', ['get', 'showRadius'], false],
      ['get', 'label2'],
      // showRadius が true または未設定の場合は label2 + label を表示
      ['concat', ['coalesce', ['get', 'label2'], ''], '\n', ['coalesce', ['get', 'label'], '']],
    ],
    'text-size': ['get', 'text-size'],
    'text-offset': ['get', 'offsetValue'],
    'text-anchor': ['coalesce', ['get', 'textAnchor'], 'left'],
    'text-justify': ['coalesce', ['get', 'textJustify'], 'left'],
    'text-max-width': 100,
    'text-line-height': 1.5, // 行間を広げる（デフォルトは1.2）
  },
  paint: {
    'text-color': ['get', 'color'],
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 0,
}
export const clickCircSymbolLayer = {
  id: 'click-circle-symbol-layer',
  type: 'circle',
  source: 'click-circle-source',
  filter: ['all', ['==', '$type', 'Point'], ['!has', 'bearing']],
  paint: {
    'circle-radius': [
      'case',
      ['==', ['get', 'isRadius'], true],
      4,
      // フリーメモポイントは2倍サイズ
      ['has', 'isFieldNoteMemo'],
      16,
      8, // それ以外
    ],
    'circle-color': ['get', 'point-color'],
    'circle-stroke-width': 2,
    // 'circle-stroke-color': [
    //     'case',
    //     ['==', ['get', 'point-color'], 'rgba(0,0,0,0)'],  // point-color が透明なら…
    //     'rgba(0,0,0,0)',                                   // ストロークも透明に
    //     'white'                                          // それ以外は白
    // ],
    'circle-stroke-color': [
      'case',
      // point-color が透明ならストロークも透明
      ['==', ['get', 'point-color'], 'rgba(0,0,0,0)'],
      'rgba(0,0,0,0)',
      // それ以外は白
      'white',
    ],
    'circle-opacity': [
      'case',
      // 円の中心ポイント（isCircleCenter=1）かつ showRadius が false の場合は非表示
      ['all', ['==', ['get', 'isCircleCenter'], 1], ['==', ['get', 'showRadius'], false]],
      0,
      1, // それ以外
    ],
    'circle-stroke-opacity': [
      'case',
      // 円の中心ポイント（isCircleCenter=1）かつ showRadius が false の場合は非表示
      ['all', ['==', ['get', 'isCircleCenter'], 1], ['==', ['get', 'showRadius'], false]],
      0,
      1, // それ以外
    ],
  },
}
// ----------------------------------------------------------------------------
// ドロー選択表示用ソース（クリックされたドロー図形のコピーを上に載せる）
export const drawSelectionSource = {
  id: 'draw-selection-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
// ドロー選択表示用レイヤー（ポリゴン塗りつぶし）
export const drawSelectionPolygonFillLayer = {
  id: 'draw-selection-polygon-fill-layer',
  type: 'fill',
  source: 'draw-selection-source',
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'fill-color': '#FFEB3B', // 明るい黄色
    'fill-opacity': 0, // 透明
  },
}
// ドロー選択表示用レイヤー（ポリゴン枠線）
export const drawSelectionPolygonLineLayer = {
  id: 'draw-selection-polygon-line-layer',
  type: 'line',
  source: 'draw-selection-source',
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'line-color': '#00E5FF', // シアン
    'line-width': 8, // 太く目立たせる
  },
}
// ドロー選択表示用レイヤー（ライン）
export const drawSelectionLineLayer = {
  id: 'draw-selection-line-layer',
  type: 'line',
  source: 'draw-selection-source',
  filter: ['==', '$type', 'LineString'],
  paint: {
    'line-color': '#00E5FF', // シアン
    'line-width': 12, // より太く
  },
}
// ドロー選択表示用レイヤー（ポイント）
export const drawSelectionPointLayer = {
  id: 'draw-selection-point-layer',
  type: 'circle',
  source: 'draw-selection-source',
  filter: ['==', '$type', 'Point'],
  paint: {
    'circle-radius': 14, // さらに大きく
    'circle-color': 'transparent', // 中は透明
    'circle-stroke-width': 6, // 太い枠線
    'circle-stroke-color': '#00E5FF', // シアン
  },
}
// ----------------------------------------------------------------------------
// 矢印ソース
export const endPointSouce = {
  id: 'end-point-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
// 矢印レイヤー（始点、終点にのみ表示）
export const arrowsEndpointLayer = {
  id: 'arrows-endpoint-layer',
  type: 'symbol',
  source: 'end-point-source',
  filter: [
    'all',
    [
      'any',
      ['all', ['==', ['get', 'arrow-type'], 'end'], ['has', 'endpoint'], ['==', ['get', 'endpoint'], 'end']],
      ['==', ['get', 'arrow-type'], 'both'],
    ],
  ],
  layout: {
    'symbol-placement': 'point', // ポイントに配置
    'icon-image': 'arrow-sdf', // SDFアイコンを使用
    'icon-size': ['/', ['coalesce', ['get', 'line-width'], 3], 12], // 線幅に応じてサイズ変更（1/4）
    'icon-rotate': ['get', 'bearing'], // ラインの方向に合わせて回転
    'icon-rotation-alignment': 'map',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-offset': [-1, 0], // 矢印を終点から内側にずらす
  },
  paint: {
    'icon-opacity': 1,
    'icon-color': ['coalesce', ['get', 'arrow-color'], '#000000'], // 動的に色を設定
  },
}
// 矢印レイヤー（ラベル）
export const arrowsEndpointLabelLayer = {
  // ⭐️線のラベルを線の傾きに合わせる
  id: 'arrows-endpoint-label-layer',
  type: 'symbol',
  source: 'end-point-source',
  layout: {
    'text-field': ['get', 'label'],
    'text-size': 18,
    'text-offset': [0, -1],
    'text-anchor': 'center', // テキストのアンカー位置
    'text-allow-overlap': true,
    'text-ignore-placement': true,
    'text-rotate': ['get', 'textBearing'],
  },
  paint: {
    'icon-opacity': 1,
    'text-color': '#000000', // ラベルの色は常に黒色
    'text-halo-color': '#ffffff',
    'text-halo-width': 1,
  },
}
// 頂点レイヤー------------------------------------------------------------------
export const vertexSource = {
  id: 'vertex-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const vertexLayer = {
  id: 'vertex-layer',
  type: 'circle',
  source: 'vertex-source',
  paint: {
    'circle-radius': 7,
    'circle-color': '#2196f3',
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 2,
  },
  filter: ['==', '$type', 'Point'],
}
// 中点レイヤー--------------------------------------------------------------------
export const midpointSource = {
  id: 'midpoint-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
// シンプルなcircleレイヤーで中点表示
export const midpointLayer = {
  id: 'midpoint-layer',
  type: 'circle',
  source: 'midpoint-source',
  paint: {
    'circle-radius': 6,
    'circle-color': 'green',
    'circle-opacity': 0.8,
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
  layout: {},
  filter: ['==', '$type', 'Point'],
}
// 移動レイヤー ドラッグアイコン---------------------------------------------------------------------
export const dragHandlesSource = {
  id: 'drag-handles-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const dragHandleslayer = {
  id: 'drag-handles-layer',
  type: 'symbol',
  source: 'drag-handles-source',
  layout: {
    'icon-image': 'drag-icon',
    'icon-size': 0.1, // タッチ対応で少し大きめに
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  },
}
// 距離ラベルレイヤー-----------------------------------------------------------------
export const segmentLabelSource = {
  id: 'segment-label-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const segmentLabeleLayer = {
  // ⭐️線の距離ラベルはこれ
  id: 'segment-label-layer',
  type: 'symbol',
  source: 'segment-label-source',
  filter: ['==', ['get', 'calc'], 1],
  layout: {
    'text-field': ['get', 'distance'],
    'text-size': 18,
    'text-offset': [0, 0.6],
    'text-anchor': 'top',
    'text-rotate': ['get', 'textBearing'],
  },
  paint: {
    'text-color': '#000000',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1,
  },
}
// ポリゴン辺ラベルレイヤー（面積表示ON時の距離・内角）-----------------------------------------------------------------
export const polygonEdgeLabelSource = {
  id: 'polygon-edge-label-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const polygonEdgeLabelLayer = {
  id: 'polygon-edge-label-layer',
  type: 'symbol',
  source: 'polygon-edge-label-source',
  layout: {
    'text-field': ['get', 'label'],
    'text-size': 14,
    'text-anchor': 'center',
    'text-rotate': ['get', 'textBearing'],
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': ['case', ['get', 'isAngle'], '#ff6600', '#0066cc'],
    'text-halo-color': '#ffffff',
    'text-halo-width': 2,
  },
}
// フリーハンドプレビューレイヤー（ガイド）----------------------------------------------------------------
export const freehandPreviewSource = {
  id: 'freehand-preview-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const freehandPreviewLayer = {
  id: 'freehand-preview-layer',
  type: 'line',
  source: 'freehand-preview-source',
  paint: {
    'line-color': [
      'case',
      ['==', ['get', 'keiko'], 0],
      ['get', 'color'], // keiko が 0 のとき
      ['get', 'keiko-color'], // それ以外
    ],
    // 'line-color': 'black',
    'line-width': ['case', ['==', ['get', 'keiko'], 0], ['get', 'line-width'], ['*', ['get', 'line-width'], 3]],
    'line-opacity': [
      'case',
      ['==', ['get', 'keiko'], 0],
      0.6, // keiko が 0 のとき
      0.3, // それ以外
    ],
    // 'line-opacity': 0.3,
    // 'line-dasharray': [2, 2]
  },
}
// ガイドレイヤー-----------------------------------------------------------------
export const guideLineSource = {
  id: 'guide-line-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const guideLineLayer = {
  id: 'guide-line-layer',
  type: 'line',
  source: 'guide-line-source',
  paint: {
    'line-color': '#00f',
    'line-width': 2,
    'line-dasharray': [2, 2],
  },
}
export const guideLineVertexLayer = {
  id: 'guide-line-vertex-layer',
  type: 'circle',
  source: 'guide-line-source',
  // filter: ['==', '$type', 'Point'],
  paint: {
    'circle-radius': 4,
    'circle-color': '#00f',
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
// ガイドライン距離・角度ラベルレイヤー-----------------------------------------------------------------
export const guideLabelSource = {
  id: 'guide-label-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const guideLabelLayer = {
  id: 'guide-label-layer',
  type: 'symbol',
  source: 'guide-label-source',
  layout: {
    'text-field': ['get', 'distance'],
    'text-size': 16,
    'text-offset': [0, 0.6],
    'text-anchor': 'top',
    'text-rotate': ['get', 'textBearing'],
  },
  paint: {
    'text-color': '#0000ff',
    'text-halo-color': '#ffffff',
    'text-halo-width': 2,
  },
}
// 標高レイヤーー-----------------------------------------------------------------
export const elevationSource = {
  id: 'elevation-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}
export const elevationLayer = {
  id: 'elevation-layer',
  type: 'circle',
  source: 'elevation-source',
  paint: {
    'circle-radius': 8,
    'circle-color': '#ff0000',
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
// --------------------------------------------------------------------------------
// クリックで追加するポイントのGeoJSONソースを作成
export const clickPointSource = {
  iD: 'click-points-source',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  },
}

// ポイントを描画するレイヤーを追加
export const clickPointLayer = {
  id: 'click-points-layer',
  type: 'circle',
  source: 'click-points-source',
  paint: {
    'circle-radius': 8,
    'circle-color': '#ff0000',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
  },
}

export const extSource = {
  id: 'ext-source',
  obj: {
    type: 'raster',
    tiles: [],
  },
}
export const extLayer = {
  id: 'oh-extLayer',
  type: 'raster',
  source: 'ext-source',
}

export const geotiffSource = {
  id: 'geotiff-source',
  obj: {
    type: 'image',
    url: '',
    coordinates: '',
  },
}
export const geotiffLayer = {
  id: 'oh-geotiff-layer',
  type: 'raster',
  source: 'geotiff-source',
  paint: {
    'raster-resampling': 'nearest', // タイル境界の補間方式を変更
    // 'raster-padding': 5 // 境界パディングを追加
  },
}

export const jpgSource = {
  id: 'jpg-source',
  obj: {
    type: 'image',
    url: '',
    coordinates: '',
  },
}
export const jpgLayer = {
  id: 'oh-jpg-layer',
  type: 'raster',
  source: 'jpg-source',
  paint: {},
}

export const pngSource = {
  id: 'png-source',
  obj: {
    type: 'image',
    url: '',
    coordinates: '',
  },
}
export const pngLayer = {
  id: 'oh-png-layer',
  type: 'raster',
  source: 'png-source',
  paint: {},
}

export const vpsTileSource = {
  id: 'vpstile-source',
  obj: {
    type: 'raster',
    tiles: [],
    bounds: null,
    maxzoom: 26,
    // scheme: 'tms',
  },
}
export const vpsTileLayer = {
  id: 'oh-vpstile-layer',
  type: 'raster',
  source: 'vpstile-source',
  paint: {},
}
// 液状化（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const fjEkijyokaSourse = {
  id: 'fj-ekijyoka-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT025&z={z}&x={x}&y={y}'],
    minzoom: 10,
    maxzoom: 22,
  },
}

const fjEkijyokatLayer = {
  id: 'oh-fj-ekijyoka-layer',
  type: 'fill',
  source: 'fj-ekijyoka-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'liquefaction_tendency_level'],
      1,
      'rgba(8, 64, 129, 0.8)', // 濃い色
      2,
      'rgba(8, 104, 172, 0.8)',
      3,
      'rgba(67, 162, 202, 0.8)',
      4,
      'rgba(123, 204, 196, 0.8)',
      5,
      'rgba(204, 235, 197, 0.8)',
      6,
      'rgba(247, 252, 240, 0.8)', // 薄い色
      'rgba(136, 136, 136, 0.8)', // デフォルト
    ],
  },
  minzoom: 9,
  maxzoom: 22,
}
const fjEkijyokatLayerLine = {
  id: 'oh-fj-ekijyoka-layer-line',
  type: 'line',
  source: 'fj-ekijyoka-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'gray',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      15,
      1,
    ],
  },
}
// 立地適正化計画（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const fjRicchitekiseiksSourse = {
  id: 'fj-ricchitekiseika-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT003&z={z}&x={x}&y={y}'],
    minzoom: 10,
    maxzoom: 22,
  },
}

const fjRicchitekiseikaLayer = {
  id: 'oh-fj-ricchitekiseika-layer',
  type: 'fill',
  source: 'fj-ricchitekiseika-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'kubun_id'],
      0,
      'rgba(8, 64, 129, 0.8)', // 立地適正化計画区域
      31,
      'rgba(0, 255, 0, 0.8)', // 居住誘導区域
      32,
      'rgba(6, 162, 2, 0.8)', // 都市機能誘導区域
      'rgba(0, 0, 0, 0.8)', // デフォルト
    ],
  },
  minzoom: 9,
  maxzoom: 22,
}
const fjRicchitekiseikaLayerLine = {
  id: 'oh-fj-ricchitekiseika-layer-line',
  type: 'line',
  source: 'fj-ricchitekiseika-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'gray',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0, 15, 1],
  },
}

// 国土調査（災害履歴）（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const saigaiRirekiSource = {
  id: 'saigai-rireki-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XST001&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}

const saigaiRirekiLayer = {
  id: 'oh-saigai-rireki-layer',
  type: 'fill',
  source: 'saigai-rireki-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'disastertype_code'],
      '11', 'rgba(30, 144, 255, 0.6)',   // 浸水域等 - ドジャーブルー
      '12', 'rgba(0, 0, 139, 0.6)',      // 堤防決壊箇所等 - ダークブルー
      '13', 'rgba(135, 206, 250, 0.6)',  // 高潮浸水域等 - ライトスカイブルー
      '14', 'rgba(75, 0, 130, 0.6)',     // 高潮破堤箇所等 - インディゴ
      '21', 'rgba(139, 69, 19, 0.6)',    // がけ崩れ等 - サドルブラウン
      '22', 'rgba(210, 105, 30, 0.6)',   // 地すべり等 - チョコレート
      '23', 'rgba(184, 134, 11, 0.6)',   // 河道閉塞箇所等 - ダークゴールデンロッド
      '24', 'rgba(178, 34, 34, 0.6)',    // 土石流等 - ファイアブリック
      '33', 'rgba(255, 215, 0, 0.6)',    // 液状化 - ゴールド
      '34', 'rgba(255, 140, 0, 0.6)',    // 地震土砂災害 - ダークオレンジ
      '37', 'rgba(0, 206, 209, 0.6)',    // 津波高 - ダークターコイズ
      '38', 'rgba(32, 178, 170, 0.6)',   // 津波浸水域 - ライトシーグリーン
      'rgba(128, 128, 128, 0.6)',        // デフォルト - グレー
    ],
  },
  minzoom: 9,
  maxzoom: 22,
}

const saigaiRirekiLayerLine = {
  id: 'oh-saigai-rireki-layer-line',
  type: 'line',
  source: 'saigai-rireki-source',
  'source-layer': 'hits',
  paint: {
    'line-color': [
      'match',
      ['get', 'disastertype_code'],
      '11', 'rgba(30, 144, 255, 1)',
      '12', 'rgba(0, 0, 139, 1)',
      '13', 'rgba(135, 206, 250, 1)',
      '14', 'rgba(75, 0, 130, 1)',
      '21', 'rgba(139, 69, 19, 1)',
      '22', 'rgba(210, 105, 30, 1)',
      '23', 'rgba(184, 134, 11, 1)',
      '24', 'rgba(178, 34, 34, 1)',
      '33', 'rgba(255, 215, 0, 1)',
      '34', 'rgba(255, 140, 0, 1)',
      '37', 'rgba(0, 206, 209, 1)',
      '38', 'rgba(32, 178, 170, 1)',
      'rgba(128, 128, 128, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 15, 2],
  },
  minzoom: 9,
  maxzoom: 22,
}

// 都市計画区域/区域区分（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibToshikeikakuSource = {
  id: 'reinfolib-toshikeikaku-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT001&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibToshikeikakuLayer = {
  id: 'oh-reinfolib-toshikeikaku-layer',
  type: 'fill',
  source: 'reinfolib-toshikeikaku-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'kubun_id'],
      21, 'rgba(173, 216, 230, 0.5)',    // 都市計画区域 - ライトブルー
      22, 'rgba(255, 200, 150, 0.5)',    // 市街化区域 - オレンジ（senbiki2024と同系）
      23, 'rgba(150, 220, 150, 0.5)',    // 市街化調整区域 - グリーン（senbiki2024と同系）
      24, 'rgba(200, 180, 230, 0.5)',    // 非線引き都市計画区域 - ラベンダー
      'rgba(200, 200, 200, 0.5)',        // デフォルト
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibToshikeikakuLayerLine = {
  id: 'oh-reinfolib-toshikeikaku-layer-line',
  type: 'line',
  source: 'reinfolib-toshikeikaku-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 1.5],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 用途地域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibYotochiikiSource = {
  id: 'reinfolib-yotochiiki-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT002&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibYotochiikiLayer = {
  id: 'oh-reinfolib-yotochiiki-layer',
  type: 'fill',
  source: 'reinfolib-yotochiiki-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'youto_id'],
      1, 'rgba(0, 180, 0, 0.6)',       // 第1種低層住居専用地域（youto2024と同系色）
      2, 'rgba(50, 200, 50, 0.6)',     // 第2種低層住居専用地域
      3, 'rgba(100, 200, 100, 0.6)',   // 第1種中高層住居専用地域
      4, 'rgba(150, 220, 150, 0.6)',   // 第2種中高層住居専用地域
      5, 'rgba(200, 240, 200, 0.6)',   // 第1種住居地域
      6, 'rgba(230, 250, 230, 0.6)',   // 第2種住居地域
      7, 'rgba(255, 230, 180, 0.6)',   // 準住居地域
      8, 'rgba(180, 255, 180, 0.6)',   // 田園住居地域
      9, 'rgba(255, 180, 180, 0.6)',   // 近隣商業地域
      10, 'rgba(255, 100, 100, 0.6)',  // 商業地域
      11, 'rgba(200, 150, 255, 0.6)',  // 準工業地域
      12, 'rgba(150, 150, 200, 0.6)',  // 工業地域
      13, 'rgba(100, 100, 180, 0.6)',  // 工業専用地域
      'rgba(200, 200, 200, 0.6)',      // デフォルト
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibYotochiikiLayerLine = {
  id: 'oh-reinfolib-yotochiiki-layer-line',
  type: 'line',
  source: 'reinfolib-yotochiiki-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(50, 50, 50, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.3, 15, 1],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 小学校区（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibShogakkokuSource = {
  id: 'reinfolib-shogakkoku-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT004&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibShogakkokuLayer = {
  id: 'oh-reinfolib-shogakkoku-layer',
  type: 'fill',
  source: 'reinfolib-shogakkoku-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(255, 223, 0, 0.3)',
    'fill-outline-color': 'rgba(255, 165, 0, 0.8)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibShogakkokuLayerLine = {
  id: 'oh-reinfolib-shogakkoku-layer-line',
  type: 'line',
  source: 'reinfolib-shogakkoku-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(255, 165, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 15, 3],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibShogakkokuLayerLabel = {
  id: 'oh-reinfolib-shogakkoku-layer-label',
  type: 'symbol',
  source: 'reinfolib-shogakkoku-source',
  'source-layer': 'hits',
  layout: {
    'text-field': ['get', 'school_name'],
    'text-size': 12,
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 中学校区（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibChugakkokuSource = {
  id: 'reinfolib-chugakkoku-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT005&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibChugakkokuLayer = {
  id: 'oh-reinfolib-chugakkoku-layer',
  type: 'fill',
  source: 'reinfolib-chugakkoku-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(135, 206, 250, 0.3)',
    'fill-outline-color': 'rgba(70, 130, 180, 0.8)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibChugakkokuLayerLine = {
  id: 'oh-reinfolib-chugakkoku-layer-line',
  type: 'line',
  source: 'reinfolib-chugakkoku-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(70, 130, 180, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1, 15, 3],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibChugakkokuLayerLabel = {
  id: 'oh-reinfolib-chugakkoku-layer-label',
  type: 'symbol',
  source: 'reinfolib-chugakkoku-source',
  'source-layer': 'hits',
  layout: {
    'text-field': ['get', 'school_name'],
    'text-size': 12,
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 防火地域/準防火地域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibBokachiikiSource = {
  id: 'reinfolib-bokachiiki-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT014&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibBokachiikiLayer = {
  id: 'oh-reinfolib-bokachiiki-layer',
  type: 'fill',
  source: 'reinfolib-bokachiiki-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': [
      'match',
      ['get', 'fire_prevention_district_code'],
      '1', 'rgba(255, 0, 0, 0.5)',       // 防火地域 - 赤
      '2', 'rgba(255, 165, 0, 0.5)',     // 準防火地域 - オレンジ
      'rgba(200, 200, 200, 0.5)',        // デフォルト
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibBokachiikiLayerLine = {
  id: 'oh-reinfolib-bokachiiki-layer-line',
  type: 'line',
  source: 'reinfolib-bokachiiki-source',
  'source-layer': 'hits',
  paint: {
    'line-color': [
      'match',
      ['get', 'fire_prevention_district_code'],
      '1', 'rgba(200, 0, 0, 1)',
      '2', 'rgba(200, 130, 0, 1)',
      'rgba(100, 100, 100, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 1.5],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 土砂災害警戒区域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibDosyaSource = {
  id: 'reinfolib-dosya-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT029&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibDosyaLayer = {
  id: 'oh-reinfolib-dosya-layer',
  type: 'fill',
  source: 'reinfolib-dosya-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: sediment_disaster_alert_area）
  paint: {
    'fill-color': [
      'match',
      ['get', 'A33_002'],
      2, 'rgba(220, 50, 50, 0.6)',    // 特別警戒区域（レッドゾーン）
      1, 'rgba(255, 200, 0, 0.6)',    // 警戒区域（イエローゾーン）
      'rgba(255, 200, 0, 0.4)',       // fallback
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibDosyaLayerLine = {
  id: 'oh-reinfolib-dosya-layer-line',
  type: 'line',
  source: 'reinfolib-dosya-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: sediment_disaster_alert_area）
  paint: {
    'line-color': [
      'match',
      ['get', 'A33_002'],
      2, 'rgba(180, 30, 30, 0.8)',    // レッド境界線
      1, 'rgba(200, 150, 0, 0.8)',    // イエロー境界線
      'rgba(200, 100, 0, 0.8)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 土砂災害警戒区域（ハザード人口分析用 — 専用ソース・レイヤー）
// oh-reinfolib-dosya と同じデータだが、レイヤーON/OFF時のソース競合を避けるため専用IDで定義
const hazpopDosyaSource = {
  id: 'hazpop-dosya-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT029&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const hazpopDosyaFill = {
  id: 'oh-hazpop-dosya-fill',
  type: 'fill',
  source: 'hazpop-dosya-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: sediment_disaster_alert_area）
  paint: {
    'fill-color': [
      'match',
      ['get', 'A33_002'],
      2, 'rgba(220, 50, 50, 0.6)',    // 特別警戒区域（レッドゾーン）
      1, 'rgba(255, 200, 0, 0.6)',    // 警戒区域（イエローゾーン）
      'rgba(255, 200, 0, 0.4)',       // fallback
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const hazpopDosyaLine = {
  id: 'oh-hazpop-dosya-line',
  type: 'line',
  source: 'hazpop-dosya-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: sediment_disaster_alert_area）
  paint: {
    'line-color': [
      'match',
      ['get', 'A33_002'],
      2, 'rgba(180, 30, 30, 0.8)',    // レッド境界線
      1, 'rgba(200, 150, 0, 0.8)',    // イエロー境界線
      'rgba(200, 100, 0, 0.8)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 浸水深（文字列型属性）→ 深度別色の MapLibre 式を生成
// 「Xm以上Ym未満」形式の文字列から下限値のパターンで色を判定
// oh-kozui-polygon と同じ凡例カラーに統一
// ※小さい値から順にチェック（"0.5m以上"が"5m以上"を部分文字列として含む問題を回避）
function _floodDepthStringColor (attr) {
  const s = ['to-string', ['coalesce', ['get', attr], '']]
  return [
    'case',
    ['in', '0.3m以上', s],
    'rgba(247, 245, 169, 0.7)',   // 0.3-0.5m
    ['in', '0.5m以上', s],
    'rgba(255, 230, 200, 0.7)',   // 0.5-1m
    ['any', ['in', '1m以上', s], ['in', '1.0m以上', s]],
    'rgba(255, 216, 192, 0.7)',   // 1-2m
    ['any', ['in', '2m以上', s], ['in', '2.0m以上', s]],
    'rgba(255, 216, 192, 0.7)',   // 2-3m
    ['any', ['in', '3m以上', s], ['in', '3.0m以上', s]],
    'rgba(255, 183, 183, 0.7)',   // 3-5m
    ['any', ['in', '5m以上', s], ['in', '5.0m以上', s]],
    'rgba(255, 145, 145, 0.7)',   // 5-10m
    ['any', ['in', '10m以上', s], ['in', '10.0m以上', s]],
    'rgba(242, 133, 201, 0.7)',   // 10-20m
    ['any', ['in', '20m以上', s], ['in', '20.0m以上', s]],
    'rgba(220, 122, 220, 0.7)',   // 20m以上
    'rgba(247, 245, 169, 0.5)',   // 0.3m未満 / default
  ]
}

// 津波浸水想定（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibTsunamiSource = {
  id: 'reinfolib-tsunami-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT028&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibTsunamiLayer = {
  id: 'oh-reinfolib-tsunami-layer',
  type: 'fill',
  source: 'reinfolib-tsunami-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: tsunami_area）
  paint: {
    'fill-color': _floodDepthStringColor('A40_003'),
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibTsunamiLayerLine = {
  id: 'oh-reinfolib-tsunami-layer-line',
  type: 'line',
  source: 'reinfolib-tsunami-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: tsunami_area）
  paint: {
    'line-color': 'rgba(0, 0, 0, 0)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 洪水浸水想定区域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibKozuiSource = {
  id: 'reinfolib-kozui-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT026&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibKozuiLayer = {
  id: 'oh-reinfolib-kozui-layer',
  type: 'fill',
  source: 'reinfolib-kozui-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: flood_area_maximum_scale）
  paint: {
    'fill-color': [
      'match',
      ['get', 'A31a_205'],
      1, 'rgba(247, 245, 169, 0.7)',   // 0.5m未満
      2, 'rgba(255, 216, 192, 0.7)',   // 0.5-3m
      3, 'rgba(255, 183, 183, 0.7)',   // 3-5m
      4, 'rgba(255, 145, 145, 0.7)',   // 5-10m
      5, 'rgba(242, 133, 201, 0.7)',   // 10-20m
      6, 'rgba(220, 122, 220, 0.7)',   // 20m以上
      'rgba(200, 200, 200, 0.5)',
    ],
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibKozuiLayerLine = {
  id: 'oh-reinfolib-kozui-layer-line',
  type: 'line',
  source: 'reinfolib-kozui-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: flood_area_maximum_scale）
  paint: {
    'line-color': 'rgba(0, 0, 0, 0)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 小学校（施設）（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibShogakkoSource = {
  id: 'reinfolib-shogakko-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT006&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibShogakkoLayer = {
  id: 'oh-reinfolib-shogakko-layer',
  type: 'circle',
  source: 'reinfolib-shogakko-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(255, 223, 0, 0.9)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 15, 8],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#333',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibShogakkoLayerLabel = {
  id: 'oh-reinfolib-shogakko-layer-label',
  type: 'symbol',
  source: 'reinfolib-shogakko-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'P29_004_ja'],
      ['get', 'P29_004'],
      ['get', 'A27_004_ja'],
      ['get', 'A27_004'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 11,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 14,
  maxzoom: 22,
}

// 保育園・幼稚園等（不動産情報ライブラリ）XKT007 - point---------------------------------------------------------------------------------------------
const reinfolibHoikuenSource = {
  id: 'reinfolib-hoikuen-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT007&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibHoikuenLayer = {
  id: 'oh-reinfolib-hoikuen-layer',
  type: 'circle',
  source: 'reinfolib-hoikuen-source',
  'source-layer': 'hits',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 15, 8],
    'circle-color': 'rgba(255, 182, 193, 0.9)',
    'circle-stroke-width': 1.5,
    'circle-stroke-color': '#fff',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibHoikuenLayerLabel = {
  id: 'oh-reinfolib-hoikuen-layer-label',
  type: 'symbol',
  source: 'reinfolib-hoikuen-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'preSchoolName_ja'],
      ['get', 'preSchoolName'],
      ['get', 'P29_004_ja'],
      ['get', 'P29_004'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.2],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#d63384',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 14,
  maxzoom: 22,
}

// 医療施設（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibIryoSource = {
  id: 'reinfolib-iryo-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT010&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibIryoLayer = {
  id: 'oh-reinfolib-iryo-layer',
  type: 'circle',
  source: 'reinfolib-iryo-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(255, 0, 0, 0.9)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 15, 7],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibIryoLayerLabel = {
  id: 'oh-reinfolib-iryo-layer-label',
  type: 'symbol',
  source: 'reinfolib-iryo-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'P04_002_ja'],
      ['get', 'P04_002'],
      ['get', 'P04_001_name_ja'],
      ['get', 'P04_001'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 14,
  maxzoom: 22,
}

// 福祉施設（児童）（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibFukushiJidoSource = {
  id: 'reinfolib-fukushi-jido-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT011&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibFukushiJidoLayer = {
  id: 'oh-reinfolib-fukushi-jido-layer',
  type: 'circle',
  source: 'reinfolib-fukushi-jido-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(255, 182, 193, 0.9)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 15, 7],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#333',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibFukushiJidoLayerLabel = {
  id: 'oh-reinfolib-fukushi-jido-layer-label',
  type: 'symbol',
  source: 'reinfolib-fukushi-jido-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'P14_008_ja'],
      ['get', 'P14_008'],
      ['get', 'welfareFacilityName_ja'],
      ['get', 'P14_004_ja'],
      ['get', 'P14_004'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 将来推計人口250mメッシュ（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibJinkouSuikeiSource = {
  id: 'reinfolib-jinkou-suikei-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT013&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
// デフォルトは指数100の色（extコンポーネントが色を制御する）
const reinfolibJinkouSuikeiLayer = {
  id: 'oh-reinfolib-jinkou-suikei-layer',
  type: 'fill',
  source: 'reinfolib-jinkou-suikei-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(170, 220, 160, 0.6)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibJinkouSuikeiLayerLine = {
  id: 'oh-reinfolib-jinkou-suikei-layer-line',
  type: 'line',
  source: 'reinfolib-jinkou-suikei-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(200, 100, 0, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.3, 15, 1],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 将来推計人口1kmメッシュ R6国政局推計（PMTiles）---------------------------------------------------------------------------------------------
const suikeiR6_1kmSource = {
  id: 'suikei-r6-1km-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/1km_suikei/1km_mesh_r6_nosimplify.pmtiles',
    maxzoom: 12,
  },
}
// デフォルトは指数100の色（extコンポーネントが色を制御する）
const suikeiR6_1kmLayer = {
  id: 'oh-suikei-r6-1km-layer',
  type: 'fill',
  source: 'suikei-r6-1km-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(170, 220, 160, 0.6)',
  },
  minzoom: 2,
  maxzoom: 22,
}
const suikeiR6_1kmLayerLine = {
  id: 'oh-suikei-r6-1km-layer-line',
  type: 'line',
  source: 'suikei-r6-1km-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 100, 0, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.3, 12, 0.5, 14, 1],
  },
  minzoom: 9,
  maxzoom: 22,
}

// 将来推計人口250mメッシュ R6国政局推計（PMTiles）---------------------------------------------------------------------------------------------
const suikeiR6_250mSource = {
  id: 'suikei-r6-250m-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/250m_suikei/250m_mesh_r6_nosimplify.pmtiles',
    maxzoom: 14,
  },
}
const suikeiR6_250mLayer = {
  id: 'oh-suikei-r6-250m-layer',
  type: 'fill',
  source: 'suikei-r6-250m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(170, 220, 160, 0.6)',
  },
  minzoom: 2,
  maxzoom: 22,
}
const suikeiR6_250mLayerLine = {
  id: 'oh-suikei-r6-250m-layer-line',
  type: 'line',
  source: 'suikei-r6-250m-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 100, 0, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.2, 14, 0.5, 16, 1],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 駅別乗降客数（不動産情報ライブラリ）XKT015 - point---------------------------------------------------------------------------------------------
const reinfolibEkiJokokyakuSource = {
  id: 'reinfolib-eki-jokokyaku-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT015&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibEkiJokokyakuLayer = {
  id: 'oh-reinfolib-eki-jokokyaku-layer',
  type: 'circle',
  source: 'reinfolib-eki-jokokyaku-source',
  'source-layer': 'hits',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 15, 12],
    'circle-color': 'rgba(0, 100, 200, 0.8)',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibEkiJokokyakuLayerLabel = {
  id: 'oh-reinfolib-eki-jokokyaku-layer-label',
  type: 'symbol',
  source: 'reinfolib-eki-jokokyaku-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'S12_001_ja'],
      ['get', 'S12_001'],
      ['get', 'station_name_ja'],
      ['get', 'station_name'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#0064c8',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 災害危険区域（不動産情報ライブラリ）XKT016 - polygon---------------------------------------------------------------------------------------------
const reinfolibSaigaiKikenSource = {
  id: 'reinfolib-saigai-kiken-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT016&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibSaigaiKikenLayer = {
  id: 'oh-reinfolib-saigai-kiken-layer',
  type: 'fill',
  source: 'reinfolib-saigai-kiken-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(255, 69, 0, 0.5)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibSaigaiKikenLayerLine = {
  id: 'oh-reinfolib-saigai-kiken-layer-line',
  type: 'line',
  source: 'reinfolib-saigai-kiken-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(220, 20, 60, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 図書館（不動産情報ライブラリ）XKT017 - point---------------------------------------------------------------------------------------------
const reinfolibToshokanSource = {
  id: 'reinfolib-toshokan-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT017&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibToshokanLayer = {
  id: 'oh-reinfolib-toshokan-layer',
  type: 'circle',
  source: 'reinfolib-toshokan-source',
  'source-layer': 'hits',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 15, 8],
    'circle-color': 'rgba(139, 69, 19, 0.8)',
    'circle-stroke-width': 1.5,
    'circle-stroke-color': '#fff',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibToshokanLayerLabel = {
  id: 'oh-reinfolib-toshokan-layer-label',
  type: 'symbol',
  source: 'reinfolib-toshokan-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'P27_005_ja'],
      ['get', 'P27_005'],
      ['get', 'P27_003_ja'],
      ['get', 'P27_003'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 市区町村役場及び集会施設等（不動産情報ライブラリ）XKT018 - point---------------------------------------------------------------------------------------------
const reinfolibYakubaSource = {
  id: 'reinfolib-yakuba-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT018&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibYakubaLayer = {
  id: 'oh-reinfolib-yakuba-layer',
  type: 'circle',
  source: 'reinfolib-yakuba-source',
  'source-layer': 'hits',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 15, 10],
    'circle-color': 'rgba(128, 0, 128, 0.8)',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibYakubaLayerLabel = {
  id: 'oh-reinfolib-yakuba-layer-label',
  type: 'symbol',
  source: 'reinfolib-yakuba-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'P05_003_ja'],
      ['get', 'P05_003'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#800080',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 自然公園地域（不動産情報ライブラリ）XKT019 - polygon---------------------------------------------------------------------------------------------
const reinfolibShizenKoenSource = {
  id: 'reinfolib-shizen-koen-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT019&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibShizenKoenLayer = {
  id: 'oh-reinfolib-shizen-koen-layer',
  type: 'fill',
  source: 'reinfolib-shizen-koen-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(0, 128, 0, 0.3)',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibShizenKoenLayerLine = {
  id: 'oh-reinfolib-shizen-koen-layer-line',
  type: 'line',
  source: 'reinfolib-shizen-koen-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(0, 100, 0, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0.5, 15, 2],
  },
  minzoom: 9,
  maxzoom: 22,
}

// 大規模盛土造成地マップ（不動産情報ライブラリ）XKT020 - polygon---------------------------------------------------------------------------------------------
const reinfolibMoridoSource = {
  id: 'reinfolib-morido-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT020&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibMoridoLayer = {
  id: 'oh-reinfolib-morido-layer',
  type: 'fill',
  source: 'reinfolib-morido-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(139, 69, 19, 0.5)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibMoridoLayerLine = {
  id: 'oh-reinfolib-morido-layer-line',
  type: 'line',
  source: 'reinfolib-morido-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(101, 67, 33, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 地区計画（不動産情報ライブラリ）XKT023 - polygon---------------------------------------------------------------------------------------------
const reinfolibChikuKeikakuSource = {
  id: 'reinfolib-chiku-keikaku-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT023&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibChikuKeikakuLayer = {
  id: 'oh-reinfolib-chiku-keikaku-layer',
  type: 'fill',
  source: 'reinfolib-chiku-keikaku-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(70, 130, 180, 0.4)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibChikuKeikakuLayerLine = {
  id: 'oh-reinfolib-chiku-keikaku-layer-line',
  type: 'line',
  source: 'reinfolib-chiku-keikaku-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(0, 0, 139, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 高度利用地区（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibKodoRiyoSource = {
  id: 'reinfolib-kodo-riyo-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT024&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibKodoRiyoLayer = {
  id: 'oh-reinfolib-kodo-riyo-layer',
  type: 'fill',
  source: 'reinfolib-kodo-riyo-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(255, 140, 0, 0.4)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibKodoRiyoLayerLine = {
  id: 'oh-reinfolib-kodo-riyo-layer-line',
  type: 'line',
  source: 'reinfolib-kodo-riyo-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(255, 100, 0, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 1.5],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 高潮浸水想定区域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibTakashioSource = {
  id: 'reinfolib-takashio-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT027&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibTakashioLayer = {
  id: 'oh-reinfolib-takashio-layer',
  type: 'fill',
  source: 'reinfolib-takashio-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: storm_surge_area）
  paint: {
    'fill-color': _floodDepthStringColor('A49_003'),
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibTakashioLayerLine = {
  id: 'oh-reinfolib-takashio-layer-line',
  type: 'line',
  source: 'reinfolib-takashio-source',
  'source-layer': 'hits',  // 不動産情報ライブラリAPI仕様変更（旧: storm_surge_area）
  paint: {
    'line-color': 'rgba(0, 0, 0, 0)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 地すべり防止区域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibJisuberiSource = {
  id: 'reinfolib-jisuberi-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT021&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibJisuberiLayer = {
  id: 'oh-reinfolib-jisuberi-layer',
  type: 'fill',
  source: 'reinfolib-jisuberi-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(139, 69, 19, 0.5)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibJisuberiLayerLine = {
  id: 'oh-reinfolib-jisuberi-layer-line',
  type: 'line',
  source: 'reinfolib-jisuberi-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(139, 69, 19, 0.9)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 急傾斜地崩壊危険区域（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibKyukeishaSource = {
  id: 'reinfolib-kyukeisha-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT022&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibKyukeishaLayer = {
  id: 'oh-reinfolib-kyukeisha-layer',
  type: 'fill',
  source: 'reinfolib-kyukeisha-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(178, 34, 34, 0.5)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibKyukeishaLayerLine = {
  id: 'oh-reinfolib-kyukeisha-layer-line',
  type: 'line',
  source: 'reinfolib-kyukeisha-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(178, 34, 34, 0.9)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 都市計画道路（不動産情報ライブラリ）XKT030 - line---------------------------------------------------------------------------------------------
const reinfolibToshikeikakuDoroSource = {
  id: 'reinfolib-toshikeikaku-doro-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT030&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibToshikeikakuDoroLayer = {
  id: 'oh-reinfolib-toshikeikaku-doro-layer',
  type: 'line',
  source: 'reinfolib-toshikeikaku-doro-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(255, 0, 0, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 2, 15, 6],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 指定緊急避難場所（不動産情報ライブラリ）XGT001 - point---------------------------------------------------------------------------------------------
const reinfolibHinanbashoSource = {
  id: 'reinfolib-hinanbasho-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XGT001&z={z}&x={x}&y={y}'],
    minzoom: 9,
    maxzoom: 22,
  },
}
const reinfolibHinanbashoLayer = {
  id: 'oh-reinfolib-hinanbasho-layer',
  type: 'circle',
  source: 'reinfolib-hinanbasho-source',
  'source-layer': 'hits',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 4, 15, 10],
    'circle-color': 'rgba(0, 128, 0, 0.9)',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 9,
  maxzoom: 22,
}
const reinfolibHinanbashoLayerLabel = {
  id: 'oh-reinfolib-hinanbasho-layer-label',
  type: 'symbol',
  source: 'reinfolib-hinanbasho-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'facility_name_ja'],
      ['get', 'facility_name'],
      ['get', 'P20_003_ja'],
      ['get', 'P20_003'],
      ['get', 'P20_002_ja'],
      ['get', 'P20_002'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#006400',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 人口集中地区（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibDidSource = {
  id: 'reinfolib-did-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XKT031&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibDidLayer = {
  id: 'oh-reinfolib-did-layer',
  type: 'fill',
  source: 'reinfolib-did-source',
  'source-layer': 'hits',
  paint: {
    'fill-color': 'rgba(255, 100, 100, 0.3)',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibDidLayerLine = {
  id: 'oh-reinfolib-did-layer-line',
  type: 'line',
  source: 'reinfolib-did-source',
  'source-layer': 'hits',
  paint: {
    'line-color': 'rgba(200, 50, 50, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.5, 15, 2],
  },
  minzoom: 11,
  maxzoom: 22,
}

// 不動産取引価格情報（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
const reinfolibTorihikiSource = {
  id: 'reinfolib-torihiki-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XPT001&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibTorihikiLayer = {
  id: 'oh-reinfolib-torihiki-layer',
  type: 'circle',
  source: 'reinfolib-torihiki-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(255, 215, 0, 0.8)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 15, 7],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#333',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibTorihikiLayerLabel = {
  id: 'oh-reinfolib-torihiki-layer-label',
  type: 'symbol',
  source: 'reinfolib-torihiki-source',
  'source-layer': 'hits',
  layout: {
    'text-field': [
      'coalesce',
      ['get', 'u_transaction_price_total_ja'],
      ['get', 'u_transaction_price_total'],
      ['get', 'land_type_name_ja'],
      ['get', 'district_name_ja'],
      ['get', 'name_ja'],
      ['get', 'name'],
    ],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 13,
  maxzoom: 22,
}

// 地価公示（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
// priceClassification=0 で地価公示のみ取得
const reinfolibChikaKojiSource = {
  id: 'reinfolib-chikakoji-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XPT002&priceClassification=0&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibChikaKojiLayer = {
  id: 'oh-reinfolib-chikakoji-layer',
  type: 'circle',
  source: 'reinfolib-chikakoji-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(25, 118, 210, 0.9)', // 青（#1976d2）
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 15, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibChikaKojiLayerLabel = {
  id: 'oh-reinfolib-chikakoji-layer-label',
  type: 'symbol',
  source: 'reinfolib-chikakoji-source',
  'source-layer': 'hits',
  layout: {
    'text-field': ['concat', '¥', ['get', 'L01_006']],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#1565c0', // 濃い青
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 14,
  maxzoom: 22,
}

// 都道府県地価調査（不動産情報ライブラリ）---------------------------------------------------------------------------------------------
// priceClassification=1 で都道府県地価調査のみ取得
const reinfolibChikaChosaSource = {
  id: 'reinfolib-chikachosa-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/myphp/proxy.php?api=XPT002&priceClassification=1&z={z}&x={x}&y={y}'],
    minzoom: 11,
    maxzoom: 22,
  },
}
const reinfolibChikaChosaLayer = {
  id: 'oh-reinfolib-chikachosa-layer',
  type: 'circle',
  source: 'reinfolib-chikachosa-source',
  'source-layer': 'hits',
  paint: {
    'circle-color': 'rgba(56, 142, 60, 0.9)', // 緑（#388e3c）
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 4, 15, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 11,
  maxzoom: 22,
}
const reinfolibChikaChosaLayerLabel = {
  id: 'oh-reinfolib-chikachosa-layer-label',
  type: 'symbol',
  source: 'reinfolib-chikachosa-source',
  'source-layer': 'hits',
  layout: {
    'text-field': ['concat', '¥', ['get', 'L02_006']],
    'text-size': 10,
    'text-offset': [0, 1.5],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
  },
  paint: {
    'text-color': '#2e7d32', // 濃い緑
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 14,
  maxzoom: 22,
}

// 民電 -----------------------------------------------------------------------------------------------------------------
const mindenSource = {
  id: 'minden-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/ntrip/2025_minden5.geojson',
  },
}
const mindenCenterPointLayer = {
  id: 'oh-minden-center',
  type: 'circle',
  source: 'minden-source',
  paint: {
    'circle-color': 'rgba(255,0,0,1)', // 赤色で中心点を強調
    'circle-radius': 5, // 固定サイズの点
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const mindenCircleSource = {
  id: 'minden-circle-source',
  obj: {
    type: 'geojson',
    data: null,
  },
}
async function convertToGeoJSONforMinden() {
  // オフライン時はスキップ
  if (!navigator.onLine) {
    return
  }
  try {
    const response = await fetch('https://kenzkenz.net/geojson/ntrip/2025_minden5.geojson')
    const geojson = await response.json()
    store.state.mindenGeojson = geojson
  } catch (error) {
    console.warn('[OH3] Failed to fetch minden geojson:', error.message)
  }
}
convertToGeoJSONforMinden()

// 緯度35度での1ピクセルあたりのメートル（ズーム0）: 156543.03 * cos(35°) ≈ 128230
const METERS_PER_PIXEL_ZOOM0_LAT35 = 128230

// 円形レイヤー（circleタイプ - ポイントから直接描画、高速）
const mindenCircleLayer = {
  id: 'oh-minden-circle',
  type: 'circle',
  source: 'minden-source', // ポイントデータを直接使用
  paint: {
    'circle-color': 'rgba(0,105,180,0.3)',
    'circle-radius': [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      0, 20000 / METERS_PER_PIXEL_ZOOM0_LAT35,
      22, 20000 / METERS_PER_PIXEL_ZOOM0_LAT35 * Math.pow(2, 22)
    ],
    'circle-stroke-width': 1,
    'circle-stroke-color': 'rgba(0,105,180,0.8)',
  },
}
// mindenCircleLayerLine は不要（circleにstrokeがある）

// ntrip --------------------------------------------------------------------------------------------
const ntripSource = {
  id: 'ntrip-source',
  obj: {
    type: 'geojson',
    // 'data': 'https://kenzkenz.net/geojson/ntrip/sonohoka.geojson',
    // 'data': 'https://kenzkenz.net/geojson/ntrip/ntrip0.geojson',
  },
}
const ntripCenterPointLayer = {
  id: 'oh-ntrip-center',
  type: 'circle',
  source: 'ntrip-source',
  paint: {
    'circle-color': 'rgba(255,0,0,1)', // 赤色で中心点を強調
    'circle-radius': 5, // 固定サイズの点
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const ntripCircleSource = {
  id: 'ntrip-circle-source',
  obj: {
    type: 'geojson',
    data: null,
  },
}
async function convertAndMergeGeoJSON() {
  try {
    // 最初のデータをフェッチ
    const response1 = await fetch(
      'https://raw.githubusercontent.com/Bolero-fk/ZeniKijunkyokuChecker/main/Viewer/resource/result.json'
    )
    const data1 = await response1.json()

    // 2つ目のGeoJSONデータをフェッチ
    const response2 = await fetch('https://kenzkenz.net/geojson/ntrip/ntrip0.geojson')
    const data2 = await response2.json()

    // 最初のデータをGeoJSONに変換
    const geojson1 = {
      type: 'FeatureCollection',
      features: data1.ReferenceStationData.map((station) => ({
        type: 'Feature',
        properties: {
          id: station.id,
          city_name: station.city_name,
          station_name: station.station_name,
          geoid_height: parseFloat(station.geoid_height),
          server_address: station.server_address,
          port_number: station.port_number,
          data_type: station.data_type,
          connection_type: station.connection_type,
          status: station.status,
          mail: station.mail,
          comment: station.comment,
        },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(station.longitude), parseFloat(station.latitude)],
        },
      })),
    }
    // 2つのGeoJSONデータをマージ
    const mergedGeoJSON = {
      type: 'FeatureCollection',
      features: [...geojson1.features, ...data2.features],
    }
    ntripSource.obj.data = mergedGeoJSON
    store.state.ntripGeojson = mergedGeoJSON
  } catch (e) {
    console.warn('[NTRIP] GeoJSONの取得に失敗:', e.message)
  }
}
if (!store.state.isOffline) {
  convertAndMergeGeoJSON()
}

// 円形レイヤー（circleタイプ - ポイントから直接描画、高速）
const ntripCircleLayer = {
  id: 'oh-ntrip-circle',
  type: 'circle',
  source: 'ntrip-source', // ポイントデータを直接使用
  filter: [
    '!=',
    ['get', 'status'],
    '休止', // 'status'が'休止'ではない場合のみ表示
  ],
  paint: {
    'circle-color': 'rgba(255,105,180,0.3)',
    'circle-radius': [
      'interpolate',
      ['exponential', 2],
      ['zoom'],
      0, 20000 / METERS_PER_PIXEL_ZOOM0_LAT35,
      22, 20000 / METERS_PER_PIXEL_ZOOM0_LAT35 * Math.pow(2, 22)
    ],
    'circle-stroke-width': 1,
    'circle-stroke-color': 'rgba(255,105,180,0.8)',
  },
}
// ntripCircleLayerLine は不要（circleにstrokeがある）
const ntripCircleLayerLine = {
  id: 'oh-ntrip-circle-line',
  type: 'line',
  source: 'ntrip-circle-source',
  filter: [
    '!=',
    ['get', 'status'],
    '休止',
  ],
  paint: {
    'line-color': 'rgba(0,0,0,1)',
    'line-width': 1,
  },
}

// // 善意の基準局 --------------------------------------------------------------------------------------------
// const zeniSource = {
//     id: "zeni-source", obj: {
//         'type': 'geojson',
//         'data': null,
//     }
// }
// const zeniCenterPointLayer = {
//     id: "oh-zeni-center",
//     type: "circle",
//     source: "zeni-source",
//     paint: {
//         // 'circle-color': 'rgba(255,0,0,1)', // 赤色で中心点を強調
//         'circle-color': [
//             'match',
//             ['get', 'status'], // 'status'フィールドの値を取得
//             '公開', 'rgba(255,0,0,1)', // 公開の場合は赤色
//             '休止', 'rgba(0,0,255,1)', // 中止の場合は青色
//             'rgba(0,0,0,0)' // その他の場合は透明（黒を指定しても見えないようにする）
//         ],
//         'circle-radius': 5, // 固定サイズの点
//         'circle-opacity': 1,
//         'circle-stroke-width': 1,
//         'circle-stroke-color': '#fff'
//     }
// };
// const zeniCircleSource = {
//     id: "zeni-circle-source", obj: {
//         'type': 'geojson',
//         'data': null,
//     }
// };
// // 円形レイヤー
// const zeniCircleLayer = {
//     id: 'oh-zeni-circle',
//     type: 'fill',
//     source: 'zeni-circle-source',
//     layout: {},
//     filter: [
//         '!=', ['get', 'status'], '休止' // 'status'が'休止'ではない場合のみ表示
//     ],
//     paint: {
//         'fill-color': 'rgba(0,255,0,0.3)',
//         'fill-outline-color': '#000'
//     }
// };
// const zeniCircleLayerLine = {
//     id: 'oh-zeni-circle-line',
//     type: 'line',
//     source: 'zeni-circle-source',
//     filter: [
//         '!=', ['get', 'status'], '休止' // 'status'が'休止'ではない場合のみ表示
//     ],
//     paint: {
//         'line-color': 'rgba(0,0,0,1)',
//         'line-width': 1
//     }
// };
//
// // eslint-disable-next-line no-unexpected-multiline
// async function convertToGeoJSON() {
//     try {
//         // データをフェッチ
//         const response = await fetch('https://raw.githubusercontent.com/Bolero-fk/ZeniKijunkyokuChecker/main/Viewer/resource/result.json');
//         const data = await response.json();
//
//         // GeoJSONフォーマットに変換
//         const geojson = {
//             type: "FeatureCollection",
//             features: data.ReferenceStationData.map(station => ({
//                 type: "Feature",
//                 properties: {
//                     id: station.id,
//                     city_name: station.city_name,
//                     station_name: station.station_name,
//                     geoid_height: parseFloat(station.geoid_height),
//                     server_address: station.server_address,
//                     port_number: station.port_number,
//                     data_type: station.data_type,
//                     connection_type: station.connection_type,
//                     status: station.status,
//                     mail: station.mail,
//                     comment: station.comment
//                 },
//                 geometry: {
//                     type: "Point",
//                     coordinates: [
//                         parseFloat(station.longitude),
//                         parseFloat(station.latitude)
//                     ]
//                 }
//             }))
//         };
//
//         const radiusInKm = 20;
//         // 各ポイントごとに円を生成し、プロパティを引き継ぐ
//         const circleGeoJSON = {
//             type: "FeatureCollection",
//             features: geojson.features.map(feature => {
//                 const circle = turf.circle(feature.geometry.coordinates, radiusInKm, {
//                     steps: 64, // 円の滑らかさ
//                     units: 'kilometers'
//                 });
//                 return {
//                     type: "Feature",
//                     properties: feature.properties, // プロパティを引き継ぐ
//                     geometry: circle.geometry
//                 };
//             })
//         };
//         zeniCircleSource.obj.data = circleGeoJSON
//         zeniSource.obj.data = geojson;
//         store.state.zeniGeojson = geojson
//         return { geojson, circleGeoJSON };
//     } catch (error) {
//         console.error('Error converting to GeoJSON:', error);
//     }
// }
// convertToGeoJSON()

// import std from '@/assets/json/modified_std.json'
import fxBasic from '@/assets/json/modified_fx_basic.json'
import mono from '@/assets/json/modified_mono.json'
import fxDark from '@/assets/json/modified_fx-dark.json'
import maniawaseLight from '@/assets/json/gsi-style-maniawase.json'
import maniawaseDark from '@/assets/json/gsi-style-maniawase-dark.json'
import bvmapBasic from '@/assets/json/basic.json'
import osmBright from '@/assets/json/osm_bright.json'
import osmBrightLabelOnly from '@/assets/json/osm_bright_label_only.json'
import osmToner from '@/assets/json/osm_toner.json'
import osm3d from '@/assets/json/osmfj_nopoi.json'
import axios from 'axios'
import { convertGsiTileJson, fetchGsiTile, fetchTochibunruiLayers, convertTochibunruiJson, findLayerById } from '@/js/downLoad'
import { cs } from 'vuetify/lib/locale'
import maskDep from '@/js/mask-dep.json'
import { layers as protomapsLayers, namedTheme } from 'protomaps-themes-base'
//-------------------------------------------------------
// Protomaps ベクタータイル
const PROTOMAPS_SOURCE_ID = 'protomaps'
const protomapsSource = {
  id: PROTOMAPS_SOURCE_ID,
  obj: {
    type: 'vector',
    tiles: ['https://api.protomaps.com/tiles/v4/{z}/{x}/{y}.mvt?key=441e47a6dde8dc7b'],
    maxzoom: 15,
    attribution: '<a href="https://osm.org/copyright">© OpenStreetMap</a>',
  },
}
export const protomapsSources = [protomapsSource]
// テーマごとにレイヤー生成（すべて日本語ラベル）
function makeProtomapsLayers(themeName) {
  return protomapsLayers(PROTOMAPS_SOURCE_ID, namedTheme(themeName), { lang: 'ja' })
    .map((layer) => ({
      ...layer,
      id: `oh-protomaps-${themeName}-${layer.id}`,
      metadata: { group: `protomaps-${themeName}` },
    }))
}
export const protomapsLightLayers = makeProtomapsLayers('light')
export const protomapsDarkLayers = makeProtomapsLayers('dark')
export const protomapsGrayscaleLayers = makeProtomapsLayers('grayscale')
export const protomapsBlackLayers = makeProtomapsLayers('black')
export const protomapsWhiteLayers = makeProtomapsLayers('white')
//-------------------------------------------------------
export const osm3dSources = []
export const osm3dLayers = osm3d.layers.map((layer) => {
  return {
    ...layer, // 既存のレイヤー設定を維持
    metadata: {
      group: 'osm-3d', // 新しいmetadataプロパティを追加
    },
  }
})
Object.keys(osm3d.sources).forEach(function (key) {
  osm3dSources.push({
    id: key,
    obj: osm3d.sources[key],
  })
})
//----------------------------------------------------------
const osmTonerSources = []
const osmTonerLayers = osmToner.layers.map((layer) => {
  return {
    ...layer, // 既存のレイヤー設定を維持
    metadata: {
      group: 'osm-toner', // 新しいmetadataプロパティを追加
    },
  }
})
Object.keys(osmToner.sources).forEach(function (key) {
  osmTonerSources.push({
    id: key,
    obj: osmToner.sources[key],
  })
})
//-------------------------------------------------------
export const osmBrightSources = []
export const osmBrightLayers = osmBright.layers.map((layer) => {
  return {
    ...layer, // 既存のレイヤー設定を維持
    metadata: {
      group: 'osm-bright', // 新しいmetadataプロパティを追加
    },
  }
})
Object.keys(osmBright.sources).forEach(function (key) {
  osmBrightSources.push({
    id: key,
    obj: osmBright.sources[key],
  })
})
//-------------------------------------------------------
export const osmBrightLabelOnlySources = []
export const osmBrightLabelOnlyLayers = osmBrightLabelOnly.layers.map((layer) => {
  return {
    ...layer, // 既存のレイヤー設定を維持
    metadata: {
      group: 'osm-bright-label-only', // 新しいmetadataプロパティを追加
    },
  }
})
Object.keys(osmBrightLabelOnly.sources).forEach(function (key) {
  osmBrightLabelOnlySources.push({
    id: key,
    obj: osmBrightLabelOnly.sources[key],
  })
})
//----------------------------------------------------------
const overpassSource = {
  id: 'osm-overpass-source',
  obj: {
    type: 'geojson',
    data: null,
  },
}
const overpassPoint = {
  id: 'oh-osm-overpass-layer-point',
  type: 'circle',
  source: 'osm-overpass-source',
  filter: ['==', '$type', 'Point'],
  paint: {
    'circle-radius': 8,
    'circle-color': 'green',
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
}
const overpassPolygon = {
  id: 'oh-osm-overpass-layer-polygon',
  type: 'fill',
  source: 'osm-overpass-source',
  filter: ['==', '$type', 'Polygon'],
  paint: {
    'fill-color': 'rgba(0,0,255,0.5)',
  },
}
const overpassPolygonLine = {
  id: 'oh-osm-overpass-layer-polygon-line',
  type: 'line',
  source: 'osm-overpass-source',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      1,
      12,
      1.5,
    ],
  },
}
const overpassLine = {
  id: 'oh-osm-overpass-layer-line',
  type: 'line',
  source: 'osm-overpass-source',
  filter: ['==', '$type', 'LineString'],
  paint: {
    'line-color': 'blue',
    'line-width': [
      'case',
      ['boolean', ['get', 'name'], false], // name フィールドが存在する場合 効いていない
      4, // 倍の太さ
      2, // 通常の太さ
    ],
  },
}

// const stdSources = []
// const stdLayers = std.layers
// Object.keys(std.sources).forEach(function(key) {
//     stdSources.push({
//         id: key,
//         obj: std.sources[key]
//     })
// })
// ---------------------------------------------------------------------
export const monoSources = []
export const monoLayers = mono.layers
Object.keys(mono.sources).forEach(function (key) {
  monoSources.push({
    id: key,
    obj: mono.sources[key],
  })
})
// ---------------------------------------------------------------------
// 最適化ベクトルタイル (PMTiles)
const BVMAP_PMTILES_URL = 'pmtiles://https://cyberjapandata.gsi.go.jp/xyz/optimal_bvmap-v1/optimal_bvmap-v1.pmtiles'
const BVMAP_SOURCE_ID = 'gsi-optimal-bvmap'

export const bvmapSources = [{
  id: BVMAP_SOURCE_ID,
  obj: {
    type: 'vector',
    url: BVMAP_PMTILES_URL
  }
}]

// レイヤーのsourceをPMTilesソースに変更し、idにプレフィックスを追加
export const bvmapLayers = bvmapBasic.layers.map((layer) => {
  // backgroundレイヤーはsourceなし
  if (layer.type === 'background') {
    return {
      ...layer,
      id: `oh-bvmap-${layer.id}`,
      metadata: {
        group: 'gsi-bvmap',
      },
    }
  }
  return {
    ...layer,
    id: `oh-bvmap-${layer.id}`,
    source: BVMAP_SOURCE_ID,
    metadata: {
      group: 'gsi-bvmap',
    },
  }
})
// ---------------------------------------------------------------------
const fxDarkSources = []
const fxDarkLayers = fxDark.layers
Object.keys(fxDark.sources).forEach(function (key) {
  fxDarkSources.push({
    id: key,
    obj: fxDark.sources[key],
  })
})
// ---------------------------------------------------------------------
const fxBasicSources = []
const fxBasicLayers = fxBasic.layers
Object.keys(fxBasic.sources).forEach(function (key) {
  fxBasicSources.push({
    id: key,
    obj: fxBasic.sources[key],
  })
})
// ---------------------------------------------------------------------
// gsi-style-maniawase（軽量ベクトルタイル）
// レイヤーIDに oh- プレフィックスを付与（ベースマップ切替時の削除ロジックに必要）
const MANIAWASE_SOURCE_ID = 'gsibv-vectortile-source-1-4-16'
const maniawaseLightSources = [{
  id: MANIAWASE_SOURCE_ID,
  obj: maniawaseLight.sources[MANIAWASE_SOURCE_ID],
}]
const maniawaseLightLayers = maniawaseLight.layers.map((layer) => {
  if (layer.type === 'background') {
    return {
      ...layer,
      id: `oh-maniawase-light-${layer.id}`,
      metadata: { group: 'maniawase-light' },
    }
  }
  return {
    ...layer,
    id: `oh-maniawase-light-${layer.id}`,
    source: MANIAWASE_SOURCE_ID,
    metadata: { group: 'maniawase-light' },
  }
})

const maniawaseDarkSources = [{
  id: MANIAWASE_SOURCE_ID,
  obj: maniawaseDark.sources[MANIAWASE_SOURCE_ID],
}]
const maniawaseDarkLayers = maniawaseDark.layers.map((layer) => {
  if (layer.type === 'background') {
    return {
      ...layer,
      id: `oh-maniawase-dark-${layer.id}`,
      metadata: { group: 'maniawase-dark' },
    }
  }
  return {
    ...layer,
    id: `oh-maniawase-dark-${layer.id}`,
    source: MANIAWASE_SOURCE_ID,
    metadata: { group: 'maniawase-dark' },
  }
})

// カスタム版（初期値はLightのコピー、実行時にext-maniawase-customで変更）
const maniawaseCustomSources = [{
  id: MANIAWASE_SOURCE_ID,
  obj: maniawaseLight.sources[MANIAWASE_SOURCE_ID],
}]
const maniawaseCustomLayers = maniawaseLight.layers.map((layer) => {
  if (layer.type === 'background') {
    return {
      ...layer,
      id: `oh-maniawase-custom-${layer.id}`,
      metadata: { group: 'maniawase-custom' },
    }
  }
  return {
    ...layer,
    id: `oh-maniawase-custom-${layer.id}`,
    source: MANIAWASE_SOURCE_ID,
    metadata: { group: 'maniawase-custom' },
  }
})
// ---------------------------------------------------------------------

export let konUrls = [
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1896-1909年', timeFolder: '2man' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1917-1924年', timeFolder: '00' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1927-1939年', timeFolder: '01' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1944-1954年', timeFolder: '02' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1965-1968年', timeFolder: '03' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1975-1978年', timeFolder: '04' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1983-1987年', timeFolder: '05' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1992-1995年', timeFolder: '06' },
  { name: '首都圏', datasetFolder: 'tokyo50', time: '1998-2005年', timeFolder: '07' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1888-1898年', timeFolder: '2man' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1920年', timeFolder: '00' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1932年', timeFolder: '01' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1937-1938年', timeFolder: '02' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1947年', timeFolder: '03' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1959-1960年', timeFolder: '04' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1968-1973年', timeFolder: '05' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1976-1980年', timeFolder: '06' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1984-1989年', timeFolder: '07' },
  { name: '中京圏', datasetFolder: 'chukyo', time: '1992-1996年', timeFolder: '08' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1892-1910年', timeFolder: '2man' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1922-1923年', timeFolder: '00' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1927-1935年', timeFolder: '01' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1947-1950年', timeFolder: '02' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1954-1956年', timeFolder: '03' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1961-1964年', timeFolder: '03x' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1967-1970年', timeFolder: '04' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1975-1979年', timeFolder: '05' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1983-1988年', timeFolder: '06' },
  { name: '京阪神圏', datasetFolder: 'keihansin', time: '1993-1997年', timeFolder: '07' },
  { name: '札幌', datasetFolder: 'sapporo', time: '1916年', timeFolder: '00' },
  { name: '札幌', datasetFolder: 'sapporo', time: '1935年', timeFolder: '01' },
  { name: '札幌', datasetFolder: 'sapporo', time: '1950-1952年', timeFolder: '02' },
  { name: '札幌', datasetFolder: 'sapporo', time: '1975-1976年', timeFolder: '03' },
  { name: '札幌', datasetFolder: 'sapporo', time: '1995-1998年', timeFolder: '04' },
  { name: '仙台', datasetFolder: 'sendai', time: '1928-1933年', timeFolder: '00' },
  { name: '仙台', datasetFolder: 'sendai', time: '1946年', timeFolder: '01' },
  { name: '仙台', datasetFolder: 'sendai', time: '1963-1967年', timeFolder: '02' },
  { name: '仙台', datasetFolder: 'sendai', time: '1977-1978年', timeFolder: '03' },
  { name: '仙台', datasetFolder: 'sendai', time: '1995-2000年', timeFolder: '04' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1894-1899年', timeFolder: '2man' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1925-1932年', timeFolder: '00' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1950-1954年', timeFolder: '01' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1967-1969年', timeFolder: '02' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1984-1990年', timeFolder: '03' },
  { name: '広島', datasetFolder: 'hiroshima', time: '1992-2001年', timeFolder: '04' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1922-1926年', timeFolder: '00' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1936-1938年', timeFolder: '01' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1948-1956年', timeFolder: '02' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1967-1972年', timeFolder: '03' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1982-1986年', timeFolder: '04' },
  { name: '福岡・北九州', datasetFolder: 'fukuoka', time: '1991-2000年', timeFolder: '05' },
  { name: '東北地方太平洋岸', datasetFolder: 'tohoku_pacific_coast', time: '1901-1913年', timeFolder: '00' },
  { name: '東北地方太平洋岸', datasetFolder: 'tohoku_pacific_coast', time: '1949-1953年', timeFolder: '01' },
  { name: '東北地方太平洋岸', datasetFolder: 'tohoku_pacific_coast', time: '1969-1982年', timeFolder: '02' },
  { name: '東北地方太平洋岸', datasetFolder: 'tohoku_pacific_coast', time: '1990-2008年', timeFolder: '03' },
  { name: '関東', datasetFolder: 'kanto', time: '1894-1915年', timeFolder: '00' },
  { name: '関東', datasetFolder: 'kanto', time: '1928-1945年', timeFolder: '01' },
  { name: '関東', datasetFolder: 'kanto', time: '1972-1982年', timeFolder: '02' },
  { name: '関東', datasetFolder: 'kanto', time: '1988-2008年', timeFolder: '03' },
  { name: '沖縄本島南部', datasetFolder: 'okinawas', time: '1919年', timeFolder: '00' },
  { name: '沖縄本島南部', datasetFolder: 'okinawas', time: '1973-1975年', timeFolder: '01' },
  { name: '沖縄本島南部', datasetFolder: 'okinawas', time: '1992-1994年', timeFolder: '02' },
  { name: '沖縄本島南部', datasetFolder: 'okinawas', time: '2005-2008年', timeFolder: '03' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1889-1890年', timeFolder: '2man' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1916-1918年', timeFolder: '00' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1938-1950年', timeFolder: '01' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1956-1959年', timeFolder: '02' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1975-1988年', timeFolder: '03' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1988-1995年', timeFolder: '04' },
  { name: '浜松・豊橋', datasetFolder: 'hamamatsu', time: '1996-2010年', timeFolder: '05' },
  { name: '熊本', datasetFolder: 'kumamoto', time: '1900-1901年', timeFolder: '2man' },
  { name: '熊本', datasetFolder: 'kumamoto', time: '1926年', timeFolder: '00' },
  { name: '熊本', datasetFolder: 'kumamoto', time: '1965-1971年', timeFolder: '01' },
  { name: '熊本', datasetFolder: 'kumamoto', time: '1983年', timeFolder: '02' },
  { name: '熊本', datasetFolder: 'kumamoto', time: '1998-2000年', timeFolder: '03' },
  { name: '新潟', datasetFolder: 'niigata', time: '1910-1911年', timeFolder: '00' },
  { name: '新潟', datasetFolder: 'niigata', time: '1930-1931年', timeFolder: '01' },
  { name: '新潟', datasetFolder: 'niigata', time: '1966-1968年', timeFolder: '02' },
  { name: '新潟', datasetFolder: 'niigata', time: '1980-1988年', timeFolder: '03' },
  { name: '新潟', datasetFolder: 'niigata', time: '1997-2001年', timeFolder: '04' },
  { name: '姫路', datasetFolder: 'himeji', time: '1903-1910年', timeFolder: '2man' },
  { name: '姫路', datasetFolder: 'himeji', time: '1923年', timeFolder: '00' },
  { name: '姫路', datasetFolder: 'himeji', time: '1967年', timeFolder: '01' },
  { name: '姫路', datasetFolder: 'himeji', time: '1981-1985年', timeFolder: '02' },
  { name: '姫路', datasetFolder: 'himeji', time: '1997-2001年', timeFolder: '03' },
  { name: '岡山・福山', datasetFolder: 'okayama', time: '1895-1898年', timeFolder: '2man' },
  { name: '岡山・福山', datasetFolder: 'okayama', time: '1925年', timeFolder: '00' },
  { name: '岡山・福山', datasetFolder: 'okayama', time: '1965-1970年', timeFolder: '01' },
  { name: '岡山・福山', datasetFolder: 'okayama', time: '1978-1988年', timeFolder: '02' },
  { name: '岡山・福山', datasetFolder: 'okayama', time: '1990-2000年', timeFolder: '03' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1902年', timeFolder: '5man' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1902年', timeFolder: '2man' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1932年', timeFolder: '00' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1966年', timeFolder: '01' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1982-1983年', timeFolder: '02' },
  { name: '鹿児島', datasetFolder: 'kagoshima', time: '1996-2001年', timeFolder: '03' },
  { name: '松山', datasetFolder: 'matsuyama', time: '1903年', timeFolder: '2man' },
  { name: '松山', datasetFolder: 'matsuyama', time: '1928-1955年', timeFolder: '00' },
  { name: '松山', datasetFolder: 'matsuyama', time: '1968年', timeFolder: '01' },
  { name: '松山', datasetFolder: 'matsuyama', time: '1985年', timeFolder: '02' },
  { name: '松山', datasetFolder: 'matsuyama', time: '1998-1999年', timeFolder: '03' },
  { name: '大分', datasetFolder: 'oita', time: '1914年', timeFolder: '00' },
  { name: '大分', datasetFolder: 'oita', time: '1973年', timeFolder: '01' },
  { name: '大分', datasetFolder: 'oita', time: '1984-1986年', timeFolder: '02' },
  { name: '大分', datasetFolder: 'oita', time: '1997-2001年', timeFolder: '03' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1900-1901年', timeFolder: '2man' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1924-1926年', timeFolder: '00' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1954年', timeFolder: '01' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1970年', timeFolder: '02' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1982-1983年', timeFolder: '03' },
  { name: '長崎', datasetFolder: 'nagasaki', time: '1996-2000年', timeFolder: '03' },
  { name: '金沢・富山', datasetFolder: 'kanazawa', time: '1909-1910年', timeFolder: '2man' },
  { name: '金沢・富山', datasetFolder: 'kanazawa', time: '1930年', timeFolder: '00' },
  { name: '金沢・富山', datasetFolder: 'kanazawa', time: '1968-1969年', timeFolder: '01' },
  { name: '金沢・富山', datasetFolder: 'kanazawa', time: '1981-1985年', timeFolder: '02' },
  { name: '金沢・富山', datasetFolder: 'kanazawa', time: '1994-2001年', timeFolder: '03' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1908-1912年', timeFolder: '2man' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1934年', timeFolder: '00' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1947年', timeFolder: '01' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1966-1967年', timeFolder: '02' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1984-1985年', timeFolder: '03' },
  { name: '和歌山', datasetFolder: 'wakayama', time: '1998-2000年', timeFolder: '04' },
  { name: '青森', datasetFolder: 'aomori', time: '1912年', timeFolder: '00' },
  { name: '青森', datasetFolder: 'aomori', time: '1939-1955年', timeFolder: '01' },
  { name: '青森', datasetFolder: 'aomori', time: '1970年', timeFolder: '02' },
  { name: '青森', datasetFolder: 'aomori', time: '1984-1989年', timeFolder: '03' },
  { name: '青森', datasetFolder: 'aomori', time: '2003-2011年', timeFolder: '04' },
  { name: '高松', datasetFolder: 'takamatsu', time: '1896-1910年', timeFolder: '2man' },
  { name: '高松', datasetFolder: 'takamatsu', time: '1928年', timeFolder: '00' },
  { name: '高松', datasetFolder: 'takamatsu', time: '1969年', timeFolder: '01' },
  { name: '高松', datasetFolder: 'takamatsu', time: '1983-1984年', timeFolder: '02' },
  { name: '高松', datasetFolder: 'takamatsu', time: '1990-2000年', timeFolder: '03' },
  { name: '長野', datasetFolder: 'nagano', time: '1912年', timeFolder: '00' },
  { name: '長野', datasetFolder: 'nagano', time: '1937年', timeFolder: '01' },
  { name: '長野', datasetFolder: 'nagano', time: '1960年', timeFolder: '02' },
  { name: '長野', datasetFolder: 'nagano', time: '1972-1973年', timeFolder: '03' },
  { name: '長野', datasetFolder: 'nagano', time: '1985年', timeFolder: '04' },
  { name: '長野', datasetFolder: 'nagano', time: '2001年', timeFolder: '05' },
  { name: '福島', datasetFolder: 'fukushima', time: '1908年', timeFolder: '00' },
  { name: '福島', datasetFolder: 'fukushima', time: '1931年', timeFolder: '01' },
  { name: '福島', datasetFolder: 'fukushima', time: '1972-1973年', timeFolder: '02' },
  { name: '福島', datasetFolder: 'fukushima', time: '1983年', timeFolder: '03' },
  { name: '福島', datasetFolder: 'fukushima', time: '1996-2000年', timeFolder: '04' },
  { name: '福井', datasetFolder: 'fukui', time: '1909年', timeFolder: '2man' },
  { name: '福井', datasetFolder: 'fukui', time: '1930年', timeFolder: '00' },
  { name: '福井', datasetFolder: 'fukui', time: '1969-1973年', timeFolder: '01' },
  { name: '福井', datasetFolder: 'fukui', time: '1988-1990年', timeFolder: '02' },
  { name: '福井', datasetFolder: 'fukui', time: '1996-2000年', timeFolder: '03' },
  { name: '秋田', datasetFolder: 'akita', time: '1912年', timeFolder: '00' },
  { name: '秋田', datasetFolder: 'akita', time: '1971-1972年', timeFolder: '01' },
  { name: '秋田', datasetFolder: 'akita', time: '1985-1990年', timeFolder: '02' },
  { name: '秋田', datasetFolder: 'akita', time: '2006-2007年', timeFolder: '03' },
  { name: '盛岡', datasetFolder: 'morioka', time: '1811-1912年', timeFolder: '00' },
  { name: '盛岡', datasetFolder: 'morioka', time: '1939年', timeFolder: '01' },
  { name: '盛岡', datasetFolder: 'morioka', time: '1968-1969年', timeFolder: '02' },
  { name: '盛岡', datasetFolder: 'morioka', time: '1983-1988年', timeFolder: '03' },
  { name: '盛岡', datasetFolder: 'morioka', time: '1999-2002年', timeFolder: '04' },
  { name: '鳥取', datasetFolder: 'tottori', time: '1897年', timeFolder: '2man' },
  { name: '鳥取', datasetFolder: 'tottori', time: '1932年', timeFolder: '00' },
  { name: '鳥取', datasetFolder: 'tottori', time: '1973年', timeFolder: '01' },
  { name: '鳥取', datasetFolder: 'tottori', time: '1988年', timeFolder: '02' },
  { name: '鳥取', datasetFolder: 'tottori', time: '1999-2001年', timeFolder: '03' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1896-1909年', timeFolder: '2man' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1917年', timeFolder: '00' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1928-1934年', timeFolder: '01' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1969-1970年', timeFolder: '02' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1981-1987年', timeFolder: '03' },
  { name: '徳島', datasetFolder: 'tokushima', time: '1997-2000年', timeFolder: '04' },
  { name: '高知', datasetFolder: 'kochi', time: '1906-1907年', timeFolder: '2man' },
  { name: '高知', datasetFolder: 'kochi', time: '1933年', timeFolder: '00' },
  { name: '高知', datasetFolder: 'kochi', time: '1965年', timeFolder: '01' },
  { name: '高知', datasetFolder: 'kochi', time: '1982年', timeFolder: '02' },
  { name: '高知', datasetFolder: 'kochi', time: '1998-2003年', timeFolder: '03' },
  { name: '宮崎', datasetFolder: 'miyazaki', time: '1902年', timeFolder: '00' },
  { name: '宮崎', datasetFolder: 'miyazaki', time: '1935年', timeFolder: '01' },
  { name: '宮崎', datasetFolder: 'miyazaki', time: '1962年', timeFolder: '02' },
  { name: '宮崎', datasetFolder: 'miyazaki', time: '1979年', timeFolder: '03' },
  { name: '宮崎', datasetFolder: 'miyazaki', time: '1999-2001年', timeFolder: '04' },
  { name: '山形', datasetFolder: 'yamagata', time: '1901-1903年', timeFolder: '2man' },
  { name: '山形', datasetFolder: 'yamagata', time: '1931年', timeFolder: '00' },
  { name: '山形', datasetFolder: 'yamagata', time: '1970年', timeFolder: '01' },
  { name: '山形', datasetFolder: 'yamagata', time: '1980-1989年', timeFolder: '02' },
  { name: '山形', datasetFolder: 'yamagata', time: '1999-2001年', timeFolder: '03' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1900-1911年', timeFolder: '2man' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1914-1926年', timeFolder: '00' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1931-1940年', timeFolder: '01' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1958-1964年', timeFolder: '02' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1977-1982年', timeFolder: '03' },
  { name: '佐賀・久留米', datasetFolder: 'saga', time: '1998-2001年', timeFolder: '04' },
  { name: '松江・米子', datasetFolder: 'matsue', time: '1915年', timeFolder: '00' },
  { name: '松江・米子', datasetFolder: 'matsue', time: '1934年', timeFolder: '01' },
  { name: '松江・米子', datasetFolder: 'matsue', time: '1975年', timeFolder: '02' },
  { name: '松江・米子', datasetFolder: 'matsue', time: '1989-1990年', timeFolder: '03' },
  { name: '松江・米子', datasetFolder: 'matsue', time: '1997-2003年', timeFolder: '04' },
  { name: '津', datasetFolder: 'tsu', time: '1892-1898年', timeFolder: '2man' },
  { name: '津', datasetFolder: 'tsu', time: '1920年', timeFolder: '00' },
  { name: '津', datasetFolder: 'tsu', time: '1937年', timeFolder: '01' },
  { name: '津', datasetFolder: 'tsu', time: '1959年', timeFolder: '02' },
  { name: '津', datasetFolder: 'tsu', time: '1980-1982年', timeFolder: '03' },
  { name: '津', datasetFolder: 'tsu', time: '1991-1999年', timeFolder: '04' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '1897-1909年', timeFolder: '2man' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '1922-1927年', timeFolder: '00' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '1936-1951年', timeFolder: '01' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '1969年', timeFolder: '02' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '1983-1989年', timeFolder: '03' },
  { name: '山口', datasetFolder: 'yamaguchi', time: '2000-2001年', timeFolder: '04' },
  { name: '旭川', datasetFolder: 'asahikawa', time: '1916-1917年', timeFolder: '00' },
  { name: '旭川', datasetFolder: 'asahikawa', time: '1950-1952年', timeFolder: '01' },
  { name: '旭川', datasetFolder: 'asahikawa', time: '1972-1974年', timeFolder: '02' },
  { name: '旭川', datasetFolder: 'asahikawa', time: '1986年', timeFolder: '03' },
  { name: '旭川', datasetFolder: 'asahikawa', time: '1999-2001年', timeFolder: '04' },
  { name: '函館', datasetFolder: 'hakodate', time: '19159年', timeFolder: '00' },
  { name: '函館', datasetFolder: 'hakodate', time: '1951-1955年', timeFolder: '01' },
  { name: '函館', datasetFolder: 'hakodate', time: '1968年', timeFolder: '02' },
  { name: '函館', datasetFolder: 'hakodate', time: '1986-1989年', timeFolder: '03' },
  { name: '函館', datasetFolder: 'hakodate', time: '1996-2001年', timeFolder: '04' },
  { name: '松本', datasetFolder: 'matsumoto', time: '1910年', timeFolder: '00' },
  { name: '松本', datasetFolder: 'matsumoto', time: '1931年', timeFolder: '01' },
  { name: '松本', datasetFolder: 'matsumoto', time: '1974-1975年', timeFolder: '02' },
  { name: '松本', datasetFolder: 'matsumoto', time: '1987-1992年', timeFolder: '03' },
  { name: '松本', datasetFolder: 'matsumoto', time: '1996-2001年', timeFolder: '04' },
  { name: '佐世保', datasetFolder: 'sasebo', time: '1900-1901年', timeFolder: '2man' },
  { name: '佐世保', datasetFolder: 'sasebo', time: '1924年', timeFolder: '00' },
  { name: '佐世保', datasetFolder: 'sasebo', time: '1971年', timeFolder: '01' },
  { name: '佐世保', datasetFolder: 'sasebo', time: '1985-1987年', timeFolder: '02' },
  { name: '佐世保', datasetFolder: 'sasebo', time: '1997-1998年', timeFolder: '03' },
  { name: '弘前', datasetFolder: 'hirosaki', time: '1912年', timeFolder: '00' },
  { name: '弘前', datasetFolder: 'hirosaki', time: '1939年', timeFolder: '01' },
  { name: '弘前', datasetFolder: 'hirosaki', time: '1970-1971年', timeFolder: '02' },
  { name: '弘前', datasetFolder: 'hirosaki', time: '1980-1986年', timeFolder: '03' },
  { name: '弘前', datasetFolder: 'hirosaki', time: '1994-1997年', timeFolder: '04' },
  { name: '会津', datasetFolder: 'aizu', time: '1908-1910年', timeFolder: '00' },
  { name: '会津', datasetFolder: 'aizu', time: '1931年', timeFolder: '01' },
  { name: '会津', datasetFolder: 'aizu', time: '1972-1975年', timeFolder: '02' },
  { name: '会津', datasetFolder: 'aizu', time: '1988-1991年', timeFolder: '03' },
  { name: '会津', datasetFolder: 'aizu', time: '1997-2000年', timeFolder: '04' },
  { name: '釧路', datasetFolder: 'kushiro', time: '1897年', timeFolder: '00' },
  { name: '釧路', datasetFolder: 'kushiro', time: '1922年', timeFolder: '01' },
  { name: '釧路', datasetFolder: 'kushiro', time: '1958年', timeFolder: '02' },
  { name: '釧路', datasetFolder: 'kushiro', time: '1981年', timeFolder: '03' },
  { name: '釧路', datasetFolder: 'kushiro', time: '2001年', timeFolder: '04' },
  { name: '苫小牧', datasetFolder: 'tomakomai', time: '1896年', timeFolder: '00' },
  { name: '苫小牧', datasetFolder: 'tomakomai', time: '1935年', timeFolder: '01' },
  { name: '苫小牧', datasetFolder: 'tomakomai', time: '1954-1955年', timeFolder: '02' },
  { name: '苫小牧', datasetFolder: 'tomakomai', time: '1983-1984年', timeFolder: '03' },
  { name: '苫小牧', datasetFolder: 'tomakomai', time: '1993-999年', timeFolder: '04' },
  { name: '帯広', datasetFolder: 'obihiro', time: '1896年', timeFolder: '00' },
  { name: '帯広', datasetFolder: 'obihiro', time: '1930年', timeFolder: '01' },
  { name: '帯広', datasetFolder: 'obihiro', time: '1956-1957年', timeFolder: '02' },
  { name: '帯広', datasetFolder: 'obihiro', time: '1985年', timeFolder: '03' },
  { name: '帯広', datasetFolder: 'obihiro', time: '1998-2000年', timeFolder: '04' },
  { name: '都城', datasetFolder: 'miyakonojyou', time: '1902年', timeFolder: '00' },
  { name: '都城', datasetFolder: 'miyakonojyou', time: '1932年', timeFolder: '01' },
  { name: '都城', datasetFolder: 'miyakonojyou', time: '1966年', timeFolder: '02' },
  { name: '都城', datasetFolder: 'miyakonojyou', time: '1979-1980年', timeFolder: '03' },
  { name: '都城', datasetFolder: 'miyakonojyou', time: '1998-2001年', timeFolder: '04' },
  { name: '東予', datasetFolder: 'toyo', time: '1898-1906年', timeFolder: '00' },
  { name: '東予', datasetFolder: 'toyo', time: '1928年', timeFolder: '01' },
  { name: '東予', datasetFolder: 'toyo', time: '1966-1969年', timeFolder: '02' },
  { name: '東予', datasetFolder: 'toyo', time: '1984-1989年', timeFolder: '03' },
  { name: '東予', datasetFolder: 'toyo', time: '1994-2001年', timeFolder: '04' },
  { name: '庄内', datasetFolder: 'syonai', time: '1913年', timeFolder: '00' },
  { name: '庄内', datasetFolder: 'syonai', time: '1934年', timeFolder: '01' },
  { name: '庄内', datasetFolder: 'syonai', time: '1974年', timeFolder: '02' },
  { name: '庄内', datasetFolder: 'syonai', time: '1987年', timeFolder: '03' },
  { name: '庄内', datasetFolder: 'syonai', time: '1997-2001年', timeFolder: '04' },
  { name: '室蘭', datasetFolder: 'muroran', time: '1896年', timeFolder: '00' },
  { name: '室蘭', datasetFolder: 'muroran', time: '1917年', timeFolder: '01' },
  { name: '室蘭', datasetFolder: 'muroran', time: '1955年', timeFolder: '02' },
  { name: '室蘭', datasetFolder: 'muroran', time: '1986-1987年', timeFolder: '03' },
  { name: '室蘭', datasetFolder: 'muroran', time: '1998-2000年', timeFolder: '04' },
  { name: '近江', datasetFolder: 'omi', time: '1891-1909年', timeFolder: '2man' },
  { name: '近江', datasetFolder: 'omi', time: '1920-1922年', timeFolder: '00' },
  { name: '近江', datasetFolder: 'omi', time: '1954年', timeFolder: '01' },
  { name: '近江', datasetFolder: 'omi', time: '1967-1971年', timeFolder: '02' },
  { name: '近江', datasetFolder: 'omi', time: '1979-1986年', timeFolder: '03' },
  { name: '近江', datasetFolder: 'omi', time: '1992-1999年', timeFolder: '04' },
  { name: '岩手県南', datasetFolder: 'iwatekennan', time: '1913年', timeFolder: '00' },
  { name: '岩手県南', datasetFolder: 'iwatekennan', time: '1951年', timeFolder: '01' },
  { name: '岩手県南', datasetFolder: 'iwatekennan', time: '1968年', timeFolder: '02' },
  { name: '岩手県南', datasetFolder: 'iwatekennan', time: '1985-1986年', timeFolder: '03' },
  { name: '岩手県南', datasetFolder: 'iwatekennan', time: '1996-2001年', timeFolder: '04' },
  { name: '延岡', datasetFolder: 'nobeoka', time: '1901年', timeFolder: '00' },
  { name: '延岡', datasetFolder: 'nobeoka', time: '1932-1942年', timeFolder: '01' },
  { name: '延岡', datasetFolder: 'nobeoka', time: '1965年', timeFolder: '02' },
  { name: '延岡', datasetFolder: 'nobeoka', time: '1978年', timeFolder: '03' },
  { name: '延岡', datasetFolder: 'nobeoka', time: '1999-2000年', timeFolder: '04' },
  { name: '八代', datasetFolder: 'yatsushiro', time: '1913年', timeFolder: '00' },
  { name: '八代', datasetFolder: 'yatsushiro', time: '1951年', timeFolder: '01' },
  { name: '八代', datasetFolder: 'yatsushiro', time: '1968年', timeFolder: '02' },
  { name: '八代', datasetFolder: 'yatsushiro', time: '1983-1986年', timeFolder: '03' },
  { name: '八代', datasetFolder: 'yatsushiro', time: '1997-2000年', timeFolder: '04' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1910年', timeFolder: '00' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1941-1942年', timeFolder: '01' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1970年', timeFolder: '02' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1983-1987年', timeFolder: '03' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1993-1994年', timeFolder: '04' },
  { name: '大牟田・島原', datasetFolder: 'omuta', time: '1999-2000年', timeFolder: '05' },
  { name: '周南', datasetFolder: 'shunan', time: '1899年', timeFolder: '00' },
  { name: '周南', datasetFolder: 'shunan', time: '1949年', timeFolder: '01' },
  { name: '周南', datasetFolder: 'shunan', time: '1968-1969年', timeFolder: '02' },
  { name: '周南', datasetFolder: 'shunan', time: '1985年', timeFolder: '03' },
  { name: '周南', datasetFolder: 'shunan', time: '1994-2001年', timeFolder: '04' },
  { name: '米沢', datasetFolder: 'yonezawa', time: '1908-1910年', timeFolder: '00' },
  { name: '米沢', datasetFolder: 'yonezawa', time: '1952-1953年', timeFolder: '01' },
  { name: '米沢', datasetFolder: 'yonezawa', time: '1970-1973年', timeFolder: '02' },
  { name: '米沢', datasetFolder: 'yonezawa', time: '1984年', timeFolder: '03' },
  { name: '米沢', datasetFolder: 'yonezawa', time: '1999-2001年', timeFolder: '04' },
  { name: '伊那', datasetFolder: 'ina', time: '1911年', timeFolder: '00' },
  { name: '伊那', datasetFolder: 'ina', time: '1951-1952年', timeFolder: '01' },
  { name: '伊那', datasetFolder: 'ina', time: '1976年', timeFolder: '02' },
  { name: '伊那', datasetFolder: 'ina', time: '1987-1990年', timeFolder: '03' },
  { name: '伊那', datasetFolder: 'ina', time: '1998-2001年', timeFolder: '04' },
  { name: '伊賀', datasetFolder: 'iga', time: '1892年', timeFolder: '00' },
  { name: '伊賀', datasetFolder: 'iga', time: '1937年', timeFolder: '01' },
  { name: '伊賀', datasetFolder: 'iga', time: '1968年', timeFolder: '02' },
  { name: '伊賀', datasetFolder: 'iga', time: '1980-1986年', timeFolder: '03' },
  { name: '伊賀', datasetFolder: 'iga', time: '1996-2001年', timeFolder: '04' },
]

konUrls = konUrls.map((url) => {
  const times = url.time.replace('年', '').split('-')
  return {
    name: url.name,
    time: url.time,
    id: 'oh-kon-' + url.name + '-source-' + url.timeFolder,
    source: 'oh-kon-' + url.name + '-source-' + url.timeFolder,
    tiles: ['https://ktgis.net/kjmapw/kjtilemap/' + url.datasetFolder + '/' + url.timeFolder + '/{z}/{x}/{y}.png'],
    timeStart: Number(times[0]),
    timeEnd: isNaN(Number(times[1])) ? Number(times[0]) : Number(times[1]),
  }
})

const konSources = []
const konLayers = []
konUrls.forEach((url) => {
  konSources.push({
    id: url.id,
    obj: {
      type: 'raster',
      tiles: url.tiles,
      scheme: 'tms',
    },
  })
  konLayers.push({
    id: url.id,
    source: url.source,
    name0: url.name,
    name: url.name + url.time,
    type: 'raster',
  })
})
const konLayers2 = konLayers.map((layer, i) => {
  return {
    id: layer.id,
    name: layer.name0,
    label: layer.name,
    source: konSources[i],
    layers: [layer],
    attribution: "<a href='https://ktgis.net/kjmapw/tilemapservice.html' target='_blank'>今昔マップ</a>",
    credit: '今昔マップ',
  }
})
// 親ノードごとにグループ化する
const groupedLayers = konLayers2.reduce((acc, layer) => {
  // 親ノードを `name` フィールドでグループ化
  if (!acc[layer.name]) {
    acc[layer.name] = {
      id: 'kon-' + layer.name, // 親ノードの ID
      label: layer.name, // 親ノードのラベル
      nodes: [], // 子ノードとしてレイヤーを格納する配列
    }
  }
  acc[layer.name].nodes.push(layer) // 子ノードとして追加
  return acc
}, {})
// オブジェクトを配列に変換
const konjyakuMapWithParentNodes = Object.values(groupedLayers)

// 「今昔マップ」の親ノードとしてまとめる
const konjyakuMap = {
  id: 'konjyaku',
  label: '今昔マップ',
  description: '今昔マップ。埼玉大学の故・谷謙二教授が作成・公開した時系列地形図閲覧サイトのデータ。明治（1888年）〜2008年の地形図を全国67地域・320以上の時期で比較できる。タイムスライダーと自動再生機能（4段階速度）で時間旅行体験が可能。谷教授の2022年逝去後は東京大学が運用を引き継いでいる',
  nodes: konjyakuMapWithParentNodes,
}

// 戦後米軍地図-----------------------------------------------------------------------------------------------------------
let urls = [
  {
    '50on': 'みやざきし',
    name: '宮崎市',
    url: 'https://kenzkenz2.xsrv.jp/usarmy/miyazaki/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.38730562869546, 31.94874904974968, 131.47186495009896, 31.85909130381588],
  },
  {
    '50on': 'のべおかし',
    name: '延岡市',
    url: 'https://kenzkenz2.xsrv.jp/usarmy/nobeoka/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.63757572120534, 32.62500535406083, 131.72436281180384, 32.54331840955494],
  },
  {
    '50on': 'みやこのじょうし',
    name: '都城市',
    url: 'https://t.tilemap.jp/jcp_maps/miyakonojo/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.01477802559293, 31.759362148868007, 131.10179776971427, 31.69135798671786],
  },
  {
    '50on': 'かごしまし',
    name: '鹿児島市',
    url: 'https://kenzkenz2.xsrv.jp/usarmy/kagosima/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.5199329928549, 31.625904260596627, 130.60040625640664, 31.53839471681782],
  },
  {
    '50on': 'むろらんし',
    name: '室蘭市',
    url: 'https://t.tilemap.jp/jcp_maps/muroran/{z}/{x}/{y}.png',
    tms: true,
    bounds: [140.90440038348575, 42.38405121586513, 141.05923356332505, 42.29059914480226],
  },
  {
    '50on': 'あかしし',
    name: '明石市',
    url: 'https://t.tilemap.jp/jcp_maps/akashi/{z}/{x}/{y}.png',
    tms: true,
    bounds: [134.9178464474012, 34.710923217623645, 135.04999909886737, 34.6239626197396],
  },
  {
    '50on': 'あいおいし',
    name: '相生市',
    url: 'https://t.tilemap.jp/jcp_maps/harima/{z}/{x}/{y}.png',
    tms: true,
    bounds: [134.40899456747056, 34.827906424389056, 134.51680327661515, 34.734588690535986],
  },
  {
    '50on': 'あきたし',
    name: '秋田市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/akita/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.0717562014137, 39.73918236291928, 140.14706081310268, 39.6820763867207],
  },
  {
    '50on': 'あおもりし',
    name: '青森市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/aomori/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.70599744657008, 40.85260097581303, 140.79743395188777, 40.7935556203787],
  },
  {
    '50on': 'あさひかわし',
    name: '旭川市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/asahikawa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [142.30618215432563, 43.8296431351975, 142.425113984255, 43.7268853650624],
  },
  {
    '50on': 'ちばし',
    name: '千葉市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/chiba/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.08764378681573, 35.62917380173154, 140.15972144856843, 35.554932756846156],
  },
  {
    '50on': 'ふじのみやし',
    name: '富士宮市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/fujinomiya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [138.5809732397614, 35.25121212484136, 138.6515394767342, 35.18069262503313],
  },
  {
    '50on': 'ふくいし',
    name: '福井市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/fukui/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.16942055402976, 36.0976050324894, 136.25602908312064, 36.01592560352381],
  },
  {
    '50on': 'ふくしまし',
    name: '福島市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/fukushima/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.44319076482216, 37.78090343507313, 140.5007616991178, 37.72953717372495],
  },
  {
    '50on': 'ふしきし',
    name: '伏木',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/fushiki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.02508679699687, 36.8245491449906, 137.1126408047655, 36.7604369200003],
  },
  {
    '50on': 'ぎふし',
    name: '岐阜市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/gifu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.70751515094838, 35.470986568936624, 136.82609225873549, 35.37258621685527],
  },
  {
    '50on': 'はぶ',
    name: 'habu',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/habu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.1350108897345, 34.32262791592599, 133.2262140428679, 34.24586042631],
  },
  {
    '50on': 'はちのへ',
    name: '八戸市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hachinohe/{z}/{x}/{y}.png',
    tms: false,
    bounds: [141.4589812322207, 40.56255590542611, 141.57631044226363, 40.48799652674896],
  },
  {
    '50on': 'はぎし',
    name: '萩市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hagi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.3620548860769, 34.4605107256224, 131.43111774489685, 34.38617650807586],
  },
  {
    '50on': 'はこだてし',
    name: '函館市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hakodate/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.68097748426095, 41.82926676413055, 140.7901943531384, 41.73143517073271],
  },
  {
    '50on': 'はんだし',
    name: '半田市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/handa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.89242595186886, 34.945077593842996, 137.00722449770626, 34.85734903493045],
  },
  {
    '50on': 'ひがしいわせ',
    name: '東岩瀬',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/higashiiwase/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.16815167238158, 36.78583199289653, 137.25717016627235, 36.71700039282162],
  },
  {
    '50on': 'ひこねし',
    name: '彦根市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hikone/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.1900204514088, 35.32992409688262, 136.30520216294815, 35.224721393195296],
  },
  {
    '50on': 'ひめじし',
    name: '姫路市',
    url: 'https://t.tilemap.jp/jcp_maps/himeji/{z}/{x}/{-y}.png',
    tms: false,
    bounds: [134.64098624425583, 34.8708631072014, 134.7378282897613, 34.80268817759354],
  },
  {
    '50on': 'ひらかたし',
    name: '枚方市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hirakata/{z}/{x}/{y}.png',
    tms: false,
    bounds: [135.57194920276365, 34.87011100775344, 135.68724932168683, 34.800562973217296],
  },
  {
    '50on': 'ひらつかし',
    name: '平塚市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hiratsuka/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.31229120023235, 35.35904999646574, 139.3878805441378, 35.28874804475291],
  },
  {
    '50on': 'ひろまち',
    name: '広町',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hiromachi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [132.5731525471544, 34.26760068261592, 132.67292535809037, 34.1905277967504],
  },
  {
    '50on': 'ひろさきし',
    name: '弘前市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hirosaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.4235199121323, 40.631355509931836, 140.5010183369034, 40.56109320751867],
  },
  {
    '50on': 'ひろしまし',
    name: '広島市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hiroshima/{z}/{x}/{y}.png',
    tms: false,
    bounds: [132.3884909359741, 34.431458513241665, 132.52820720355984, 34.330487127792665],
  },
  {
    '50on': 'ひたちし',
    name: '日立市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hitachi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.57999833282472, 36.64482954491233, 140.6999440377617, 36.53230602703728],
  },
  {
    '50on': 'ひとよしし',
    name: '人吉市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/hitoyoshi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.73763423508643, 32.2355050464609, 130.79378929829835, 32.1887013821016],
  },
  {
    '50on': 'いちのみやし',
    name: '一宮市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/ichinomiya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.73726867270835, 35.34563130774367, 136.8879913849867, 35.19549105733337],
  },
  {
    '50on': 'いさはやし',
    name: '諫早市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/isahaya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.02394444628715, 32.86659294976408, 130.108573505125, 32.81428582145436],
  },
  {
    '50on': 'いいづかし',
    name: '飯塚市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/iizuka/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.65216494469635, 33.671544924421084, 130.71290557949533, 33.589367508699866],
  },
  {
    '50on': 'かじきし',
    name: '加治木',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kajiki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.6334297037225, 31.757366116931934, 130.6942427581649, 31.706007526498084],
  },
  {
    '50on': 'かまいしし',
    name: '釜石市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kamaishi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [141.83555625727507, 39.29839433432599, 141.9190507416997, 39.24137226047998],
  },
  {
    '50on': 'かなざわし',
    name: '金沢市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kanazawa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.57678119459374, 36.616141434534484, 136.70692666986213, 36.51525054188362],
  },
  {
    '50on': 'かんだ',
    name: '苅田町',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kanda/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.94005143590758, 33.81584169930497, 131.02026222594566, 33.747149681232116],
  },
  {
    '50on': 'からつし',
    name: '唐津市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/karatsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [129.921021511898, 33.50559209417867, 130.005309930191, 33.43203779434077],
  },
  {
    '50on': 'かりやし',
    name: '刈谷市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kariya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.94200877634015, 35.03318584159486, 137.0601647773119, 34.959046536665284],
  },
  {
    '50on': 'かしわざきし',
    name: '柏崎市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kashiwazaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [138.50568101943807, 37.40203383548953, 138.59532647968607, 37.342488307171394],
  },
  {
    '50on': 'かわごえし',
    name: '川越市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kawagoe/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.4570958921943, 35.942500179173905, 139.52681723332742, 35.87309357493287],
  },
  {
    '50on': 'とよたし',
    name: '豊田市（挙母）',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/koromo/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.08772963931085, 35.13859479723625, 137.1960466270638, 35.027068132737384],
  },
  {
    '50on': 'くだまつし',
    name: '下松市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kudamatsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.8186521172428, 34.038862259919966, 131.89699206649303, 33.96796119128024],
  },
  {
    '50on': 'くわなし',
    name: '桑名市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kuwana/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.61704718511825, 35.13788876963871, 136.77256971042877, 34.97847375883852],
  },
  {
    '50on': 'こうちし',
    name: '高知市',
    url: 'https://t.tilemap.jp/jcp_maps/kochi/{z}/{x}/{y}.png',
    tms: true,
    bounds: [133.4697604427741, 33.593332069856146, 133.58746118164257, 33.48248877930844],
  },
  {
    '50on': 'こうふし',
    name: '甲府市',
    url: 'https://t.tilemap.jp/jcp_maps/kofu/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.5192430532455, 35.713904772878024, 138.61371447806357, 35.62085584514263],
  },
  {
    '50on': 'こくら',
    name: '小倉',
    url: 'https://t.tilemap.jp/jcp_maps/kokura/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.83351113737584, 33.92073003726122, 130.95307633268283, 33.83365615261114],
  },
  {
    '50on': 'こおりやまし',
    name: '郡山市',
    url: 'https://t.tilemap.jp/jcp_maps/koriyama/{z}/{x}/{y}.png',
    tms: true,
    bounds: [140.33108953120356, 37.43688472335944, 140.4249240446428, 37.367662796402],
  },
  {
    '50on': 'くまもとし',
    name: '熊本市',
    url: 'https://t.tilemap.jp/jcp_maps/kumamoto/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.6754534531498, 32.83726270469704, 130.77889418491364, 32.75070766375866],
  },
  {
    '50on': 'くるめし',
    name: '久留米市',
    url: 'https://t.tilemap.jp/jcp_maps/kurume/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.4842734730671, 33.33550789416525, 130.58554027442437, 33.26640037986165],
  },
  {
    '50on': 'くしろし',
    name: '釧路市',
    url: 'https://t.tilemap.jp/jcp_maps/kushiro/{z}/{x}/{y}.png',
    tms: true,
    bounds: [144.34146030236244, 43.03742598091131, 144.44208605576515, 42.94577285147719],
  },
  {
    '50on': 'きょうとし１',
    name: '京都市(北)',
    url: 'https://t.tilemap.jp/jcp_maps/kyoto_north/{z}/{x}/{y}.png',
    tms: true,
    bounds: [135.69243533265592, 35.07483494738305, 135.80488895726202, 34.987790816357],
  },
  {
    '50on': 'きょうとし２',
    name: '京都市(南)',
    url: 'https://t.tilemap.jp/jcp_maps/kyoto_south/{z}/{x}/{y}.png',
    tms: true,
    bounds: [135.6943010138845, 34.996985089808334, 135.80539677017686, 34.916474628902776],
  },
  {
    '50on': 'まえはしし',
    name: '前橋市',
    url: 'https://t.tilemap.jp/jcp_maps/maebashi/{z}/{x}/{y}.png',
    tms: true,
    bounds: [139.03692848520149, 36.42476537937992, 139.10921177449703, 36.36642121340162],
  },
  {
    '50on': 'まくらざきし',
    name: '枕崎市',
    url: 'https://t.tilemap.jp/jcp_maps/makurazaki/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.27057404867296, 31.293447219997688, 130.3296372970093, 31.248059353360432],
  },
  {
    '50on': 'まつえし',
    name: '松江市',
    url: 'https://t.tilemap.jp/jcp_maps/matsue/{z}/{x}/{y}.png',
    tms: true,
    bounds: [132.99721215263972, 35.49991858248744, 133.10912531212935, 35.42483380090842],
  },
  {
    '50on': 'みはらし',
    name: '三原市',
    url: 'https://t.tilemap.jp/jcp_maps/mihara_itozaki/{z}/{x}/{y}.png',
    tms: true,
    bounds: [133.02333144783972, 34.425199058736396, 133.13130511283873, 34.356677113032205],
  },
  {
    '50on': 'みとし',
    name: '水戸市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/mito/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.4179578231387, 36.413769771836456, 140.545391585093, 36.350511004933594],
  },
  {
    '50on': 'ながのし',
    name: '長野市',
    url: 'https://t.tilemap.jp/jcp_maps/nagano/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.14073032198428, 36.69619682804296, 138.2340108459997, 36.615546045100615],
  },
  {
    '50on': 'ながおかし',
    name: '長岡市',
    url: 'https://t.tilemap.jp/jcp_maps/nagaoka/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.81563140898004, 37.48632713465814, 138.87307359724298, 37.41690979613216],
  },
  {
    '50on': 'ながさきし',
    name: '長崎市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/nagasaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [129.82426933289048, 32.80116245898009, 129.92143838942525, 32.69767898831918],
  },
  {
    '50on': 'なごやし１',
    name: '名古屋市(北東)',
    url: 'https://t.tilemap.jp/jcp_maps/nagoya_ne/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.8897098705461, 35.22421193160979, 136.98818271993733, 35.13233810303973],
  },
  {
    '50on': 'なごやし２',
    name: '名古屋市(北西)',
    url: 'https://t.tilemap.jp/jcp_maps/nagoya_nw/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.80611080422372, 35.225824131738065, 136.90547197474476, 35.13243456671647],
  },
  {
    '50on': 'なごやし３',
    name: '名古屋市(南東)',
    url: 'https://t.tilemap.jp/jcp_maps/nagoya_se/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.88919877397763, 35.14167895962797, 136.98842441427456, 35.04951319684166],
  },
  {
    '50on': 'なごやし４',
    name: '名古屋市(南西)',
    url: 'https://t.tilemap.jp/jcp_maps/nagoya_sw/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.80523113396558, 35.141184354273065, 136.90487806511402, 35.048543288426316],
  },
  {
    '50on': 'ななおし',
    name: '七尾市',
    url: 'https://t.tilemap.jp/jcp_maps/nanao/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.90164078074454, 37.09788232838679, 137.0370105286217, 37.0228343649801],
  },
  {
    '50on': 'なおえつ',
    name: '直江津',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/naoetsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [138.20747979491517, 37.20133085529935, 138.27912193297908, 37.1554182778262],
  },
  {
    '50on': 'にいがたし',
    name: '新潟市',
    url: 'https://t.tilemap.jp/jcp_maps/niigata/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.99186708819562, 37.96788748388437, 139.0933551717966, 37.8983254944878],
  },
  {
    '50on': 'にいはまし',
    name: '新居浜市',
    url: 'https://t.tilemap.jp/jcp_maps/niihama/{z}/{x}/{y}.png',
    tms: true,
    bounds: [133.22255519907048, 34.00367828974015, 133.31210141758493, 33.92086221440381],
  },
  {
    '50on': 'にっこうし',
    name: '日光市',
    url: 'https://t.tilemap.jp/jcp_maps/nikko/{z}/{x}/{y}.png',
    tms: true,
    bounds: [139.48407988645982, 36.78855612641945, 139.64399721124124, 36.71216243946745],
  },
  {
    '50on': 'のおがたし',
    name: '直方市',
    url: 'https://t.tilemap.jp/jcp_maps/nogata/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.69598996782418, 33.77216740394658, 130.75809919977306, 33.71618955385571],
  },
  {
    '50on': 'ぬまづし',
    name: '沼津市',
    url: 'https://t.tilemap.jp/jcp_maps/numazu/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.8324112693624, 35.127752627941, 138.89685871702, 35.06471308977173],
  },
  {
    '50on': 'おおがきし',
    name: '大垣市',
    url: 'https://t.tilemap.jp/jcp_maps/ogaki/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.55413836399077, 35.40195135119643, 136.66695408681392, 35.33227207940551],
  },
  {
    '50on': 'おおいたし',
    name: '大分市',
    url: 'https://t.tilemap.jp/jcp_maps/oita/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.55698081039802, 33.27340799027354, 131.65331704103366, 33.203760920003575],
  },
  {
    '50on': 'おかやまし',
    name: '岡山市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/okayama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.87770654276844, 34.708880685750856, 133.97912757114406, 34.6163776195384],
  },
  {
    '50on': 'おけがわし',
    name: '桶川市',
    url: 'https://t.tilemap.jp/jcp_maps/okegawa/{z}/{x}/{y}.png',
    tms: true,
    bounds: [139.52177323905948, 36.0269588999937, 139.6205921455574, 35.95245423653812],
  },
  {
    '50on': 'おおみなと',
    name: '大湊',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/ominato/{z}/{x}/{y}.png',
    tms: false,
    bounds: [141.10173496456386, 41.30304157411669, 141.22543978543519, 41.20456356172045],
  },
  {
    '50on': 'おおむらし',
    name: '大村市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/omura/{z}/{x}/{y}.png',
    tms: false,
    bounds: [129.89270322251053, 32.95816906007779, 129.98746633755894, 32.8826256032659],
  },
  {
    '50on': 'おおむたし',
    name: '大牟田市',
    url: 'https://t.tilemap.jp/jcp_maps/omuta/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.37973611582464, 33.056731972736316, 130.48797531951612, 32.98711858353457],
  },
  {
    '50on': 'さんようおのだし',
    name: '山陽小野田市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/onoda/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.13791842343807, 34.01405470527139, 131.20457467021942, 33.944588710860714],
  },
  {
    '50on': 'おたるし',
    name: '小樽市',
    url: 'https://t.tilemap.jp/jcp_maps/otaru/{z}/{x}/{y}.png',
    tms: true,
    bounds: [140.95003125711892, 43.2498339236171, 141.06840585812543, 43.1510898825083],
  },
  {
    '50on': 'おおつし',
    name: '大津市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/otsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [135.8276778607642, 35.04236537027731, 135.9406216590678, 34.950260003281315],
  },
  {
    '50on': 'るもいし',
    name: '留萌市',
    url: 'https://t.tilemap.jp/jcp_maps/rumoi/{z}/{x}/{y}.png',
    tms: true,
    bounds: [141.6042188327817, 43.98252204659977, 141.70030494742667, 43.90720818339608],
  },
  {
    '50on': 'さえきし',
    name: '佐伯市',
    url: 'https://t.tilemap.jp/jcp_maps/saeki/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.85777088174342, 32.99383225285267, 131.93490104102835, 32.90668177072595],
  },
  {
    '50on': 'さかたし',
    name: '酒田市',
    url: 'https://t.tilemap.jp/jcp_maps/sakata/{z}/{x}/{y}.png',
    tms: true,
    bounds: [139.79572723006245, 38.94697215540117, 139.86472236310482, 38.893991742680754],
  },
  {
    '50on': 'さっぽろし',
    name: '札幌市',
    url: 'https://t.tilemap.jp/jcp_maps/sapporo/{z}/{x}/{y}.png',
    tms: true,
    bounds: [141.2872269967413, 43.12652987403498, 141.4241048765094, 43.0095195482877],
  },
  {
    '50on': 'させぼし',
    name: '佐世保市',
    url: 'https://t.tilemap.jp/jcp_maps/sasebo/{z}/{x}/{y}.png',
    tms: true,
    bounds: [129.67363106960568, 33.21108635181264, 129.77110120410237, 33.10068154282844],
  },
  {
    '50on': 'さつませんだいし',
    name: '薩摩川内市',
    url: 'https://t.tilemap.jp/jcp_maps/satsuma_sendai/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.2660942035549, 31.84126574443949, 130.32700482335025, 31.789400580167452],
  },
  {
    '50on': 'せんだいし',
    name: '仙台市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/sendai/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.82992065404846, 38.293472864168336, 140.9386044338842, 38.20103124986022],
  },
  {
    '50on': 'しかま',
    name: '飾磨',
    url: 'https://t.tilemap.jp/jcp_maps/shikama/{z}/{x}/{y}.png',
    tms: true,
    bounds: [134.57381908301352, 34.830801155046686, 134.69907891456126, 34.7487640772753],
  },
  {
    '50on': 'しまばらし',
    name: '島原市',
    url: 'https://t.tilemap.jp/jcp_maps/shimabara/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.33991189911603, 32.80562992452464, 130.40247509494066, 32.75341095906735],
  },
  {
    '50on': 'しみずし',
    name: '清水市',
    url: 'https://t.tilemap.jp/jcp_maps/shimizu/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.44154622744944, 35.05407685272354, 138.53987265717413, 34.97184831284642],
  },
  {
    '50on': 'しものせきし',
    name: '下関市',
    url: 'https://t.tilemap.jp/jcp_maps/shimonoseki_moji/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.86186034693543, 33.99364348447547, 130.99910898222745, 33.9010277531139],
  },
  {
    '50on': 'すいたし',
    name: '吹田市',
    url: 'https://t.tilemap.jp/jcp_maps/suita/{z}/{x}/{y}.png',
    tms: true,
    bounds: [135.46811271576692, 34.81241595216801, 135.58729734389593, 34.73599802051933],
  },
  {
    '50on': 'たかだし',
    name: '高田市',
    url: 'https://t.tilemap.jp/jcp_maps/takada/{z}/{x}/{y}.png',
    tms: true,
    bounds: [138.2140323701369, 37.14730687866512, 138.28622804908625, 37.08329203410612],
  },
  {
    '50on': 'たかまつし',
    name: '高松市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/takamatsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.99545893909394, 34.39269918414061, 134.11711991669594, 34.301570982649295],
  },
  {
    '50on': 'たかさごし',
    name: '高砂市',
    url: "https://kenzkenz3.xsrv.jp/jcp_maps/takasago/{z}/{x}/{y}.png'",
    tms: false,
    bounds: [134.74741530689215, 34.79614117291294, 134.91089795800664, 34.698735552479135],
  },
  {
    '50on': 'たかさきし',
    name: '高崎市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/takasaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [138.97691229387246, 36.35389563865152, 139.03623605175935, 36.294564407602934],
  },
  {
    '50on': 'とばし',
    name: '鳥羽市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/toba/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.80242859001117, 34.5282360211439, 136.8797379004474, 34.4474052593944],
  },
  {
    '50on': 'たまのし',
    name: '玉野市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tomano/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.8988852990205, 34.519890070517164, 133.99005626564573, 34.436591886068825],
  },
  {
    '50on': 'とくしまし',
    name: '徳島市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tokushima/{z}/{x}/{y}.png',
    tms: false,
    bounds: [134.4936030594145, 34.12235468581463, 134.61755866682876, 34.02929803919676],
  },
  {
    '50on': 'とくやま',
    name: '徳山',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tokuyama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.74538715278192, 34.08462384762474, 131.84149338399453, 33.99426773843973],
  },
  {
    '50on': 'とっとりし',
    name: '鳥取市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tottori/{z}/{x}/{y}.png',
    tms: false,
    bounds: [134.1900909428445, 35.52696479360749, 134.28330172942555, 35.46730267834758],
  },
  {
    '50on': 'とやまし',
    name: '富山市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/toyama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.1684165214623, 36.72884284984751, 137.25429548970115, 36.66525139660453],
  },
  {
    '50on': 'とよはしし１',
    name: '豊橋市(北)',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/toyohashi_north/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.30419119783116, 34.79819174398203, 137.4371489692516, 34.699863481299786],
  },
  {
    '50on': 'とよはしし２',
    name: '豊橋市(南)',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/toyohashi_south/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.3040570873804, 34.77214256985337, 137.4375519711561, 34.67231637720508],
  },
  {
    '50on': 'とよかわし',
    name: '豊川市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/toyokawa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.3300625528809, 34.87697931243254, 137.4370303894993, 34.78739376103327],
  },
  {
    '50on': 'つちざき',
    name: '土崎',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tsuchizaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.0322280044941, 39.77748578452113, 140.10570276186743, 39.73013139828953],
  },
  {
    '50on': 'つるがし',
    name: '敦賀市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tsuruga/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.02015594007466, 35.67725529086043, 136.1043337172458, 35.61019094654843],
  },
  {
    '50on': 'つやまし',
    name: '津山市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tsuyama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.9546515527164, 35.093894098921695, 134.05467984406104, 35.028394837132595],
  },
  {
    '50on': 'うべし',
    name: '宇部市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/ube/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.21090737917757, 33.985496410975, 131.27556202748153, 33.91514344585086],
  },
  {
    '50on': 'うおつし',
    name: '魚津市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/uotsu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [137.359654715174, 36.84154335048109, 137.4231533313926, 36.78819929793053],
  },
  {
    '50on': 'うつのみやし',
    name: '宇都宮市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/utsunomiya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.80726931534997, 36.60692094994177, 139.94236346625078, 36.51922096392269],
  },
  {
    '50on': 'あいづわかまつし',
    name: '会津若松市',
    url: 'https://t.tilemap.jp/jcp_maps/wakamatsu/{z}/{x}/{y}.png',
    tms: true,
    bounds: [139.89971460019447, 37.5246569332612, 139.9580861738811, 37.4662552780602],
  },
  {
    '50on': 'わかやまし',
    name: '和歌山市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/wakayama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [135.10168442914156, 34.278745841537926, 135.2230356116023, 34.16603880752899],
  },
  {
    '50on': 'やまがたし',
    name: '山形市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yamagata/{z}/{x}/{y}.png',
    tms: false,
    bounds: [140.31046887145618, 38.2869380235048, 140.37764680789093, 38.223610501260254],
  },
  {
    '50on': 'やまぐちし',
    name: '山口市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yamaguchi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [131.44143730059048, 34.20638353543529, 131.50447591796302, 34.15296649850379],
  },
  {
    '50on': 'やはた',
    name: '八幡',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yawata/{z}/{x}/{y}.png',
    tms: false,
    bounds: [130.7352353711359, 33.93668107746137, 130.8548940797083, 33.848555614389184],
  },
  {
    '50on': 'やつしろし',
    name: '八代市',
    url: 'https://t.tilemap.jp/jcp_maps/yatsushiro/{z}/{x}/{y}.png',
    tms: true,
    bounds: [130.56782128418257, 32.52529691909547, 130.64291911328604, 32.472253822463],
  },
  {
    '50on': 'よっかいち',
    name: '四日市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yokkaichi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [136.5812700596467, 34.997838813507684, 136.66969176203165, 34.92287302729859],
  },
  {
    '50on': 'よなごし',
    name: '米子市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yonago/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.28164837924874, 35.480611440807465, 133.3806044575397, 35.397899772912595],
  },
  {
    '50on': 'ぜんつじし',
    name: '善通寺市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/zentsuji/{z}/{x}/{y}.png',
    tms: false,
    bounds: [133.74462727940428, 34.25811177699647, 133.81558511889327, 34.199615898483586],
  },
  {
    '50on': 'たかなべちょう',
    name: '高鍋町',
    url: 'https://t.tilemap.jp/jcp_maps/takanabe/{z}/{x}/{y}.png',
    tms: true,
    bounds: [131.49437198658654, 32.14926230220921, 131.55480667761816, 32.097161098583],
  },
  {
    '50on': 'ん01',
    name: '高岡',
    url: 'https://t.tilemap.jp/jcp_maps/takaoka/{z}/{x}/{y}.png',
    tms: true,
    bounds: [136.95122553245773, 36.78462123872494, 137.07285137091316, 36.72027436365445],
  },
  {
    '50on': 'ん02',
    name: '大宮',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/omiya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.58288807604737, 35.936471778972816, 139.68984384272522, 35.82067028475796],
  },
  {
    '50on': 'ん03',
    name: '板橋区',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/itabashi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.55659942810058, 35.84279388096407, 139.6851201552582, 35.74376046340615],
  },
  {
    '50on': 'ん04',
    name: '川口市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kawaguchi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.67232242769714, 35.8422159012266, 139.8030720705461, 35.74493041694202],
  },
  {
    '50on': 'ん05',
    name: '松戸市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/matsudo/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.787265544735, 35.84270580004852, 139.92278817852593, 35.74447067756418],
  },
  {
    '50on': 'ん06',
    name: '立川市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tachikawa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.3257750300442, 35.7677794416578, 139.45130978801595, 35.66939179444587],
  },
  {
    '50on': 'ん07',
    name: '田無',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/tanashi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.4374114036049, 35.768251043426986, 139.57501073772565, 35.6676261138138],
  },
  {
    '50on': 'ん08',
    name: '中野区',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/nakano/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.55876057440844, 35.766645453219354, 139.6859080105504, 35.66883929724342],
  },
  {
    '50on': 'ん09',
    name: '小石川',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/koishikawa/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.67579968662739, 35.7659536404624, 139.8011419961023, 35.67149249224876],
  },
  {
    '50on': 'ん10',
    name: '本所',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/honjo/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.78699439491749, 35.76692444138439, 139.92178432779318, 35.66956521078299],
  },
  {
    '50on': 'ん11',
    name: '調布',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/chofu/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.43793800309393, 35.69264409802105, 139.5735330565283, 35.5942285900592],
  },
  {
    '50on': 'ん12',
    name: '世田谷区',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/setagaya/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.55750953987325, 35.69020645502415, 139.68686778679574, 35.59558138323757],
  },
  {
    '50on': 'ん13',
    name: '日本橋',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/nihonbashi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.67133578494247, 35.691982412751216, 139.8057094274467, 35.59480793294374],
  },
  {
    '50on': 'ん14',
    name: '砂町',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/sunamachi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.7912620438637, 35.6927029142241, 139.9203983379902, 35.595771132326846],
  },
  {
    '50on': 'ん15',
    name: '田園調布',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/denen/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.5552674729985, 35.61684183769074, 139.68731262280164, 35.520543926413254],
  },
  {
    '50on': 'ん16',
    name: '大森',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/omori/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.67116572453156, 35.61793041851628, 139.80602518214357, 35.53127408650961],
  },
  {
    '50on': 'ん17',
    name: '川崎',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/kawasaki/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.67419982036674, 35.54299845717924, 139.80459004720768, 35.4553511140454],
  },
  {
    '50on': 'ん18',
    name: '横浜市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yokohama/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.55378204664274, 35.466367420903566, 139.689263106194, 35.37842491230309],
  },
  {
    '50on': 'ん19',
    name: '根岸湾',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/negishi/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.56020459612833, 35.4006437745536, 139.68445926984774, 35.30432787941882],
  },
  {
    '50on': 'ん20',
    name: '横須賀市',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/yokosuka/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.60289262315248, 35.342237358161995, 139.72813703199841, 35.24850065660095],
  },
  {
    '50on': 'ん21',
    name: '浦賀',
    url: 'https://kenzkenz3.xsrv.jp/jcp_maps/uraga/{z}/{x}/{y}.png',
    tms: false,
    bounds: [139.65567449304083, 35.29220632171602, 139.75966105433923, 35.179361639527656],
  },
]
// position を計算して追加する
urls = urls.map((item) => {
  let [minLon, minLat, maxLon, maxLat] = item.bounds
  return {
    ...item,
    position: [(minLon + maxLon) / 2, (minLat + maxLat) / 2],
  }
})
urls.sort((a, b) => {
  const aValue = a['50on'] || ''
  const bValue = b['50on'] || ''
  return aValue.localeCompare(bValue, 'ja')
})
const amsSources = []
const amsLayers = []
const amsGeojsonSources = []
const amsGeojsonLayers = []
const amsGeojsonLabelLayers = []
urls.forEach((url) => {
  function compareFunc(a, b) {
    return a - b
  }
  url.bounds.sort(compareFunc)
  url.bounds = [url.bounds[2], url.bounds[0], url.bounds[3], url.bounds[1]]
  if (!url.tms) {
    amsSources.push({
      id: 'oh-ams-' + url.name,
      obj: {
        type: 'raster',
        tiles: [url.url],
        minzoom: 0,
        bounds: url.bounds,
      },
    })
  } else {
    amsSources.push({
      id: 'oh-ams-' + url.name,
      obj: {
        type: 'raster',
        tiles: [url.url],
        scheme: 'tms',
        minzoom: 0,
        bounds: url.bounds,
      },
    })
  }
  amsLayers.push({
    id: 'oh-' + url.name,
    source: 'oh-ams-' + url.name,
    type: 'raster',
    position: url.position,
  })

  const geojson = turf.point(url.position, { name: url.name })

  amsGeojsonSources.push({
    id: 'oh-ams-geojson-' + url.name,
    obj: {
      type: 'geojson',
      data: geojson,
    },
  })
  amsGeojsonLayers.push({
    id: 'oh-ams-geojson-' + url.name,
    type: 'circle',
    source: 'oh-ams-geojson-' + url.name,
    paint: {
      'circle-color': 'red',
      'circle-radius': [
        'interpolate', // Zoom-based interpolation
        ['linear'],
        ['zoom'], // Use the zoom level as the input
        10,
        8,
        12,
        0,
      ],
    },
  })
  amsGeojsonLabelLayers.push({
    id: 'oh-ams-geojson-label-' + url.name,
    type: 'symbol',
    source: 'oh-ams-geojson-' + url.name,
    layout: {
      'text-field': ['get', 'name'],
      'text-offset': [0, 1],
    },
    paint: {
      'text-color': 'rgba(255, 0, 0, 1)',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: 6,
    maxzoom: 12,
  })
})
const amsLayers2 = amsLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.id.replace('oh-', '') + '戦後米軍地図',
    sources: [amsSources[i], amsGeojsonSources[i]],
    layers: [layer, amsGeojsonLayers[i], amsGeojsonLabelLayers[i]],
    position: layer.position,
    flyZoom: 13,
    attribution: '<a href="https://maps.lib.utexas.edu/maps/ams/japan/" target="_blank">Perry-Castañeda Map Collection, UT Austin</a>',
    credit: 'Perry-Castañeda Map Collection, UT Austin',
  }
})

// 江戸切絵図-----------------------------------------------------------------------------------------------------------
// https://mapwarper.h-gis.jp/maps/tile/4915/{z}/{x}/{y}.png
const kirieurls = [
  { name: '麻布絵図', url: '4915' },
  { name: '御江戸大名小路絵図', url: '4709' },
  { name: '築地八町堀日本橋南絵図', url: '4901' },
  { name: '外桜田永田町絵図', url: '4910' },
  { name: '芝高輪辺絵図（挿入図）', url: '4986' },
  { name: '芝高輪辺絵図', url: '4908' },
  { name: '東都小石川繪圖', url: '4942' },
  { name: '目黒白金辺図', url: '4939' },
  { name: '今戸箕輪浅草絵図', url: '4935' },
  { name: '駒込絵図', url: '4937' },
  { name: '巣鴨絵図', url: '4938' },
  { name: '根岸谷中辺絵図', url: '4941' },
  { name: '隅田川向島絵図', url: '4940' },
  { name: '本郷湯島絵図', url: '4934' },
  { name: '音羽絵図', url: '4933' },
  { name: '青山渋谷絵図', url: '4932' },
  { name: '浅草御蔵前辺図', url: '4931' },
  { name: '本所絵図', url: '4930' },
  { name: '小日向絵図', url: '4929' },
  { name: '深川絵図', url: '4928' },
  { name: '下谷絵図', url: '4919' },
  { name: '内藤新宿千駄ヶ谷絵図', url: '4918' },
  { name: '大久保絵図', url: '4917' },
  { name: '市ヶ谷牛込絵図', url: '4916' },
  { name: '赤坂絵図', url: '4913' },
  { name: '御江戸番町絵図', url: '4914' },
  { name: '四ツ谷絵図', url: '4911' },
  { name: '日本橋北神田浜町絵図', url: '4902' },
  { name: '駿河台小川町絵図', url: '4909' },
  { name: '芝愛宕下絵図', url: '4907' },
  { name: '今戸箕輪浅草絵図', url: '3899' },
]
const kirieSources = []
const kirieLayers = []
kirieurls.forEach((url) => {
  kirieSources.push({
    id: 'oh-kirie-' + url.name,
    obj: {
      type: 'raster',
      tiles: ['https://mapwarper.h-gis.jp/maps/tile/' + url.url + '/{z}/{x}/{y}.png'],
      minzoom: 0,
    },
  })
  kirieLayers.push({
    url: url.url,
    id: 'oh-kirie-' + url.name,
    source: 'oh-kirie-' + url.name,
    type: 'raster',
  })
})
const kirieLayers2 = kirieLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.id.replace('oh-kirie-', '') + '',
    source: kirieSources[i],
    layers: [layer],
    attribution:
      '<a href="http://codh.rois.ac.jp/edo-maps/owariya/" target="_blank">人文学オープンデータ共同利用センター</a>' +
      '<br><br><a href="https://mapwarper.h-gis.jp/maps/' +
      layer.url +
      '" target="_blank">日本版Map Warper</a>',
    credit: '人文学オープンデータ共同利用センター  +  日本版Map Warper',
  }
})
// 大正13,14年古地図-----------------------------------------------------------------------------------------------------------
// OH2から移植。各県のタイルはTMS形式。県境GeoJSONでclipレイヤーによるマスキング。
// タイル出典: https://kenzkenz.github.io/bunkenzu*/
// 原資料: 最新詳密金刺分縣圖 https://dl.ndl.go.jp/pid/1880805
const kotizuAttribution = '<a href="https://dl.ndl.go.jp/pid/1880805" target="_blank">最新詳密金刺分縣圖</a>'
const kotizuUrls = [
  { id: 'kotizu01', name: '01北海道古地図', region: null, url: 'https://kenzkenz.github.io/bunkenzu4/tile/01hokkaidou0/{z}/{x}/{y}.png', bounds: [139.537, 41.186, 146.422, 46.263], maxzoom: 17, zoom: 8, center: [142.694, 43.242], mask: null },
  { id: 'kotizu02', name: '02青森県古地図(大正13年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu/tile/02aomoriken/{z}/{x}/{y}.png', bounds: [139.143, 40.079, 141.908, 41.653], maxzoom: 13, zoom: 9, center: [140.675, 40.842], mask: 'aomori' },
  { id: 'kotizu03', name: '03岩手県古地図(大正14年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu3/tile/3iwateken/{z}/{x}/{y}.png', bounds: [140.465, 38.604, 142.433, 40.582], maxzoom: 13, zoom: 9, center: [141.408, 39.651], mask: 'iwate' },
  { id: 'kotizu04', name: '04宮城県古地図(大正14年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu/tile/04miyagiken/{z}/{x}/{y}.png', bounds: [140.129, 37.705, 141.808, 39.456], maxzoom: 13, zoom: 9, center: [140.913, 38.493], mask: 'miyagi' },
  { id: 'kotizu05', name: '05秋田県古地図(大正13年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu3/tile/5akitaken/{z}/{x}/{y}.png', bounds: [139.577, 38.809, 141.466, 40.693], maxzoom: 13, zoom: 9, center: [140.343, 39.747], mask: 'akita' },
  { id: 'kotizu06', name: '06山形県古地図(大正13年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu3/tile/6yamagataken/{z}/{x}/{y}.png', bounds: [139.302, 37.620, 141.103, 39.264], maxzoom: 13, zoom: 9, center: [140.098, 38.505], mask: 'yamagata' },
  { id: 'kotizu07', name: '07福島県古地図(大正13年)', region: '東北', url: 'https://kenzkenz.github.io/bunkenzu3/tile/7hukusimaken/{z}/{x}/{y}.png', bounds: [138.930, 36.715, 141.325, 38.145], maxzoom: 13, zoom: 9, center: [140.234, 37.485], mask: 'hukusima' },
  { id: 'kotizu08', name: '08茨城県古地図(大正14年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu3/tile/8ibarakiken/{z}/{x}/{y}.png', bounds: [139.586, 35.584, 141.057, 37.052], maxzoom: 13, zoom: 9, center: [140.333, 36.289], mask: 'ibaraki' },
  { id: 'kotizu09', name: '09栃木県古地図(大正14年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu3/tile/9tochigiken/{z}/{x}/{y}.png', bounds: [139.165, 36.035, 140.364, 37.288], maxzoom: 13, zoom: 9, center: [139.799, 36.679], mask: 'tochigi' },
  { id: 'kotizu10', name: '10群馬県古地図(大正14年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu3/tile/10gunmaken/{z}/{x}/{y}.png', bounds: [137.947, 35.728, 140.038, 37.354], maxzoom: 13, zoom: 9, center: [138.966, 36.508], mask: 'gunma' },
  { id: 'kotizu11', name: '11埼玉県古地図(大正14年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu2/tile/11saitamaken/{z}/{x}/{y}.png', bounds: [138.518, 35.562, 140.120, 36.480], maxzoom: 13, zoom: 9, center: [139.357, 36.030], mask: 'saitama' },
  { id: 'kotizu12', name: '12千葉県古地図(大正13年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu/tile/12chibaken/{z}/{x}/{y}.png', bounds: [139.577, 34.769, 141.051, 36.234], maxzoom: 13, zoom: 9, center: [140.205, 35.483], mask: 'chiba' },
  { id: 'kotizu13', name: '13東京都古地図(大正13年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu/tile/13tokyo/{z}/{x}/{y}.png', bounds: [138.823, 35.301, 140.053, 36.042], maxzoom: 13, zoom: 9, center: [139.427, 35.696], mask: 'tokyo' },
  { id: 'kotizu14', name: '14神奈川県古地図(大正13年)', region: '関東', url: 'https://kenzkenz.github.io/bunkenzu2/tile/14kanagawaken/{z}/{x}/{y}.png', bounds: [138.838, 35.031, 140.056, 35.798], maxzoom: 13, zoom: 9, center: [139.323, 35.433], mask: 'kanagawa' },
  { id: 'kotizu15', name: '15新潟県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/15niigataken/{z}/{x}/{y}.png', bounds: [137.076, 36.147, 140.720, 39.100], maxzoom: 13, zoom: 9, center: [138.999, 37.516], mask: 'niigata' },
  { id: 'kotizu16', name: '16富山県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/16toyamaken/{z}/{x}/{y}.png', bounds: [136.412, 36.131, 137.959, 37.203], maxzoom: 13, zoom: 9, center: [137.212, 36.652], mask: 'toyama' },
  { id: 'kotizu17', name: '17石川県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/17isikawaken/{z}/{x}/{y}.png', bounds: [136.043, 35.953, 137.715, 37.685], maxzoom: 13, zoom: 9, center: [136.797, 36.816], mask: 'isikawa' },
  { id: 'kotizu18', name: '18福井県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu/tile/18fukuiken/{z}/{x}/{y}.png', bounds: [135.175, 35.242, 137.079, 36.478], maxzoom: 13, zoom: 9, center: [136.180, 35.842], mask: 'fukui' },
  { id: 'kotizu19', name: '19山梨県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/19yamanasiken/{z}/{x}/{y}.png', bounds: [138.036, 35.114, 139.464, 36.059], maxzoom: 13, zoom: 9, center: [138.637, 35.651], mask: 'yamanasi' },
  { id: 'kotizu20', name: '20長野県古地図(大正12年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/20naganoken/{z}/{x}/{y}.png', bounds: [137.106, 35.040, 139.261, 37.282], maxzoom: 13, zoom: 9, center: [138.002, 36.126], mask: 'nagano' },
  { id: 'kotizu21', name: '21岐阜県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu2/tile/21gihuken/{z}/{x}/{y}.png', bounds: [136.073, 34.964, 137.959, 36.713], maxzoom: 13, zoom: 9, center: [137.080, 35.805], mask: 'gihu' },
  { id: 'kotizu22', name: '22静岡県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu/tile/22sizuokaken/{z}/{x}/{y}.png', bounds: [137.181, 34.495, 139.406, 35.802], maxzoom: 13, zoom: 9, center: [138.353, 35.031], mask: 'sizuoka' },
  { id: 'kotizu23', name: '23愛知県古地図(大正14年)', region: '中部', url: 'https://kenzkenz.github.io/bunkenzu/tile/23aichiken/{z}/{x}/{y}.png', bounds: [136.287, 34.543, 137.904, 35.537], maxzoom: 13, zoom: 9, center: [137.181, 34.997], mask: 'aichi' },
  { id: 'kotizu24', name: '24三重県古地図(大正13年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/24mieken/{z}/{x}/{y}.png', bounds: [135.558, 33.668, 137.212, 35.418], maxzoom: 13, zoom: 9, center: [136.415, 34.493], mask: 'mie' },
  { id: 'kotizu25', name: '25滋賀県古地図(大正14年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/25sigaken/{z}/{x}/{y}.png', bounds: [135.631, 34.774, 136.644, 35.760], maxzoom: 13, zoom: 9, center: [136.091, 35.252], mask: 'siga' },
  { id: 'kotizu26', name: '26京都府古地図(大正13年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/26kyoutohu/{z}/{x}/{y}.png', bounds: [134.666, 34.505, 136.171, 36.087], maxzoom: 13, zoom: 9, center: [135.490, 34.661], mask: 'kyouto' },
  { id: 'kotizu27', name: '27大阪府古地図(大正14年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/27osaka/{z}/{x}/{y}.png', bounds: [134.892, 34.162, 135.860, 35.129], maxzoom: 13, zoom: 9, center: [135.490, 34.661], mask: 'osaka' },
  { id: 'kotizu28', name: '28兵庫県古地図(大正13年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/28hyogoken/{z}/{x}/{y}.png', bounds: [134.053, 34.081, 135.716, 35.847], maxzoom: 13, zoom: 9, center: [134.837, 34.970], mask: 'hyogo' },
  { id: 'kotizu29', name: '29奈良県古地図(大正14年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/29naraken/{z}/{x}/{y}.png', bounds: [135.362, 33.800, 136.302, 34.824], maxzoom: 13, zoom: 9, center: [135.408, 33.938], mask: 'nara' },
  { id: 'kotizu30', name: '30和歌山県古地図(大正14年)', region: '近畿', url: 'https://kenzkenz.github.io/bunkenzu/tile/30wakayamaken/{z}/{x}/{y}.png', bounds: [134.880, 33.287, 136.131, 34.578], maxzoom: 13, zoom: 9, center: [135.408, 33.938], mask: 'wakayama' },
  { id: 'kotizu31', name: '31鳥取県古地図(大正14年)', region: '中国', url: 'https://kenzkenz.github.io/bunkenzu/tile/31tottoriken/{z}/{x}/{y}.png', bounds: [132.872, 34.688, 134.917, 36.094], maxzoom: 13, zoom: 9, center: [133.864, 35.428], mask: 'tottori' },
  { id: 'kotizu32', name: '32島根県古地図(大正14年)', region: '中国', url: 'https://kenzkenz.github.io/bunkenzu/tile/32shimaneken/{z}/{x}/{y}.png', bounds: [131.105, 33.960, 133.781, 36.077], maxzoom: 13, zoom: 9, center: [132.387, 35.002], mask: 'shimane' },
  { id: 'kotizu33', name: '33岡山県古地図(大正14年)', region: '中国', url: 'https://kenzkenz.github.io/bunkenzu/tile/33okayamaken/{z}/{x}/{y}.png', bounds: [133.128, 34.074, 134.563, 35.497], maxzoom: 13, zoom: 9, center: [133.800, 34.879], mask: 'okayama' },
  { id: 'kotizu34', name: '34広島県古地図(大正13年)', region: '中国', url: 'https://kenzkenz.github.io/bunkenzu/tile/34hiroshimaken/{z}/{x}/{y}.png', bounds: [131.673, 33.775, 133.958, 35.363], maxzoom: 13, zoom: 9, center: [132.759, 34.568], mask: 'hiroshima' },
  { id: 'kotizu35', name: '35山口県古地図(大正13年)', region: '中国', url: 'https://kenzkenz.github.io/bunkenzu/tile/35yamaguchiken/{z}/{x}/{y}.png', bounds: [130.489, 33.623, 132.735, 34.974], maxzoom: 13, zoom: 9, center: [131.593, 34.231], mask: 'yamaguchi' },
  { id: 'kotizu36', name: '36徳島県古地図(大正14年)', region: '四国', url: 'https://kenzkenz.github.io/bunkenzu/tile/36tokusimaken/{z}/{x}/{y}.png', bounds: [133.562, 33.475, 134.950, 34.402], maxzoom: 13, zoom: 9, center: [134.251, 33.938], mask: 'tokusima' },
  { id: 'kotizu37', name: '37香川県古地図(大正14年)', region: '四国', url: 'https://kenzkenz.github.io/bunkenzu/tile/37kagawaken/{z}/{x}/{y}.png', bounds: [133.394, 33.932, 134.593, 34.724], maxzoom: 13, zoom: 9, center: [133.992, 34.241], mask: 'kagawa' },
  { id: 'kotizu38', name: '38愛媛県古地図(大正14年)', region: '四国', url: 'https://kenzkenz.github.io/bunkenzu/tile/38ehimeken/{z}/{x}/{y}.png', bounds: [131.520, 32.490, 134.254, 34.824], maxzoom: 13, zoom: 9, center: [132.838, 33.674], mask: 'ehime' },
  { id: 'kotizu39', name: '39高知県古地図(大正14年)', region: '四国', url: 'https://kenzkenz.github.io/bunkenzu/tile/39kochiken/{z}/{x}/{y}.png', bounds: [132.228, 32.587, 134.508, 34.046], maxzoom: 13, zoom: 9, center: [133.317, 33.537], mask: 'kochi' },
  { id: 'kotizu40', name: '40福岡県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/40fukuokaken/{z}/{x}/{y}.png', bounds: [129.918, 32.793, 131.313, 34.311], maxzoom: 13, zoom: 9, center: [130.634, 33.526], mask: 'fukuoka' },
  { id: 'kotizu41', name: '41佐賀県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/41sagaken/{z}/{x}/{y}.png', bounds: [129.622, 32.898, 130.870, 33.686], maxzoom: 13, zoom: 9, center: [130.095, 33.305], mask: 'saga' },
  { id: 'kotizu42', name: '42長崎県古地図(大正13年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/42nagasakiken/{z}/{x}/{y}.png', bounds: [128.270, 32.477, 130.681, 33.995], maxzoom: 13, zoom: 9, center: [129.536, 33.119], mask: 'nagasaki' },
  { id: 'kotizu43', name: '43熊本県古地図(大正13年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/43kumamotoken/{z}/{x}/{y}.png', bounds: [129.997, 32.023, 131.380, 33.516], maxzoom: 13, zoom: 9, center: [130.769, 32.663], mask: 'kumamoto' },
  { id: 'kotizu44', name: '44大分県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/44oitaken/{z}/{x}/{y}.png', bounds: [130.717, 32.713, 132.487, 33.866], maxzoom: 13, zoom: 9, center: [131.465, 33.228], mask: 'oita' },
  { id: 'kotizu45', name: '45宮崎県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/45miyazakiken/{z}/{x}/{y}.png', bounds: [130.464, 31.272, 132.079, 32.993], maxzoom: 13, zoom: 9, center: [131.300, 32.134], mask: 'miyazaki' },
  { id: 'kotizu46', name: '46鹿児島県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/46kagoshimaken/{z}/{x}/{y}.png', bounds: [129.982, 30.874, 131.331, 32.366], maxzoom: 13, zoom: 9, center: [130.659, 31.600], mask: 'kagoshima' },
  { id: 'kotizu47', name: '47沖縄県古地図(大正14年)', region: '九州沖縄', url: 'https://kenzkenz.github.io/bunkenzu/tile/47okinawaken/{z}/{x}/{y}.png', bounds: [126.308, 25.883, 128.712, 27.432], maxzoom: 13, zoom: 9, center: [127.853, 26.473], mask: 'okinawa' },
]

// 全県のソース・レイヤーを一括生成
// マスキングはカスタムプロトコル kotizumask:// でCanvas上で県境ポリゴンクリップを行う
const kotizuAllSources = []
const kotizuAllLayers = []

kotizuUrls.forEach((item) => {
  const sourceId = `oh-kotizu-${item.id}-source`
  const layerId = `oh-kotizu-${item.id}`

  // マスクありの県はカスタムプロトコル経由、なし（北海道）は直接TMS
  const tileUrl = item.mask
    ? `kotizumask://${item.mask}/${item.url.replace('https://', '')}`
    : item.url

  kotizuAllSources.push({
    id: sourceId,
    obj: {
      type: 'raster',
      tiles: [tileUrl],
      ...(item.mask ? {} : { scheme: 'tms' }),
      tileSize: 256,
      bounds: item.bounds,
      maxzoom: item.maxzoom,
    },
  })

  kotizuAllLayers.push({
    id: layerId,
    source: sourceId,
    type: 'raster',
  })
})

// 個別県レイヤー定義（ツリーの子ノード用）
const kotizuIndividualLayers = kotizuUrls.map((item, i) => {
  return {
    id: `oh-kotizu-${item.id}`,
    label: item.name,
    sources: [kotizuAllSources[i]],
    layers: [kotizuAllLayers[i]],
    flyZoom: item.zoom,
    flyCenter: item.center,
    attribution: kotizuAttribution,
  }
})

// 地域グループごとにまとめる
const kotizuRegions = ['東北', '関東', '中部', '近畿', '中国', '四国', '九州沖縄']
const kotizuRegionNodes = kotizuRegions.map((region) => {
  return {
    id: `kotizu-${region}`,
    label: region,
    nodes: kotizuIndividualLayers.filter(
      (_, i) => kotizuUrls[i].region === region
    ),
  }
})

// 大正古地図ツリーノード（kochizuグループのnodesに追加用）
const kotizuTreeNode = {
  id: 'kotizu',
  label: '大正13,14年古地図',
  description: '大正13〜14年の古地図。全47都道府県をカバー。大正時代の日本',
  nodes: [
    {
      id: 'oh-kotizu-all',
      label: '大正古地図全て',
      sources: [...kotizuAllSources],
      layers: [...kotizuAllLayers],
      attribution: kotizuAttribution,
    },
    // 北海道は地域グループに入れず直下に
    kotizuIndividualLayers[0],
    ...kotizuRegionNodes,
  ],
}

// 東京市火災動態地図（大正12年・関東大震災）---------------------------------------------------------------
// 日本版MapWarperから9枚のタイルを1つのレイヤーとして合成
const tokyokasaiTiles = [
  { id: 'tokyokasai1', mapId: 5449, label: '本所' },
  { id: 'tokyokasai2', mapId: 5444, label: '上野' },
  { id: 'tokyokasai3', mapId: 5442, label: '四谷' },
  { id: 'tokyokasai4', mapId: 5448, label: '新橋' },
  { id: 'tokyokasai5', mapId: 5447, label: '三田' },
  { id: 'tokyokasai6', mapId: 5445, label: '向島' },
  { id: 'tokyokasai7', mapId: 5443, label: '早稲田' },
  { id: 'tokyokasai8', mapId: 5446, label: '深川' },
  { id: 'tokyokasai9', mapId: 5441, label: '日本橋' },
]
const tokyokasaiSources = tokyokasaiTiles.map(t => ({
  id: `oh-${t.id}-source`,
  obj: {
    type: 'raster',
    tiles: [`https://mapwarper.h-gis.jp/maps/tile/${t.mapId}/{z}/{x}/{y}.png`],
    tileSize: 256,
    minzoom: 1,
    maxzoom: 18,
  },
}))
const tokyokasaiLayers = tokyokasaiTiles.map((t, i) => ({
  id: `oh-${t.id}`,
  source: `oh-${t.id}-source`,
  type: 'raster',
  ...(i === 0 ? { position: [139.775, 35.689], z: 13 } : {}),
}))

// 兵庫県以外遺跡立体図-----------------------------------------------------------------------------------------------------------
const isekiurls2 = [
  {
    name: '横地城跡（静岡県菊川市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/yokochi-tile',
    position: [133.9494864960191, 34.72801130848333],
    bounds: [34.725992296143794, 133.9529354877004, 34.73003032082288, 133.9460375043378],
  },
  {
    name: '八王子城跡（東京都八王子市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/hachioji-castle',
    position: [139.2545, 35.6528],
    bounds: [35.6471, 139.2447, 35.6586, 139.2644],
  },
  {
    name: '月舘館跡・中館跡・赤岩館跡（宮城県気仙沼市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tsukidate',
    position: [141.546728, 38.917783],
    bounds: [38.913725, 141.539422, 38.921842, 141.554031],
  },
  {
    name: '江戸城跡（東京都千代田区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/edo',
    position: [139.753114, 35.686456],
    bounds: [35.675742, 139.743825, 35.697169, 139.762406],
  },
  {
    name: '世田谷城跡（東京都世田谷区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/setagaya',
    position: [139.647744, 35.646758],
    bounds: [35.645619, 139.645772, 35.647897, 139.649714],
  },
  {
    name: '品川第３・第６台場（東京都品川区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/shinagawa',
    position: [139.770325, 35.633614],
    bounds: [35.630772, 139.765403, 35.636456, 139.775247],
  },
  {
    name: '滝山城跡（東京都八王子市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/takiyama',
    position: [139.327603, 35.700144],
    bounds: [35.693428, 139.321758, 35.706861, 139.333444],
  },
  {
    name: '深大寺城跡（東京都調布市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/jindaiji',
    position: [139.550633, 35.664853],
    bounds: [35.662953, 139.547347, 35.666753, 139.553922],
  },
  {
    name: '小机城跡（神奈川県横浜市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kozukue',
    position: [138.260736, 35.512303],
    bounds: [35.510403, 138.257456, 35.5142, 138.264017],
  },
  {
    name: '駿府城跡（静岡県静岡市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/sunpu',
    position: [138.383358, 34.980464],
    bounds: [34.975064, 138.378714, 34.985864, 138.388],
  },
  {
    name: '下田城跡（静岡県下田市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/shimoda',
    position: [138.945192, 34.668686],
    bounds: [34.666128, 138.940825, 34.671244, 138.949558],
  },
  {
    name: '山中城（静岡県三島市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/yamanaka',
    position: [138.993744, 35.154853],
    bounds: [35.15015, 138.989683, 35.159556, 138.997808],
  },
  {
    name: '諏訪原城跡（静岡県島田市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/suwahara',
    position: [138.119594, 34.817653],
    bounds: [34.815636, 138.116142, 34.819672, 138.123047],
  },
  {
    name: '小長谷城跡（静岡県榛原郡川根本町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/konagaya',
    position: [138.140122, 35.102372],
    bounds: [35.100622, 138.137119, 35.104122, 138.143125],
  },
  {
    name: '名古屋城跡（愛知県名古屋市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/nagoya',
    position: [136.901422, 35.182711],
    bounds: [35.173222, 136.885181, 35.1922, 136.917664],
  },
  {
    name: '伏見城跡（京都府京都市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/fushimi',
    position: [135.774539, 34.937022],
    bounds: [34.925608, 135.754967, 34.948439, 135.794111],
  },
  {
    name: '上赤坂城跡（大阪府南河内郡千早赤阪村）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kamiakazaka',
    position: [135.632267, 34.448094],
    bounds: [34.445411, 135.629972, 34.450778, 135.634564],
  },
  {
    name: '和歌山城跡（和歌山市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/wakayama',
    position: [135.172725, 34.228064],
    bounds: [34.222647, 135.163556, 34.233481, 135.181894],
  },
  {
    name: '吉田郡山城跡（広島県安芸高田市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/yoshidakoriyama',
    position: [132.709581, 34.672181],
    bounds: [34.668364, 132.703075, 34.675994, 132.716086],
  },
  {
    name: '福山城跡（広島県福山市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/fukuyama',
    position: [133.360756, 34.491256],
    bounds: [34.486531, 133.356689, 34.495981, 133.364825],
  },
  {
    name: '亀居城跡（広島県大竹市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kamei',
    position: [132.217603, 34.241161],
    bounds: [34.238483, 132.213042, 34.243842, 132.222164],
  },
  {
    name: '朝倉城（高知県高知市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/asakura',
    position: [133.479606, 33.549383],
    bounds: [33.546706, 133.475081, 33.552061, 133.484128],
  },
]
const isekiSources2 = []
const isekiLayers22 = []
const isekiGeojsonSources2 = []
const isekiGeojsonLayers2 = []
const isekiGeojsonLabelLayers2 = []
isekiurls2.forEach((url) => {
  function compareFunc(a, b) {
    return a - b
  }
  url.bounds.sort(compareFunc)
  url.bounds = [url.bounds[2], url.bounds[0], url.bounds[3], url.bounds[1]]
  isekiSources2.push({
    id: 'oh-iseki-' + url.name,
    obj: {
      type: 'raster',
      tiles: [url.url + '/{z}/{x}/{y}.png'],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 20,
      bounds: url.bounds,
    },
  })
  isekiLayers22.push({
    url: url.url,
    id: 'oh-iseki-' + url.name,
    source: 'oh-iseki-' + url.name,
    position: url.position,
    type: 'raster',
  })

  const geojson = turf.point(url.position, { name: url.name })
  isekiGeojsonSources2.push({
    id: 'oh-iseki-geojson2-' + url.name,
    obj: {
      type: 'geojson',
      data: geojson,
    },
  })
  isekiGeojsonLayers2.push({
    id: 'oh-iseki-geojson2-' + url.name,
    type: 'circle',
    source: 'oh-iseki-geojson2-' + url.name,
    paint: {
      'circle-color': '#228B22',
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10,
        8,
        13,
        0,
      ],
    },
  })
  isekiGeojsonLabelLayers2.push({
    id: 'oh-iseki-geojson2-label-' + url.name,
    type: 'symbol',
    source: 'oh-iseki-geojson2-' + url.name,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['NotoSansJP-Regular'],
      'text-offset': [0, 1],
    },
    paint: {
      'text-color': 'rgba(34, 139, 34, 1)',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: 6,
    maxzoom: 13,
  })
})
const isekiLayers222 = isekiLayers22.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.id.replace('oh-iseki-', '') + '',
    sources: [isekiSources2[i], isekiGeojsonSources2[i]],
    layers: [layer, isekiGeojsonLayers2[i], isekiGeojsonLabelLayers2[i]],
    position: layer.position,
    flyZoom: 17,
    attribution:
      '<a href="https://www.geospatial.jp/ckan/dataset/iseki" target="_blank">全国遺跡立体図（奈良文化財研究所）</a>',
    credit: '全国遺跡立体図（奈良文化財研究所）',
  }
})
// 遺跡立体図-----------------------------------------------------------------------------------------------------------
const isekiurls = [
  {
    name: '摩耶山城',
    url: 'https://kenzkenz.net/hyougoiseki/mayasanjyou',
    position: [135.20985199593505, 34.72741190512792],
    bounds: [135.20105045211955, 34.73279014407757, 135.21883391747724, 34.722107316142726],
  },
  {
    name: '滝山城',
    url: 'https://kenzkenz.net/hyougoiseki/takiyamajyou',
    position: [135.1897115742397, 34.708324634323816],
    bounds: [135.1811636521441, 34.71356094319536, 135.1989171157232, 34.70295673805569],
  },
  {
    name: '山下城',
    url: 'https://kenzkenz.net/hyougoiseki/yamashitajyou',
    position: [135.4093350403387, 34.900789307859384],
    bounds: [135.40595830756104, 34.90297874530522, 135.41291561094295, 34.898833833523184],
  },
  {
    name: '有子山城',
    url: 'https://kenzkenz.net/hyougoiseki/arikoyamajyou',
    position: [134.87834538212564, 35.45526548044384],
    bounds: [134.86921146149115, 35.46161227722132, 134.88782312819717, 35.450662087068324],
  },
  {
    name: '八木城',
    url: 'https://kenzkenz.net/hyougoiseki/yagijyou',
    position: [134.71133629505152, 35.389083335934004],
    bounds: [134.70144039714773, 35.39450401424877, 134.72006291468165, 35.38357515514262],
  },
  {
    name: '岩尾城',
    url: 'https://kenzkenz.net/hyougoiseki/iwaojyou',
    position: [134.97477380615132, 35.09959197988046],
    bounds: [134.97054375114186, 35.10349471085739, 134.97715122438717, 35.09571185635595],
  },
  {
    name: '黒井城',
    url: 'https://kenzkenz.net/hyougoiseki/kuroijyou',
    position: [135.104463141811, 35.17901861202279],
    bounds: [135.09125485809108, 35.186551363082145, 135.11452796371236, 35.17272942931801],
  },
  {
    name: '篠山城',
    url: 'https://kenzkenz.net/hyougoiseki/sasayamajyou',
    position: [135.2176231845597, 35.07313601425689],
    bounds: [135.21067062750097, 35.07723659438874, 135.22457719282704, 35.06895762382021],
  },
  {
    name: '八上城',
    url: 'https://kenzkenz.net/hyougoiseki/yakamijyou',
    position: [135.2552485425144, 35.06204731412491],
    bounds: [135.24767699390202, 35.067020572658194, 135.26391061635007, 35.05735417422389],
  },
  {
    name: '洲本城',
    url: 'https://kenzkenz.net/hyougoiseki/sumotojyou',
    position: [134.90319944266878, 34.337478918073074],
    bounds: [134.89626883902872, 34.34139875471037, 134.90778467141337, 34.334485389039344],
  },
  {
    name: '白巣城',
    url: 'https://kenzkenz.net/hyougoiseki/shirasujyou',
    position: [134.8259033029899, 34.38150414125458],
    bounds: [134.82013715493042, 34.3853353447405, 134.83166360685556, 34.378462801992896],
  },
  {
    name: '由良古城',
    url: 'https://kenzkenz.net/hyougoiseki/yurakojyou',
    position: [134.9480666440312, 34.2959981627496],
    bounds: [134.9442422389984, 34.29915852698697, 134.9512267112732, 34.29196133115981],
  },
  {
    name: '炬口城',
    url: 'https://kenzkenz.net/hyougoiseki/takenokuchijyou',
    position: [134.8898952135649, 34.35267276116555],
    bounds: [134.8877334397722, 34.35487280694649, 134.8968472599871, 34.34943863860731],
  },
  {
    name: '竹田城・観音寺山城',
    url: 'https://kenzkenz.net/hyougoiseki/takedajyou',
    position: [134.82901156539475, 35.30050695947483],
    bounds: [134.82715461493302, 35.30456725526851, 134.83369077542886, 35.29689752045901],
  },
  {
    name: '有岡城',
    url: 'https://kenzkenz.net/hyougoiseki/ariokajyou',
    position: [135.42095254641023, 34.78123344214727],
    bounds: [135.4114554147389, 34.78669658496949, 135.42611879302478, 34.771058180218404],
  },
  {
    name: '感状山城',
    url: 'https://kenzkenz.net/hyougoiseki/kanjyousanjyou',
    position: [134.45129592319765, 34.88099338453634],
    bounds: [134.44783080193332, 34.882416265679495, 134.4548245213308, 34.878333487443555],
  },
  {
    name: '鶏籠山城（龍野古城）',
    url: 'https://kenzkenz.net/hyougoiseki/keirousanjyou',
    position: [134.54521904569506, 34.87164477770631],
    bounds: [134.5419758472067, 34.87526787552183, 134.5485310782828, 34.86757240459204],
  },
  {
    name: '三木城',
    url: 'https://kenzkenz.net/hyougoiseki/mikijyou',
    position: [134.98743099511933, 34.79910988651466],
    bounds: [134.98231945734608, 34.80123645077782, 134.9962428841048, 34.79304042076464],
  },
  {
    name: '篠の丸城',
    url: 'https://kenzkenz.net/hyougoiseki/sasanomarujyou',
    position: [134.53574276742006, 35.01356494052959],
    bounds: [134.5306233267566, 35.016378648842476, 134.5399937281045, 35.010809782033775],
  },
  {
    name: '小谷城',
    url: 'https://kenzkenz.net/hyougoiseki/kodanijyou',
    position: [134.8319333497427, 34.945512107423895],
    bounds: [134.82939862060977, 34.948400543391585, 134.8343025174825, 34.94266854256047],
  },
  {
    name: '上月城',
    url: 'https://kenzkenz.net/hyougoiseki/koudukijyou',
    position: [134.32357851200157, 34.97535169002647],
    bounds: [134.32030338353172, 34.97778774733989, 134.32729889143042, 34.973688782383846],
  },
  {
    name: '城山城',
    url: 'https://kenzkenz.net/hyougoiseki/kinoyamajyou',
    position: [134.52762221800918, 34.89462795246601],
    bounds: [134.52261448236823, 34.90081639479682, 134.5309124254295, 34.8911684626696],
  },
  {
    name: '仁井山城',
    url: 'https://kenzkenz.net/hyougoiseki/niisanjyou',
    position: [134.33284507693375, 34.9790563824087],
    bounds: [134.32827254655837, 34.98215771605422, 134.33763287275258, 34.976615080071795],
  },
  {
    name: '赤穂城',
    url: 'https://kenzkenz.net/hyougoiseki/akoujyou',
    position: [134.3888453601225, 34.74598150175633],
    bounds: [134.38473699411367, 34.751164323898266, 134.3914859691194, 34.743374620541005],
  },
  {
    name: '置塩城',
    url: 'https://kenzkenz.net/hyougoiseki/oshiojyou',
    position: [134.6830576345791, 34.92332845324563],
    bounds: [134.67459731119794, 34.92625928854153, 134.68621715261793, 34.91940721541867],
  },
  {
    name: '中道子山城',
    url: 'https://kenzkenz.net/hyougoiseki/tyuudoushisanjyou',
    position: [134.85475696735378, 34.83149611922066],
    bounds: [134.85120350915133, 34.834983695995945, 134.8578004701689, 34.82725278455213],
  },
  {
    name: '播磨高倉山城',
    url: 'https://kenzkenz.net/hyougoiseki/takakurayamajyou',
    position: [134.355514070868, 34.978895498696204],
    bounds: [134.34926770508866, 34.982082295662636, 134.36098090425196, 34.975282668782285],
  },
  {
    name: '白旗城',
    url: 'https://kenzkenz.net/hyougoiseki/shirohatajyou',
    position: [134.38063468592168, 34.90809762405915],
    bounds: [134.37166347576454, 34.91289369844226, 134.38810242288082, 34.90326433635215],
  },
  {
    name: '姫路城',
    url: 'https://kenzkenz.net/hyougoiseki/himejijyou',
    position: [134.69313061573334, 34.83893632057601],
    bounds: [134.68979898141345, 34.84258881669952, 134.69640995169885, 34.83491596939099],
  },
  {
    name: '明石城',
    url: 'https://kenzkenz.net/hyougoiseki/akashijyou',
    position: [134.9927713856956, 34.65286024595012],
    bounds: [134.9877140821119, 34.65792140535697, 134.9960404310877, 34.64831907000149],
  },
  {
    name: '利神城',
    url: 'https://kenzkenz.net/hyougoiseki/rikanjyou',
    position: [134.37733576935972, 35.04615261130358],
    bounds: [134.36990790872557, 35.049536077045985, 134.38151009873025, 35.04270684275602],
  },

  {
    name: '下土井城跡（相生市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/shimodoi-tile',
    position: [134.43595760697713, 34.844884088076114],
    bounds: [34.84298829198103, 134.43920742120724, 34.846779884171205, 134.43270779274704],
  },
  {
    name: '柿の木谷城跡（南あわじ市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kakinokidani-tile',
    position: [134.81087814227217, 34.28864064255846],
    bounds: [34.28527158705211, 134.8165995048252, 34.29200969806482, 134.80515677971914],
  },
  {
    name: '金鑵城跡（小野市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kanatsurube-tile',
    position: [134.90708402156795, 34.878267704837505],
    bounds: [34.87683646219006, 134.909530334671, 34.87969894748495, 134.90463770846492],
  },
  {
    name: '光明山城痕（相生市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/komyosan-tile',
    position: [134.47002262773958, 34.84724440558978],
    bounds: [34.84420930408064, 134.4752239654938, 34.85027950709892, 134.4648212899854],
  },
  {
    name: '恒屋城跡（姫路市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tsuneya-tile',
    position: [134.71672996190716, 34.947095856521244],
    bounds: [34.944050440586665, 134.72194647817182, 34.95014127245582, 134.71151344564248],
  },
  {
    name: '志知城跡（南あわじ市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/shichi-tile',
    position: [134.7455981609756, 34.301430075912634],
    bounds: [34.29874517013217, 134.75016041727295, 34.3041149816931, 134.74103590467826],
  },
  {
    name: '生野城跡（朝来市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/ikuno-tile',
    position: [134.7998680812666, 35.17147382765063],
    bounds: [35.168615394723496, 134.8047746977891, 35.17433226057776, 134.7949614647441],
  },
  {
    name: '石野氏館跡（三木市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/ishino-tile',
    position: [134.93306572481305, 34.794430883865346],
    bounds: [34.792521788625066, 134.93632510066152, 34.79633997910562, 134.9298063489645],
  },
  {
    name: '端谷城跡（神戸市西区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/hashitani-tile',
    position: [135.05132547119246, 34.72790313341303],
    bounds: [34.726469036009995, 135.05376975799234, 34.729337230816064, 135.04888118439254],
  },
  {
    name: '段ノ城跡下段（築ヶ鼻城跡、多可町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/dannoshiro-tile',
    position: [134.89669785467362, 35.08019550651794],
    bounds: [35.078287216606235, 134.899967600773, 35.08210379642965, 134.8934281085742],
  },
  {
    name: '段ノ城跡上段（多可町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/dannoshiroue-tile',
    position: [134.89957517736428, 35.087081582754834],
    bounds: [35.08304986605937, 134.90305758574328, 35.0911132994503, 134.89609276898528],
  },
  {
    name: '池谷城跡（神戸市西区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/iketani-tile',
    position: [135.02723227922036, 34.70878200965534],
    bounds: [34.7073484770029, 135.02967566398198, 34.710215542307765, 135.0247888944587],
  },
  {
    name: '中の子城跡（南あわじ市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/nakanoko-tile',
    position: [134.80418245745335, 34.25849369387224],
    bounds: [34.25647225428759, 134.80761405496435, 34.2605151334569, 134.80075085994238],
  },
  {
    name: '猪鼻城跡（洲本市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/inohana-tile',
    position: [134.87594815753798, 34.30572928978148],
    bounds: [34.303031397066555, 134.88052845603355, 34.3084271824964, 134.8713678590424],
  },
  {
    name: '釣鐘尾城跡（香美町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tsuriganeo-tile',
    position: [134.62478465970315, 35.619449661609295],
    bounds: [35.61773877165105, 134.62774155609276, 35.62116055156755, 134.62182776331358],
  },
  {
    name: '天神山城跡（加古川市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tenjinyama-tile',
    position: [134.82501551796724, 34.828073336839466],
    bounds: [34.82521424748591, 134.82990242798698, 34.83093242619302, 134.8201286079475],
  },
  {
    name: '福田城跡（神戸市西区）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/fukuda-tile',
    position: [135.04239448741896, 34.71874514957057],
    bounds: [34.71731133503643, 135.04483850777535, 34.72017896410472, 135.03995046706257],
  },
  {
    name: '法界寺山ノ上付城・朝日ヶ丘土塁（三木市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/hokaiji-tile',
    position: [134.96605257482443, 34.79175568733935],
    bounds: [34.78889091018418, 134.97094216259921, 34.794620464494514, 134.96116298704965],
  },
  {
    name: '堀井城跡・河合城跡・小堀城跡（小野市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/horii-tile',
    position: [134.9246964964473, 34.88493901697369],
    bounds: [34.88064444131104, 134.93203706078467, 34.88923359263633, 134.91735593210993],
  },
  {
    name: '満久城跡（加西市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/maku-tile',
    position: [134.8802539292252, 34.952348916121394],
    bounds: [34.95044136279089, 134.88351814514985, 34.954256469451906, 134.8769897133006],
  },
  {
    name: '湊城跡（南あわじ市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/minato-tile',
    position: [134.72839586225896, 34.32611965009076],
    bounds: [34.324100498722835, 134.73182848649716, 34.32813880145868, 134.72496323802076],
  },
  {
    name: '門村構居（多可町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kadomura-tile',
    position: [134.91990599380097, 35.126018348978484],
    bounds: [35.12363252506995, 134.9239960319977, 35.128404172887016, 134.91581595560422],
  },
  {
    name: '養宜館痕（南あわじ市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/yanagikata-tile',
    position: [134.79506796448158, 34.305191677316365],
    bounds: [34.3022273288832, 134.8001032457949, 34.30815602574953, 134.79003268316828],
  },
  {
    name: '野村城跡（加古川市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/nomura-tile',
    position: [134.92389188024887, 34.787648911997884],
    bounds: [34.78574019471035, 134.927150529112, 34.78955762928542, 134.92063323138575],
  },
  // {name:'',url:'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/-tile', position:[,],bounds:[]},

  {
    name: '吉島古墳（たつの市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/yoshima-tile',
    position: [134.54688130043553, 34.93555770118321],
    bounds: [34.93271362826281, 134.54932695739282, 34.9384017741036, 134.54443564347824],
  },
  {
    name: '玉丘古墳群（加西市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tamaoka-tile',
    position: [134.8504914852914, 34.921500394087985],
    bounds: [34.919477654884005, 134.85395175056618, 34.92352313329196, 134.84703122001662],
  },
  {
    name: '五色塚（千壺）古墳・小壺古墳（神戸市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/gosikiduka-tile',
    position: [135.0454792120451, 34.62914336740906],
    bounds: [135.04053534197493, 34.632079997356385, 135.0502499065675, 34.62626646890381],
  },
  {
    name: '五斗長垣内遺跡（淡路市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/gossakaito-tile',
    position: [134.90880718822058, 34.50434474517985],
    bounds: [34.49894678660786, 134.91799121828302, 34.50974270375183, 134.8996231581581],
  },
  {
    name: '山陽道野磨駅家（上郡町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/sanyo-tile',
    position: [134.32640583497596, 34.84832033262013],
    bounds: [34.8454805283103, 134.32884194702086, 34.85116013692998, 134.32396972293108],
  },
  {
    name: '舟木遺跡（淡路市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/funaki-tile',
    position: [134.9537192047685, 34.54259466043517],
    bounds: [34.53498964199028, 134.9602463096072, 34.55019967888007, 134.94719209992985],
  },
  {
    name: '書写山圓教寺',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/syosya-tile',
    position: [134.65541545404145, 34.892755659757974],
    bounds: [34.881371318662566, 134.6652083546048, 34.90414000085338, 134.6456225534781],
  },
  {
    name: '西条古墳群（加古川市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/saijyo-tile',
    position: [134.88559914036207, 34.78296164187521],
    bounds: [34.777260308076904, 134.89050439085932, 34.78866297567352, 134.88069388986483],
  },
  {
    name: '石の宝殿及び竜山石採石遺跡（高砂市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/ishi-tile',
    position: [134.79265339372392, 34.78044711870095],
    bounds: [34.77190021051588, 134.800001694576, 34.78899402688601, 134.7853050928718],
  },
  {
    name: '多田院（川西市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tadain-tile',
    position: [135.40334153530273, 34.860861365416184],
    bounds: [34.855426632586166, 135.41259585052342, 34.866296098246195, 135.39408722008207],
  },
  {
    name: '多田銀銅山遺跡（猪名川町）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/tadagin-tile',
    position: [135.350519435385, 34.900806255594716],
    bounds: [34.88746140289514, 135.36205541443198, 34.91415110829429, 135.338983456338],
  },
  {
    name: '大坂城石垣石丁場跡 東六甲石丁場跡（西宮市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/osakajyo-tile',
    position: [135.33905603802287, 34.76983201591138],
    bounds: [135.33676493533846, 34.77242443397971, 135.3411954120524, 34.76741401412228],
  },
  {
    name: '壇場山古墳と播磨国分寺跡（姫路市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/danjyo-tile',
    position: [134.73310188683377, 34.82141913842871],
    bounds: [34.81872667792709, 134.73770585972392, 34.82411159893032, 134.7284979139435],
  },
  {
    name: '丁瓢塚古墳（姫路市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/hisago-tile',
    position: [134.6000101473541, 34.818145009745336],
    bounds: [34.816627691284644, 134.6013135835035, 34.81966232820603, 134.59870671120473],
  },
  {
    name: '衣笠城跡（三木市）',
    url: 'https://kenzkenz4.xsrv.jp/tile/isekirittaizu/kinugasa-tile',
    position: [135.0428908139503, 34.84621030969268],
    bounds: [34.84429819857235, 135.04615450920315, 34.848122420813006, 135.03962711869744],
  },
]
const isekiSources = []
const isekiLayers = []
const isekiGeojsonSources = []
const isekiGeojsonLayers = []
const isekiGeojsonLabelLayers = []
isekiurls.forEach((url) => {
  function compareFunc(a, b) {
    return a - b
  }

  url.bounds.sort(compareFunc)
  url.bounds = [url.bounds[2], url.bounds[0], url.bounds[3], url.bounds[1]]
  isekiSources.push({
    id: 'oh-iseki-' + url.name,
    obj: {
      type: 'raster',
      tiles: [url.url + '/{z}/{x}/{y}.png'],
      tileSize: 256,
      minzoom: 0,
      maxzoom: 20,
      bounds: url.bounds,
    },
  })
  isekiLayers.push({
    id: 'oh-iseki-' + url.name,
    source: 'oh-iseki-' + url.name,
    position: url.position,
    type: 'raster',
  })

  const geojson = turf.point(url.position, { name: url.name })

  isekiGeojsonSources.push({
    id: 'oh-iseki-geojson-' + url.name,
    obj: {
      type: 'geojson',
      data: geojson,
    },
  })
  isekiGeojsonLayers.push({
    id: 'oh-iseki-geojson-' + url.name,
    type: 'circle',
    source: 'oh-iseki-geojson-' + url.name,
    paint: {
      'circle-color': 'red',
      'circle-radius': [
        'interpolate', // Zoom-based interpolation
        ['linear'],
        ['zoom'], // Use the zoom level as the input
        10,
        8,
        13,
        0,
      ],
    },
  })
  isekiGeojsonLabelLayers.push({
    id: 'oh-iseki-geojson-label-' + url.name,
    type: 'symbol',
    source: 'oh-iseki-geojson-' + url.name,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['NotoSansJP-Regular'],
      'text-offset': [0, 1],
    },
    paint: {
      'text-color': 'rgba(255, 0, 0, 1)',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: 6,
    maxzoom: 13,
  })
})
const isekiLayersMarge = isekiLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.id.replace('oh-iseki-', '') + '',
    sources: [isekiSources[i], isekiGeojsonSources[i]],
    layers: [layer, isekiGeojsonLayers[i], isekiGeojsonLabelLayers[i]],
    position: layer.position,
    flyZoom: 17,
    attribution:
      '<a href="https://www.geospatial.jp/ckan/dataset/2021-2022-hyogo-shiseki" target="_blank">兵庫県遺跡立体図</a>',
    credit: '兵庫県遺跡立体図',
  }
})
// ---------------------------------------------------------------------------------------------------------------------
// 市町村地番図
export const sicyosonChibanzuUrls = [
  // 北海道
  {
    prefname: '北海道',
    prefcode: '01',
    code: '01205',
    name: '室蘭市',
    chiban: ['get', '地番'],
    bbox: [140.932617, 42.281818, 141.051931, 42.394547],
    position: [140.99286678768675, 42.36472347973418],
    url: 'muroranshi',
    page: 'https://murorancity-opendata-muroran.hub.arcgis.com/datasets/f5a188ea9ae94d7abd68983eb351aeff/explore?layer=3&location=42.368631%2C140.979326%2C12.64',
  },
  {
    prefname: '北海道',
    prefcode: '01',
    code: '01234',
    name: '北広島市',
    chiban: ['concat', ['get', 'Chiban'], '-', ['get', 'Edaban']],
    bbox: [141.442421, 42.903004, 141.602161, 43.003404],
    position: [141.56311494973653, 42.98537981878215],
    url: 'kitahiroshimashi2',
    page: 'https://www.harp.lg.jp/opendata/dataset/2061.html',
  },
  {
    prefname: '北海道',
    prefcode: '01',
    code: '01204',
    name: '旭川市',
    chiban: ['get', 'TXTCD'],
    bbox: [142.213058, 43.568596, 142.586277, 43.925816],
    position: [142.36538083068262, 43.77140401060319],
    url: 'asahikawashi',
    page: 'https://www.city.asahikawa.hokkaido.jp/kurashi/112/113/116/d080184.html',
  },
  {
    prefname: '北海道',
    prefcode: '01',
    code: '01395',
    name: 'ニセコ町',
    chiban: ['get', '地番'],
    bbox: [140.626111, 42.738333, 140.841667, 42.911111],
    position: [140.68806409835815, 42.80495731522012],
    url: 'nisekocyo',
    page: 'https://www.harp.lg.jp/opendata/dataset/1750.html',
  },
  {
    prefname: '北海道',
    prefcode: '01',
    code: '01631',
    name: '音更町',
    chiban: ['get', 'chiban'],
    bbox: [143.05, 42.85, 143.35, 43.05],
    position: [143.19816291534738, 42.99393583189044],
    url: 'otohukechyo',
    page: 'https://www.harp.lg.jp/opendata/dataset/2035.html',
  },
  // 宮城県
  {
    prefname: '宮城県',
    prefcode: '04',
    code: '04100',
    name: '仙台市',
    chiban: ['get', 'DNO'],
    bbox: [140.7025, 38.144722, 140.999444, 38.412222],
    position: [140.86946408465997, 38.26816199999661],
    url: 'sendaishi',
    page: 'https://www.geospatial.jp/ckan/dataset/chibanzu',
  },
  // 秋田県
  {
    prefname: '秋田県',
    prefcode: '05',
    code: '05209',
    name: '鹿角市',
    chiban: ['get', '地番'],
    bbox: [140.626111, 40.109167, 140.841667, 40.215778],
    position: [140.7886727460321, 40.21559737412008],
    url: 'kazunoshi',
    page: 'https://www.city.kazuno.lg.jp/soshiki/somu/digital/gyomu/opendata/9788.html',
  },
  // 山形県
  {
    prefname: '山形県',
    prefcode: '06',
    code: '06363',
    name: '舟形町',
    chiban: ['get', 'TIBAN'],
    bbox: [140.1875, 38.6375, 140.3875, 38.7875],
    position: [140.32022099999745, 38.691255021073715],
    url: 'funagatamachi',
    page: 'https://www.town.funagata.yamagata.jp/s012/opendata/010/010/20230711101613.html',
  },
  // 福島県
  {
    prefname: '福島県',
    prefcode: '07',
    code: '07201',
    name: '福島市',
    chiban: ['get', 'TXTCD'],
    bbox: [140.3801617, 37.699613, 140.469673, 37.7430938],
    position: [140.47460922099737, 37.76082999999447],
    url: 'fukushimashi2',
    page: 'https://www.city.fukushima.fukushima.jp/d-kikaku/shise/opendate/machidukuri.html',
  },
  // 茨城県
  {
    prefname: '茨城県',
    prefcode: '08',
    code: '08223',
    name: '潮来市',
    chiban: ['get', '地番'],
    bbox: [140.5, 35.9, 140.6, 36.0],
    position: [140.55540531603634, 35.947116226224],
    url: 'itakoshi',
    page: 'https://www.city.itako.lg.jp/opendata.php',
  },
  {
    prefname: '茨城県',
    prefcode: '08',
    code: '08564',
    name: '利根町',
    chiban: ['get', '地番'],
    bbox: [140.1, 35.85, 140.2, 35.95],
    position: [140.1391729153425, 35.85756030920088],
    url: 'tonechyo',
    page: 'https://www.town.tone.ibaraki.jp/opendata.php',
  },
  // 群馬県
  {
    prefname: '群馬県',
    prefcode: '10',
    code: '10202',
    name: '高崎市',
    chiban: ['get', '地番_地番図_label'],
    bbox: [138.816667, 36.183333, 139.116667, 36.416667],
    position: [139.00336683069094, 36.32182569208747],
    url: 'takasakishi',
    page: 'https://www.city.takasaki.gunma.jp/page/3130.html',
  },
  // 埼玉県
  {
    prefname: '埼玉県',
    prefcode: '11',
    code: '11222',
    name: '越谷市',
    chiban: ['concat', ['get', '本番'], '-', ['get', '枝番']],
    bbox: [139.75, 35.85, 139.85, 35.95],
    position: [139.79105245767255, 35.890609232668695],
    url: 'koshigayashi',
    page: 'https://www.city.koshigaya.saitama.jp/kurashi_shisei/kurashi/zeikin/koteisisan_tosikeikaku/tibanzu_opendate.html',
  },
  {
    prefname: '埼玉県',
    prefcode: '11',
    code: '11218',
    name: '深谷市',
    chiban: ['concat', ['get', '本番'], '-', ['get', '枝番']],
    bbox: [138.983333, 36.1, 139.25, 36.283333],
    position: [139.281707999998, 36.197104580699204],
    url: 'fukayashi',
    page: 'https://opendata.pref.saitama.lg.jp/resources/6204',
  },
  // 東京都
  {
    prefname: '東京都',
    prefcode: '13',
    code: '13209',
    name: '町田市',
    chiban: ['get', 'CHIBAN'],
    bbox: [139.266667, 35.5, 139.516667, 35.616667],
    position: [139.4387823343277, 35.546591812173475],
    url: 'machidashi',
    page: 'https://www.city.machida.tokyo.jp/shisei/opendata/chizujoho/chisekizu.html',
  },
  {
    prefname: '東京都',
    prefcode: '13',
    code: '13211',
    name: '小平市',
    chiban: ['get', '表示地番'],
    bbox: [139.45, 35.71, 139.52, 35.75],
    position: [139.47753045767575, 35.728369484747276],
    url: 'kodairashi',
    page: 'https://www.city.kodaira.tokyo.jp/kurashi/111/111978.html',
  },
  {
    prefname: '東京都',
    prefcode: '13',
    code: '13213',
    name: '東村山市',
    chiban: ['get', 'TXTCD'],
    bbox: [139.44, 35.74, 139.5, 35.78],
    position: [139.4687322037018, 35.75465000000037],
    url: 'higashimurayamashi',
    page: 'https://catalog.data.metro.tokyo.lg.jp/dataset/t132136d0000000005',
  },
  {
    prefname: '東京都',
    prefcode: '13',
    code: '13215',
    name: '国立市',
    chiban: ['get', '表示文字列'],
    bbox: [139.42, 35.67, 139.46, 35.71],
    position: [139.44141245767452, 35.68379371055923],
    url: 'kunitachishi3',
    page: 'https://www.city.kunitachi.tokyo.jp/soshiki/Dept01/Div03/Sec01/oshirase/11883.html',
  },
  // 山梨県
  {
    prefname: '山梨県',
    prefcode: '19',
    code: '19201',
    name: '甲府市',
    chiban: ['get', '地番'],
    bbox: [138.5, 35.6, 138.65, 35.7],
    position: [138.56829800000241, 35.66205666308586],
    url: 'kofushi',
    page: 'https://kofu.geocloud.jp/webgis/?p=0&bt=0&mp=337-1&',
  },
  // 静岡県
  {
    prefname: '静岡県',
    prefcode: '22',
    code: '22100',
    name: '静岡市',
    chiban: ['get', '地番'],
    bbox: [138.08306, 34.89861, 138.63944, 35.64583],
    position: [138.38294267724638, 34.974974010631584],
    url: 'shizuokashi',
    page: 'https://dataset.city.shizuoka.jp/dataset/1707986930/resource/4a40cc33-0aef-4426-825e-3b034347812b',
  },
  {
    prefname: '静岡県',
    prefcode: '22',
    code: '22211',
    name: '磐田市',
    chiban: ['get', 'TXTCD'],
    bbox: [137.8, 34.65, 137.95, 34.8],
    position: [137.85162388532535, 34.7178716931619],
    url: 'iwatashi',
    page: 'https://www.city.iwata.shizuoka.jp/shiseijouhou/1006207/1002775.html',
  },
  // 愛知県
  {
    prefname: '愛知県',
    prefcode: '23',
    code: '23100',
    name: '名古屋市',
    chiban: ['get', 'TXTCD'],
    bbox: [136.8, 35.0, 137.05, 35.3],
    position: [136.9066772883615, 35.1812626194769],
    url: 'nagoyashi',
    page: 'https://data.bodik.jp/dataset/231002_0703030000_chibansankouzu',
  },
  {
    prefname: '愛知県',
    prefcode: '23',
    code: '23205',
    name: '半田市',
    chiban: ['concat', ['get', '番地'], '-', ['get', '枝番'], '-', ['get', '小枝']],
    bbox: [136.9, 34.85, 137.05, 35.0],
    position: [136.93819204686196, 34.891670467553226],
    url: 'handashi',
    page: 'https://www.city.handa.lg.jp/opendata/1005557/1005561/1004329.html',
  },
  // 京都府
  {
    prefname: '京都府',
    prefcode: '26',
    code: '26100',
    name: '京都市',
    chiban: ['get', 'TIBAN'],
    bbox: [135.55, 34.866667, 135.866667, 35.316667],
    position: [135.76794033862097, 35.011458104660534],
    url: 'kyotoshi',
    page: 'https://data.city.kyoto.lg.jp/resource/?id=18537',
  },
  {
    prefname: '京都府',
    prefcode: '26',
    code: '26207',
    name: '城陽市',
    chiban: ['get', 'chiban'],
    bbox: [135.74946, 34.8216, 135.84821, 34.87],
    position: [135.7800419153457, 34.85309321719859],
    url: 'jyoyoshi',
    page: 'https://data.bodik.jp/dataset/262072_koteishisanzeichibanzu/resource/8f5c8de3-f574-45b4-9291-3791ffa41407',
  },
  {
    prefname: '京都府',
    prefcode: '26',
    code: '26209',
    name: '長岡京市',
    chiban: ['get', '表示文字列'],
    bbox: [135.6806947, 34.921, 135.71, 34.94],
    position: [135.69535828836183, 34.926424814234565],
    url: 'nagaokakyoshi',
    page: 'https://www.city.nagaokakyo.lg.jp/0000012991.html',
  },
  {
    prefname: '京都府',
    prefcode: '26',
    code: '26303',
    name: '大山崎町',
    chiban: ['get', '所在'],
    bbox: [135.65, 34.88, 135.7, 34.93],
    position: [135.68867837302201, 34.902743804081595],
    url: 'oyamasakichyo',
    page: 'https://www.town.oyamazaki.kyoto.jp/annai/zeijyuminka/zeimukakari/chozei/koteisisanzei/5857.html',
  },
  // 大阪府
  {
    prefname: '大阪府',
    prefcode: '27',
    code: '27202',
    name: '岸和田市',
    chiban: ['get', 'CHIBAN'],
    bbox: [135.34, 34.43, 135.46, 34.54],
    position: [135.37090737539307, 34.46054514396512],
    url: 'kishiwadashi',
    page: 'https://www.city.kishiwada.osaka.jp/soshiki/16/tochi-chibankrnnsakuichiranhyou.html',
  },
  {
    prefname: '大阪府',
    prefcode: '27',
    code: '27228',
    name: '泉南市',
    chiban: ['get', '表示文字列'],
    bbox: [135.16, 34.33, 135.25, 34.4],
    position: [135.2734460647299, 34.36591222577401],
    url: 'sennanshi',
    page: 'https://www.city.sennan.lg.jp/kakuka/soumu/zeimuka/kazeikakari/zeikin/koteishisan/1505179465186.html',
  },
  {
    prefname: '大阪府',
    prefcode: '27',
    code: '27203',
    name: '豊中市',
    chiban: ['get', 'TEXTCODE1'],
    bbox: [135.42, 34.75, 135.52, 34.82],
    position: [135.4697598501699, 34.78130126564767],
    url: 'toyonakashi',
    page: 'https://data.bodik.jp/dataset/272035_chibansanko/resource/c5858297-c312-489f-b244-885074c70daa',
  },
  // 兵庫県
  // {prefname:'', prefcode:'', code:'28201, name:'姫路市', chiban: ['get', 'TXTCD'], position:[134.68545471163947,34.815118449686565], url:'himejishi', page:''},
  // {prefname:'兵庫県', prefcode:'28', code:'28204', name:'西宮市', chiban: ['get', 'TXTCD'], position:[135.34190737301537,34.73766946698089], url:'nishinomiyashi', page:'https://opendata.nishi.or.jp/opendata/ResultDetail.php?id=69'},
  {
    prefname: '兵庫県',
    prefcode: '28',
    code: '28207',
    name: '伊丹市',
    chiban: ['get', '所在地番'],
    bbox: [135.35, 34.75, 135.46, 34.82],
    position: [135.40037589806093, 34.784457867420755],
    url: 'itamishi',
    page: 'https://www.geospatial.jp/ckan/dataset/202401/resource/ec8eb484-66e4-40a7-bf05-248476b0d61d',
  },
  {
    prefname: '兵庫県',
    prefcode: '28',
    code: '28210',
    name: '加古川市',
    chiban: ['get', 'TXTCODE1'],
    bbox: [134.8, 34.7, 134.95, 34.85],
    position: [134.84064926983825, 34.75678296665963],
    url: 'kakogawashi',
    page: 'https://opendata-api-kakogawa.jp/ckan/dataset/landhouse/resource/c37f9b63-54c8-4094-9e48-8d3817918476',
  },
  {
    prefname: '兵庫県',
    prefcode: '28',
    code: '28217',
    name: '川西市',
    chiban: ['get', '地番'],
    bbox: [135.35, 34.8, 135.5, 34.95],
    position: [135.4173282883612, 34.82989631871767],
    url: 'kawanishishi',
    page: 'https://www.city.kawanishi.hyogo.jp/opendata/1017548/1009883/1016648.html',
  },
  {
    prefname: '兵庫県',
    prefcode: '28',
    code: '28501',
    name: '佐用町',
    chiban: ['get', 'CHIBAN'],
    bbox: [134.3, 34.95, 134.5, 35.15],
    position: [134.35598183068578, 35.004205695261106],
    url: 'sayochyo',
    page: 'https://www.town.sayo.lg.jp/cms-sypher/www/service/detail.jsp?id=9343',
  },
  // 奈良県
  {
    prefname: '奈良県',
    prefcode: '29',
    code: '29201',
    name: '奈良市',
    chiban: ['concat', ['get', '地番本番'], '-', ['get', '地番枝番'], '-', ['get', '地番小枝']],
    bbox: [135.7, 34.6, 135.95, 34.8],
    position: [135.80463216931253, 34.684824554866864],
    url: 'narashi2',
    page: 'https://www.city.nara.lg.jp/soshiki/14/104605.html',
  },
  {
    prefname: '奈良県',
    prefcode: '29',
    code: '29210',
    name: '香芝市',
    chiban: ['get', '地番名称'],
    bbox: [135.65, 34.51, 135.73, 34.57],
    position: [135.69894062698785, 34.5410982491169],
    url: 'kashibashi',
    page: 'https://www.city.kashiba.lg.jp/soshiki/5/31946.html',
  },
  // 岡山県
  {
    prefname: '岡山県',
    prefcode: '33',
    code: '33643',
    name: '西粟倉村',
    chiban: ['get', 'TIBAN'],
    bbox: [134.3, 35.1, 134.5, 35.25],
    position: [134.33562862698358, 35.171553518278486],
    url: 'nishiawakuramura',
    page: 'https://www.vill.nishiawakura.okayama.jp/open_data/others/index.html',
  },
  // 広島県
  {
    prefname: '広島県',
    prefcode: '34',
    code: '34207',
    name: '福山市',
    chiban: ['get', '地番'],
    bbox: [133.2, 34.35, 133.55, 34.65],
    position: [133.36252445767389, 34.485761252632884],
    url: 'fukuyamashi',
    page: 'https://data.city.fukuyama.hiroshima.jp/dataset/digital_numbers_map/resource/93328fe8-64fb-4321-9964-84eab4f68fcd',
  },
  // 香川県
  {
    prefname: '香川県',
    prefcode: '37',
    code: '37203',
    name: '坂出市',
    chiban: ['concat', ['get', '所在地番3'], '-', ['get', '所在地番5']],
    bbox: [133.8, 34.25, 134.0, 34.4],
    position: [133.8605011373076, 34.3166967696347],
    url: 'sakaideshi',
    page: 'https://www.city.sakaide.lg.jp/soshiki/kouminrenkei/opendata.html',
  },
  {
    prefname: '香川県',
    prefcode: '37',
    code: '37204',
    name: '善通寺市',
    chiban: ['concat', ['get', '本番'], '-', ['get', '枝番'], '-', ['get', '孫番']],
    bbox: [133.74, 34.21, 133.8, 34.26],
    position: [133.78706908666885, 34.22705330992301],
    url: 'zentujishi',
    page: 'https://opendata.pref.kagawa.lg.jp/dataset/701.html',
  },
  // 福岡県
  // {prefname:'福岡県', prefcode:'40', code:'40130', name:'福岡市', chiban: ['get', '地番'], bbox:[130.200000, 33.500000, 130.500000, 33.750000], position:[130.40177928836357,33.5897855042562], url:'fukuokashi2', page:'https://webmap.city.fukuoka.lg.jp/fukuoka/OpenData?mids=&pno=1'},
  {
    prefname: '福岡県',
    prefcode: '40',
    code: '40204',
    name: '直方市',
    chiban: ['get', '地番'],
    bbox: [130.65, 33.7, 130.8, 33.78],
    position: [130.72966337301625, 33.743983314699236],
    url: 'nogatashi',
    page: 'https://data.bodik.jp/dataset/402044_land_lot_correspondence_map/resource/3e57cf4e-a84b-4a6a-adba-fd27013531c7',
  },
  // 佐賀県
  {
    prefname: '佐賀県',
    prefcode: '41',
    code: '41202',
    name: '唐津市',
    chiban: ['get', 'SAFIELD001'],
    bbox: [129.8, 33.3, 130.1, 33.6],
    position: [129.9677463730212, 33.44981990361434],
    url: 'karatsushi',
    page: 'https://www.open-data-portal.jp/opendata/dataset/chiban',
  },
  // 長崎県
  {
    prefname: '長崎県',
    prefcode: '42',
    code: '42307',
    name: '長与町',
    chiban: ['get', '地番'],
    bbox: [129.84, 32.8, 129.9, 32.85],
    position: [129.87507937301513, 32.82524812594376],
    url: 'nagayochyo',
    page: 'https://data.bodik.jp/dataset/423076_tibansankouzu/resource/580da941-74d1-4ddd-a0f0-fb6b88fc793a',
  },
]
function chibanzuLayersCreate(url) {
  let sourceLayer
  if (url.name === '高崎市') {
    sourceLayer = 'oh3'
  } else {
    sourceLayer = 'chibanzu'
  }
  const chibanzuSource = {
    id: 'oh-chibanzu-' + url.name + '-source',
    obj: {
      type: 'vector',
      url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/' + url.url + '.pmtiles',
      tileSize: 512, // タイルサイズを適切に設定
    },
    // minzoom: 11
    minzoom: 13,
  }
  const chibanzuLayer = {
    id: 'oh-chibanzu-' + url.name,
    source: 'oh-chibanzu-' + url.name + '-source',
    type: 'fill',
    'source-layer': sourceLayer,
    'fill-color': 'rgba(0,0,0,0)',
    position: url.position,
    page: url.page,
    name: `${url.prefname}-${url.name}`,
  }
  const chibanzuLayerLine = {
    id: 'oh-chibanzu-line-' + url.name,
    source: 'oh-chibanzu-' + url.name + '-source',
    type: 'line',
    'source-layer': sourceLayer,
    paint: {
      'line-color': 'blue',
      'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
    },
  }
  const chibanzuLayerLineGreen = {
    id: 'oh-chibanzu-line-' + url.name,
    source: 'oh-chibanzu-' + url.name + '-source',
    type: 'line',
    'source-layer': sourceLayer,
    paint: {
      'line-color': 'green',
      'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
    },
    // minzoom: 11
    minzoom: 13,
  }
  const chibanzuLayerLabel = {
    id: 'oh-chibanzu-label-' + url.name,
    type: 'symbol',
    source: 'oh-chibanzu-' + url.name + '-source',
    'source-layer': sourceLayer,
    layout: {
      'text-field': url.chiban,
    },
    paint: {
      'text-color': 'navy',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    maxzoom: 24,
    minzoom: 15,
  }
  const chibanzuLayerLabelGreen = {
    id: 'oh-chibanzu-label-green-' + url.name,
    type: 'symbol',
    source: 'oh-chibanzu-' + url.name + '-source',
    'source-layer': sourceLayer,
    layout: {
      'text-field': url.chiban,
    },
    paint: {
      'text-color': 'green',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    maxzoom: 24,
    minzoom: 15,
  }
  // 頂点レイヤー（現在は非表示）
  const chibanzuLayerVertex = {
    id: 'oh-chibanzu-vertex-' + url.name,
    type: 'circle',
    source: 'oh-chibanzu-' + url.name + '-source',
    'source-layer': sourceLayer,
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
      'circle-color': 'green',
    },
  }
  const chibanzuLayerVertexGreen = {
    id: 'oh-chibanzu-vertex-green-' + url.name,
    type: 'circle',
    source: 'oh-chibanzu-' + url.name + '-source',
    'source-layer': sourceLayer,
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
      'circle-color': 'green',
    },
  }
  // ここの filter: ['==', '$type', 'Point']が効いたり効かなかったり。様修正
  // ポイントレイヤー（現在は非表示）
  const chibanzuLayerPoint = {
    id: 'oh-chibanzu-point-' + url.name,
    type: 'circle',
    source: 'oh-chibanzu-' + url.name + '-source',
    'source-layer': sourceLayer,
    filter: ['==', '$type', 'Point'],
    layout: {
      visibility: 'none', // 常に非表示
    },
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
      'circle-color': 'green',
    },
  }
  return {
    chibanzuSource,
    chibanzuLayer,
    chibanzuLayerLine,
    chibanzuLayerLineGreen,
    chibanzuLayerLabel,
    chibanzuLayerLabelGreen,
    chibanzuLayerVertex,
    chibanzuLayerVertexGreen,
    chibanzuLayerPoint,
  }
}
export const chibanzuSources = []
export const chibanzuLayers = []
const chibanzuLayerLines = []
const chibanzuLayerLinesGreen = []
const chibanzuLayerLabel = []
const chibanzuLayerLabelGreen = []
const chibanzuLayerVertex = []
const chibanzuLayerVertexGreen = []
const chibanzuLayerPoint = []
sicyosonChibanzuUrls.forEach((url) => {
  const layers = chibanzuLayersCreate(url)
  chibanzuSources.push(layers.chibanzuSource)
  chibanzuLayers.push(layers.chibanzuLayer)
  chibanzuLayerLines.push(layers.chibanzuLayerLine)
  chibanzuLayerLinesGreen.push(layers.chibanzuLayerLineGreen)
  chibanzuLayerLabel.push(layers.chibanzuLayerLabel)
  chibanzuLayerLabelGreen.push(layers.chibanzuLayerLabelGreen)
  chibanzuLayerVertex.push(layers.chibanzuLayerVertex)
  chibanzuLayerVertexGreen.push(layers.chibanzuLayerVertexGreen)
  chibanzuLayerPoint.push(layers.chibanzuLayerPoint)
})
const chibanzuLayers2 = chibanzuLayers.map((layer, i) => {
  return {
    id: layer.id,
    label: layer.name,
    source: chibanzuSources[i],
    layers: [layer, chibanzuLayerLines[i], chibanzuLayerLabel[i]],
    attribution: '<a href="' + layer.page + '" target="_blank">' + name + '</a>',
    credit: name,
    position: layer.position,
    ext: { name: 'ext-chibanzu' },
  }
})
// 並びを様修正
const chibanzuLayers1 = [
  ...chibanzuLayers,
  ...chibanzuLayerLinesGreen,
  ...chibanzuLayerLabelGreen,
]

// 五街道----------------------------------------------------------------------------------------------------------------
const gokaidoSource = {
  id: 'gokaido-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.xsrv.jp/open-hinata/geojson/gokaido.geojson',
  },
}
const gokaidoColors = {
  '東海道': '#e63946', '東海道2': '#e63946',
  '中山道': '#457b9d', '甲州街道': '#2a9d8f',
  '日光街道': '#e9c46a', '奥州街道': '#f4a261',
}
const gokaidoLayer = {
  id: 'oh-gokaido',
  type: 'line',
  source: 'gokaido-source',
  layout: { 'line-cap': 'round', 'line-join': 'round' },
  paint: {
    'line-color': ['match', ['get', 'Name'],
      '東海道', '#e63946', '東海道2', '#e63946',
      '中山道', '#457b9d', '甲州街道', '#2a9d8f',
      '日光街道', '#e9c46a', '奥州街道', '#f4a261',
      '#e63946',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 4, 15, 6],
    'line-opacity': 0.85,
  },
}
const gokaidoLayerLabel = {
  id: 'oh-gokaido-label',
  type: 'symbol',
  source: 'gokaido-source',
  layout: {
    'symbol-placement': 'line',
    'text-field': ['get', 'Name'],
    'text-size': 12,
    'text-font': ['Open Sans Bold'],
    'symbol-spacing': 300,
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
}

// 古地図----------------------------------------------------------------------------------------------------------------
// 戦前の旧版地形図（PMTiles版・超解像プロトコル対応）
const MW5_PMTILES = 'https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/mw5/mw5.pmtiles'
const mw5TileSource = {
  id: 'mw5TileSource',
  obj: {
    type: 'raster',
    tiles: [`superres://{z}/{x}/{y}.png?pmtiles=${encodeURIComponent(MW5_PMTILES)}&maxzoom=15`],
    tileSize: 256,
    maxzoom: 17,  // z15→z17 まで超解像（4倍拡大）
  },
}
const mw5TileLayer = {
  id: 'oh-mw5-xyz',
  source: 'mw5TileSource',
  type: 'raster',
}
const mw5TileCenterSource = {
  id: 'mw5TileCenterSource',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.xsrv.jp/open-hinata/geojson/mw5centerNew.geojson',
  },
}
const mw5TileCenterLabel = {
  id: 'oh-mw5-xyz-center',
  type: 'symbol',
  source: 'mw5TileCenterSource',
  layout: {
    'text-field': ['get', 'title'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 7, 10, 10, 14, 15, 16],
    'text-allow-overlap': false,
    'text-padding': 2,
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0, 0, 0, 0.85)',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 7,
}
const mw5CenterSource = {
  id: 'mw5CenterSource',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.xsrv.jp/open-hinata/geojson/mw5centerNew.geojson',
  },
}
const mw5CenterLabel = {
  id: 'oh-mw5-center',
  type: 'symbol',
  source: 'mw5CenterSource',
  layout: {
    'text-field': ['get', 'title'],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 7,
}
// 北海道----------------------------------------------------------------------------------------------------------
const jissokuSource = {
  id: 'jissoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/jissoku/{z}/{x}/{y}.png'],
  },
}
const jissokuLayer = {
  id: 'oh-jissoku-layer',
  type: 'raster',
  source: 'jissoku-source',
}
const jissokuMatsumaeSource = {
  id: 'jissoku-matsumae-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/jissokumatsumae/{z}/{x}/{y}.png'],
    tileSize: 256,
  },
}
const jissokuMatsumaeLayer = {
  id: 'oh-jissoku-matsumae-layer',
  type: 'raster',
  source: 'jissoku-matsumae-source',
}
// 東西蝦夷山川地理取調図-------------------------------------------------------------------------------
const ezosansenSource = {
  id: 'ezosansen-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/hokkaidou/touzai/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const ezosansenLayer = {
  id: 'oh-ezosansen-layer',
  type: 'raster',
  source: 'ezosansen-source',
}
const ezosansen2Source = {
  id: 'ezosansen2-source',
  obj: {
    type: 'raster',
    tiles: ['https://koukita.github.io/touzaiezo/tile/{z}/{x}/{y}.jpg'],
  },
}
const ezosansen2Layer = {
  id: 'oh-ezosansen2-layer',
  type: 'raster',
  source: 'ezosansen2-source',
}
// 迅速測図----------------------------------------------------------------------------------------------
const jinsokuSource = {
  id: 'jinsoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://boiledorange73.sakura.ne.jp/ws/tile/Kanto_Rapid-900913/{z}/{x}/{y}.png'],
  },
}
const jinsokuLayer = {
  id: 'oh-jinsoku',
  type: 'raster',
  source: 'jinsoku-source',
}
// ---------------------------------------------------------------------------------------------------------------------
// syochiikiソース
const syochiikiSource = {
  id: 'syochiikiSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/syochiiki/syochiiki.pmtiles',
    attribution: '<a href="https://github.com/shiwaku/mlit-plateau-bldg-pmtiles">mlit-plateau-bldg-pmtiles</a>',
  },
}
const syochiikiLayer = {
  id: 'oh-syochiiki-layer',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  type: 'fill',
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',
  },
}
const syochiikLayerLine = {
  id: 'oh-syochiiki-line',
  type: 'line',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'red',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      4,
      0.2,
      6,
      0.5,
      8,
      0.7,
      11,
      1.0,
      12,
      1.5,
      14,
      2,
      16,
      3,
      18,
      6,
      30,
      10,
    ],
  },
}
const syochiikLayerLine2 = {
  id: 'oh-syochiiki-line',
  type: 'line',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'black',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      11,
      2.0,
      12,
      3,
      14,
      4,
      16,
      6,
      18,
      12,
      30,
      14,
    ],
  },
}
const syochiikiLayerLabel = {
  id: 'oh-syochiiki-label',
  type: 'symbol',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['format', ['get', 'S_NAME'], {}, '\n', {}, ['get', 'JINKO'], { 'font-scale': 1.2 }, '人', {}],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0.5, 0],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
const syochiikiLayerLabel2 = {
  id: 'oh-syochiiki-label',
  type: 'symbol',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['format', ['get', 'S_NAME'], {}],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0.5, 0],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
const syochiikiLayerHeight = {
  id: 'oh-syochiiki-height',
  type: 'fill-extrusion',
  source: 'syochiikiSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['/', ['get', 'JINKO'], ['get', 'AREA']], 0, 100, 0.2, 10000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['/', ['get', 'JINKO'], ['get', 'AREA']],
      0,
      'silver',
      0.1,
      'black',
    ],
  },
}
// 高速道路--------------------------------------
const highwaySource = {
  id: 'highwaySource',
  obj: {
    // 'type': 'geojson',
    // 'data': require('@/assets/json/highway_sections_2024.geojson'),
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kosoku/kosoku.pmtiles',
  },
}
const highwayLayerGreen = {
  id: 'oh-highwayLayer-green-lines',
  type: 'line',
  source: 'highwaySource',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#007356',
    'line-width': 5,
    'line-blur': 0.8,
    'line-opacity': 1,
  },
  filter: ['<', 'N06_002', 2024],
}
const highwayLayerRed = {
  id: 'oh-highwayLayer-red-lines',
  type: 'line',
  source: 'highwaySource',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#FF0000',
    'line-width': 5,
    'line-blur': 0.8,
    'line-opacity': 1,
  },
  filter: ['==', 'N06_002', 2024],
}
// バス--------------------------------------
const busSource = {
  id: 'bus-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/bus/bus-h.pmtiles',
  },
}
const busteiSource = {
  id: 'bustei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/bus/bustei-h.pmtiles',
  },
}
const busLayer = {
  id: 'oh-bus-lines',
  type: 'line',
  source: 'bus-source',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    // 'line-color': ['get', 'random_color'],
    'line-color': ['get', 'random_color_hash'],
    'line-blur': 0.8,
    'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 11, 5],
  },
}
const busteiLayer = {
  id: 'oh-bus-points',
  type: 'circle',
  source: 'bustei-source',
  'source-layer': 'point',
  paint: {
    // 'circle-color': 'dodgerblue',
    'circle-color': ['get', 'random_color_hash'],
    // 'circle-color': [
    //     'match',
    //     ['slice', ['get', 'P11_004_01'], 0, 1], // 最初の1文字を取得
    //     '1', '#ff0000', // 最初の1文字が '"1' の場合の色 路線バス（民間）
    //     '2', '#00ff00', // 最初の1文字が '"2' の場合の色 路線バス（公営）
    //     '3', '#0000ff', // 最初の1文字が '"3' の場合の色 コミュニティバス
    //     '4', '#ffff00', // 最初の1文字が '"4' の場合の色
    //     '5', '#ff00ff', // 最初の1文字が '"5' の場合の色
    //     '#ffffff' // マッチしない場合のデフォルト色
    // ],
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      12,
      0,
      13,
      10,
      // 6, 0.1,
      // 13, 10
    ],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
const busteiLayerLabel = {
  id: 'oh-bus-label',
  type: 'symbol',
  source: 'bustei-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P11_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.5,
  },
  maxzoom: 24,
  minzoom: 13,
}
// 鉄道--------------------------------------
const tetsudoSource = {
  id: 'tetsudo-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tetsudo/tetsudo-r6.pmtiles',
  },
}
const tetsudoLayerRed = {
  id: 'oh-tetsudo-red-lines',
  type: 'line',
  source: 'tetsudo-source',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': 'orangered',
    'line-blur': 0.8,
    // 'line-width': 5,
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      3,
      11,
      5,
    ],
  },
}
const tetsudoLayerBlue = {
  id: 'oh-tetsudo-blue-lines',
  type: 'line',
  source: 'tetsudo-source',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': 'dodgerblue',
    'line-blur': 0.8,
    // 'line-width': 5,
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      3,
      11,
      5,
    ],
  },
  filter: ['==', 'N05_005e', '9999'],
}
const tetsudoLayerPointRed = {
  id: 'oh-tetsudo-points-red',
  type: 'circle',
  source: 'tetsudo-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'orangered',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 10],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
const tetsudoLayerPointBlue = {
  id: 'oh-tetsudo-points-blue',
  type: 'circle',
  source: 'tetsudo-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'dodgerblue',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 10],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
  filter: ['==', 'N05_005e', '9999'],
}
const tetsudoLayerLabel = {
  id: 'oh-tetsudo-label',
  type: 'symbol',
  source: 'tetsudo-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'N05_011'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.5,
  },
  maxzoom: 24,
  minzoom: 13,
}
// 鉄道時系列--------------------------------------
const tetsudojikeiretsuSource = {
  id: 'tetsudojikeiretsu-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tetsudo/tetsudo-r6.pmtiles',
  },
}
const tetsudojikeiretsuLayerPoint = {
  id: 'oh-tetsudojikeiretsu-points',
  type: 'circle',
  source: 'tetsudojikeiretsu-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'white', // 固定の赤色
    'circle-radius': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      12,
      0,
      13,
      10,
    ],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
  filter: [
    'all',
    ['<=', ['number', ['to-number', ['get', 'N05_005b']]], 2024],
    ['>=', ['number', ['to-number', ['get', 'N05_005e']]], 2024],
  ],
}
const tetsudojikeiretsuLayerBlue = {
  id: 'oh-tetsudojikeiretsu-blue-lines',
  type: 'line',
  source: 'tetsudojikeiretsu-source',
  'source-layer': 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': 'dodgerblue',
    'line-blur': 0.8,
    // 'line-width': 5,
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      3,
      11,
      5,
    ],
  },
  filter: [
    'all',
    ['<=', ['number', ['to-number', ['get', 'N05_005b']]], 2024],
    ['>=', ['number', ['to-number', ['get', 'N05_005e']]], 2024],
  ],
}
// export const tetsudojikeiretsuLayerRed = {
//     'id': 'oh-tetsudojikeiretsu-red-lines',
//     'type': 'line',
//     'source': 'tetsudojikeiretsu-source',
//     "source-layer": "line",
//     'layout': {
//         'line-join': 'round',
//         'line-cap': 'round'
//     },
//     'paint': {
//         'line-color': '#FF0000',
//         'line-width': 5,
//         'line-blur': 0.8,
//         'line-opacity':1
//     },
//     'filter': ['==', 'N06_002', 2024]
// }
// 標準地図--------------------------------------------------------------------------------------------------------------
const osmSource = {
  id: 'osm-source',
  obj: {
    type: 'raster',
    tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
    tileSize: 128,
    maxzoom: 19,
  },
}
const osmLayer = {
  id: 'oh-osm-layer',
  type: 'raster',
  source: 'osm-source',
}
// 標準地図--------------------------------------------------------------------------------------------------------------
const stdSource = {
  id: 'stdSource',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 18,
  },
}
const stdLayer = {
  id: 'oh-stdLayer',
  type: 'raster',
  source: 'stdSource',
}
// 淡色地図--------------------------------------------------------------------------------------------------------------
export const paleSource = {
  id: 'pale-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 18,
  },
}
export const paleLayer = {
  id: 'oh-pale-layer',
  type: 'raster',
  source: 'pale-source',
}
// Mapbox Satellite------------------------------------------------------------------------------------------------------
const MAPBOX_TOKEN = 'pk.eyJ1Ijoia2VuemtlbnprZW56IiwiYSI6ImNqMG1nNzN6YTAwOXUzMnA5bWpxaXBjbTUifQ.colaTAgKQEB-_QCNNEqtQA'
const mapboxSatelliteSource = {
  id: 'mapbox-satellite',
  obj: {
    type: 'raster',
    tiles: [`https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${MAPBOX_TOKEN}`],
    tileSize: 512,
    maxzoom: 22,
    attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.maxar.com/">Maxar</a>',
  },
}
const mapboxSatelliteLayer = {
  id: 'oh-mapbox-satellite',
  source: mapboxSatelliteSource.id,
  type: 'raster',
}

// Mapbox Streets--------------------------------------------------------------------------------------------------------
const mapboxStreetsSource = {
  id: 'mapbox-streets',
  obj: {
    type: 'raster',
    tiles: [`https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`],
    tileSize: 512,
    maxzoom: 22,
    attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>',
  },
}
const mapboxStreetsLayer = {
  id: 'oh-mapbox-streets',
  source: mapboxStreetsSource.id,
  type: 'raster',
}

// Mapbox Satellite Streets（衛星写真+道路名ラベル）-------------------------------------------------------------
const mapboxSatelliteStreetsSource = {
  id: 'mapbox-satellite-streets',
  obj: {
    type: 'raster',
    tiles: [`https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}@2x?access_token=${MAPBOX_TOKEN}`],
    tileSize: 512,
    maxzoom: 22,
    attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a> &copy; <a href="https://www.maxar.com/">Maxar</a>',
  },
}
const mapboxSatelliteStreetsLayer = {
  id: 'oh-mapbox-satellite-streets',
  source: mapboxSatelliteStreetsSource.id,
  type: 'raster',
}

// Sentinel-2 衛星画像（CDSE Sentinel Hub）--------------------------------------------------------------------------------------------------------------
const SENTINEL_HUB_INSTANCE_ID = '79d13f71-8f67-4acd-926a-cc963a830db5'
const SENTINEL_HUB_WMTS = `https://sh.dataspace.copernicus.eu/ogc/wmts/${SENTINEL_HUB_INSTANCE_ID}`

// Sentinel Hub ヘルパー: ソースとレイヤーを生成
function createSentinelSource(layerName, format = 'image/jpeg') {
  return {
    id: `sentinel-${layerName.toLowerCase().replace(/_/g, '-')}`,
    obj: {
      type: 'raster',
      tiles: [
        `${SENTINEL_HUB_WMTS}?SERVICE=WMTS&REQUEST=GetTile`
        + `&LAYER=${layerName}`
        + `&TILEMATRIXSET=PopularWebMercator256`
        + `&TILEMATRIX={z}&TILECOL={x}&TILEROW={y}`
        + `&FORMAT=${format}`,
      ],
      tileSize: 256,
      maxzoom: 16,
      attribution: '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus Data Space Ecosystem</a> / ESA Sentinel-2',
    },
  }
}
function createSentinelLayer(layerName) {
  const sourceId = `sentinel-${layerName.toLowerCase().replace(/_/g, '-')}`
  return {
    id: `oh-${sourceId}`,
    source: sourceId,
    type: 'raster',
  }
}

// Sentinel-2 真色合成
const sentinelTrueColorSource = createSentinelSource('TRUE_COLOR')
const sentinelTrueColorLayer = createSentinelLayer('TRUE_COLOR')

// Sentinel-2 フォルスカラー（植生が赤く表示）
const sentinelFalseColorSource = createSentinelSource('FALSE_COLOR')
const sentinelFalseColorLayer = createSentinelLayer('FALSE_COLOR')

// Sentinel-2 NDVI（植生指数）
const sentinelNdviSource = createSentinelSource('NDVI', 'image/png')
const sentinelNdviLayer = createSentinelLayer('NDVI')

// Sentinel-2 水分指数
const sentinelMoistureSource = createSentinelSource('MOISTURE_INDEX', 'image/png')
const sentinelMoistureLayer = createSentinelLayer('MOISTURE_INDEX')

// Sentinel-2 NDWI（水域指数）
const sentinelNdwiSource = createSentinelSource('NDWI', 'image/png')
const sentinelNdwiLayer = createSentinelLayer('NDWI')

// Sentinel-2 SWIR（短波赤外）
const sentinelSwirSource = createSentinelSource('SWIR')
const sentinelSwirLayer = createSentinelLayer('SWIR')

// Sentinel-2 農業
const sentinelAgricultureSource = createSentinelSource('AGRICULTURE')
const sentinelAgricultureLayer = createSentinelLayer('AGRICULTURE')

// Sentinel-2 地質
const sentinelGeologySource = createSentinelSource('GEOLOGY')
const sentinelGeologyLayer = createSentinelLayer('GEOLOGY')

// Sentinel-2 NBR（焼損面積指数）
const sentinelNbrSource = createSentinelSource('NBR_RAW', 'image/png')
const sentinelNbrLayer = createSentinelLayer('NBR_RAW')

// Sentinel-2 都市フォルスカラー
const sentinelUrbanSource = createSentinelSource('FALSE_COLOR_URBAN')
const sentinelUrbanLayer = createSentinelLayer('FALSE_COLOR_URBAN')

// Sentinel-2 水深
const sentinelBathymetricSource = createSentinelSource('BATHYMETRIC', 'image/png')
const sentinelBathymetricLayer = createSentinelLayer('BATHYMETRIC')

// Sentinel-2 EVI（拡張植生指数）
const sentinelEviSource = createSentinelSource('EVI', 'image/png')
const sentinelEviLayer = createSentinelLayer('EVI')

// 最新写真--------------------------------------------------------------------------------------------------------------
// 超解像プロトコル対応: z18を超えてズームした際にシャープ化して表示
const SEAMLESSPHOTO_BASE = 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg'
const seamlessphotoSource = {
  id: 'seamlessphoto',
  obj: {
    type: 'raster',
    tiles: [`superres://{z}/{x}/{y}.jpg?url=${encodeURIComponent(SEAMLESSPHOTO_BASE)}&maxzoom=18`],
    tileSize: 256,
    maxzoom: 20,  // z18→z20 まで超解像（最大4倍拡大）
  },
}
const seamlessphotoLayer = {
  id: 'oh-seamlessphoto',
  source: seamlessphotoSource.id,
  type: 'raster',
}
// 87写真--------------------------------------------------------------------------------------------------------------
const sp87Source = {
  id: 'sp87-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo4/{z}/{x}/{y}.jpg'],
  },
}
const sp87Layer = {
  id: 'oh-sp87',
  source: 'sp87-source',
  type: 'raster',
}
// 84写真--------------------------------------------------------------------------------------------------------------
const sp84Source = {
  id: 'sp84-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo3/{z}/{x}/{y}.jpg'],
  },
}
const sp84Layer = {
  id: 'oh-sp84',
  source: 'sp84-source',
  type: 'raster',
}
// 79写真--------------------------------------------------------------------------------------------------------------
const sp79Source = {
  id: 'sp79-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo2/{z}/{x}/{y}.jpg'],
  },
}
const sp79Layer = {
  id: 'oh-sp79',
  source: 'sp79-source',
  type: 'raster',
}
// 74写真--------------------------------------------------------------------------------------------------------------
const sp74Source = {
  id: 'sp74-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/gazo1/{z}/{x}/{y}.jpg'],
  },
}
const sp74Layer = {
  id: 'oh-sp74',
  source: 'sp74-source',
  type: 'raster',
}
// 61写真--------------------------------------------------------------------------------------------------------------
const sp61Source = {
  id: 'sp61-source',
  obj: {
    type: 'raster',
    tiles: ['https://maps.gsi.go.jp/xyz/ort_old10/{z}/{x}/{y}.png'],
  },
}
const sp61Layer = {
  id: 'oh-sp61',
  source: 'sp61-source',
  type: 'raster',
  // paint: {
  //     // 明るさ、コントラスト、彩度を調整
  //     'raster-brightness-min': 0.3, // 最小明るさ
  //     'raster-brightness-max': 0.8, // 最大明るさ
  //     'raster-hue-rotate': 180,     // 色相
  //     'raster-contrast': 0.7,       // コントラスト
  //     'raster-saturation': 1,       // 彩度
  // },
}
// 45写真--------------------------------------------------------------------------------------------------------------
const sp45Source = {
  id: 'sp45-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/ort_USA10/{z}/{x}/{y}.png'],
  },
}
const sp45Layer = {
  id: 'oh-sp45',
  source: 'sp45-source',
  type: 'raster',
}
// 36写真--------------------------------------------------------------------------------------------------------------
const sp36Source = {
  id: 'sp36-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/ort_riku10/{z}/{x}/{y}.png'],
  },
}
const sp36Layer = {
  id: 'oh-sp36',
  source: 'sp36-source',
  type: 'raster',
}
// 28写真--------------------------------------------------------------------------------------------------------------
const sp28Source = {
  id: 'sp28-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/ort_1928/{z}/{x}/{y}.png'],
  },
}
const sp28Layer = {
  id: 'oh-sp28',
  source: 'sp28-source',
  type: 'raster',
}
// 山梨県航空写真--------------------------------------------------------------------------------------------------------------
const yamanashiOrthoSource = {
  id: 'yamanashi-ortho-source',
  obj: {
    type: 'raster',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yamanashi-ortho/yamanashi-ortho.pmtiles',
    tileSize: 256,
  },
}
const yamanashiOrthoLayer = {
  id: 'oh-yamanashi-ortho',
  source: 'yamanashi-ortho-source',
  type: 'raster',
}
// 東京都23区航空写真--------------------------------------------------------------------------------------------------------------
const tokyoOrthoSource = {
  id: 'tokyo-ortho-source',
  obj: {
    type: 'raster',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tokyo-ortho/tokyo-ortho.pmtiles',
    tileSize: 256,
  },
}
const tokyoOrthoLayer = {
  id: 'oh-tokyo-ortho',
  source: 'tokyo-ortho-source',
  type: 'raster',
}
// 宮崎県航空写真--------------------------------------------------------------------------------------------------------------
const miyazakiSyashinSource = {
  id: 'miyazaki-syashin-source',
  obj: {
    type: 'raster',
    tiles: ['https://mtile.pref.miyazaki.lg.jp/tile/ort/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const miyazakiSyashinLayer = {
  id: 'oh-miyazaki-syashin',
  source: 'miyazaki-syashin-source',
  type: 'raster',
}
// 神奈川県航空写真--------------------------------------------------------------------------------------------------------------
const kanagawaSyashinSource = {
  id: 'kanagawa-syashin-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/kokusyashin/kanagawa2/{z}/{x}/{y}.png'],
  },
}
const kanagawaSyashinLayer = {
  id: 'oh-kanagawa-syashin',
  source: 'kanagawa-syashin-source',
  type: 'raster',
}
// 横浜北部、川崎航空写真--------------------------------------------------------------------------------------------------------------
const yokohamaSyashinSource = {
  id: 'yokohama-syashin-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/kokusyashin/yokohama/{z}/{x}/{y}.png'],
  },
}
const yokohamaSyashinLayer = {
  id: 'oh-yokohama-syashin',
  source: 'yokohama-syashin-source',
  type: 'raster',
}
// plateau-tokyo23ku-building-mvt-2020--------------------------------------------------------------------------------------------
const plateauTokyo23kuSource = {
  id: 'plateau-tokyo23ku-source',
  obj: {
    type: 'vector',
    tiles: ['https://indigo-lab.github.io/plateau-tokyo23ku-building-mvt-2020/{z}/{x}/{y}.pbf'],
    attribution: '国土地理院ベクトルタイル提供実験',
    minzoom: 10,
    maxzoom: 16,
  },
}
const plateauTokyo23kuLayer = {
  id: 'oh-plateau-tokyo23ku-layer',
  source: 'plateau-tokyo23ku-source',
  'source-layer': 'bldg',
  type: 'fill-extrusion',
  filter: ['>', ['coalesce', ['get', 'measuredHeight'], 0], 0], // 高さ0の建物を非表示
  paint: {
    'fill-extrusion-height': ['get', 'measuredHeight'],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['get', 'measuredHeight'],
      0,
      '#d9d9d9', // 0m: グレー
      10,
      '#a6bddb', // 10m: 明るいブルー
      30,
      '#74a9cf', // 30m: 中間ブルー
      60,
      '#2b8cbe', // 60m: 濃いブルー
      100,
      '#045a8d', // 100m以上: 非常に濃いブルー
    ],
  },
  'max-opacity': 0.9,
}
// ---------------------------------------------------------------------------------------------------------------------
// PLATEAU建物（PMTiles）ソース
const plateauPmtilesSource = {
  id: 'plateauPmtiles',
  obj: {
    type: 'vector',
    // url: "pmtiles://https://shiworks.xsrv.jp/pmtiles-data/plateau/PLATEAU_2022_LOD1.pmtiles",
    url: 'pmtiles://https://shiworks.xsrv.jp/pmtiles-data/plateau/PLATEAU_2023_LOD0.pmtiles',
    minzoom: 16,
    maxzoom: 16,
    attribution: '<a href="https://github.com/shiwaku/mlit-plateau-bldg-pmtiles">mlit-plateau-bldg-pmtiles</a>',
  },
}
const plateauPmtilesLayer = {
  id: 'oh-plateauPmtiles',
  source: 'plateauPmtiles',
  // 'source-layer': "PLATEAU",
  'source-layer': 'PLATEAU_2023_LOD0',
  minzoom: 14,
  maxzoom: 23,
  type: 'fill-extrusion',
  filter: ['>', ['coalesce', ['get', 'measured_height'], 0], 0], // 高さ0の建物を非表示
  paint: {
    'fill-extrusion-color': '#797979',
    'fill-extrusion-opacity': 1.0,
    // "fill-extrusion-height": ["get", "measuredHeight"]
    'fill-extrusion-height': ['get', 'measured_height'],
  },
}
// ---------------------------------------------------------------------------------------------------------------------
// 東京都緑のオープンデータ（PMTiles）
const tokyoGreenPmtilesSource = {
  id: 'tokyoGreenPmtiles',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tokyo_green/tokyo_green.pmtiles',
    minzoom: 8,
    maxzoom: 16,
    attribution: '<a href="https://catalog.data.metro.tokyo.lg.jp/">東京都オープンデータカタログサイト</a>',
  },
}
const tokyoGreenPmtilesLayer = {
  id: 'oh-tokyoGreenPmtiles',
  source: 'tokyoGreenPmtiles',
  'source-layer': 'green',
  minzoom: 10,
  maxzoom: 23,
  type: 'fill',
  paint: {
    'fill-color': [
      'match',
      ['get', '_category'],
      'kouen', '#228B22',
      'gairoju', '#32CD32',
      'jurinchi', '#006400',
      'nouchi', '#9ACD32',
      'gaisen', '#90EE90',
      'kyuuryou', '#8FBC8F',
      'sanchi', '#2E8B57',
      'suikei', '#4682B4',
      'minkan', '#66CDAA',
      'koukyou', '#3CB371',
      'shizen', '#20B2AA',
      'toshi', '#00FA9A',
      'jourei', '#98FB98',
      'kouteki', '#7CFC00',
      '#228B22' // デフォルト
    ],
    'fill-opacity': 0.6,
    'fill-outline-color': '#006400',
  },
}
// 横浜北部、川崎CS立体図---------------------------------------------------------------------------------------------------------
const csYokohamSource = {
  id: 'cs-yokohama-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/cs/yokohama/{z}/{x}/{y}.png'],
  },
}
const csYokohamaLayer = {
  id: 'oh-cs-yokohama-layer',
  type: 'raster',
  source: 'cs-yokohama-source',
}
// 神奈川県CS立体図---------------------------------------------------------------------------------------------------------
const csKanagawaSource = {
  id: 'cs-kanagawa-source',
  obj: {
    type: 'raster',
    tiles: ['https://shiworks.xsrv.jp/raster-tiles/pref-kanagawa/kanagawapc-cs-tiles/{z}/{x}/{y}.png'],
  },
}
const csKanagawaLayer = {
  id: 'oh-cs-kanagawa-layer',
  type: 'raster',
  source: 'cs-kanagawa-source',
}
// 色別標高図---------------------------------------------------------------------------------------------------------
const shikibetsuSource = {
  id: 'shikibetsu-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/relief/{z}/{x}/{y}.png'],
  },
}
const shikibetsuLayer = {
  id: 'oh-shikibetsu-layer',
  type: 'raster',
  source: 'shikibetsu-source',
}
// 陰影起伏図---------------------------------------------------------------------------------------------------------
const ineiSource = {
  id: 'inei-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/hillshademap/{z}/{x}/{y}.png'],
  },
}
const ineiLayer = {
  id: 'oh-inei-layer',
  type: 'raster',
  source: 'inei-source',
}
// 自然地形（白黒）---------------------------------------------------------------------------------------------------------
const shizenChikeiSource = {
  id: 'shizen-chikei-source',
  obj: {
    type: 'raster',
    tiles: ['https://maps.gsi.go.jp/xyz/landform1_mono/{z}/{x}/{y}.png'],
  },
}
const shizenChikeiLayer = {
  id: 'oh-shizen-chikei-layer',
  type: 'raster',
  source: 'shizen-chikei-source',
}
// 人工改変地形（白黒）---------------------------------------------------------------------------------------------------------
const jinkoChikeiSource = {
  id: 'jinko-chikei-source',
  obj: {
    type: 'raster',
    tiles: ['https://maps.gsi.go.jp/xyz/landform2_mono/{z}/{x}/{y}.png'],
  },
}
const jinkoChikeiLayer = {
  id: 'oh-jinko-chikei-layer',
  type: 'raster',
  source: 'jinko-chikei-source',
}
// 日本土壌インベントリー（農研機構）---------------------------------------------------------------------------------------------------------
const dojoSource = {
  id: 'dojo-source',
  obj: {
    type: 'raster',
    tiles: ['https://soil-inventory.rad.naro.go.jp/tile/figure/{z}/{x}/{y}.png'],
    minzoom: 6,
    maxzoom: 15,
  },
}
const dojoLayer = {
  id: 'oh-rgb-dojo-layer',
  type: 'raster',
  source: 'dojo-source',
}
// プリセット合成レイヤー（レイヤー合成を使いやすくするためのプリセット）---------------------------------------------------------------------------------------------------------
const HILLSHADE_TILES = 'https://cyberjapandata.gsi.go.jp/xyz/hillshademap/{z}/{x}/{y}.png'
const SEAMLESSPHOTO_TILES = 'https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg'
const PALE_TILES = 'https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'
const STD_TILES = 'https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png'

// 二層重ね方式：陰影の濃さスライダー対応（ベースソース/レイヤー）
const blendBasePhotoSource = {
  id: 'oh-blend-base-photo',
  obj: { type: 'raster', tiles: [SEAMLESSPHOTO_TILES], tileSize: 256, maxzoom: 18 },
}
const blendBasePhotoLayer = {
  id: 'oh-blend-base-photo-layer',
  type: 'raster',
  source: 'oh-blend-base-photo',
}
const blendBasePaleSource = {
  id: 'oh-blend-base-pale',
  obj: { type: 'raster', tiles: [PALE_TILES], tileSize: 256, maxzoom: 18 },
}
const blendBasePaleLayer = {
  id: 'oh-blend-base-pale-layer',
  type: 'raster',
  source: 'oh-blend-base-pale',
}
const blendBaseStdSource = {
  id: 'oh-blend-base-std',
  obj: { type: 'raster', tiles: [STD_TILES], tileSize: 256, maxzoom: 18 },
}
const blendBaseStdLayer = {
  id: 'oh-blend-base-std-layer',
  type: 'raster',
  source: 'oh-blend-base-std',
}
// 航空写真×陰影起伏図（multiply）
const blendPhotoHillshadeSource = {
  id: 'oh-blend-source-photo-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(SEAMLESSPHOTO_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 18, // 航空写真のmaxzoom（陰影起伏図はoverlayMaxzoom=16で引き伸ばし）
  },
}
const blendPhotoHillshadeLayer = {
  id: 'oh-blend-photo-hillshade',
  type: 'raster',
  source: 'oh-blend-source-photo-hillshade',
}

// 淡色地図×陰影起伏図（multiply）
const blendPaleHillshadeSource = {
  id: 'oh-blend-source-pale-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(PALE_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 18, // 淡色地図のmaxzoom（陰影起伏図はoverlayMaxzoom=16で引き伸ばし）
  },
}
const blendPaleHillshadeLayer = {
  id: 'oh-blend-pale-hillshade',
  type: 'raster',
  source: 'oh-blend-source-pale-hillshade',
}

// 標準地図×陰影起伏図（multiply）— ブレンド合成レイヤー（二層重ね方式の上層として使用）
const blendStdHillshadeSource = {
  id: 'oh-blend-source-std-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(STD_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 18,
  },
}
const blendStdHillshadeLayer = {
  id: 'oh-blend-std-hillshade',
  type: 'raster',
  source: 'oh-blend-source-std-hillshade',
}

// 淡色地図×陰影×色別標高図（3レイヤーブレンド：淡色地図 + 陰影(multiply) + 色別標高図(soft-light)）
const RELIEF_TILES = 'https://cyberjapandata.gsi.go.jp/xyz/relief/{z}/{x}/{y}.png'
const blendPaleHillshadeReliefSource = {
  id: 'oh-blend-source-pale-hillshade-relief',
  obj: {
    type: 'raster',
    tiles: [
      `blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(PALE_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16&overlay2=${encodeURIComponent(RELIEF_TILES)}&overlay2Mode=soft-light&overlay2Maxzoom=15`,
    ],
    tileSize: 256,
    maxzoom: 18,
  },
}
const blendPaleHillshadeReliefLayer = {
  id: 'oh-blend-pale-hillshade-relief',
  type: 'raster',
  source: 'oh-blend-source-pale-hillshade-relief',
}

// 陰影起伏図×色別標高図（2レイヤーブレンド：陰影起伏図 + 色別標高図(soft-light)）
const blendHillshadeReliefSource = {
  id: 'oh-blend-source-hillshade-relief',
  obj: {
    type: 'raster',
    tiles: [
      `blend://soft-light/{z}/{x}/{y}.png?base=${encodeURIComponent(HILLSHADE_TILES)}&overlay=${encodeURIComponent(RELIEF_TILES)}&overlayMaxzoom=15`,
    ],
    tileSize: 256,
    maxzoom: 16, // 陰影起伏図のmaxzoom
  },
}
const blendHillshadeReliefLayer = {
  id: 'oh-blend-hillshade-relief',
  type: 'raster',
  source: 'oh-blend-source-hillshade-relief',
}

// 土地利用細分メッシュ×陰影起伏図（multiply）
const TOCHIRIYO_TILES = 'https://kenzkenz4.xsrv.jp/xyz/tochiriyo/{z}/{x}/{y}.png'
const blendTochiriyoHillshadeSource = {
  id: 'oh-blend-source-tochiriyo-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(TOCHIRIYO_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 14,
  },
}
const blendTochiriyoHillshadeLayer = {
  id: 'oh-blend-tochiriyo-hillshade',
  type: 'raster',
  source: 'oh-blend-source-tochiriyo-hillshade',
}
const blendBaseTochiriyoSource = {
  id: 'oh-blend-base-tochiriyo',
  obj: { type: 'raster', tiles: [TOCHIRIYO_TILES], tileSize: 256, maxzoom: 14 },
}
const blendBaseTochiriyoLayer = {
  id: 'oh-blend-base-tochiriyo-layer',
  type: 'raster',
  source: 'oh-blend-base-tochiriyo',
}

// シームレス地質図×陰影起伏図（multiply）
const SEAMLESS_GEOLOGY_TILES = 'https://gbank.gsj.jp/seamless/v2/api/1.2/tiles/{z}/{y}/{x}.png?layer=glf'
const blendGeologyHillshadeSource = {
  id: 'oh-blend-source-geology-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(SEAMLESS_GEOLOGY_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 13,
  },
}
const blendGeologyHillshadeLayer = {
  id: 'oh-blend-geology-hillshade',
  type: 'raster',
  source: 'oh-blend-source-geology-hillshade',
}
const blendBaseGeologySource = {
  id: 'oh-blend-base-geology',
  obj: { type: 'raster', tiles: [SEAMLESS_GEOLOGY_TILES], tileSize: 256, maxzoom: 13 },
}
const blendBaseGeologyLayer = {
  id: 'oh-blend-base-geology-layer',
  type: 'raster',
  source: 'oh-blend-base-geology',
}

// 戦前旧版地形図×陰影起伏図（multiply）
const MW5_TILES = 'https://kenzkenz.jp/xyz/mw5/{z}/{x}/{y}.png'
const blendMw5HillshadeSource = {
  id: 'oh-blend-source-mw5-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(MW5_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 15,
  },
}
const blendMw5HillshadeLayer = {
  id: 'oh-blend-mw5-hillshade',
  type: 'raster',
  source: 'oh-blend-source-mw5-hillshade',
}
const blendBaseMw5Source = {
  id: 'oh-blend-base-mw5',
  obj: { type: 'raster', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/mw5/mw5.pmtiles', tileSize: 256, maxzoom: 15 },
}
const blendBaseMw5Layer = {
  id: 'oh-blend-base-mw5-layer',
  type: 'raster',
  source: 'oh-blend-base-mw5',
}
const blendMw5CenterSource = {
  id: 'blendMw5CenterSource',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.xsrv.jp/open-hinata/geojson/mw5centerNew.geojson',
  },
}
const blendMw5CenterLabel = {
  id: 'oh-blend-mw5-hillshade-center',
  type: 'symbol',
  source: 'blendMw5CenterSource',
  layout: {
    'text-field': ['get', 'title'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 7, 10, 10, 14, 15, 16],
    'text-allow-overlap': false,
    'text-padding': 2,
  },
  paint: {
    'text-color': '#ffffff',
    'text-halo-color': 'rgba(0, 0, 0, 0.85)',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 7,
}

// 戦前旧版地形図×航空写真（soft-light）
const blendMw5PhotoSource = {
  id: 'oh-blend-source-mw5-photo',
  obj: {
    type: 'raster',
    tiles: [`blend://soft-light/{z}/{x}/{y}.png?base=${encodeURIComponent(MW5_TILES)}&overlay=${encodeURIComponent(SEAMLESSPHOTO_TILES)}&overlayMaxzoom=18`],
    tileSize: 256,
    maxzoom: 15,
  },
}
const blendMw5PhotoLayer = {
  id: 'oh-blend-mw5-photo',
  type: 'raster',
  source: 'oh-blend-source-mw5-photo',
}
const blendBaseMw5Source2 = {
  id: 'oh-blend-base-mw5-2',
  obj: { type: 'raster', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/mw5/mw5.pmtiles', tileSize: 256, maxzoom: 15 },
}
const blendBaseMw5Layer2 = {
  id: 'oh-blend-base-mw5-2-layer',
  type: 'raster',
  source: 'oh-blend-base-mw5-2',
}

// 戦前旧版地形図×土地利用（overlay）
const blendMw5TochiriyoSource = {
  id: 'oh-blend-source-mw5-tochiriyo',
  obj: {
    type: 'raster',
    tiles: [`blend://overlay/{z}/{x}/{y}.png?base=${encodeURIComponent(MW5_TILES)}&overlay=${encodeURIComponent(TOCHIRIYO_TILES)}&overlayMaxzoom=14`],
    tileSize: 256,
    maxzoom: 14,
  },
}
const blendMw5TochiriyoLayer = {
  id: 'oh-blend-mw5-tochiriyo',
  type: 'raster',
  source: 'oh-blend-source-mw5-tochiriyo',
}
const blendBaseMw5Source3 = {
  id: 'oh-blend-base-mw5-3',
  obj: { type: 'raster', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/mw5/mw5.pmtiles', tileSize: 256, maxzoom: 15 },
}
const blendBaseMw5Layer3 = {
  id: 'oh-blend-base-mw5-3-layer',
  type: 'raster',
  source: 'oh-blend-base-mw5-3',
}
// ポップアップ用：透明ベクタ土地利用レイヤー（クリック検知用）
const blendMw5TochiriyoVecSource = {
  id: 'oh-blend-mw5-tochiriyo-vec-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tochiriyo/tochiriyoD.pmtiles',
  },
}
const blendMw5TochiriyoVecLayer = {
  id: 'oh-blend-mw5-tochiriyo-vec',
  type: 'fill',
  source: 'oh-blend-mw5-tochiriyo-vec-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
    'fill-opacity': 0,
  },
}

// 戦前旧版地形図×色別標高図（soft-light）
const blendMw5ReliefSource = {
  id: 'oh-blend-source-mw5-relief',
  obj: {
    type: 'raster',
    tiles: [`blend://soft-light/{z}/{x}/{y}.png?base=${encodeURIComponent(MW5_TILES)}&overlay=${encodeURIComponent(RELIEF_TILES)}&overlayMaxzoom=15`],
    tileSize: 256,
    maxzoom: 15,
  },
}
const blendMw5ReliefLayer = {
  id: 'oh-blend-mw5-relief',
  type: 'raster',
  source: 'oh-blend-source-mw5-relief',
}
const blendBaseMw5Source4 = {
  id: 'oh-blend-base-mw5-4',
  obj: { type: 'raster', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/mw5/mw5.pmtiles', tileSize: 256, maxzoom: 15 },
}
const blendBaseMw5Layer4 = {
  id: 'oh-blend-base-mw5-4-layer',
  type: 'raster',
  source: 'oh-blend-base-mw5-4',
}

// 航空写真×色別標高図（soft-light）
const blendPhotoReliefSource = {
  id: 'oh-blend-source-photo-relief',
  obj: {
    type: 'raster',
    tiles: [`blend://soft-light/{z}/{x}/{y}.png?base=${encodeURIComponent(SEAMLESSPHOTO_TILES)}&overlay=${encodeURIComponent(RELIEF_TILES)}&overlayMaxzoom=15`],
    tileSize: 256,
    maxzoom: 18,
  },
}
const blendPhotoReliefLayer = {
  id: 'oh-blend-photo-relief',
  type: 'raster',
  source: 'oh-blend-source-photo-relief',
}
const blendBasePhotoSource2 = {
  id: 'oh-blend-base-photo-2',
  obj: { type: 'raster', tiles: [SEAMLESSPHOTO_TILES], tileSize: 256, maxzoom: 18 },
}
const blendBasePhotoLayer2 = {
  id: 'oh-blend-base-photo-2-layer',
  type: 'raster',
  source: 'oh-blend-base-photo-2',
}

// 航空写真×地質図（soft-light）
const blendPhotoGeologySource = {
  id: 'oh-blend-source-photo-geology',
  obj: {
    type: 'raster',
    tiles: [`blend://soft-light/{z}/{x}/{y}.png?base=${encodeURIComponent(SEAMLESSPHOTO_TILES)}&overlay=${encodeURIComponent(SEAMLESS_GEOLOGY_TILES)}&overlayMaxzoom=13`],
    tileSize: 256,
    maxzoom: 18,
  },
}
const blendPhotoGeologyLayer = {
  id: 'oh-blend-photo-geology',
  type: 'raster',
  source: 'oh-blend-source-photo-geology',
}
const blendBasePhotoSource3 = {
  id: 'oh-blend-base-photo-3',
  obj: { type: 'raster', tiles: [SEAMLESSPHOTO_TILES], tileSize: 256, maxzoom: 18 },
}
const blendBasePhotoLayer3 = {
  id: 'oh-blend-base-photo-3-layer',
  type: 'raster',
  source: 'oh-blend-base-photo-3',
}

// 色別標高図×陰影×地質図（3レイヤーブレンド：色別標高図 + 陰影(multiply) + 地質図(soft-light)）
const blendReliefHillshadeGeologySource = {
  id: 'oh-blend-source-relief-hillshade-geology',
  obj: {
    type: 'raster',
    tiles: [
      `blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(RELIEF_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16&overlay2=${encodeURIComponent(SEAMLESS_GEOLOGY_TILES)}&overlay2Mode=soft-light&overlay2Maxzoom=13`,
    ],
    tileSize: 256,
    maxzoom: 15,
  },
}
const blendReliefHillshadeGeologyLayer = {
  id: 'oh-blend-relief-hillshade-geology',
  type: 'raster',
  source: 'oh-blend-source-relief-hillshade-geology',
}

// 日本土壌インベントリー×陰影起伏図（multiply）
const DOJO_TILES = 'https://soil-inventory.rad.naro.go.jp/tile/figure/{z}/{x}/{y}.png'
const blendDojoHillshadeSource = {
  id: 'oh-blend-source-dojo-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(DOJO_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
    maxzoom: 15,
  },
}
const blendDojoHillshadeLayer = {
  id: 'oh-blend-dojo-hillshade',
  type: 'raster',
  source: 'oh-blend-source-dojo-hillshade',
}
const blendBaseDojoSource = {
  id: 'oh-blend-base-dojo',
  obj: { type: 'raster', tiles: [DOJO_TILES], tileSize: 256, maxzoom: 15 },
}
const blendBaseDojoLayer = {
  id: 'oh-blend-base-dojo-layer',
  type: 'raster',
  source: 'oh-blend-base-dojo',
}

// エコリス第6-7回植生図×陰影起伏図（multiply）
const ECORIS67_TILES = 'https://map.ecoris.info/tiles/vege67/{z}/{x}/{y}.png'
const blendEcoris67HillshadeSource = {
  id: 'oh-blend-source-ecoris67-hillshade',
  obj: {
    type: 'raster',
    tiles: [`blend://multiply/{z}/{x}/{y}.png?base=${encodeURIComponent(ECORIS67_TILES)}&overlay=${encodeURIComponent(HILLSHADE_TILES)}&overlayMaxzoom=16`],
    tileSize: 256,
  },
}
const blendEcoris67HillshadeLayer = {
  id: 'oh-blend-ecoris67-hillshade',
  type: 'raster',
  source: 'oh-blend-source-ecoris67-hillshade',
}
const blendBaseEcoris67Source = {
  id: 'oh-blend-base-ecoris67',
  obj: { type: 'raster', tiles: [ECORIS67_TILES], tileSize: 256 },
}
const blendBaseEcoris67Layer = {
  id: 'oh-blend-base-ecoris67-layer',
  type: 'raster',
  source: 'oh-blend-base-ecoris67',
}

// 栃木県CS立体図---------------------------------------------------------------------------------------------------------
const csTochigiSource = {
  id: 'cs-tochigi',
  obj: {
    type: 'raster',
    tiles: [
      '/oh3/php/proxy.php?url=https://rinya-tochigi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png',
    ],
  },
}
const csTochigiLayer = {
  id: 'oh-cs-tochigi-layer',
  type: 'raster',
  source: 'cs-tochigi',
}
// 長野県CS立体図---------------------------------------------------------------------------------------------------------
const csNaganoSource = {
  id: 'cs-nagano',
  obj: {
    type: 'raster',
    tiles: ['https://tile.geospatial.jp/CS/VER2/{z}/{x}/{y}.png'],
  },
}
const csNaganoLayer = {
  id: 'oh-cs-nagano-layer',
  type: 'raster',
  source: 'cs-nagano',
}
// 広島県CS立体図shi-works氏-CS 1m---------------------------------------------------------------------------------------------------------
const csHiroshimaSource = {
  id: 'cs-hiroshima',
  obj: {
    type: 'raster',
    tiles: ['https://xs489works.xsrv.jp/raster-tiles/pref-hiroshima/hiroshimapc-cs-tiles/{z}/{x}/{y}.png'],
  },
}
const csHiroshimaLayer = {
  id: 'oh-cs-hiroshima-layer',
  type: 'raster',
  source: 'cs-hiroshima',
}
// 岡山県CS立体図---------------------------------------------------------------------------------------------------------
const csOkayamaSource = {
  id: 'cs-okayama',
  obj: {
    type: 'raster',
    tiles: ['https://www2.ffpri.go.jp/soilmap/tile/cs_okayama/{z}/{x}/{y}.png'],
  },
}
const csOkayamaLayer = {
  id: 'oh-cs-okayama-layer',
  type: 'raster',
  source: 'cs-okayama',
}
// 福島県CS立体図---------------------------------------------------------------------------------------------------------
const csFukushimaSource = {
  id: 'cs-fukushima',
  obj: {
    type: 'raster',
    tiles: ['https://www2.ffpri.go.jp/soilmap/tile/cs_fukushima/{z}/{x}/{y}.png'],
  },
}
const csFukushimaLayer = {
  id: 'oh-cs-fukushima-layer',
  type: 'raster',
  source: 'cs-fukushima',
}
// 愛媛県CS立体図---------------------------------------------------------------------------------------------------------
const csEhimeSource = {
  id: 'cs-ehime-source',
  obj: {
    type: 'raster',
    tiles: ['https://www2.ffpri.go.jp/soilmap/tile/cs_ehime/{z}/{x}/{y}.png'],
  },
}
const csEhimeLayer = {
  id: 'oh-cs-ehime-layer',
  type: 'raster',
  source: 'cs-ehime-source',
}
// 高知県CS立体図---------------------------------------------------------------------------------------------------------
const csKochiSource = {
  id: 'cs-kochi-source',
  obj: {
    type: 'raster',
    tiles: ['https://rinya-kochi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png'],
  },
}
const csKochiLayer = {
  id: 'oh-cs-kochi-layer',
  type: 'raster',
  source: 'cs-kochi-source',
}
// 熊本県・大分県CS立体図---------------------------------------------------------------------------------------------------------
const csKumamotoSource = {
  id: 'cs-kumamoto-source',
  obj: {
    type: 'raster',
    tiles: ['https://www2.ffpri.go.jp/soilmap/tile/cs_kumamoto_oita/{z}/{x}/{y}.png'],
  },
}
const csKumamotoLayer = {
  id: 'oh-cs-kumamoto-layer',
  type: 'raster',
  source: 'cs-kumamoto-source',
}
// 岐阜県CS立体図---------------------------------------------------------------------------------------------------------
const csGifuSource = {
  id: 'csGifu',
  obj: {
    type: 'raster',
    tiles: ['/oh3/php/proxy.php?url=https://kenzkenz2.xsrv.jp/gihucs/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const csGifuLayer = {
  id: 'oh-csGifuLayer',
  type: 'raster',
  source: 'csGifu',
}
// 能登CS立体図------------------------------------------------------------------------------------------------------
const csNotoSource = {
  id: 'csNotoSource',
  obj: {
    type: 'raster',
    tiles: [
      '/oh3/php/proxy.php?url=https://www2.ffpri.go.jp/soilmap/tile/cs_noto/{z}/{x}/{y}.png',
    ],
    tileSize: 256,
  },
}
const csNotoLayer = {
  id: 'oh-csNotoLayer',
  type: 'raster',
  source: 'csNotoSource',
  // paint: {
  //     "raster-resampling": "nearest" // ぼやけを防ぐために「nearest」を使用
  // }
}
// 静岡県県CS立体図------------------------------------------------------------------------------------------------------
const csShizuokaSource = {
  id: 'csShizuokaSource',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://kenzkenz3.xsrv.jp/cs/shizuoka/{z}/{x}/{y}.png'],
    tileSize: 256,
  },
}
const csShizuokaLayer = {
  id: 'oh-csShizuokaLayer',
  type: 'raster',
  source: 'csShizuokaSource',
}
// 兵庫県CS立体図------------------------------------------------------------------------------------------------------
const csHyogoSource = {
  id: 'cs-hiyogo-source',
  obj: {
    type: 'raster',
    tiles: [
      '/oh3/php/proxy.php?url=https://rinya-hyogo.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png',
    ],
    tileSize: 256,
  },
}
const csHyogoLayer = {
  id: 'oh-csHyogoLayer',
  type: 'raster',
  source: 'cs-hiyogo-source',
}
// 愛知県赤色立体図------------------------------------------------------------------------------------------------------
// const aichiSekisyokuSource = {
//     id: 'aichiSekisyokuSource', obj: {
//         type: 'raster',
//         tiles: ['/oh3/php/proxy.php?url=https://bg.maps.pref.aichi.jp/tiles/w213665/{z}/{x}/{y}.png'],
//         tileSize: 256,
//         crossOrigin: 'anonymous',
//     }
// }
// const aichiSekisyokuLayer = {
//     'id': 'oh-aichiSekisyokuLayer',
//     'type': 'raster',
//     'source': 'aichiSekisyokuSource',
// }
// 神奈川県赤色立体図------------------------------------------------------------------------------------------------------
const kanagawaSekisyokuSource = {
  id: 'kanagawa-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://kenzkenz3.xsrv.jp/sekisyoku/kanagawa/{z}/{x}/{y}.png'],
  },
}
const kanagawaSekisyokuLayer = {
  id: 'oh-kanagawa-sekisyoku-layer',
  type: 'raster',
  source: 'kanagawa-sekisyoku-source',
}
// 神奈川県相模原市赤色立体地図------------------------------------------------------------------------------------------------------
const sagamiharaSekisyokuSource = {
  id: 'sagamihara-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://kenzkenz4.xsrv.jp/tile/sekisyoku/sagamihara/{z}/{x}/{y}.png'],
  },
}
const sagamiharaSekisyokuLayer = {
  id: 'oh-sagamihara-sekisyoku-layer',
  type: 'raster',
  source: 'sagamihara-sekisyoku-source',
}
// 神奈川西部赤色立体地図------------------------------------------------------------------------------------------------------
const kanagawakenseibuSekisyokuSource = {
  id: 'kanagawakenseibu-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://kenzkenz4.xsrv.jp/tile/sekisyoku/kanagawakenseibu/{z}/{x}/{y}.png'],
  },
}
const kanagawakenseibuSekisyokuLayer = {
  id: 'oh-kanagawakenseibu-sekisyoku-layer',
  type: 'raster',
  source: 'kanagawakenseibu-sekisyoku-source',
}
// 神奈川横浜市南部赤色立体地図------------------------------------------------------------------------------------------------------
const kanagawakenyokohamaSekisyokuSource = {
  id: 'kanagawakenyokohama-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://kenzkenz4.xsrv.jp/tile/sekisyoku/yokohamananbu/{z}/{x}/{y}.png'],
  },
}
const kanagawakenyokohamaSekisyokuLayer = {
  id: 'oh-kanagawakenyokohama-sekisyoku-layer',
  type: 'raster',
  source: 'kanagawakenyokohama-sekisyoku-source',
}
// 多摩地域赤色立体地図------------------------------------------------------------------------------------------------------
const tamaSekisyokuSource = {
  id: 'tamaSekisyokuSource',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tamasekisyoku/{z}/{x}/{y}.png'],
    tileSize: 256,
    scheme: 'tms',
  },
}
const tamaSekisyokuLayer = {
  id: 'oh-tamaSekisyokuLayer',
  type: 'raster',
  source: 'tamaSekisyokuSource',
}
// 東京都23区赤色立体地図------------------------------------------------------------------------------------------------------
const tokyo23SekisyokuSource = {
  id: 'tokyo23-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/sekisyoku/tokyo/{z}/{x}/{y}.png'],
  },
}
const tokyo23SekisyokuLayer = {
  id: 'oh-tokyo23-sekisyoku-layer',
  type: 'raster',
  source: 'tokyo23-sekisyoku-source',
}
// 東京都島しょ地域赤色立体地図------------------------------------------------------------------------------------------------------
const tosyo01SekisyokuSource = {
  id: 'tosyo01-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku01/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const tosyo01SekisyokuLayer = {
  id: 'oh-tosyo01-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo01-sekisyoku-source',
}
const tosyo02SekisyokuSource = {
  id: 'tosyo02-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku02/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const tosyo02SekisyokuLayer = {
  id: 'oh-tosyo02-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo02-sekisyoku-source',
}
const tosyo03SekisyokuSource = {
  id: 'tosyo03-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku03/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const tosyo03SekisyokuLayer = {
  id: 'oh-tosyo03-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo03-sekisyoku-source',
  'raster-resampling': 'nearest',
}
const tosyo04SekisyokuSource = {
  id: 'tosyo04-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku04/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const tosyo04SekisyokuLayer = {
  id: 'oh-tosyo04-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo04-sekisyoku-source',
}
const tosyo05SekisyokuSource = {
  id: 'tosyo05-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku05/{z}/{x}/{y}.png'],
    scheme: 'tms',
  },
}
const tosyo05SekisyokuLayer = {
  id: 'oh-tosyo05-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo05-sekisyoku-source',
}
const tosyo06SekisyokuSource = {
  id: 'tosyo06-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tokyo/tousyosekisyoku06/{z}/{x}/{y}.png'],
    scheme: 'tms',
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 18, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([139.74046773079067, 32.48017775032204, 139.78740051701084, 32.439009913129155]),
  },
}
const tosyo06SekisyokuLayer = {
  id: 'oh-tosyo06-sekisyoku-layer',
  type: 'raster',
  source: 'tosyo06-sekisyoku-source',
}
// 高知県赤色立体地図------------------------------------------------------------------------------------------------------
const kochiSekisyokuSource = {
  id: 'kochi-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz3.xsrv.jp/sekisyoku/kochi/{z}/{x}/{y}.png'],
  },
}
const kochiSekisyokuLayer = {
  id: 'oh-kochi-sekisyoku-layer',
  type: 'raster',
  source: 'kochi-sekisyoku-source',
}
// 富山県赤色立体地図------------------------------------------------------------------------------------------------------
const toyamaSekisyokuSource = {
  id: 'toyama-sekisyoku-source',
  obj: {
    type: 'raster',
    tiles: ['transparentWhite://https://rinya-toyama.geospatial.jp/tile/rinya/2024/rrimap/{z}/{x}/{y}.png'],
  },
}
const toyamaSekisyokuLayer = {
  id: 'oh-toyama-sekisyoku-layer',
  type: 'raster',
  source: 'toyama-sekisyoku-source',
}
// 微地形表現図マップタイル（林野庁・森林情報オープンデータ）-----------------------------------------------------------
// https://forestgeo.info/ CC-BY 4.0
// 01: 宮城県CS立体図
const fgMiyagiCsSource = {
  id: 'fg-miyagi-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/4_miyagi/csmap_2023/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgMiyagiCsLayer = { id: 'oh-fg-miyagi-cs', type: 'raster', source: 'fg-miyagi-cs-source' }
// 02: 宮城県地形強調図
const fgMiyagiTopoSource = {
  id: 'fg-miyagi-topo-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/4_miyagi/topography_2023/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgMiyagiTopoLayer = { id: 'oh-fg-miyagi-topo', type: 'raster', source: 'fg-miyagi-topo-source' }
// 03: 栃木県CS立体図
const fgTochigiCsSource = {
  id: 'fg-tochigi-cs-source',
  obj: { type: 'raster', tiles: ['https://rinya-tochigi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgTochigiCsLayer = { id: 'oh-fg-tochigi-cs', type: 'raster', source: 'fg-tochigi-cs-source' }
// 04: 埼玉県CS立体図
const fgSaitamaCsSource = {
  id: 'fg-saitama-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/11_saitama/csmap/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgSaitamaCsLayer = { id: 'oh-fg-saitama-cs', type: 'raster', source: 'fg-saitama-cs-source' }
// 05: 東京都CS立体図（多摩地域）
const fgTokyoCsSource = {
  id: 'fg-tokyo-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/13_tokyo/csmap_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgTokyoCsLayer = { id: 'oh-fg-tokyo-cs', type: 'raster', source: 'fg-tokyo-cs-source' }
// 06: 東京都陰陽図（多摩地域）
const fgTokyoInyouzuSource = {
  id: 'fg-tokyo-inyouzu-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/13_tokyo/inyouzu_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgTokyoInyouzuLayer = { id: 'oh-fg-tokyo-inyouzu', type: 'raster', source: 'fg-tokyo-inyouzu-source' }
// 07: 神奈川県CS立体図
const fgKanagawaCsSource = {
  id: 'fg-kanagawa-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/14_kanagawa/csmap_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgKanagawaCsLayer = { id: 'oh-fg-kanagawa-cs', type: 'raster', source: 'fg-kanagawa-cs-source' }
// 08: 神奈川県赤色立体地図
const fgKanagawaRrimSource = {
  id: 'fg-kanagawa-rrim-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/14_kanagawa/rrim_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgKanagawaRrimLayer = { id: 'oh-fg-kanagawa-rrim', type: 'raster', source: 'fg-kanagawa-rrim-source' }
// 09: 新潟県CS立体図（長岡地域）
const fgNiigataCsSource = {
  id: 'fg-niigata-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/15_niigata/nagaoka/csmap_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgNiigataCsLayer = { id: 'oh-fg-niigata-cs', type: 'raster', source: 'fg-niigata-cs-source' }
// 10: 新潟県陰陽図（長岡地域）
const fgNiigataInyouzuSource = {
  id: 'fg-niigata-inyouzu-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/15_niigata/nagaoka/inyouzu_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgNiigataInyouzuLayer = { id: 'oh-fg-niigata-inyouzu', type: 'raster', source: 'fg-niigata-inyouzu-source' }
// 11: 富山県赤色立体地図
const fgToyamaRrimSource = {
  id: 'fg-toyama-rrim-source',
  obj: { type: 'raster', tiles: ['https://rinya-toyama.geospatial.jp/tile/rinya/2024/rrimap/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgToyamaRrimLayer = { id: 'oh-fg-toyama-rrim', type: 'raster', source: 'fg-toyama-rrim-source' }
// 12: 石川県CS立体図（能登地域）
const fgIshikawaCsSource = {
  id: 'fg-ishikawa-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/17_ishikawa/noto/csmap_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgIshikawaCsLayer = { id: 'oh-fg-ishikawa-cs', type: 'raster', source: 'fg-ishikawa-cs-source' }
// 13: 石川県赤色立体地図（能登地域）
const fgIshikawaRrimSource = {
  id: 'fg-ishikawa-rrim-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/17_ishikawa/noto/rrim_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgIshikawaRrimLayer = { id: 'oh-fg-ishikawa-rrim', type: 'raster', source: 'fg-ishikawa-rrim-source' }
// 14: 石川県陰陽図（能登地域）
const fgIshikawaInyouzuSource = {
  id: 'fg-ishikawa-inyouzu-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/17_ishikawa/noto/inyouzu_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgIshikawaInyouzuLayer = { id: 'oh-fg-ishikawa-inyouzu', type: 'raster', source: 'fg-ishikawa-inyouzu-source' }
// 15: 山梨県CS立体図
const fgYamanashiCsSource = {
  id: 'fg-yamanashi-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/19_yamanashi/csmap_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgYamanashiCsLayer = { id: 'oh-fg-yamanashi-cs', type: 'raster', source: 'fg-yamanashi-cs-source' }
// 16: 山梨県陰陽図
const fgYamanashiInyouzuSource = {
  id: 'fg-yamanashi-inyouzu-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/19_yamanashi/inyouzu_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgYamanashiInyouzuLayer = { id: 'oh-fg-yamanashi-inyouzu', type: 'raster', source: 'fg-yamanashi-inyouzu-source' }
// 17: 長野県CS立体図
const fgNaganoCsSource = {
  id: 'fg-nagano-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/20_nagano/csmap_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgNaganoCsLayer = { id: 'oh-fg-nagano-cs', type: 'raster', source: 'fg-nagano-cs-source' }
// 18: 岐阜県CS立体図
const fgGifuCsSource = {
  id: 'fg-gifu-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/21_gifu/csmap_2023/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgGifuCsLayer = { id: 'oh-fg-gifu-cs', type: 'raster', source: 'fg-gifu-cs-source' }
// 19: 静岡県CS立体図
const fgShizuokaCsSource = {
  id: 'fg-shizuoka-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/22_shizuoka/csmap_2023/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgShizuokaCsLayer = { id: 'oh-fg-shizuoka-cs', type: 'raster', source: 'fg-shizuoka-cs-source' }
// 20: 滋賀県CS立体図
const fgShigaCsSource = {
  id: 'fg-shiga-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/25_shiga/csmap_2023/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgShigaCsLayer = { id: 'oh-fg-shiga-cs', type: 'raster', source: 'fg-shiga-cs-source' }
// 21: 京都府赤色立体地図
const fgKyotoRrimSource = {
  id: 'fg-kyoto-rrim-source',
  obj: { type: 'raster', tiles: ['https://rinya-kyoto.geospatial.jp/tile/rinya/2024/rrim_KyotoPref/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgKyotoRrimLayer = { id: 'oh-fg-kyoto-rrim', type: 'raster', source: 'fg-kyoto-rrim-source' }
// 22: 大阪府CS立体図
const fgOsakaCsSource = {
  id: 'fg-osaka-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/27_osaka/csmap_2020/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgOsakaCsLayer = { id: 'oh-fg-osaka-cs', type: 'raster', source: 'fg-osaka-cs-source' }
// 23: 兵庫県CS立体図
const fgHyogoCsSource = {
  id: 'fg-hyogo-cs-source',
  obj: { type: 'raster', tiles: ['https://rinya-hyogo.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgHyogoCsLayer = { id: 'oh-fg-hyogo-cs', type: 'raster', source: 'fg-hyogo-cs-source' }
// 24: 鳥取県CS立体図
const fgTottoriCsSource = {
  id: 'fg-tottori-cs-source',
  obj: { type: 'raster', tiles: ['https://rinya-tottori.geospatial.jp/tile/rinya/2024/csmap_tottori/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgTottoriCsLayer = { id: 'oh-fg-tottori-cs', type: 'raster', source: 'fg-tottori-cs-source' }
// 25: 岡山県CS立体図
const fgOkayamaCsSource = {
  id: 'fg-okayama-cs-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/33_okayama/csmap_2024/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgOkayamaCsLayer = { id: 'oh-fg-okayama-cs', type: 'raster', source: 'fg-okayama-cs-source' }
// 26: 愛媛県CS立体図（TMS）
const fgEhimeCsSource = {
  id: 'fg-ehime-cs-source',
  obj: { type: 'raster', tiles: ['https://rinya-ehime.geospatial.jp/tile/rinya/2024/csmap_Ehime/{z}/{x}/{y}.png'], tileSize: 256, scheme: 'tms' },
}
const fgEhimeCsLayer = { id: 'oh-fg-ehime-cs', type: 'raster', source: 'fg-ehime-cs-source' }
// 27: 愛媛県赤色立体地図（TMS）
const fgEhimeRrimSource = {
  id: 'fg-ehime-rrim-source',
  obj: { type: 'raster', tiles: ['https://rinya-ehime.geospatial.jp/tile/rinya/2024/rrimap_Ehime/{z}/{x}/{y}.png'], tileSize: 256, scheme: 'tms' },
}
const fgEhimeRrimLayer = { id: 'oh-fg-ehime-rrim', type: 'raster', source: 'fg-ehime-rrim-source' }
// 28: 高知県CS立体図
const fgKochiCsSource = {
  id: 'fg-kochi-cs-source',
  obj: { type: 'raster', tiles: ['https://rinya-kochi.geospatial.jp/2023/rinya/tile/csmap/{z}/{x}/{y}.png'], tileSize: 256 },
}
const fgKochiCsLayer = { id: 'oh-fg-kochi-cs', type: 'raster', source: 'fg-kochi-cs-source' }
// 29: 長崎県赤色立体地図/地形起伏図
const fgNagasakiRrimSource = {
  id: 'fg-nagasaki-rrim-source',
  obj: { type: 'raster', tiles: ['https://forestgeo.info/opendata/42_nagasaki/rrim_2022/{z}/{x}/{y}.webp'], tileSize: 256 },
}
const fgNagasakiRrimLayer = { id: 'oh-fg-nagasaki-rrim', type: 'raster', source: 'fg-nagasaki-rrim-source' }

// 東京都23区CS立体図------------------------------------------------------------------------------------------------------
const tokyo23CsSource = {
  id: 'tokyo23-cs-source',
  obj: {
    type: 'raster',
    tiles: ['https://shiworks.xsrv.jp/raster-tiles/tokyo-digitaltwin/tokyopc-23ku-2024-cs-tiles/{z}/{x}/{y}.png'],
  },
}
const tokyo23CsLayer = {
  id: 'oh-tokyo23-cs-layer',
  type: 'raster',
  source: 'tokyo23-cs-source',
}
// 大阪府CS立体地図------------------------------------------------------------------------------------------------------
const csOsakaSource = {
  id: 'csOsakaSource',
  obj: {
    type: 'raster',
    tiles: ['https://xs489works.xsrv.jp/raster-tiles/pref-osaka/osaka-cs-tiles/{z}/{x}/{y}.png'],
    tileSize: 256,
    crossOrigin: 'anonymous',
  },
}
const csOsakaLayer = {
  id: 'oh-csOsakaLayer',
  type: 'raster',
  source: 'csOsakaSource',
}
// 川だけ地形地図------------------------------------------------------------------------------------------------------
const kawadakeSource = {
  id: 'kawadake-source',
  obj: {
    type: 'raster',
    // tiles: ['http://www.gridscapes.net/AllRivers/1.0.0/t/{z}/{x}/{y}.png'],
    tiles: ['transparentWhite://https://kenzkenz.xsrv.jp/kawadake/{z}/{x}/{y}.png'],
    tileSize: 256,
    // scheme: 'tms',
    maxzoom: 14,
    minzoom: 5,
  },
}
const kawddakeLayer = {
  id: 'oh-kawadakeLayer',
  type: 'raster',
  source: 'kawadake-source',
}
// // 川と流域地図------------------------------------------------------------------------------------------------------
// const ryuikiSource = {
//     id: 'ryuiki-source', obj: {
//         type: 'raster',
//         tiles: ['/oh3/php/proxy.php?url=https://tiles.dammaps.jp/ryuiki_t/1/{z}/{x}/{y}.png'],
//         tileSize: 256,
//         crossOrigin: 'anonymous',
//         minzoom: 5,
//         maxzoom: 14
//     }
// }
// const ryuikiLayer = {
//     'id': 'oh-ryuikiLayer',
//     'type': 'raster',
//     'source': 'ryuiki-source',
// }
// =============================================================================
// オフラインエリア用ソース・レイヤー（名前付きエリアキャッシュ対応）
// =============================================================================

// final3（メイン詳細データ z14-16）
export const offlineAreaFinal3Source = {
  id: 'offline-area-final3-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/pmtiles/2025final3.pmtiles/{z}/{x}/{y}.pbf'],
    minzoom: 14,
    maxzoom: 16,
  },
}

// dissolved（小ズーム用 z10-13）
export const offlineAreaDissolvedSource = {
  id: 'offline-area-dissolved-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/pmtiles/homusyo/2025/2025dissolved.pmtiles/{z}/{x}/{y}.pbf'],
    minzoom: 10,
    maxzoom: 13,
  },
}

// kijyunten（基準点 z10-16）
export const offlineAreaKijyuntenSource = {
  id: 'offline-area-kijyunten-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/pmtiles/homusyo/2025/2025kijyuntenFull.pmtiles/{z}/{x}/{y}.pbf'],
    minzoom: 10,
    maxzoom: 16,
  },
}

// レイヤースタイル（オンライン版に準拠）
export const offlineAreaLayers = {
  // final3 ポリゴン（透明、クリック用）
  polygon: {
    id: 'oh-offline-area-polygon',
    type: 'fill',
    source: 'offline-area-final3-source',
    'source-layer': 'fude',
    paint: {
      'fill-color': 'rgba(0, 0, 0, 0)',
    },
    minzoom: 14,
  },
  // final3 ライン
  line: {
    id: 'oh-offline-area-line',
    type: 'line',
    source: 'offline-area-final3-source',
    'source-layer': 'fude',
    paint: {
      'line-color': 'red',
      'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
    },
    minzoom: 14,
  },
  // final3 ラベル
  label: {
    id: 'oh-offline-area-label',
    type: 'symbol',
    source: 'offline-area-final3-source',
    'source-layer': 'fude',
    layout: {
      'text-field': ['get', '地番'],
      'text-font': ['NotoSansJP-Regular'],
    },
    paint: {
      'text-color': 'red',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    maxzoom: 24,
    minzoom: 15,
  },
  // dissolved ポリゴン（小ズーム用）
  dissolvedPolygon: {
    id: 'oh-offline-area-dissolved-polygon',
    type: 'fill',
    source: 'offline-area-dissolved-source',
    'source-layer': 'polygon',
    paint: {
      'fill-color': 'rgba(255, 0, 0, 0.1)',
    },
    maxzoom: 14,
  },
  // dissolved ライン（小ズーム用）
  dissolvedLine: {
    id: 'oh-offline-area-dissolved-line',
    type: 'line',
    source: 'offline-area-dissolved-source',
    'source-layer': 'polygon',
    paint: {
      'line-color': 'red',
      'line-width': 1,
    },
    maxzoom: 14,
  },
  // 基準点
  kijyunten: {
    id: 'oh-offline-area-kijyunten',
    type: 'circle',
    source: 'offline-area-kijyunten-source',
    'source-layer': 'point',
    paint: {
      'circle-color': 'navy',
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 14, 3, 20, 6],
      'circle-opacity': 1,
      'circle-stroke-width': 1,
      'circle-stroke-color': '#fff',
    },
  },
  // 基準点ラベル
  kijyuntenLabel: {
    id: 'oh-offline-area-kijyunten-label',
    type: 'symbol',
    source: 'offline-area-kijyunten-source',
    'source-layer': 'point',
    layout: {
      'text-field': ['get', '名称'],
      'text-offset': [0, 1],
    },
    paint: {
      'text-color': 'navy',
      'text-halo-color': 'rgba(255,255,255,1)',
      'text-halo-width': 1.0,
    },
    minzoom: 14,
  },
}

// 登記所備付地図データ ----------------------------------------------------------------------------------------------------
export const HOMUSYO_2025_SOURCE_URL = 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/tokizyo_v4.pmtiles'
let homusyo2025Source
if (!store.state.isOffline) {
  homusyo2025Source = {
    id: 'homusyo-2025-source',
    obj: {
      type: 'vector',
      url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/tokizyo_v4.pmtiles',
      maxzoom: 16,
    },
  }
} else {
  homusyo2025Source = {
    id: 'homusyo-2025-source',
    obj: {
      type: 'vector',
      tiles: ['https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/tokizyo_v4.pmtiles/{z}/{x}/{y}.pbf'],
      minzoom: 14,
      maxzoom: 16,
    },
  }
}

const homusyo2025KijyuntenSource = {
  id: 'homusyo-2025-kijyunten-source',
  obj: {
    type: 'vector',
    // url: "pmtiles://https://kenzkenz.net/pmtiles/homusyo/2025/2025kijyunten.pmtiles",
    url: 'pmtiles://https://kenzkenz.net/pmtiles/homusyo/2025/2025kijyuntenFull.pmtiles',
  },
}
const homusyo2025ZukakuSource = {
  id: 'homusyo-2025-zukaku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/homusyo/2025/2025zukaku.pmtiles',
  },
}
export const homusyo2025LayerLine = {
  id: 'oh-homusyo-2025-line',
  type: 'line',
  source: 'homusyo-2025-source',
  'source-layer': 'parcels',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
const homusyo2025LayerKijyunten = {
  id: 'oh-homusyo-2025-kijyunten',
  type: 'circle',
  source: 'homusyo-2025-kijyunten-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 14, 3, 20, 6],
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const homusyo2025LayerKijyuntenLabel = {
  id: 'oh-homusyo-2025-kijyunten-label',
  type: 'symbol',
  source: 'homusyo-2025-kijyunten-source',
  'source-layer': 'point',
  layout: {
    // 'text-field': ['concat', ['get', '基準点種別'], '\n', ['get', '名称']],
    'text-field': ['get', '名称'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'navy',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}
const homusyo2025LayerZukaku = {
  id: 'oh-homusyo-2025-zukaku',
  type: 'fill',
  source: 'homusyo-2025-zukaku-source',
  'source-layer': 'polygon',
  paint: {
    // "fill-color": "rgba(0, 0, 0, 0.1)"
    'fill-color': [
      'step',
      ['zoom'],
      'rgba(0, 0, 0, 0.1)', // ズーム < 14 → 不透明
      14,
      'rgba(0, 0, 0, 0)', // ズーム >= 14 → 完全に透明
    ],
  },
}
const homusyo2025LayerZukakuLine = {
  id: 'oh-homusyo-2025-zukaku-line',
  type: 'line',
  source: 'homusyo-2025-zukaku-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
  minzoom: 12,
}
const homusyo2025LayerZukakuLabel = {
  id: 'oh-homusyo-2025-zukaku-label',
  type: 'symbol',
  source: 'homusyo-2025-zukaku-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', '地図番号'],
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}

// 2026登記所地図-基準点
const homusyo2026KijyuntenSource = {
  id: 'homusyo-2026-kijyunten-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/kijyunten_v4_2026.pmtiles',
    maxzoom: 16,
  },
}
const homusyo2026LayerKijyunten = {
  id: 'oh-homusyo-2026-kijyunten',
  type: 'circle',
  source: 'homusyo-2026-kijyunten-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 14, 3, 20, 6],
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const homusyo2026LayerKijyuntenLabel = {
  id: 'oh-homusyo-2026-kijyunten-label',
  type: 'symbol',
  source: 'homusyo-2026-kijyunten-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', '名称'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'navy',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}

// 2026登記所地図-図郭
const homusyo2026ZukakuSource = {
  id: 'homusyo-2026-zukaku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/zukaku_v4_2026.pmtiles',
    maxzoom: 16,
  },
}
const homusyo2026LayerZukaku = {
  id: 'oh-homusyo-2026-zukaku',
  type: 'fill',
  source: 'homusyo-2026-zukaku-source',
  'source-layer': 'zukaku',
  paint: {
    'fill-color': [
      'step',
      ['zoom'],
      'rgba(0, 0, 0, 0.1)',
      14,
      'rgba(0, 0, 0, 0)',
    ],
  },
}
const homusyo2026LayerZukakuLine = {
  id: 'oh-homusyo-2026-zukaku-line',
  type: 'line',
  source: 'homusyo-2026-zukaku-source',
  'source-layer': 'zukaku',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
  minzoom: 12,
}
const homusyo2026LayerZukakuLabel = {
  id: 'oh-homusyo-2026-zukaku-label',
  type: 'symbol',
  source: 'homusyo-2026-zukaku-source',
  'source-layer': 'zukaku',
  layout: {
    'text-field': ['get', '地図番号'],
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}

// 2026登記所地図-筆
const homusyo2026Source = {
  id: 'homusyo-2026-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/tokizyo_v4_2026.pmtiles',
    maxzoom: 16,
  },
}
const homusyo2026Fill = {
  id: 'oh-homusyo-2026-fill',
  type: 'fill',
  source: 'homusyo-2026-source',
  'source-layer': 'parcels',
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',
  },
}
// ペイント用オーバーレイ（base fill の直上、line の直下）。
// ext-coloring-chibanzu2026.vue が fill-color / fill-opacity を更新する。
const homusyo2026Coloring = {
  id: 'oh-homusyo-2026-coloring',
  type: 'fill',
  source: 'homusyo-2026-source',
  'source-layer': 'parcels',
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',
    'fill-opacity': 0.6,
  },
}
const homusyo2026Line = {
  id: 'oh-homusyo-2026-line',
  type: 'line',
  source: 'homusyo-2026-source',
  'source-layer': 'parcels',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
const homusyo2026Label = {
  id: 'oh-homusyo-2026-label',
  type: 'symbol',
  source: 'homusyo-2026-source',
  'source-layer': 'parcels',
  layout: {
    'text-field': ['get', '地番'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'red',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 15,
}
export const homusyo2026Vertex = {
  id: 'oh-homusyo-2026-vertex',
  type: 'circle',
  source: 'homusyo-2026-source',
  'source-layer': 'parcels',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
    'circle-color': 'rgba(0,0,0,0)',
  },
}
// 2026低ズーム用（concave hull による範囲表示）
const homusyo2026LowZoomSource = {
  id: 'homusyo-2026-lowzoom-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/homusyo/2026/dissolved_2026.pmtiles',
    maxzoom: 14,
  },
}
const homusyo2026LowZoomFill = {
  id: 'oh-homusyo-2026-lowzoom-fill',
  type: 'fill',
  source: 'homusyo-2026-lowzoom-source',
  'source-layer': 'dissolved',
  paint: {
    'fill-color': 'rgba(255, 100, 100, 0.15)',
  },
  maxzoom: 14,
}
const homusyo2026LowZoomLine = {
  id: 'oh-homusyo-2026-lowzoom-line',
  type: 'line',
  source: 'homusyo-2026-lowzoom-source',
  'source-layer': 'dissolved',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 14, 1],
  },
  maxzoom: 14,
}

// 低ズーム用ソース（2025final3.pmtiles: z14以下で代替表示）
const homusyo2025LowZoomSource = {
  id: 'homusyo-2025-lowzoom-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/2025final3.pmtiles',
    maxzoom: 16,
  },
}

// 低ズーム用レイヤー
const homusyo2025LowZoomFill = {
  id: 'oh-homusyo-2025-lowzoom-fill',
  type: 'fill',
  source: 'homusyo-2025-lowzoom-source',
  'source-layer': 'fude',
  paint: {
    'fill-color': 'rgba(255, 100, 100, 0.15)',
  },
  maxzoom: 14,
}
const homusyo2025LowZoomLine = {
  id: 'oh-homusyo-2025-lowzoom-line',
  type: 'line',
  source: 'homusyo-2025-lowzoom-source',
  'source-layer': 'fude',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 14, 1],
  },
  maxzoom: 14,
}
const homusyo2025Fill = {
  id: 'oh-homusyo-2025-fill',
  type: 'fill',
  source: 'homusyo-2025-source',
  'source-layer': 'parcels',
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',
  },
}
const homusyo2025Line = {
  id: 'oh-homusyo-2025-line',
  type: 'line',
  source: 'homusyo-2025-source',
  'source-layer': 'parcels',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
const homusyo2025Label = {
  id: 'oh-homusyo-2025-label',
  type: 'symbol',
  source: 'homusyo-2025-source',
  'source-layer': 'parcels',
  layout: {
    'text-field': ['get', '地番'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'red',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 15,
}

export const homusyo2025Vertex = {
  id: 'oh-homusyo-2025-vertex',
  type: 'circle',
  source: 'homusyo-2025-source',
  'source-layer': 'parcels',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
    'circle-color': 'rgba(0,0,0,0)',
  },
}

// 2026登記所地図-図郭
const homusyo2025TestZukakuSource = {
  id: 'homusyo-2025-test-zukaku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/homusyo/2025test/zukaku.pmtiles',
    maxzoom: 16,
  },
}
const homusyo2025TestZukakuFill = {
  id: 'oh-homusyo-2025-zukaku',
  type: 'fill',
  source: 'homusyo-2025-test-zukaku-source',
  'source-layer': 'zukaku',
  paint: {
    'fill-color': [
      'step',
      ['zoom'],
      'rgba(0, 0, 0, 0.1)', // ズーム < 14 → 半透明
      14,
      'rgba(0, 0, 0, 0)', // ズーム >= 14 → 透明
    ],
  },
}
const homusyo2025TestZukakuLine = {
  id: 'oh-homusyo-2025-zukaku-line',
  type: 'line',
  source: 'homusyo-2025-test-zukaku-source',
  'source-layer': 'zukaku',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
  minzoom: 12,
}
const homusyo2025TestZukakuLabel = {
  id: 'oh-homusyo-2025-zukaku-label',
  type: 'symbol',
  source: 'homusyo-2025-test-zukaku-source',
  'source-layer': 'zukaku',
  layout: {
    'text-field': ['get', '地図番号'],
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}

// 2026登記所地図-基準点
const homusyo2025TestKijyuntenSource = {
  id: 'homusyo-2025-test-kijyunten-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/homusyo/2025test/kijyunten.pmtiles',
    maxzoom: 16,
  },
}
const homusyo2025TestKijyunten = {
  id: 'oh-homusyo-2025-kijyunten',
  type: 'circle',
  source: 'homusyo-2025-test-kijyunten-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 14, 3, 20, 6],
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const homusyo2025TestKijyuntenLabel = {
  id: 'oh-homusyo-2025-kijyunten-label',
  type: 'symbol',
  source: 'homusyo-2025-test-kijyunten-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', '名称'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'navy',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}

// ---------------------------------------------------------------------------------------------------------------------
const amx2024Source = {
  id: 'amx-a-2024-pmtiles',
  obj: {
    type: 'vector',
    // url: 'pmtiles://https://kenzkenz4.xsrv.jp/pmtiles/homusyo/2024/MojMap_amx_2024v2.pmtiles'
    url: 'pmtiles://https://kenzkenz.net/pmtiles/amx/MojMap_amx_2024.pmtiles',
    // url: 'pmtiles://https://data.source.coop/smartmaps/amx-2024-04/MojMap_amx_2024.pmtiles',
  },
}
// 登記所備付地図データ 間引きなし
const amx2024Layer = {
  id: 'oh-amx-a-fude',
  type: 'fill',
  source: 'amx-a-2024-pmtiles',
  'source-layer': 'fude',
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',
    'fill-outline-color': 'rgba(255, 0, 0, 1)',
  },
}
const amx2024LayerLine = {
  id: 'oh-amx-a-fude-line',
  type: 'line',
  source: 'amx-a-2024-pmtiles',
  'source-layer': 'fude',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
const amx2024LayerLabel = {
  id: 'oh-amx-label',
  type: 'symbol',
  source: 'amx-a-2024-pmtiles',
  'source-layer': 'fude',
  layout: {
    'text-field': ['get', '地番'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'rgba(255, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 15,
}
const amx2024LayerVertex = {
  id: 'oh-amx-vertex',
  type: 'circle',
  source: 'amx-a-2024-pmtiles',
  'source-layer': 'fude',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
    'circle-color': 'red',
  },
}
// ---------------------------------------------------------------------------------------------------------------------
const amxSource = {
  id: 'amx-a-pmtiles',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://habs.rad.naro.go.jp/spatial_data/amx/a.pmtiles',
    // url: 'https://data.source.coop/smartmaps/amx-2024-04/MojMap_amx_2024.pmtiles',
  },
}
// 登記所備付地図データ 間引きなし
const amxLayer = {
  id: 'oh-amx-a-fude',
  type: 'fill',
  source: 'amx-a-pmtiles',
  'source-layer': 'fude',
  paint: {
    'fill-color': 'rgba(254, 217, 192, 0)',
    'fill-outline-color': 'rgba(255, 0, 0, 1)',
  },
}
const amxLayerLine = {
  id: 'oh-amx-a-fude-line',
  type: 'line',
  source: 'amx-a-pmtiles',
  'source-layer': 'fude',
  paint: {
    'line-color': 'red',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
// 登記所備付地図データ 代表点レイヤ
const amxLayerDaihyou = {
  id: 'oh-amx-a-daihyo',
  // ヒートマップ
  type: 'heatmap',
  source: 'amx-a-pmtiles',
  // ベクトルタイルソースから使用するレイヤ
  'source-layer': 'daihyo',
  paint: {
    // ヒートマップの密度に基づいて各ピクセルの色を定義
    'heatmap-color': [
      // 入力値と出力値のペア（"stop"）の間を補間することにより、連続的で滑らかな結果を生成する
      'interpolate',
      // 入力より小さいストップと大きいストップのペアを直線的に補間
      ['linear'],
      // ヒートマップレイヤーの密度推定値を取得
      ['heatmap-density'],
      0,
      'rgba(255, 255, 255, 0)',
      0.5,
      'rgba(255, 255, 0, 0.5)',
      // 1に近づくほど密度が高い
      1,
      'rgba(255, 0, 0, 0.5)',
    ],
    // ヒートマップ1点の半径（ピクセル単位）
    'heatmap-radius': [
      // 入力値と出力値のペア（"stop"）の間を補間することにより、連続的で滑らかな結果を生成する
      'interpolate',
      // 出力が増加する割合を制御する、1に近づくほど出力が増加する
      ['exponential', 10],
      // ズームレベルに応じて半径を調整する
      ['zoom'],
      2,
      5,
      14,
      50,
    ],
  },
}
const amxLayerLabel = {
  id: 'oh-amx-label',
  type: 'symbol',
  source: 'amx-a-pmtiles',
  'source-layer': 'fude',
  layout: {
    'text-field': ['get', '地番'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'rgba(255, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 17,
}
const amxLayerVertex = {
  id: 'oh-amx-vertex',
  type: 'circle',
  source: 'amx-a-pmtiles',
  'source-layer': 'fude',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 0, 18, 4],
    'circle-color': 'red',
    // 'circle-stroke-width': 1,
    // 'circle-stroke-color': 'white'
  },
}
// 幕末近世 --------------------------------------------------------------------------------------------
const bakumatsuSource = {
  id: 'bakumatsu',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/bakumatsu/b410.pmtiles',
  },
}
const bakumatsuLayer = {
  id: 'oh-bakumatsu-layer',
  type: 'fill',
  source: 'bakumatsu',
  'source-layer': 'b41',
  paint: {
    'fill-color': ['get', 'random_color_ryobun'],
  },
}
const bakumatsuLayerHeight = {
  id: 'oh-bakumatsu-kokudaka-height',
  type: 'fill-extrusion',
  source: 'bakumatsu',
  'source-layer': 'b41',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['/', ['get', '石高計'], ['get', 'area']],
      0,
      100,
      50000000,
      10000,
    ],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['/', ['get', '石高計'], ['get', 'area']],
      0,
      'white',
      10000000,
      'red',
      50000000,
      'black',
    ],
  },
}
const bakumatsuLayerLine = {
  id: 'oh-bakumatsu-line',
  type: 'line',
  source: 'bakumatsu',
  'source-layer': 'b41',
  paint: {
    'line-color': '#000',
    // 'line-width': 0.5
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const bakumatsuLayerLabel = {
  id: 'oh-bakumatsu-label',
  type: 'symbol',
  source: 'bakumatsu',
  'source-layer': 'b41',
  layout: {
    'text-field': [
      'let',
      'splitIndex',
      ['index-of', '・', ['get', '村名']],
      ['case', ['>=', ['var', 'splitIndex'], 0], ['slice', ['get', '村名'], 0, ['var', 'splitIndex']], ['get', '村名']],
    ],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0.5, 0],
    visibility: 'visible',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 10,
}
// 幕末近世ポイント --------------------------------------------------------------------------------------------
const bakumatsuPointSource = {
  id: 'bakumatsu-point-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/bakumatsupoint/bp.pmtiles',
  },
}
const bakumatsuPointLayer = {
  id: 'oh-bakumatsu-point',
  type: 'circle',
  source: 'bakumatsu-point-source',
  'source-layer': 'bp',
  paint: {
    'circle-color': ['get', 'random_color_ryobun'],
    'circle-radius': ['interpolate', ['linear'], ['sqrt', ['get', '石高計']], 0, 1, 200, 40],
  },
}
// 小学校-------------------------------------------------------------------------------------------
const syogakkoR05Source = {
  id: 'syogakkoR05Source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/syogakko/r05/s4joint.pmtiles',
  },
}
const syogakkoR05Layer = {
  id: 'oh-syogakkoR05',
  type: 'fill',
  source: 'syogakkoR05Source',
  'source-layer': 's4polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const syogakkoR05LayerLine = {
  id: 'oh-syogakkoR05-line',
  type: 'line',
  source: 'syogakkoR05Source',
  'source-layer': 's4polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const syogakkoR05LayerLabel = {
  id: 'oh-syogakkoR05-label',
  type: 'symbol',
  source: 'syogakkoR05Source',
  'source-layer': 's4point',
  layout: {
    'text-field': ['get', 'P29_004'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
const syogakkoR05LayerPoint = {
  id: 'oh-syogakkoR05-point',
  type: 'circle',
  source: 'syogakkoR05Source',
  'source-layer': 's4point',
  paint: {
    'circle-color': '#000', // 固定の赤色
    'circle-radius': 6, // 半径を設定
  },
}
// 中学校ソース --------------------------------------------------------------------------------------------
const cyugakuR05Source = {
  id: 'cyugakuR05Source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/cyugakko/r05/t4joint.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
// 中学校レイヤー
const cyugakuR05Layer = {
  id: 'oh-cyugakuR05',
  type: 'fill',
  source: 'cyugakuR05Source',
  'source-layer': 't4polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const cyugakuR05LayerLine = {
  id: 'oh-cyugakuR05-line',
  type: 'line',
  source: 'cyugakuR05Source',
  'source-layer': 't4polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const cyugakuR05LayerLabel = {
  id: 'oh-cyugakuR05-label',
  type: 'symbol',
  source: 'cyugakuR05Source',
  'source-layer': 't4point',
  layout: {
    'text-field': ['get', 'P29_004'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
const cyugakuR05LayerPoint = {
  id: 'oh-cyugakuR05-point',
  type: 'circle',
  source: 'cyugakuR05Source',
  'source-layer': 't4point',
  paint: {
    'circle-color': '#000',
    'circle-radius': 6,
  },
}
// 低位地帯--------------------------------------------------------------------------------------------
const teiiSource = {
  id: 'teii-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/teiichitai/teiichitai.pmtiles',
  },
}
const teiiLayer = {
  id: 'oh-teii',
  type: 'fill',
  source: 'teii-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'G08_002'],
      // 0, 'rgba(30, 30, 30, 0.8)', // 最小値の色（濃い灰色）
      // 5, 'rgba(0, 80, 200, 0.8)', // 任意の色（濃い青）
      // 10, 'rgba(0, 0, 180, 0.8)', // 任意の色（深い青）
      // 15, 'rgba(0, 0, 130, 0.8)', // 任意の色（濃紺）
      // 20, 'rgba(0, 0, 90, 0.8)' // 最大値の色（非常に濃い紺）
      0,
      'rgba(255,255,179,0.8)', // 0.3m未満
      0.3,
      'rgba(247,245,169,0.8)', // 0.3~0.5m
      0.5,
      'rgba(248,225,116,0.8)', // 0.5~1.0m
      1.0,
      'rgba(255,216,192,0.8)', // 1.0~3.0m
      3.0,
      'rgba(255,183,183,0.8)', // 3.0~5.0m
      5.0,
      'rgba(255,145,145,0.8)', // 5.0~10.0m
      10.0,
      'rgba(242,133,201,0.8)', // 10.0~20.0m
      20.0,
      'rgba(220,122,220,0.8)', // 20.0m以上
    ],
  },
}
// 地形分類--------------------------------------------------------------------------------------------
const chikeibunruiSource = {
  id: 'chikeibunruiSource',
  obj: {
    type: 'vector',
    tiles: ['https://optgeo.github.io/unite-one/zxy/{z}/{x}/{y}.pbf'],
    attribution: '国土地理院ベクトルタイル提供実験',
    minzoom: 10,
    maxzoom: 12,
  },
}
const chikeibunruiLayer = {
  id: 'oh-chikeibunrui',
  type: 'fill',
  source: 'chikeibunruiSource',
  'source-layer': 'one',
  paint: {
    'fill-color': [
      'match',
      ['get', 'code'],
      '山地',
      'rgba(217, 203, 174, 0.8)',
      '崖・段丘崖',
      'rgba(148, 102, 171, 0.8)',
      '地すべり地形',
      'rgba(204, 153, 255, 0.8)',
      '台地・段丘',
      'rgba(255, 170, 0, 0.8)',
      '山麓堆積地形',
      'rgba(153, 128, 77, 0.8)',
      '扇状地',
      'rgba(202, 204, 96, 0.8)',
      '自然堤防',
      'rgba(255, 255, 51, 0.8)',
      '天井川',
      'rgba(251, 224, 157, 0.8)',
      '砂州・砂丘',
      'rgba(255, 255, 153, 0.8)',
      '凹地・浅い谷',
      'rgba(163, 204, 126, 0.8)',
      '氾濫平野',
      'rgba(187, 255, 153, 0.8)',
      '後背低地・湿地',
      'rgba(0, 209, 164, 0.8)',
      '旧河道',
      'rgba(102, 153, 255, 0.8)',
      '落堀',
      'rgba(31, 153, 153, 0.8)',
      '河川敷・浜',
      'rgba(159, 159, 196, 0.8)',
      '水部',
      'rgba(229, 255, 255, 0.8)',
      '旧水部',
      'rgba(119, 153, 153, 0.8)',
      'rgba(255, 0, 0, 0.8)',
    ],
  },
}
// 日本歴史地名大系ソース（旧版・コメントアウト）--------------------------------------------------------------------------------------------
// const nihonrekishiSource = {
//   id: 'nihonrekishiSouce',
//   obj: {
//     type: 'vector',
//     // minzoom: 0,
//     // maxzoom: 15,
//     url: 'pmtiles://https://kenzkenz.net/pmtiles/chimei/c.pmtiles',
//     attribution: "<a href='' target='_blank'></a>",
//   },
// }
// // 日本歴史地名大系レイヤー
// const nihonrekishiLayer = {
//   id: 'oh-nihonrekishi',
//   type: 'circle',
//   source: 'nihonrekishiSouce',
//   'source-layer': 'point',
//   paint: {
//     'circle-color': 'red',
//     'circle-radius': 6,
//   },
// }
// const nihonrekishiLayerLabel = {
//   id: 'oh-nihonrekishi-label',
//   type: 'symbol',
//   source: 'nihonrekishiSouce',
//   'source-layer': 'point',
//   layout: {
//     'text-field': ['get', '名称'],
//     'text-font': ['NotoSansJP-Regular'],
//     // 'text-anchor': 'left',
//     'text-offset': [0, 1],
//     visibility: 'visible',
//   },
//   paint: {
//     'text-color': 'rgba(255, 255, 255, 0.7)',
//     'text-halo-color': 'rgba(0,0,0,0.7)',
//     'text-halo-width': 1.0,
//   },
//   maxzoom: 24,
//   minzoom: 10,
// }
// ---------------------------------------------------------------------------------------------------------------------
const iryokikanSource = {
  id: 'iryokikanSource',
  obj: {
    type: 'vector',
    // minzoom: 0,
    // maxzoom: 15,
    url: 'pmtiles://https://kenzkenz.net/pmtiles/iryo/i.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
//
const iryokikanLayer = {
  id: 'oh-iryokikan',
  type: 'circle',
  source: 'iryokikanSource',
  'source-layer': 'i',
  paint: {
    'circle-color': ['match', ['get', 'P04_001'], 1, 'red', 2, 'green', 3, 'blue', 'black'],
    'circle-radius': 6,
  },
}
const iryokikanLayerLabel = {
  id: 'oh-iryokikan-label',
  type: 'symbol',
  source: 'iryokikanSource',
  'source-layer': 'i',
  layout: {
    'text-field': ['get', 'P04_002'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 2],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 12,
}
// 100mメッシュソース --------------------------------------------------------------------------------------------
const m100mSource = {
  id: 'm100mSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/100m/100m.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const m100mLayer = {
  id: 'oh-m100m',
  type: 'fill',
  source: 'm100mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'PopT'],
      0,
      'rgba(255,255,255,0.8)', // Color for low values
      500,
      'rgba(255,0,0,0.8)', // Intermediate value
      1400,
      'rgba(0,0,0,0.8)', // Color for high values
    ],
    'fill-opacity': 1,
  },
}
const m100mLayerLine = {
  id: 'oh-m100m-line',
  type: 'line',
  source: 'm100mSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
const m100mLayerLabel = {
  id: 'oh-m100m-label',
  type: 'symbol',
  source: 'm100mSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['to-string', ['round', ['get', 'PopT']]],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 15,
}
const m100mLayerHeight = {
  id: 'oh-m100m-height',
  type: 'fill-extrusion',
  source: 'm100mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 100, 1400, 5000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      500,
      'red',
      1400,
      'black',
    ],
  },
}
// 125mメッシュソース --------------------------------------------------------------------------------------------
// 地図表示用は軽量版(125m.pmtiles)を使用。AI分析用はareaAnalyzer.tsで125m_full.pmtilesを直接参照
// ※問題があれば以下に戻す:
//   url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/125m/125m_full.pmtiles'
//   プロパティ: jinko → T001225001
export const m125mSource = {
  id: 'm125mSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/125m/125m.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
export const m125mLayer = {
  id: 'oh-m125m',
  type: 'fill',
  source: 'm125mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 'white', 1000, 'red', 3000, 'black'],
  },
}
export const m125mLayerLine = {
  id: 'oh-m125m-line',
  type: 'line',
  source: 'm125mSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
export const m125mLayerLabel = {
  id: 'oh-m125m-label',
  type: 'symbol',
  source: 'm125mSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'jinko'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 13,
}
export const m125mLayerHeight = {
  id: 'oh-m125m-height',
  type: 'fill-extrusion',
  source: 'm125mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 100, 3000, 5000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      1000,
      'red',
      3000,
      'black',
    ],
  },
}
// 125mメッシュ高齢化率 --------------------------------------------------------------------------------------------
export const m125mAgingLayer = {
  id: 'oh-m125m-aging',
  type: 'fill',
  source: 'm125mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['to-number', ['get', 'jinko'], 0], 0],
      'rgba(200, 200, 200, 0.3)',
      [
        'interpolate',
        ['linear'],
        ['/', ['to-number', ['get', 'ronen'], 0], ['to-number', ['get', 'jinko'], 1]],
        0.0, '#2166ac',
        0.15, '#67a9cf',
        0.20, '#d1e5f0',
        0.25, '#f7f7f7',
        0.30, '#fddbc7',
        0.35, '#ef8a62',
        0.40, '#b2182b',
        0.50, '#67001f',
      ],
    ],
  },
}
export const m125mAgingLayerLabel = {
  id: 'oh-m125m-aging-label',
  type: 'symbol',
  source: 'm125mSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': [
      'case',
      ['==', ['to-number', ['get', 'jinko'], 0], 0],
      '-',
      [
        'concat',
        ['to-string', ['round', ['*', 100, ['/', ['to-number', ['get', 'ronen'], 0], ['to-number', ['get', 'jinko'], 1]]]]],
        '%',
      ],
    ],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 13,
}
export const m125mAgingLayerHeight = {
  id: 'oh-m125m-aging-height',
  type: 'fill-extrusion',
  source: 'm125mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': [
      'case',
      ['==', ['to-number', ['get', 'jinko'], 0], 0],
      0,
      ['interpolate', ['linear'], ['/', ['to-number', ['get', 'ronen'], 0], ['to-number', ['get', 'jinko'], 1]], 0.0, 100, 0.50, 5000],
    ],
    'fill-extrusion-color': [
      'case',
      ['==', ['to-number', ['get', 'jinko'], 0], 0],
      'rgba(200, 200, 200, 0.3)',
      [
        'interpolate',
        ['linear'],
        ['/', ['to-number', ['get', 'ronen'], 0], ['to-number', ['get', 'jinko'], 1]],
        0.0, '#2166ac',
        0.15, '#67a9cf',
        0.20, '#d1e5f0',
        0.25, '#f7f7f7',
        0.30, '#fddbc7',
        0.35, '#ef8a62',
        0.40, '#b2182b',
        0.50, '#67001f',
      ],
    ],
  },
}
// 250mメッシュソース --------------------------------------------------------------------------------------------
export const m250mSource = {
  id: 'm250mSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/250m/250m.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
export const m250mLayer = {
  id: 'oh-m250m',
  type: 'fill',
  source: 'm250mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 'white', 1000, 'red', 3000, 'black'],
  },
}
export const m250mLayerLine = {
  id: 'oh-m250m-line',
  type: 'line',
  source: 'm250mSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
export const m250mLayerLabel = {
  id: 'oh-m250m-label',
  type: 'symbol',
  source: 'm250mSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'jinko'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 13,
}
export const m250mLayerHeight = {
  id: 'oh-m250m-height',
  type: 'fill-extrusion',
  source: 'm250mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 100, 3000, 5000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      1000,
      'red',
      3000,
      'black',
    ],
  },
}
// 500mメッシュソース --------------------------------------------------------------------------------------------
const m500mSource = {
  id: 'm500mSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/500m/500m2.pmtiles',
  },
}
const m500mLayer = {
  id: 'oh-m500m',
  type: 'fill',
  source: 'm500mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 'white', 5000, 'red', 17500, 'black'],
  },
}
const m500mLayerLine = {
  id: 'oh-m500m-line',
  type: 'line',
  source: 'm500mSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
const m500mLayerLabel = {
  id: 'oh-m500m-label',
  type: 'symbol',
  source: 'm500mSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'jinko'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
    'text-anchor': 'center',
    'text-allow-overlap': false, // 重複を許可しない
    'text-ignore-placement': false, // 配置を尊重
    'symbol-placement': 'point', // ポイントごとに1つのラベルを表示
    'text-padding': 10, // ラベル同士の間隔を調整
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
const m500mLayerHeight = {
  id: 'oh-m500m-height',
  type: 'fill-extrusion',
  source: 'm500mSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 100, 17500, 5000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      5000,
      'red',
      17500,
      'black',
    ],
  },
}
// r02推計人口250m --------------------------------------------------------------------------------------------
const r02suikei250mSource = {
  id: 'r02suikei250m-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/r02suikei250m.pmtiles',
  },
}
const r02suikei250mLayer = {
  id: 'oh-r02suikei250m',
  type: 'fill',
  source: 'r02suikei250m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2070'], 0],
      'rgba(196, 253, 187, 0.8)',
      // 'rgba(0, 0, 255, 0.8)',
      // Check if PTN_2050 or PTN_2020 is NaN
      ['any', ['!=', ['typeof', ['get', 'PTN_2070']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)', // Color for NaN
      // 'rgba(0, 0, 255, 0.8)',
      ['==', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)', // Default color (fully transparent)
    ],
  },
}
const r02suikei250mLayerLine = {
  id: 'ohr02-suikei250m-line',
  type: 'line',
  source: 'r02suikei250m-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// r02推計人口500m --------------------------------------------------------------------------------------------
const r02suikei500mSource = {
  id: 'r02suikei500m-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/r02suikei500m.pmtiles',
  },
}
const r02suikei500mLayer = {
  id: 'oh-r02suikei500m',
  type: 'fill',
  source: 'r02suikei500m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2070'], 0],
      'rgba(196, 253, 187, 0.8)',
      // 'rgba(0, 0, 255, 0.8)',
      // Check if PTN_2050 or PTN_2020 is NaN
      ['any', ['!=', ['typeof', ['get', 'PTN_2070']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)', // Color for NaN
      // 'rgba(0, 0, 255, 0.8)',
      ['==', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)', // Default color (fully transparent)
    ],
  },
}
const r02suikei500mLayerLine = {
  id: 'ohr02-suikei500m-line',
  type: 'line',
  source: 'r02suikei500m-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// r02推計人口1km --------------------------------------------------------------------------------------------
const r02suikei1kmSource = {
  id: 'r02suikei1km-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/r02suikei1km.pmtiles',
  },
}
const r02suikei1kmLayer = {
  id: 'oh-r02suikei1km',
  type: 'fill',
  source: 'r02suikei1km-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2070'], 0],
      'rgba(196, 253, 187, 0.8)',
      // 'rgba(0, 0, 255, 0.8)',
      // Check if PTN_2050 or PTN_2020 is NaN
      ['any', ['!=', ['typeof', ['get', 'PTN_2070']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)', // Color for NaN
      // 'rgba(0, 0, 255, 0.8)',
      ['==', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2070'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)', // Default color (fully transparent)
    ],
  },
}
const r02suikei1kmLayerLine = {
  id: 'ohr02-suikei1km-line',
  type: 'line',
  source: 'r02suikei1km-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 推計人口500mm --------------------------------------------------------------------------------------------
const suikei500mSource = {
  id: 'suikei500m-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/suikei500mMini2.pmtiles',
  },
}
const suikei500mLayer = {
  id: 'oh-suikei500m',
  type: 'fill',
  source: 'suikei500m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2050'], 0],
      'rgba(196, 253, 187, 0.8)',
      ['any', ['!=', ['typeof', ['get', 'PTN_2050']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)',
      ['==', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)',
    ],
  },
}
const suikei500mLayerLine = {
  id: 'oh-suikei500m-line',
  type: 'line',
  source: 'suikei500m-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 0.5],
  },
}
// 推計人口1kmフル --------------------------------------------------------------------------------------------
const suikei1kmFullSource = {
  id: 'suikei1km-full-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/suikei1km.pmtiles',
  },
}
const suikei1kmFullLayer = {
  id: 'oh-suikei1km-full',
  type: 'fill',
  source: 'suikei1km-full-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2050'], 0],
      'rgba(196, 253, 187, 0.8)',
      ['any', ['!=', ['typeof', ['get', 'PTN_2050']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)',
      ['==', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)',
    ],
  },
}
const suikei1kmFullLayerLine = {
  id: 'oh-suikei1km-full-line',
  type: 'line',
  source: 'suikei1km-full-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 推計人口1km --------------------------------------------------------------------------------------------
const suikei1kmSource = {
  id: 'suikei1km-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/suikei/suikei1kmMini2.pmtiles',
  },
}
const suikei1kmLayer = {
  id: 'oh-suikei1km',
  type: 'fill',
  source: 'suikei1km-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'PTN_2050'], 0],
      'rgba(196, 253, 187, 0.8)',
      // 'rgba(0, 0, 255, 0.8)',
      // Check if PTN_2050 or PTN_2020 is NaN
      ['any', ['!=', ['typeof', ['get', 'PTN_2050']], 'number'], ['!=', ['typeof', ['get', 'PTN_2020']], 'number']],
      'rgba(196, 253, 187, 0.8)', // Color for NaN
      // 'rgba(0, 0, 255, 0.8)',
      ['==', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0],
      'rgba(196, 253, 187, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.1],
      'rgba(255, 0, 0, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 1.0],
      'rgba(184, 38, 25, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.7],
      'rgba(89, 119, 246, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.5],
      'rgba(97, 197, 250, 0.8)',
      ['>', ['/', ['get', 'PTN_2050'], ['get', 'PTN_2020']], 0.00000000000001],
      'rgba(140, 252, 114, 0.8)',
      'rgba(0, 0, 0, 0)', // Default color (fully transparent)
    ],
  },
}
const suikei1kmLayerLine = {
  id: 'oh-suikei1km-line',
  type: 'line',
  source: 'suikei1km-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 1kmメッシュソース --------------------------------------------------------------------------------------------
export const m1kmSource = {
  id: 'm1kmSource',
  obj: {
    type: 'vector',
    // minzoom: 0,
    // maxzoom: 15,
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/1km/1km2.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
export const m1kmLayer = {
  id: 'oh-m1km',
  type: 'fill',
  source: 'm1kmSource',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      10000,
      'red',
      35000,
      'black',
    ],
  },
}
export const m1kmLayerLine = {
  id: 'oh-m1km-line',
  type: 'line',
  source: 'm1kmSource',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
export const m1kmLayerLabel = {
  id: 'oh-m1km-label',
  type: 'symbol',
  source: 'm1kmSource',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'jinko'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
    'text-anchor': 'center',
    'text-allow-overlap': false, // 重複を許可しない
    'text-ignore-placement': false, // 配置を尊重
    'symbol-placement': 'point', // ポイントごとに1つのラベルを表示
    'text-padding': 10, // ラベル同士の間隔を調整
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
export const m1kmLayerHeight = {
  id: 'oh-m1km-height',
  type: 'fill-extrusion',
  source: 'm1kmSource',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'jinko']], 0, 100, 35000, 10000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'jinko']],
      0,
      'white',
      10000,
      'red',
      35000,
      'black',
    ],
  },
}
// 地理院250mメッシュソース --------------------------------------------------------------------------------------------
export const chiriin250mSource = {
  id: 'chiriin250m-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/mesh/chiriin250m/chiriin250m3.pmtiles',
  },
}
export const chiriin250mLayer = {
  id: 'oh-chiriin250m',
  type: 'fill',
  source: 'chiriin250m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', '_fillColor'],
  },
  'max-opacity': 0.9,
}
export const chiriin250mLayerLine = {
  id: 'oh-m250m-line',
  type: 'line',
  source: 'chiriin250m-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 0.5],
  },
}
export const chiriin250mLayerLabel = {
  id: 'oh-chiriin250m-label',
  type: 'symbol',
  source: 'chiriin250m-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'jinko'],
    'text-font': ['NotoSansJP-Regular'],
    // 'text-anchor': 'left',
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  maxzoom: 24,
  minzoom: 13,
}
const chiriin250mLayerHeight = {
  id: 'oh-chiriin250m-height',
  type: 'fill-extrusion',
  source: 'chiriin250m-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['get', '人口（人）'],
      0,
      50.0, // 高さの最小値を50mに設定
      10000,
      5000, // 高さの最大値を5000mに設定
    ],
    'fill-extrusion-color': ['get', '_fillColor'],
  },
}
// ---------------------------------------------------------------------------------------------------------------------
// 全国旧石器
export const kyusekkiSource = {
  id: 'kyusekkiSource',
  obj: {
    type: 'vector',
    // minzoom: 0,
    // maxzoom: 15,
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kyusekki/kyusekki.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
//
export const kyusekkiLayer = {
  id: 'oh-kyusekki',
  type: 'circle',
  source: 'kyusekkiSource',
  'source-layer': 'point',
  minzoom: 12,
  paint: {
    'circle-color': '#3cb371',
    'circle-radius': 8,
  },
}
export const kyusekkiLayerHeatmap = {
  id: 'oh-kyusekki-heatmap',
  type: 'heatmap',
  source: 'kyusekkiSource',
  'source-layer': 'point',
  maxzoom: 12,
  paint: {
    // ヒートマップの密度に基づいて各ピクセルの色を定義
    'heatmap-color': [
      // 入力値と出力値のペア（"stop"）の間を補間することにより、連続的で滑らかな結果を生成する
      'interpolate',
      // 入力より小さいストップと大きいストップのペアを直線的に補間
      ['linear'],
      // ヒートマップレイヤーの密度推定値を取得
      ['heatmap-density'],
      0,
      'rgba(255, 255, 255, 0)',
      0.5,
      'rgba(60, 179, 113, 0.6)',
      // 1に近づくほど密度が高い
      1,
      'rgba(255, 215, 0, 0.8)',
    ],
    // ヒートマップ1点の半径（ピクセル単位）
    'heatmap-radius': [
      // 入力値と出力値のペア（"stop"）の間を補間することにより、連続的で滑らかな結果を生成する
      'interpolate',
      // 出力が増加する割合を制御する、1に近づくほど出力が増加する
      ['exponential', 10],
      // ズームレベルに応じて半径を調整する
      ['zoom'],
      2,
      20,
      5,
      20,
      14,
      20,
      50,
      20,
    ],
  },
}
// 都道府県指定文化財（P32）--------------------------------------------------------------------------------------------
export const isekiP32Source = {
  id: 'iseki-p32-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.xsrv.jp/pmtiles/iseki/iseki.pmtiles',
  },
}
export const isekiP32Layer = {
  id: 'oh-iseki-p32',
  type: 'circle',
  source: 'iseki-p32-source',
  'source-layer': 'point',
  minzoom: 10,
  paint: {
    'circle-color': [
      'match',
      ['get', 'P32_004'],
      1, '#e74c3c', // 有形文化財
      2, '#9b59b6', // 無形文化財
      3, '#f39c12', // 民俗文化財
      4, '#27ae60', // 記念物
      5, '#3498db', // 文化的景観
      6, '#1abc9c', // 伝統的建造物群
      7, '#95a5a6', // 文化財の保存技術
      '#7f8c8d',
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 8, 18, 12],
    'circle-stroke-color': 'white',
    'circle-stroke-width': 1,
  },
}
export const isekiP32LayerHeatmap = {
  id: 'oh-iseki-p32-heatmap',
  type: 'heatmap',
  source: 'iseki-p32-source',
  'source-layer': 'point',
  maxzoom: 10,
  paint: {
    'heatmap-color': [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0, 'rgba(33,102,172,0)',
      0.2, 'rgb(103,169,207)',
      0.4, 'rgb(209,229,240)',
      0.6, 'rgb(253,219,199)',
      0.8, 'rgb(239,138,98)',
      1, 'rgb(178,24,43)',
    ],
    'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 20],
    'heatmap-opacity': 0.8,
  },
}
export const isekiP32LayerLabel = {
  id: 'oh-iseki-p32-label',
  type: 'symbol',
  source: 'iseki-p32-source',
  'source-layer': 'point',
  minzoom: 14,
  layout: {
    'text-field': ['get', 'P32_006'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 1.5],
    'text-anchor': 'top',
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': 'white',
    'text-halo-width': 1.5,
  },
}
// 河川ソース --------------------------------------------------------------------------------------------
export const kasenSource = {
  id: 'kasenSource',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kasen/kasen3.pmtiles',
  },
}
export const kasenLayer = {
  id: 'oh-kasen',
  type: 'line',
  source: 'kasenSource',
  'source-layer': 'line',
  paint: {
    'line-color': 'blue',
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      1,
      ['*', 0.1, ['case', ['==', ['get', 'W05_003'], '1'], 3, 1]],
      11,
      ['*', 1, ['case', ['==', ['get', 'W05_003'], '1'], 3, 1]],
      12,
      ['*', 2, ['case', ['==', ['get', 'W05_003'], '1'], 3, 1]],
      14,
      ['*', 3, ['case', ['==', ['get', 'W05_003'], '1'], 3, 1]],
      16,
      ['*', 5, ['case', ['==', ['get', 'W05_003'], '1'], 3, 1]],
    ],
  },
}
const kasenGlowLayer = {
  id: 'oh-kasen-glow',
  type: 'line',
  source: 'kasenSource',
  'source-layer': 'line',
  paint: {
    'line-color': 'rgba(0, 0, 255, 0.3)', // Outer glow color
    'line-width': ['interpolate', ['linear'], ['zoom'], 1, 1, 18, 20],
    'line-blur': ['interpolate', ['linear'], ['zoom'], 1, 2, 18, 7],
  },
  layout: {
    visibility: 'visible',
  },
}
export const kasenLayerLabel = {
  id: 'oh-kasen-label',
  type: 'symbol',
  source: 'kasenSource',
  'source-layer': 'line',
  layout: {
    'text-field': ['get', 'W05_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
    'text-anchor': 'center',
    'text-allow-overlap': false, // 重複を許可しない
    'text-ignore-placement': false, // 配置を尊重
    'symbol-placement': 'point', // ポイントごとに1つのラベルを表示
    'text-padding': 10, // ラベル同士の間隔を調整
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 12,
  filter: ['!=', 'W05_004', '名称不明'],
}
// ---------------------------------------------------------------------------------------------------------------------
// 災害伝承碑
const densyohiSource = {
  id: 'densyohi-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/densyohi/densyohi.pmtiles',
  },
}
const densyohiLayer = {
  id: 'oh-densyohi',
  type: 'circle',
  source: 'densyohi-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#3cb371',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 1, 4, 3, 7, 6, 11, 10],
  },
}
const densyohiLayerLabel = {
  id: 'oh-densyohi-label',
  type: 'symbol',
  source: 'densyohi-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', '碑名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 10,
}

// const densyohiLayer2 = {
//     id: "oh-densyohi",
//     type: "symbol",
//     source: "densyohiSource",
//     "source-layer": "point",
//     "layout": {
//         "icon-image": "densyouhi",  // JSONに記述されたアイコン名を指定
//         "icon-size": 1.0
//     }
// }
// did------------------------------------------------------------------------------------------------------------------
const didSource = {
  id: 'did-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/did/did.pmtiles',
  },
}
const didLayer = {
  id: 'oh-did',
  type: 'fill',
  source: 'did-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255,192,203,0.8)',
  },
}
const didLayerLine = {
  id: 'oh-did_line',
  type: 'line',
  source: 'did-source',
  'source-layer': '4polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
// did時系列-------------------------------------------------------------------------------------------------------------
const didTimelineSource = {
  id: 'did-timeline-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/did-timeline/did-timeline.pmtiles',
  },
}
const DID_TL_YEARS = [
  's35','s40','s45','s50','s55','s60',
  'h02','h07','h12','h17','h22','h27','r02',
]
const didTimelineLayers = []
DID_TL_YEARS.forEach(key => {
  didTimelineLayers.push({
    id: `oh-did-tl-${key}`,
    type: 'fill',
    source: 'did-timeline-source',
    'source-layer': `did-${key}`,
    paint: { 'fill-color': 'rgba(75,0,130,0.5)', 'fill-opacity': 0 },
  })
  didTimelineLayers.push({
    id: `oh-did-tl-${key}_line`,
    type: 'line',
    source: 'did-timeline-source',
    'source-layer': `did-${key}`,
    paint: {
      'line-color': '#fff',
      'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0, 11, 0.5],
      'line-opacity': 0,
    },
  })
})
// 標高・傾斜度3次メッシュ-----------------------------------------------------------------------------------------------
const elevation3MeshSource = {
  id: 'elevation3-mesh-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/elevation3_mesh/elevation3_mesh.pmtiles',
  },
}
const elevation3MeshFill = {
  id: 'oh-elevation3-mesh-fill',
  type: 'fill',
  source: 'elevation3-mesh-source',
  'source-layer': 'elevation3_mesh',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, 'rgba(0, 128, 0, 0.3)',
      100, 'rgba(144, 238, 144, 0.3)',
      500, 'rgba(255, 255, 0, 0.3)',
      1000, 'rgba(255, 165, 0, 0.3)',
      2000, 'rgba(255, 0, 0, 0.3)',
      3000, 'rgba(139, 69, 19, 0.3)',
    ],
  },
}
const elevation3MeshLine = {
  id: 'oh-elevation3-mesh-line',
  type: 'line',
  source: 'elevation3-mesh-source',
  'source-layer': 'elevation3_mesh',
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.4)',
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      4, 0,
      8, 0.3,
      12, 0.8,
    ],
  },
}
const elevation3MeshLabel = {
  id: 'oh-elevation3-mesh-label',
  type: 'symbol',
  source: 'elevation3-mesh-source',
  'source-layer': 'elevation3_mesh',
  layout: {
    'text-field': ['format', ['to-string', ['round', ['to-number', ['get', 'avg_elev'], 0]]], {}, 'm', {}],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 10,
    visibility: 'visible',
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': 'rgba(255,255,255,0.8)',
    'text-halo-width': 1.0,
  },
  minzoom: 9,
}
const elevation3MeshHeight = {
  id: 'oh-elevation3-mesh-height',
  type: 'fill-extrusion',
  source: 'elevation3-mesh-source',
  'source-layer': 'elevation3_mesh',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, 0,
      100, 500,
      500, 2500,
      1000, 5000,
      2000, 10000,
      3000, 15000,
    ],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, '#2d7d46',
      100, '#90ee90',
      500, '#f0e68c',
      1000, '#ff8c00',
      2000, '#ff3300',
      3000, '#8b4513',
    ],
    'fill-extrusion-opacity': 0.85,
  },
}
// 標高・傾斜度4次メッシュ-----------------------------------------------------------------------------------------------
const elevationMeshSource = {
  id: 'elevation-mesh-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/elevation_mesh/elevation_mesh.pmtiles',
  },
}
const elevationMeshFill = {
  id: 'oh-elevation-mesh-fill',
  type: 'fill',
  source: 'elevation-mesh-source',
  'source-layer': 'elevation_mesh',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, 'rgba(0, 128, 0, 0.3)',
      100, 'rgba(144, 238, 144, 0.3)',
      500, 'rgba(255, 255, 0, 0.3)',
      1000, 'rgba(255, 165, 0, 0.3)',
      2000, 'rgba(255, 0, 0, 0.3)',
      3000, 'rgba(139, 69, 19, 0.3)',
    ],
  },
}
const elevationMeshLine = {
  id: 'oh-elevation-mesh-line',
  type: 'line',
  source: 'elevation-mesh-source',
  'source-layer': 'elevation_mesh',
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.4)',
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      6, 0,
      10, 0.3,
      14, 0.8,
    ],
  },
}
const elevationMeshLabel = {
  id: 'oh-elevation-mesh-label',
  type: 'symbol',
  source: 'elevation-mesh-source',
  'source-layer': 'elevation_mesh',
  layout: {
    'text-field': ['format', ['to-string', ['round', ['to-number', ['get', 'avg_elev'], 0]]], {}, 'm', {}],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 10,
    visibility: 'visible',
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': 'rgba(255,255,255,0.8)',
    'text-halo-width': 1.0,
  },
  minzoom: 11,
}
const elevationMeshHeight = {
  id: 'oh-elevation-mesh-height',
  type: 'fill-extrusion',
  source: 'elevation-mesh-source',
  'source-layer': 'elevation_mesh',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, 0,
      100, 500,
      500, 2500,
      1000, 5000,
      2000, 10000,
      3000, 15000,
    ],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'avg_elev'], 0],
      0, '#2d7d46',
      100, '#90ee90',
      500, '#f0e68c',
      1000, '#ff8c00',
      2000, '#ff3300',
      3000, '#8b4513',
    ],
    'fill-extrusion-opacity': 0.85,
  },
}
// 選挙区----------------------------------------------------------------------------------------------------------------
const senkyokuSource = {
  id: 'senkyoku-source',
  obj: {
    type: 'vector',
    // minzoom: 0,
    // maxzoom: 15,
    url: 'pmtiles://https://kenzkenz.net/pmtiles/senkyoku/senkyoku2022.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const senkyokuLayer = {
  id: 'oh-senkyoku',
  type: 'fill',
  source: 'senkyoku-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'gray',
  },
}
const senkyokuLayerLine = {
  id: 'oh-senkyoku_line',
  type: 'line',
  source: 'senkyoku-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0.5,
      11,
      1,
    ],
  },
}
const senkyokuLayerLabel = {
  id: 'oh-senkyoku-label',
  type: 'symbol',
  source: 'senkyoku-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'kuname'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 2],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
// 地価--------------------------------------------------------------------------------------------------------------
const chikaSource3d = {
  id: 'chika-source-3d',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chika/chika3d.pmtiles',
  },
}
const chikaLayerLabel = {
  id: 'oh-chika-label',
  type: 'symbol',
  source: 'chika-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'L01_025'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 2],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 12,
}
const chikaLayerPoint = {
  id: 'oh-koji_point',
  type: 'circle',
  source: 'koji-source',
  'source-layer': 'point',
  paint: {
    'circle-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'L01_008']],
      0,
      'white',
      500000,
      'red',
      50000000,
      'black',
    ],
    'circle-stroke-color': 'black',
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 1],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 8],
  },
}
const chikalayerheight = {
  id: 'oh-chika-height',
  type: 'fill-extrusion',
  source: 'chika-source-3d',
  'source-layer': 'polygon',
  filter: [
    '!=',
    ['get', 'L02_003'],
    '020', // フィールドL02_003が020のときは表示しない
  ],
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['get', 'L02_006'],
      0,
      50.0, // 高さの最小値を50mに設定
      50000000,
      5000.0, // 高さの最大値を5000mに設定
    ],
    'fill-extrusion-color': [
      'step',
      ['get', 'L02_006'],
      'rgb(245, 245, 81)', // 10万未満の色
      100000,
      'rgb(239, 211, 71)', // 10万から25万の色
      250000,
      'rgb(235, 178, 61)', // 25万から50万の色
      500000,
      'rgb(231, 145, 52)', // 50万から75万の色
      750000,
      'rgb(226, 84, 39)', // 75万から100万の色
      1000000,
      'rgb(225, 61, 35)', // 100万から1000万の色
      10000000,
      'rgb(225, 49, 33)', // 1000万から2000万の色
      20000000,
      'rgb(180, 30, 25)', // 2000万から4000万をさらに赤黒く
      40000000,
      'rgb(160, 20, 20)', // 4000万以上をさらにさらに赤黒く
    ],
  },
}
// 公示価格--------------------------------------------------------------------------------------------------------------
const r07kojiSource = {
  id: 'r07koji-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz4.xsrv.jp/pmtiles/kojikakaku/r07kojikakaku.pmtiles',
  },
}
const r07kojiLayerLabel = {
  id: 'oh-r07koji-label',
  type: 'symbol',
  source: 'r07koji-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'L01_025'],
    'text-offset': [0, 2],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 12,
}
const r07kojiLayerPoint = {
  id: 'oh-r07koji_point',
  type: 'circle',
  source: 'r07koji-source',
  'source-layer': 'point',
  paint: {
    'circle-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'L01_008']],
      0,
      'white',
      500000,
      'red',
      50000000,
      'black',
    ],
    'circle-stroke-color': 'black',
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 1],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 8],
  },
}
const r07kojilayerheight = {
  id: 'oh-r07koji-height',
  type: 'fill-extrusion',
  source: 'r07koji-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'L01_008']],
      0,
      50.0, // 高さの最小値を50mに設定
      50000000,
      5000.0, // 高さの最大値を5000mに設定
    ],
    'fill-extrusion-color': [
      'step',
      ['get', 'L01_008'],
      'rgb(245, 245, 81)', // 10万未満の色
      100000,
      'rgb(239, 211, 71)', // 10万から25万の色
      250000,
      'rgb(235, 178, 61)', // 25万から50万の色
      500000,
      'rgb(231, 145, 52)', // 50万から75万の色
      750000,
      'rgb(226, 84, 39)', // 75万から100万の色
      1000000,
      'rgb(225, 61, 35)', // 100万から1000万の色
      10000000,
      'rgb(225, 49, 33)', // 1000万から2000万の色
      20000000,
      'rgb(180, 30, 25)', // 2000万から4000万をさらに赤黒く
      40000000,
      'rgb(160, 20, 20)', // 4000万以上をさらにさらに赤黒く
    ],
  },
}

const kojiSource = {
  id: 'koji-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kojikakaku/kojikakaku2.pmtiles',
  },
}
const kojiLayerLabel = {
  id: 'oh-koji-label',
  type: 'symbol',
  source: 'koji-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'L01_025'],
    'text-offset': [0, 2],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 1)',
    'text-halo-color': 'rgba(0,0,0,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 12,
}
const kojiLayerPoint = {
  id: 'oh-koji_point',
  type: 'circle',
  source: 'koji-source',
  'source-layer': 'point',
  paint: {
    'circle-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'L01_008']],
      0,
      'white',
      500000,
      'red',
      50000000,
      'black',
    ],
    'circle-stroke-color': 'black',
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 1],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 8],
  },
}
const kojilayerheight = {
  id: 'oh-koji-height',
  type: 'fill-extrusion',
  source: 'koji-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'L01_008']],
      0,
      50.0, // 高さの最小値を50mに設定
      50000000,
      5000.0, // 高さの最大値を5000mに設定
    ],
    'fill-extrusion-color': [
      'step',
      ['get', 'L01_008'],
      'rgb(245, 245, 81)', // 10万未満の色
      100000,
      'rgb(239, 211, 71)', // 10万から25万の色
      250000,
      'rgb(235, 178, 61)', // 25万から50万の色
      500000,
      'rgb(231, 145, 52)', // 50万から75万の色
      750000,
      'rgb(226, 84, 39)', // 75万から100万の色
      1000000,
      'rgb(225, 61, 35)', // 100万から1000万の色
      10000000,
      'rgb(225, 49, 33)', // 1000万から2000万の色
      20000000,
      'rgb(180, 30, 25)', // 2000万から4000万をさらに赤黒く
      40000000,
      'rgb(160, 20, 20)', // 4000万以上をさらにさらに赤黒く
    ],
  },
}
// 神社 --------------------------------------------------------------------------------------------
const jinjyaSource = {
  id: 'jinjya-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/jinjya/jinjya.pmtiles',
  },
}
const jinjyaLayer = {
  id: 'oh-jinjya-layer',
  type: 'circle',
  source: 'jinjya-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      2,
      1,
      4,
      3,
      7,
      6,
      11,
      10,
    ],
  },
}
const jinjyaLayerLabel = {
  id: 'oh-jinjya-label',
  type: 'symbol',
  source: 'jinjya-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 9,
}
// 道の駅 --------------------------------------------------------------------------------------------
const michinoekiSource = {
  id: 'michinoeki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/michinoeki/michinoeki.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const michinoekiLayer = {
  id: 'oh-michinoeki',
  type: 'circle',
  source: 'michinoeki-source',
  'source-layer': 'michinoeki',
  paint: {
    'circle-color': 'navy',
    'circle-radius': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      2,
      1,
      4,
      3,
      7,
      6,
      11,
      10,
    ],
  },
}
const michinoekiLayerLabel = {
  id: 'oh-michinoeki-label',
  type: 'symbol',
  source: 'michinoeki-source',
  'source-layer': 'michinoeki',
  layout: {
    'text-field': ['get', 'P35_006'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 9,
}
// 東京地震----------------------------------------------------------------------------------------------------------------
const tokyojishinSource = {
  id: 'tokyojishin-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tokyojishin/tokyojishin.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const tokyojishinLayerSogo = {
  id: 'oh-tokyojishin',
  type: 'fill',
  source: 'tokyojishin-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', '総合_ラ'],
      1,
      'rgb(162,209,229,0.8)',
      2,
      'rgb(125,170,118,0.8)',
      3,
      'rgb(206,135,52,0.8)',
      4,
      'rgb(213,64,43,0.8)',
      5,
      'rgb(79,19,19,0.8)',
      'red', // Default color (if no match)
    ],
  },
}
export const tokyojishinLayer = {
  id: 'oh-tokyojishin',
  type: 'fill',
  source: 'tokyojishin-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'gray',
  },
}
const tokyojishinLayerLine = {
  id: 'oh-tokyojishin_line',
  type: 'line',
  source: 'tokyojishin-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0.5,
      11,
      1,
    ],
  },
}
const tokyojishinLayerLabel = {
  id: 'oh-tokyojishin-label',
  type: 'symbol',
  source: 'tokyojishin-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', '町丁目名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 2],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 16,
}
// export const tokyojishinheight = {
//     id: 'oh-tokyojishin-height',
//     type: 'fill-extrusion',
//     source: "tokyojishin-source",
//     "source-layer": "polygon",
//     paint: {
//         'fill-extrusion-height': [
//             'interpolate',
//             ['linear'],
//             ["to-number",['get', '総合_順']],
//             0, 10,
//             6000, 3000
//         ],
//         'fill-extrusion-color': [
//             'interpolate',
//             ['linear'],
//             ["to-number",['get', '総合_順']],
//             0, 'white',
//             4000, 'red',
//             6000, 'black'
//         ]
//     }
// }
const tokyojishinheightSogo = {
  id: 'oh-tokyojishin-height',
  type: 'fill-extrusion',
  source: 'tokyojishin-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': [
      'match',
      ['get', '総合_ラ'],
      1,
      100,
      2,
      500,
      3,
      1500,
      4,
      2000,
      5,
      2500,
      0, // Default color (if no match)
    ],
    'fill-extrusion-color': [
      'match',
      ['get', '総合_ラ'],
      1,
      'rgb(162,209,229,0.8)',
      2,
      'rgb(125,170,118,0.8)',
      3,
      'rgb(206,135,52,0.8)',
      4,
      'rgb(213,64,43,0.8)',
      5,
      'rgb(79,19,19,0.8)',
      'red',
    ],
  },
}
// ハザードマップ等--------------------------------------------------------------------------------------------------------
// 避難所洪水 --------------------------------------------------------------------------------------------
const hinanjyoKozuiSource = {
  id: 'hinanjyo-kozui-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/hinanjyo/hinanjyo-kozui.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const hinanjyoKozuiLayer = {
  id: 'oh-hinanjyo-kozui',
  type: 'circle',
  source: 'hinanjyo-kozui-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'steelblue',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 10],
  },
}
const hinanjyoKozuiLayerLabel = {
  id: 'oh-hinanjyo-kozui-label',
  type: 'symbol',
  source: 'hinanjyo-kozui-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 13,
}
// 避難所津波 --------------------------------------------------------------------------------------------
const hinanjyoTsunamiSource = {
  id: 'hinanjyo-tsunami-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/hinanjyo/hinanjyo-tsunami.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const hinanjyoTsunamiLayer = {
  id: 'oh-hinanjyo-tsunami',
  type: 'circle',
  source: 'hinanjyo-tsunami-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'blue',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 10],
  },
}
const hinanjyoTsunamiLayerLabel = {
  id: 'oh-hinanjyo-tsunami-label',
  type: 'symbol',
  source: 'hinanjyo-tsunami-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  minzoom: 13,
}
// 避難所土石流 --------------------------------------------------------------------------------------------
const hinanjyoDosekiryuSource = {
  id: 'hinanjyo-dosekiryu-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/hinanjyo/hinanjyo-dosekiryu.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const hinanjyoDosekiryuLayer = {
  id: 'oh-hinanjyo-dosekiryu',
  type: 'circle',
  source: 'hinanjyo-dosekiryu-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'tomato',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 10],
  },
}
const hinanjyoDosekiryuLayerLabel = {
  id: 'oh-hinanjyo-dosekiryu-label',
  type: 'symbol',
  source: 'hinanjyo-dosekiryu-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 13,
}
// 警察署 --------------------------------------------------------------------------------------------
const keisatsuSource = {
  id: 'keisatsu-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/keisatsu/keisatsu.pmtiles',
  },
}
// 警察署管轄区域（ポリゴン）
const keisatsuPolygonLayer = {
  id: 'oh-keisatsu-polygon',
  type: 'fill',
  source: 'keisatsu-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0, 100, 200, 0.15)',
    'fill-outline-color': 'rgba(0, 100, 200, 0.6)',
  },
  minzoom: 0,
}
const keisatsuPolygonLineLayer = {
  id: 'oh-keisatsu-polygon-line',
  type: 'line',
  source: 'keisatsu-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 100, 200, 0.8)',
    'line-width': 1.5,
  },
  minzoom: 0,
}
// 警察署位置（ポイント）
const keisatsuPointLayer = {
  id: 'oh-keisatsu-point',
  type: 'circle',
  source: 'keisatsu-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#1565c0',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const keisatsuPointLayerLabel = {
  id: 'oh-keisatsu-point-label',
  type: 'symbol',
  source: 'keisatsu-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P18_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#1565c0',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// 郵便局 --------------------------------------------------------------------------------------------
const yubinkyokuSource = {
  id: 'yubinkyoku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yubinkyoku/yubinkyoku.pmtiles',
  },
}
const yubinkyokuPointLayer = {
  id: 'oh-yubinkyoku-point',
  type: 'circle',
  source: 'yubinkyoku-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#e53935',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const yubinkyokuPointLayerLabel = {
  id: 'oh-yubinkyoku-point-label',
  type: 'symbol',
  source: 'yubinkyoku-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P30_005'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#e53935',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// 消防署 --------------------------------------------------------------------------------------------
const shoboshoSource = {
  id: 'shobosho-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shobosho/shobosho.pmtiles',
  },
}
// 消防署管轄区域（ポリゴン）
const shoboshoPolygonLayer = {
  id: 'oh-shobosho-polygon',
  type: 'fill',
  source: 'shobosho-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 87, 34, 0.15)',
    'fill-outline-color': 'rgba(255, 87, 34, 0.6)',
  },
  minzoom: 0,
}
const shoboshoPolygonLineLayer = {
  id: 'oh-shobosho-polygon-line',
  type: 'line',
  source: 'shobosho-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(255, 87, 34, 0.8)',
    'line-width': 1.5,
  },
  minzoom: 0,
}
// 消防署位置（ポイント）
const shoboshoPointLayer = {
  id: 'oh-shobosho-point',
  type: 'circle',
  source: 'shobosho-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#ff5722',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const shoboshoPointLayerLabel = {
  id: 'oh-shobosho-point-label',
  type: 'symbol',
  source: 'shobosho-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P17_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#ff5722',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// 燃料給油所 --------------------------------------------------------------------------------------------
const gasStationSource = {
  id: 'gas-station-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gas_station/gas_station.pmtiles',
  },
}
const gasStationPointLayer = {
  id: 'oh-gas-station-point',
  type: 'circle',
  source: 'gas-station-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#43a047',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const gasStationPointLayerLabel = {
  id: 'oh-gas-station-point-label',
  type: 'symbol',
  source: 'gas-station-source',
  'source-layer': 'point',
  layout: {
    'text-field': 'GS',
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 10,
  },
  paint: {
    'text-color': '#fff',
  },
  minzoom: 12,
}
// 都市公園 --------------------------------------------------------------------------------------------
const cityParkSource = {
  id: 'city-park-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/city_park/city_park.pmtiles',
  },
}
const cityParkPointLayer = {
  id: 'oh-city-park-point',
  type: 'circle',
  source: 'city-park-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#66bb6a',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const cityParkPointLayerLabel = {
  id: 'oh-city-park-point-label',
  type: 'symbol',
  source: 'city-park-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P13_003'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.2],
  },
  paint: {
    'text-color': '#2e7d32',
    'text-halo-color': '#fff',
    'text-halo-width': 1,
  },
  minzoom: 12,
}
// 工業用地 --------------------------------------------------------------------------------------------
const industrialLandSource = {
  id: 'industrial-land-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/industrial_land/industrial_land.pmtiles',
  },
}
const industrialComplexLayer = {
  id: 'oh-industrial-complex',
  type: 'fill',
  source: 'industrial-land-source',
  'source-layer': 'complex',
  paint: {
    'fill-color': 'rgba(123, 31, 162, 0.8)',
  },
  minzoom: 0,
}
const industrialComplexLineLayer = {
  id: 'oh-industrial-complex-line',
  type: 'line',
  source: 'industrial-land-source',
  'source-layer': 'complex',
  paint: {
    'line-color': '#7b1fa2',
    'line-width': 2,
  },
  minzoom: 0,
}
const industrialIsolatedLayer = {
  id: 'oh-industrial-isolated',
  type: 'fill',
  source: 'industrial-land-source',
  'source-layer': 'isolated',
  paint: {
    'fill-color': 'rgba(156, 39, 176, 0.8)',
  },
  minzoom: 0,
}
const industrialIsolatedLineLayer = {
  id: 'oh-industrial-isolated-line',
  type: 'line',
  source: 'industrial-land-source',
  'source-layer': 'isolated',
  paint: {
    'line-color': '#9c27b0',
    'line-width': 2,
  },
  minzoom: 0,
}
// 漁港 --------------------------------------------------------------------------------------------
const gyokoSource = {
  id: 'gyoko-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gyoko/gyoko.pmtiles',
  },
}
// 漁港区域（ライン）
const gyokoLineLayer = {
  id: 'oh-gyoko-line',
  type: 'line',
  source: 'gyoko-source',
  'source-layer': 'line',
  paint: {
    'line-color': 'rgba(0, 150, 200, 0.8)',
    'line-width': 2,
  },
  minzoom: 8,
}
// 漁港位置（ポイント）
const gyokoPointLayer = {
  id: 'oh-gyoko-point',
  type: 'circle',
  source: 'gyoko-source',
  'source-layer': 'point',
  paint: {
    'circle-color': '#0097a7',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 6, 14, 10],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const gyokoPointLayerLabel = {
  id: 'oh-gyoko-point-label',
  type: 'symbol',
  source: 'gyoko-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'C09_002'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#0097a7',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 過疎地域（平成12年） --------------------------------------------------------------------------------------------
const kasoH12Source = {
  id: 'kaso-h12-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kaso/kaso_h12.pmtiles',
  },
}
const kasoH12Layer = {
  id: 'oh-kaso-h12',
  type: 'fill',
  source: 'kaso-h12-source',
  'source-layer': 'kaso',
  paint: {
    'fill-color': 'rgba(139, 69, 19, 0.4)',
    'fill-outline-color': 'rgba(139, 69, 19, 0.8)',
  },
  minzoom: 0,
}
const kasoH12LineLayer = {
  id: 'oh-kaso-h12-line',
  type: 'line',
  source: 'kaso-h12-source',
  'source-layer': 'kaso',
  paint: {
    'line-color': '#8b4513',
    'line-width': 1,
  },
  minzoom: 0,
}
const kasoH12LabelLayer = {
  id: 'oh-kaso-h12-label',
  type: 'symbol',
  source: 'kaso-h12-source',
  'source-layer': 'kaso',
  layout: {
    'text-field': ['get', 'A17_006'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
  },
  paint: {
    'text-color': '#8b4513',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 振興山村（2016年） --------------------------------------------------------------------------------------------
const shinkoSansonSource = {
  id: 'shinko-sanson-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shinko_sanson/shinko_sanson.pmtiles',
  },
}
const shinkoSansonLayer = {
  id: 'oh-shinko-sanson',
  type: 'fill',
  source: 'shinko-sanson-source',
  'source-layer': 'sanson',
  paint: {
    'fill-color': 'rgba(34, 139, 34, 0.4)',
    'fill-outline-color': 'rgba(34, 139, 34, 0.8)',
  },
  minzoom: 0,
}
const shinkoSansonLineLayer = {
  id: 'oh-shinko-sanson-line',
  type: 'line',
  source: 'shinko-sanson-source',
  'source-layer': 'sanson',
  paint: {
    'line-color': '#228b22',
    'line-width': 1,
  },
  minzoom: 0,
}
const shinkoSansonLabelLayer = {
  id: 'oh-shinko-sanson-label',
  type: 'symbol',
  source: 'shinko-sanson-source',
  'source-layer': 'sanson',
  layout: {
    'text-field': ['get', 'A24_006'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
  },
  paint: {
    'text-color': '#228b22',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 特定農山村地域（平成28年） --------------------------------------------------------------------------------------------
const tokuteiNosanSource = {
  id: 'tokutei-nosan-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tokutei_nosan/tokutei_nosan.pmtiles',
  },
}
const tokuteiNosanLayer = {
  id: 'oh-tokutei-nosan',
  type: 'fill',
  source: 'tokutei-nosan-source',
  'source-layer': 'nogyo',
  paint: {
    'fill-color': 'rgba(255, 193, 7, 0.4)',
    'fill-outline-color': 'rgba(255, 152, 0, 0.8)',
  },
  minzoom: 0,
}
const tokuteiNosanLineLayer = {
  id: 'oh-tokutei-nosan-line',
  type: 'line',
  source: 'tokutei-nosan-source',
  'source-layer': 'nogyo',
  paint: {
    'line-color': '#ff9800',
    'line-width': 1,
  },
  minzoom: 0,
}
const tokuteiNosanLabelLayer = {
  id: 'oh-tokutei-nosan-label',
  type: 'symbol',
  source: 'tokutei-nosan-source',
  'source-layer': 'nogyo',
  layout: {
    'text-field': ['get', 'A25_006'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
  },
  paint: {
    'text-color': '#e65100',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 豪雪地帯（国土数値情報） --------------------------------------------------------------------------------------------
const gousetsuChitaiSource = {
  id: 'gousetsu-chitai-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gousetsu_chitai/gousetsu_chitai.pmtiles',
  },
}
// ポリゴン（雪害地区）
const gousetsuChitaiLayer = {
  id: 'oh-gousetsu-chitai',
  type: 'fill',
  source: 'gousetsu-chitai-source',
  'source-layer': 'snow_disaster',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'A22_100005'], 1], 'rgba(0, 100, 200, 0.5)', // 特別豪雪地帯（青）
      'rgba(135, 206, 250, 0.4)' // 豪雪地帯（水色）
    ],
    'fill-outline-color': [
      'case',
      ['==', ['get', 'A22_100005'], 1], 'rgba(0, 50, 150, 0.8)',
      'rgba(70, 130, 180, 0.8)'
    ],
  },
  minzoom: 0,
}
const gousetsuChitaiLineLayer = {
  id: 'oh-gousetsu-chitai-line',
  type: 'line',
  source: 'gousetsu-chitai-source',
  'source-layer': 'snow_disaster',
  paint: {
    'line-color': [
      'case',
      ['==', ['get', 'A22_100005'], 1], '#003399',
      '#4682b4'
    ],
    'line-width': 1,
  },
  minzoom: 0,
}
const gousetsuChitaiLabelLayer = {
  id: 'oh-gousetsu-chitai-label',
  type: 'symbol',
  source: 'gousetsu-chitai-source',
  'source-layer': 'snow_disaster',
  layout: {
    'text-field': ['get', 'A22_100002'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
  },
  paint: {
    'text-color': '#003366',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 8,
}
// ポイント（気象観測点・最深積雪）
const gousetsuPointLayer = {
  id: 'oh-gousetsu-point',
  type: 'circle',
  source: 'gousetsu-chitai-source',
  'source-layer': 'weather_points',
  paint: {
    'circle-radius': 5,
    'circle-color': '#ff6b6b',
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1.5,
  },
  minzoom: 0,
}
const gousetsuPointLabelLayer = {
  id: 'oh-gousetsu-point-label',
  type: 'symbol',
  source: 'gousetsu-chitai-source',
  'source-layer': 'weather_points',
  layout: {
    'text-field': ['get', 'A22_000002'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 10,
    'text-offset': [0, 1.2],
    'text-anchor': 'top',
  },
  paint: {
    'text-color': '#c0392b',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// ヘリポート（平成25年） --------------------------------------------------------------------------------------------
const heliportSource = {
  id: 'heliport-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/heliport/heliport.pmtiles',
  },
}
const heliportLayer = {
  id: 'oh-heliport',
  type: 'circle',
  source: 'heliport-source',
  'source-layer': 'heliport',
  paint: {
    'circle-radius': 6,
    'circle-color': '#1565c0',
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 2,
  },
  minzoom: 0,
}
const heliportLabelLayer = {
  id: 'oh-heliport-label',
  type: 'symbol',
  source: 'heliport-source',
  'source-layer': 'heliport',
  layout: {
    'text-field': ['get', 'N11_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#1565c0',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// 上水道（給水区域）PMTiles --------------------------------------------------------------------------------------------
const josuidouAreaSource = {
  id: 'josuidou-area-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/josuidou/josuidou_area.pmtiles',
  },
}
const josuidouAreaLayer = {
  id: 'oh-josuidou-area',
  type: 'fill',
  source: 'josuidou-area-source',
  'source-layer': 'josuidou_area',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.15)',
      ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.15)',
      ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.15)',
      'rgba(30, 136, 229, 0.15)',
    ],
    'fill-outline-color': [
      'case',
      ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.6)',
      ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.6)',
      ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.6)',
      'rgba(30, 136, 229, 0.6)',
    ],
  },
  minzoom: 0,
}
const josuidouAreaLineLayer = {
  id: 'oh-josuidou-area-line',
  type: 'line',
  source: 'josuidou-area-source',
  'source-layer': 'josuidou_area',
  paint: {
    'line-color': [
      'case',
      ['==', ['get', 'P21A_003'], 1], 'rgba(30, 136, 229, 0.8)',
      ['==', ['get', 'P21A_003'], 2], 'rgba(67, 160, 71, 0.8)',
      ['==', ['get', 'P21A_003'], 3], 'rgba(255, 152, 0, 0.8)',
      'rgba(30, 136, 229, 0.8)',
    ],
    'line-width': 1.5,
  },
  minzoom: 0,
}
// 上水道（浄水場）GeoJSON --------------------------------------------------------------------------------------------
const josuidouPointSource = {
  id: 'josuidou-point-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/pmtiles/josuidou/josuidou_point.geojson',
  },
}
const josuidouPointLayer = {
  id: 'oh-josuidou-point',
  type: 'circle',
  source: 'josuidou-point-source',
  paint: {
    'circle-color': '#0277bd',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 5, 14, 8],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#fff',
  },
  minzoom: 0,
}
const josuidouPointLabelLayer = {
  id: 'oh-josuidou-point-label',
  type: 'symbol',
  source: 'josuidou-point-source',
  layout: {
    'text-field': ['get', 'P21B_003'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#0277bd',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// GSI単写真 --------------------------------------------------------------------------------------------
const gsiPpFullSource = {
  id: 'gsi-pp-full-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gsi_pp/gsi_pp_full.pmtiles',
  },
}
const gsiPpFullLayer = {
  id: 'oh-gsi-pp-full',
  type: 'circle',
  source: 'gsi-pp-full-source',
  'source-layer': 'pp',
  paint: {
    'circle-color': [
      'match',
      ['get', '撮影計画機関'],
      '国土地理院', '#1976D2',  // 青
      '米軍', '#4CAF50',        // 緑
      '陸軍', '#795548',        // 茶
      '#9E9E9E'                 // その他（グレー）
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 3, 12, 5, 14, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 1],
  },
  minzoom: 10,  // Z10以上でのみ表示（低ズームは重いため）
  maxzoom: 24,
}
// 日本の主な山岳（国土地理院） --------------------------------------------------------------------------------------------
const mountainSource = {
  id: 'mountain-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/mountain.geojson',
  },
}
const mountainLayer = {
  id: 'oh-mountain',
  type: 'circle',
  source: 'mountain-source',
  paint: {
    'circle-color': [
      'interpolate', ['linear'], ['get', '標高'],
      0, '#4CAF50',
      500, '#8BC34A',
      1000, '#FFC107',
      1500, '#FF9800',
      2000, '#FF5722',
      2500, '#D32F2F',
      3000, '#B71C1C',
      3776, '#880E4F',
    ],
    'circle-radius': [
      'interpolate', ['linear'], ['zoom'],
      0, 3,
      8, 5,
      12, 8,
      16, 12,
    ],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1.5,
    'circle-opacity': 0.9,
  },
  minzoom: 0,
}
const mountainLabelLayer = {
  id: 'oh-mountain-label',
  type: 'symbol',
  source: 'mountain-source',
  layout: {
    'text-field': ['concat', ['get', '山名'], '\n', ['to-string', ['get', '標高']], 'm'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 13,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#5D4037',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 8,
}
// 自然災害伝承碑 --------------------------------------------------------------------------------------------
const denshoSource = {
  id: 'densho-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/pmtiles/densho/20260129.geojson',
  },
}
const denshoLayer = {
  id: 'oh-densho',
  type: 'symbol',
  source: 'densho-source',
  layout: {
    'icon-image': [
      'case',
      ['any', ['in', '地震', ['get', '災害種別']]],
      'densho-jishin',
      ['any', ['in', '津波', ['get', '災害種別']]],
      'densho-tsunami',
      ['any',
        ['in', '土砂', ['get', '災害種別']],
        ['in', '崩壊', ['get', '災害種別']],
      ],
      'densho-dosya',
      ['any',
        ['in', '洪水', ['get', '災害種別']],
        ['in', '水害', ['get', '災害種別']],
      ],
      'densho-suigai',
      ['any',
        ['in', '火災', ['get', '災害種別']],
      ],
      'densho-kasai',
      ['any',
        ['in', '火山', ['get', '災害種別']],
        ['in', '噴火', ['get', '災害種別']],
      ],
      'densho-kazan',
      ['any',
        ['in', '高潮', ['get', '災害種別']],
      ],
      'densho-takashio',
      'densho-default',
    ],
    'icon-size': 1.0,
    'icon-allow-overlap': true,
  },
  minzoom: 0,
}
const denshoLabelLayer = {
  id: 'oh-densho-label',
  type: 'symbol',
  source: 'densho-source',
  layout: {
    'text-field': ['get', '碑名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 16,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#8B4513',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 都道府県指定文化財 --------------------------------------------------------------------------------------------
const bunkazaiSource = {
  id: 'bunkazai-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/bunkazai.geojson',
  },
}
const bunkazaiLayer = {
  id: 'oh-bunkazai',
  type: 'circle',
  source: 'bunkazai-source',
  paint: {
    'circle-color': [
      'match',
      ['get', '種別'],
      '建造物', '#8B4513',
      '史跡', '#2E7D32',
      '天然記念物', '#1565C0',
      '有形民俗文化財', '#7B1FA2',
      '無形民俗文化財', '#7B1FA2',
      '名勝', '#F57C00',
      '芸能', '#C2185B',
      '#795548'
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 8, 4, 12, 6, 16, 10],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
  minzoom: 0,
}
const bunkazaiLabelLayer = {
  id: 'oh-bunkazai-label',
  type: 'symbol',
  source: 'bunkazai-source',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 1.2],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 12,
}
// =====================================================================================================================
// OH2からの遺跡・文化財レイヤー移植
// =====================================================================================================================

// --- 全国規模 ---

// 国指定文化財等データベース（文化庁）--------------------------------------------------------------------------------------------
const bunkazaiDbSource = {
  id: 'bunkazaidb-source',
  obj: {
    type: 'vector',
    tiles: ['https://mtile.pref.miyazaki.lg.jp/tile/mvt/bunkatyoudb/{z}/{x}/{y}.mvt'],
    maxzoom: 15,
  },
}
const bunkazaiDbLayer = {
  id: 'oh-bunkazaidb',
  type: 'circle',
  source: 'bunkazaidb-source',
  'source-layer': 'bunkatyoudbgeojson',
  paint: {
    'circle-color': '#2E7D32',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
const bunkazaiDbLabelLayer = {
  id: 'oh-bunkazaidb-label',
  type: 'symbol',
  source: 'bunkazaidb-source',
  'source-layer': 'bunkatyoudbgeojson',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#2E7D32',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 全国の文化財データ（S50・国土数値情報）--------------------------------------------------------------------------------------------
const zenkokuBunkazaiSource = {
  id: 'zenkoku-bunkazai-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/zenkokubunkazai/z.pmtiles',
  },
}
const zenkokuBunkazaiLayer = {
  id: 'oh-zenkoku-bunkazai',
  type: 'circle',
  source: 'zenkoku-bunkazai-source',
  'source-layer': 'z',
  paint: {
    'circle-color': '#FF8C00',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 5, 14, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
const zenkokuBunkazaiLabelLayer = {
  id: 'oh-zenkoku-bunkazai-label',
  type: 'symbol',
  source: 'zenkoku-bunkazai-source',
  'source-layer': 'z',
  layout: {
    'text-field': ['get', 'P01_002'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#FF8C00',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 全国縄文・弥生集落遺跡 --------------------------------------------------------------------------------------------
const yayoiIsekiSource = {
  id: 'yayoi-iseki-source',
  obj: {
    type: 'vector',
    tiles: ['https://mtile.pref.miyazaki.lg.jp/tile/mvt/iseki/yoyoi/{z}/{x}/{y}.mvt'],
    maxzoom: 15,
  },
}
const yayoiIsekiLayer = {
  id: 'oh-yayoi-iseki',
  type: 'circle',
  source: 'yayoi-iseki-source',
  'source-layer': 'yayoi2geojson',
  paint: {
    'circle-color': '#8B4513',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
const yayoiIsekiLabelLayer = {
  id: 'oh-yayoi-iseki-label',
  type: 'symbol',
  source: 'yayoi-iseki-source',
  'source-layer': 'yayoi2geojson',
  layout: {
    'text-field': ['get', '遺跡名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#8B4513',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// --- 各県・市の埋蔵文化財 ---

// 北海道埋蔵文化財包蔵地 --------------------------------------------------------------------------------------------
const hokkaidoMaibunSource = {
  id: 'hokkaido-maibun-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.github.io/hokkaidou/{z}/{x}/{y}.mvt'],
    maxzoom: 17,
  },
}
const hokkaidoMaibunFill = {
  id: 'oh-hokkaido-maibun-fill',
  type: 'fill',
  source: 'hokkaido-maibun-source',
  'source-layer': 'merged',
  paint: { 'fill-color': 'rgba(211,47,47,0.35)', 'fill-outline-color': '#D32F2F' },
}
const hokkaidoMaibunLine = {
  id: 'oh-hokkaido-maibun-line',
  type: 'line',
  source: 'hokkaido-maibun-source',
  'source-layer': 'merged',
  paint: { 'line-color': '#D32F2F', 'line-width': 1.5 },
}
const hokkaidoMaibunLabel = {
  id: 'oh-hokkaido-maibun-label',
  type: 'symbol',
  source: 'hokkaido-maibun-source',
  'source-layer': 'merged',
  layout: {
    'text-field': ['get', 'SiteName'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#D32F2F',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 岩手県遺跡地図 --------------------------------------------------------------------------------------------
const iwateIsekiSource = {
  id: 'iwate-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/iwateiseki/i.pmtiles',
  },
}
const iwateIsekiFill = {
  id: 'oh-iwate-iseki-fill',
  type: 'fill',
  source: 'iwate-iseki-source',
  'source-layer': 'i',
  paint: { 'fill-color': 'rgba(255,140,0,0.35)', 'fill-outline-color': '#FF8C00' },
}
const iwateIsekiLine = {
  id: 'oh-iwate-iseki-line',
  type: 'line',
  source: 'iwate-iseki-source',
  'source-layer': 'i',
  paint: { 'line-color': '#FF8C00', 'line-width': 1.5 },
}
const iwateIsekiLabel = {
  id: 'oh-iwate-iseki-label',
  type: 'symbol',
  source: 'iwate-iseki-source',
  'source-layer': 'i',
  layout: {
    'text-field': ['get', 'ISEKIMEI'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#FF8C00',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 宮城県遺跡地図 --------------------------------------------------------------------------------------------
const miyagiIsekiSource = {
  id: 'miyagi-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/miyagiiseki/m.pmtiles',
  },
}
const miyagiIsekiFill = {
  id: 'oh-miyagi-iseki-fill',
  type: 'fill',
  source: 'miyagi-iseki-source',
  'source-layer': 'm',
  paint: { 'fill-color': 'rgba(21,101,192,0.35)', 'fill-outline-color': '#1565C0' },
}
const miyagiIsekiLine = {
  id: 'oh-miyagi-iseki-line',
  type: 'line',
  source: 'miyagi-iseki-source',
  'source-layer': 'm',
  paint: { 'line-color': '#1565C0', 'line-width': 1.5 },
}
const miyagiIsekiLabel = {
  id: 'oh-miyagi-iseki-label',
  type: 'symbol',
  source: 'miyagi-iseki-source',
  'source-layer': 'm',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#1565C0',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 富山県埋蔵文化財 --------------------------------------------------------------------------------------------
const toyamaIsekiSource = {
  id: 'toyama-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toyamaiseki/ti.pmtiles',
  },
}
const toyamaIsekiFill = {
  id: 'oh-toyama-iseki-fill',
  type: 'fill',
  source: 'toyama-iseki-source',
  'source-layer': 'ti',
  paint: { 'fill-color': 'rgba(123,31,162,0.35)', 'fill-outline-color': '#7B1FA2' },
}
const toyamaIsekiLine = {
  id: 'oh-toyama-iseki-line',
  type: 'line',
  source: 'toyama-iseki-source',
  'source-layer': 'ti',
  paint: { 'line-color': '#7B1FA2', 'line-width': 1.5 },
}
const toyamaIsekiLabel = {
  id: 'oh-toyama-iseki-label',
  type: 'symbol',
  source: 'toyama-iseki-source',
  'source-layer': 'ti',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#7B1FA2',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 東京都文化財 --------------------------------------------------------------------------------------------
const tokyoBunkazaiSource = {
  id: 'tokyo-bunkazai-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.xsrv.jp/open-hinata/geojson/tokyobunkazai.geojson',
  },
}
const tokyoBunkazaiLayer = {
  id: 'oh-tokyo-bunkazai',
  type: 'circle',
  source: 'tokyo-bunkazai-source',
  paint: {
    'circle-color': '#2E7D32',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
const tokyoBunkazaiLabelLayer = {
  id: 'oh-tokyo-bunkazai-label',
  type: 'symbol',
  source: 'tokyo-bunkazai-source',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#2E7D32',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 世田谷区埋蔵文化財 --------------------------------------------------------------------------------------------
const setagayaMaibunSource = {
  id: 'setagaya-maibun-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/setagayakuiseki/s.pmtiles',
  },
}
const setagayaMaibunFill = {
  id: 'oh-setagaya-maibun-fill',
  type: 'fill',
  source: 'setagaya-maibun-source',
  'source-layer': 's',
  paint: { 'fill-color': 'rgba(255,140,0,0.35)', 'fill-outline-color': '#FF8C00' },
}
const setagayaMaibunLine = {
  id: 'oh-setagaya-maibun-line',
  type: 'line',
  source: 'setagaya-maibun-source',
  'source-layer': 's',
  paint: { 'line-color': '#FF8C00', 'line-width': 1.5 },
}
const setagayaMaibunLabel = {
  id: 'oh-setagaya-maibun-label',
  type: 'symbol',
  source: 'setagaya-maibun-source',
  'source-layer': 's',
  layout: {
    'text-field': ['get', 'SAFIELD005'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#FF8C00',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 四日市市遺跡 --------------------------------------------------------------------------------------------
const yokkaichiIsekiSource = {
  id: 'yokkaichi-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yokkaichisiiseki/y.pmtiles',
  },
}
const yokkaichiIsekiFill = {
  id: 'oh-yokkaichi-iseki-fill',
  type: 'fill',
  source: 'yokkaichi-iseki-source',
  'source-layer': 'y',
  paint: { 'fill-color': 'rgba(230,81,0,0.35)', 'fill-outline-color': '#E65100' },
}
const yokkaichiIsekiLine = {
  id: 'oh-yokkaichi-iseki-line',
  type: 'line',
  source: 'yokkaichi-iseki-source',
  'source-layer': 'y',
  paint: { 'line-color': '#E65100', 'line-width': 1.5 },
}
const yokkaichiIsekiLabel = {
  id: 'oh-yokkaichi-iseki-label',
  type: 'symbol',
  source: 'yokkaichi-iseki-source',
  'source-layer': 'y',
  layout: {
    'text-field': ['get', '遺跡名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#E65100',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 岡山県埋蔵文化財 --------------------------------------------------------------------------------------------
const okayamaMaibunSource = {
  id: 'okayama-maibun-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.github.io/okayama/{z}/{x}/{y}.mvt'],
    maxzoom: 15,
  },
}
const okayamaMaibunFill = {
  id: 'oh-okayama-maibun',
  type: 'fill',
  source: 'okayama-maibun-source',
  'source-layer': 'okayama',
  paint: { 'fill-color': 'rgba(46,125,50,0.35)', 'fill-outline-color': '#2E7D32' },
}
const okayamaMaibunLine = {
  id: 'oh-okayama-maibun-line',
  type: 'line',
  source: 'okayama-maibun-source',
  'source-layer': 'okayama',
  paint: { 'line-color': '#2E7D32', 'line-width': 1.5 },
}
const okayamaMaibunLabel = {
  id: 'oh-okayama-maibun-label',
  type: 'symbol',
  source: 'okayama-maibun-source',
  'source-layer': 'okayama',
  layout: {
    'text-field': ['get', '遺跡名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#2E7D32',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 和歌山県埋蔵文化財 --------------------------------------------------------------------------------------------
const wakayamaMaibunSource = {
  id: 'wakayama-maibun-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/wakayamakeniseki/w.pmtiles',
  },
}
const wakayamaMaibunFill = {
  id: 'oh-wakayama-maibun-fill',
  type: 'fill',
  source: 'wakayama-maibun-source',
  'source-layer': 'w',
  paint: { 'fill-color': 'rgba(0,131,143,0.35)', 'fill-outline-color': '#00838F' },
}
const wakayamaMaibunLine = {
  id: 'oh-wakayama-maibun-line',
  type: 'line',
  source: 'wakayama-maibun-source',
  'source-layer': 'w',
  paint: { 'line-color': '#00838F', 'line-width': 1.5 },
}
const wakayamaMaibunLabel = {
  id: 'oh-wakayama-maibun-label',
  type: 'symbol',
  source: 'wakayama-maibun-source',
  'source-layer': 'w',
  layout: {
    'text-field': ['get', '遺跡名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#00838F',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 山口県遺跡地図 --------------------------------------------------------------------------------------------
const yamaguchiIsekiSource = {
  id: 'yamaguchi-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yamaguchiiseki/y.pmtiles',
  },
}
const yamaguchiIsekiFill = {
  id: 'oh-yamaguchi-iseki-fill',
  type: 'fill',
  source: 'yamaguchi-iseki-source',
  'source-layer': 'y',
  paint: { 'fill-color': 'rgba(198,40,40,0.35)', 'fill-outline-color': '#C62828' },
}
const yamaguchiIsekiLine = {
  id: 'oh-yamaguchi-iseki-line',
  type: 'line',
  source: 'yamaguchi-iseki-source',
  'source-layer': 'y',
  paint: { 'line-color': '#C62828', 'line-width': 1.5 },
}
const yamaguchiIsekiLabel = {
  id: 'oh-yamaguchi-iseki-label',
  type: 'symbol',
  source: 'yamaguchi-iseki-source',
  'source-layer': 'y',
  layout: {
    'text-field': ['get', '遺跡名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#C62828',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 熊本県遺跡 --------------------------------------------------------------------------------------------
const kumamotoIsekiSource = {
  id: 'kumamoto-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kumamotoiseki/k.pmtiles',
  },
}
const kumamotoIsekiFill = {
  id: 'oh-kumamoto-iseki-fill',
  type: 'fill',
  source: 'kumamoto-iseki-source',
  'source-layer': 'k',
  filter: ['==', ['geometry-type'], 'Polygon'],
  paint: { 'fill-color': 'rgba(27,94,32,0.4)', 'fill-outline-color': '#1B5E20' },
}
const kumamotoIsekiLine = {
  id: 'oh-kumamoto-iseki-line',
  type: 'line',
  source: 'kumamoto-iseki-source',
  'source-layer': 'k',
  filter: ['in', ['geometry-type'], ['literal', ['Polygon', 'LineString']]],
  paint: { 'line-color': '#1B5E20', 'line-width': 1.5 },
}
const kumamotoIsekiCircle = {
  id: 'oh-kumamoto-iseki',
  type: 'circle',
  source: 'kumamoto-iseki-source',
  'source-layer': 'k',
  filter: ['==', ['geometry-type'], 'Point'],
  paint: {
    'circle-color': '#E65100',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 13, 5, 16, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
  minzoom: 10,
}
const kumamotoIsekiLabel = {
  id: 'oh-kumamoto-iseki-label',
  type: 'symbol',
  source: 'kumamoto-iseki-source',
  'source-layer': 'k',
  layout: {
    'text-field': ['get', 'm_cont2'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 2.0],
  },
  paint: {
    'text-color': '#1B5E20',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 沖縄県遺跡 --------------------------------------------------------------------------------------------
const okinawaIsekiSource = {
  id: 'okinawa-iseki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/okinawakeniseki/o.pmtiles',
  },
}
const okinawaIsekiFill = {
  id: 'oh-okinawa-iseki-fill',
  type: 'fill',
  source: 'okinawa-iseki-source',
  'source-layer': 'o',
  paint: { 'fill-color': 'rgba(2,119,189,0.35)', 'fill-outline-color': '#0277BD' },
}
const okinawaIsekiLine = {
  id: 'oh-okinawa-iseki-line',
  type: 'line',
  source: 'okinawa-iseki-source',
  'source-layer': 'o',
  paint: { 'line-color': '#0277BD', 'line-width': 1.5 },
}
const okinawaIsekiLabel = {
  id: 'oh-okinawa-iseki-label',
  type: 'symbol',
  source: 'okinawa-iseki-source',
  'source-layer': 'o',
  layout: {
    'text-field': ['get', '遺跡名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#0277BD',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// 富田林市埋蔵文化財 --------------------------------------------------------------------------------------------
const tondabayashiMaibunSource = {
  id: 'tondabayashi-maibun-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tondabayashiiseki/t.pmtiles',
  },
}
const tondabayashiMaibunFill = {
  id: 'oh-tondabayashi-maibun-fill',
  type: 'fill',
  source: 'tondabayashi-maibun-source',
  'source-layer': 't',
  paint: { 'fill-color': 'rgba(173,20,87,0.35)', 'fill-outline-color': '#AD1457' },
}
const tondabayashiMaibunLine = {
  id: 'oh-tondabayashi-maibun-line',
  type: 'line',
  source: 'tondabayashi-maibun-source',
  'source-layer': 't',
  paint: { 'line-color': '#AD1457', 'line-width': 1.5 },
}
const tondabayashiMaibunLabel = {
  id: 'oh-tondabayashi-maibun-label',
  type: 'symbol',
  source: 'tondabayashi-maibun-source',
  'source-layer': 't',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 0],
  },
  paint: {
    'text-color': '#AD1457',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}

// =====================================================================================================================
// OH2からの遺跡・文化財レイヤー移植 ここまで
// =====================================================================================================================

// 日本歴史地名大系（NRCT-POI） --------------------------------------------------------------------------------------------
const nrctPoiSource = {
  id: 'nrct-poi-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/nrct-poi.geojson',
  },
}
const nrctPoiLayer = {
  id: 'oh-nrct-poi',
  type: 'circle',
  source: 'nrct-poi-source',
  paint: {
    'circle-color': [
      'match',
      ['get', '分類2'],
      '埋葬・信仰・宗教施設', '#8B4513',  // 茶色
      '史跡・文化財・遺跡', '#9C27B0',    // 紫
      '古墳・貝塚', '#4CAF50',             // 緑
      '城・軍事施設', '#F44336',           // 赤
      '産業施設', '#2196F3',               // 青
      '#795548'                             // その他
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 1, 4, 2, 8, 4, 12, 7, 16, 12],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
    'circle-opacity': 0.85,
  },
  minzoom: 0,
}
const nrctPoiLabelLayer = {
  id: 'oh-nrct-poi-label',
  type: 'symbol',
  source: 'nrct-poi-source',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
    'text-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 1],
  },
  minzoom: 0,
}
// 日本歴史地名大系 市区町村・地区（NRCT-MCT） --------------------------------------------------------------------------------------------
const nrctMctSource = {
  id: 'nrct-mct-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/nrct-mct.geojson',
  },
}
const nrctMctLayer = {
  id: 'oh-nrct-mct',
  type: 'circle',
  source: 'nrct-mct-source',
  paint: {
    'circle-color': '#e65100',  // オレンジ
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 1, 4, 2, 8, 4, 12, 7, 16, 12],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
    'circle-opacity': 0.85,
  },
  minzoom: 0,
}
const nrctMctLabelLayer = {
  id: 'oh-nrct-mct-label',
  type: 'symbol',
  source: 'nrct-mct-source',
  layout: {
    'text-field': ['get', '名称'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 11,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
    'text-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 11, 1],
  },
  minzoom: 0,
}
// 冠水 --------------------------------------------------------------------------------------------
const kansuiSource = {
  id: 'kansui-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kansui/kansui.pmtiles',
  },
}
const kansuiLayer = {
  id: 'oh-kansui',
  type: 'circle',
  source: 'kansui-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.2, 4, 1, 7, 4, 11, 10],
  },
}
const kansuiLayerLabel = {
  id: 'oh-kansui-label',
  type: 'symbol',
  source: 'kansui-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 13,
}
// R05大規模盛土造成地-----------------------------------------------------------------------------------------------------
const zoseiSource = {
  id: 'zosei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/zosei/zosei.pmtiles',
  },
}
const zoseiLayer = {
  id: 'oh-zosei',
  type: 'fill',
  source: 'zosei-source',
  'source-layer': 'polygon',
  paint: {
    // 'fill-color': 'rgba(179,253,165,0.8)'
    'fill-color': [
      'match',
      ['get', 'A54_001'],
      '1',
      'rgba(179,253,165,0.8)',
      '2',
      'rgba(155,155,248,0.8)',
      '9',
      'rgba(0,255,0,0.8)',
      'red',
    ],
  },
}
const zoseiLayerLine = {
  id: 'oh-zosei-line',
  type: 'line',
  source: 'zosei-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0.5,
      11,
      1,
    ],
  },
}
const zoseiLayerLabel = {
  id: 'oh-zosei-label',
  type: 'symbol',
  source: 'zosei-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': [
      'match',
      ['get', 'A54_001'],
      '1',
      '谷埋め型',
      '2',
      '腹付け型',
      '9',
      '区分をしていない',
      'その他', // デフォルトのテキスト
    ],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}
// rgbで値を取得するレイヤー------------------------------------------------------------------------------------------------
// シームレス地質図--------------------------------------------------------------------------------------------------------
const seamlessSource = {
  id: 'seamless-source',
  obj: {
    type: 'raster',
    tiles: ['https://gbank.gsj.jp/seamless/v2/api/1.2/tiles/{z}/{y}/{x}.png?layer=glf'],
    rasterResampling: 'nearest',
  },
}
const seamlessLayer = {
  id: 'oh-rgb-seamless-layer',
  type: 'raster',
  source: 'seamless-source',
  'max-opacity': 0.8,
}
// 土砂災害警戒区域--------------------------------------------------------------------------------------------------------
const dosyaSource = {
  id: 'dosya-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/05_dosekiryukeikaikuiki/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const dosyaLayer = {
  id: 'oh-rgb-dosya-layer',
  type: 'raster',
  source: 'dosya-source',
  'max-opacity': 0.8,
}
// 土石流危険渓流------------------------------------------------------------------------------------------------------
const dosekiryuSource = {
  id: 'dosekiryu-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/05_dosekiryukikenkeiryu/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const dosekiryuLayer = {
  id: 'oh-rgb-dosekiryu-layer',
  type: 'raster',
  source: 'dosekiryu-source',
  'max-opacity': 0.8,
}
// 急傾斜地崩壊危険箇所------------------------------------------------------------------------------------------------------
const kyukeisyaSource = {
  id: 'kyukeisya-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/05_kyukeisyachihoukai/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const kyukeisyaLayer = {
  id: 'oh-rgb-kyukeisya-layer',
  type: 'raster',
  source: 'kyukeisya-source',
  'max-opacity': 0.8,
  paint: {
    'raster-hue-rotate': 180, // 色相
    'raster-brightness-min': 0.1, // 最小輝度
    'raster-brightness-max': 1.0, // 最大輝度
    'raster-saturation': 0.8, // 彩度
  },
}
// 地すべり危険箇所------------------------------------------------------------------------------------------------------
const jisuberiSource = {
  id: 'jisuberi-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/05_jisuberikikenkasyo/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const jisuberiLayer = {
  id: 'oh-rgb-jisuberi-layer',
  type: 'raster',
  source: 'jisuberi-source',
  'max-opacity': 0.8,
}
// 洪水浸水想定（最大）------------------------------------------------------------------------------------------------------
const kozuiSaidaiSource = {
  id: 'kozui-saidai-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/01_flood_l2_shinsuishin/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const kozuiSaidaiLayer = {
  id: 'oh-rgb-kozui-saidai-layer',
  type: 'raster',
  source: 'kozui-saidai-source',
  'max-opacity': 0.8,
}
// 洪水浸水想定（計画規模）------------------------------------------------------------------------------------------------------
const kozuikeikskuSource = {
  id: 'kozui-keikaku-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/01_flood_l1_shinsuishin_newlegend_kuni_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const kozuiKeikskuLayer = {
  id: 'oh-rgb-kozui-keikaku-layer',
  type: 'raster',
  source: 'kozui-keikaku-source',
  'max-opacity': 0.8,
}
// 津波------------------------------------------------------------------------------------------------------
const tsunamiSource = {
  id: 'tsunami-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/04_tsunami_newlegend_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const tsunamiLayer = {
  id: 'oh-rgb-tsunami-layer',
  type: 'raster',
  source: 'tsunami-source',
  'max-opacity': 0.8,
}
// ため池決壊------------------------------------------------------------------------------------------------------
const tameikeSource = {
  id: 'tameike-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportal.gsi.go.jp/data/raster/07_tameike/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const tameikeLayer = {
  id: 'oh-rgb-tameike-layer',
  type: 'raster',
  source: 'tameike-source',
  'max-opacity': 0.8,
}
// 浸水継続時間（想定最大規模）------------------------------------------------------------------------------------------------------
const keizokuSource = {
  id: 'keizoku-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/01_flood_l2_keizoku_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const keizokuLayer = {
  id: 'oh-rgb-keizoku-layer',
  type: 'raster',
  source: 'keizoku-source',
  'max-opacity': 0.8,
}
// 家屋倒壊等氾濫想定区域（氾濫流）------------------------------------------------------------------------------------------------------
const kaokutoukaiHanranSource = {
  id: 'kaokutoukai-hanran-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/01_flood_l2_kaokutoukai_hanran_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const kaokutoukaiHanranLayer = {
  id: 'oh-rgb-kaokutoukai-hanran-layer',
  type: 'raster',
  source: 'kaokutoukai-hanran-source',
  'max-opacity': 0.8,
}
// 高潮浸水想定区域------------------------------------------------------------------------------------------------------
const hightideSource = {
  id: 'hightide-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/03_hightide_l2_shinsuishin_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const hightideLayer = {
  id: 'oh-rgb-hightide-layer',
  type: 'raster',
  source: 'hightide-source',
  'max-opacity': 0.8,
}
// 内水浸水想定区域------------------------------------------------------------------------------------------------------
const naisuiSource = {
  id: 'naisui-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/02_naisui_data/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const naisuiLayer = {
  id: 'oh-rgb-naisui-layer',
  type: 'raster',
  source: 'naisui-source',
  'max-opacity': 0.8,
}
// 雪崩危険箇所------------------------------------------------------------------------------------------------------
const nadareSource = {
  id: 'nadare-source',
  obj: {
    type: 'raster',
    tiles: ['https://disaportaldata.gsi.go.jp/raster/05_nadarekikenkasyo/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const nadareLayer = {
  id: 'oh-rgb-nadare-layer',
  type: 'raster',
  source: 'nadare-source',
  'max-opacity': 0.8,
}
// rgbで値を取得するレイヤー ここまで-----------------------------------------------------------------------------------------

// クマ類全国分布メッシュ 2019（環境省 2015-2018年調査）------------------------------------------------------------------------------------------------------
const kumaSource = {
  id: 'kuma-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/kuma_2015_2018.geojson',
  },
}
const kumaFillLayer = {
  id: 'oh-kuma-fill',
  type: 'fill',
  source: 'kuma-source',
  paint: {
    'fill-color': '#8B4513',
    'fill-opacity': 0.35,
  },
}
const kumaLineLayer = {
  id: 'oh-kuma-line',
  type: 'line',
  source: 'kuma-source',
  paint: {
    'line-color': '#5D2E0C',
    'line-width': 0.5,
    'line-opacity': 0.4,
  },
}

// 現存植生図2024（環境省生物多様性センター）------------------------------------------------------------------------------------------------------
// 低ズーム（z4-z11）: ラスターPMTiles / 高ズーム（z11-z14）: ベクターPMTiles（z11でクロスフェード）
const veg2024RasterSource = {
  id: 'veg2024-raster-source',
  obj: {
    type: 'raster',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/veg2024/veg2024_raster.pmtiles',
    tileSize: 256,
  },
}
const veg2024RasterLayer = {
  id: 'oh-veg2024-raster',
  type: 'raster',
  source: 'veg2024-raster-source',
  maxzoom: 12,
  paint: {
    'raster-opacity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 10, 0.6, 11, 0.6, 12, 0],
  },
}
const veg2024Source = {
  id: 'veg2024-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/veg2024/veg2024.pmtiles',
  },
}
// 植生自然度で色分け
const veg2024Colors = [
  'match',
  ['get', '植生自然度'],
  '1', '#b0b0b0',   // 市街地等
  '2', '#e8d87c',   // 農耕地（水田・畑）
  '3', '#c4d45a',   // 農耕地（樹園地）
  '4', '#a8d88c',   // 二次草原（低）
  '5', '#7cc464',   // 二次草原（高）
  '6', '#6aaa5c',   // 植林地
  '7', '#4e9450',   // 二次林
  '8', '#2e7d3a',   // 二次林（自然林に近い）
  '9', '#1a5e28',   // 自然林
  '10', '#9b6eb5',  // 自然草原
  '98', '#a0845c',  // 自然裸地
  '99', '#6ca0d4',  // 開放水域
  '#cccccc',        // default
]
const veg2024FillLayer = {
  id: 'oh-veg2024-fill',
  type: 'fill',
  source: 'veg2024-source',
  'source-layer': 'veg2024',
  minzoom: 11,
  paint: {
    'fill-color': veg2024Colors,
    'fill-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 11.5, 0.5, 14, 0.5],
  },
}
const veg2024LineLayer = {
  id: 'oh-veg2024-line',
  type: 'line',
  source: 'veg2024-source',
  'source-layer': 'veg2024',
  paint: {
    'line-color': veg2024Colors,
    'line-width': 0.5,
    'line-opacity': 0.3,
  },
  minzoom: 12,
}

// 明治期の低湿地------------------------------------------------------------------------------------------------------
const shitchiSource = {
  id: 'shitchi-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/swale/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const shitchiLayer = {
  id: 'oh-rgb-shitchi-layer',
  type: 'raster',
  source: 'shitchi-source',
  'max-opacity': 0.8,
}

// Q地図橋梁 --------------------------------------------------------------------------------------------
const qKyouryoSource = {
  id: 'q-kyoryo-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/q/kyoryo/kyoryo.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const qKyoryoLayer = {
  id: 'oh-q-kyoryo',
  type: 'circle',
  source: 'q-kyoryo-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'steelblue',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 10],
  },
}
const qKyoryoLayerLabel = {
  id: 'oh-q-kyoryo-label',
  type: 'symbol',
  source: 'q-kyoryo-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', '_html'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 13,
}
// Q地図トンネル --------------------------------------------------------------------------------------------
const qTunnelSource = {
  id: 'q-tunnel-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/q/tunnel/tunnel.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const qTunnelLayer = {
  id: 'oh-q-tunnel',
  type: 'circle',
  source: 'q-tunnel-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'peru',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.1, 4, 0.5, 7, 2, 11, 10],
  },
}
const qTunnelLayerLabel = {
  id: 'oh-q-tunnel-label',
  type: 'symbol',
  source: 'q-tunnel-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', '_html'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
    visibility: 'visible',
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 10,
}
// R05用途地域-----------------------------------------------------------------------------------------------------
const yotochiikiSource = {
  id: 'yotochiiki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yotochiiki/r05/y.pmtiles',
  },
}
const yotochiikiLayer = {
  id: 'oh-yotochiiki',
  type: 'fill',
  source: 'yotochiiki-source',
  'source-layer': 'y',
  paint: {
    'fill-color': [
      'match',
      ['get', 'YoutoID'],
      1,
      'rgba(92,201,59,0.8)',
      2,
      'rgba(92,201,59,0.8)',
      3,
      'rgba(214,254,81,0.8)',
      4,
      'rgba(255,255,209,0.8)',
      5,
      'rgba(255,255,84,0.8)',
      6,
      'rgba(247,206,160,0.8)',
      7,
      'rgba(247,206,85,0.8)',
      8,
      'rgba(247,206,85,0.8)',
      9,
      'rgba(247,206,252,0.8)',
      10,
      'rgba(241,158,202,0.8)',
      11,
      'rgba(196,155,249,0.8)',
      12,
      'rgba(214,254,254,0.8)',
      13,
      'rgba(164,203,203,0.8)',
      99,
      'rgba(200,200,200,0.8)',
      'red',
    ],
  },
}
const yotochiikiLayerHeight = {
  id: 'oh-yotochiiki-height',
  type: 'fill-extrusion',
  source: 'yotochiiki-source',
  'source-layer': 'y',
  paint: {
    'fill-extrusion-color': [
      'match',
      ['get', 'YoutoID'],
      1,
      'rgba(92,201,59,0.8)',
      2,
      'rgba(92,201,59,0.8)',
      3,
      'rgba(214,254,81,0.8)',
      4,
      'rgba(255,255,209,0.8)',
      5,
      'rgba(255,255,84,0.8)',
      6,
      'rgba(247,206,160,0.8)',
      7,
      'rgba(247,206,85,0.8)',
      8,
      'rgba(247,206,85,0.8)',
      9,
      'rgba(247,206,252,0.8)',
      10,
      'rgba(241,158,202,0.8)',
      11,
      'rgba(196,155,249,0.8)',
      12,
      'rgba(214,254,254,0.8)',
      13,
      'rgba(164,203,203,0.8)',
      99,
      'rgba(200,200,200,0.8)',
      'red',
    ],
    'fill-extrusion-height': [
      'match',
      ['get', 'YoutoID'],
      1,
      20, // 住宅用: 高さ20メートル
      2,
      20,
      3,
      30,
      4,
      30,
      5,
      20,
      6,
      20,
      7,
      20,
      9,
      50, // 商業用: 高さ40メートル
      10,
      60,
      11,
      40, // 工業用: 高さ30メートル
      12,
      40,
      13,
      40,
      10, // その他: 高さ10メートル
    ],
  },
}
const yotochiikiLayerLine = {
  id: 'oh-yotochiiki-line',
  type: 'line',
  source: 'yotochiiki-source',
  'source-layer': 'y',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 9, 0, 11, 1],
  },
}
const yotochiikiLayerLabel = {
  id: 'oh-yotochiiki-label',
  type: 'symbol',
  source: 'yotochiiki-source',
  'source-layer': 'y',
  layout: {
    'text-field': ['get', '用途地域'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}
// 関東小字地図-----------------------------------------------------------------------------------------------------
const koazaSource = {
  id: 'koaza-source',
  obj: {
    type: 'vector',
    // url: "pmtiles:///oh3/php/proxy2.php?url=https://koaza.net/border/koaza.pmtiles",
    url: 'pmtiles://https://kenzkenz.jp/pmtiles/koaza/koaza.pmtiles',
  },
}
const koazaLayer = {
  id: 'oh-koaza',
  type: 'fill',
  source: 'koaza-source',
  'source-layer': 'koaza',
  paint: {
    'fill-color': 'rgba(255,255,255,0.1)',
  },
}
const koazaLayerLine = {
  id: 'oh-koaza-line',
  type: 'line',
  source: 'koaza-source',
  'source-layer': 'koaza',
  paint: {
    'line-color': 'hotpink',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1, 14, 4],
  },
}
const koazaLayerLabel = {
  id: 'oh-koaza-label',
  type: 'symbol',
  source: 'koaza-source',
  'source-layer': 'koaza',
  layout: {
    'text-field': ['get', 'KOAZA_NAME'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'hotpink',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 14,
}
// ---------------------------------------------------
const muraSource = {
  id: 'mura-source',
  obj: {
    type: 'vector',
    // url: "pmtiles://https://koaza.net/border/mura.pmtiles",
    url: 'pmtiles://https://kenzkenz.jp/pmtiles/koaza/mura.pmtiles',
  },
}
const muraLayer = {
  id: 'oh-mura',
  type: 'fill',
  source: 'mura-source',
  'source-layer': 'mura',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const muraLayerLine = {
  id: 'oh-mura-line',
  type: 'line',
  source: 'mura-source',
  'source-layer': 'mura',
  paint: {
    'line-color': 'blue',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 4],
  },
}
// const muraLayerLabel = {
//     id: "oh-mura-label",
//     type: "symbol",
//     source: "mura-source",
//     "source-layer": "polygon",
//     // 'layout': {
//     //     'text-field': ['get', 'MURA_NAME'],
//     //     'text-font': ['NotoSansJP-Regular'],
//     // },
//     layout: {
//         'text-field': ['get', 'MURA_NAME'],
//         'text-font': ['NotoSansJP-Regular'],
//         'text-anchor': 'center',
//         'symbol-placement': 'point', // ポイントとして配置
//         'text-allow-overlap': false, // ラベルの重複を防ぐ
//         'text-size': 20,
//         'text-max-width': 50,
//         'text-offset': [0, 0],
//     },
//     'paint': {
//         'text-color': 'blue',
//         'text-halo-color': 'white',
//         'text-halo-width': 1.0,
//     },
//     'minzoom': 13
// }

const muraCenterSource = {
  id: 'mura-center-source',
  obj: {
    type: 'vector',
    // url: "pmtiles://https://osaru-san1.github.io/border/mura.pmtiles",
    url: 'pmtiles://https://kenzkenz.net/pmtiles/koaza/mura_center_point.pmtiles',
  },
}
const muraCenterLabel = {
  id: 'oh-mura-center-label',
  type: 'symbol',
  source: 'mura-center-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'MURA_NAME'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'blue',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 13,
}
// dronebird---------------------------------------------------------------------------------------------------------
const dronebirdSource = {
  id: 'dronebird-source',
  obj: {
    type: 'raster',
    tiles: ['https://apps.kontur.io/raster-tiler/oam/mosaic/{z}/{x}/{y}.png'],
    tileSize: 256,
  },
}
const dronebirdLayer = {
  id: 'oh-dronebird-layer',
  type: 'raster',
  source: 'dronebird-source',
}
// 室蘭市航空写真--------------------------------------------------------------------------------------------------------------
const muroranOrthoSource = {
  id: 'muroran-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/muroran3/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
    bounds: boundsSort([140.888332, 42.2961046, 141.076206, 42.44097007]),
    scheme: 'tms',
  },
}
const muroranOrthoLayer = {
  id: 'oh-muroran-ortho-layer',
  type: 'raster',
  source: 'muroran-ortho-source',
}
// 糸魚川市航空写真--------------------------------------------------------------------------------------------------------------
const itoigawaOrthoSource = {
  id: 'itoigawa-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['/oh3/php/tile-proxy.php?src=itoigawa&z={z}&x={x}&y={y}'],
    tileSize: 256,
    maxzoom: 19,
    minzoom: 12,
    bounds: boundsSort([137.5733, 37.11702, 138.1806, 36.79012]),
    scheme: 'tms',
  },
}
const itoigawaOrthoLayer = {
  id: 'oh-itoigawa-ortho-layer',
  type: 'raster',
  source: 'itoigawa-ortho-source',
}
// 練馬区航空写真--------------------------------------------------------------------------------------------------------------
const nerimaOrthoSource = {
  id: 'nerima-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['/oh3/php/tile-proxy.php?src=nerima&z={z}&x={x}&y={y}'],
    tileSize: 256,
    maxzoom: 19,
    minzoom: 10,
    bounds: boundsSort([139.55791, 35.708959, 139.684391, 35.78395978]),
  },
}
const nerimaOrthoLayer = {
  id: 'oh-nerima-ortho-layer',
  type: 'raster',
  source: 'nerima-ortho-source',
}
// 深谷市航空写真--------------------------------------------------------------------------------------------------------------
const fukayaOrthoSource = {
  id: 'fukaya-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['/oh3/php/tile-proxy.php?src=fukaya&z={z}&x={x}&y={y}'],
    tileSize: 256,
    maxzoom: 19,
    minzoom: 12,
    bounds: boundsSort([139.16936, 36.09796, 139.343577, 36.25483689]),
    scheme: 'tms',
  },
}
const fukayaOrthoLayer = {
  id: 'oh-fukaya-ortho-layer',
  type: 'raster',
  source: 'fukaya-ortho-source',
}
// 厚木市航空写真--------------------------------------------------------------------------------------------------------------
const atsugiOrthoSource = {
  id: 'atsugi-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['/oh3/php/tile-proxy.php?src=atsugi&z={z}&x={x}&y={y}'],
    tileSize: 256,
    maxzoom: 19,
    minzoom: 12,
    bounds: boundsSort([139.2161, 35.3932, 139.384260, 35.529670]),
    scheme: 'tms',
  },
}
const atsugiOrthoLayer = {
  id: 'oh-atsugi-ortho-layer',
  type: 'raster',
  source: 'atsugi-ortho-source',
}
// 掛川市航空写真--------------------------------------------------------------------------------------------------------------
const kakegawaOrthoSource = {
  id: 'kakegawa-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/tile/kakegawa/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 18,
    minzoom: 12,
    bounds: boundsSort([137.9241, 34.9255, 138.1404, 34.6174]),
    scheme: 'tms',
  },
}
const kakegawaOrthoLayer = {
  id: 'oh-kakegawa-ortho-layer',
  type: 'raster',
  source: 'kakegawa-ortho-source',
}
// 半田市航空写真--------------------------------------------------------------------------------------------------------------
const handaOrthoSource = {
  id: 'handa-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz2.xsrv.jp/handasi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 18,
    bounds: boundsSort([136.8485, 34.9523, 136.9931, 34.8481]),
  },
}
const handaOrthoLayer = {
  id: 'oh-handa-ortho-layer',
  type: 'raster',
  source: 'handa-ortho-source',
}
// 富田林市航空写真--------------------------------------------------------------------------------------------------------------
const tondabayashiOrthoSource = {
  id: 'tondabayashi-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://www.city.tondabayashi.lg.jp/map2/tile/1010/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
    minzoom: 12,
    bounds: boundsSort([135.5529, 34.53991, 135.6433, 34.43539]),
  },
}
const tondabayashiOrthoLayer = {
  id: 'oh-tondabayashi-ortho-layer',
  type: 'raster',
  source: 'tondabayashi-ortho-source',
}
// 鹿児島市航空写真--------------------------------------------------------------------------------------------------------------
const kagoshimaOrthoSource = {
  id: 'kagoshima-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://mtile.pref.miyazaki.lg.jp/tile/orts/kagoshima/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
    bounds: boundsSort([130.370675, 31.2819, 130.732, 31.767]),
    scheme: 'tms',
  },
}
const kagoshimaOrthoLayer = {
  id: 'oh-kagoshima-ortho-layer',
  type: 'raster',
  source: 'kagoshima-ortho-source',
}
// PLATEAUオルソ--------------------------------------------------------------------------------------------------------------
const plateauOrthoSource = {
  id: 'plateau-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://gic-plateau.s3.ap-northeast-1.amazonaws.com/2020/ortho/tiles/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const plateauOrthoLayer = {
  id: 'oh-plateau-ortho-layer',
  type: 'raster',
  source: 'plateau-ortho-source',
}
// OSMFJ航空写真--------------------------------------------------------------------------------------------------------------
const osmfjKyotoSource = {
  id: 'osmfj-kyoto-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/kyoto-fu/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjKyotoLayer = {
  id: 'oh-osmfj-kyoto-layer',
  type: 'raster',
  source: 'osmfj-kyoto-source',
}
const osmfjOsakaSource = {
  id: 'osmfj-osaka-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/osaka-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjOsakaLayer = {
  id: 'oh-osmfj-osaka-layer',
  type: 'raster',
  source: 'osmfj-osaka-source',
}
const osmfjHimejiSource = {
  id: 'osmfj-himeji-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/himeji-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjHimejiLayer = {
  id: 'oh-osmfj-himeji-layer',
  type: 'raster',
  source: 'osmfj-himeji-source',
}
const osmfjKochiSource = {
  id: 'osmfj-kochi-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/kouchi-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjKochiLayer = {
  id: 'oh-osmfj-kochi-layer',
  type: 'raster',
  source: 'osmfj-kochi-source',
}
const osmfjNagahamaSource = {
  id: 'osmfj-nagahama-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/nagahama-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjNagahamaLayer = {
  id: 'oh-osmfj-nagahama-layer',
  type: 'raster',
  source: 'osmfj-nagahama-source',
}
const osmfjNagaokaSource = {
  id: 'osmfj-nagaoka-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/nagaoka-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjNagaokaLayer = {
  id: 'oh-osmfj-nagaoka-layer',
  type: 'raster',
  source: 'osmfj-nagaoka-source',
}
const osmfjAsagoSource = {
  id: 'osmfj-asago-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/asago-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjAsagoLayer = {
  id: 'oh-osmfj-asago-layer',
  type: 'raster',
  source: 'osmfj-asago-source',
}
const osmfjHandaSource = {
  id: 'osmfj-handa-source',
  obj: {
    type: 'raster',
    tiles: ['https://tiles.osmf.jp/handa-shi/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 19,
  },
}
const osmfjHandaLayer = {
  id: 'oh-osmfj-handa-layer',
  type: 'raster',
  source: 'osmfj-handa-source',
}
// 明治中期の郡 --------------------------------------------------------------------------------------------
const cityGunSource = {
  id: 'oh-city-gun-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gun/gun.pmtiles',
  },
}
const cityGunLayer = {
  id: 'oh-city-gun',
  type: 'fill',
  source: 'oh-city-gun-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const cityGunLayerLine = {
  id: 'oh-city-gun-line',
  type: 'line',
  source: 'oh-city-gun-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const cityGunLayerLabel = {
  id: 'oh-city-gun-label',
  type: 'symbol',
  source: 'oh-city-gun-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'GUN'],
    'text-font': ['NotoSansJP-Regular'],
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 8,
}
// 市町村大正09年 --------------------------------------------------------------------------------------------
const cityT09Source = {
  id: 'oh-city-t09-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/city/t09/t09.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const cityT09Layer = {
  id: 'oh-city-t09',
  type: 'fill',
  source: 'oh-city-t09-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const cityT09LayerLine = {
  id: 'oh-city-t09-line',
  type: 'line',
  source: 'oh-city-t09-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const cityT09LayerLabel = {
  id: 'oh-city-t09-label',
  type: 'symbol',
  source: 'oh-city-t09-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 10,
}
// 市町村昭和25年 --------------------------------------------------------------------------------------------
const cityS25Source = {
    id: 'oh-city-s25-source',
    obj: {
        type: 'vector',
        url: 'pmtiles://https://kenzkenz.net/pmtiles/city/s25/s25.pmtiles',
        attribution: "<a href='' target='_blank'></a>",
    },
}
const cityS25Layer = {
    id: 'oh-city-s25',
    type: 'fill',
    source: 'oh-city-s25-source',
    'source-layer': 'polygon',
    paint: {
        'fill-color': ['get', 'random_color'],
    },
}
const cityS25LayerLine = {
    id: 'oh-city-s25-line',
    type: 'line',
    source: 'oh-city-s25-source',
    'source-layer': 'polygon',
    paint: {
        'line-color': '#000',
        'line-width': [
            'interpolate', // Zoom-based interpolation
            ['linear'],
            ['zoom'], // Use the zoom level as the input
            7,
            0,
            11,
            0.5,
        ],
    },
}
const cityS25LayerLabel = {
    id: 'oh-city-s25-label',
    type: 'symbol',
    source: 'oh-city-s25-source',
    'source-layer': 'polygon',
    layout: {
        'text-field': ['get', 'N03_004'],
        'text-font': ['NotoSansJP-Regular'],
        'symbol-placement': 'point',
    },
    paint: {
        'text-color': 'black',
        'text-halo-color': 'white',
        'text-halo-width': 1.0,
    },
    minzoom: 10,
}
// 市町村平成12年 --------------------------------------------------------------------------------------------
const cityH12Source = {
  id: 'oh-city-h12-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/city/h12/h12.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const cityH12Layer = {
  id: 'oh-city-h12',
  type: 'fill',
  source: 'oh-city-h12-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const cityH12LayerLine = {
  id: 'oh-city-h12-line',
  type: 'line',
  source: 'oh-city-h12-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const cityH12LayerLabel = {
  id: 'oh-city-h12-label',
  type: 'symbol',
  source: 'oh-city-h12-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 10,
}
// 市町村令和５年 --------------------------------------------------------------------------------------------
const cityR05Source = {
  id: 'oh-city-r05-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/city/r05/r05.pmtiles',
    attribution: "<a href='' target='_blank'></a>",
  },
}
const cityR05Layer = {
  id: 'oh-city-r05',
  type: 'fill',
  source: 'oh-city-r05-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
}
const cityR05LayerLine = {
  id: 'oh-city-r05-line',
  type: 'line',
  source: 'oh-city-r05-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
}
const cityR05LayerLabel = {
  id: 'oh-city-r05-label',
  type: 'symbol',
  source: 'oh-city-r05-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'N03_004'],
    'text-font': ['NotoSansJP-Regular'],
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.0,
  },
  minzoom: 10,
}
// エコリス植生図---------------------------------------------------------------------------------------------------------
const ecoris67Source = {
  id: 'ecoris67-source',
  obj: {
    type: 'raster',
    tiles: ['https://map.ecoris.info/tiles/vege67/{z}/{x}/{y}.png'],
  },
}
const ecoris67Layer = {
  id: 'oh-ecoris67-layer',
  type: 'raster',
  source: 'ecoris67-source',
  'raster-resampling': 'nearest',
}
// 東京都土地利用現況調査----------------------------------------------------------------------------------------------------------------
export const tochiriyoSource = {
  id: 'tochiriyo-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tokyotochiriyo/t.pmtiles',
  },
}
export const tochiriyoLayer = {
  id: 'oh-tochiriyo',
  type: 'fill',
  source: 'tochiriyo-source',
  'source-layer': 't',
  paint: {
    'fill-color': [
      'match',
      ['get', 'LU_1'],

      '111',
      'rgba(128,128,128,0.7)', // 官公庁施設
      '112',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(0,192,255,0.7)', // 教育施設
        '2',
        'rgba(240,230,140,0.7)', // 文化施設
        '3',
        'rgba(255,215,0,0.7)', // 宗教施設
        /* default */ 'rgba(0,0,0,0)', // Default color if LU_2 doesn't match
      ],
      '113',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(255,192,203,0.7)', // 医療施設
        '2',
        'rgba(219,112,147,0.7)', // 厚生施設
        /* default */ 'rgba(0,0,0,0)',
      ],
      '114',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(0,0,255,0.7)', // 供給施設
        '2',
        'rgba(0,0,139,0.7)', // 処理施設
        /* default */ 'rgba(0,0,0,0)',
      ],
      '121',
      'rgba(255,127,80,0.7)', // 事務所建築物
      '122',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(255,0,0,0.7)', // 商業施設
        '2',
        'rgba(255,99,71,0.7)', // 公衆浴場等
        /* default */ 'rgba(0,0,0,0)',
      ],
      '123',
      'rgba(255,69,0,0.7)', // 住商併用建物
      '124',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(0,255,127,0.7)', // 宿泊施設
        '2',
        'rgba(124,255,2,0.7)', // 遊興施設
        /* default */ 'rgba(0,0,0,0)',
      ],
      '125',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(0,255,255,0.7)', // スポーツ施設
        '2',
        'rgba(64,224,208,0.7)', // 興行施設
        /* default */ 'rgba(0,0,0,0)',
      ],
      '131',
      'rgba(60,179,113,0.7)', // 独立住宅
      '132',
      'rgba(0,100,0,0.7)', // 集合住宅
      '141',
      'rgba(105,105,105,0.7)', // 専用工場
      '142',
      'rgba(169,169,169,0.7)', // 住居併用工場
      '143',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(32,178,120,0.7)', // 運輸施設等
        '2',
        'rgba(95,158,160,0.7)', // 倉庫施設等
        /* default */ 'rgba(0,0,0,0)',
      ],
      '150',
      'rgba(85,107,47,0.7)', // 農林漁業施設
      '210',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(250,250,120,0.7)', // 太陽光発電
        '2',
        'rgba(119,136,153,0.7)', // 屋外駐車場
        '3',
        'rgba(176,196,222,0.7)', // その他
        /* default */ 'rgba(0,0,0,0)',
      ],
      '300',
      [
        'match',
        ['get', 'LU_2'],
        '1',
        'rgba(120,100,100,0.7)', // ゴルフ場
        '2',
        'rgba(154,205,50,0.7)', // その他
        /* default */ 'rgba(0,0,0,0)',
      ],
      '400',
      'rgba(255,222,173,0.7)', // 未利用地等
      '510',
      'rgba(192,192,192,0.7)', // 道路
      '520',
      'rgba(105,105,105,0.7)', // 鉄道・港湾等
      '611',
      'rgba(255,165,0,0.7)', // 田
      '612',
      'rgba(255,165,122,0.7)', // 畑
      '613',
      'rgba(128,0,0,0.7)', // 樹園地
      '620',
      'rgba(34,139,34,0.7)', // 採草放牧地
      '700',
      'rgba(0,0,205,0.7)', // 水面・河川・水路
      '800',
      'rgba(128,128,0,0.7)', // 原野
      '900',
      'rgba(205,133,63,0.7)', // 森林
      '220',
      'rgba(106,90,205,0.7)', // その他
      '0',
      'rgba(128,0,128,0.7)', // 不明
      '9',
      'rgba(75,0,130,0.7)', // 不整合

      /* default */ 'rgba(0,0,0,0)', // Default color if LU_1 doesn't match
    ],
  },
}
export const tochiriyoLayerLine = {
  id: 'oh-tochiriyo-line',
  type: 'line',
  source: 'tochiriyo-source',
  'source-layer': 't',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0.1,
      11,
      0.5,
      16,
      1,
    ],
  },
}
// 夜の光----------------------------------------------------------------------------------------------------------------
const hikariSource = {
  id: 'hikari-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/hikari/hikari.pmtiles',
  },
}
// 宇宙から見た夜景カラー: 暗い紺→琥珀→オレンジ→白熱
const hikariColorRamp = [
  'interpolate', ['linear'], ['to-number', ['get', 'light']],
  1,   '#0a0a2e',  // 深い夜空
  30,  '#1b1464',  // 薄い紫
  60,  '#6b2fa0',  // 紫
  100, '#d4511e',  // 琥珀
  150, '#ff8c00',  // オレンジ
  200, '#ffd000',  // 輝く黄金
  240, '#fff5cc',  // 白熱
  255, '#ffffff',  // 純白
]
const hikariLayer = {
  id: 'oh-hikari',
  type: 'fill',
  source: 'hikari-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': hikariColorRamp,
    'fill-opacity': 0.9,
  },
}
const hikariLayerHeight = {
  id: 'oh-hikari-height',
  type: 'fill-extrusion',
  source: 'hikari-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-color': hikariColorRamp,
    'fill-extrusion-height': [
      'interpolate', ['exponential', 1.5], ['to-number', ['get', 'light']],
      1,   50,
      100, 2000,
      200, 8000,
      255, 15000,
    ],
    'fill-extrusion-opacity': 0.85,
  },
}
// 津波浸水想定-----------------------------------------------------------------------------------------------------
const tsunamiSource2 = {
  id: 'tsunami-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.xsrv.jp/pmtiles/tsunami/tsunami.pmtiles',
  },
}
const tsunamiLayerHeight = {
  id: 'oh-tsunami-height',
  type: 'fill-extrusion',
  source: 'tsunami-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-color': [
      'match',
      ['get', 'A40_003'],
      '0.01m以上 ～ 0.3m未満',
      '#f7fcf0', // 最小カテゴリーの色（新しく追加）
      '～0.3m未満',
      '#d1e5f0', // 0.3m未満の色
      '0.3m未満',
      '#d1e5f0',
      '0.3m以上 ～ 1.0m未満',
      '#a6dba0', // 0.3~1.0mの色
      '0.3m以上 ～ 1m未満',
      '#a6dba0',
      '1.0m以上 ～ 2.0m未満',
      '#7bccc4', // 1.0~2.0mの色
      '1m以上 ～ 2m未満',
      '#7bccc4',

      '0.3m以上 ～ 0.5m未満',
      '#d1e5f0',
      '0.5m以上 ～ 1.0m未満',
      '#a6dba0',
      '1.0m以上 ～ 3.0m未満',
      '#7bccc4',
      '1m以上 ～ 3m未満',
      '#7bccc4',

      '2m以上 ～ 3m未満',
      '#7bccc4',
      '3m以上 ～ 4m未満',
      '#2b8cbe',
      '4m以上 ～ 5m未満',
      '#2b8cbe',

      '3.0m以上 ～ 5.0m未満',
      '#2b8cbe',
      '3m以上 ～ 5m未満',
      '#2b8cbe',
      '5m以上 ～ 10m未満',
      '#0868ac',
      '10m以上 ～ 20m未満',
      '#084081',
      '10.0m以上 ～ 20.0m未満',
      '#084081',

      '2.0m以上 ～ 5.0m未満',
      '#2b8cbe', // 2.0~5.0mの色
      '2m以上 ～ 5m未満',
      '#2b8cbe',
      '5.0m以上 ～ 10.0m未満',
      '#0868ac', // 5.0~10.0mの色
      'gray', // 該当しない場合のデフォルトカラー
    ],
    'fill-extrusion-opacity': 0.8,
    'fill-extrusion-height': [
      'match',
      ['get', 'A40_003'],
      '0.01m以上 ～ 0.3m未満',
      2, // 新しく追加した最小カテゴリーの高さ
      '～0.3m未満',
      5,
      '0.3m未満',
      5,
      '0.3m以上 ～ 1.0m未満',
      20,
      '0.3m以上 ～ 1m未満',
      20,
      '1.0m以上 ～ 2.0m未満',
      40,
      '1m以上 ～ 2m未満',
      40,

      '0.3m以上 ～ 0.5m未満',
      4,
      '0.5m以上 ～ 1.0m未満',
      20,
      '1.0m以上 ～ 3.0m未満',
      40,
      '1m以上 ～ 3m未満',
      40,

      '2m以上 ～ 3m未満',
      50,
      '3m以上 ～ 4m未満',
      60,
      '4m以上 ～ 5m未満',
      70,

      '3.0m以上 ～ 5.0m未満',
      80,
      '3m以上 ～ 5m未満',
      80,
      '5m以上 ～ 10m未満',
      100,
      '10m以上 ～ 20m未満',
      120,
      '10.0m以上 ～ 20.0m未満',
      120,

      '2.0m以上 ～ 5.0m未満',
      80,
      '2m以上 ～ 5m未満',
      80,
      '5.0m以上 ～ 10.0m未満',
      100,
      10, // デフォルトの高さ
    ],
  },
}
// 宮崎県南海トラフ-----------------------------------------------------------------------------------------------------
const nantoraSource = {
  id: 'nantora-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.xsrv.jp/pmtiles/tsunami/nantora.pmtiles',
  },
}
const nantoraLayerHeight = {
  id: 'oh-nantora-height',
  type: 'fill-extrusion',
  source: 'nantora-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['get', '最大浸水深'],
      0,
      'rgba(255,255,179,0.8)', // 0.3m未満
      0.3,
      'rgba(247,245,169,0.8)', // 0.3~0.5m
      0.5,
      'rgba(248,225,116,0.8)', // 0.5~1.0m
      1.0,
      'rgba(255,216,192,0.8)', // 1.0~3.0m
      3.0,
      'rgba(255,183,183,0.8)', // 3.0~5.0m
      5.0,
      'rgba(255,145,145,0.8)', // 5.0~10.0m
      10.0,
      'rgba(242,133,201,0.8)', // 10.0~20.0m
      20.0,
      'rgba(220,122,220,0.8)', // 20.0m以上
    ],
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['get', '最大浸水深'],
      0,
      10, // 0mで高さ10
      0.5,
      15, // 0.5mで高さ15
      1.0,
      20, // 1.0mで高さ20
      3.0,
      50, // 3.0mで高さ50
      5.0,
      75, // 5.0mで高さ75
      10.0,
      125, // 10.0mで高さ125
      20.0,
      200, // 20mで高さ200
    ],
  },
}
// =====================================================================================================================
// 宮崎県防災ハイブリッドレイヤー（ラスター低ズーム + MVT高ズーム）
// OH2からの移植。ベースURL: https://kenzkenz.net/miyazaki/
// =====================================================================================================================

// --- 日向灘津波到達時間 ---
// ラスター（～z15）+ MVT（z16～）
const hyuganadaTotatsuRasterSource = {
  id: 'hyuganada-totatsu-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadatotatsuraster/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 15,
  },
}
const hyuganadaTotatsuRasterLayer = {
  id: 'oh-hyuganada-totatsu-raster',
  type: 'raster',
  source: 'hyuganada-totatsu-raster-source',
  maxzoom: 16,
  'max-opacity': 0.8,
}
// MVT（z16～）source-layer: 'ht'
const hyuganadaTotatsuMvtSource = {
  id: 'hyuganada-totatsu-mvt-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadatotatsu/{z}/{x}/{y}.mvt'],
    maxzoom: 16,
  },
}
const hyuganadaTotatsuMvtLayer = {
  id: 'oh-hyuganada-totatsu-mvt',
  type: 'fill',
  source: 'hyuganada-totatsu-mvt-source',
  'source-layer': 'ht',
  minzoom: 16,
  paint: {
    'fill-color': [
      'step',
      ['get', '到達時間'],
      'rgba(0,0,0,0)',  // デフォルト（0未満）
      0, 'rgba(192,21,27,0.8)',     // ～15分（900秒）
      900, 'rgba(241,74,55,0.8)',   // 15～20分
      1200, 'rgba(251,124,92,0.8)', // 20～25分
      1500, 'rgba(252,179,152,0.8)',// 25～30分
      1800, 'rgba(254,227,214,0.8)',// 30分～
    ],
    'fill-opacity': 0.8,
  },
}
const hyuganadaTotatsuMvtLine = {
  id: 'oh-hyuganada-totatsu-mvt-line',
  type: 'line',
  source: 'hyuganada-totatsu-mvt-source',
  'source-layer': 'ht',
  minzoom: 16,
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 16, 0.3, 20, 1],
  },
}

// --- 日向灘震度分布 ---
// ラスター（～z13）
const hyuganadaShindoRasterSource = {
  id: 'hyuganada-shindo-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadashindoraster/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 13,
  },
}
const hyuganadaShindoRasterLayer = {
  id: 'oh-hyuganada-shindo-raster',
  type: 'raster',
  source: 'hyuganada-shindo-raster-source',
  maxzoom: 14,
  'max-opacity': 0.8,
}
// MVT（z14～）source-layer: 要確認（MVTアップ後に設定）
const hyuganadaShindoMvtSource = {
  id: 'hyuganada-shindo-mvt-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadashindo/{z}/{x}/{y}.mvt'],
    maxzoom: 14,
  },
}
const hyuganadaShindoMvtLayer = {
  id: 'oh-hyuganada-shindo-mvt',
  type: 'fill',
  source: 'hyuganada-shindo-mvt-source',
  'source-layer': 'hs',
  minzoom: 14,
  paint: {
    // 最大JMA: 震度値（実数）→ 7段階色分け
    'fill-color': [
      'step',
      ['get', '最大JMA'],
      'rgba(211,235,249,0.8)',  // ～3.5 → 震度3
      3.5, 'rgba(117,251,253,0.8)',  // 3.5～4.5 → 震度4
      4.5, 'rgba(0,0,245,0.8)',      // 4.5～5.0 → 震度5弱
      5.0, 'rgba(117,251,76,0.8)',   // 5.0～5.5 → 震度5強
      5.5, 'rgba(255,255,84,0.8)',   // 5.5～6.0 → 震度6弱
      6.0, 'rgba(239,135,51,0.8)',   // 6.0～6.5 → 震度6強
      6.5, 'rgba(188,39,27,0.8)',    // 6.5～   → 震度7
    ],
    'fill-opacity': 0.8,
  },
}
const hyuganadaShindoMvtLine = {
  id: 'oh-hyuganada-shindo-mvt-line',
  type: 'line',
  source: 'hyuganada-shindo-mvt-source',
  'source-layer': 'hs',
  minzoom: 14,
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.3, 20, 1],
  },
}

// --- 南海トラフ震度分布 ---
// ラスター（～z13）
const nantoraShindoRasterSource = {
  id: 'nantora-shindo-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz.net/miyazaki/nantorashindoraster/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 13,
  },
}
const nantoraShindoRasterLayer = {
  id: 'oh-nantora-shindo-raster',
  type: 'raster',
  source: 'nantora-shindo-raster-source',
  maxzoom: 14,
  'max-opacity': 0.8,
}
// MVT（z14～）※震度と液状化で同一MVTソースを共有
// source-layer: 要確認（MVTアップ後に設定）
const nantoraShindoMvtSource = {
  id: 'nantora-shindo-mvt-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/miyazaki/nantorashindo/{z}/{x}/{y}.mvt'],
    maxzoom: 14,
  },
}
const nantoraShindoMvtLayer = {
  id: 'oh-nantora-shindo-mvt',
  type: 'fill',
  source: 'nantora-shindo-mvt-source',
  'source-layer': 'e',
  minzoom: 14,
  paint: {
    // JMA独自: 震度値（実数）→ 7段階色分け
    'fill-color': [
      'step',
      ['get', 'JMA独自'],
      'rgba(211,235,249,0.8)',  // ～3.5 → 震度3
      3.5, 'rgba(117,251,253,0.8)',  // 震度4
      4.5, 'rgba(0,0,245,0.8)',      // 震度5弱
      5.0, 'rgba(117,251,76,0.8)',   // 震度5強
      5.5, 'rgba(255,255,84,0.8)',   // 震度6弱
      6.0, 'rgba(239,135,51,0.8)',   // 震度6強
      6.5, 'rgba(188,39,27,0.8)',    // 震度7
    ],
    'fill-opacity': 0.8,
  },
}
const nantoraShindoMvtLine = {
  id: 'oh-nantora-shindo-mvt-line',
  type: 'line',
  source: 'nantora-shindo-mvt-source',
  'source-layer': 'e',
  minzoom: 14,
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.3, 20, 1],
  },
}

// --- 南海トラフ液状化 ---
// ラスター（～z13）
const nantoraEkijyokaRasterSource = {
  id: 'nantora-ekijyoka-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz.net/miyazaki/nantoraekijyokaraster/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 13,
  },
}
const nantoraEkijyokaRasterLayer = {
  id: 'oh-nantora-ekijyoka-raster',
  type: 'raster',
  source: 'nantora-ekijyoka-raster-source',
  maxzoom: 14,
  'max-opacity': 0.8,
}
// MVT（z14～）※震度と同じMVTソースを使用（nantorashindo）
const nantoraEkijyokaMvtLayer = {
  id: 'oh-nantora-ekijyoka-mvt',
  type: 'fill',
  source: 'nantora-shindo-mvt-source',
  'source-layer': 'e',
  minzoom: 14,
  paint: {
    // PL独自: 液状化危険度 → 4段階色分け
    'fill-color': [
      'case',
      ['<', ['get', 'PL独自'], 0], 'rgba(0,0,0,0)',          // 負値 → 透明
      ['==', ['get', 'PL独自'], 0], 'rgba(192,192,192,0.8)', // 0 → なし（灰色）
      ['<=', ['get', 'PL独自'], 5], 'rgba(116,249,75,0.8)',  // ～5 → 小（緑）
      ['<=', ['get', 'PL独自'], 15], 'rgba(255,255,84,0.8)', // 5～15 → 中（黄）
      'rgba(234,51,35,0.8)',                                   // 15超 → 大（赤）
    ],
    'fill-opacity': 0.8,
  },
}
const nantoraEkijyokaMvtLine = {
  id: 'oh-nantora-ekijyoka-mvt-line',
  type: 'line',
  source: 'nantora-shindo-mvt-source',
  'source-layer': 'e',
  minzoom: 14,
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.3, 20, 1],
  },
}

// --- 日向灘津波浸水深 ---
// ラスター（～z15）
const hyuganadaTsunamiRasterSource = {
  id: 'hyuganada-tsunami-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadatsunamiraster/{z}/{x}/{y}.png'],
    tileSize: 256,
    maxzoom: 15,
  },
}
const hyuganadaTsunamiRasterLayer = {
  id: 'oh-hyuganada-tsunami-raster',
  type: 'raster',
  source: 'hyuganada-tsunami-raster-source',
  maxzoom: 16,
  'max-opacity': 0.8,
}
// MVT（z16～）
const hyuganadaTsunamiMvtSource = {
  id: 'hyuganada-tsunami-mvt-source',
  obj: {
    type: 'vector',
    tiles: ['https://kenzkenz.net/miyazaki/hyuganadatsunami/{z}/{x}/{y}.mvt'],
    maxzoom: 16,
  },
}
const hyuganadaTsunamiMvtLayer = {
  id: 'oh-hyuganada-tsunami-mvt',
  type: 'fill',
  source: 'hyuganada-tsunami-mvt-source',
  'source-layer': 'ht',
  minzoom: 16,
  paint: {
    'fill-color': [
      'step', ['get', '最大浸水深'],
      'rgba(0,0,0,0)',
      0,    'rgba(255,255,179,0.8)',     // 0.3m未満
      0.3,  'rgba(247,245,169,0.8)',     // 0.3～0.5m
      0.5,  'rgba(248,225,166,0.8)',     // 0.5～1.0m
      1,    'rgba(255,216,192,0.8)',     // 1.0～3.0m
      3,    'rgba(255,183,183,0.8)',     // 3.0～5.0m
      5,    'rgba(255,145,145,0.8)',     // 5.0～10.0m
      10,   'rgba(242,133,201,0.8)',     // 10.0～20.0m
      20,   'rgba(220,122,220,0.8)',     // 20.0m以上
    ],
    'fill-opacity': 0.8,
  },
}
const hyuganadaTsunamiMvtLine = {
  id: 'oh-hyuganada-tsunami-mvt-line',
  type: 'line',
  source: 'hyuganada-tsunami-mvt-source',
  'source-layer': 'ht',
  minzoom: 16,
  paint: {
    'line-color': 'rgba(100, 100, 100, 0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 16, 0.3, 20, 1],
  },
}

// 北海道津波-----------------------------------------------------------------------------------------------------
const hokkaidotsunamiSource = {
  id: 'hokkaidotsunami-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.xsrv.jp/pmtiles/tsunami/hokkaidotsunami.pmtiles',
  },
}
const hokkaidotsunamiLayerHeight = {
  id: 'oh-hokkaidotsunami-height',
  type: 'fill-extrusion',
  source: 'hokkaidotsunami-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'max'], ['get', 'MAX_SIN']],
      0,
      'rgba(255,255,179,0.8)', // 0.3m未満
      0.3,
      'rgba(247,245,169,0.8)', // 0.3~0.5m
      0.5,
      'rgba(248,225,116,0.8)', // 0.5~1.0m
      1.0,
      'rgba(255,216,192,0.8)', // 1.0~3.0m
      3.0,
      'rgba(255,183,183,0.8)', // 3.0~5.0m
      5.0,
      'rgba(255,145,145,0.8)', // 5.0~10.0m
      10.0,
      'rgba(242,133,201,0.8)', // 10.0~20.0m
      20.0,
      'rgba(220,122,220,0.8)', // 20.0m以上
    ],
    'fill-extrusion-height': [
      'interpolate',
      ['linear'],
      ['coalesce', ['get', 'max'], ['get', 'MAX_SIN']],
      0,
      10, // 0mで高さ10
      0.5,
      15, // 0.5mで高さ15
      1.0,
      20, // 1.0mで高さ20
      3.0,
      50, // 3.0mで高さ50
      5.0,
      75, // 5.0mで高さ75
      10.0,
      125, // 10.0mで高さ125
      20.0,
      200, // 20mで高さ200
    ],
  },
}
// 土地利用100mメッシュ----------------------------------------------------------------------------------------------------------------
const tochiriyo100Source = {
  id: 'tochiriyo100-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tochiriyo/tochiriyoD.pmtiles',
    // minzoom: 11
  },
}
const tochiriyo100Layer = {
  id: 'oh-tochiriyo100',
  type: 'fill',
  source: 'tochiriyo100-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', '土地利用種別'],
      '0100',
      'rgba(255,255,0,0.8)', // 田
      '0200',
      'rgba(255,204,153,0.8)', // その他の農用地
      '0500',
      'rgba(0,170,0,0.8)', // 森林
      '0600',
      'rgba(0,255,153,0.8)', // 荒地
      '0700',
      'rgba(255,0,0,0.8)', // 建物用地
      '0901',
      'rgba(140,140,140,0.8)', // 道路
      '0902',
      'rgba(180,180,180,0.8)', // 鉄道
      '1000',
      'rgba(200,70,15,0.8)', // その他の用地
      '1100',
      'rgba(0,0,255,0.8)', // 河川地及び湖沼
      '1400',
      'rgba(255,255,153,0.8)', // 海浜
      '1500',
      'rgba(0,204,255,0.8)', // 海水域
      '1600',
      'rgba(100,200,100,0.8)', // ゴルフ場
      'rgba(0,0,0,0)', // デフォルト値（該当しない場合は透明）
    ],
  },
} // 郵便区 --------------------------------------------------------------------------------------------
const yubinkuSource = {
  id: 'yubinku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yubinku/yubinbango.pmtiles',
  },
}
const yubinkuFillLayer = {
  id: 'oh-yubinku',
  type: 'fill',
  source: 'yubinku-source',
  'source-layer': 'yubinku',
  paint: {
    'fill-color': ['get', 'color'],
  },
}
const yubinkuLineLayer = {
  id: 'oh-yubinku-line',
  type: 'line',
  source: 'yubinku-source',
  'source-layer': 'yubinku',
  paint: {
    'line-color': 'rgba(50, 50, 50, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.3, 10, 1, 14, 2],
  },
}
const yubinkuLabelLayer = {
  id: 'oh-yubinku-label',
  type: 'symbol',
  source: 'yubinku-source',
  'source-layer': 'yubinku',
  layout: {
    'text-field': ['concat', '〒', ['slice', ['get', 'code'], 0, 3], '-', ['slice', ['get', 'code'], 3], 'xx', '\n', ['get', 'name']],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 9, 14, 13],
    'text-allow-overlap': false,
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': 'rgba(255,255,255,0.9)',
    'text-halo-width': 1.5,
  },
  minzoom: 10,
}
// 土地利用細分メッシュ（ラスタ版）-------------------------------------------------------------------
const tochiriyoRasterSource = {
  id: 'tochiriyo-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/xyz/tochiriyo/{z}/{x}/{y}.png'],
    rasterResampling: 'nearest',
  },
}
const tochiriyoRasterLayer = {
  id: 'oh-rgb-tochiriyo-raster-layer',
  type: 'raster',
  source: 'tochiriyo-raster-source',
  'max-opacity': 0.8,
}
// 幼稚園 --------------------------------------------------------------------------------------------
const yochienSource = {
  id: 'yochien-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/yochien/yochien.pmtiles',
  },
}
const yochienLayer = {
  id: 'oh-yochien-layer',
  type: 'circle',
  source: 'yochien-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'pink',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.5, 4, 1, 7, 6, 11, 10],
  },
}
const yochienLayerLabel = {
  id: 'oh-yochien-label',
  type: 'symbol',
  source: 'yochien-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'P29_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 9,
}
// 空港等の周辺空域 --------------------------------------------------------------------------------------------
const kokuareaSource = {
  id: 'kokuarea-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kokuarea/kokuarea.pmtiles',
    maxzoom: 8,
    minzoom: 1,
  },
}
const kokuareaLayer = {
  id: 'oh-kokuarea-layer',
  type: 'fill',
  source: 'kokuarea-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'rgba'],
  },
}
const kokuareaLayerLine = {
  id: 'oh-kokuarea-line',
  type: 'line',
  source: 'kokuarea-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.5, 20, 6],
  },
}
// 海洋地質図---------------------------------------------------------------------------------------------------------
const kaiyochishitsuource = {
  id: 'kaiyochishitsu-source',
  obj: {
    type: 'raster',
    tiles: ['https://gbank.gsj.jp/geonavi/maptile/wmts/1.0.0/MA3000_23japanes/default/EPSG900913/{z}/{y}/{x}.png'],
  },
}
const kaiyochishitsuLayer = {
  id: 'oh-kaiyochishitsu-layer',
  type: 'raster',
  source: 'kaiyochishitsu-source',
  'raster-resampling': 'nearest',
}
// MSIL 沈船 ----------------------------------------------------------------------------------------------------
const msilWrecksSource = {
  id: 'msil-wrecks-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/myphp/msilProxy.php?api=wrecks&layer=1',
  },
}
const msilWrecksLayer = {
  id: 'oh-msil-wrecks',
  type: 'symbol',
  source: 'msil-wrecks-source',
  layout: {
    'icon-image': 'wreck-ship',
    'icon-size': ['interpolate', ['linear'], ['zoom'], 3, 0.4, 8, 0.65, 12, 0.9],
    'icon-allow-overlap': true,
  },
  paint: {
    'icon-color': '#1a237e',
  },
}
// MSIL さんご[底質] --------------------------------------------------------------------------------------------
const msilCoralSource = {
  id: 'msil-coral-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/myphp/msilProxy.php?api=coral&layer=1',
  },
}
const msilCoralLayer = {
  id: 'oh-msil-coral',
  type: 'circle',
  source: 'msil-coral-source',
  paint: {
    'circle-color': '#ff7043',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 2, 8, 5, 12, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
    'circle-opacity': 0.8,
  },
}
// MSIL 泥・粘土[底質] ------------------------------------------------------------------------------------------
const msilMudSource = {
  id: 'msil-mud-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/myphp/msilProxy.php?api=mud-caly&layer=1',
  },
}
const msilMudLayer = {
  id: 'oh-msil-mud',
  type: 'circle',
  source: 'msil-mud-source',
  paint: {
    'circle-color': '#8d6e63',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 3, 2, 8, 5, 12, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
    'circle-opacity': 0.8,
  },
}
// 人工地形-----------------------------------------------------------------------------------------------------
const jinkochikeiSource = {
  id: 'jinkochikei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/jinkochikei/jinkochikei.pmtiles',
  },
}
const jinkochikeiLayer = {
  id: 'oh-jinkochikei',
  type: 'fill',
  source: 'jinkochikei-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'code'],
      '11001',
      'rgba(133, 196, 209, 0.8)',
      '11003',
      'rgba(133, 196, 209, 0.8)',
      '11009',
      'rgba(133, 196, 209, 0.8)',
      '11011',
      'rgba(133, 196, 209, 0.8)',
      '4010301',
      'rgba(133, 196, 209, 0.8)',
      '11002',
      'rgba(138, 216, 182, 0.8)',
      '11004',
      'rgba(239, 136, 136, 0.8)',
      '11006',
      'rgba(239, 136, 136, 0.8)',
      '11007',
      'rgba(239, 136, 136, 0.8)',
      '11014',
      'rgba(239, 136, 136, 0.8)',
      '4010201',
      'rgba(255, 79, 79, 0.8)',
      '11005',
      'rgba(255, 79, 79, 0.8)',
      '11008',
      'rgba(195, 122, 255, 0.8)',
      '4010101',
      'rgba(195, 122, 255, 0.8)',
      '11010',
      'rgba(255, 232, 232, 0.8)',
      'rgba(0, 0, 0, 0.8)', // デフォルトカラー
    ],
  },
}
// 自然地形-----------------------------------------------------------------------------------------------------
const shizenchikeiSource = {
  id: 'shizenchikei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shizenchikei/shizenchikei_mini.pmtiles',
  },
}
const shizenchikeiLayer = {
  id: 'oh-shizenchikei',
  type: 'fill',
  source: 'shizenchikei-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['to-string', ['get', 'code']],
      '10101',
      'rgba(217, 203, 174, 0.8)',
      '1010101',
      'rgba(217, 203, 174, 0.8)',
      '11201',
      'rgba(217, 203, 174, 0.8)',
      '11202',
      'rgba(217, 203, 174, 0.8)',
      '11203',
      'rgba(217, 203, 174, 0.8)',
      '11204',
      'rgba(217, 203, 174, 0.8)',
      '10202',
      'rgba(148, 102, 171, 0.8)',
      '10204',
      'rgba(148, 102, 171, 0.8)',
      '2010201',
      'rgba(148, 102, 171, 0.8)',
      '10205',
      'rgba(204, 153, 255, 0.8)',
      '10206',
      'rgba(204, 153, 255, 0.8)',
      '10301',
      'rgba(255, 170, 0, 0.8)',
      '10302',
      'rgba(255, 170, 0, 0.8)',
      '10303',
      'rgba(255, 170, 0, 0.8)',
      '10304',
      'rgba(255, 170, 0, 0.8)',
      '10308',
      'rgba(255, 170, 0, 0.8)',
      '10314',
      'rgba(255, 170, 0, 0.8)',
      '10305',
      'rgba(255, 170, 0, 0.8)',
      '10508',
      'rgba(255, 170, 0, 0.8)',
      '2010101',
      'rgba(255, 170, 0, 0.8)',
      '10306',
      'rgba(255, 170, 0, 0.8)',
      '10307',
      'rgba(255, 170, 0, 0.8)',
      '10310',
      'rgba(255, 170, 0, 0.8)',
      '10312',
      'rgba(255, 170, 0, 0.8)',
      '10401',
      'rgba(153, 128, 77, 0.8)',
      '10402',
      'rgba(153, 128, 77, 0.8)',
      '10403',
      'rgba(153, 128, 77, 0.8)',
      '10404',
      'rgba(153, 128, 77, 0.8)',
      '10406',
      'rgba(153, 128, 77, 0.8)',
      '10407',
      'rgba(153, 128, 77, 0.8)',
      '3010101',
      'rgba(153, 128, 77, 0.8)',
      '10501',
      'rgba(202, 204, 96, 0.8)',
      '10502',
      'rgba(202, 204, 96, 0.8)',
      '3020101',
      'rgba(202, 204, 96, 0.8)',
      '10503',
      'rgba(255, 255, 51, 0.8)',
      '3040101',
      'rgba(255, 255, 51, 0.8)',
      '10506',
      'rgba(251, 224, 157, 0.8)',
      '10507',
      'rgba(251, 224, 157, 0.8)',
      '10801',
      'rgba(251, 224, 157, 0.8)',
      '10504',
      'rgba(255, 255, 153, 0.8)',
      '10505',
      'rgba(255, 255, 153, 0.8)',
      '10512',
      'rgba(255, 255, 153, 0.8)',
      '3050101',
      'rgba(255, 255, 153, 0.8)',
      '10601',
      'rgba(163, 204, 126, 0.8)',
      '2010301',
      'rgba(163, 204, 126, 0.8)',
      '10701',
      'rgba(187, 255, 153, 0.8)',
      '3030101',
      'rgba(187, 255, 153, 0.8)',
      '10702',
      'rgba(187, 255, 153, 0.8)',
      '10705',
      'rgba(187, 255, 153, 0.8)',
      '10703',
      'rgba(0, 209, 164, 0.8)',
      '10804',
      'rgba(0, 209, 164, 0.8)',
      '3030201',
      'rgba(0, 209, 164, 0.8)',
      '10704',
      'rgba(102, 153, 255, 0.8)',
      '3040201',
      'rgba(102, 153, 255, 0.8)',
      '3040202',
      'rgba(102, 153, 255, 0.8)',
      '3040301',
      'rgba(31, 153, 153, 0.8)',
      '10802',
      'rgba(159, 159, 196, 0.8)',
      '10803',
      'rgba(159, 159, 196, 0.8)',
      '10807',
      'rgba(159, 159, 196, 0.8)',
      '10808',
      'rgba(159, 159, 196, 0.8)',
      '10805',
      'rgba(229, 255, 255, 0.8)',
      '10806',
      'rgba(229, 255, 255, 0.8)',
      '10901',
      'rgba(229, 255, 255, 0.8)',
      '10903',
      'rgba(229, 255, 255, 0.8)',
      '5010201',
      'rgba(229, 255, 255, 0.8)',
      '10904',
      'rgba(119, 153, 153, 0.8)',
      '5010301',
      'rgba(119, 153, 153, 0.8)',
      '11001',
      'rgba(133, 196, 209, 0.8)',
      '11003',
      'rgba(133, 196, 209, 0.8)',
      '11009',
      'rgba(133, 196, 209, 0.8)',
      '11011',
      'rgba(133, 196, 209, 0.8)',
      '4010301',
      'rgba(133, 196, 209, 0.8)',
      '11002',
      'rgba(138, 216, 182, 0.8)',
      '11004',
      'rgba(239, 136, 136, 0.8)',
      '11006',
      'rgba(239, 136, 136, 0.8)',
      '11007',
      'rgba(239, 136, 136, 0.8)',
      '11014',
      'rgba(239, 136, 136, 0.8)',
      '4010201',
      'rgba(255, 79, 79, 0.8)',
      '11005',
      'rgba(255, 79, 79, 0.8)',
      '11008',
      'rgba(195, 122, 255, 0.8)',
      '4010101',
      'rgba(195, 122, 255, 0.8)',
      '11010',
      'rgba(255, 232, 232, 0.8)',
      '999999',
      'rgba(20, 77, 250, 0.8)',
      '101',
      'rgba(230, 230, 0, 0.8)',
      '102',
      'rgba(0, 226, 230, 0.8)',
      '103',
      'rgba(42, 230, 0, 0.8)',
      '104',
      'rgba(230, 4, 0, 0.8)',
      '105',
      'rgba(94, 92, 230, 0.8)',
      '11',
      'rgba(153, 139, 121, 0.8)',
      '12',
      'rgba(102, 77, 85, 0.8)',
      '13',
      'rgba(117, 128, 210, 0.8)',
      '21',
      'rgba(201, 255, 5, 0.8)',
      '31',
      'rgba(255, 1, 22, 0.8)',
      '32',
      'rgba(255, 81, 1, 0.8)',
      '33',
      'rgba(201, 117, 176, 0.8)',
      '34',
      'rgba(255, 187, 252, 0.8)',
      '41',
      'rgba(255, 179, 26, 0.8)',
      '51',
      'rgba(199, 255, 247, 0.8)',
      '61',
      'rgba(255, 234, 1, 0.8)',
      '71',
      'rgba(18, 1, 255, 0.8)',
      '1',
      'rgba(153, 140, 122, 0.8)',
      '3',
      'rgba(255, 1, 22, 0.8)',
      '4',
      'rgba(255, 179, 26, 0.8)',
      '5',
      'rgba(199, 255, 247, 0.8)',
      '6',
      'rgba(255, 234, 1, 0.8)',
      '7',
      'rgba(18, 1, 255, 0.8)',
      '',
      'rgba(133, 182, 231, 0.8)',
      '9999',
      'rgba(255, 0, 255, 0.8)',
      'rgba(0, 0, 0, 0.8)', // デフォルトカラー
    ],
  },
}
// 都市計画決定情報パッケージ 2024年版
// ベースURL
const TOSHIKEIKAKU_2024_BASE = 'pmtiles://https://kenzkenz.xsrv.jp/pmtiles/toshikeikaku/2024'

// 1. tokei (都市計画区域) --------------------------------------------------------------------------------------------
const tokei2024Source = {
  id: 'tokei2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokei.pmtiles`,
  },
}
const tokei2024Layer = {
  id: 'oh-tokei2024-fill',
  type: 'fill',
  source: 'tokei2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 200, 200, 0.5)', // 薄い赤
  },
}
const tokei2024LayerLine = {
  id: 'oh-tokei2024-line',
  type: 'line',
  source: 'tokei2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 0, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2],
  },
}

// 2. youto (用途地域) --------------------------------------------------------------------------------------------
const youto2024Source = {
  id: 'youto2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/youto.pmtiles`,
  },
}
const youto2024Layer = {
  id: 'oh-youto2024-fill',
  type: 'fill',
  source: 'youto2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'YoutoCode'],
      1, 'rgba(0, 180, 0, 0.6)',       // 第1種低層住居専用地域（緑）
      2, 'rgba(50, 200, 50, 0.6)',     // 第2種低層住居専用地域
      3, 'rgba(100, 200, 100, 0.6)',   // 第1種中高層住居専用地域
      4, 'rgba(150, 220, 150, 0.6)',   // 第2種中高層住居専用地域
      5, 'rgba(200, 240, 200, 0.6)',   // 第1種住居地域
      6, 'rgba(230, 250, 230, 0.6)',   // 第2種住居地域
      7, 'rgba(255, 230, 180, 0.6)',   // 準住居地域（オレンジ寄り）
      8, 'rgba(180, 255, 180, 0.6)',   // 田園住居地域
      9, 'rgba(255, 180, 180, 0.6)',   // 近隣商業地域（ピンク）
      10, 'rgba(255, 100, 100, 0.6)',  // 商業地域（赤）
      11, 'rgba(200, 150, 255, 0.6)',  // 準工業地域（紫）
      12, 'rgba(150, 150, 200, 0.6)',  // 工業地域（青紫）
      13, 'rgba(100, 100, 180, 0.6)',  // 工業専用地域（濃い青紫）
      'rgba(200, 200, 200, 0.6)',
    ],
  },
}
const youto2024LayerLine = {
  id: 'oh-youto2024-line',
  type: 'line',
  source: 'youto2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#333',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 14, 1],
  },
}

// 3. senbiki (区域区分) --------------------------------------------------------------------------------------------
const senbiki2024Source = {
  id: 'senbiki2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/senbiki.pmtiles`,
  },
}
const senbiki2024Layer = {
  id: 'oh-senbiki2024-fill',
  type: 'fill',
  source: 'senbiki2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'AreaCode'],
      22, 'rgba(255, 200, 150, 0.5)', // 市街化区域（オレンジ）
      23, 'rgba(150, 220, 150, 0.5)', // 市街化調整区域（緑）
      'rgba(200, 200, 200, 0.5)',
    ],
  },
}
const senbiki2024LayerLine = {
  id: 'oh-senbiki2024-line',
  type: 'line',
  source: 'senbiki2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': [
      'match',
      ['get', 'AreaCode'],
      22, 'rgba(200, 100, 0, 1)',
      23, 'rgba(0, 150, 0, 1)',
      'rgba(100, 100, 100, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2],
  },
}

// 4. ritteki (立地適正化計画) --------------------------------------------------------------------------------------------
const ritteki2024Source = {
  id: 'ritteki2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/ritteki.pmtiles`,
  },
}
const ritteki2024Layer = {
  id: 'oh-ritteki2024-fill',
  type: 'fill',
  source: 'ritteki2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'AreaCode'],
      31, 'rgba(255, 200, 100, 0.5)', // 居住誘導区域（オレンジ）
      32, 'rgba(255, 100, 100, 0.5)', // 都市機能誘導区域（赤）
      'rgba(255, 255, 200, 0.3)',     // 立地適正化計画区域（薄黄）
    ],
  },
}
const ritteki2024LayerLine = {
  id: 'oh-ritteki2024-line',
  type: 'line',
  source: 'ritteki2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': [
      'match',
      ['get', 'AreaCode'],
      31, 'rgba(200, 150, 0, 1)',
      32, 'rgba(200, 0, 0, 1)',
      'rgba(150, 150, 0, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 14, 2],
  },
}

// 5. douro (都市計画道路) --------------------------------------------------------------------------------------------
const douro2024Source = {
  id: 'douro2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/douro.pmtiles`,
  },
}
const douro2024Layer = {
  id: 'oh-douro2024-fill',
  type: 'fill',
  source: 'douro2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'DouroCode'],
      3011, 'rgba(255, 200, 0, 0.5)', // 道路（黄色）
      3023, 'rgba(150, 255, 150, 0.5)', // 広場（緑）
      'rgba(255, 200, 0, 0.5)',
    ],
  },
}
const douro2024LayerLine = {
  id: 'oh-douro2024-line',
  type: 'line',
  source: 'douro2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 150, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 6. kouen (公園) --------------------------------------------------------------------------------------------
const kouen2024Source = {
  id: 'kouen2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/kouen.pmtiles`,
  },
}
const kouen2024Layer = {
  id: 'oh-kouen2024-fill',
  type: 'fill',
  source: 'kouen2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'ParkCode'],
      3021, 'rgba(100, 200, 100, 0.6)', // 公園（緑）
      3022, 'rgba(50, 180, 50, 0.6)',   // 緑地（濃い緑）
      3024, 'rgba(150, 150, 180, 0.6)', // 墓園（紫がかったグレー）
      'rgba(100, 200, 100, 0.6)',
    ],
  },
}
const kouen2024LayerLine = {
  id: 'oh-kouen2024-line',
  type: 'line',
  source: 'kouen2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 100, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 7. bouka (防火地域・準防火地域) --------------------------------------------------------------------------------------------
const bouka2024Source = {
  id: 'bouka2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/bouka.pmtiles`,
  },
}
const bouka2024Layer = {
  id: 'oh-bouka2024-fill',
  type: 'fill',
  source: 'bouka2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'AreaCode'],
      24, 'rgba(255, 0, 0, 0.5)',    // 防火地域（赤）
      25, 'rgba(255, 150, 100, 0.5)', // 準防火地域（オレンジ）
      'rgba(255, 100, 100, 0.5)',
    ],
  },
}
const bouka2024LayerLine = {
  id: 'oh-bouka2024-line',
  type: 'line',
  source: 'bouka2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': [
      'match',
      ['get', 'AreaCode'],
      24, 'rgba(200, 0, 0, 1)',
      25, 'rgba(200, 100, 0, 1)',
      'rgba(200, 0, 0, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 8. chikukei (地区計画) --------------------------------------------------------------------------------------------
const chikukei2024Source = {
  id: 'chikukei2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/chikukei.pmtiles`,
  },
}
const chikukei2024Layer = {
  id: 'oh-chikukei2024-fill',
  type: 'fill',
  source: 'chikukei2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(100, 150, 255, 0.5)', // 青
  },
}
const chikukei2024LayerLine = {
  id: 'oh-chikukei2024-line',
  type: 'line',
  source: 'chikukei2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 100, 200, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 9. tochiku (土地区画整理事業) --------------------------------------------------------------------------------------------
const tochiku2024Source = {
  id: 'tochiku2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tochiku.pmtiles`,
  },
}
const tochiku2024Layer = {
  id: 'oh-tochiku2024-fill',
  type: 'fill',
  source: 'tochiku2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 180, 100, 0.5)', // オレンジ
  },
}
const tochiku2024LayerLine = {
  id: 'oh-tochiku2024-line',
  type: 'line',
  source: 'tochiku2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 100, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 10. tkbt (特別用途地区) --------------------------------------------------------------------------------------------
const tkbt2024Source = {
  id: 'tkbt2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tkbt.pmtiles`,
  },
}
const tkbt2024Layer = {
  id: 'oh-tkbt2024-fill',
  type: 'fill',
  source: 'tkbt2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'YoutoCode'],
      1, 'rgba(150, 150, 200, 0.6)',   // 特別工業地区
      2, 'rgba(100, 150, 255, 0.6)',   // 文教地区
      3, 'rgba(255, 150, 150, 0.6)',   // 小売店舗地区
      4, 'rgba(200, 200, 150, 0.6)',   // 事務所地区
      5, 'rgba(150, 200, 200, 0.6)',   // 厚生地区
      6, 'rgba(255, 200, 150, 0.6)',   // 娯楽レクリエーション地区
      7, 'rgba(255, 220, 180, 0.6)',   // 観光地区
      8, 'rgba(180, 180, 220, 0.6)',   // 特別業務地区
      9, 'rgba(150, 220, 150, 0.6)',   // 中高層階住居専用地区
      10, 'rgba(255, 100, 100, 0.6)',  // 商業専用地区
      11, 'rgba(100, 180, 255, 0.6)',  // 研究開発地区
      'rgba(180, 150, 200, 0.6)',
    ],
  },
}
const tkbt2024LayerLine = {
  id: 'oh-tkbt2024-line',
  type: 'line',
  source: 'tkbt2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(100, 50, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 11. koudori (高度利用地区) --------------------------------------------------------------------------------------------
const koudori2024Source = {
  id: 'koudori2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/koudori.pmtiles`,
  },
}
const koudori2024Layer = {
  id: 'oh-koudori2024-fill',
  type: 'fill',
  source: 'koudori2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(100, 100, 200, 0.5)', // 青紫
  },
}
const koudori2024LayerLine = {
  id: 'oh-koudori2024-line',
  type: 'line',
  source: 'koudori2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(50, 50, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 12. koudoti (高度地区) --------------------------------------------------------------------------------------------
const koudoti2024Source = {
  id: 'koudoti2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/koudoti.pmtiles`,
  },
}
const koudoti2024Layer = {
  id: 'oh-koudoti2024-fill',
  type: 'fill',
  source: 'koudoti2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(150, 100, 200, 0.5)', // 紫
  },
}
const koudoti2024LayerLine = {
  id: 'oh-koudoti2024-line',
  type: 'line',
  source: 'koudoti2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(100, 50, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 13. fuuchichiku (風致地区) --------------------------------------------------------------------------------------------
const fuuchichiku2024Source = {
  id: 'fuuchichiku2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/fuuchichiku.pmtiles`,
  },
}
const fuuchichiku2024Layer = {
  id: 'oh-fuuchichiku2024-fill',
  type: 'fill',
  source: 'fuuchichiku2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(50, 150, 100, 0.5)', // 深緑
  },
}
const fuuchichiku2024LayerLine = {
  id: 'oh-fuuchichiku2024-line',
  type: 'line',
  source: 'fuuchichiku2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 100, 50, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 14. tokuteiyouto (特定用途制限地域) --------------------------------------------------------------------------------------------
const tokuteiyouto2024Source = {
  id: 'tokuteiyouto2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokuteiyouto.pmtiles`,
  },
}
const tokuteiyouto2024Layer = {
  id: 'oh-tokuteiyouto2024-fill',
  type: 'fill',
  source: 'tokuteiyouto2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(200, 150, 200, 0.5)', // ピンク紫
  },
}
const tokuteiyouto2024LayerLine = {
  id: 'oh-tokuteiyouto2024-line',
  type: 'line',
  source: 'tokuteiyouto2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(150, 100, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 15. jyuntoshi (準都市計画区域) --------------------------------------------------------------------------------------------
const jyuntoshi2024Source = {
  id: 'jyuntoshi2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/jyuntoshi.pmtiles`,
  },
}
const jyuntoshi2024Layer = {
  id: 'oh-jyuntoshi2024-fill',
  type: 'fill',
  source: 'jyuntoshi2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(200, 255, 200, 0.4)', // 薄緑
  },
}
const jyuntoshi2024LayerLine = {
  id: 'oh-jyuntoshi2024-line',
  type: 'line',
  source: 'jyuntoshi2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(100, 200, 100, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2],
  },
}

// 16. toshisaisei (都市再生特別地区) --------------------------------------------------------------------------------------------
const toshisaisei2024Source = {
  id: 'toshisaisei2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/toshisaisei.pmtiles`,
  },
}
const toshisaisei2024Layer = {
  id: 'oh-toshisaisei2024-fill',
  type: 'fill',
  source: 'toshisaisei2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0, 100, 255, 0.5)', // 青
  },
}
const toshisaisei2024LayerLine = {
  id: 'oh-toshisaisei2024-line',
  type: 'line',
  source: 'toshisaisei2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 50, 200, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 17. tokuryoku (特別緑地保全地区) --------------------------------------------------------------------------------------------
const tokuryoku2024Source = {
  id: 'tokuryoku2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokuryoku.pmtiles`,
  },
}
const tokuryoku2024Layer = {
  id: 'oh-tokuryoku2024-fill',
  type: 'fill',
  source: 'tokuryoku2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0, 150, 50, 0.5)', // 緑
  },
}
const tokuryoku2024LayerLine = {
  id: 'oh-tokuryoku2024-line',
  type: 'line',
  source: 'tokuryoku2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(0, 100, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 18. rekifuu (歴史的風土保存地区) --------------------------------------------------------------------------------------------
const rekifuu2024Source = {
  id: 'rekifuu2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/rekifuu.pmtiles`,
  },
}
const rekifuu2024Layer = {
  id: 'oh-rekifuu2024-fill',
  type: 'fill',
  source: 'rekifuu2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'DistCode'],
      31, 'rgba(180, 100, 50, 0.5)', // 歴史的風土特別保存地区（茶）
      'rgba(200, 150, 100, 0.4)',    // 歴史的風土保存区域（薄茶）
    ],
  },
}
const rekifuu2024LayerLine = {
  id: 'oh-rekifuu2024-line',
  type: 'line',
  source: 'rekifuu2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(150, 80, 30, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 19. tokuteiyuudou (特定用途誘導地区) --------------------------------------------------------------------------------------------
const tokuteiyuudou2024Source = {
  id: 'tokuteiyuudou2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokuteiyuudou.pmtiles`,
  },
}
const tokuteiyuudou2024Layer = {
  id: 'oh-tokuteiyuudou2024-fill',
  type: 'fill',
  source: 'tokuteiyuudou2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 150, 200, 0.5)', // ピンク
  },
}
const tokuteiyuudou2024LayerLine = {
  id: 'oh-tokuteiyuudou2024-line',
  type: 'line',
  source: 'tokuteiyuudou2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 100, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 20. soubou (航空機騒音障害防止地区) --------------------------------------------------------------------------------------------
const soubou2024Source = {
  id: 'soubou2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/soubou.pmtiles`,
  },
}
const soubou2024Layer = {
  id: 'oh-soubou2024-fill',
  type: 'fill',
  source: 'soubou2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'DistCode'],
      40, 'rgba(255, 200, 200, 0.5)', // 航空機騒音障害防止地区
      41, 'rgba(255, 150, 150, 0.5)', // 航空機騒音障害防止特別地区
      'rgba(255, 180, 180, 0.5)',
    ],
  },
}
const soubou2024LayerLine = {
  id: 'oh-soubou2024-line',
  type: 'line',
  source: 'soubou2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 100, 100, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 21. tokuteibouka (特定防災街区整備地区) --------------------------------------------------------------------------------------------
const tokuteibouka2024Source = {
  id: 'tokuteibouka2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokuteibouka.pmtiles`,
  },
}
const tokuteibouka2024Layer = {
  id: 'oh-tokuteibouka2024-fill',
  type: 'fill',
  source: 'tokuteibouka2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 50, 50, 0.5)', // 濃い赤
  },
}
const tokuteibouka2024LayerLine = {
  id: 'oh-tokuteibouka2024-line',
  type: 'line',
  source: 'tokuteibouka2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 0, 0, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 22. tokureiyouseki (特例容積率適用地区) --------------------------------------------------------------------------------------------
const tokureiyouseki2024Source = {
  id: 'tokureiyouseki2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/tokureiyouseki.pmtiles`,
  },
}
const tokureiyouseki2024Layer = {
  id: 'oh-tokureiyouseki2024-fill',
  type: 'fill',
  source: 'tokureiyouseki2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(150, 100, 200, 0.5)', // 紫
  },
}
const tokureiyouseki2024LayerLine = {
  id: 'oh-tokureiyouseki2024-line',
  type: 'line',
  source: 'tokureiyouseki2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(100, 50, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 23. ryokukachiiki (緑化地域) --------------------------------------------------------------------------------------------
const ryokukachiiki2024Source = {
  id: 'ryokukachiiki2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/ryokukachiiki.pmtiles`,
  },
}
const ryokukachiiki2024Layer = {
  id: 'oh-ryokukachiiki2024-fill',
  type: 'fill',
  source: 'ryokukachiiki2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(100, 200, 100, 0.5)', // 緑
  },
}
const ryokukachiiki2024LayerLine = {
  id: 'oh-ryokukachiiki2024-line',
  type: 'line',
  source: 'ryokukachiiki2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(50, 150, 50, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 24. kyojyuchosei (居住調整地域) --------------------------------------------------------------------------------------------
const kyojyuchosei2024Source = {
  id: 'kyojyuchosei2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/kyojyuchosei.pmtiles`,
  },
}
const kyojyuchosei2024Layer = {
  id: 'oh-kyojyuchosei2024-fill',
  type: 'fill',
  source: 'kyojyuchosei2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(200, 200, 100, 0.5)', // 黄緑
  },
}
const kyojyuchosei2024LayerLine = {
  id: 'oh-kyojyuchosei2024-line',
  type: 'line',
  source: 'kyojyuchosei2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(150, 150, 50, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 25. kousoujyukyo (高層住居誘導地区) --------------------------------------------------------------------------------------------
const kousoujyukyo2024Source = {
  id: 'kousoujyukyo2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/kousoujyukyo.pmtiles`,
  },
}
const kousoujyukyo2024Layer = {
  id: 'oh-kousoujyukyo2024-fill',
  type: 'fill',
  source: 'kousoujyukyo2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(100, 150, 200, 0.5)', // 青
  },
}
const kousoujyukyo2024LayerLine = {
  id: 'oh-kousoujyukyo2024-line',
  type: 'line',
  source: 'kousoujyukyo2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(50, 100, 150, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}

// 26. fukkousaiseikyoten (一団地の復興再生拠点市街地形成施設) --------------------------------------------------------------------------------------------
const fukkousaiseikyoten2024Source = {
  id: 'fukkousaiseikyoten2024-source',
  obj: {
    type: 'vector',
    url: `${TOSHIKEIKAKU_2024_BASE}/fukkousaiseikyoten.pmtiles`,
  },
}
const fukkousaiseikyoten2024Layer = {
  id: 'oh-fukkousaiseikyoten2024-fill',
  type: 'fill',
  source: 'fukkousaiseikyoten2024-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 200, 100, 0.5)', // オレンジ
  },
}
const fukkousaiseikyoten2024LayerLine = {
  id: 'oh-fukkousaiseikyoten2024-line',
  type: 'line',
  source: 'fukkousaiseikyoten2024-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(200, 150, 50, 1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}

// 旧データ（互換性のため残す）--------------------------------------------------------------------------------------------
// 都市計画 --------------------------------------------------------------------------------------------
const tokeiSource = {
  id: 'tokei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toshikeikaku/tokei.pmtiles',
  },
}
const tokeiLayer = {
  id: 'oh-tokei',
  type: 'fill',
  source: 'tokei-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'kubunID'],
      21,
      'rgba(255, 0, 0, 0.6)', // 都市計画区域（赤色）
      26,
      'rgba(0, 255, 0, 0.6)', // 準都市計画区域（緑色）
      27,
      'rgba(0, 0, 255, 0.6)', // 都市計画区域外（青色）
      'rgba(200, 200, 200, 0.6)', // デフォルト色（グレー）
    ],
  },
}
const tokeiLayerLine = {
  id: 'oh-tokei-line',
  type: 'line',
  source: 'tokei-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 区域区分 --------------------------------------------------------------------------------------------
const kuikikubunSource = {
  id: 'kuikikubun-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toshikeikaku/senbiki.pmtiles',
  },
}
const kuikikubunLayer = {
  id: 'oh-kuikikubun',
  type: 'fill',
  source: 'kuikikubun-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'kubunID'],
      22,
      'rgba(255, 165, 0, 0.6)', // 市街化区域（オレンジ色）
      23,
      'rgba(0, 128, 0, 0.6)', // 市街化調整区域（緑色）
      'rgba(200, 200, 200, 0.6)', // デフォルト色（グレー）
    ],
  },
}
const kuikikubunLayerLine = {
  id: 'oh-kuikikubun-line',
  type: 'line',
  source: 'kuikikubun-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 用途地域 --------------------------------------------------------------------------------------------
const yotochiikiPSource = {
  id: 'yotochiikiP-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toshikeikaku/yoto.pmtiles',
  },
}
const yotochiikiPLayer = {
  id: 'oh-yotochiikiP',
  type: 'fill',
  source: 'yotochiikiP-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'YoutoID'],
      0,
      'rgba(169, 169, 169 , 0.8)', // 用途地域の指定をしない区域（ダークグレー）
      1,
      'rgba(144, 238, 144 , 0.8)', // 第1種低層住居専用地域（ライトグリーン）
      2,
      'rgba(152, 251, 152 , 0.8)', // 第2種低層住居専用地域（パレグリーン）
      3,
      'rgba(60, 179, 113 , 0.8)', // 第1種中高層住居専用地域（ミディアムシースグリーン）
      4,
      'rgba(46, 139, 87 , 0.8)', // 第2種中高層住居専用地域（シーグリーン）
      5,
      'rgba(107, 142, 35 , 0.8)', // 第1種住居地域（オリーブドラブ）
      6,
      'rgba(0, 128, 0 , 0.8)', // 第2種住居地域（緑色）
      7,
      'rgba(144, 238, 144 , 0.8)', // 準住居地域（ライトグリーン）
      8,
      'rgba(60, 179, 113 , 0.8)', // 田園住居地域（ミディアムシースグリーン）
      9,
      'rgba(255, 69, 0 , 0.8)', // 近隣商業地域（オレンジレッド）
      10,
      'rgba(255, 0, 0 , 0.8)', // 商業地域（赤色）
      11,
      'rgba(105, 105, 105 , 0.8)', // 準工業地域（ディムグレー）
      12,
      'rgba(0, 0, 0 , 0.8)', // 工業地域（黒色）
      13,
      'rgba(47, 79, 79 , 0.8)', // 工業専用地域（ダークスレートグレー）
      14,
      'rgba(218, 112, 214 , 0.8)', // 特別用途地区（オーキッド）
      15,
      'rgba(186, 85, 211 , 0.8)', // 特定用途制限地域（ミディアムオーキッド）
      16,
      'rgba(147, 112, 219 , 0.8)', // 特例容積率適用地区（ミディアムスレートブルー）
      17,
      'rgba(138, 43, 226 , 0.8)', // 高層住居誘導地区（ブルーバイオレット）
      18,
      'rgba(75, 0, 130 , 0.8)', // 高度地区（インディゴ）
      19,
      'rgba(72, 61, 139 , 0.8)', // 高度利用地区（ダークスレートブルー）
      20,
      'rgba(0, 0, 205 , 0.8)', // 特定街区（ミディアムブルー）
      21,
      'rgba(0, 0, 255 , 0.8)', // 都市再生特別地区（ブルー）
      22,
      'rgba(34, 139, 34 , 0.8)', // 居住調整地域（フォレストグリーン）
      23,
      'rgba(46, 139, 87 , 0.8)', // 特定用途誘導地区（シーグリーン）
      24,
      'rgba(255, 0, 0 , 0.8)', // 防火地域（赤色）
      25,
      'rgba(255, 99, 71 , 0.8)', // 準防火地域（トマト）
      26,
      'rgba(220, 20, 60 , 0.8)', // 特定防災街区整備地区（クリムゾン）
      27,
      'rgba(154, 205, 50 , 0.8)', // 景観地区（イエローグリーン）
      28,
      'rgba(85, 107, 47 , 0.8)', // 風致地区（ダークオリーブグリーン）
      29,
      'rgba(139, 69, 19 , 0.8)', // 駐車場整備地区（サドルブラウン）
      30,
      'rgba(210, 105, 30 , 0.8)', // 臨港地区（チョコレート）
      31,
      'rgba(160, 82, 45 , 0.8)', // 歴史的風土特別保存地区（シエナ）
      32,
      'rgba(205, 92, 92 , 0.8)', // 第1種歴史的風土保存地区（インディアンレッド）
      33,
      'rgba(233, 150, 122 , 0.8)', // 第2種歴史的風土保存地区（ダークサーモン）
      34,
      'rgba(152, 251, 152 , 0.8)', // 緑地保全地域（パレグリーン）
      35,
      'rgba(0, 128, 0 , 0.8)', // 特別緑地保全地区（緑色）
      36,
      'rgba(107, 142, 35 , 0.8)', // 緑化地域（オリーブドラブ）
      37,
      'rgba(189, 183, 107 , 0.8)', // 流通業務地区（ダークカーキ）
      38,
      'rgba(85, 107, 47 , 0.8)', // 生産緑地地区（ダークオリーブグリーン）
      39,
      'rgba(255, 20, 147 , 0.8)', // 伝統的建造物群保存地区（ディープピンク）
      40,
      'rgba(255, 105, 180 , 0.8)', // 航空機騒音障害防止地区（ホットピンク）
      41,
      'rgba(199, 21, 133 , 0.8)', // 航空機騒音障害防止特別地区（ミディアムバイオレットレッド）
      42,
      'rgba(255, 182, 193 , 0.8)', // 居住環境向上用途誘導地区（ライトピンク）
      'rgba(200, 200, 200 , 0.8)', // デフォルト色（グレー）
    ],
  },
}
const yotochiikiPLayerLine = {
  id: 'oh-yotochiikiP-line',
  type: 'line',
  source: 'yotochiikiP-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 特別用途地区 --------------------------------------------------------------------------------------------
const tkbtSource = {
  id: 'tkbt-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toshikeikaku/tkbt.pmtiles',
  },
}
const tkbtLayer = {
  id: 'oh-tkbt',
  type: 'fill',
  source: 'tkbt-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 0, 0, 0.8)',
  },
}
const tkbtLayerLine = {
  id: 'oh-tkbt-line',
  type: 'line',
  source: 'tkbt-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// ---------------------------------------------------------------------------------------------------------------------
// 湖沼 --------------------------------------------------------------------------------------------
const kosyoSource = {
  id: 'kosyo-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kosyo/kosyo.pmtiles',
  },
}
const kosyoLayer = {
  id: 'oh-kosyo',
  type: 'fill',
  source: 'kosyo-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0, 0, 255, 0.8)',
  },
}
const kosyoLine = {
  id: 'oh-kosyo-line',
  type: 'line',
  source: 'kosyo-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// ダム --------------------------------------------------------------------------------------------
const damSource = {
  id: 'dam-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/dam/dam.pmtiles',
  },
}
const damLayer = {
  id: 'oh-dam-layer',
  type: 'circle',
  source: 'dam-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'navy',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 0.5, 4, 1, 7, 6, 11, 10],
  },
}
const damLayerLabel = {
  id: 'oh-dam-label',
  type: 'symbol',
  source: 'dam-source',
  'source-layer': 'point',
  layout: {
    'text-field': ['get', 'W01_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 0.7)',
    'text-halo-color': 'rgba(0,0,0,0.7)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 9,
}
// ----------------------------------------------------------------------------------------------
const kazanSource = {
  id: 'kazan-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/vlcd/{z}/{x}/{y}.png'],
  },
}
const kazanLayer = {
  id: 'oh-kazan',
  type: 'raster',
  source: 'kazan-source',
}
// 植生被覆率 --------------------------------------------------------------------------------------------
const hifukuSource = {
  id: 'hifuku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/hifuku/hifuku.pmtiles',
  },
}
const hifukuLayer = {
  id: 'oh-hifuku',
  type: 'fill',
  source: 'hifuku-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'c_FRAC_VEG']],
      0,
      'rgba(255, 255, 200, 0.8)', // 淡い黄色
      1,
      'rgba(0, 128, 0, 0.8)', // 濃い緑
    ],
  },
}
const hifukuLine = {
  id: 'oh-hifuku-line',
  type: 'line',
  source: 'hifuku-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 世界自然遺産 --------------------------------------------------------------------------------------------
const shizenisanSource = {
  id: 'shizenisan-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/sekaiisan/shizenisan.pmtiles',
  },
}
const shizenisanLayer = {
  id: 'oh-shizenisan',
  type: 'fill',
  source: 'shizenisan-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(255, 105, 180, 0.8)',
  },
}
const shizenisanLine = {
  id: 'oh-shizenisan-line',
  type: 'line',
  source: 'shizenisan-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 世界文化遺産 --------------------------------------------------------------------------------------------
const bunkaisanSource = {
  id: 'bunkaisan-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/sekaiisan/bunkaisan.pmtiles',
  },
}
const bunkaisanLayerA34a = {
  id: 'oh-bunkaisan-layer-A34a',
  type: 'fill',
  source: 'bunkaisan-source',
  'source-layer': 'A34a',
  paint: {
    'fill-color': 'rgba(157, 209, 199, 0.8)',
  },
}
const bunkaisanLineA34a = {
  id: 'oh-bunkaisan-line-A34a',
  type: 'line',
  source: 'bunkaisan-source',
  'source-layer': 'A34a',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
const bunkaisanLayerA34f = {
  id: 'oh-bunkaisan-layer-A34f',
  type: 'fill',
  source: 'bunkaisan-source',
  'source-layer': 'A34f',
  paint: {
    'fill-color': 'rgba(242, 183, 112, 0.8)',
  },
}
const bunkaisanLineA34f = {
  id: 'oh-bunkaisan-line-A34f',
  type: 'line',
  source: 'bunkaisan-source',
  'source-layer': 'A34f',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
const bunkaisanLineA34c = {
  id: 'oh-bunkaisan-line-A34c',
  type: 'line',
  source: 'bunkaisan-source',
  'source-layer': 'A34c',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      1,
      12,
      4,
    ],
  },
}
const bunkaisanPointA34b = {
  id: 'oh-bunkaisan-point-A34b',
  type: 'circle',
  source: 'bunkaisan-source',
  'source-layer': 'A34b',
  paint: {
    'circle-color': 'rgba(145, 82, 45, 1)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 1, 13, 8],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
const bunkaisanPointA34d = {
  id: 'oh-bunkaisan-point-A34d',
  type: 'circle',
  source: 'bunkaisan-source',
  'source-layer': 'A34d',
  paint: {
    'circle-color': 'rgba(152, 125, 183, 1)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 1, 13, 8],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
const bunkaisanPointA34e = {
  id: 'oh-bunkaisan-point-A34e',
  type: 'circle',
  source: 'bunkaisan-source',
  'source-layer': 'A34e',
  paint: {
    'circle-color': 'rgba(255, 158, 23, 1)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 1, 13, 8],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
const bunkaisanPointA34g = {
  id: 'oh-bunkaisan-point-A34g',
  type: 'circle',
  source: 'bunkaisan-source',
  'source-layer': 'A34g',
  paint: {
    'circle-color': 'rgba(164, 113, 88, 1)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 1, 13, 8],
    'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 0, 13, 1],
  },
}
// ジオパーク --------------------------------------------------------------------------------------------
const geoparkSource = {
  id: 'geopark-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/geopark/geopark.pmtiles',
  },
}
const geoparkLayer = {
  id: 'oh-geopark-layer',
  type: 'fill',
  source: 'geopark-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'attr'],
      'GGP',
      'rgba(255, 182, 193, 0.8)', // 淡い赤色
      'JGP',
      'rgba(102, 187, 106, 0.8)', // 既存の色
      'rgba(200, 200, 200, 0.8)', // デフォルト色
    ],
  },
}
const geoparkLine = {
  id: 'oh-geopark-line',
  type: 'line',
  source: 'geopark-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'red',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      9,
      2,
      10,
      4,
      12,
      8,
      14,
      10,
    ],
  },
}
// 自然公園 --------------------------------------------------------------------------------------------
const shizenkoenSource = {
  id: 'shizenkoen-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shizenkoen/shizenkoen.pmtiles',
  },
}
const shizenkoenLayer = {
  id: 'oh-shizenkoen-layer',
  type: 'fill',
  source: 'shizenkoen-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['to-number', ['get', 'LAYER_NO']], // 数値に変換
      1,
      'rgba(169, 223, 191, 0.8)', // 薄い緑
      2,
      'rgba(144, 190, 109, 0.8)', // 明るい緑
      3,
      'rgba(116, 178, 97, 0.8)', // 中程度の緑
      4,
      'rgba(92, 154, 82, 0.8)', // 深い緑
      5,
      'rgba(162, 217, 206, 0.8)', // パステルの緑
      6,
      'rgba(127, 205, 153, 0.8)', // 爽やかな緑
      7,
      'rgba(139, 195, 74, 0.8)', // 少し黄緑寄り
      8,
      'rgba(102, 187, 106, 0.8)', // 鮮やかな緑
      9,
      'rgba(174, 213, 129, 0.8)', // 薄めの緑
      10,
      'rgba(76, 175, 80, 0.8)', // 標準的な緑
      11,
      'rgba(124, 179, 66, 0.8)', // 明るい深緑
      12,
      'rgba(85, 139, 47, 0.8)', // 暗めの緑
      13,
      'rgba(51, 105, 30, 0.8)', // 森っぽい深緑
      14,
      'rgba(130, 204, 171, 0.8)', // パステル深緑
      15,
      'rgba(165, 214, 167, 0.8)', // 柔らかな緑
      16,
      'rgba(200, 230, 201, 0.8)', // とても柔らかな緑
      'rgba(0, 0, 0, 0.8)', // デフォルト色
    ],
  },
}
const shizenkoenLine = {
  id: 'oh-shizenkoen-line',
  type: 'line',
  source: 'shizenkoen-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 宅地造成等工事規制区域・特定盛土等規制区域（A56-2025 / 盛土規制法）-------------------------------------
const moritokiseiSource = {
  id: 'moritokisei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/moritokisei/moritokisei.pmtiles',
  },
}
const moritokiseiLayer = {
  id: 'oh-moritokisei-layer',
  type: 'fill',
  source: 'moritokisei-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'A56_004'],
      '1', 'rgba(229, 57, 53, 0.55)',   // 宅地造成等工事規制区域（赤）
      '2', 'rgba(30, 136, 229, 0.55)',  // 特定盛土等規制区域（青）
      'rgba(158, 158, 158, 0.55)',      // 記載なし等（グレー）
    ],
  },
}
const moritokiseiLine = {
  id: 'oh-moritokisei-line',
  type: 'line',
  source: 'moritokisei-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': [
      'match',
      ['get', 'A56_004'],
      '1', 'rgba(183, 28, 28, 0.9)',
      '2', 'rgba(13, 71, 161, 0.9)',
      'rgba(66, 66, 66, 0.9)',
    ],
    'line-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      10, 0.3,
      14, 1.2,
    ],
  },
}
// 国立公園 --------------------------------------------------------------------------------------------
const kokuritsukoenSource = {
  id: 'kokuritsukoen-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kokuritsukoen/kokuritsukoen.pmtiles',
  },
}
const kokuritsukoenLayer = {
  id: 'oh-kokuritsukoen-layer',
  type: 'fill',
  source: 'kokuritsukoen-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', '地域区'],
      '特別保護地区',
      'rgba(34, 139, 34, 0.8)', // フォレストグリーン
      '第1種特別地域',
      'rgba(60, 179, 113, 0.8)', // ミディアムシーグリーン
      '第2種特別地域',
      'rgba(107, 142, 35, 0.8)', // オリーブドラブ
      '第3種特別地域',
      'rgba(85, 107, 47, 0.8)', // ダークオリーブグリーン
      '普通地域',
      'rgba(144, 238, 144, 0.8)', // ライトグリーン
      '海域公園地区',
      'rgba(32, 178, 170, 0.8)', // ライトシーグリーン
      '区分未定',
      'rgba(169, 169, 169, 0.8)', // ダークグレー
      /* default */ 'rgba(0, 0, 0, 0.8)', // デフォルトは黒
    ],
  },
}
const kokuritsukoenLine = {
  id: 'oh-kokuritsukoen-line',
  type: 'line',
  source: 'kokuritsukoen-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 都市圏活断層図----------------------------------------------------------------------------------------------------------
const dansoSource = {
  id: 'danso-source',
  obj: {
    type: 'raster',
    tiles: ['https://cyberjapandata.gsi.go.jp/xyz/afm/{z}/{x}/{y}.png'],
  },
}
const dansoLayer = {
  id: 'oh-danso-layer',
  type: 'raster',
  source: 'danso-source',
}
// J-SHIS 主要活断層帯（GeoJSON）---------------------------------------------------------------------------------------
// データ: J-SHIS GeoJSON API (Y2024) から全360断層帯を一括取得・マージ
const jshisFaultSource = {
  id: 'jshis-fault-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/jshis_major_fault.geojson',
  },
}
const jshisFaultFillLayer = {
  id: 'oh-jshis-fault-fill',
  type: 'fill',
  source: 'jshis-fault-source',
  paint: {
    'fill-color': [
      'interpolate', ['linear'],
      ['to-number', ['get', 'T30P_NUM'], 0],
      0, 'rgba(255, 255, 0, 0.3)',
      0.1, 'rgba(255, 165, 0, 0.5)',
      3, 'rgba(255, 80, 0, 0.6)',
      10, 'rgba(255, 0, 0, 0.7)',
    ],
    'fill-outline-color': 'rgba(180, 0, 0, 0.8)',
  },
  minzoom: 4,
}
const jshisFaultLineLayer = {
  id: 'oh-jshis-fault-line',
  type: 'line',
  source: 'jshis-fault-source',
  paint: {
    'line-color': '#cc0000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 2, 14, 3],
  },
  minzoom: 4,
}
const jshisFaultLabelLayer = {
  id: 'oh-jshis-fault-label',
  type: 'symbol',
  source: 'jshis-fault-source',
  layout: {
    'text-field': ['get', 'LTENAME'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 6, 9, 10, 12, 14, 14],
    'text-allow-overlap': false,
    'text-padding': 10,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#990000',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5,
  },
  minzoom: 7,
}
// 気象庁 地震情報（リアルタイム）--------------------------------------------------------------------------------------
// 公式一次ソース（気象庁 bosai API）: 震源 + 各観測点の震度 + EEW
const jmaQuakeEpicenterSource = {
  id: 'oh-jma-quake-epicenter-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const jmaQuakeStationSource = {
  id: 'oh-jma-quake-station-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const jmaQuakeEewSource = {
  id: 'oh-jma-quake-eew-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
// 観測点震度（JMA 公式震度階級色）
const jmaQuakeStationLayer = {
  id: 'oh-jma-quake-station-layer',
  type: 'circle',
  source: 'oh-jma-quake-station-source',
  paint: {
    'circle-color': ['coalesce', ['get', 'color'], '#AAAAAA'],
    'circle-radius': [
      'interpolate', ['linear'], ['zoom'],
      4, ['max', 3, ['*', ['coalesce', ['get', 'rank'], 1], 1]],
      10, ['max', 6, ['*', ['coalesce', ['get', 'rank'], 1], 2.2]],
    ],
    'circle-stroke-color': 'rgba(0,0,0,0.6)',
    'circle-stroke-width': 1,
    'circle-opacity': 0.95,
  },
}
const jmaQuakeStationLabelLayer = {
  id: 'oh-jma-quake-station-label',
  type: 'symbol',
  source: 'oh-jma-quake-station-source',
  minzoom: 8,
  layout: {
    'text-field': ['get', 'intLabel'],
    'text-size': 11,
    'text-offset': [0, 1.2],
    'text-allow-overlap': false,
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
}
// 震源（星印風の大きめ circle）
const jmaQuakeEpicenterLayer = {
  id: 'oh-jma-quake-epicenter-layer',
  type: 'circle',
  source: 'oh-jma-quake-epicenter-source',
  paint: {
    'circle-color': 'rgba(255, 60, 60, 0.8)',
    'circle-radius': [
      'interpolate', ['linear'], ['coalesce', ['get', 'mag'], 3],
      2, 8,
      5, 14,
      7, 22,
      9, 32,
    ],
    'circle-stroke-color': '#800000',
    'circle-stroke-width': 2,
  },
}
const jmaQuakeEpicenterLabelLayer = {
  id: 'oh-jma-quake-epicenter-label',
  type: 'symbol',
  source: 'oh-jma-quake-epicenter-source',
  layout: {
    'text-field': ['get', 'label'],
    'text-size': 13,
    'text-offset': [0, -1.6],
    'text-allow-overlap': true,
  },
  paint: {
    'text-color': '#800000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
}
// EEW（予想震度円）
const jmaQuakeEewLayer = {
  id: 'oh-jma-quake-eew-layer',
  type: 'circle',
  source: 'oh-jma-quake-eew-source',
  paint: {
    'circle-color': 'rgba(255, 0, 0, 0.15)',
    'circle-radius': [
      'interpolate', ['linear'], ['zoom'],
      4, 40,
      10, 120,
    ],
    'circle-stroke-color': '#d32f2f',
    'circle-stroke-width': 3,
  },
}
// Overture Maps buildings（PMTiles）----------------------------------------------------------------------------------
const overtureBuildsSource = {
  id: 'overture-buildings-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://overturemaps-tiles-us-west-2-beta.s3.amazonaws.com/2026-01-21/buildings.pmtiles',
  },
}
const overtureBuildsFillLayer = {
  id: 'oh-overture-buildings-fill',
  type: 'fill',
  source: 'overture-buildings-source',
  'source-layer': 'building',
  paint: {
    'fill-color': [
      'case',
      ['has', 'height'],
      [
        'interpolate', ['linear'], ['to-number', ['get', 'height'], 0],
        0, '#f0e6d3',
        10, '#d4a574',
        30, '#b87333',
        60, '#8b4513',
        100, '#5c2d0e',
      ],
      '#d4c5a9',
    ],
    'fill-opacity': 0.6,
  },
  minzoom: 14,
}
const overtureBuildsLineLayer = {
  id: 'oh-overture-buildings-line',
  type: 'line',
  source: 'overture-buildings-source',
  'source-layer': 'building',
  paint: {
    'line-color': '#8b7355',
    'line-width': 0.5,
  },
  minzoom: 14,
}
// 駅別乗降客数-----------------------------------------------------------------------------------------------------
const ekibetsukyakuSource = {
  id: 'ekibetsukyaku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/ekibetsukyaku/ekibetsukyaku.pmtiles',
  },
}
const ekibetsukyakuSource3d = {
  id: 'ekibetsukyaku-source-3d',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/ekibetsukyaku/ekibetsukyaku3d.pmtiles',
  },
}
const tetsudo2Source = {
  id: 'tetsudo-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tetsudo/tetsudo.pmtiles',
  },
}
const tetsudoLine = {
  id: 'oh-tetsudo-line',
  type: 'line',
  source: 'tetsudo-source',
  'source-layer': 'line',
  paint: {
    'line-color': 'blue',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      1,
      11,
      3,
    ],
  },
}
const ekibetsukyakuLine = {
  id: 'oh-ekibetsukyaku-line',
  type: 'line',
  source: 'ekibetsukyaku-source',
  'source-layer': 'line',
  paint: {
    'line-color': 'hotpink',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      5,
      11,
      10,
    ],
  },
}
const ekibetsukyakuHeight = {
  id: 'oh-ekibetsukyakue-height',
  type: 'fill-extrusion',
  source: 'ekibetsukyaku-source-3d',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['get', 'S12_053'], 0, 50, 1200000, 3000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['get', 'S12_053'],
      0,
      'hsl(240, 100%, 50%)', // 深い青
      100,
      'hsl(230, 100%, 55%)', // 明るめの青
      300,
      'hsl(220, 100%, 60%)', // 薄い青
      500,
      'hsl(210, 100%, 65%)', // 青
      1000,
      'hsl(200, 100%, 70%)', // 青みがかったシアン
      5000,
      'hsl(180, 100%, 50%)', // シアン
      20000,
      'hsl(150, 100%, 50%)', // 青緑
      100000,
      'hsl(120, 100%, 50%)', // 緑
      500000,
      'hsl(60, 100%, 50%)', // 黄色
      1200000,
      'hsl(0, 100%, 50%)', // 赤
    ],
  },
}
const ekibetsukyakuLabel = {
  id: 'oh-ekibetsukyaku-label',
  type: 'symbol',
  source: 'ekibetsukyaku-source',
  'source-layer': 'line',
  layout: {
    'text-field': ['number-format', ['to-number', ['get', 'S12_053']], { 'min-fraction-digits': 0 }],
    'text-font': ['NotoSansJP-Regular'],
    'symbol-placement': 'line', // ラインに沿って配置
    'text-offset': [0, 1], // ラインの横側に表示（Y方向にオフセット）
    'text-anchor': 'center', // テキストのアンカー位置
    'text-rotation-alignment': 'map', // マップの向きにテキストを合わせる
  },
  paint: {
    'text-color': 'rgba(255, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  filter: ['!=', ['to-number', ['get', 'S12_053']], 0], // 0以外を表示
  minzoom: 10,
}
// ---------------------------------------------------------------------------------------------------------------------
// 交通事故統計情報(2019年〜2022年) リニューアル版
const trafficAccidentSource = {
  id: 'traffic-accident-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/TrafficAccidentRecord/ta_20192022_full.pmtiles',
  },
}
// ヒートマップレイヤー（低ズーム: 0〜12）
const trafficAccidentHeatmap = {
  id: 'oh-traffic-accident-heatmap',
  type: 'heatmap',
  source: 'traffic-accident-source',
  'source-layer': 'TrafficAccidentRecord',
  maxzoom: 13,
  paint: {
    'heatmap-weight': [
      'case',
      ['>', ['get', '死者数'], 0], 5, // 死亡事故は重み5倍
      1,
    ],
    'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 12, 2],
    'heatmap-color': [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0, 'rgba(0,0,0,0)',
      0.1, 'rgba(0,0,255,0.3)',
      0.3, 'rgba(0,255,255,0.5)',
      0.5, 'rgba(0,255,0,0.6)',
      0.7, 'rgba(255,255,0,0.7)',
      0.9, 'rgba(255,128,0,0.85)',
      1.0, 'rgba(255,0,0,1)',
    ],
    'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 6, 10, 10, 20, 12, 30],
    'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 11, 1, 13, 0],
  },
}
// 事故類型別色分けサークル（高ズーム: 12〜、非死亡事故のみ）
const trafficAccidentCircle = {
  id: 'oh-traffic-accident',
  type: 'circle',
  source: 'traffic-accident-source',
  'source-layer': 'TrafficAccidentRecord',
  minzoom: 12,
  filter: ['==', ['get', '死者数'], 0], // 死亡事故はfatalレイヤーで表示
  paint: {
    'circle-color': [
      'case',
      ['==', ['get', '事故類型'], 1], '#3388ff',        // 人vs車: 青
      ['==', ['get', '事故類型'], 21], '#ff8800',       // 車vs車: オレンジ
      ['==', ['get', '事故類型'], 41], '#cc33ff',       // 車両単独: 紫
      ['==', ['get', '事故類型'], 61], '#000000',       // 列車: 黒
      '#888888', // その他: グレー
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 2, 14, 4, 18, 6],
    'circle-opacity': ['interpolate', ['linear'], ['zoom'], 12, 0.5, 14, 0.7],
    'circle-stroke-width': 0,
  },
}
// 死亡事故マーカー（zoom10〜、赤丸+白縁で明確に区別）
const trafficAccidentFatal = {
  id: 'oh-traffic-accident-fatal',
  type: 'circle',
  source: 'traffic-accident-source',
  'source-layer': 'TrafficAccidentRecord',
  minzoom: 10,
  filter: ['>', ['get', '死者数'], 0],
  paint: {
    'circle-color': '#ff0000',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 10],
    'circle-opacity': 0.8,
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
  },
}
// 経済センサス活動調査 500mメッシュ --------------------------------------------------------------------------
const keizaiCensusSource = {
  id: 'keizai-census-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/keizai_census/keizai_census_500m.pmtiles',
  },
}
const keizaiCensusLayer = {
  id: 'oh-keizai-census',
  type: 'fill',
  source: 'keizai-census-source',
  'source-layer': 'keizai_census',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'e_all'], 0],
      0, 'rgba(255,255,255,0.3)', 50, '#4fc3f7', 200, '#1976d2', 1000, '#0d47a1', 5000, '#1a0033'],
  },
}
const keizaiCensusLineLayer = {
  id: 'oh-keizai-census-line',
  type: 'line',
  source: 'keizai-census-source',
  'source-layer': 'keizai_census',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.5, 14, 1],
    'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.3, 14, 0.6],
  },
  minzoom: 10,
}
const keizaiCensusLabelLayer = {
  id: 'oh-keizai-census-label',
  type: 'symbol',
  source: 'keizai-census-source',
  'source-layer': 'keizai_census',
  layout: {
    'text-field': ['to-string', ['get', 'e_all']],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 14, 13],
    'text-allow-overlap': false,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
// 国勢調査2020 500mメッシュ 人口移動・就業状態等及び従業地・通学地 ---------------------------------------------------
const jinkoIdoSource = {
  id: 'jinko-ido-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/jinko_ido/jinko_ido_500m.pmtiles',
  },
}
const jinkoIdoLayer = {
  id: 'oh-jinko-ido',
  type: 'fill',
  source: 'jinko-ido-source',
  'source-layer': 'jinko_ido',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'emp_t'], 0],
      0, 'rgba(255,255,255,0.3)', 50, '#a5d6a7', 200, '#66bb6a', 1000, '#2e7d32', 5000, '#1b5e20'],
  },
}
const jinkoIdoLineLayer = {
  id: 'oh-jinko-ido-line',
  type: 'line',
  source: 'jinko-ido-source',
  'source-layer': 'jinko_ido',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.5, 14, 1],
    'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.3, 14, 0.6],
  },
  minzoom: 10,
}
const jinkoIdoLabelLayer = {
  id: 'oh-jinko-ido-label',
  type: 'symbol',
  source: 'jinko-ido-source',
  'source-layer': 'jinko_ido',
  layout: {
    'text-field': ['to-string', ['get', 'emp_t']],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 14, 13],
    'text-allow-overlap': false,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
// 国勢調査2020 500mメッシュ 住宅の所有及び建て方 -----------------------------------------------------------------------
const jutakuMeshSource = {
  id: 'jutaku-mesh-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/jutaku_mesh/jutaku_500m.pmtiles',
  },
}
const jutakuMeshLayer = {
  id: 'oh-jutaku-mesh',
  type: 'fill',
  source: 'jutaku-mesh-source',
  'source-layer': 'jutaku_mesh',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'h_total'], 0],
      0, 'rgba(255,255,255,0.3)', 50, '#90caf9', 200, '#42a5f5', 500, '#1565c0', 2000, '#0d47a1'],
  },
}
const jutakuMeshLineLayer = {
  id: 'oh-jutaku-mesh-line',
  type: 'line',
  source: 'jutaku-mesh-source',
  'source-layer': 'jutaku_mesh',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.5, 14, 1],
    'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.3, 14, 0.6],
  },
  minzoom: 10,
}
const jutakuMeshLabelLayer = {
  id: 'oh-jutaku-mesh-label',
  type: 'symbol',
  source: 'jutaku-mesh-source',
  'source-layer': 'jutaku_mesh',
  layout: {
    'text-field': ['to-string', ['get', 'h_total']],
    'text-font': ['Open Sans Bold'],
    'text-offset': [0, 0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 14, 13],
    'text-allow-overlap': false,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
// 国勢調査2020 250mメッシュ 世帯の種類・世帯人員 -----------------------------------------------------------------------
const setaiMeshSource = {
  id: 'setai-mesh-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/setai_mesh/setai_250m.pmtiles',
  },
}
const setaiMeshLayer = {
  id: 'oh-setai-mesh',
  type: 'fill',
  source: 'setai-mesh-source',
  'source-layer': 'setai_mesh',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'pop_t'], 0],
      0, 'rgba(255,255,255,0.3)', 100, '#a5d6a7', 500, '#66bb6a', 2000, '#2e7d32', 10000, '#1b5e20'],
  },
}
const setaiMeshLineLayer = {
  id: 'oh-setai-mesh-line',
  type: 'line',
  source: 'setai-mesh-source',
  'source-layer': 'setai_mesh',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.5, 14, 1],
    'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.3, 14, 0.6],
  },
  minzoom: 10,
}
const setaiMeshLabelLayer = {
  id: 'oh-setai-mesh-label',
  type: 'symbol',
  source: 'setai-mesh-source',
  'source-layer': 'setai_mesh',
  layout: {
    'text-field': ['to-string', ['get', 'pop_t']],
    'text-font': ['Open Sans Bold'],
    'text-offset': [0, 0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 14, 13],
    'text-allow-overlap': false,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 13,
}
// 経済センサス2021 1kmメッシュ 売上金額階級別・従業者規模別 -----------------------------------------------------------------------
const uriageMeshSource = {
  id: 'uriage-mesh-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/uriage_mesh/uriage_1km.pmtiles',
  },
}
const uriageMeshLayer = {
  id: 'oh-uriage-mesh',
  type: 'fill',
  source: 'uriage-mesh-source',
  'source-layer': 'uriage_mesh',
  paint: {
    'fill-color': ['interpolate', ['linear'], ['to-number', ['get', 'e_all'], 0],
      0, 'rgba(255,255,255,0.3)', 10, '#bbdefb', 50, '#64b5f6', 200, '#1976d2', 1000, '#0d47a1'],
  },
}
const uriageMeshLineLayer = {
  id: 'oh-uriage-mesh-line',
  type: 'line',
  source: 'uriage-mesh-source',
  'source-layer': 'uriage_mesh',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.5, 14, 1],
    'line-opacity': ['interpolate', ['linear'], ['zoom'], 10, 0, 12, 0.3, 14, 0.6],
  },
  minzoom: 10,
}
const uriageMeshLabelLayer = {
  id: 'oh-uriage-mesh-label',
  type: 'symbol',
  source: 'uriage-mesh-source',
  'source-layer': 'uriage_mesh',
  layout: {
    'text-field': ['to-string', ['get', 'e_all']],
    'text-font': ['Open Sans Bold'],
    'text-offset': [0, 0],
    'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 14, 13],
    'text-allow-overlap': false,
    'symbol-placement': 'point',
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 12,
}
// みんなで石仏調査--------------------------------------------------------------------------------------------------------
const sekibutsuSource = {
  id: 'sekibutsu-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/sekibutsu/sekibutsu.pmtiles',
  },
}
const sekibutsuLayer = {
  id: 'oh-sekibutsu',
  type: 'circle',
  source: 'sekibutsu-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'rgba(47, 79, 79, 0.9)',
    'circle-radius': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      2,
      1,
      4,
      3,
      7,
      6,
      11,
      10,
    ],
  },
}
// 学校-------------------------------------------------------------------------------------------
const schoolSource = {
  id: 'school-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/school/school.pmtiles',
  },
}
const schoolLayer = {
  id: 'oh-school',
  type: 'circle',
  source: 'school-source',
  'source-layer': 'school',
  paint: {
    'circle-color': [
      'match',
      ['get', 'P29_003'],
      '16001', '#FF6B6B', // 小学校 - 赤
      '16002', '#4ECDC4', // 中学校 - ティール
      '16003', '#45B7D1', // 中等教育学校 - 水色
      '16004', '#96CEB4', // 高等学校 - 緑
      '16005', '#FFEAA7', // 高等専門学校 - 黄
      '16006', '#DDA0DD', // 短期大学 - プラム
      '16007', '#9B59B6', // 大学 - 紫
      '16011', '#FFB6C1', // 幼稚園 - ピンク
      '16012', '#87CEEB', // 特別支援学校 - スカイブルー
      '16013', '#FFA07A', // 幼保連携型認定こども園 - サーモン
      '16014', '#FF7F50', // 義務教育学校 - コーラル
      '16015', '#A9A9A9', // 各種学校 - グレー
      '16016', '#B8860B', // 専修学校 - ダークゴールド
      '#888888', // デフォルト
    ],
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      5, 3,
      10, 7,
      14, 12,
    ],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': [
      'interpolate',
      ['linear'],
      ['zoom'],
      5, 0,
      10, 1,
      14, 2,
    ],
  },
}
const schoolLayerLabel = {
  id: 'oh-school-label',
  type: 'symbol',
  source: 'school-source',
  'source-layer': 'school',
  layout: {
    'text-field': ['get', 'P29_004'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': 12,
    'text-offset': [0, 1.3],
    'text-anchor': 'top',
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
  minzoom: 11,
}
// 流域（分水嶺）-------------------------------------------------------------------------------------------
const ryuikiSource2 = {
  id: 'ryuiki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/ryuiki/ryuiki5.pmtiles',
  },
}
const ryuikiLayer2 = {
  id: 'oh-ryuiki',
  type: 'fill',
  source: 'ryuiki-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
  filter: ['has', 'suikei'],
}
const ryuikiLayerLine2 = {
  id: 'oh-ryuiki-line',
  type: 'line',
  source: 'ryuiki-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      7,
      0,
      11,
      0.5,
    ],
  },
  filter: ['has', 'suikei'],
}
// 分水嶺-------------------------------------------------------------------------------------------
const bunsuireiSource = {
  id: 'bunsuirei-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/ryuiki/ryuiki5.pmtiles',
  },
}
const bunsuireiLayer = {
  id: 'oh-bunsuirei',
  type: 'fill',
  source: 'bunsuirei-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': ['get', 'random_color'],
  },
  // filter: ['has', 'suikei']
}
const bunsuireiLayerLine = {
  id: 'oh-bunsuirei-line',
  type: 'line',
  source: 'bunsuirei-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0, 11, 0.5],
  },
  // filter: ['has', 'suikei']
}
// 観光-------------------------------------------------------------------------------------------
const kankoSource = {
  id: 'kanko-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kanko/kanko.pmtiles',
  },
}
const kankoLayer = {
  id: 'oh-kanko',
  type: 'fill',
  source: 'kanko-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(216,233,169,0.8)',
  },
}
const kankoLayerLine = {
  id: 'oh-kanko-layer-line',
  type: 'line',
  source: 'kanko-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0, 11, 0.5],
  },
}
const kankoLine = {
  id: 'oh-kanko-line',
  type: 'line',
  source: 'kanko-source',
  'source-layer': 'line',
  paint: {
    'line-color': 'rgba(173,135,184,1)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 7, 1, 11, 4],
  },
}
const kankoPoint = {
  id: 'oh-kanko-point',
  type: 'circle',
  source: 'kanko-source',
  'source-layer': 'point',
  paint: {
    'circle-color': 'rgba(248,216,74,1.0)',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 1, 11, 10],
    'circle-stroke-color': 'rgba(255, 255, 255, 1)',
    'circle-stroke-width': 1,
  },
}
// 宿泊容量メッシュ --------------------------------------------------------------------------------------------
const syukuhakuSource = {
  id: 'syukuhaku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/syukuhaku/syukuhaku.pmtiles',
  },
}
const syukuhakuLayer = {
  id: 'oh-syukuhaku',
  type: 'fill',
  source: 'syukuhaku-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'P09_013']],
      0,
      'white',
      3000,
      'red',
      20000,
      'black',
    ],
  },
}
const syukuhakuLayerLine = {
  id: 'oh-syukuhaku-line',
  type: 'line',
  source: 'syukuhaku-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
const syukuhakuLayerHeight = {
  id: 'oh-syukuhaku-height',
  type: 'fill-extrusion',
  source: 'syukuhaku-source',
  'source-layer': 'polygon',
  paint: {
    'fill-extrusion-height': ['interpolate', ['linear'], ['to-number', ['get', 'P09_013']], 0, 50, 11000, 5000],
    'fill-extrusion-color': [
      'interpolate',
      ['linear'],
      ['to-number', ['get', 'P09_013']],
      0,
      'white',
      3000,
      'red',
      20000,
      'black',
    ],
  },
}
// 筆ポリゴン --------------------------------------------------------------------------------------------
const fudeSource = {
  id: 'fude-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/fude/f.pmtiles',
  },
}
const fudeLayer = {
  id: 'oh-fude',
  type: 'fill',
  source: 'fude-source',
  'source-layer': 'f',
  paint: {
    'fill-color': [
      'case',
      ['==', ['get', 'land_type'], 100],
      'rgba(0, 128, 0, 0.8)', // 緑色 (透明度 0.8)
      ['==', ['get', 'land_type'], 200],
      'rgba(255, 0, 0, 0.8)', // 赤色 (透明度 0.8)
      'rgba(255, 255, 255, 0.8)', // 白色 (透明度 0.8)
    ],
  },
}
const fudeLine = {
  id: 'oh-fude-line',
  type: 'line',
  source: 'fude-source',
  'source-layer': 'f',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 農業地域 --------------------------------------------------------------------------------------------
const nogyochiikiSource = {
  id: 'nogyochiiki-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/nogyo/nogyo.pmtiles',
  },
}
const nogyochiikiLayer = {
  id: 'oh-nogyochiiki',
  type: 'fill',
  source: 'nogyochiiki-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'LAYER_NO'],
      5,
      'rgba(163,222,192,0.8)',
      6,
      'rgba(166,142,186,0.8)',
    ],
  },
}
const nogyochiikiLine = {
  id: 'oh-nogyochiiki-line',
  type: 'line',
  source: 'nogyochiiki-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      11,
      0,
      12,
      0.5,
    ],
  },
}
// 基本基準点-------------------------------------------------------------------------------------------
const kizyuntenSource = {
  id: 'kizyunten-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kizyunten/kizyunten2.pmtiles',
  },
}
export const kizyuntenPoint = {
  id: 'oh-kizyunten-point',
  type: 'circle',
  source: 'kizyunten-source',
  'source-layer': 'point',
  filter: [
    'any', // OR条件でフィルタ
    ['>=', ['zoom'], 9], // ズームレベル11以上の場合は全て表示
    [
      'all', // AND条件
      ['<=', ['zoom'], 8], // ズームレベル10以下
      ['match', ['get', '基準点種別'], ['電子基準点', '一等三角点'], true, false], // 電子基準点と一等三角点のみ
    ],
  ],
  paint: {
    'circle-color': [
      'case',
      ['==', ['get', '成果状態'], '正常'],
      [
        'match',
        ['get', '基準点種別'],
        '電子基準点',
        'darkorange', // 正常な電子基準点
        '一等三角点',
        'rgba(255, 0, 0, 1.0)', // 正常な一等三角点
        '二等三角点',
        'rgba(0, 128, 0, 1.0)', // 正常な二等三角点
        '三等三角点',
        'rgba(0, 0, 255, 1.0)', // 正常な三等三角点
        '四等三角点',
        'rgba(128, 0, 128, 1.0)', // 正常な四等三角点
        '基準水準点',
        'rgba(255, 165, 0, 1.0)', // 正常な基準水準点
        '準基準水準点',
        'rgba(0, 255, 255, 1.0)', // 正常な準基準水準点
        '一等水準交差点',
        'rgba(75, 0, 130, 1.0)', // 正常な一等水準交差点
        '一等道路水準点',
        'rgba(128, 128, 0, 1.0)', // 正常な一等道路水準点
        '一等水準点',
        'rgba(255, 20, 147, 1.0)', // 正常な一等水準点
        '二等水準点',
        'rgba(0, 191, 255, 1.0)', // 正常な二等水準点
        '二等道路水準点',
        'rgba(46, 139, 87, 1.0)', // 正常な二等道路水準点
        /* デフォルト色（正常な場合） */
        'rgba(0, 0, 0, 1.0)',
      ],
      [
        'match',
        ['get', '基準点種別'],
        '電子基準点',
        'rgba(200, 200, 200, 1.0)', // 異常な電子基準点
        '一等三角点',
        'rgba(128, 0, 0, 1.0)', // 異常な一等三角点
        '二等三角点',
        'rgba(0, 64, 0, 1.0)', // 異常な二等三角点
        '三等三角点',
        'rgba(0, 0, 128, 1.0)', // 異常な三等三角点
        '四等三角点',
        'rgba(64, 0, 64, 1.0)', // 異常な四等三角点
        '基準水準点',
        'rgba(128, 85, 0, 1.0)', // 異常な基準水準点
        '準基準水準点',
        'rgba(0, 128, 128, 1.0)', // 異常な準基準水準点
        '一等水準交差点',
        'rgba(37, 0, 64, 1.0)', // 異常な一等水準交差点
        '一等道路水準点',
        'rgba(64, 64, 0, 1.0)', // 異常な一等道路水準点
        '一等水準点',
        'rgba(128, 10, 73, 1.0)', // 異常な一等水準点
        '二等水準点',
        'rgba(0, 95, 128, 1.0)', // 異常な二等水準点
        '二等道路水準点',
        'rgba(23, 64, 43, 1.0)', // 異常な二等道路水準点
        /* デフォルト色（異常な場合） */
        'rgba(0, 0, 0, 1.0)',
      ],
    ],
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      1,
      [
        'match',
        ['get', '基準点種別'],
        '電子基準点',
        8,
        '一等三角点',
        8,
        '二等三角点',
        1,
        '三等三角点',
        1,
        '四等三角点',
        1,
        1, // デフォルト
      ],
      16,
      [
        'match',
        ['get', '基準点種別'],
        '電子基準点',
        20,
        '一等三角点',
        20,
        '二等三角点',
        16,
        '三等三角点',
        16,
        '四等三角点',
        14,
        8, // デフォルト
      ],
    ],
    'circle-stroke-color': 'rgba(255, 255, 255, 1)',
    'circle-stroke-width': 1,
  },
}
const kizyuntenPointLabel = {
  id: 'oh-kizyunten-point-label',
  type: 'symbol',
  source: 'kizyunten-source',
  'source-layer': 'point',
  filter: [
    'any', // OR条件でフィルタ
    ['>=', ['zoom'], 9], // ズームレベル11以上の場合は全て表示
    [
      'all', // AND条件
      ['<=', ['zoom'], 8], // ズームレベル10以下
      ['match', ['get', '基準点種別'], ['電子基準点', '一等三角点'], true, false], // 電子基準点と一等三角点のみ
    ],
  ],
  layout: {
    'text-field': [
      'match',
      ['get', '基準点種別'], // フィールド名に基づいて条件分岐
      '電子基準点',
      '電',
      '一等三角点',
      '1',
      '二等三角点',
      '2',
      '三等三角点',
      '3',
      '四等三角点',
      '4',
      /* デフォルト値 */
      '',
    ],
    'text-size': 16, // テキストのサイズ
    // "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"], // フォント
    'text-anchor': 'center', // テキストをポイントの中心に配置
    'text-offset': [0, 0], // テキストのオフセット
    'text-allow-overlap': true, //重なっても表示されるように
    'text-ignore-placement': true, //重なっても表示されるように
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 1)', // テキストの色
    // "text-halo-color": "rgba(255, 255, 255, 1)", // テキストの背景（ハロー）色
    // "text-halo-width": 1, // テキストの背景（ハロー）の幅
  },
}
// 基本基準点 - 点名ラベル
// 電子基準点・一等三角点: minzoom制限なし、それ以外: ズーム12以上
const kizyuntenNameLabel = {
  id: 'oh-kizyunten-name-label',
  type: 'symbol',
  source: 'kizyunten-source',
  'source-layer': 'point',
  filter: [
    'any',
    ['all',
      ['match', ['get', '基準点種別'], ['電子基準点', '一等三角点'], true, false],
      ['>=', ['zoom'], 6],
    ],
    ['>=', ['zoom'], 12],
  ],
  layout: {
    'text-field': ['get', '点名'],
    'text-size': 12,
    'text-anchor': 'left',
    'text-offset': [1.5, 0], // 右にオフセット
    'text-allow-overlap': false,
    'text-optional': true,
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1.5,
  },
}
// 磐田 --------------------------------------------------------------------------------------------
const iwatapolygonSource = {
  id: 'iwatapolygon-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/iwata/iwatapolygon.pmtiles',
  },
}
const iwatapolygonLayer = {
  id: 'oh-iwatapolygon',
  type: 'fill',
  source: 'iwatapolygon-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const iwatapolygonLine = {
  id: 'oh-iwatapolygon-line',
  type: 'line',
  source: 'iwatapolygon-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'navy',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      16,
      2,
    ],
  },
}
// 奈良市 --------------------------------------------------------------------------------------------
const narashichibanSource = {
  id: 'narashichiban-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/narashi.pmtiles',
  },
}
const narashichibanLayer = {
  id: 'oh-narashichiban',
  type: 'fill',
  source: 'narashichiban-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const narashichibanLine = {
  id: 'oh-narashichiban-line',
  type: 'line',
  source: 'narashichiban-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'navy',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      16,
      2,
    ],
  },
}
// 福島市 --------------------------------------------------------------------------------------------
const fukushimachibanSource = {
  id: 'fukushimachiban-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/fukushimashi.pmtiles',
  },
}
const fukushimachibanLayer = {
  id: 'oh-fukushimachiban',
  type: 'fill',
  source: 'fukushimachiban-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const fukushimachibanLine = {
  id: 'oh-fukushimachiban-line',
  type: 'line',
  source: 'fukushimachiban-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'navy',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      16,
      2,
    ],
  },
}
// 北広島市 --------------------------------------------------------------------------------------------
const kitahiroshimachibanSource = {
  id: 'kitahiroshimachiban-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/kitahiroshimashi.pmtiles',
  },
}
const kitahiroshimachibanLayer = {
  id: 'oh-kitahiroshimachiban',
  type: 'fill',
  source: 'kitahiroshimachiban-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const kitahiroshimachibanLine = {
  id: 'oh-kitahiroshimachiban-line',
  type: 'line',
  source: 'kitahiroshimachiban-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'navy',
    'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
  },
}
// 国立市 --------------------------------------------------------------------------------------------
const kunitachishichibanSource = {
  id: 'kunitachishichiban-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/kunitachishi.pmtiles',
  },
}
const kunitachishichibanLayer = {
  id: 'oh-kunitachishi',
  type: 'fill',
  source: 'kunitachishichiban-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const kunitachishichibanLine = {
  id: 'oh-kunitachishichiban-line',
  type: 'line',
  source: 'kunitachishichiban-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'navy',
    'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
  },
}
// 福岡市 --------------------------------------------------------------------------------------------
const fukuokashichibanSource = {
  id: 'fukuokashichiban-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/fukuokashi.pmtiles',
  },
}
const fukuokashichibanLayer = {
  id: 'oh-fukuokashichiban',
  type: 'fill',
  source: 'fukuokashichiban-source',
  'source-layer': 'chibanzu',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const fukuokashichibanLine = {
  id: 'oh-fukuokashichiban-line',
  type: 'line',
  source: 'fukuokashichiban-source',
  'source-layer': 'chibanzu',
  paint: {
    'line-color': 'navy',
    'line-width': ['interpolate', ['linear'], ['zoom'], 1, 0.1, 16, 2],
  },
}
// Chibanzu_2024 --------------------------------------------------------------------------------------------
const chibanzu2024Source = {
  id: 'chibanzu2024-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/chiban/Chibanzu_2024_with_id2.pmtiles',
  },
}
const chibanzu2024Layer = {
  id: 'oh-chibanzu2024',
  type: 'fill',
  source: 'chibanzu2024-source',
  'source-layer': 'chibanzu',
  paint: {
    'fill-color': 'rgba(0,0,0,0)',
  },
}
const chibanzu2024Line = {
  id: 'oh-chibanzu2024-line',
  type: 'line',
  source: 'chibanzu2024-source',
  'source-layer': 'chibanzu',
  paint: {
    'line-color': 'navy',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      1,
      0.1,
      16,
      2,
    ],
  },
}

// 福岡県森林計画区域-------------------------------------------------------------------------------------------
const fukuokakenshinrinSource = {
  id: 'fukuokakenshinrin-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shinrin/fukuokaken/fukuokashinrin.pmtiles',
  },
}
const fukuokakenshinrinLayer = {
  id: 'oh-fukuokakenshinrin',
  type: 'fill',
  source: 'fukuokakenshinrin-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['get', 'rinsyu_nam'],
      '人工林',
      'rgba(139, 69, 19, 0.8)', // 茶色（人工林, 透過度0.8）
      '天然林',
      'rgba(34, 139, 34, 0.8)', // 緑色（天然林, 透過度0.8）
      '伐採跡地',
      'rgba(169, 169, 169, 0.8)', // 灰色（伐採跡地, 透過度0.8）
      '竹林',
      'rgba(107, 142, 35, 0.8)', // オリーブ色（竹林, 透過度0.8）
      'rgba(204, 204, 204, 0.8)', // デフォルト色（透過度0.8）
    ],
  },
}
const fukuokakenshinrinLayerLine = {
  id: 'oh-fukuokakenshinrin-line',
  type: 'line',
  source: 'fukuokakenshinrin-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      12,
      0,
      16,
      2,
    ],
  },
}

// 茨城県森林計画図（林班・小班）--------------------------------------------------------------------------------------------
const ibarakiShinrinSource = {
  id: 'ibaraki-shinrin-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shinrin/ibaraki/ibaraki_shinrin_keikakuzu.pmtiles',
  },
}
// 林班レイヤー（fill）
const ibarakiShinrinRinpanLayer = {
  id: 'oh-ibaraki-shinrin-rinpan',
  type: 'fill',
  source: 'ibaraki-shinrin-source',
  'source-layer': 'rinpan',
  paint: {
    'fill-color': 'rgba(34, 139, 34, 0.4)',
    'fill-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.3, 12, 0.2],
  },
  maxzoom: 14,
}
// 林班レイヤー（line）
const ibarakiShinrinRinpanLineLayer = {
  id: 'oh-ibaraki-shinrin-rinpan-line',
  type: 'line',
  source: 'ibaraki-shinrin-source',
  'source-layer': 'rinpan',
  paint: {
    'line-color': '#006400',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2],
  },
}
// 林班ラベル
const ibarakiShinrinRinpanLabelLayer = {
  id: 'oh-ibaraki-shinrin-rinpan-label',
  type: 'symbol',
  source: 'ibaraki-shinrin-source',
  'source-layer': 'rinpan',
  minzoom: 11,
  layout: {
    'text-field': ['get', 'RINPAN'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 11, 10, 14, 14],
  },
  paint: {
    'text-color': '#004d00',
    'text-halo-color': '#fff',
    'text-halo-width': 1.5,
  },
}
// 小班レイヤー（fill） - 林種で色分け
const ibarakiShinrinShohanLayer = {
  id: 'oh-ibaraki-shinrin-shohan',
  type: 'fill',
  source: 'ibaraki-shinrin-source',
  'source-layer': 'shohan',
  minzoom: 12,
  paint: {
    'fill-color': [
      'match',
      ['get', '林種'],
      '人工林', 'rgba(139, 69, 19, 0.6)',
      '天然林', 'rgba(34, 139, 34, 0.6)',
      '竹林', 'rgba(107, 142, 35, 0.6)',
      '無立木地', 'rgba(169, 169, 169, 0.6)',
      'rgba(200, 200, 200, 0.4)',
    ],
  },
}
// 小班レイヤー（line）
const ibarakiShinrinShohanLineLayer = {
  id: 'oh-ibaraki-shinrin-shohan-line',
  type: 'line',
  source: 'ibaraki-shinrin-source',
  'source-layer': 'shohan',
  minzoom: 12,
  paint: {
    'line-color': '#333',
    'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.3, 16, 1],
  },
}

// 街区基準点--------------------------------------------------------------------------------------------
const gaikuSource = {
  id: 'gaiku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gaiku/gaiku2.pmtiles',
  },
}
const gaikuLayer = {
  id: 'oh-gaiku-layer',
  type: 'circle',
  source: 'gaiku-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  paint: {
    'circle-color': [
      'match',
      ['get', 'type'],
      'S',
      'rgba(255, 100, 100, 1)', // Sは赤っぽく
      'T',
      'rgba(100, 100, 255, 1)', // Tは青っぽく
      'TS',
      'rgba(150, 50, 200, 1)', // TSは紫っぽく
      'SS',
      'rgba(200, 200, 0, 1)', // SSは黄色っぽく
      'H',
      'rgba(100, 200, 100, 1)', // Hは緑っぽく
      'red',
    ],
    'circle-opacity': 1, // すべて不透明
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 12, 10, 16, 18],
  },
}

const gaikuLabelLayer = {
  id: 'oh-gaiku-label-layer',
  type: 'symbol',
  source: 'gaiku-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  layout: {
    'text-field': [
      'match',
      ['get', 'type'],
      'S',
      '三',
      'T',
      '多',
      'TS',
      '多節',
      'SS',
      '三節',
      'H',
      '補',
      '', // デフォルトは空文字
    ],
    'text-size': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 14],
    'text-anchor': 'center', // テキストを中央配置
    'text-allow-overlap': true, // サークルとテキストの重なりを許可
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.5,
  },
}

const gaikuLayerLabel = {
  id: 'oh-gaiku-label',
  type: 'symbol',
  source: 'gaiku-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  layout: {
    'text-field': [
      'coalesce',
      ['get', '基準点等名称'], // 優先的に使用
      ['get', '街区点・補助点名称'], // 基準点等名称がない場合はこちらを使用
    ],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.7],
    'text-allow-overlap': true, // サークルとテキストの重なりを許可
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  // 'maxzoom': 25,
  minzoom: 15,
}
// 都市部官民基準点--------------------------------------------------------------------------------------------
const toshikanSource = {
  id: 'toshikan-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/toshikan/toshikan.pmtiles',
  },
}
const toshikanLayer = {
  id: 'oh-toshikan-layer',
  type: 'circle',
  source: 'toshikan-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  paint: {
    'circle-color': [
      'match',
      ['get', 'type'],
      'TKS',
      'rgba(200, 100, 100, 1)', // Sは赤っぽく
      'TKT',
      'green', // Tは青っぽく
      'red',
    ],
    'circle-opacity': 1, // すべて不透明
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 0, 12, 10, 16, 18],
  },
}

const toshikanLabelLayer = {
  id: 'oh-toshikan-label-layer',
  type: 'symbol',
  source: 'toshikan-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  layout: {
    'text-field': [
      'match',
      ['get', 'type'],
      'TKS',
      '都三',
      'TKT',
      '都多',
      '', // デフォルトは空文字
    ],
    'text-size': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 14],
    'text-anchor': 'center', // テキストを中央配置
    'text-allow-overlap': true, // サークルとテキストの重なりを許可
  },
  paint: {
    'text-color': 'black',
    'text-halo-color': 'white',
    'text-halo-width': 1.5,
  },
}

const toshikanLayerLabel = {
  id: 'oh-toshikan-label',
  type: 'symbol',
  source: 'toshikan-source',
  'source-layer': 'point',
  filter: ['!=', ['get', '廃点情報'], '廃点'], // 廃点情報が "廃点" の場合は非表示
  layout: {
    'text-field': [
      'coalesce',
      ['get', '基準点等名称'], // 優先的に使用
      ['get', '街区点・補助点名称'], // 基準点等名称がない場合はこちらを使用
    ],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.7],
  },
  paint: {
    'text-color': 'rgba(0, 0, 0, 1)',
    'text-halo-color': 'rgba(255,255,255,1)',
    'text-halo-width': 1.0,
  },
  // 'maxzoom': 24,
  minzoom: 15,
}

// VIRTUAL SHIZUOKA 静岡県 中・西部オルソ---------------------------------------------------------------------------------------------------------
const shizuokaOrthoSource = {
  id: 'shizuoka-ortho-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/tile/virtual-shizuoka/tyuseibu/{z}/{x}/{y}.png'],
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 19, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([137.36343383789062, 35.380092992092145, 138.702392578125, 34.51447797766275]),
    crossOrigin: 'anonymous',
  },
}
const shizuokaOrthoLayer = {
  id: 'oh-shizuoka-ortho-layer',
  type: 'raster',
  source: 'shizuoka-ortho-source',
}
// VIRTUAL SHIZUOKA 静岡県 伊豆西部オルソ-------------------------------------------------------------------------------------------------------
const shizuokaOrthoIzuseibuSource = {
  id: 'shizuoka-ortho-izuseibu-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/tile/virtual-shizuoka/izuseibu/{z}/{x}/{y}.png'],
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 19, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([138.6951786465345, 35.05418582519269, 139.0030710863183, 34.556764466321724]),
  },
}
const shizuokaOrthoIzuseibuLayer = {
  id: 'oh-shizuoka-ortho-izuseibu-layer',
  type: 'raster',
  source: 'shizuoka-ortho-izuseibu-source',
}
// VIRTUAL SHIZUOKA 静岡県 東部オルソ-------------------------------------------------------------------------------------------------------
const shizuokaOrthoTobuSource = {
  id: 'shizuoka-ortho-tobu-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/tile/virtual-shizuoka/tobu/{z}/{x}/{y}.png'],
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 19, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([138.658447265625, 35.43717666390691, 139.20639038085938, 34.61738705240718]),
  },
}
const shizuokaOrthoTobuLayer = {
  id: 'oh-shizuoka-ortho-tobu-layer',
  type: 'raster',
  source: 'shizuoka-ortho-tobu-source',
}
// VIRTUAL SHIZUOKA 静岡県 富士オルソ-------------------------------------------------------------------------------------------------------
const shizuokaOrthoFujiSource = {
  id: 'shizuoka-ortho-fuji-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/tile/virtual-shizuoka/fuji/{z}/{x}/{y}.png'],
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 19, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([138.47923278808594, 35.455636252952445, 138.9269256591797, 35.10418108827649]),
  },
}
const shizuokaOrthoFujiLayer = {
  id: 'oh-shizuoka-ortho-fuji-layer',
  type: 'raster',
  source: 'shizuoka-ortho-fuji-source',
}
// VIRTUAL SHIZUOKA 静岡県 北部 南アルプス-------------------------------------------------------------------------------------------------------
const shizuokaOrthoAlpusSource = {
  id: 'shizuoka-ortho-alpus-source',
  obj: {
    type: 'raster',
    tiles: ['https://kenzkenz4.xsrv.jp/tile/virtual-shizuoka/alpus/{z}/{x}/{y}.png'],
    tileSize: 256, // タイルサイズに合わせる
    maxzoom: 19, // 適切なズーム範囲を設定
    minzoom: 1,
    bounds: boundsSort([138.0037227785058, 35.62288537726742, 138.3478132990051, 35.077789209561246]),
  },
}
const shizuokaOrthoAlpusLayer = {
  id: 'oh-shizuoka-ortho-alpus-layer',
  type: 'raster',
  source: 'shizuoka-ortho-alpus-source',
  // minzoom: 10,
}
// 福岡県土砂災害-------------------------------------------------------------------------------------------
const fukuokakenHazardSource = {
  id: 'fukuokakenhazard-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/fukuokakenhazard/fukuokakenhazard.pmtiles',
  },
}
const fukuokakenHazardLayer = {
  id: 'oh-fukuokakenhazard',
  type: 'fill',
  source: 'fukuokakenhazard-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': [
      'match',
      ['concat', ['get', 'genshoname'], ['get', 'kubun']],
      '土石流特別警戒区域',
      'rgba(255, 0, 0, 0.8)', // 赤（特別警戒区域, 透過度0.8）
      '土石流警戒区域',
      'rgba(255, 255, 0, 0.8)', // 黄色（警戒区域, 透過度0.8）
      '急傾斜地の崩壊特別警戒区域',
      'rgba(255, 100, 0, 0.8)', // 赤（特別警戒区域, 透過度0.8）
      '急傾斜地の崩壊警戒区域',
      'rgba(255, 200, 100, 0.8)', // 黄色（警戒区域, 透過度0.8）
      '地滑り特別警戒区域',
      'rgba(255, 0, 200, 0.8)', // 赤（特別警戒区域, 透過度0.8）
      '地滑り警戒区域',
      'rgba(255, 200, 200, 0.8)', // 黄色（警戒区域, 透過度0.8）
      'rgba(0, 0, 0, 0.8)', // その他（黒, 透過度0.8）
    ],
  },
}
const fukuokakenHazardLayerLine = {
  id: 'oh-fukuokakenhazard-line',
  type: 'line',
  source: 'fukuokakenhazard-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': '#000',
    'line-width': [
      'interpolate', // Zoom-based interpolation
      ['linear'],
      ['zoom'], // Use the zoom level as the input
      12,
      0,
      16,
      2,
    ],
  },
}
// 佐賀県基準点 -----------------------------------------------------------------------------------------------------------------
const sagakijyuntenSource = {
  id: 'saga-kijyunten-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/sagakijyunten.geojson',
  },
}
const sagakijyuntenPointLayer = {
  id: 'oh-saga-kijyunten-point',
  type: 'circle',
  source: 'saga-kijyunten-source',
  paint: {
    'circle-color': 'navy', // 赤色で中心点を強調
    'circle-radius': 8, // 固定サイズの点
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}
const sagakijyuntenLabelLayer = {
  id: 'oh-saga-kijyunten-label',
  type: 'symbol',
  source: 'saga-kijyunten-source',
  layout: {
    'text-field': ['get', '点名'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 1)',
    'text-halo-color': 'rgba(0,0,0,1)',
    'text-halo-width': 1.0,
  },
  maxzoom: 24,
  minzoom: 9,
}
// 国有林
const kokuyurinSource = {
  id: 'kokuyurin-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/kokuyurin/kokuyurin.pmtiles',
  },
}
const kokuyurinLayer = {
  id: 'oh-kokuyurin-polygon',
  type: 'fill',
  source: 'kokuyurin-source',
  'source-layer': 'polygon',
  paint: {
    'fill-color': 'rgba(0, 128, 0, 0.6)',
  },
}
const kokuyurinLayerLine = {
  id: 'oh-kokuyurin-line',
  type: 'line',
  source: 'kokuyurin-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'black',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 20, 4],
  },
}
const kokuyurinLayerLabel = {
  id: 'oh-kokuyurin-label',
  type: 'symbol',
  source: 'kokuyurin-source',
  'source-layer': 'polygon',
  layout: {
    'text-field': ['get', 'A45_011'],
  },
  paint: {
    'text-color': 'rgba(255, 255, 255, 1)',
    // 'text-halo-color': 'rgba(255,255,255,1)',
    // 'text-halo-width': 1.0,
  },
  minzoom: 15,
}
// 森林地域（国土数値情報 A13-2015）--------------------------------------------------------------------------------------------
const shinrinSource = {
  id: 'shinrin-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/shinrin/shinrin.pmtiles',
  },
}
const shinrinLayer = {
  id: 'oh-shinrin-polygon',
  type: 'fill',
  source: 'shinrin-source',
  'source-layer': 'shinrin',
  paint: {
    'fill-color': [
      'match',
      ['get', 'LAYER_NO'],
      7, 'rgba(34, 139, 34, 0.5)',   // 森林地域 - 濃い緑
      8, 'rgba(0, 100, 0, 0.5)',     // 国有林 - 深緑
      9, 'rgba(107, 142, 35, 0.5)', // 地域森林計画対象民有林 - オリーブ
      10, 'rgba(46, 139, 87, 0.5)', // 保安林 - シーグリーン
      'rgba(60, 179, 113, 0.5)',    // デフォルト
    ],
  },
}
const shinrinLayerLine = {
  id: 'oh-shinrin-line',
  type: 'line',
  source: 'shinrin-source',
  'source-layer': 'shinrin',
  paint: {
    'line-color': 'rgba(0, 80, 0, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}
// 森林計画対象森林レイヤ（林野庁）--------------------------------------------------------------------------------------------
// ベクトルタイル（PMTiles, z0-14）
const shinrinkeikakuVectorSource = {
  id: 'shinrinkeikaku-vector-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/shinrinkeikaku/shinrinkeikaku.pmtiles',
    minzoom: 0,
    maxzoom: 14,
  },
}
const shinrinkeikakuKokuyurinLayer = {
  id: 'oh-shinrinkeikaku-kokuyurin',
  type: 'fill',
  source: 'shinrinkeikaku-vector-source',
  'source-layer': 'shinrinkeikaku',
  minzoom: 5,
  paint: {
    'fill-opacity': 0.7,
    'fill-color': '#004d00',
    'fill-outline-color': '#000000',
  },
  filter: ['==', '民国別', '国有林'],
}
const shinrinkeikakuMinyurinLayer = {
  id: 'oh-shinrinkeikaku-minyurin',
  type: 'fill',
  source: 'shinrinkeikaku-vector-source',
  'source-layer': 'shinrinkeikaku',
  minzoom: 5,
  paint: {
    'fill-opacity': 0.7,
    'fill-color': '#00e600',
    'fill-outline-color': '#232323',
  },
  filter: ['==', '民国別', '民有林'],
}
const shinrinkeikakuLabelLayer = {
  id: 'oh-shinrinkeikaku-label',
  type: 'symbol',
  source: 'shinrinkeikaku-vector-source',
  'source-layer': 'shinrinkeikaku',
  minzoom: 14,
  layout: {
    'text-field': ['get', '林班'],
    'text-size': 14,
    'text-anchor': 'center',
    'text-allow-overlap': false,
  },
  paint: {
    'text-color': '#000000',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1,
  },
}
// 都道府県別 森林計画図（県別PMTiles）--------------------------------------------------------------------------------------------
// 各県のオープンデータをPMTiles化したもの
function createPrefForestLayers(key, layerName, labelField) {
  return {
    source: {
      id: key + '-forest-vector-source',
      obj: {
        type: 'vector',
        url: 'pmtiles://https://kenzkenz.net/pmtiles/shinrin/' + key + '/' + key + '-forest.pmtiles',
        minzoom: 5,
        maxzoom: 14,
      },
    },
    fill: {
      id: 'oh-' + key + '-forest-fill',
      type: 'fill',
      source: key + '-forest-vector-source',
      'source-layer': layerName,
      minzoom: 8,
      paint: {
        'fill-opacity': 0.5,
        'fill-color': '#228B22',
        'fill-outline-color': '#155215',
      },
    },
    line: {
      id: 'oh-' + key + '-forest-line',
      type: 'line',
      source: key + '-forest-vector-source',
      'source-layer': layerName,
      minzoom: 12,
      paint: {
        'line-color': '#0a3d0a',
        'line-width': ['interpolate', ['linear'], ['zoom'], 12, 0.3, 14, 1],
        'line-opacity': 0.8,
      },
    },
    label: {
      id: 'oh-' + key + '-forest-label',
      type: 'symbol',
      source: key + '-forest-vector-source',
      'source-layer': layerName,
      minzoom: 13,
      layout: {
        'text-field': labelField,
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 15, 14],
        'text-anchor': 'center',
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#1a4d1a',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    },
  }
}
const prefForest = {
  hokkaido: createPrefForestLayers('hokkaido', 'hokkaido_forest', ['concat', ['get', '林班'], '-', ['get', '小班']]),
  iwate: createPrefForestLayers('iwate', 'iwate_forest', ['concat', ['get', '林班'], '-', ['get', '小班']]),
  niigata: createPrefForestLayers('niigata', 'niigata_forest', ['concat', ['to-string', ['get', '林班']], '-', ['to-string', ['get', '小班']]]),
  gunma: createPrefForestLayers('gunma', 'gunma_forest', ['get', 'NAME']),
  tochigi: createPrefForestLayers('tochigi', 'tochigi_forest', ['get', '小班']),
  nagano: createPrefForestLayers('nagano', 'nagano_forest', ['concat', ['get', 'RIN'], '-', ['get', 'SHO']]),
  shizuoka: createPrefForestLayers('shizuoka', 'shizuoka_forest', ['concat', ['to-string', ['get', '林班']], '-', ['to-string', ['get', '小班_親番']]]),
  wakayama: createPrefForestLayers('wakayama', 'wakayama_forest', ['concat', ['get', 'frh_rinpan'], '-', ['get', 'frh_syoha0'], ['get', 'frh_syoha1']]),
  ehime: createPrefForestLayers('ehime', 'ehime_forest', ['get', '林班番号']),
  yamaguchi: createPrefForestLayers('yamaguchi', 'yamaguchi_forest', ['concat', ['to-string', ['get', '林班']], '-', ['to-string', ['get', '小班']]]),
  kumamoto: createPrefForestLayers('kumamoto', 'kumamoto_forest', ['get', '表示用小班']),
  nagasaki: createPrefForestLayers('nagasaki', 'nagasaki_forest', ['concat', ['to-string', ['get', '林班']], '-', ['to-string', ['get', '小班']]]),
}
// 全国森林資源メッシュ（林野庁 20mメッシュ）--------------------------------------------------------------------------------------------
// 県別PMTiles: 栃木・兵庫・高知・鳥取
const frMesh20mPrefectures = ['tochigi', 'hyogo', 'kochi', 'tottori']
const frMesh20mDefaultFillColor = [
  'match',
  ['get', '解析樹種'],
  'スギ', 'rgba(255,75,0,0.8)',
  'ヒノキ類', 'rgba(77,196,255,0.8)',
  'マツ類', 'rgba(137,250,194,0.8)',
  'カラマツ', 'rgba(0,90,255,0.8)',
  'トドマツ', 'rgba(255,153,51,0.8)',
  'エゾマツ', 'rgba(137,250,194,0.8)',
  'ヒバ', 'rgba(255,255,0,0.8)',
  'その他針葉樹', 'rgba(0,0,0,0.6)',
  '広葉樹', 'rgba(3,175,122,0.8)',
  'タケ', 'rgba(255,202,191,0.8)',
  '針広混交林', 'rgba(191,191,191,0.7)',
  '新植地', 'rgba(191,191,191,0.5)',
  '伐採跡地', 'rgba(191,191,191,0.5)',
  'rgba(191,191,191,0.5)',
]
const frMesh20mSources = frMesh20mPrefectures.map(pref => ({
  id: `fr-mesh20m-${pref}-source`,
  obj: {
    type: 'vector',
    url: `pmtiles://https://kenzkenz.jp/pmtiles/fr_mesh20m/fr_mesh20m_${pref}.pmtiles`,
    minzoom: 0,
    maxzoom: 16,
  },
}))
const frMesh20mFillLayers = frMesh20mPrefectures.map(pref => ({
  id: `oh-fr-mesh20m-${pref}-fill`,
  type: 'fill',
  source: `fr-mesh20m-${pref}-source`,
  'source-layer': 'fr_mesh20m',
  minzoom: 12,
  paint: {
    'fill-opacity': 0.75,
    'fill-color': frMesh20mDefaultFillColor,
    'fill-outline-color': 'rgba(0,0,0,0)',
  },
}))
const frMesh20mLineLayers = frMesh20mPrefectures.map(pref => ({
  id: `oh-fr-mesh20m-${pref}-line`,
  type: 'line',
  source: `fr-mesh20m-${pref}-source`,
  'source-layer': 'fr_mesh20m',
  minzoom: 14,
  paint: {
    'line-color': 'rgba(0,0,0,0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.3, 16, 0.8],
  },
}))
const frMesh20mRasterSources = frMesh20mPrefectures.map(pref => ({
  id: `fr-mesh20m-${pref}-raster-source`,
  obj: {
    type: 'raster',
    url: `pmtiles://https://kenzkenz.jp/pmtiles/fr_mesh20m/fr_mesh20m_${pref}_raster.pmtiles`,
    tileSize: 256,
  },
}))
const frMesh20mRasterLayers = frMesh20mPrefectures.map(pref => ({
  id: `oh-fr-mesh20m-${pref}-raster`,
  type: 'raster',
  source: `fr-mesh20m-${pref}-raster-source`,
  maxzoom: 12,
  paint: {
    'raster-opacity': 0.75,
  },
}))
// 全国森林資源メッシュ（林野庁 2025 ベクトルタイル）
// 全国版はフィールド名が県別PMTilesと異なる（森林簿樹種1 vs 解析樹種）
const frMeshNationalDefaultFillColor = [
  'match',
  ['get', '森林簿樹種1'],
  'スギ', 'rgba(255,75,0,0.8)',
  'ヒノキ類', 'rgba(77,196,255,0.8)',
  'マツ類', 'rgba(137,250,194,0.8)',
  'カラマツ', 'rgba(0,90,255,0.8)',
  'トドマツ', 'rgba(255,153,51,0.8)',
  'エゾマツ', 'rgba(137,250,194,0.8)',
  'ヒバ', 'rgba(255,255,0,0.8)',
  'その他針葉樹', 'rgba(0,0,0,0.6)',
  '広葉樹', 'rgba(3,175,122,0.8)',
  'タケ', 'rgba(255,202,191,0.8)',
  '針広混交林', 'rgba(191,191,191,0.7)',
  '新植地', 'rgba(191,191,191,0.5)',
  '伐採跡地', 'rgba(191,191,191,0.5)',
  'rgba(191,191,191,0.5)',
]
const frMeshNationalSource = {
  id: 'fr-mesh-national-source',
  obj: {
    type: 'vector',
    tiles: ['https://rinya-tiles.geospatial.jp/fr_mesh20m_pbf_2025/{z}/{x}/{y}.pbf'],
    minzoom: 13,
    maxzoom: 16,
  },
}
const frMeshNationalRasterSource = {
  id: 'fr-mesh-national-raster-source',
  obj: {
    type: 'raster',
    tiles: ['https://rinya-tiles.geospatial.jp/fr_mesh20m_webp_2025/{z}/{x}/{y}.webp'],
    tileSize: 256,
    maxzoom: 16,
  },
}
const frMeshNationalRasterLayer = {
  id: 'oh-fr-mesh-national-raster',
  type: 'raster',
  source: 'fr-mesh-national-raster-source',
  maxzoom: 13,
  paint: { 'raster-opacity': 0.75 },
}
const frMeshNationalFillLayer = {
  id: 'oh-fr-mesh-national-fill',
  type: 'fill',
  source: 'fr-mesh-national-source',
  'source-layer': '全国森林資源メッシュ',
  minzoom: 13,
  paint: {
    'fill-opacity': 0.75,
    'fill-color': frMeshNationalDefaultFillColor,
    'fill-outline-color': 'rgba(0,0,0,0)',
  },
}
const frMeshNationalLineLayer = {
  id: 'oh-fr-mesh-national-line',
  type: 'line',
  source: 'fr-mesh-national-source',
  'source-layer': '全国森林資源メッシュ',
  minzoom: 14,
  paint: {
    'line-color': 'rgba(0,0,0,0.3)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 14, 0.3, 16, 0.8],
  },
}
// 低位地帯（国土数値情報 G08）--------------------------------------------------------------------------------------------
const teiichiSource = {
  id: 'teiichi-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/teiichi/teiichi.pmtiles',
  },
}
const teiichiLayerPolygon = {
  id: 'oh-teiichi-polygon',
  type: 'fill',
  source: 'teiichi-source',
  'source-layer': 'teiichi',
  paint: {
    'fill-color': [
      'interpolate',
      ['linear'],
      ['get', 'G08_002'],  // 最大浸水深
      0, 'rgba(255, 255, 200, 0.5)',   // 0m - 薄黄
      2, 'rgba(255, 230, 150, 0.6)',   // 2m - 黄
      5, 'rgba(255, 180, 100, 0.7)',   // 5m - オレンジ
      10, 'rgba(255, 100, 100, 0.8)',  // 10m - 赤
      20, 'rgba(180, 50, 150, 0.8)',   // 20m - 紫
    ],
  },
}
const teiichiLayerLine = {
  id: 'oh-teiichi-line',
  type: 'line',
  source: 'teiichi-source',
  'source-layer': 'teiichi',
  paint: {
    'line-color': 'rgba(139, 90, 43, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 16, 1.5],
  },
}
// 多段階浸水想定（国土数値情報 A53）--------------------------------------------------------------------------------------------
const tadankaiSource = {
  id: 'tadankai-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/tadankai/tadankai.pmtiles',
  },
}
const tadankaiLayerPolygon = {
  id: 'oh-tadankai-polygon',
  type: 'fill',
  source: 'tadankai-source',
  'source-layer': 'tadankai',
  filter: ['==', ['get', 'A53_005'], 100],  // デフォルト：100年に1回
  paint: {
    'fill-color': [
      'match',
      ['get', 'A53_003'],  // 浸水深ランク（青系：デフォルト）
      1, 'rgba(173, 216, 230, 0.6)',   // 0.5m未満
      2, 'rgba(100, 180, 220, 0.6)',   // 0.5m〜3m未満
      3, 'rgba(65, 145, 200, 0.6)',    // 3m〜5m未満
      4, 'rgba(30, 100, 180, 0.6)',    // 5m〜10m未満
      5, 'rgba(0, 60, 140, 0.6)',      // 10m〜20m未満
      6, 'rgba(0, 30, 100, 0.6)',      // 20m以上
      'rgba(200, 200, 200, 0.5)',      // デフォルト
    ],
  },
}
const tadankaiLayerLine = {
  id: 'oh-tadankai-line',
  type: 'line',
  source: 'tadankai-source',
  'source-layer': 'tadankai',
  filter: ['==', ['get', 'A53_005'], 100],  // デフォルト：100年に1回
  paint: {
    'line-color': 'rgba(100, 100, 200, 0.4)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.2, 16, 1],
  },
}
// 行政界履歴（国土数値情報 + hanishina.net マージ版 1920〜2025）--------------------------------------------------------------------------------------------
const gyoseiHistorySource = {
  id: 'gyosei-history-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/gyosei-history/gyosei-history-merged.pmtiles?v=20250111d',
  },
}
const gyoseiHistoryLayerPolygon = {
  id: 'oh-gyosei-history-polygon',
  type: 'fill',
  source: 'gyosei-history-source',
  'source-layer': 'polygon',
  minzoom: 6,
  paint: {
    'fill-color': [
      'match',
      ['get', 'TYPE'],
      '市', 'rgba(255, 180, 180, 0.5)',
      '区', 'rgba(255, 200, 150, 0.5)',
      '町', 'rgba(180, 220, 180, 0.5)',
      '村', 'rgba(180, 200, 255, 0.5)',
      'rgba(200, 200, 200, 0.5)',
    ],
  },
}
const gyoseiHistoryLayerLine = {
  id: 'oh-gyosei-history-line',
  type: 'line',
  source: 'gyosei-history-source',
  'source-layer': 'polygon',
  minzoom: 6,
  paint: {
    'line-color': 'rgba(100, 100, 150, 0.6)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.3, 14, 1],
  },
}
// ラベルレイヤー
const gyoseiHistoryLayerLabel = {
  id: 'oh-gyosei-history-label',
  type: 'symbol',
  source: 'gyosei-history-source',
  'source-layer': 'polygon',
  minzoom: 7,
  layout: {
    'text-field': ['get', 'NAME'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 7, 10, 14, 16],
    'text-allow-overlap': false,
    'text-optional': true,
  },
  paint: {
    'text-color': [
      'match',
      ['get', 'TYPE'],
      '市', '#C0392B',
      '区', '#D35400',
      '町', '#1E8449',
      '村', '#2471A3',
      '#555555',
    ],
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1.5,
  },
}
// クエリ用レイヤー（フィルターなし・透明）- 履歴表示用
const gyoseiHistoryLayerQuery = {
  id: 'oh-gyosei-history-query',
  type: 'fill',
  source: 'gyosei-history-source',
  'source-layer': 'polygon',
  minzoom: 6,
  paint: {
    'fill-color': 'rgba(0, 0, 0, 0)',  // 完全に透明
  },
}
// 人口ピラミッド（市区町村境界 - はにしな版2025年）--------------------------------------------------------------------------------------------
const populationPyramidSource = {
  id: 'population-pyramid-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.jp/geojson/municipality-2025.geojson',
  },
}
const populationPyramidPolygonLayer = {
  id: 'oh-population-pyramid-polygon',
  type: 'fill',
  source: 'population-pyramid-source',
  minzoom: 6,
  paint: {
    'fill-color': [
      'match',
      ['get', 'TYPE'],
      '市', 'rgba(100, 150, 255, 0.15)',
      '特別区', 'rgba(255, 150, 100, 0.15)',
      '区', 'rgba(255, 180, 100, 0.15)',
      '町', 'rgba(100, 200, 100, 0.15)',
      '村', 'rgba(150, 100, 200, 0.15)',
      'rgba(150, 150, 150, 0.15)',
    ],
  },
}
const populationPyramidLineLayer = {
  id: 'oh-population-pyramid-line',
  type: 'line',
  source: 'population-pyramid-source',
  minzoom: 6,
  paint: {
    'line-color': 'rgba(80, 80, 150, 0.5)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.3, 12, 1.5],
  },
}
// ハイライト用レイヤー（選択された市区町村）
const populationPyramidHighlightLayer = {
  id: 'oh-population-pyramid-highlight',
  type: 'fill',
  source: 'population-pyramid-source',
  minzoom: 6,
  filter: ['==', ['get', 'CODE5'], '__NONE__'], // 初期値は何もマッチしない
  paint: {
    'fill-color': 'rgba(255, 165, 0, 0.5)',
    'fill-outline-color': 'rgba(255, 140, 0, 1)',
  },
}
const populationPyramidLabelLayer = {
  id: 'oh-population-pyramid-label',
  type: 'symbol',
  source: 'population-pyramid-source',
  minzoom: 8,
  layout: {
    'text-field': ['get', 'NAME'],
    'text-font': ['NotoSansJP-Regular'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 10, 14, 14],
    'text-allow-overlap': false,
    'text-optional': true,
  },
  paint: {
    'text-color': '#444',
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1.5,
  },
}
// 鳥獣保護区（国土数値情報 A15）--------------------------------------------------------------------------------------------
const chojuSource = {
  id: 'choju-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz.net/pmtiles/choju/choju.pmtiles',
  },
}
const chojuLayer = {
  id: 'oh-choju-polygon',
  type: 'fill',
  source: 'choju-source',
  'source-layer': 'choju',
  paint: {
    'fill-color': [
      'match',
      ['get', 'A15_004'],
      '1', 'rgba(255, 165, 0, 0.5)',   // 鳥獣保護区 - オレンジ
      '2', 'rgba(255, 69, 0, 0.5)',    // 特別保護地区 - 赤オレンジ
      '3', 'rgba(255, 215, 0, 0.5)',   // 休猟区 - ゴールド
      '4', 'rgba(255, 140, 0, 0.5)',   // 特例休猟区 - ダークオレンジ
      'rgba(255, 200, 100, 0.5)',      // デフォルト
    ],
  },
}
const chojuLayerLine = {
  id: 'oh-choju-line',
  type: 'line',
  source: 'choju-source',
  'source-layer': 'choju',
  paint: {
    'line-color': 'rgba(200, 100, 0, 0.8)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 2],
  },
}
// 洪水浸水想定区域（国土数値情報 A31a）--------------------------------------------------------------------------------------------
const kozuiSource = {
  id: 'kozui-source',
  obj: {
    type: 'vector',
      url: 'pmtiles://https://kenzkenz.net/pmtiles/kozui/kozui_5percent_pkpf.pmtiles',
    // url: 'pmtiles://https://kenzkenz.net/pmtiles/kozui/kozui_simplified_full.pmtiles',
      // url: 'pmtiles://https://kenzkenz.net/pmtiles/kozui/kozui_dissolved.pmtiles',
  },
}
const kozuiLayer = {
  id: 'oh-kozui-polygon',
  type: 'fill',
  source: 'kozui-source',
  'source-layer': 'kozui',
  paint: {
    'fill-color': [
      'match',
      ['get', 'A31a_205'],
      1, 'rgba(247, 245, 169, 0.7)',   // 0.5m未満
      2, 'rgba(255, 216, 192, 0.7)',   // 0.5-3m
      3, 'rgba(255, 183, 183, 0.7)',   // 3-5m
      4, 'rgba(255, 145, 145, 0.7)',   // 5-10m
      5, 'rgba(242, 133, 201, 0.7)',   // 10-20m
      6, 'rgba(220, 122, 220, 0.7)',   // 20m以上
      'rgba(200, 200, 200, 0.5)',      // デフォルト
    ],
  },
}
const kozuiLayerLine = {
  id: 'oh-kozui-line',
  type: 'line',
  source: 'kozui-source',
  'source-layer': 'kozui',
  paint: {
    'line-color': 'rgba(100, 100, 200, 0.5)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 14, 0.5],
  },
}
// 洪水浸水想定区域3D（国土数値情報 A31a）
const kozui3dLayer = {
  id: 'oh-kozui-3d',
  type: 'fill-extrusion',
  source: 'kozui-source',
  'source-layer': 'kozui',
  paint: {
    'fill-extrusion-color': [
      'match',
      ['get', 'A31a_205'],
      1, 'rgb(247, 245, 169)',   // 0.5m未満
      2, 'rgb(255, 216, 192)',   // 0.5-3m
      3, 'rgb(255, 183, 183)',   // 3-5m
      4, 'rgb(255, 145, 145)',   // 5-10m
      5, 'rgb(242, 133, 201)',   // 10-20m
      6, 'rgb(220, 122, 220)',   // 20m以上
      'rgb(180, 180, 180)',      // デフォルト
    ],
    'fill-extrusion-height': [
      'match',
      ['get', 'A31a_205'],
      1, 5,     // 0.5m未満 → 5m
      2, 30,    // 0.5-3m → 30m
      3, 50,    // 3-5m → 50m
      4, 100,   // 5-10m → 100m
      5, 200,   // 10-20m → 200m
      6, 300,   // 20m以上 → 300m
      10,       // デフォルト
    ],
    'fill-extrusion-base': 0,
    'fill-extrusion-vertical-gradient': false,
    'fill-extrusion-opacity': 0.7,
  },
}
// // 地形分類テスト --------------------------------------------------------------------------------------------
// const chikeibunruiSource2 = {
//     id: "chikeibunrui-source", obj: {
//         type: "vector",
//         url: "pmtiles://https://kenzkenz.net/pmtiles/chikeibunrui/chikeibunrui.pmtiles",
//     }
// }
// const chikeibunruiLayer2 = {
//     id: "oh-chikeibunrui",
//     type: "fill",
//     source: "chikeibunrui-source",
//     "source-layer": "polygon",
//     'paint': {
//         'fill-color': 'rgba(255, 0, 0, 0.8)',
//     }
// }
// const chikeibunruiLine2 = {
//     id: "oh-chikeibunrui-line",
//     type: "line",
//     source: "chikeibunrui-source",
//     "source-layer": "polygon",
//     paint: {
//         'line-color': '#000',
//         'line-width': [
//             'interpolate', // Zoom-based interpolation
//             ['linear'],
//             ['zoom'], // Use the zoom level as the input
//             11, 0,
//             12, 0.5
//         ]
//     },
// }

// const aaa = new CustomMultiplyLayer({
//     id: 'oh-custom-multiply',
//     label: "乗算合成テスト",
//     source: 'multiply-source',
//     baseSource: 'dosya-source',
//     opacity: 0.8
// })
//

// // グループでクリックで追加するポイントのGeoJSONソースを作成
// const groupPointsSource ={
//     iD: 'group-points-source', obj: {
//         type: 'geojson',
//         data: {
//             type: 'FeatureCollection',
//             features: [
//             ]
//         }
//     }
// };
//
// // グループでポイントを描画するレイヤーを追加
// const groupPointsLayer = {
//     id: 'oh-group-points-layer',
//     type: 'circle',
//     source: 'group-points-source',
//     paint: {
//         'circle-radius': 8,
//         'circle-color': '#ff0000',
//         'circle-stroke-width': 2,
//         'circle-stroke-color': '#ffffff'
//     }
// }

export const groupGeojson = ref({
  type: 'FeatureCollection',
  features: [],
})

export const groupPointsSource = {
  id: 'group-points-source',
  obj: {
    type: 'geojson',
    data: groupGeojson.value,
  },
}
export const groupPointsLayer = {
  id: 'oh-group-points-layer',
  type: 'circle',
  source: 'group-points-source',
  paint: {
    'circle-color': 'navy', // 赤色で中心点を強調
    'circle-radius': 8, // 固定サイズの点
    'circle-opacity': 1,
    'circle-stroke-width': 1,
    'circle-stroke-color': '#fff',
  },
}

const getLayerData = (layerId) => {
  try {
    const layer = store?.state?.pointLayers?.find((l) => l.id === layerId)
    return layer || { color: '#ff0000', features: [] }
  } catch (e) {
    console.warn('💥 store.state.pointLayers アクセスエラー:', e)
    return { color: '#ff0000', features: [] }
  }
}

// 1回だけ呼ぶ
const layerData = getLayerData('layer1')

const pointLayerSource1 = {
  id: 'point-layer-source1',
  obj: {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: layerData.features,
    },
  },
}

const pointLayerLayer1 = {
  id: 'oh-point-layer-layer1',
  type: 'circle',
  source: 'point-layer-source1',
  paint: {
    'circle-radius': 6,
    'circle-color': layerData.color, // ← レイヤーごとの色を反映
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 2,
  },
}
export const ohPointLayer = {
  id: 'oh-point-layer',
  type: 'circle',
  source: 'oh-point-source',
  paint: {
    'circle-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      5,
      4, // ズーム5では半径4
      10,
      8, // ズーム10では半径8
      15,
      20, // ズーム15では半径20
    ],
    // 'circle-color': 'navy',
    'circle-color': ['coalesce', ['get', 'color'], 'black'],
    'circle-stroke-width': 2,
    'circle-stroke-color': '#ffffff',
  },
}
export const ohLabelLayer = {
  id: 'oh-point-label-layer',
  type: 'symbol',
  source: 'oh-point-source',
  layout: {
    'text-field': ['get', 'title'], // ← GeoJSON の properties.title を使う
    'text-font': ['Noto Sans Regular', 'Arial Unicode MS Regular'],
    'text-size': 14,
    'text-offset': [0, 0.5],
    'text-anchor': 'top',
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 1.2,
  },
}
// ---------------------------------------------------------------------------------------------------------------------
export const seaLevelSource = {
  id: 'gsi-terrain-rgb',
  obj: {
    type: 'raster-dem',
    tiles: ['https://xs489works.xsrv.jp/raster-tiles/gsi/gsi-dem-terrain-rgb/{z}/{x}/{y}.png'],
    tileSize: 256,
    attribution:
      "<a href='https://maps.gsi.go.jp/development/ichiran.html#dem' target='_blank'>国土地理院 標高タイル</a>",
  },
}

export const seaLevellLayer = {
  id: 'oh-sea-level',
  type: 'color-relief',
  source: 'gsi-terrain-rgb',
  paint: {
    'color-relief-color': ['interpolate', ['linear'], ['elevation'], 0, 'rgba(0,0,0,0)', 0.01, 'rgba(0,0,0,0)'],
    'color-relief-opacity': 1,
  },
}
// 産総研 dem_png → Terrarium 変換ソース（海陸段彩図用）
export const gsjTerrainSource = {
  id: 'gsj-terrain',
  obj: {
    type: 'raster-dem',
    tiles: ['demterrainrgb://https://tiles.gsj.jp/tiles/elev/mixed/{z}/{y}/{x}.png'],
    tileSize: 256,
    maxzoom: 17,
    encoding: 'terrarium',
    attribution:
      '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank">産総研シームレス標高タイル</a>',
  },
}
export const hypsometricLayer = {
  id: 'oh-hypsometric',
  type: 'color-relief',
  source: 'gsj-terrain',
  paint: {
    'color-relief-color': ['interpolate', ['linear'], ['elevation'],
      -11000, 'rgba(0, 8, 30, 0.95)',
      -8000, 'rgba(0, 20, 60, 0.95)',
      -6000, 'rgba(0, 40, 100, 0.93)',
      -3000, 'rgba(0, 70, 140, 0.9)',
      -1500, 'rgba(0, 100, 170, 0.85)',
      -500, 'rgba(0, 140, 195, 0.8)',
      -100, 'rgba(60, 185, 215, 0.7)',
      -10, 'rgba(120, 210, 235, 0.6)',
      0, 'rgba(170, 225, 245, 0.55)',
      0.01, 'rgba(0, 128, 0, 0.7)',
      100, 'rgba(144, 238, 144, 0.7)',
      500, 'rgba(255, 255, 0, 0.7)',
      1000, 'rgba(255, 165, 0, 0.75)',
      2000, 'rgba(200, 100, 30, 0.8)',
      3000, 'rgba(139, 69, 19, 0.85)',
      5000, 'rgba(180, 180, 180, 0.85)',
      9000, 'rgba(255, 255, 255, 0.9)',
    ],
    'color-relief-opacity': 1,
  },
}
// ---------------------------------------------------------------------------------------------------------------------
export const gebcoTerrainSource = {
  id: 'gebco-terrain-rgb',
  obj: {
    type: 'raster-dem',
    tiles: ['https://gbank.gsj.jp/seamless/elev/terrainRGB/gebco/{z}/{y}/{x}.png'],
    tileSize: 256,
    maxzoom: 9,
    encoding: 'mapbox',
    attribution:
      '<a href="https://tiles.gsj.jp/tiles/elev2/#h_gebco" target="_blank">GEBCO Grid</a>',
  },
}

export const gebcoSeafloorLayer = {
  id: 'oh-gebco-seafloor',
  type: 'color-relief',
  source: 'gebco-terrain-rgb',
  paint: {
    'color-relief-color': [
      'interpolate',
      ['linear'],
      ['elevation'],
      -11000,
      'rgba(0, 16, 48, 0.9)',
      -8000,
      'rgba(0, 32, 90, 0.9)',
      -6000,
      'rgba(0, 52, 120, 0.9)',
      -3000,
      'rgba(0, 86, 150, 0.85)',
      -1500,
      'rgba(0, 120, 175, 0.8)',
      -500,
      'rgba(0, 150, 190, 0.7)',
      -100,
      'rgba(80, 190, 200, 0.5)',
      0,
      'rgba(0, 0, 0, 0)',
    ],
    'color-relief-opacity': 1,
  },
}

export const gebcoLandLayer = {
  id: 'oh-gebco-land',
  type: 'color-relief',
  source: 'gebco-terrain-rgb',
  paint: {
    'color-relief-color': [
      'interpolate',
      ['linear'],
      ['elevation'],
      -11000,
      'rgba(0, 0, 0, 0)',
      0,
      'rgba(0, 0, 0, 0)',
      10,
      'rgba(237, 245, 224, 0.7)',
      100,
      'rgba(213, 231, 198, 0.8)',
      500,
      'rgba(186, 210, 160, 0.85)',
      1000,
      'rgba(156, 186, 122, 0.9)',
      2000,
      'rgba(201, 176, 124, 0.9)',
      3000,
      'rgba(186, 150, 108, 0.9)',
      5000,
      'rgba(166, 128, 94, 0.9)',
      8000,
      'rgba(148, 112, 84, 0.95)',
    ],
    'color-relief-opacity': 1,
  },
}

export const gebcoLandSeaLayer = {
  id: 'oh-gebco-landsea',
  type: 'color-relief',
  source: 'gebco-terrain-rgb',
  paint: {
    'color-relief-color': [
      'interpolate',
      ['linear'],
      ['elevation'],
      -11000,
      'rgba(0, 16, 48, 0.9)',
      -8000,
      'rgba(0, 32, 90, 0.9)',
      -6000,
      'rgba(0, 52, 120, 0.9)',
      -3000,
      'rgba(0, 86, 150, 0.85)',
      -1500,
      'rgba(0, 120, 175, 0.8)',
      -500,
      'rgba(0, 150, 190, 0.7)',
      -100,
      'rgba(80, 190, 200, 0.5)',
      0,
      'rgba(230, 242, 223, 0.55)',
      10,
      'rgba(237, 245, 224, 0.7)',
      100,
      'rgba(213, 231, 198, 0.8)',
      500,
      'rgba(186, 210, 160, 0.85)',
      1000,
      'rgba(156, 186, 122, 0.9)',
      2000,
      'rgba(201, 176, 124, 0.9)',
      3000,
      'rgba(186, 150, 108, 0.9)',
      5000,
      'rgba(166, 128, 94, 0.9)',
      8000,
      'rgba(148, 112, 84, 0.95)',
    ],
    'color-relief-opacity': 1,
  },
}
// ---------------------------------------------------------------------------------------------------------------------
// ソース
const t23kuSource = {
  id: 't23ku-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz2.xsrv.jp/pmtiles/23kuNew.pmtiles',
  },
}
const t23kuLayer = {
  id: 't23ku-source-line',
  type: 'line',
  source: 't23ku-source',
  'source-layer': 'oh3',
  paint: {
    'line-color': 'blue',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.1, 20, 4],
  },
}

// ソース（raster）— demtint:// で dem_png を色別標高PNGへ
const demTintSrc = {
  id: 'oh-dem-tint-src',
  obj: {
    type: 'raster',
    // level=0 をクエリで渡す。値を変えたい時は setTiles(...) で ?level= を更新
    tiles: ['demtint://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

// レイヤ（最近傍で拡大＝ボケなし）
const demTintLayer = {
  id: 'oh-dem-tint',
  type: 'raster',
  source: 'oh-dem-tint-src',
  paint: {
    'raster-resampling': 'nearest', // ← 補間禁止（ドット強調）
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}
// -------------------------------------------------------------------

// ソース（raster）— demslope:// で dem_png を傾斜角PNGへ
const demSlopeSrc = {
  id: 'oh-dem-slope-src',
  obj: {
    type: 'raster',
    tiles: ['demslope://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demSlopeLayer = {
  id: 'oh-dem-slope',
  type: 'raster',
  source: 'oh-dem-slope-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}

// ソース（raster）— demaspect:// で dem_png を斜面方位PNGへ
const demAspectSrc = {
  id: 'oh-dem-aspect-src',
  obj: {
    type: 'raster',
    tiles: ['demaspect://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demAspectLayer = {
  id: 'oh-dem-aspect',
  type: 'raster',
  source: 'oh-dem-aspect-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}

// ソース（raster）— demhillshade:// で dem_png を陰影起伏図PNGへ
const demHillshadeSrc = {
  id: 'oh-dem-hillshade-src',
  obj: {
    type: 'raster',
    tiles: ['demhillshade://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?az=315&alt=45&str=0.8'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demHillshadeLayer = {
  id: 'oh-dem-hillshade',
  type: 'raster',
  source: 'oh-dem-hillshade-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}

// ソース（raster）— demcurvature:// で dem_png を曲率マップPNGへ
const demCurvatureSrc = {
  id: 'oh-dem-curvature-src',
  obj: {
    type: 'raster',
    tiles: ['demcurvature://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demCurvatureLayer = {
  id: 'oh-dem-curvature',
  type: 'raster',
  source: 'oh-dem-curvature-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}

// ソース（raster）— demlandform:// で dem_png を地形分類マップPNGへ
const demLandformSrc = {
  id: 'oh-dem-landform-src',
  obj: {
    type: 'raster',
    tiles: ['demlandform://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demLandformLayer = {
  id: 'oh-dem-landform',
  type: 'raster',
  source: 'oh-dem-landform-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}

// ソース（raster）— demflow:// で dem_png を流域解析マップPNGへ
const demFlowSrc = {
  id: 'oh-dem-flow-src',
  obj: {
    type: 'raster',
    tiles: ['demflow://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
    tileSize: 256,
    scheme: 'xyz',
    maxzoom: 22,
  },
}

const demFlowLayer = {
  id: 'oh-dem-flow',
  type: 'raster',
  source: 'oh-dem-flow-src',
  paint: {
    'raster-resampling': 'nearest',
    'raster-fade-duration': 0,
    'raster-opacity': 1,
  },
}
// -------------------------------------------------------------------

const tochikukakuseiriSource = {
  id: 'tochikukaku-source', // ← ソース名
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz2.xsrv.jp/citygis/r07tochikukakuseiri.pmtiles',
  },
}

// --- Layer 定義（塗り + 枠線）---
const tochikukakuseiriLayer = {
  id: 'oh-tochikukaku-fill',
  type: 'fill',
  source: 'tochikukaku-source', // ↑ の id と一致
  'source-layer': 'polygon', // PMTiles 内のレイヤ名
  paint: {
    'fill-color': 'rgba(255, 152, 0, 0.8)',
  },
}

const tochikukakuseiriLineLayer = {
  id: 'oh-tochikukaku-line',
  type: 'line',
  source: 'tochikukaku-source',
  'source-layer': 'polygon',
  paint: {
    'line-color': 'rgba(230, 81, 0, 1)',
    'line-width': 1,
  },
}
// ------------------------------------------------------------------
const terrain22_41Source = {
  id: 'terrain22-41-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://kenzkenz2.xsrv.jp/pmtiles/terrain22_41.pmtiles',
  },
}
const terrain22_41Layer = {
  id: 'oh-terrain22-41-polygon',
  type: 'fill',
  source: 'terrain22-41-source',
  'source-layer': 'terrain22',
  paint: {
    'fill-color': [
      'case',
      ['all', ['has', 'GCLUSTER15'], ['has', 'Sinks']],
      [
        'case',
        ['all', ['==', ['get', 'GCLUSTER15'], 1], ['==', ['get', 'Sinks'], 0]],
        'rgb(251,216,15)',
        ['all', ['==', ['get', 'GCLUSTER15'], 1], ['==', ['get', 'Sinks'], 1]],
        'rgb(154,255,255)',
        ['all', ['==', ['get', 'GCLUSTER15'], 2]],
        'rgb(112,79,41)',
        ['all', ['==', ['get', 'GCLUSTER15'], 3]],
        'rgb(172,82,50)',
        ['all', ['==', ['get', 'GCLUSTER15'], 4]],
        'rgb(243,162,243)',
        ['all', ['==', ['get', 'GCLUSTER15'], 5]],
        'rgb(248,3,204)',
        ['all', ['==', ['get', 'GCLUSTER15'], 6], ['==', ['get', 'Sinks'], 0]],
        'rgb(249,180,4)',
        ['all', ['==', ['get', 'GCLUSTER15'], 6], ['==', ['get', 'Sinks'], 1]],
        'rgb(194,251,71)',
        ['all', ['==', ['get', 'GCLUSTER15'], 7], ['==', ['get', 'Sinks'], 0]],
        'rgb(223,134,31)',
        ['all', ['==', ['get', 'GCLUSTER15'], 7], ['==', ['get', 'Sinks'], 1]],
        'rgb(30,218,74)',
        ['all', ['==', ['get', 'GCLUSTER15'], 8], ['==', ['get', 'Sinks'], 0]],
        'rgb(125,162,143)',
        ['all', ['==', ['get', 'GCLUSTER15'], 8], ['==', ['get', 'Sinks'], 1]],
        'rgb(155,141,169)',
        ['all', ['==', ['get', 'GCLUSTER15'], 9], ['==', ['get', 'Sinks'], 0]],
        'rgb(251,147,0)',
        ['all', ['==', ['get', 'GCLUSTER15'], 9], ['==', ['get', 'Sinks'], 1]],
        'rgb(122,236,40)',
        ['all', ['==', ['get', 'GCLUSTER15'], 10]],
        'rgb(40,135,108)',
        ['all', ['==', ['get', 'GCLUSTER15'], 11], ['==', ['get', 'Sinks'], 0]],
        'rgb(20,172,132)',
        ['all', ['==', ['get', 'GCLUSTER15'], 11], ['==', ['get', 'Sinks'], 1]],
        'rgb(31,195,105)',
        ['all', ['==', ['get', 'GCLUSTER15'], 12]],
        'rgb(251,154,153)',
        ['all', ['==', ['get', 'GCLUSTER15'], 13]],
        'rgb(153,131,83)',
        ['all', ['==', ['get', 'GCLUSTER15'], 14]],
        'rgb(145,150,185)',
        ['all', ['==', ['get', 'GCLUSTER15'], 15], ['==', ['get', 'Sinks'], 0]],
        'rgb(255,253,120)',
        ['all', ['==', ['get', 'GCLUSTER15'], 15], ['==', ['get', 'Sinks'], 1]],
        'rgb(0,183,255)',
        'rgb(200,200,200)',
      ],
      'rgb(200,200,200)',
    ],
  },
}
const terrain22_41LayerLine = {
  id: 'oh-terrain22-41-line',
  type: 'line',
  source: 'terrain22-41-source',
  'source-layer': 'terrain22',
  paint: {
    'line-color': 'rgba(0,0,0,0)',
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.1, 20, 4],
  },
}

// ジオランドぎふ WMS ---------------------------------------------------------------------------------------------------------
function gifuWmsTilesUrl(layerName) {
  const ps = new URLSearchParams({
    SERVICE: 'WMS',
    REQUEST: 'GetMap',
    VERSION: '1.3.0',
    FORMAT: 'image/png',
    TRANSPARENT: 'TRUE',
    STYLES: '',
    LAYERS: layerName,
    TILED: 'TRUE',
    CRS: 'EPSG:3857',
    WIDTH: '256',
    HEIGHT: '256',
    BBOX: '{bbox-epsg-3857}',
    EXCEPTIONS: 'INIMAGE',
  })
  const qs = ps.toString().replace('BBOX=%7Bbbox-epsg-3857%7D', 'BBOX={bbox-epsg-3857}')
  return '/oh3/php/wms-proxy.php?' + qs
}

// 詳細地質図
const wmsGifuSource = {
  id: 'wms-gifu-src',
  obj: {
    type: 'raster',
    tiles: [gifuWmsTilesUrl('gifu_detail')],
    tileSize: 256,
  },
}
const wmsGifuLayer = {
  id: 'oh-wms-gifu',
  type: 'raster',
  source: 'wms-gifu-src',
  paint: { 'raster-opacity': 0.9 },
}
// 断層
const wmsGifuFaultSource = {
  id: 'wms-gifu-fault-src',
  obj: {
    type: 'raster',
    tiles: [gifuWmsTilesUrl('gifu_fault')],
    tileSize: 256,
  },
}
const wmsGifuFaultLayer = {
  id: 'oh-wms-gifu-fault',
  type: 'raster',
  source: 'wms-gifu-fault-src',
}
// 活断層
const wmsGifuActiveFaultSource = {
  id: 'wms-gifu-active-fault-src',
  obj: {
    type: 'raster',
    tiles: [gifuWmsTilesUrl('active_fault')],
    tileSize: 256,
  },
}
const wmsGifuActiveFaultLayer = {
  id: 'oh-wms-gifu-active-fault',
  type: 'raster',
  source: 'wms-gifu-active-fault-src',
}

// ---------------------------------------------------------------------------------------------------------------------
// 産総研地質調査総合センター 5万分の1地質図
// ---------------------------------------------------------------------------------------------------------------------

// グローバル管理用
let gsjGeologyDataCache = [] // 全地質図データ（CSV読み込み後）
let loadedGeologyMaps = new Set() // 既に読み込み済みの図幅番号
let isLoadingCsv = false // CSV読み込み中フラグ
let gsjGeologyEnabled = false // 地質図表示フラグ

// 読み込み状況をグローバルに公開（コンポーネントから参照可能）
if (typeof window !== 'undefined') {
  // 既に定義されている場合は再定義しない
  if (!Object.prototype.hasOwnProperty.call(window, 'gsjLoadedCount')) {
    Object.defineProperty(window, 'gsjLoadedCount', {
      get: () => loadedGeologyMaps.size,
      enumerable: true,
      configurable: true,
    })
  }
  if (!Object.prototype.hasOwnProperty.call(window, 'gsjTotalCount')) {
    Object.defineProperty(window, 'gsjTotalCount', {
      get: () => gsjGeologyDataCache.length,
      enumerable: true,
      configurable: true,
    })
  }
  // デバッグ用: 読み込み済みの地質図リスト
  window.gsjLoadedMaps = loadedGeologyMaps
  // デバッグ用: 地質図レイヤーを検索する関数
  window.findGsjLayers = (mapInstance) => {
    if (!mapInstance || !mapInstance.getStyle) return []
    const layers = mapInstance.getStyle().layers || []
    return layers.filter((l) => l.id.includes('oh-gsj-'))
  }
}

// 産総研地質図CSVを読み込む（初回のみ）
// CSV形式: 図幅番号,図幅名,PMTiles_URL,Style_URL,Map_preview_URL,min_lon,min_lat,max_lon,max_lat,center_lon,center_lat
async function loadGsjGeologyCSV() {
  if (gsjGeologyDataCache.length > 0) return gsjGeologyDataCache
  if (isLoadingCsv) {
    // 重複読み込み防止
    await new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (gsjGeologyDataCache.length > 0 || !isLoadingCsv) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 100)
    })
    return gsjGeologyDataCache
  }

  isLoadingCsv = true

  try {
    // 産総研公式CSVの自サーバーコピー（bounds情報付き）
    // 元データ: https://gbank.gsj.jp/owscontents/vector_tile/list/g50k_vector_tile_list.csv
    // bounds: PMTilesヘッダーから取得した緯度経度範囲
    // publicPath相対パス（開発環境: /data/...、本番: /oh3/data/...）
    const csvUrl = `${window.location.origin}${process.env.BASE_URL}data/g50k_vector_tile_list.csv`

    const response = await axios.get(csvUrl, { timeout: 15000 })
    const csv = response.data

    // CSVパース（UTF-8 BOM考慮）
    const lines = csv
      .replace(/^\uFEFF/, '')
      .split('\n')
      .filter((line) => line.trim())

    gsjGeologyDataCache = lines
      .slice(1)
      .map((line) => {
        const values = line.split(',')
        return {
          mapCode: values[0]?.trim(),
          mapName: values[1]?.trim(),
          pmtilesUrl: values[2]?.trim(),
          styleUrl: values[3]?.trim(),
          previewUrl: values[4]?.trim(),
          // bounds（PMTilesヘッダーから取得済み）
          minLon: parseFloat(values[5]) || 0,
          minLat: parseFloat(values[6]) || 0,
          maxLon: parseFloat(values[7]) || 0,
          maxLat: parseFloat(values[8]) || 0,
          centerLon: parseFloat(values[9]) || 0,
          centerLat: parseFloat(values[10]) || 0,
        }
      })
      .filter((item) => item.pmtilesUrl && item.styleUrl)

  } catch (error) {
    console.error('産総研地質図CSV読み込みエラー:', error)
    gsjGeologyDataCache = []
  } finally {
    isLoadingCsv = false
  }

  return gsjGeologyDataCache
}

// スタイルJSONから地質図レイヤーを作成
async function createGsjGeologyLayersFromStyle(item) {
  try {
    // 既に読み込み済みならスキップ
    if (loadedGeologyMaps.has(item.mapCode)) {
      return null
    }

    // スタイルJSONを直接取得（産総研サーバーから）
    const styleResponse = await axios.get(item.styleUrl, { timeout: 15000 })
    const style = styleResponse.data

    const sourceId = `gsj-geology-${item.mapCode}`

    // ソース定義（OH3のpmtiles形式に合わせる）
    const source = {
      id: sourceId,
      obj: {
        type: 'vector',
        url: `pmtiles://${item.pmtilesUrl}`,
        minzoom: 4,
        maxzoom: 16,
        attribution: '<a href="https://www.gsj.jp/license/license.html" target="_blank">産総研地質調査総合センター</a>',
      },
    }

    // レイヤー定義（産総研スタイルを流用）
    const layers = style.layers
      .filter((layer) => layer.id !== 'background') // 背景層は除外
      .map((layer) => {
        const newLayer = {
          ...layer,
          id: `oh-gsj-${item.mapCode}-${layer.id}`, // IDをOH3用に変更
          source: sourceId, // ソースIDを置き換え
          minzoom: layer.type === 'fill' ? 9 : 11, // fillは9、line/symbolは11
          metadata: {
            'oh3:geology-map': item.mapCode,
            'oh3:map-name': item.mapName,
          },
        }

        // 不透明度を明示的に設定
        if (newLayer.type === 'fill' && newLayer.paint) {
          newLayer.paint = {
            ...newLayer.paint,
            'fill-opacity': newLayer.paint['fill-opacity'] || 0.7,
          }
        } else if (newLayer.type === 'line' && newLayer.paint) {
          newLayer.paint = {
            ...newLayer.paint,
            'line-opacity': newLayer.paint['line-opacity'] || 1.0,
          }
        }

        return newLayer
      })

    // 読み込み済みとしてマーク
    loadedGeologyMaps.add(item.mapCode)

    return { source, layers, mapName: item.mapName, mapCode: item.mapCode }
  } catch (error) {
    console.error(`地質図スタイル取得エラー [${item.mapCode}]:`, error)
    return null
  }
}

// 現在のビューポートと重なる地質図を取得（boundsベース）
function getVisibleGeologyMaps(map, limit = 20) {
  const zoom = map.getZoom()

  // zoom < 9 の場合は読み込まない（fillレイヤーのminzoomと一致）
  if (zoom < 9) return []

  // 現在の表示範囲を取得
  const bounds = map.getBounds()
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  const center = map.getCenter()

  // ビューポートと重なる未読み込みの図幅を取得
  const candidates = gsjGeologyDataCache.filter((item) => {
    // 既に読み込み済みならスキップ
    if (loadedGeologyMaps.has(item.mapCode)) return false
    // boundsが無ければスキップ
    if (!item.minLon || !item.maxLon) return false
    // ビューポートとの重なり判定（AABB交差判定）
    return !(item.maxLon < sw.lng || item.minLon > ne.lng ||
             item.maxLat < sw.lat || item.minLat > ne.lat)
  })

  // 中心から近い順にソート
  candidates.sort((a, b) => {
    const distA = Math.pow(a.centerLon - center.lng, 2) + Math.pow(a.centerLat - center.lat, 2)
    const distB = Math.pow(b.centerLon - center.lng, 2) + Math.pow(b.centerLat - center.lat, 2)
    return distA - distB
  })

  const result = candidates.slice(0, limit)

  if (result.length > 0) {
  }

  return result
}

// マップに地質図を動的追加（ビューポート内のみ）
export async function loadVisibleGeologyMaps(map) {
  if (!gsjGeologyEnabled) return

  try {
    // CSV未読み込みなら読み込む
    await loadGsjGeologyCSV()

    if (gsjGeologyDataCache.length === 0) {
      console.warn('産総研地質図データが空です')
      return
    }

    // ズームレベル制限: 9以上（fillレイヤーのminzoomと一致）
    const zoom = map.getZoom()
    if (zoom < 9) return

    // ビューポート内の未読み込み地質図を取得
    const visibleMaps = getVisibleGeologyMaps(map, 20)

    if (visibleMaps.length === 0) return

    // 並列読み込み（5件ずつバッチ処理 — スタイルJSON取得があるので控えめに）
    const batchSize = 5
    for (let i = 0; i < visibleMaps.length; i += batchSize) {
      const batch = visibleMaps.slice(i, i + batchSize)
      const promises = batch.map((item) => createGsjGeologyLayersFromStyle(item))
      const results = await Promise.all(promises)

      // マップに追加
      results.forEach((result) => {
        if (result && map.getSource(result.source.id) === undefined) {
          // ソースを追加
          map.addSource(result.source.id, result.source.obj)

          // レイヤーを追加（既存レイヤーの下に挿入）
          const beforeLayer = findGeologyBeforeLayerId(map)
          result.layers.forEach((layer) => {
            try {
              if (beforeLayer && map.getLayer(beforeLayer)) {
                map.addLayer(layer, beforeLayer)
              } else {
                map.addLayer(layer)
              }
            } catch (err) {
              console.warn(`レイヤー追加エラー [${layer.id}]:`, err)
            }
          })
        }
      })
    }
  } catch (error) {
    console.error('地質図の遅延読み込みエラー:', error)
  }
}

// レイヤーを挿入する位置を探す
function findGeologyBeforeLayerId(map) {
  const layers = map.getStyle()?.layers || []
  // 地質図自身のレイヤーは除外して、ベースマップのラベルレイヤーを探す
  const labelLayer = layers.find(
    (layer) =>
      !layer.id.includes('oh-gsj-') && // 地質図レイヤーを除外
      (layer.type === 'symbol' || layer.id.includes('label'))
  )
  return labelLayer?.id || null
}

// 地質図レイヤーを全て削除
export function clearAllGeologyMaps(map) {
  loadedGeologyMaps.forEach((mapCode) => {
    const sourceId = `gsj-geology-${mapCode}`

    // レイヤーを削除
    const layers = map.getStyle()?.layers || []
    layers.forEach((layer) => {
      if (layer.source === sourceId) {
        try {
          map.removeLayer(layer.id)
        } catch (err) {
          // レイヤーが既に削除されている場合はスキップ
        }
      }
    })

    // ソースを削除
    if (map.getSource(sourceId)) {
      try {
        map.removeSource(sourceId)
      } catch (err) {
        // ソースが既に削除されている場合はスキップ
      }
    }
  })

  loadedGeologyMaps.clear()
}

// マップへの統合（moveend/zoomendで自動読み込み）
export function setupGeologyLayerDynamicLoading(map) {
  let isLoading = false

  const onMapIdle = async () => {
    if (!gsjGeologyEnabled) return
    if (isLoading) return

    isLoading = true
    try {
      await loadVisibleGeologyMaps(map)
    } finally {
      isLoading = false
    }
  }

  // マップ移動終了時に自動読み込み
  map.on('moveend', onMapIdle)
  map.on('zoomend', onMapIdle)

  // 初回読み込み
  if (gsjGeologyEnabled) {
    map.once('idle', onMapIdle)
  }

  // クリーンアップ関数を返す
  return () => {
    map.off('moveend', onMapIdle)
    map.off('zoomend', onMapIdle)
  }
}

// 地質図表示の切り替え
export function toggleGeologyLayers(map, enabled) {
  gsjGeologyEnabled = enabled

  if (enabled) {
    // 即座に読み込み開始
    loadVisibleGeologyMaps(map)
  } else {
    clearAllGeologyMaps(map)
  }
}

// ダミーのソース・レイヤー（layers01配列用）
const gsjGeologyDummySource = {
  id: 'gsj-geology-dummy-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}

const gsjGeologyDummyLayer = {
  id: 'oh-gsj-geology-map01',
  type: 'circle',
  source: 'gsj-geology-dummy-source',
  paint: { 'circle-radius': 0 },
}

// 地籍調査進捗 -----------------------------------------------------------------------------------------
// N03行政区域GeoJSON（mapshaper加工済み）+ progress.jsonで表示
// ダミーのソース・レイヤー（layers01配列用）
const chisekiDummySource = {
  id: 'chiseki-dummy-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const chisekiDummyLayer = {
  id: 'oh-chiseki-progress',
  type: 'circle',
  source: 'chiseki-dummy-source',
  paint: { 'circle-radius': 0 },
}

// 進捗率 → 色マッピング
const CHISEKI_COLORS = {
  high:   '#1a6b1a', // 80-100%
  mid2:   '#4caf50', // 60-80%
  mid1:   '#8bc34a', // 40-60%
  low2:   '#ffeb3b', // 20-40%
  low1:   '#ff9800', // 1-20%
  zero:   '#f44336', // 0%
}

function getChisekiColor(progress) {
  const p = parseFloat(progress) || 0
  if (p >= 80) return CHISEKI_COLORS.high
  if (p >= 60) return CHISEKI_COLORS.mid2
  if (p >= 40) return CHISEKI_COLORS.mid1
  if (p >= 20) return CHISEKI_COLORS.low2
  if (p >= 1) return CHISEKI_COLORS.low1
  return CHISEKI_COLORS.zero
}

// 地籍調査データ管理
let chisekiEnabled = false
let chisekiProgressData = null
let chisekiDataLoaded = false

const CHISEKI_DATA_BASE = `${window.location.origin}${process.env.BASE_URL}data/chiseki`
const CHISEKI_SOURCE_ID = 'chiseki-source'
const CHISEKI_FILL_LAYER = 'oh-chiseki-fill'
const CHISEKI_LINE_LAYER = 'oh-chiseki-line'
const CHISEKI_LABEL_LAYER = 'oh-chiseki-label'

// 地籍調査データを読み込んでMapLibreに追加
async function loadChisekiData(map) {
  if (chisekiDataLoaded) return
  chisekiDataLoaded = true

  try {
    // municipalities.geojson と progress.json を並列取得
    const [geoRes, progRes] = await Promise.all([
      fetch(`${CHISEKI_DATA_BASE}/municipalities.geojson`),
      fetch(`${CHISEKI_DATA_BASE}/progress.json`),
    ])
    if (!geoRes.ok || !progRes.ok) throw new Error('データ取得失敗')

    const [fc, progData] = await Promise.all([geoRes.json(), progRes.json()])
    chisekiProgressData = progData

    // 各featureにprogress/status/colorを付与
    const progressMap = progData.progress || {}
    const statusMap = progData.status || {}
    fc.features.forEach(f => {
      const code = f.properties.code
      const progress = progressMap[code] || '0'
      const status = statusMap[code] || ''
      f.properties.progress = progress
      f.properties.status = status
      f.properties._color = getChisekiColor(progress)
    })


    // MapLibreにソース＆レイヤー追加
    if (map.getSource(CHISEKI_SOURCE_ID)) return

    map.addSource(CHISEKI_SOURCE_ID, { type: 'geojson', data: fc })

    map.addLayer({
      id: CHISEKI_FILL_LAYER,
      type: 'fill',
      source: CHISEKI_SOURCE_ID,
      paint: {
        'fill-color': ['get', '_color'],
        'fill-opacity': 0.45,
      },
      minzoom: 4,
    })

    map.addLayer({
      id: CHISEKI_LINE_LAYER,
      type: 'line',
      source: CHISEKI_SOURCE_ID,
      paint: {
        'line-color': 'rgba(100, 100, 100, 0.5)',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.3, 10, 1, 14, 1.5],
      },
      minzoom: 4,
    })

    map.addLayer({
      id: CHISEKI_LABEL_LAYER,
      type: 'symbol',
      source: CHISEKI_SOURCE_ID,
      layout: {
        'text-field': ['concat', ['get', 'name'], '\n', ['get', 'progress'], '%'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 8, 13, 12, 16, 15, 18],
        'text-allow-overlap': false,
        'text-padding': 5,
        'symbol-placement': 'point',
      },
      paint: {
        'text-color': '#333333',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
      minzoom: 9,
    })
  } catch (e) {
    console.error('[chiseki] データ読み込みエラー:', e)
    chisekiDataLoaded = false
  }
}

// 全クリア
function clearAllChisekiLayers(map) {
  ;[CHISEKI_LABEL_LAYER, CHISEKI_LINE_LAYER, CHISEKI_FILL_LAYER].forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id)
  })
  if (map.getSource(CHISEKI_SOURCE_ID)) map.removeSource(CHISEKI_SOURCE_ID)
  chisekiDataLoaded = false
}

// 地籍調査表示の切り替え（extコンポーネントから呼ぶ）
export function toggleChisekiLayers(map, enabled) {
  chisekiEnabled = enabled

  if (enabled) {
    loadChisekiData(map)
  } else {
    clearAllChisekiLayers(map)
  }
}

// 進捗データのゲッター（extコンポーネントから参照）
export function getChisekiProgressData() {
  return chisekiProgressData
}

// fillレイヤーID（クリックハンドラ用）
export function getChisekiFillLayerId() {
  return CHISEKI_FILL_LAYER
}

// 市区町村コードからfeatureプロパティを取得（URL復元用）
export function getChisekiMuniByCode(map, code) {
  if (!map || !code) return null
  const source = map.getSource(CHISEKI_SOURCE_ID)
  if (!source) return null
  const features = map.querySourceFeatures(CHISEKI_SOURCE_ID)
  const f = features.find(f => f.properties.code === code)
  return f ? f.properties : null
}

// ============================================================
// 市区町村詳細ビュー（大字レベルの調査状況）
// ============================================================

// 調査ステータス → 色・名前マッピング（chiseki.go.jpの survey_status_list 準拠）
const SURVEY_STATUS = {
  1:  { color: 'rgba(255,79,84,0.6)',  name: '地籍調査実施済み・実施中' },
  2:  { color: 'rgba(255,79,84,0.6)',  name: '地籍調査実施済み・実施中' },
  3:  { color: 'rgba(255,79,84,0.6)',  name: '地籍調査実施済み・実施中' },
  4:  { color: 'rgba(255,79,84,0.6)',  name: '地籍調査実施済み・実施中' },
  91: { color: 'rgba(255,79,84,0.6)',  name: '地籍調査実施済み・実施中' },
  5:  { color: 'rgba(155,223,255,0.6)', name: '国基本調査実施済み・実施中' },
  6:  { color: 'rgba(155,223,255,0.6)', name: '国基本調査実施済み・実施中' },
  92: { color: 'rgba(155,223,255,0.6)', name: '国基本調査実施済み・実施中' },
  7:  { color: 'rgba(49,206,12,0.6)',  name: '対象外（国有林野等）' },
  8:  { color: 'rgba(49,206,12,0.6)',  name: '対象外（国有林野等）' },
  93: { color: 'rgba(49,206,12,0.6)',  name: '対象外（国有林野等）' },
  9:  { color: 'rgba(170,255,0,0.6)',  name: '大規模国・公有地' },
  10: { color: 'rgba(255,255,0,0.6)',  name: '19条15項指定区域' },
  11: { color: 'rgba(223,115,255,0.6)', name: '地籍が一定程度明らか' },
  12: { color: 'rgba(223,115,255,0.6)', name: '地籍が一定程度明らか' },
  13: { color: 'rgba(223,115,255,0.6)', name: '地籍が一定程度明らか' },
  14: { color: 'rgba(223,115,255,0.6)', name: '地籍が一定程度明らか' },
  94: { color: 'rgba(223,115,255,0.6)', name: '地籍が一定程度明らか' },
}

const CHISEKI_DETAIL_SOURCE = 'chiseki-detail-source'
const CHISEKI_DETAIL_BORDER_SOURCE = 'chiseki-detail-border-source'
const CHISEKI_DETAIL_FILL = 'oh-chiseki-detail-fill'
const CHISEKI_DETAIL_LINE = 'oh-chiseki-detail-line'
const CHISEKI_DETAIL_BORDER = 'oh-chiseki-detail-border'

let chisekiDetailActive = false

// 詳細ビューのレイヤーをクリア
function clearChisekiDetail(map) {
  ;[CHISEKI_DETAIL_BORDER, CHISEKI_DETAIL_LINE, CHISEKI_DETAIL_FILL].forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id)
  })
  ;[CHISEKI_DETAIL_SOURCE, CHISEKI_DETAIL_BORDER_SOURCE].forEach(id => {
    if (map.getSource(id)) map.removeSource(id)
  })
  chisekiDetailActive = false
}

// 現在詳細表示中の市区町村コード
let chisekiDetailCode = null

// 市区町村詳細ビューを表示
export async function showChisekiDetail(map, code) {
  const prefCode = code.slice(0, 2)
  const proxyBase = '/oh3/php/chisekiProxy.php'

  // 既存の詳細レイヤーをクリア
  clearChisekiDetail(map)

  // 広域fillレイヤーで該当市区町村を透明にする
  chisekiDetailCode = code
  if (map.getLayer(CHISEKI_FILL_LAYER)) {
    map.setPaintProperty(CHISEKI_FILL_LAYER, 'fill-opacity', [
      'case',
      ['==', ['get', 'code'], code], 0,
      0.45,
    ])
    // ラベルも該当市区町村を非表示に
    if (map.getLayer(CHISEKI_LABEL_LAYER)) {
      map.setLayoutProperty(CHISEKI_LABEL_LAYER, 'text-field', [
        'case',
        ['==', ['get', 'code'], code], '',
        ['concat', ['get', 'name'], '\n', ['get', 'progress'], '%'],
      ])
    }
  }

  // cadastral_data と border を並列取得
  const [cadastralRes, borderRes] = await Promise.all([
    fetch(`${proxyBase}?action=cadastral&pref=${prefCode}&code=${code}`),
    fetch(`${proxyBase}?action=muni_border&pref=${prefCode}&code=${code}`),
  ])

  if (!cadastralRes.ok) throw new Error(`cadastral取得失敗: ${cadastralRes.status}`)

  const cadastralFc = await cadastralRes.json()
  if (cadastralFc.error) throw new Error(cadastralFc.error)

  // 各featureに色を付与
  cadastralFc.features.forEach(f => {
    const st = f.properties.survey_status
    const info = SURVEY_STATUS[st]
    f.properties._detail_color = info ? info.color : 'rgba(200,200,200,0.3)'
  })

  // cadastralデータをMapLibreに追加
  map.addSource(CHISEKI_DETAIL_SOURCE, { type: 'geojson', data: cadastralFc })
  map.addLayer({
    id: CHISEKI_DETAIL_FILL,
    type: 'fill',
    source: CHISEKI_DETAIL_SOURCE,
    paint: {
      'fill-color': ['get', '_detail_color'],
      'fill-opacity': 0.7,
    },
  })
  map.addLayer({
    id: CHISEKI_DETAIL_LINE,
    type: 'line',
    source: CHISEKI_DETAIL_SOURCE,
    paint: {
      'line-color': 'rgba(80, 80, 80, 0.4)',
      'line-width': 0.5,
    },
  })

  // 市区町村境界（太線）
  let borderFc = null
  if (borderRes.ok) {
    borderFc = await borderRes.json()
    if (!borderFc.error && borderFc.features) {
      map.addSource(CHISEKI_DETAIL_BORDER_SOURCE, { type: 'geojson', data: borderFc })
      map.addLayer({
        id: CHISEKI_DETAIL_BORDER,
        type: 'line',
        source: CHISEKI_DETAIL_BORDER_SOURCE,
        paint: {
          'line-color': '#ff0000',
          'line-width': 2.5,
        },
      })
    } else {
      borderFc = null
    }
  }

  chisekiDetailActive = true
}

// 詳細ビューを閉じる
export function hideChisekiDetail(map) {
  clearChisekiDetail(map)
  chisekiDetailCode = null
  // 広域fillレイヤーの透明化を元に戻す
  if (map.getLayer(CHISEKI_FILL_LAYER)) {
    map.setPaintProperty(CHISEKI_FILL_LAYER, 'fill-opacity', 0.45)
  }
  if (map.getLayer(CHISEKI_LABEL_LAYER)) {
    map.setLayoutProperty(CHISEKI_LABEL_LAYER, 'text-field', [
      'concat', ['get', 'name'], '\n', ['get', 'progress'], '%',
    ])
  }
}

// 詳細ビューがアクティブかどうか
export function isChisekiDetailActive() {
  return chisekiDetailActive
}

// 詳細fill レイヤーID（クリックハンドラ用）
export function getChisekiDetailFillLayerId() {
  return CHISEKI_DETAIL_FILL
}

export { CHISEKI_COLORS, SURVEY_STATUS }

// 地籍調査進捗 v2 =====================================================================================
// 単一GeoJSON（chiseki_progress.geojson）で表示
// プロパティ: name(市区町村名), pref(都道府県名), code(行政区域コード), progress(0-100)
const chisekiV2DummySource = {
  id: 'chiseki-v2-dummy-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const chisekiV2DummyLayer = {
  id: 'oh-chiseki-v2',
  type: 'circle',
  source: 'chiseki-v2-dummy-source',
  paint: { 'circle-radius': 0 },
}

// 進捗率 → 色（v2: CHISEKI_COLORSを再利用）
const CHISEKI_V2_COLOR_EXPR = [
  'step', ['get', 'progress'],
  CHISEKI_COLORS.zero,
  1, CHISEKI_COLORS.low1,
  20, CHISEKI_COLORS.low2,
  40, CHISEKI_COLORS.mid1,
  60, CHISEKI_COLORS.mid2,
  80, CHISEKI_COLORS.high,
]

let chisekiV2Loaded = false
let chisekiV2Fc = null

const CHISEKI_V2_URL = `${window.location.origin}${process.env.BASE_URL}data/chiseki_progress.geojson`
const CHISEKI_V2_SOURCE = 'chiseki-v2-source'
const CHISEKI_V2_FILL = 'oh-chiseki-v2-fill'
const CHISEKI_V2_LINE = 'oh-chiseki-v2-line'
const CHISEKI_V2_LABEL = 'oh-chiseki-v2-label'

async function loadChisekiV2(map) {
  if (chisekiV2Loaded) return
  chisekiV2Loaded = true

  try {
    const res = await fetch(CHISEKI_V2_URL)
    if (!res.ok) throw new Error('データ取得失敗')
    const fc = await res.json()
    chisekiV2Fc = fc

    if (map.getSource(CHISEKI_V2_SOURCE)) return
    map.addSource(CHISEKI_V2_SOURCE, { type: 'geojson', data: fc })

    map.addLayer({
      id: CHISEKI_V2_FILL,
      type: 'fill',
      source: CHISEKI_V2_SOURCE,
      paint: {
        'fill-color': CHISEKI_V2_COLOR_EXPR,
        'fill-opacity': 0.5,
      },
      minzoom: 4,
    })
    map.addLayer({
      id: CHISEKI_V2_LINE,
      type: 'line',
      source: CHISEKI_V2_SOURCE,
      paint: {
        'line-color': 'rgba(100, 100, 100, 0.5)',
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.3, 10, 1, 14, 1.5],
      },
      minzoom: 4,
    })
    map.addLayer({
      id: CHISEKI_V2_LABEL,
      type: 'symbol',
      source: CHISEKI_V2_SOURCE,
      layout: {
        'text-field': ['concat', ['get', 'name'], '\n', ['to-string', ['get', 'progress']], '%'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 8, 13, 12, 16, 15, 18],
        'text-allow-overlap': false,
        'text-padding': 5,
        'symbol-placement': 'point',
      },
      paint: {
        'text-color': '#333333',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
      minzoom: 9,
    })
  } catch (e) {
    console.error('[chiseki-v2] データ読み込みエラー:', e)
    chisekiV2Loaded = false
  }
}

function clearChisekiV2(map) {
  ;[CHISEKI_V2_HIGHLIGHT, CHISEKI_V2_LABEL, CHISEKI_V2_LINE, CHISEKI_V2_FILL].forEach(id => {
    if (map.getLayer(id)) map.removeLayer(id)
  })
  if (map.getSource(CHISEKI_V2_SOURCE)) map.removeSource(CHISEKI_V2_SOURCE)
  chisekiV2Loaded = false
  chisekiV2Fc = null
}

export function toggleChisekiV2(map, enabled) {
  if (enabled) {
    loadChisekiV2(map)
  } else {
    clearChisekiV2(map)
  }
}

export function getChisekiV2Features() {
  return chisekiV2Fc
}

export function getChisekiV2FillLayerId() {
  return CHISEKI_V2_FILL
}

const CHISEKI_V2_HIGHLIGHT = 'oh-chiseki-v2-highlight'

// クリックした市区町村のポリゴン枠を強調
export function highlightChisekiV2(map, code) {
  if (!map.getSource(CHISEKI_V2_SOURCE)) return
  if (!map.getLayer(CHISEKI_V2_HIGHLIGHT)) {
    map.addLayer({
      id: CHISEKI_V2_HIGHLIGHT,
      type: 'line',
      source: CHISEKI_V2_SOURCE,
      paint: {
        'line-color': '#ff0000',
        'line-width': 3,
        'line-opacity': 0.9,
      },
      filter: ['==', ['get', 'code'], ''],
    })
  }
  map.setFilter(CHISEKI_V2_HIGHLIGHT, ['==', ['get', 'code'], code])
}

export function clearChisekiV2Highlight(map) {
  if (map.getLayer(CHISEKI_V2_HIGHLIGHT)) {
    map.setFilter(CHISEKI_V2_HIGHLIGHT, ['==', ['get', 'code'], ''])
  }
}

// 進捗率範囲でフィルタリング（複数範囲対応）
// ranges: [{ min, max }] の配列。空配列→フィルタクリア（全表示）、null→全非表示
export function filterChisekiV2(map, ranges) {
  const layerIds = [CHISEKI_V2_FILL, CHISEKI_V2_LINE, CHISEKI_V2_LABEL]

  if (ranges === null) {
    // 全非表示
    const falseFilter = ['==', ['get', 'code'], '__none__']
    layerIds.forEach(id => { if (map.getLayer(id)) map.setFilter(id, falseFilter) })
    return
  }

  if (!ranges || ranges.length === 0) {
    // フィルタクリア（全表示）
    layerIds.forEach(id => { if (map.getLayer(id)) map.setFilter(id, null) })
    return
  }

  // 各範囲を条件に変換
  const conditions = ranges.map(r => {
    if (r.min === 0 && r.max === 0) return ['==', ['get', 'progress'], 0]
    return ['all', ['>=', ['get', 'progress'], r.min], ['<=', ['get', 'progress'], r.max]]
  })
  const filter = conditions.length === 1 ? conditions[0] : ['any', ...conditions]
  layerIds.forEach(id => { if (map.getLayer(id)) map.setFilter(id, filter) })
}

// 雨雲レーダー（気象庁ナウキャスト）----------------------------------------------------------------------------------
// OH3のレイヤー管理システムで扱う実際のラスターソース/レイヤー
// タイルURLはextコンポーネントが map.getSource().setTiles() で更新する
// 初期状態は透明1x1 PNG（data URL）を設定（MapLibreは空配列を受け付けない）
const TRANSPARENT_TILE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
const rainRadarSource = {
  id: 'oh-rain-radar-source',
  obj: {
    type: 'raster',
    tiles: [TRANSPARENT_TILE],
    tileSize: 256,
    minzoom: 4,
    maxzoom: 10,
  },
}
const rainRadarLayer = {
  id: 'oh-rain-radar-layer',
  type: 'raster',
  source: 'oh-rain-radar-source',
  minzoom: 4,
  maxzoom: 22,
  paint: {
    'raster-opacity': 0.7,
    'raster-fade-duration': 300,
  },
}

// ひまわり衛星画像（気象庁）----------------------------------------------------------------------------------
// OH3のレイヤー管理システムで扱う実際のラスターソース/レイヤー
const himawariSource = {
  id: 'oh-himawari-source',
  obj: {
    type: 'raster',
    tiles: [TRANSPARENT_TILE],
    tileSize: 256,
    minzoom: 1,
    maxzoom: 6,
  },
}
const himawariLayer = {
  id: 'oh-himawari-layer',
  type: 'raster',
  source: 'oh-himawari-source',
  minzoom: 1,
  maxzoom: 22,
  paint: {
    'raster-opacity': 0.8,
    'raster-fade-duration': 300,
  },
}

// キキクル（危険度分布）（気象庁）----------------------------------------------------------------------------------
// OH3のレイヤー管理システムで扱う実際のラスターソース/レイヤー
// キキクルは3種類（土砂災害/浸水害/洪水）を1つのソース/レイヤーで切り替える
const kikukuruSource = {
  id: 'oh-kikukuru-source',
  obj: {
    type: 'raster',
    tiles: [TRANSPARENT_TILE],
    tileSize: 256,
    minzoom: 1,
    maxzoom: 10,
  },
}
const kikukuruLayer = {
  id: 'oh-kikukuru-layer',
  type: 'raster',
  source: 'oh-kikukuru-source',
  minzoom: 1,
  maxzoom: 22,
  paint: {
    'raster-opacity': 0.7,
    'raster-fade-duration': 300,
  },
}

// OpenWeatherMap 天気タイル（Maps 1.0）---------------------------------------------------------
// https://openweathermap.org/api/weathermaps
// 無料枠: 月100万リクエスト。「現在」のデータのみ（時刻選択不要）
const OWM_API_KEY = process.env.VUE_APP_OWM_API_KEY || ''
const owmTileUrl = (layer) =>
  OWM_API_KEY
    ? `https://tile.openweathermap.org/map/${layer}/{z}/{x}/{y}.png?appid=${OWM_API_KEY}`
    : TRANSPARENT_TILE

// 降水量
const owmPrecipitationSource = {
  id: 'oh-owm-precipitation-source',
  obj: { type: 'raster', tiles: [owmTileUrl('precipitation_new')], tileSize: 256, minzoom: 0, maxzoom: 8 },
}
const owmPrecipitationLayer = {
  id: 'oh-owm-precipitation-layer',
  type: 'raster',
  source: 'oh-owm-precipitation-source',
  minzoom: 0,
  maxzoom: 22,
  paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 300 },
}

// 雲量
const owmCloudsSource = {
  id: 'oh-owm-clouds-source',
  obj: { type: 'raster', tiles: [owmTileUrl('clouds_new')], tileSize: 256, minzoom: 0, maxzoom: 8 },
}
const owmCloudsLayer = {
  id: 'oh-owm-clouds-layer',
  type: 'raster',
  source: 'oh-owm-clouds-source',
  minzoom: 0,
  maxzoom: 22,
  paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 300 },
}

// 気温
const owmTempSource = {
  id: 'oh-owm-temp-source',
  obj: { type: 'raster', tiles: [owmTileUrl('temp_new')], tileSize: 256, minzoom: 0, maxzoom: 8 },
}
const owmTempLayer = {
  id: 'oh-owm-temp-layer',
  type: 'raster',
  source: 'oh-owm-temp-source',
  minzoom: 0,
  maxzoom: 22,
  paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 300 },
}

// 風速
const owmWindSource = {
  id: 'oh-owm-wind-source',
  obj: { type: 'raster', tiles: [owmTileUrl('wind_new')], tileSize: 256, minzoom: 0, maxzoom: 8 },
}
const owmWindLayer = {
  id: 'oh-owm-wind-layer',
  type: 'raster',
  source: 'oh-owm-wind-source',
  minzoom: 0,
  maxzoom: 22,
  paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 300 },
}

// 気圧
const owmPressureSource = {
  id: 'oh-owm-pressure-source',
  obj: { type: 'raster', tiles: [owmTileUrl('pressure_new')], tileSize: 256, minzoom: 0, maxzoom: 8 },
}
const owmPressureLayer = {
  id: 'oh-owm-pressure-layer',
  type: 'raster',
  source: 'oh-owm-pressure-source',
  minzoom: 0,
  maxzoom: 22,
  paint: { 'raster-opacity': 0.7, 'raster-fade-duration': 300 },
}

// 航空機リアルタイムトラッキング（OpenSky Network）---------------------------------------------------
// GeoJSONソース（空のFeatureCollectionで初期化、extコンポーネントが setData() で更新）
const aircraftSource = {
  id: 'oh-aircraft-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const aircraftLayer = {
  id: 'oh-aircraft-layer',
  type: 'symbol',
  source: 'oh-aircraft-source',
  layout: {
    'icon-image': 'aircraft-sdf',
    'icon-size': ['interpolate', ['linear'], ['zoom'],
      3, 0.3,
      6, 0.45,
      10, 0.6,
      14, 0.8,
    ],
    'icon-rotate': ['get', 'trueTrack'],       // 進行方向に回転
    'icon-rotation-alignment': 'map',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-pitch-alignment': 'map',
  },
  paint: {
    'icon-opacity': 0.9,
    'icon-color': ['get', 'color'],            // SDF + 高度色
  },
}
const aircraftLabelLayer = {
  id: 'oh-aircraft-label-layer',
  type: 'symbol',
  source: 'oh-aircraft-source',
  minzoom: 8,
  layout: {
    'text-field': ['get', 'callsign'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 9, 14, 12],
    'text-offset': [0, 1.5],
    'text-anchor': 'top',
    'text-allow-overlap': false,
    'text-optional': true,
    'text-font': ['Noto Sans CJK JP Regular'],
  },
  paint: {
    'text-color': '#1a237e',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5,
    'text-opacity': 0.9,
  },
}

// --- AMeDAS 気象観測 ---
const amedasSource = {
  id: 'oh-amedas-source',
  obj: {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  },
}
const amedasLayer = {
  id: 'oh-amedas-layer',
  type: 'circle',
  source: 'oh-amedas-source',
  paint: {
    'circle-radius': ['interpolate', ['linear'], ['zoom'],
      4, 3,
      8, 5,
      12, 8,
      16, 12,
    ],
    'circle-color': ['get', 'color'],
    'circle-stroke-width': 1,
    'circle-stroke-color': '#ffffff',
    'circle-opacity': 0.85,
  },
}
const amedasLabelLayer = {
  id: 'oh-amedas-label-layer',
  type: 'symbol',
  source: 'oh-amedas-source',
  minzoom: 10,
  layout: {
    'text-field': ['get', 'label'],
    'text-size': ['interpolate', ['linear'], ['zoom'], 10, 9, 14, 12],
    'text-offset': [0, 1.5],
    'text-anchor': 'top',
    'text-allow-overlap': false,
    'text-optional': true,
    'text-font': ['Noto Sans CJK JP Regular'],
  },
  paint: {
    'text-color': '#333333',
    'text-halo-color': '#ffffff',
    'text-halo-width': 1.5,
    'text-opacity': 0.85,
  },
}

// 風向矢印レイヤー（風速選択時のみ表示）
const amedasWindArrowLayer = {
  id: 'oh-amedas-wind-arrow',
  type: 'symbol',
  source: 'oh-amedas-source',
  layout: {
    'icon-image': 'arrow-sdf',
    'icon-size': ['interpolate', ['linear'], ['zoom'],
      4, 0.25,
      8, 0.4,
      12, 0.6,
      16, 0.8,
    ],
    'icon-rotate': ['coalesce', ['get', 'windDeg'], 0],
    'icon-rotation-alignment': 'map',
    'icon-pitch-alignment': 'map',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'visibility': 'none',
  },
  paint: {
    'icon-color': ['get', 'color'],
    'icon-opacity': ['case', ['==', ['get', 'windDeg'], null], 0, 0.9],
  },
}

// 国土数値情報 - 国土（水・土地）カテゴリ ===================================================================
// W01 ダム
const kkDamSource = {
  id: 'kk-dam-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/dam.pmtiles' },
}
const kkDamCircle = {
  id: 'oh-kk-dam',
  type: 'circle',
  source: 'kk-dam-source',
  'source-layer': 'dam',
  paint: {
    'circle-color': [
      'interpolate', ['linear'], ['to-number', ['get', 'W01_007'], 15],
      15, '#90CAF9',
      50, '#42A5F5',
      100, '#1565C0',
      150, '#0D47A1',
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 1, 7, 5, 11, 9],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
}
const kkDamLabel = {
  id: 'oh-kk-dam-label',
  type: 'symbol',
  source: 'kk-dam-source',
  'source-layer': 'dam',
  layout: {
    'text-field': ['get', 'W01_001'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.5],
    'text-size': ['interpolate', ['linear'], ['zoom'], 8, 12, 12, 16],
  },
  paint: {
    'text-color': '#fff',
    'text-halo-color': 'rgba(0,0,100,0.8)',
    'text-halo-width': 2,
  },
  minzoom: 8,
}
// W09 湖沼
const kkKoshoSource = {
  id: 'kk-kosho-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kosho.pmtiles' },
}
const kkKoshoFill = {
  id: 'oh-kk-kosho',
  type: 'fill',
  source: 'kk-kosho-source',
  'source-layer': 'kosho',
  paint: { 'fill-color': 'rgba(30, 100, 200, 0.6)' },
}
const kkKoshoLine = {
  id: 'oh-kk-kosho-line',
  type: 'line',
  source: 'kk-kosho-source',
  'source-layer': 'kosho',
  paint: {
    'line-color': '#0D47A1',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 0.5, 14, 2],
  },
}
// L01 地価公示
const kkChikaKojiSource = {
  id: 'kk-chika-koji-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/chika-koji.pmtiles' },
}
const kkChikaKojiCircle = {
  id: 'oh-kk-chika-koji',
  type: 'circle',
  source: 'kk-chika-koji-source',
  'source-layer': 'chika-koji',
  paint: {
    'circle-color': [
      'interpolate', ['linear'], ['get', 'L01_008'],
      10000, '#FFEB3B',
      50000, '#FFC107',
      100000, '#FF9800',
      250000, '#FF5722',
      500000, '#F44336',
      1000000, '#D32F2F',
      5000000, '#B71C1C',
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
  },
}
const kkChikaKojiLabel = {
  id: 'oh-kk-chika-koji-label',
  type: 'symbol',
  source: 'kk-chika-koji-source',
  'source-layer': 'chika-koji',
  layout: {
    'text-field': ['concat', ['to-string', ['/', ['get', 'L01_008'], 10000]], '万'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.5],
    'text-size': ['interpolate', ['linear'], ['zoom'], 11, 12, 15, 16],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 11,
}
// L02 都道府県地価調査
const kkChikaChosaSource = {
  id: 'kk-chika-chosa-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/chika-chosa.pmtiles' },
}
const kkChikaChosaCircle = {
  id: 'oh-kk-chika-chosa',
  type: 'circle',
  source: 'kk-chika-chosa-source',
  'source-layer': 'chika-chosa',
  paint: {
    'circle-color': [
      'interpolate', ['linear'], ['get', 'L02_006'],
      10000, '#CE93D8',
      50000, '#AB47BC',
      100000, '#8E24AA',
      250000, '#6A1B9A',
      500000, '#4A148C',
      1000000, '#311B92',
      5000000, '#1A237E',
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
  },
}
const kkChikaChosaLabel = {
  id: 'oh-kk-chika-chosa-label',
  type: 'symbol',
  source: 'kk-chika-chosa-source',
  'source-layer': 'chika-chosa',
  layout: {
    'text-field': ['concat', ['to-string', ['/', ['get', 'L02_006'], 10000]], '万'],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.5],
    'text-size': ['interpolate', ['linear'], ['zoom'], 11, 12, 15, 16],
  },
  paint: {
    'text-color': '#333',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 11,
}
// A54 大規模盛土造成地
const kkMoridoSource = {
  id: 'kk-morido-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/morido.pmtiles' },
}
const kkMoridoFill = {
  id: 'oh-kk-morido',
  type: 'fill',
  source: 'kk-morido-source',
  'source-layer': 'morido',
  paint: {
    'fill-color': [
      'match', ['to-string', ['get', 'fillSlopeClassification']],
      '1', 'rgba(179,253,165,0.7)',
      '2', 'rgba(155,155,248,0.7)',
      'rgba(0,255,0,0.7)',
    ],
  },
}
const kkMoridoLine = {
  id: 'oh-kk-morido-line',
  type: 'line',
  source: 'kk-morido-source',
  'source-layer': 'morido',
  paint: {
    'line-color': '#333',
    'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.3, 14, 1.5],
  },
}
// C23 海岸線
const kkKaiganSource = {
  id: 'kk-kaigan-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kaigan.pmtiles' },
}
const kkKaiganLine = {
  id: 'oh-kk-kaigan',
  type: 'line',
  source: 'kk-kaigan-source',
  'source-layer': 'kaigan',
  paint: {
    'line-color': '#0277BD',
    'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 10, 2, 14, 4],
  },
}
// P23 海岸保全施設
const kkKaiganHozenSource = {
  id: 'kk-kaigan-hozen-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kaigan-hozen.pmtiles' },
}
const kkKaiganHozenCircle = {
  id: 'oh-kk-kaigan-hozen',
  type: 'circle',
  source: 'kk-kaigan-hozen-source',
  'source-layer': 'kaigan-hozen',
  paint: {
    'circle-color': '#E65100',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
  },
}
// G08 低位地帯
const kkTeiiChitaiSource = {
  id: 'kk-teii-chitai-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/teii-chitai.pmtiles' },
}
const kkTeiiChitaiFill = {
  id: 'oh-kk-teii-chitai',
  type: 'fill',
  source: 'kk-teii-chitai-source',
  'source-layer': 'teii-chitai',
  paint: {
    'fill-color': [
      'interpolate', ['linear'], ['get', 'G08_002'],
      0, 'rgba(255,255,200,0.6)',
      2, 'rgba(255,230,150,0.6)',
      5, 'rgba(255,200,100,0.6)',
      10, 'rgba(255,150,50,0.6)',
      20, 'rgba(255,100,0,0.6)',
    ],
  },
}
const kkTeiiChitaiLine = {
  id: 'oh-kk-teii-chitai-line',
  type: 'line',
  source: 'kk-teii-chitai-source',
  'source-layer': 'teii-chitai',
  paint: {
    'line-color': '#BF360C',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 0.5, 14, 1.5],
  },
}
// A12 農業地域
const kkNogyoSource = {
  id: 'kk-nogyo-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/nogyo-chiiki.pmtiles' },
}
const kkNogyoFill = {
  id: 'oh-kk-nogyo',
  type: 'fill',
  source: 'kk-nogyo-source',
  'source-layer': 'nogyo-chiiki',
  paint: {
    'fill-color': 'rgba(139, 195, 74, 0.5)',
  },
}
const kkNogyoLine = {
  id: 'oh-kk-nogyo-line',
  type: 'line',
  source: 'kk-nogyo-source',
  'source-layer': 'nogyo-chiiki',
  paint: {
    'line-color': '#558B2F',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 0.5, 14, 1.5],
  },
}
// A13 森林地域
const kkShinrinSource = {
  id: 'kk-shinrin-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shinrin.pmtiles' },
}
const kkShinrinFill = {
  id: 'oh-kk-shinrin',
  type: 'fill',
  source: 'kk-shinrin-source',
  'source-layer': 'shinrin',
  paint: {
    'fill-color': 'rgba(27, 94, 32, 0.5)',
  },
}
const kkShinrinLine = {
  id: 'oh-kk-shinrin-line',
  type: 'line',
  source: 'kk-shinrin-source',
  'source-layer': 'shinrin',
  paint: {
    'line-color': '#1B5E20',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 0.5, 14, 1.5],
  },
}
// A45 国有林野
const kkKokuyurinSource = {
  id: 'kk-kokuyurin-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kokuyurin.pmtiles' },
}
const kkKokuyurinFill = {
  id: 'oh-kk-kokuyurin',
  type: 'fill',
  source: 'kk-kokuyurin-source',
  'source-layer': 'kokuyurin',
  paint: { 'fill-color': 'rgba(46, 125, 50, 0.5)' },
}
const kkKokuyurinLine = {
  id: 'oh-kk-kokuyurin-line',
  type: 'line',
  source: 'kk-kokuyurin-source',
  'source-layer': 'kokuyurin',
  paint: {
    'line-color': '#2E7D32',
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0, 10, 0.5, 14, 1.5],
  },
}
// W05 河川
const kkKasenSource = {
  id: 'kk-kasen-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kasen.pmtiles' },
}
const kkKasenLine = {
  id: 'oh-kk-kasen',
  type: 'line',
  source: 'kk-kasen-source',
  'source-layer': 'kasen',
  paint: {
    'line-color': '#1976D2',
    'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.5, 10, 2, 14, 4],
  },
}
const kkKasenLabel = {
  id: 'oh-kk-kasen-label',
  type: 'symbol',
  source: 'kk-kasen-source',
  'source-layer': 'kasen',
  layout: {
    'text-field': ['get', 'W05_004'],
    'text-font': ['NotoSansJP-Regular'],
    'symbol-placement': 'line',
    'text-size': ['interpolate', ['linear'], ['zoom'], 9, 12, 13, 16],
  },
  paint: {
    'text-color': '#0D47A1',
    'text-halo-color': '#fff',
    'text-halo-width': 2,
  },
  minzoom: 9,
}
// 国土数値情報 - 2. 政策区域 > 行政地域 ===================================================================
// N03 行政区域
const kkGyoseiSource = {
  id: 'kk-gyosei-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/gyosei-kuiki.pmtiles' },
}
const kkGyoseiFill = {
  id: 'oh-kk-gyosei',
  type: 'fill',
  source: 'kk-gyosei-source',
  'source-layer': 'gyosei-kuiki',
  paint: { 'fill-color': 'rgba(100, 150, 200, 0.3)' },
}
const kkGyoseiLine = {
  id: 'oh-kk-gyosei-line',
  type: 'line',
  source: 'kk-gyosei-source',
  'source-layer': 'gyosei-kuiki',
  paint: { 'line-color': 'rgba(50, 100, 150, 0.7)', 'line-width': 1 },
}
// A16 DID人口集中地区
const kkDidSource = {
  id: 'kk-did-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/did.pmtiles' },
}
const kkDidFill = {
  id: 'oh-kk-did',
  type: 'fill',
  source: 'kk-did-source',
  'source-layer': 'did',
  paint: { 'fill-color': 'rgba(220, 50, 50, 0.3)' },
}
const kkDidLine = {
  id: 'oh-kk-did-line',
  type: 'line',
  source: 'kk-did-source',
  'source-layer': 'did',
  paint: { 'line-color': 'rgba(200, 30, 30, 0.7)', 'line-width': 1 },
}
// A27 小学校区
const kkShogakkokuSource = {
  id: 'kk-shogakkoku-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shogakko-ku.pmtiles' },
}
const kkShogakkokuFill = {
  id: 'oh-kk-shogakkoku',
  type: 'fill',
  source: 'kk-shogakkoku-source',
  'source-layer': 'shogakko-ku',
  paint: { 'fill-color': 'rgba(255, 180, 50, 0.3)' },
}
const kkShogakkokuLine = {
  id: 'oh-kk-shogakkoku-line',
  type: 'line',
  source: 'kk-shogakkoku-source',
  'source-layer': 'shogakko-ku',
  paint: { 'line-color': 'rgba(230, 150, 30, 0.7)', 'line-width': 1 },
}
// A32 中学校区
const kkChugakkokuSource = {
  id: 'kk-chugakkoku-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/chugakko-ku.pmtiles' },
}
const kkChugakkokuFill = {
  id: 'oh-kk-chugakkoku',
  type: 'fill',
  source: 'kk-chugakkoku-source',
  'source-layer': 'chugakko-ku',
  paint: { 'fill-color': 'rgba(50, 180, 100, 0.3)' },
}
const kkChugakkokuLine = {
  id: 'oh-kk-chugakkoku-line',
  type: 'line',
  source: 'kk-chugakkoku-source',
  'source-layer': 'chugakko-ku',
  paint: { 'line-color': 'rgba(30, 150, 80, 0.7)', 'line-width': 1 },
}
// A38 医療圏
const kkIryoukenSource = {
  id: 'kk-iryouken-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/iryouken.pmtiles' },
}
const kkIryoukenFill = {
  id: 'oh-kk-iryouken',
  type: 'fill',
  source: 'kk-iryouken-source',
  'source-layer': 'iryouken',
  paint: { 'fill-color': 'rgba(150, 50, 200, 0.3)' },
}
const kkIryoukenLine = {
  id: 'oh-kk-iryouken-line',
  type: 'line',
  source: 'kk-iryouken-source',
  'source-layer': 'iryouken',
  paint: { 'line-color': 'rgba(130, 30, 180, 0.7)', 'line-width': 1 },
}
// A35a 景観計画区域
const kkKeikanKeikakuSource = {
  id: 'kk-keikan-keikaku-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/keikan-keikaku.pmtiles' },
}
const kkKeikanKeikakuFill = {
  id: 'oh-kk-keikan-keikaku',
  type: 'fill',
  source: 'kk-keikan-keikaku-source',
  'source-layer': 'keikan-keikaku',
  paint: { 'fill-color': 'rgba(154, 205, 50, 0.3)' },
}
const kkKeikanKeikakuLine = {
  id: 'oh-kk-keikan-keikaku-line',
  type: 'line',
  source: 'kk-keikan-keikaku-source',
  'source-layer': 'keikan-keikaku',
  paint: { 'line-color': 'rgba(120, 170, 30, 0.7)', 'line-width': 1 },
}
// A35b 景観地区・準景観地区
const kkKeikanChikuSource = {
  id: 'kk-keikan-chiku-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/keikan-chiku.pmtiles' },
}
const kkKeikanChikuFill = {
  id: 'oh-kk-keikan-chiku',
  type: 'fill',
  source: 'kk-keikan-chiku-source',
  'source-layer': 'keikan-chiku',
  paint: { 'fill-color': 'rgba(34, 139, 34, 0.3)' },
}
const kkKeikanChikuLine = {
  id: 'oh-kk-keikan-chiku-line',
  type: 'line',
  source: 'kk-keikan-chiku-source',
  'source-layer': 'keikan-chiku',
  paint: { 'line-color': 'rgba(20, 120, 20, 0.7)', 'line-width': 1 },
}
// A35c 景観重要建造物・樹木
const kkKeikanKenzobutsuSource = {
  id: 'kk-keikan-kenzobutsu-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/keikan-kenzobutsu.pmtiles' },
}
const kkKeikanKenzobutsuCircle = {
  id: 'oh-kk-keikan-kenzobutsu',
  type: 'circle',
  source: 'kk-keikan-kenzobutsu-source',
  'source-layer': 'keikan-kenzobutsu',
  paint: { 'circle-radius': 5, 'circle-color': '#2e7d32', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' },
  minzoom: 5,
}
const kkKeikanKenzobutsuLabel = {
  id: 'oh-kk-keikan-kenzobutsu-label',
  type: 'symbol',
  source: 'kk-keikan-kenzobutsu-source',
  'source-layer': 'keikan-kenzobutsu',
  layout: {
    'text-field': ['coalesce', ['get', 'A35g_007'], ['get', 'A35h_007'], ''],
    'text-font': ['NotoSansJP-Regular'],
    'text-offset': [0, 1.5],
    'text-size': ['interpolate', ['linear'], ['zoom'], 10, 12, 14, 16],
  },
  paint: {
    'text-color': '#fff',
    'text-halo-color': 'rgba(30, 80, 30, 0.8)',
    'text-halo-width': 2,
  },
  minzoom: 10,
}
// A43 伝統的建造物群保存地区
const kkDentouHozonSource = {
  id: 'kk-dentou-hozon-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/dentou-hozon.pmtiles' },
}
const kkDentouHozonFill = {
  id: 'oh-kk-dentou-hozon',
  type: 'fill',
  source: 'kk-dentou-hozon-source',
  'source-layer': 'dentou-hozon',
  paint: { 'fill-color': 'rgba(255, 20, 147, 0.3)' },
}
const kkDentouHozonLine = {
  id: 'oh-kk-dentou-hozon-line',
  type: 'line',
  source: 'kk-dentou-hozon-source',
  'source-layer': 'dentou-hozon',
  paint: { 'line-color': 'rgba(220, 20, 130, 0.7)', 'line-width': 1 },
}
// A44 歴史的風致維持向上計画の重点地区
const kkRekishiFuuchiSource = {
  id: 'kk-rekishi-fuuchi-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/rekishi-fuuchi.pmtiles' },
}
const kkRekishiFuuchiFill = {
  id: 'oh-kk-rekishi-fuuchi',
  type: 'fill',
  source: 'kk-rekishi-fuuchi-source',
  'source-layer': 'rekishi-fuuchi',
  paint: { 'fill-color': 'rgba(139, 69, 19, 0.3)' },
}
const kkRekishiFuuchiLine = {
  id: 'oh-kk-rekishi-fuuchi-line',
  type: 'line',
  source: 'kk-rekishi-fuuchi-source',
  'source-layer': 'rekishi-fuuchi',
  paint: { 'line-color': 'rgba(120, 60, 15, 0.7)', 'line-width': 1 },
}
// 国土数値情報 - 2. 政策区域 > 都市計画決定情報 ===================================================================
// A09 都市地域
const kkToshiChiikiSource = {
  id: 'kk-toshi-chiiki-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/toshi-chiiki.pmtiles' },
}
const kkToshiChiikiFill = {
  id: 'oh-kk-toshi-chiiki',
  type: 'fill',
  source: 'kk-toshi-chiiki-source',
  'source-layer': 'toshi-chiiki',
  paint: {
    'fill-color': 'rgba(255, 165, 0, 0.3)',
  },
}
const kkToshiChiikiLine = {
  id: 'oh-kk-toshi-chiiki-line',
  type: 'line',
  source: 'kk-toshi-chiiki-source',
  'source-layer': 'toshi-chiiki',
  paint: { 'line-color': 'rgba(200, 120, 0, 0.7)', 'line-width': 1 },
}
// A29 用途地域
const kkYoutoChiikiSource = {
  id: 'kk-youto-chiiki-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/youto-chiiki.pmtiles' },
}
const kkYoutoChiikiFill = {
  id: 'oh-kk-youto-chiiki',
  type: 'fill',
  source: 'kk-youto-chiiki-source',
  'source-layer': 'youto-chiiki',
  paint: {
    'fill-color': [
      'match', ['to-string', ['get', 'A29_004']],
      '1', 'rgba(0, 168, 70, 0.5)',
      '2', 'rgba(104, 201, 142, 0.5)',
      '3', 'rgba(187, 219, 68, 0.5)',
      '4', 'rgba(197, 227, 128, 0.5)',
      '5', 'rgba(255, 255, 0, 0.5)',
      '6', 'rgba(255, 204, 102, 0.5)',
      '7', 'rgba(255, 153, 0, 0.5)',
      '8', 'rgba(255, 102, 204, 0.5)',
      '9', 'rgba(255, 0, 0, 0.5)',
      '10', 'rgba(204, 153, 255, 0.5)',
      '11', 'rgba(0, 204, 255, 0.5)',
      '12', 'rgba(0, 102, 255, 0.5)',
      '13', 'rgba(152, 251, 152, 0.5)',
      'rgba(200, 200, 200, 0.3)',
    ],
  },
}
const kkYoutoChiikiLine = {
  id: 'oh-kk-youto-chiiki-line',
  type: 'line',
  source: 'kk-youto-chiiki-source',
  'source-layer': 'youto-chiiki',
  paint: { 'line-color': 'rgba(100, 100, 100, 0.5)', 'line-width': 0.5 },
}
// A50 立地適正化計画区域
const kkRicchiTekiseikaSource = {
  id: 'kk-ricchi-tekiseika-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/ricchi-tekiseika.pmtiles' },
}
const kkRicchiTekiseikaFill = {
  id: 'oh-kk-ricchi-tekiseika',
  type: 'fill',
  source: 'kk-ricchi-tekiseika-source',
  'source-layer': 'ricchi-tekiseika',
  paint: {
    'fill-color': [
      'match', ['get', 'A50_006'],
      '1', 'rgba(255, 100, 100, 0.4)',
      '2', 'rgba(100, 100, 255, 0.4)',
      'rgba(150, 150, 150, 0.3)',
    ],
  },
}
const kkRicchiTekiseikaLine = {
  id: 'oh-kk-ricchi-tekiseika-line',
  type: 'line',
  source: 'kk-ricchi-tekiseika-source',
  'source-layer': 'ricchi-tekiseika',
  paint: { 'line-color': 'rgba(80, 80, 180, 0.7)', 'line-width': 1 },
}
// A42 歴史的風土保存区域
const kkRekishiFudoSource = {
  id: 'kk-rekishi-fudo-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/rekishi-fudo.pmtiles' },
}
const kkRekishiFudoFill = {
  id: 'oh-kk-rekishi-fudo',
  type: 'fill',
  source: 'kk-rekishi-fudo-source',
  'source-layer': 'rekishi-fudo',
  paint: { 'fill-color': 'rgba(160, 82, 45, 0.4)' },
}
const kkRekishiFudoLine = {
  id: 'oh-kk-rekishi-fudo-line',
  type: 'line',
  source: 'kk-rekishi-fudo-source',
  'source-layer': 'rekishi-fudo',
  paint: { 'line-color': 'rgba(139, 69, 19, 0.8)', 'line-width': 1.5 },
}
// P13 都市公園
const kkToshiKoenSource = {
  id: 'kk-toshi-koen-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/toshi-koen.pmtiles' },
}
const kkToshiKoenCircle = {
  id: 'oh-kk-toshi-koen',
  type: 'circle',
  source: 'kk-toshi-koen-source',
  'source-layer': 'toshi-koen',
  paint: {
    'circle-color': '#2E7D32',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 4, 14, 7],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
  },
  minzoom: 5,
}
// A55 都市計画決定情報（国土数値情報 R6）
// 1. 用途地域
const kkA55YoutoSource = {
  id: 'kk-a55-youto-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-youto.pmtiles' },
}
const kkA55YoutoFill = {
  id: 'oh-kk-a55-youto',
  type: 'fill',
  source: 'kk-a55-youto-source',
  'source-layer': 'a55-youto',
  paint: {
    'fill-color': [
      'match', ['get', 'YoutoCode'],
      1, 'rgba(0, 180, 0, 0.5)',
      2, 'rgba(50, 200, 50, 0.5)',
      3, 'rgba(100, 200, 100, 0.5)',
      4, 'rgba(150, 220, 150, 0.5)',
      5, 'rgba(200, 240, 200, 0.5)',
      6, 'rgba(230, 250, 230, 0.5)',
      7, 'rgba(255, 230, 180, 0.5)',
      8, 'rgba(180, 255, 180, 0.5)',
      9, 'rgba(255, 180, 180, 0.5)',
      10, 'rgba(255, 100, 100, 0.5)',
      11, 'rgba(200, 150, 255, 0.5)',
      12, 'rgba(150, 150, 200, 0.5)',
      13, 'rgba(100, 100, 180, 0.5)',
      'rgba(200, 200, 200, 0.3)',
    ],
  },
}
const kkA55YoutoLine = {
  id: 'oh-kk-a55-youto-line',
  type: 'line',
  source: 'kk-a55-youto-source',
  'source-layer': 'a55-youto',
  paint: { 'line-color': '#333', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0, 14, 1] },
}
// 2. 区域区分（線引き）
const kkA55SenbikiSource = {
  id: 'kk-a55-senbiki-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-senbiki.pmtiles' },
}
const kkA55SenbikiFill = {
  id: 'oh-kk-a55-senbiki',
  type: 'fill',
  source: 'kk-a55-senbiki-source',
  'source-layer': 'a55-senbiki',
  paint: {
    'fill-color': [
      'match', ['get', 'AreaCode'],
      22, 'rgba(255, 200, 150, 0.5)',
      23, 'rgba(150, 220, 150, 0.5)',
      'rgba(200, 200, 200, 0.3)',
    ],
  },
}
const kkA55SenbikiLine = {
  id: 'oh-kk-a55-senbiki-line',
  type: 'line',
  source: 'kk-a55-senbiki-source',
  'source-layer': 'a55-senbiki',
  paint: {
    'line-color': [
      'match', ['get', 'AreaCode'],
      22, 'rgba(200, 100, 0, 1)',
      23, 'rgba(0, 150, 0, 1)',
      'rgba(100, 100, 100, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2],
  },
}
// 3. 都市計画区域
const kkA55TokeiSource = {
  id: 'kk-a55-tokei-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-tokei.pmtiles' },
}
const kkA55TokeiFill = {
  id: 'oh-kk-a55-tokei',
  type: 'fill',
  source: 'kk-a55-tokei-source',
  'source-layer': 'a55-tokei',
  paint: { 'fill-color': 'rgba(255, 200, 200, 0.4)' },
}
const kkA55TokeiLine = {
  id: 'oh-kk-a55-tokei-line',
  type: 'line',
  source: 'kk-a55-tokei-source',
  'source-layer': 'a55-tokei',
  paint: { 'line-color': 'rgba(200, 0, 0, 0.8)', 'line-width': ['interpolate', ['linear'], ['zoom'], 8, 0.5, 14, 2] },
}
// 4. 防火・準防火地域
const kkA55BoukaSource = {
  id: 'kk-a55-bouka-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-bouka.pmtiles' },
}
const kkA55BoukaFill = {
  id: 'oh-kk-a55-bouka',
  type: 'fill',
  source: 'kk-a55-bouka-source',
  'source-layer': 'a55-bouka',
  paint: {
    'fill-color': [
      'match', ['get', 'AreaCode'],
      24, 'rgba(255, 0, 0, 0.4)',
      25, 'rgba(255, 150, 100, 0.4)',
      'rgba(255, 100, 100, 0.3)',
    ],
  },
}
const kkA55BoukaLine = {
  id: 'oh-kk-a55-bouka-line',
  type: 'line',
  source: 'kk-a55-bouka-source',
  'source-layer': 'a55-bouka',
  paint: {
    'line-color': [
      'match', ['get', 'AreaCode'],
      24, 'rgba(200, 0, 0, 1)',
      25, 'rgba(200, 100, 0, 1)',
      'rgba(200, 0, 0, 1)',
    ],
    'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5],
  },
}
// 5. 高度地区
const kkA55KoudotiSource = {
  id: 'kk-a55-koudoti-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-koudoti.pmtiles' },
}
const kkA55KoudotiFill = {
  id: 'oh-kk-a55-koudoti',
  type: 'fill',
  source: 'kk-a55-koudoti-source',
  'source-layer': 'a55-koudoti',
  paint: { 'fill-color': 'rgba(200, 180, 100, 0.4)' },
}
const kkA55KoudotiLine = {
  id: 'oh-kk-a55-koudoti-line',
  type: 'line',
  source: 'kk-a55-koudoti-source',
  'source-layer': 'a55-koudoti',
  paint: { 'line-color': 'rgba(180, 140, 50, 0.8)', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5] },
}
// 6. 高度利用地区
const kkA55KoudoriSource = {
  id: 'kk-a55-koudori-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-koudori.pmtiles' },
}
const kkA55KoudoriFill = {
  id: 'oh-kk-a55-koudori',
  type: 'fill',
  source: 'kk-a55-koudori-source',
  'source-layer': 'a55-koudori',
  paint: { 'fill-color': 'rgba(255, 140, 0, 0.4)' },
}
const kkA55KoudoriLine = {
  id: 'oh-kk-a55-koudori-line',
  type: 'line',
  source: 'kk-a55-koudori-source',
  'source-layer': 'a55-koudori',
  paint: { 'line-color': 'rgba(200, 100, 0, 0.8)', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5] },
}
// 7. 地区計画
const kkA55ChikukeiSource = {
  id: 'kk-a55-chikukei-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-chikukei.pmtiles' },
}
const kkA55ChikukeiFill = {
  id: 'oh-kk-a55-chikukei',
  type: 'fill',
  source: 'kk-a55-chikukei-source',
  'source-layer': 'a55-chikukei',
  paint: { 'fill-color': 'rgba(100, 150, 255, 0.4)' },
}
const kkA55ChikukeiLine = {
  id: 'oh-kk-a55-chikukei-line',
  type: 'line',
  source: 'kk-a55-chikukei-source',
  'source-layer': 'a55-chikukei',
  paint: { 'line-color': 'rgba(0, 100, 200, 0.8)', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5] },
}
// 8. 特別用途地区
const kkA55TkbtSource = {
  id: 'kk-a55-tkbt-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/a55-tkbt.pmtiles' },
}
const kkA55TkbtFill = {
  id: 'oh-kk-a55-tkbt',
  type: 'fill',
  source: 'kk-a55-tkbt-source',
  'source-layer': 'a55-tkbt',
  paint: {
    'fill-color': [
      'match', ['get', 'YoutoCode'],
      1, 'rgba(150, 150, 200, 0.5)',
      2, 'rgba(100, 150, 255, 0.5)',
      3, 'rgba(255, 150, 150, 0.5)',
      4, 'rgba(200, 200, 150, 0.5)',
      5, 'rgba(150, 200, 200, 0.5)',
      6, 'rgba(255, 200, 150, 0.5)',
      7, 'rgba(255, 220, 180, 0.5)',
      8, 'rgba(180, 180, 220, 0.5)',
      9, 'rgba(150, 220, 150, 0.5)',
      10, 'rgba(255, 100, 100, 0.5)',
      11, 'rgba(100, 180, 255, 0.5)',
      'rgba(180, 150, 200, 0.5)',
    ],
  },
}
const kkA55TkbtLine = {
  id: 'oh-kk-a55-tkbt-line',
  type: 'line',
  source: 'kk-a55-tkbt-source',
  'source-layer': 'a55-tkbt',
  paint: { 'line-color': 'rgba(100, 50, 150, 0.8)', 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.5, 16, 1.5] },
}
// 国土数値情報 - 2. 政策区域 > 大都市圏・条件不利地域 ===================================================================
// A03 三大都市圏計画区域
const kkSandaiToshikenSource = {
  id: 'kk-sandai-toshiken-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/sandai-toshiken.pmtiles' },
}
const kkSandaiToshikenFill = {
  id: 'oh-kk-sandai-toshiken',
  type: 'fill',
  source: 'kk-sandai-toshiken-source',
  'source-layer': 'sandai-toshiken',
  paint: { 'fill-color': 'rgba(255, 140, 0, 0.3)' },
}
const kkSandaiToshikenLine = {
  id: 'oh-kk-sandai-toshiken-line',
  type: 'line',
  source: 'kk-sandai-toshiken-source',
  'source-layer': 'sandai-toshiken',
  paint: { 'line-color': 'rgba(200, 100, 0, 0.7)', 'line-width': 1 },
}
// A17 過疎地域
const kkKasoChiikiSource = {
  id: 'kk-kaso-chiiki-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kaso-chiiki.pmtiles' },
}
const kkKasoChiikiFill = {
  id: 'oh-kk-kaso-chiiki',
  type: 'fill',
  source: 'kk-kaso-chiiki-source',
  'source-layer': 'kaso-chiiki',
  paint: {
    'fill-color': [
      'match', ['get', 'A17_009'],
      1, 'rgba(255, 100, 100, 0.4)',
      2, 'rgba(255, 180, 100, 0.4)',
      3, 'rgba(255, 220, 150, 0.4)',
      'rgba(200, 200, 200, 0.3)',
    ],
  },
}
const kkKasoChiikiLine = {
  id: 'oh-kk-kaso-chiiki-line',
  type: 'line',
  source: 'kk-kaso-chiiki-source',
  'source-layer': 'kaso-chiiki',
  paint: { 'line-color': 'rgba(200, 50, 50, 0.6)', 'line-width': 0.5 },
}
// A24 振興山村
const kkShinkoSansonSource = {
  id: 'kk-shinko-sanson-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shinko-sanson.pmtiles' },
}
const kkShinkoSansonFill = {
  id: 'oh-kk-shinko-sanson',
  type: 'fill',
  source: 'kk-shinko-sanson-source',
  'source-layer': 'shinko-sanson',
  paint: { 'fill-color': 'rgba(34, 139, 34, 0.3)' },
}
const kkShinkoSansonLine = {
  id: 'oh-kk-shinko-sanson-line',
  type: 'line',
  source: 'kk-shinko-sanson-source',
  'source-layer': 'shinko-sanson',
  paint: { 'line-color': 'rgba(0, 100, 0, 0.6)', 'line-width': 0.5 },
}
// A25 特定農山村地域
const kkTokuteiNosanSource = {
  id: 'kk-tokutei-nosan-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/tokutei-nosan.pmtiles' },
}
const kkTokuteiNosanFill = {
  id: 'oh-kk-tokutei-nosan',
  type: 'fill',
  source: 'kk-tokutei-nosan-source',
  'source-layer': 'tokutei-nosan',
  paint: { 'fill-color': 'rgba(139, 119, 42, 0.3)' },
}
const kkTokuteiNosanLine = {
  id: 'oh-kk-tokutei-nosan-line',
  type: 'line',
  source: 'kk-tokutei-nosan-source',
  'source-layer': 'tokutei-nosan',
  paint: { 'line-color': 'rgba(100, 80, 20, 0.6)', 'line-width': 0.5 },
}
// A19 離島振興対策実施地域
const kkRitoShinkoSource = {
  id: 'kk-rito-shinko-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/rito-shinko.pmtiles' },
}
const kkRitoShinkoFill = {
  id: 'oh-kk-rito-shinko',
  type: 'fill',
  source: 'kk-rito-shinko-source',
  'source-layer': 'rito-shinko',
  paint: { 'fill-color': 'rgba(0, 150, 200, 0.3)' },
}
const kkRitoShinkoLine = {
  id: 'oh-kk-rito-shinko-line',
  type: 'line',
  source: 'kk-rito-shinko-source',
  'source-layer': 'rito-shinko',
  paint: { 'line-color': 'rgba(0, 100, 150, 0.7)', 'line-width': 1 },
}
// A18 半島振興対策実施地域
const kkHantoShinkoSource = {
  id: 'kk-hanto-shinko-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hanto-shinko.pmtiles' },
}
const kkHantoShinkoFill = {
  id: 'oh-kk-hanto-shinko',
  type: 'fill',
  source: 'kk-hanto-shinko-source',
  'source-layer': 'hanto-shinko',
  paint: { 'fill-color': 'rgba(100, 149, 237, 0.3)' },
}
const kkHantoShinkoLine = {
  id: 'oh-kk-hanto-shinko-line',
  type: 'line',
  source: 'kk-hanto-shinko-source',
  'source-layer': 'hanto-shinko',
  paint: { 'line-color': 'rgba(65, 105, 225, 0.7)', 'line-width': 1 },
}
// A22 豪雪地帯
const kkGosetsuChitaiSource = {
  id: 'kk-gosetsu-chitai-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/gosetsu-chitai.pmtiles' },
}
const kkGosetsuChitaiFill = {
  id: 'oh-kk-gosetsu-chitai',
  type: 'fill',
  source: 'kk-gosetsu-chitai-source',
  'source-layer': 'gosetsu-chitai',
  paint: {
    'fill-color': [
      'match', ['get', 'A22_009'],
      '1', 'rgba(135, 206, 235, 0.4)',
      '2', 'rgba(70, 130, 180, 0.4)',
      'rgba(173, 216, 230, 0.3)',
    ],
  },
}
const kkGosetsuChitaiLine = {
  id: 'oh-kk-gosetsu-chitai-line',
  type: 'line',
  source: 'kk-gosetsu-chitai-source',
  'source-layer': 'gosetsu-chitai',
  paint: { 'line-color': 'rgba(70, 130, 180, 0.6)', 'line-width': 0.5 },
}
// A23 特殊土壌地帯
const kkTokushuDojoSource = {
  id: 'kk-tokushu-dojo-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/tokushu-dojo.pmtiles' },
}
const kkTokushuDojoFill = {
  id: 'oh-kk-tokushu-dojo',
  type: 'fill',
  source: 'kk-tokushu-dojo-source',
  'source-layer': 'tokushu-dojo',
  paint: {
    'fill-color': [
      'match', ['to-string', ['get', 'A23_004']],
      '1', 'rgba(169, 169, 169, 0.35)',
      '2', 'rgba(210, 180, 140, 0.35)',
      '3', 'rgba(160, 82, 45, 0.35)',
      '4', 'rgba(178, 34, 34, 0.35)',
      '5', 'rgba(205, 133, 63, 0.35)',
      '6', 'rgba(139, 119, 101, 0.35)',
      '7', 'rgba(85, 107, 47, 0.35)',
      '8', 'rgba(107, 142, 35, 0.35)',
      'rgba(160, 82, 45, 0.3)',
    ],
  },
}
const kkTokushuDojoLine = {
  id: 'oh-kk-tokushu-dojo-line',
  type: 'line',
  source: 'kk-tokushu-dojo-source',
  'source-layer': 'tokushu-dojo',
  paint: { 'line-color': 'rgba(139, 69, 19, 0.6)', 'line-width': 0.5 },
}
// A37 半島循環道路
const kkHantoDouroSource = {
  id: 'kk-hanto-douro-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hanto-douro.pmtiles' },
}
const kkHantoDouroLine2 = {
  id: 'oh-kk-hanto-douro',
  type: 'line',
  source: 'kk-hanto-douro-source',
  'source-layer': 'hanto-douro',
  paint: { 'line-color': 'rgba(255, 69, 0, 0.7)', 'line-width': 2 },
}
// A39 密集市街地
const kkMisshuShigaichiSource = {
  id: 'kk-misshu-shigaichi-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/misshu-shigaichi.pmtiles' },
}
const kkMisshuShigaichiFill = {
  id: 'oh-kk-misshu-shigaichi',
  type: 'fill',
  source: 'kk-misshu-shigaichi-source',
  'source-layer': 'misshu-shigaichi',
  paint: { 'fill-color': 'rgba(220, 20, 60, 0.4)' },
}
const kkMisshuShigaichiLine = {
  id: 'oh-kk-misshu-shigaichi-line',
  type: 'line',
  source: 'kk-misshu-shigaichi-source',
  'source-layer': 'misshu-shigaichi',
  paint: { 'line-color': 'rgba(178, 34, 34, 0.8)', 'line-width': 1 },
}
// A20 奄美群島 - データ提供終了（サーバーから削除済み）
// A21 小笠原諸島 - データ提供終了（サーバーから削除済み）
// A19s 離島振興統計（H22）--------------------------------------------------------------------------------------------
const kkRitoTokeiSource = { id: 'kk-rito-tokei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/rito-tokei.pmtiles' } }
const kkRitoTokeiFill = { id: 'oh-kk-rito-tokei', type: 'fill', source: 'kk-rito-tokei-source', 'source-layer': 'rito-tokei', paint: { 'fill-color': 'rgba(0, 150, 136, 0.3)', 'fill-outline-color': 'rgba(0, 150, 136, 0.8)' } }
const kkRitoTokeiLine = { id: 'oh-kk-rito-tokei-line', type: 'line', source: 'kk-rito-tokei-source', 'source-layer': 'rito-tokei', paint: { 'line-color': 'rgba(0, 150, 136, 0.8)', 'line-width': 1 } }
// A22s 豪雪地帯統計（H22）--------------------------------------------------------------------------------------------
const kkGosetsuTokeiSource = { id: 'kk-gosetsu-tokei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/gosetsu-tokei.pmtiles' } }
const kkGosetsuTokeiFill = { id: 'oh-kk-gosetsu-tokei', type: 'fill', source: 'kk-gosetsu-tokei-source', 'source-layer': 'gosetsu-tokei', paint: { 'fill-color': 'rgba(33, 150, 243, 0.3)', 'fill-outline-color': 'rgba(33, 150, 243, 0.8)' } }
const kkGosetsuTokeiLine = { id: 'oh-kk-gosetsu-tokei-line', type: 'line', source: 'kk-gosetsu-tokei-source', 'source-layer': 'gosetsu-tokei', paint: { 'line-color': 'rgba(33, 150, 243, 0.8)', 'line-width': 1 } }
// A18s-a 半島振興統計（H22）--------------------------------------------------------------------------------------------
const kkHantoTokeiSource = { id: 'kk-hanto-tokei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hanto-tokei.pmtiles' } }
const kkHantoTokeiFill = { id: 'oh-kk-hanto-tokei', type: 'fill', source: 'kk-hanto-tokei-source', 'source-layer': 'hanto-tokei', paint: { 'fill-color': 'rgba(255, 152, 0, 0.3)', 'fill-outline-color': 'rgba(255, 152, 0, 0.8)' } }
const kkHantoTokeiLine = { id: 'oh-kk-hanto-tokei-line', type: 'line', source: 'kk-hanto-tokei-source', 'source-layer': 'hanto-tokei', paint: { 'line-color': 'rgba(255, 152, 0, 0.8)', 'line-width': 1 } }
// A20s 奄美群島統計（H22）--------------------------------------------------------------------------------------------
const kkAmamiTokeiSource = { id: 'kk-amami-tokei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/amami-tokei.pmtiles' } }
const kkAmamiTokeiFill = { id: 'oh-kk-amami-tokei', type: 'fill', source: 'kk-amami-tokei-source', 'source-layer': 'amami-tokei', paint: { 'fill-color': 'rgba(233, 30, 99, 0.3)', 'fill-outline-color': 'rgba(233, 30, 99, 0.8)' } }
const kkAmamiTokeiLine = { id: 'oh-kk-amami-tokei-line', type: 'line', source: 'kk-amami-tokei-source', 'source-layer': 'amami-tokei', paint: { 'line-color': 'rgba(233, 30, 99, 0.8)', 'line-width': 1 } }
// A21s 小笠原諸島統計（H22）--------------------------------------------------------------------------------------------
const kkOgasawaraTokeiSource = { id: 'kk-ogasawara-tokei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/ogasawara-tokei.pmtiles' } }
const kkOgasawaraTokeiFill = { id: 'oh-kk-ogasawara-tokei', type: 'fill', source: 'kk-ogasawara-tokei-source', 'source-layer': 'ogasawara-tokei', paint: { 'fill-color': 'rgba(76, 175, 80, 0.3)', 'fill-outline-color': 'rgba(76, 175, 80, 0.8)' } }
const kkOgasawaraTokeiLine = { id: 'oh-kk-ogasawara-tokei-line', type: 'line', source: 'kk-ogasawara-tokei-source', 'source-layer': 'ogasawara-tokei', paint: { 'line-color': 'rgba(76, 175, 80, 0.8)', 'line-width': 1 } }
// 国土数値情報 - 2. 政策区域 > 災害・防災 ===================================================================
// P20 避難施設
const kkHinanShisetsuSource = {
  id: 'kk-hinan-shisetsu-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hinan-shisetsu.pmtiles' },
}
const kkHinanShisetsuCircle = {
  id: 'oh-kk-hinan-shisetsu',
  type: 'circle',
  source: 'kk-hinan-shisetsu-source',
  'source-layer': 'hinan-shisetsu',
  paint: {
    'circle-color': '#1B5E20',
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 1, 10, 3, 14, 6],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 0.5,
  },
  minzoom: 8,
}
// 指定避難所（地理院）
const kkHinanJoSource = {
  id: 'kk-hinan-jo-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hinan-jo.pmtiles' },
}
const kkHinanJoCircle = {
  id: 'oh-kk-hinan-jo',
  type: 'symbol',
  source: 'kk-hinan-jo-source',
  'source-layer': 'hinan-jo',
  layout: {
    'icon-image': 'hinan-icon',
    'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.15, 10, 0.3, 14, 0.7],
    'icon-allow-overlap': true,
  },
  minzoom: 8,
}
const kkHinanJoLabel = { id: 'oh-kk-hinan-jo-label', type: 'symbol', source: 'kk-hinan-jo-source', 'source-layer': 'hinan-jo', layout: { 'text-field': ['get', '施設・場所名'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(21,101,192,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
// 指定緊急避難場所（地理院）
const kkHinanBashoSource = {
  id: 'kk-hinan-basho-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hinan-basho.pmtiles' },
}
const kkHinanBashoCircle = {
  id: 'oh-kk-hinan-basho',
  type: 'symbol',
  source: 'kk-hinan-basho-source',
  'source-layer': 'hinan-basho',
  layout: {
    'icon-image': 'skhb-icon',
    'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.15, 10, 0.3, 14, 0.7],
    'icon-allow-overlap': true,
  },
  minzoom: 8,
}
const kkHinanBashoLabel = { id: 'oh-kk-hinan-basho-label', type: 'symbol', source: 'kk-hinan-basho-source', 'source-layer': 'hinan-basho', layout: { 'text-field': ['get', '施設・場所名'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(46,125,50,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
// A30b 竜巻等の突風
const kkTatsumakiSource = {
  id: 'kk-tatsumaki-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/tatsumaki.pmtiles' },
}
const kkTatsumakiCircle = {
  id: 'oh-kk-tatsumaki',
  type: 'circle',
  source: 'kk-tatsumaki-source',
  'source-layer': 'tatsumaki',
  paint: {
    'circle-color': [
      'match', ['to-string', ['get', 'A30b_002']],
      '1', '#E65100',
      '2', '#1565C0',
      '3', '#78909C',
      '#E65100',
    ],
    'circle-radius': ['interpolate', ['linear'], ['zoom'], 4, 2, 10, 5, 14, 8],
    'circle-stroke-color': '#fff',
    'circle-stroke-width': 1,
  },
  minzoom: 5,
}
// A46 地すべり防止区域
const kkJisuberiSource = {
  id: 'kk-jisuberi-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/jisuberi.pmtiles' },
}
const kkJisuberiFill = {
  id: 'oh-kk-jisuberi',
  type: 'fill',
  source: 'kk-jisuberi-source',
  'source-layer': 'jisuberi',
  paint: { 'fill-color': 'rgba(139, 90, 43, 0.4)' },
}
const kkJisuberiLine = {
  id: 'oh-kk-jisuberi-line',
  type: 'line',
  source: 'kk-jisuberi-source',
  'source-layer': 'jisuberi',
  paint: { 'line-color': 'rgba(139, 69, 19, 0.8)', 'line-width': 1 },
}
// A47 急傾斜地崩壊危険区域
const kkKyukeishaSource = {
  id: 'kk-kyukeisha-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kyukeisha.pmtiles' },
}
const kkKyukeishaFill = {
  id: 'oh-kk-kyukeisha',
  type: 'fill',
  source: 'kk-kyukeisha-source',
  'source-layer': 'kyukeisha',
  paint: { 'fill-color': 'rgba(178, 34, 34, 0.4)' },
}
const kkKyukeishaLine = {
  id: 'oh-kk-kyukeisha-line',
  type: 'line',
  source: 'kk-kyukeisha-source',
  'source-layer': 'kyukeisha',
  paint: { 'line-color': 'rgba(139, 0, 0, 0.8)', 'line-width': 1 },
}
// A48 災害危険区域
const kkSaigaiKikenSource = {
  id: 'kk-saigai-kiken-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/saigai-kiken.pmtiles' },
}
const kkSaigaiKikenFill = {
  id: 'oh-kk-saigai-kiken',
  type: 'fill',
  source: 'kk-saigai-kiken-source',
  'source-layer': 'saigai-kiken',
  paint: {
    'fill-color': [
      'match', ['to-string', ['get', 'A48_007']],
      '1', 'rgba(0, 0, 200, 0.35)',
      '2', 'rgba(0, 100, 180, 0.35)',
      '3', 'rgba(30, 144, 255, 0.35)',
      '4', 'rgba(178, 34, 34, 0.35)',
      '5', 'rgba(139, 90, 43, 0.35)',
      '6', 'rgba(100, 149, 237, 0.35)',
      '7', 'rgba(128, 128, 128, 0.35)',
      'rgba(255, 0, 0, 0.3)',
    ],
  },
}
const kkSaigaiKikenLine = {
  id: 'oh-kk-saigai-kiken-line',
  type: 'line',
  source: 'kk-saigai-kiken-source',
  'source-layer': 'saigai-kiken',
  paint: { 'line-color': 'rgba(200, 0, 0, 0.8)', 'line-width': 1 },
}
// A52 砂防指定地
const kkSaboShiteichiSource = {
  id: 'kk-sabo-shiteichi-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/sabo-shiteichi.pmtiles' },
}
const kkSaboShiteichiFill = {
  id: 'oh-kk-sabo-shiteichi',
  type: 'fill',
  source: 'kk-sabo-shiteichi-source',
  'source-layer': 'sabo-shiteichi',
  paint: { 'fill-color': 'rgba(205, 133, 63, 0.4)' },
}
const kkSaboShiteichiLine = {
  id: 'oh-kk-sabo-shiteichi-line',
  type: 'line',
  source: 'kk-sabo-shiteichi-source',
  'source-layer': 'sabo-shiteichi',
  paint: { 'line-color': 'rgba(160, 82, 45, 0.8)', 'line-width': 1 },
}
// A40 津波浸水想定
const kkTsunamiShinsuiSource = {
  id: 'kk-tsunami-shinsui-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/tsunami-shinsui.pmtiles' },
}
const kkTsunamiShinsuiFill = {
  id: 'oh-kk-tsunami-shinsui',
  type: 'fill',
  source: 'kk-tsunami-shinsui-source',
  'source-layer': 'tsunami-shinsui',
  paint: { 'fill-color': _floodDepthStringColor('A40_003') },
}
const kkTsunamiShinsuiLine = {
  id: 'oh-kk-tsunami-shinsui-line',
  type: 'line',
  source: 'kk-tsunami-shinsui-source',
  'source-layer': 'tsunami-shinsui',
  paint: { 'line-color': 'rgba(0, 0, 0, 0)', 'line-width': 1 },
}
// A49 高潮浸水想定区域
const kkTakashioShinsuiSource = {
  id: 'kk-takashio-shinsui-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/takashio-shinsui.pmtiles' },
}
const kkTakashioShinsuiFill = {
  id: 'oh-kk-takashio-shinsui',
  type: 'fill',
  source: 'kk-takashio-shinsui-source',
  'source-layer': 'takashio-shinsui',
  paint: { 'fill-color': _floodDepthStringColor('A49_003') },
}
const kkTakashioShinsuiLine = {
  id: 'oh-kk-takashio-shinsui-line',
  type: 'line',
  source: 'kk-takashio-shinsui-source',
  'source-layer': 'takashio-shinsui',
  paint: { 'line-color': 'rgba(0, 0, 0, 0)', 'line-width': 1 },
}
// A51 雨水出水浸水想定
const kkUsuiShinsuiSource = {
  id: 'kk-usui-shinsui-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/usui-shinsui.pmtiles' },
}
const kkUsuiShinsuiFill = {
  id: 'oh-kk-usui-shinsui',
  type: 'fill',
  source: 'kk-usui-shinsui-source',
  'source-layer': 'usui-shinsui',
  paint: { 'fill-color': _floodDepthStringColor('A51_005') },
}
const kkUsuiShinsuiLine = {
  id: 'oh-kk-usui-shinsui-line',
  type: 'line',
  source: 'kk-usui-shinsui-source',
  'source-layer': 'usui-shinsui',
  paint: { 'line-color': 'rgba(0, 0, 0, 0)', 'line-width': 1 },
}
// A33 土砂災害警戒区域
const kkDoshaSaigaiSource = {
  id: 'kk-dosha-saigai-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/dosha-saigai.pmtiles' },
}
const kkDoshaSaigaiFill = {
  id: 'oh-kk-dosha-saigai',
  type: 'fill',
  source: 'kk-dosha-saigai-source',
  'source-layer': 'dosha-saigai',
  paint: {
    'fill-color': [
      'match', ['to-string', ['get', 'A33_002']],
      '1', 'rgba(255, 200, 0, 0.4)',
      '2', 'rgba(255, 50, 50, 0.4)',
      'rgba(255, 165, 0, 0.4)',
    ],
  },
}
const kkDoshaSaigaiLine = {
  id: 'oh-kk-dosha-saigai-line',
  type: 'line',
  source: 'kk-dosha-saigai-source',
  'source-layer': 'dosha-saigai',
  paint: {
    'line-color': [
      'match', ['to-string', ['get', 'A33_002']],
      '1', 'rgba(180, 150, 0, 0.8)',
      '2', 'rgba(200, 0, 0, 0.8)',
      'rgba(200, 120, 0, 0.8)',
    ],
    'line-width': 1,
  },
}
// A31a 洪水浸水想定区域
const kkKozuiShinsuiSource = {
  id: 'kk-kozui-shinsui-source',
  obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-shinsui.pmtiles' },
}
const kkKozuiShinsuiFill = {
  id: 'oh-kk-kozui-shinsui',
  type: 'fill',
  source: 'kk-kozui-shinsui-source',
  'source-layer': 'kozui-shinsui',
  paint: {
    // A31_001 = 2桁コード（十の位=想定規模, 一の位=浸水ランク）→ %10 でランク抽出
    'fill-color': [
      'match', ['%', ['to-number', ['coalesce', ['get', 'A31_001'], 0]], 10],
      1, 'rgba(247, 245, 169, 0.7)',
      2, 'rgba(255, 216, 192, 0.7)',
      3, 'rgba(255, 183, 183, 0.7)',
      4, 'rgba(255, 145, 145, 0.7)',
      5, 'rgba(242, 133, 201, 0.7)',
      6, 'rgba(220, 122, 220, 0.7)',
      7, 'rgba(220, 122, 220, 0.7)',
      'rgba(200, 200, 200, 0.5)',
    ],
  },
}
const kkKozuiShinsuiLine = {
  id: 'oh-kk-kozui-shinsui-line',
  type: 'line',
  source: 'kk-kozui-shinsui-source',
  'source-layer': 'kozui-shinsui',
  paint: { 'line-color': 'rgba(0, 0, 200, 0.6)', 'line-width': 0.5 },
}
// A53 多段階浸水想定 - 国土数値情報にデータなし（既存の tadankai レイヤーを使用）
// 国土数値情報 - 3. 地域 > 施設 ===================================================================
// P28 国・都道府県の機関
const kkKuniKikanSource = { id: 'kk-kuni-kikan-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kuni-kikan.pmtiles' } }
const kkKuniKikanCircle = { id: 'oh-kk-kuni-kikan', type: 'circle', source: 'kk-kuni-kikan-source', 'source-layer': 'kuni-kikan', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6], 'circle-color': '#1565C0', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } }
// P05 市町村役場等及び公的集会施設
const kkYakubaSource = { id: 'kk-yakuba-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/yakuba.pmtiles' } }
const kkYakubaCircle = { id: 'oh-kk-yakuba', type: 'circle', source: 'kk-yakuba-source', 'source-layer': 'yakuba', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['get', 'P05_002'],
    '1', '#E65100',
    '2', '#FF8F00',
    '3', '#FFC107',
    '4', '#8D6E63',
    '5', '#A1887F',
    '#E65100',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P34 市区町村役場
const kkShikuchosonYakubaSource = { id: 'kk-shikuchoson-yakuba-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shikuchoson-yakuba.pmtiles' } }
const kkShikuchosonYakubaCircle = { id: 'oh-kk-shikuchoson-yakuba', type: 'circle', source: 'kk-shikuchoson-yakuba-source', 'source-layer': 'shikuchoson-yakuba', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['get', 'P34_002'],
    '1', '#BF360C',
    '2', '#FF7043',
    '#BF360C',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P02 公共施設
const kkKokyouShisetsuSource = { id: 'kk-kokyou-shisetsu-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kokyou-shisetsu.pmtiles' } }
const kkKokyouShisetsuCircle = { id: 'oh-kk-kokyou-shisetsu', type: 'circle', source: 'kk-kokyou-shisetsu-source', 'source-layer': 'kokyou-shisetsu', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 5],
  'circle-color': [
    'match', ['get', 'P02_002'],
    '1', '#546E7A',
    '2', '#1565C0',
    '3', '#2E7D32',
    '4', '#F06292',
    '5', '#1A237E',
    '6', '#D32F2F',
    '7', '#FF8F00',
    '8', '#C62828',
    '9', '#E65100',
    '10', '#AB47BC',
    '11', '#9E9E9E',
    '#4527A0',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P22 下水道関連施設
const kkGesuidouSource = { id: 'kk-gesuidou-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/gesuidou.pmtiles' } }
const kkGesuidouCircle = { id: 'oh-kk-gesuidou', type: 'circle', source: 'kk-gesuidou-source', 'source-layer': 'gesuidou', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 5], 'circle-color': '#00695C', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } }
// P15 廃棄物処理施設
const kkHaikibutsuSource = { id: 'kk-haikibutsu-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/haikibutsu.pmtiles' } }
const kkHaikibutsuCircle = { id: 'oh-kk-haikibutsu', type: 'circle', source: 'kk-haikibutsu-source', 'source-layer': 'haikibutsu', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 5],
  'circle-color': [
    'match', ['get', 'P15_003'],
    '1', '#D32F2F',
    '2', '#795548',
    '3', '#4CAF50',
    '4', '#FF9800',
    '5', '#78909C',
    '6', '#9C27B0',
    '#795548',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P03 発電施設
const kkHatsudenSource = { id: 'kk-hatsuden-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hatsuden.pmtiles' } }
const kkHatsudenCircle = { id: 'oh-kk-hatsuden', type: 'circle', source: 'kk-hatsuden-source', 'source-layer': 'hatsuden', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 14, 7], 'circle-color': '#FFD600', 'circle-stroke-width': 1, 'circle-stroke-color': '#333' } }
// P26 ニュータウン
const kkNewtownSource = { id: 'kk-newtown-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/newtown.pmtiles' } }
const kkNewtownCircle = { id: 'oh-kk-newtown', type: 'circle', source: 'kk-newtown-source', 'source-layer': 'newtown', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6], 'circle-color': '#00BCD4', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } }
// P16 研究機関
const kkKenkyuKikanSource = { id: 'kk-kenkyu-kikan-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kenkyu-kikan.pmtiles' } }
const kkKenkyuKikanCircle = { id: 'oh-kk-kenkyu-kikan', type: 'circle', source: 'kk-kenkyu-kikan-source', 'source-layer': 'kenkyu-kikan', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P16_002']],
    '1', '#1565C0',
    '2', '#2E7D32',
    '3', '#E65100',
    '4', '#7B1FA2',
    '#311B92',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P24 地場産業関連施設
const kkJibaSangyoSource = { id: 'kk-jiba-sangyo-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/jiba-sangyo.pmtiles' } }
const kkJibaSangyoCircle = { id: 'oh-kk-jiba-sangyo', type: 'circle', source: 'kk-jiba-sangyo-source', 'source-layer': 'jiba-sangyo', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6], 'circle-color': '#F57F17', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } }
// P31 物流拠点
const kkButsuryuKyotenSource = { id: 'kk-butsuryu-kyoten-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/butsuryu-kyoten.pmtiles' } }
const kkButsuryuKyotenCircle = { id: 'oh-kk-butsuryu-kyoten', type: 'circle', source: 'kk-butsuryu-kyoten-source', 'source-layer': 'butsuryu-kyoten', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P31_002']],
    '1', '#D32F2F',
    '2', '#FF8F00',
    '3', '#795548',
    '4', '#1565C0',
    '5', '#2E7D32',
    '6', '#0097A7',
    '7', '#78909C',
    '#546E7A',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P33 集客施設
const kkShukyakuSource = { id: 'kk-shukyaku-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shukyaku.pmtiles' } }
const kkShukyakuCircle = { id: 'oh-kk-shukyaku', type: 'circle', source: 'kk-shukyaku-source', 'source-layer': 'shukyaku', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P33_004']],
    '1', '#D81B60',
    '2', '#1565C0',
    '3', '#E65100',
    '4', '#2E7D32',
    '5', '#FF8F00',
    '6', '#78909C',
    '#D81B60',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P14 福祉施設
const kkFukushiSource = { id: 'kk-fukushi-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/fukushi.pmtiles' } }
const kkFukushiCircle = { id: 'oh-kk-fukushi', type: 'circle', source: 'kk-fukushi-source', 'source-layer': 'fukushi', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 5],
  'circle-color': [
    'match', ['to-string', ['get', 'P14_005']],
    '1', '#E91E63',
    '2', '#9C27B0',
    '3', '#3F51B5',
    '4', '#2196F3',
    '5', '#FF9800',
    '6', '#F06292',
    '7', '#78909C',
    '#F06292',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// P27 文化施設
const kkBunkaShisetsuSource = { id: 'kk-bunka-shisetsu-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/bunka-shisetsu.pmtiles' } }
const kkBunkaShisetsuCircle = { id: 'oh-kk-bunka-shisetsu', type: 'circle', source: 'kk-bunka-shisetsu-source', 'source-layer': 'bunka-shisetsu', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P27_002']],
    '1', '#7B1FA2',
    '2', '#4A148C',
    '3', '#E91E63',
    '4', '#2E7D32',
    '5', '#0277BD',
    '6', '#FF6F00',
    '#7B1FA2',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// 国土数値情報 - 3. 地域 > 地域資源・観光 ===================================================================
// P32 都道府県指定文化財
const kkBunkazaiSource = { id: 'kk-bunkazai-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/bunkazai.pmtiles' } }
const kkBunkazaiCircle = { id: 'oh-kk-bunkazai', type: 'circle', source: 'kk-bunkazai-source', 'source-layer': 'bunkazai', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P32_004']],
    '1', '#FF6F00',
    '2', '#AB47BC',
    '3', '#8D6E63',
    '4', '#2E7D32',
    '5', '#0097A7',
    '6', '#D81B60',
    '7', '#546E7A',
    '#FF6F00',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// A34 世界文化遺産
const kkSekaiBunkaIsanSource = { id: 'kk-sekai-bunka-isan-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/sekai-bunka-isan.pmtiles' } }
const kkSekaiBunkaIsanFill = { id: 'oh-kk-sekai-bunka-isan', type: 'fill', source: 'kk-sekai-bunka-isan-source', 'source-layer': 'sekai-bunka-isan', paint: { 'fill-color': 'rgba(183, 28, 28, 0.35)' } }
const kkSekaiBunkaIsanLine = { id: 'oh-kk-sekai-bunka-isan-line', type: 'line', source: 'kk-sekai-bunka-isan-source', 'source-layer': 'sekai-bunka-isan', paint: { 'line-color': 'rgba(183, 28, 28, 0.8)', 'line-width': 2 } }
// A28 世界自然遺産
const kkSekaiShizenIsanSource = { id: 'kk-sekai-shizen-isan-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/sekai-shizen-isan.pmtiles' } }
const kkSekaiShizenIsanFill = { id: 'oh-kk-sekai-shizen-isan', type: 'fill', source: 'kk-sekai-shizen-isan-source', 'source-layer': 'sekai-shizen-isan', paint: { 'fill-color': 'rgba(27, 94, 32, 0.35)' } }
const kkSekaiShizenIsanLine = { id: 'oh-kk-sekai-shizen-isan-line', type: 'line', source: 'kk-sekai-shizen-isan-source', 'source-layer': 'sekai-shizen-isan', paint: { 'line-color': 'rgba(27, 94, 32, 0.8)', 'line-width': 2 } }
// P19 地域資源
const kkChiikiShigenSource = { id: 'kk-chiiki-shigen-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/chiiki-shigen.pmtiles' } }
const kkChiikiShigenCircle = { id: 'oh-kk-chiiki-shigen', type: 'circle', source: 'kk-chiiki-shigen-source', 'source-layer': 'chiiki-shigen', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6],
  'circle-color': [
    'match', ['to-string', ['get', 'P19_004']],
    '1', '#D32F2F',
    '2', '#795548',
    '3', '#9E9E9E',
    '4', '#0277BD',
    '5', '#2E7D32',
    '6', '#00695C',
    '7', '#1565C0',
    '8', '#FF6F00',
    '9', '#7B1FA2',
    '10', '#F06292',
    '#FF8F00',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// 国土数値情報 - 3. 地域 > 保護保全 ===================================================================
// A10 自然公園地域
const kkShizenKoenSource = { id: 'kk-shizen-koen-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shizen-koen.pmtiles' } }
const kkShizenKoenFill = { id: 'oh-kk-shizen-koen', type: 'fill', source: 'kk-shizen-koen-source', 'source-layer': 'shizen-koen', paint: { 'fill-color': 'rgba(76, 175, 80, 0.3)' } }
const kkShizenKoenLine = { id: 'oh-kk-shizen-koen-line', type: 'line', source: 'kk-shizen-koen-source', 'source-layer': 'shizen-koen', paint: { 'line-color': 'rgba(46, 125, 50, 0.7)', 'line-width': 1 } }
// A11 自然保全地域
const kkShizenHozenSource = { id: 'kk-shizen-hozen-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/shizen-hozen.pmtiles' } }
const kkShizenHozenFill = { id: 'oh-kk-shizen-hozen', type: 'fill', source: 'kk-shizen-hozen-source', 'source-layer': 'shizen-hozen', paint: { 'fill-color': 'rgba(0, 150, 136, 0.3)' } }
const kkShizenHozenLine = { id: 'oh-kk-shizen-hozen-line', type: 'line', source: 'kk-shizen-hozen-source', 'source-layer': 'shizen-hozen', paint: { 'line-color': 'rgba(0, 105, 92, 0.7)', 'line-width': 1 } }
// A15 鳥獣保護区
const kkChoujuHogoSource = { id: 'kk-chouju-hogo-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/chouju-hogo.pmtiles' } }
const kkChoujuHogoFill = { id: 'oh-kk-chouju-hogo', type: 'fill', source: 'kk-chouju-hogo-source', 'source-layer': 'chouju-hogo', paint: {
  'fill-color': [
    'match', ['to-string', ['get', 'A15_004']],
    '1', 'rgba(255, 165, 0, 0.35)',
    '2', 'rgba(255, 69, 0, 0.4)',
    '3', 'rgba(255, 215, 0, 0.35)',
    '4', 'rgba(255, 140, 0, 0.4)',
    'rgba(139, 195, 74, 0.3)',
  ],
} }
const kkChoujuHogoLine = { id: 'oh-kk-chouju-hogo-line', type: 'line', source: 'kk-chouju-hogo-source', 'source-layer': 'chouju-hogo', paint: { 'line-color': 'rgba(85, 139, 47, 0.7)', 'line-width': 1 } }
// 国土数値情報 - 4. 交通 ===================================================================
// N10 緊急輸送道路
const kkKinkyuYusoSource = { id: 'kk-kinkyu-yuso-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kinkyu-yuso.pmtiles' } }
const kkKinkyuYusoLine2 = { id: 'oh-kk-kinkyu-yuso', type: 'line', source: 'kk-kinkyu-yuso-source', 'source-layer': 'kinkyu-yuso', paint: {
  'line-color': [
    'match', ['to-string', ['get', 'N10_002']],
    '1', '#D32F2F',
    '2', '#FF6F00',
    '3', '#FFC107',
    '#D32F2F',
  ],
  'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 14, 3],
} }
// N12 重要物流道路
const kkButsuryuDouroSource = { id: 'kk-butsuryu-douro-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/butsuryu-douro.pmtiles' } }
const kkButsuryuDouroLine2 = { id: 'oh-kk-butsuryu-douro', type: 'line', source: 'kk-butsuryu-douro-source', 'source-layer': 'butsuryu-douro', paint: {
  'line-color': [
    'match', ['to-string', ['get', 'N12_002']],
    '1', '#1565C0',
    '2', '#42A5F5',
    '#1565C0',
  ],
  'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 14, 3],
} }
// P36 高速バス停留所
const kkKosokuBusTeiSource = { id: 'kk-kosoku-bus-tei-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kosoku-bus-tei.pmtiles' } }
const kkKosokuBusTeiCircle = { id: 'oh-kk-kosoku-bus-tei', type: 'circle', source: 'kk-kosoku-bus-tei-source', 'source-layer': 'kosoku-bus-tei', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 14, 6], 'circle-color': '#00897B', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' } }
// C28 空港
const kkKuukouSource = { id: 'kk-kuukou-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kuukou.pmtiles' } }
const kkKuukouFill = { id: 'oh-kk-kuukou', type: 'fill', source: 'kk-kuukou-source', 'source-layer': 'kuukou', paint: { 'fill-color': 'rgba(33, 150, 243, 0.3)' } }
const kkKuukouLine = { id: 'oh-kk-kuukou-line', type: 'line', source: 'kk-kuukou-source', 'source-layer': 'kuukou', paint: { 'line-color': 'rgba(21, 101, 192, 0.8)', 'line-width': 2 } }
// C02 港湾
const kkKowanSource = { id: 'kk-kowan-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kowan.pmtiles' } }
const kkKowanCircle = { id: 'oh-kk-kowan', type: 'circle', source: 'kk-kowan-source', 'source-layer': 'kowan', paint: {
  'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 14, 7],
  'circle-color': [
    'match', ['to-string', ['get', 'C02_002']],
    '1', '#D32F2F',
    '2', '#E65100',
    '3', '#1565C0',
    '4', '#42A5F5',
    '5', '#78909C',
    '#0277BD',
  ],
  'circle-stroke-width': 1, 'circle-stroke-color': '#fff',
} }
// N09 定期旅客航路
const kkTeikiKouroSource = { id: 'kk-teiki-kouro-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/teiki-kouro.pmtiles' } }
const kkTeikiKouroLine2 = { id: 'oh-kk-teiki-kouro', type: 'line', source: 'kk-teiki-kouro-source', 'source-layer': 'teiki-kouro', paint: { 'line-color': '#0097A7', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1, 14, 3], 'line-dasharray': [4, 2] } }
// S10a 港湾間流通量
const kkKowanRyutsuSource = { id: 'kk-kowan-ryutsu-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kowan-ryutsu.pmtiles' } }
const kkKowanRyutsuLine2 = { id: 'oh-kk-kowan-ryutsu', type: 'line', source: 'kk-kowan-ryutsu-source', 'source-layer': 'kowan-ryutsu', paint: { 'line-color': '#00838F', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 14, 2], 'line-opacity': 0.7 } }
// S10b 空港間流通量
const kkKuukouRyutsuSource = { id: 'kk-kuukou-ryutsu-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kuukou-ryutsu.pmtiles' } }
const kkKuukouRyutsuLine2 = { id: 'oh-kk-kuukou-ryutsu', type: 'line', source: 'kk-kuukou-ryutsu-source', 'source-layer': 'kuukou-ryutsu', paint: { 'line-color': '#5C6BC0', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 14, 2], 'line-opacity': 0.7 } }
// S05-d 貨物旅客地域流動量
const kkKamotsuRyokakuSource = { id: 'kk-kamotsu-ryokaku-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kamotsu-ryokaku.pmtiles' } }
const kkKamotsuRyokakuLine2 = { id: 'oh-kk-kamotsu-ryokaku', type: 'line', source: 'kk-kamotsu-ryokaku-source', 'source-layer': 'kamotsu-ryokaku', paint: { 'line-color': '#7E57C2', 'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 14, 2], 'line-opacity': 0.7 } }
// S05-c 駅別乗降客数（パーソントリップ）
const kkEkiJokoPtSource = { id: 'kk-eki-joko-pt-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/eki-joko-pt.pmtiles' } }
const kkEkiJokoPtCircle = { id: 'oh-kk-eki-joko-pt', type: 'circle', source: 'kk-eki-joko-pt-source', 'source-layer': 'eki-joko-pt', paint: { 'circle-color': '#AB47BC', 'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 10, 5, 14, 8], 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 } }
const kkEkiJokoPtLabel = { id: 'oh-kk-eki-joko-pt-label', type: 'symbol', source: 'kk-eki-joko-pt-source', 'source-layer': 'eki-joko-pt', layout: { 'text-field': ['get', 'S05c_003'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(171,71,188,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
// 国土数値情報 ポイントレイヤーラベル ===================================================================
const kkKuniKikanLabel = { id: 'oh-kk-kuni-kikan-label', type: 'symbol', source: 'kk-kuni-kikan-source', 'source-layer': 'kuni-kikan', layout: { 'text-field': ['get', 'P28_003'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(21,101,192,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkYakubaLabel = { id: 'oh-kk-yakuba-label', type: 'symbol', source: 'kk-yakuba-source', 'source-layer': 'yakuba', layout: { 'text-field': ['get', 'P05_003_ja'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(230,81,0,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkShikuchosonYakubaLabel = { id: 'oh-kk-shikuchoson-yakuba-label', type: 'symbol', source: 'kk-shikuchoson-yakuba-source', 'source-layer': 'shikuchoson-yakuba', layout: { 'text-field': ['get', 'P34_003'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(191,54,12,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkKokyouShisetsuLabel = { id: 'oh-kk-kokyou-shisetsu-label', type: 'symbol', source: 'kk-kokyou-shisetsu-source', 'source-layer': 'kokyou-shisetsu', layout: { 'text-field': ['get', 'P02_004'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(69,39,160,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkGesuidouLabel = { id: 'oh-kk-gesuidou-label', type: 'symbol', source: 'kk-gesuidou-source', 'source-layer': 'gesuidou', layout: { 'text-field': ['coalesce', ['get', 'P22b_002'], ['get', 'P22a_002'], ''], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(0,105,92,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkHaikibutsuLabel = { id: 'oh-kk-haikibutsu-label', type: 'symbol', source: 'kk-haikibutsu-source', 'source-layer': 'haikibutsu', layout: { 'text-field': ['get', 'P15_001'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(121,85,72,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkHatsudenLabel = { id: 'oh-kk-hatsuden-label', type: 'symbol', source: 'kk-hatsuden-source', 'source-layer': 'hatsuden', layout: { 'text-field': ['get', 'P03_0002'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(50,50,50,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkNewtownLabel = { id: 'oh-kk-newtown-label', type: 'symbol', source: 'kk-newtown-source', 'source-layer': 'newtown', layout: { 'text-field': ['get', 'P26_004'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(0,96,100,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkKenkyuKikanLabel = { id: 'oh-kk-kenkyu-kikan-label', type: 'symbol', source: 'kk-kenkyu-kikan-source', 'source-layer': 'kenkyu-kikan', layout: { 'text-field': ['get', 'P16_001'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(49,27,146,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkJibaSangyoLabel = { id: 'oh-kk-jiba-sangyo-label', type: 'symbol', source: 'kk-jiba-sangyo-source', 'source-layer': 'jiba-sangyo', layout: { 'text-field': ['get', 'P24_005'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(230,81,0,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkButsuryuKyotenLabel = { id: 'oh-kk-butsuryu-kyoten-label', type: 'symbol', source: 'kk-butsuryu-kyoten-source', 'source-layer': 'butsuryu-kyoten', layout: { 'text-field': ['get', 'P31_001'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(84,110,122,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkShukyakuLabel = { id: 'oh-kk-shukyaku-label', type: 'symbol', source: 'kk-shukyaku-source', 'source-layer': 'shukyaku', layout: { 'text-field': ['get', 'P33_005'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(136,14,79,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkFukushiLabel = { id: 'oh-kk-fukushi-label', type: 'symbol', source: 'kk-fukushi-source', 'source-layer': 'fukushi', layout: { 'text-field': ['get', 'P14_008'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(136,14,79,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkBunkaShisetsuLabel = { id: 'oh-kk-bunka-shisetsu-label', type: 'symbol', source: 'kk-bunka-shisetsu-source', 'source-layer': 'bunka-shisetsu', layout: { 'text-field': ['get', 'P27_005'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(74,20,140,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkBunkazaiLabel = { id: 'oh-kk-bunkazai-label', type: 'symbol', source: 'kk-bunkazai-source', 'source-layer': 'bunkazai', layout: { 'text-field': ['get', 'P32_006'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(230,81,0,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkChiikiShigenLabel = { id: 'oh-kk-chiiki-shigen-label', type: 'symbol', source: 'kk-chiiki-shigen-source', 'source-layer': 'chiiki-shigen', layout: { 'text-field': ['get', 'P19_008'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(230,81,0,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkKosokuBusTeiLabel = { id: 'oh-kk-kosoku-bus-tei-label', type: 'symbol', source: 'kk-kosoku-bus-tei-source', 'source-layer': 'kosoku-bus-tei', layout: { 'text-field': ['get', 'name'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(0,77,64,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkKowanLabel = { id: 'oh-kk-kowan-label', type: 'symbol', source: 'kk-kowan-source', 'source-layer': 'kowan', layout: { 'text-field': ['get', 'C02_005'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 8, 12, 14, 16] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(1,87,155,0.8)', 'text-halo-width': 2 }, minzoom: 8 }
const kkKaiganHozenLabel = { id: 'oh-kk-kaigan-hozen-label', type: 'symbol', source: 'kk-kaigan-hozen-source', 'source-layer': 'kaigan-hozen', layout: { 'text-field': ['get', 'P23_001'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(191,54,12,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkToshiKoenLabel = { id: 'oh-kk-toshi-koen-label', type: 'symbol', source: 'kk-toshi-koen-source', 'source-layer': 'toshi-koen', layout: { 'text-field': ['get', 'P13_003'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(27,94,32,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkHinanShisetsuLabel = { id: 'oh-kk-hinan-shisetsu-label', type: 'symbol', source: 'kk-hinan-shisetsu-source', 'source-layer': 'hinan-shisetsu', layout: { 'text-field': ['get', 'P20_002'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 10, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(27,94,32,0.8)', 'text-halo-width': 2 }, minzoom: 10 }
const kkTatsumakiLabel = { id: 'oh-kk-tatsumaki-label', type: 'symbol', source: 'kk-tatsumaki-source', 'source-layer': 'tatsumaki', layout: { 'text-field': ['get', 'A30b_005'], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.5], 'text-size': ['interpolate', ['linear'], ['zoom'], 8, 11, 14, 15] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(191,54,12,0.8)', 'text-halo-width': 2 }, minzoom: 8 }
// 国土数値情報 メッシュデータ（新規8データセット）===================================================================
// G04-d 標高・傾斜度5次メッシュ(250m)
const kkHyoko5MeshSource = { id: 'kk-hyoko5-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/hyoko5-mesh.pmtiles' } }
const kkHyoko5MeshFill = { id: 'oh-kk-hyoko5-mesh', type: 'fill', source: 'kk-hyoko5-mesh-source', 'source-layer': 'hyoko5-mesh', paint: { 'fill-color': 'rgba(139, 90, 43, 0.3)', 'fill-outline-color': 'rgba(139, 90, 43, 0.6)' } }
const kkHyoko5MeshLine = { id: 'oh-kk-hyoko5-mesh-line', type: 'line', source: 'kk-hyoko5-mesh-source', 'source-layer': 'hyoko5-mesh', paint: { 'line-color': 'rgba(139, 90, 43, 0.6)', 'line-width': 0.5 } }
// G02 平年値メッシュ(1km)
const kkHeinenchiMeshSource = { id: 'kk-heinenchi-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/heinenchi-mesh.pmtiles' } }
const kkHeinenchiMeshFill = { id: 'oh-kk-heinenchi-mesh', type: 'fill', source: 'kk-heinenchi-mesh-source', 'source-layer': 'heinenchi-mesh', paint: { 'fill-color': 'rgba(33, 150, 243, 0.3)', 'fill-outline-color': 'rgba(33, 150, 243, 0.6)' } }
const kkHeinenchiMeshLine = { id: 'oh-kk-heinenchi-mesh-line', type: 'line', source: 'kk-heinenchi-mesh-source', 'source-layer': 'heinenchi-mesh', paint: { 'line-color': 'rgba(33, 150, 243, 0.6)', 'line-width': 0.5 } }
// L03-a 土地利用3次メッシュ(1km)
const kkTochiriyo3MeshSource = { id: 'kk-tochiriyo3-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/kokudo/tochiriyo3-mesh.pmtiles' } }
const kkTochiriyo3MeshFill = { id: 'oh-kk-tochiriyo3-mesh', type: 'fill', source: 'kk-tochiriyo3-mesh-source', 'source-layer': 'tochiriyo3-mesh', paint: { 'fill-color': 'rgba(76, 175, 80, 0.3)', 'fill-outline-color': 'rgba(76, 175, 80, 0.6)' } }
const kkTochiriyo3MeshLine = { id: 'oh-kk-tochiriyo3-mesh-line', type: 'line', source: 'kk-tochiriyo3-mesh-source', 'source-layer': 'tochiriyo3-mesh', paint: { 'line-color': 'rgba(76, 175, 80, 0.6)', 'line-width': 0.5 } }
// L03-b-u 都市地域土地利用細分メッシュ(100m)
const kkTochiriyoToshiMeshSource = { id: 'kk-tochiriyo-toshi-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/kokudo/tochiriyo-toshi-mesh.pmtiles' } }
const kkTochiriyoToshiMeshFill = { id: 'oh-kk-tochiriyo-toshi-mesh', type: 'fill', source: 'kk-tochiriyo-toshi-mesh-source', 'source-layer': 'tochiriyo-toshi-mesh', paint: {
  'fill-color': [
    'match', ['get', '土地利用種別'],
    '0100', 'rgba(255,255,0,0.8)',       // 田
    '0200', 'rgba(255,204,153,0.8)',     // その他の農用地
    '0500', 'rgba(0,170,0,0.8)',         // 森林
    '0600', 'rgba(0,255,153,0.8)',       // 荒地
    '0701', 'rgba(255,100,100,0.8)',     // 低層建物
    '0702', 'rgba(255,0,0,0.8)',         // 低層建物（密集）
    '0703', 'rgba(180,0,0,0.8)',         // 高層建物
    '0704', 'rgba(150,0,150,0.8)',       // 工場
    '0901', 'rgba(140,140,140,0.8)',     // 道路
    '0902', 'rgba(180,180,180,0.8)',     // 鉄道
    '1001', 'rgba(200,70,15,0.8)',       // 公共施設等用地
    '1002', 'rgba(220,200,180,0.8)',     // 空地
    '1003', 'rgba(50,200,50,0.8)',       // 公園・緑地
    '1100', 'rgba(0,0,255,0.8)',         // 河川地及び湖沼
    '1400', 'rgba(255,255,153,0.8)',     // 海浜
    '1500', 'rgba(0,204,255,0.8)',       // 海水域
    '1600', 'rgba(100,200,100,0.8)',     // ゴルフ場
    'rgba(0,0,0,0)',
  ],
  'fill-outline-color': 'rgba(0,0,0,0)',
} }
const kkTochiriyoToshiMeshLine = { id: 'oh-kk-tochiriyo-toshi-mesh-line', type: 'line', source: 'kk-tochiriyo-toshi-mesh-source', 'source-layer': 'tochiriyo-toshi-mesh', paint: { 'line-color': 'rgba(255, 152, 0, 0.6)', 'line-width': 0.5 } }
// L03-b-c 土地利用詳細メッシュ(50m)
const kkTochiriyoShosaiMeshSource = { id: 'kk-tochiriyo-shosai-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/kokudo/tochiriyo-shosai-mesh.pmtiles' } }
const kkTochiriyoShosaiMeshFill = { id: 'oh-kk-tochiriyo-shosai-mesh', type: 'fill', source: 'kk-tochiriyo-shosai-mesh-source', 'source-layer': 'tochiriyo-shosai-mesh', paint: { 'fill-color': 'rgba(255, 87, 34, 0.3)', 'fill-outline-color': 'rgba(255, 87, 34, 0.6)' } }
const kkTochiriyoShosaiMeshLine = { id: 'oh-kk-tochiriyo-shosai-mesh-line', type: 'line', source: 'kk-tochiriyo-shosai-mesh-source', 'source-layer': 'tochiriyo-shosai-mesh', paint: { 'line-color': 'rgba(255, 87, 34, 0.6)', 'line-width': 0.5 } }
// A30a5 土砂災害・雪崩メッシュ(5km)
const kkDoshaNadareMeshSource = { id: 'kk-dosha-nadare-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/dosha-nadare-mesh.pmtiles' } }
const kkDoshaNadareMeshFill = { id: 'oh-kk-dosha-nadare-mesh', type: 'fill', source: 'kk-dosha-nadare-mesh-source', 'source-layer': 'dosha-nadare-mesh', paint: { 'fill-color': 'rgba(121, 85, 72, 0.3)', 'fill-outline-color': 'rgba(121, 85, 72, 0.6)' } }
const kkDoshaNadareMeshLine = { id: 'oh-kk-dosha-nadare-mesh-line', type: 'line', source: 'kk-dosha-nadare-mesh-source', 'source-layer': 'dosha-nadare-mesh', paint: { 'line-color': 'rgba(121, 85, 72, 0.6)', 'line-width': 0.5 } }
// A31b 洪水浸水想定区域1次メッシュ（5サブタイプ）
// 浸水深ランク色（計画規模・想定最大規模共通）: 1=0.5m未満 → 6=20m以上
const kozuiMeshDepthColor = [
  'match', ['get', 'rank'],
  1, 'rgba(255, 255, 178, 0.5)',
  2, 'rgba(255, 237, 100, 0.5)',
  3, 'rgba(255, 180, 50, 0.5)',
  4, 'rgba(255, 100, 0, 0.5)',
  5, 'rgba(230, 30, 0, 0.5)',
  6, 'rgba(150, 0, 0, 0.5)',
  'rgba(0, 0, 255, 0.25)',
]
// 10: 計画規模
const kkKozuiMesh10Source = { id: 'kk-kozui-mesh-10-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-mesh-keikaku.pmtiles' } }
const kkKozuiMesh10Fill = { id: 'oh-kk-kozui-mesh-10', type: 'fill', source: 'kk-kozui-mesh-10-source', 'source-layer': 'kozui-mesh-keikaku', paint: { 'fill-color': kozuiMeshDepthColor } }
const kkKozuiMesh10Line = { id: 'oh-kk-kozui-mesh-10-line', type: 'line', source: 'kk-kozui-mesh-10-source', 'source-layer': 'kozui-mesh-keikaku', paint: { 'line-color': 'rgba(0, 0, 200, 0.4)', 'line-width': 0.5 } }
// 20: 想定最大規模
const kkKozuiMesh20Source = { id: 'kk-kozui-mesh-20-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-mesh-saidai.pmtiles' } }
const kkKozuiMesh20Fill = { id: 'oh-kk-kozui-mesh-20', type: 'fill', source: 'kk-kozui-mesh-20-source', 'source-layer': 'kozui-mesh-saidai', paint: { 'fill-color': kozuiMeshDepthColor } }
const kkKozuiMesh20Line = { id: 'oh-kk-kozui-mesh-20-line', type: 'line', source: 'kk-kozui-mesh-20-source', 'source-layer': 'kozui-mesh-saidai', paint: { 'line-color': 'rgba(0, 0, 200, 0.4)', 'line-width': 0.5 } }
// 30: 浸水継続時間
const kkKozuiMesh30Source = { id: 'kk-kozui-mesh-30-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-mesh-jikan.pmtiles' } }
const kkKozuiMesh30Fill = { id: 'oh-kk-kozui-mesh-30', type: 'fill', source: 'kk-kozui-mesh-30-source', 'source-layer': 'kozui-mesh-jikan', paint: {
  'fill-color': [
    'match', ['get', 'rank'],
    1, 'rgba(173, 216, 230, 0.5)',
    2, 'rgba(100, 149, 237, 0.5)',
    3, 'rgba(30, 100, 200, 0.5)',
    4, 'rgba(0, 50, 150, 0.6)',
    5, 'rgba(0, 20, 100, 0.7)',
    'rgba(0, 0, 255, 0.25)',
  ],
} }
const kkKozuiMesh30Line = { id: 'oh-kk-kozui-mesh-30-line', type: 'line', source: 'kk-kozui-mesh-30-source', 'source-layer': 'kozui-mesh-jikan', paint: { 'line-color': 'rgba(0, 0, 150, 0.4)', 'line-width': 0.5 } }
// 41: 家屋倒壊等（氾濫流）
const kkKozuiMesh41Source = { id: 'kk-kozui-mesh-41-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-mesh-hanran.pmtiles' } }
const kkKozuiMesh41Fill = { id: 'oh-kk-kozui-mesh-41', type: 'fill', source: 'kk-kozui-mesh-41-source', 'source-layer': 'kozui-mesh-hanran', paint: { 'fill-color': 'rgba(200, 0, 0, 0.4)' } }
const kkKozuiMesh41Line = { id: 'oh-kk-kozui-mesh-41-line', type: 'line', source: 'kk-kozui-mesh-41-source', 'source-layer': 'kozui-mesh-hanran', paint: { 'line-color': 'rgba(200, 0, 0, 0.6)', 'line-width': 0.5 } }
// 42: 家屋倒壊等（河岸浸食）
const kkKozuiMesh42Source = { id: 'kk-kozui-mesh-42-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/kozui-mesh-gangan.pmtiles' } }
const kkKozuiMesh42Fill = { id: 'oh-kk-kozui-mesh-42', type: 'fill', source: 'kk-kozui-mesh-42-source', 'source-layer': 'kozui-mesh-gangan', paint: { 'fill-color': 'rgba(128, 0, 128, 0.4)' } }
const kkKozuiMesh42Line = { id: 'oh-kk-kozui-mesh-42-line', type: 'line', source: 'kk-kozui-mesh-42-source', 'source-layer': 'kozui-mesh-gangan', paint: { 'line-color': 'rgba(128, 0, 128, 0.6)', 'line-width': 0.5 } }
// N04 道路密度・道路延長メッシュ(1km)
const kkDouroMitsudoMeshSource = { id: 'kk-douro-mitsudo-mesh-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/kokudo/douro-mitsudo-mesh.pmtiles' } }
const kkDouroMitsudoMeshFill = {
  id: 'oh-kk-douro-mitsudo-mesh', type: 'fill', source: 'kk-douro-mitsudo-mesh-source', 'source-layer': 'douro-mitsudo-mesh',
  paint: {
    'fill-color': [
      'case',
      ['all', ['has', 'N04_056'], ['!=', ['get', 'N04_056'], ''], ['!=', ['get', 'N04_056'], 'unknown']],
      ['interpolate', ['linear'], ['to-number', ['get', 'N04_056']],
        0, 'rgba(240,249,232,0.6)',
        2000, 'rgba(186,228,179,0.7)',
        5000, 'rgba(116,196,118,0.7)',
        10000, 'rgba(255,237,160,0.7)',
        15000, 'rgba(254,178,76,0.7)',
        20000, 'rgba(240,59,32,0.7)',
        30000, 'rgba(189,0,38,0.8)',
      ],
      'rgba(0,0,0,0)',
    ],
    'fill-outline-color': 'rgba(96,125,139,0.3)',
  },
}
const kkDouroMitsudoMeshLine = { id: 'oh-kk-douro-mitsudo-mesh-line', type: 'line', source: 'kk-douro-mitsudo-mesh-source', 'source-layer': 'douro-mitsudo-mesh', paint: { 'line-color': 'rgba(96, 125, 139, 0.4)', 'line-width': 0.5 } }
// GSI 街区レベル位置参照情報（住居表示住所）
const gsiJushoSource = { id: 'gsi-jusho-source', obj: { type: 'vector', url: 'pmtiles://https://kenzkenz.net/pmtiles/gsi-jusho/gsi-jusho.pmtiles' } }
const gsiJushoCircle = { id: 'oh-gsi-jusho', type: 'circle', source: 'gsi-jusho-source', 'source-layer': 'gsi-jusho', paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 2, 18, 5], 'circle-color': '#e65100', 'circle-stroke-width': 1, 'circle-stroke-color': '#fff' }, minzoom: 14 }
const gsiJushoLabel = { id: 'oh-gsi-jusho-label', type: 'symbol', source: 'gsi-jusho-source', 'source-layer': 'gsi-jusho', layout: { 'text-field': ['concat', ['get', '街区符号'], '-', ['get', '基礎番号']], 'text-font': ['NotoSansJP-Regular'], 'text-offset': [0, 1.2], 'text-size': ['interpolate', ['linear'], ['zoom'], 16, 10, 19, 14] }, paint: { 'text-color': '#fff', 'text-halo-color': 'rgba(180,60,0,0.9)', 'text-halo-width': 2 }, minzoom: 16 }
// 国土数値情報 ここまで ===================================================================

// 小型無人機等飛行禁止法 ドローン飛行禁止区域 ==================================================
// 事前マージ済みGeoJSON（scripts/fetch-drone-zone.mjsで生成）
const droneZoneSource = {
  id: 'drone-zone-source',
  obj: {
    type: 'geojson',
    data: 'https://kenzkenz.net/geojson/drone-zone.geojson',
  },
}

const droneZoneYellowFillLayer = {
  id: 'oh-drone-zone-yellow-fill',
  type: 'fill',
  source: 'drone-zone-source',
  filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', '_zone'], 'yz']],
  paint: {
    'fill-color': '#ffff00',
    'fill-opacity': 0.5,
  },
}

const droneZoneYellowLineLayer = {
  id: 'oh-drone-zone-yellow-line',
  type: 'line',
  source: 'drone-zone-source',
  filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', '_zone'], 'yz']],
  paint: {
    'line-color': '#ffff00',
    'line-width': 2,
    'line-opacity': 1,
  },
}

const droneZoneRedFillLayer = {
  id: 'oh-drone-zone-red-fill',
  type: 'fill',
  source: 'drone-zone-source',
  filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['==', ['get', '_zone'], 'rz']],
  paint: {
    'fill-color': 'rgba(255, 120, 50, 0.6)',
    'fill-opacity': 0.6,
  },
}

const droneZoneRedLineLayer = {
  id: 'oh-drone-zone-red-line',
  type: 'line',
  source: 'drone-zone-source',
  filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', '_zone'], 'rz']],
  paint: {
    'line-color': '#cc0000',
    'line-width': 3,
    'line-opacity': 1,
  },
}
// ドローン飛行禁止区域 ここまで =============================================================

// 無人航空機飛行計画データ（DIPS2.0）==================================================
const dronePlanSource = {
  id: 'drone-plan-source',
  obj: {
    type: 'vector',
    url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/drone/drone_flight_plan.pmtiles',
  },
}

const dronePlanFillColor = [
  'case',
  ['==', ['get', '飛行目的（業務）_空撮'], 1], '#2196F3',
  ['==', ['get', '飛行目的（業務）_報道取材'], 1], '#9C27B0',
  ['==', ['get', '飛行目的（業務）_警備'], 1], '#F44336',
  ['==', ['get', '飛行目的（業務）_農林水産業'], 1], '#4CAF50',
  ['==', ['get', '飛行目的（業務）_測量'], 1], '#FF9800',
  ['==', ['get', '飛行目的（業務）_環境調査'], 1], '#00BCD4',
  ['==', ['get', '飛行目的（業務）_設備メンテナンス'], 1], '#795548',
  ['==', ['get', '飛行目的（業務）_インフラ点検・保守 '], 1], '#607D8B',
  ['==', ['get', '飛行目的（業務）_資材管理'], 1], '#E91E63',
  ['==', ['get', '飛行目的（業務）_輸送・宅配'], 1], '#FFEB3B',
  ['==', ['get', '飛行目的（業務）_自然観測'], 1], '#8BC34A',
  ['==', ['get', '飛行目的（業務）_事故・災害対応等'], 1], '#FF5722',
  ['==', ['get', '飛行目的（業務）_その他'], 1], '#9E9E9E',
  ['==', ['get', '飛行目的（業務以外）_趣味'], 1], '#03A9F4',
  ['==', ['get', '飛行目的（業務以外）_その他'], 1], '#BDBDBD',
  '#999999'
]

const dronePlanFillLayer = {
  id: 'oh-drone-plan-fill',
  type: 'fill',
  source: 'drone-plan-source',
  'source-layer': 'drone',
  paint: {
    'fill-color': dronePlanFillColor,
    'fill-opacity': 0.4,
  },
}

const dronePlanLineLayer = {
  id: 'oh-drone-plan-line',
  type: 'line',
  source: 'drone-plan-source',
  'source-layer': 'drone',
  paint: {
    'line-color': dronePlanFillColor,
    'line-width': 0.5,
    'line-opacity': 0.6,
  },
}
// 無人航空機飛行計画データ ここまで =============================================================

// ---------------------------------------------------------------------------------------------------------------------
let layers01 = [
  {
    id: 'oh-jma-quake-top',
    label: '🆕地震情報（気象庁）',
    description:
      '気象庁の公式一次ソースから取得する最新地震情報。震源・各観測点の震度（JMA震度階級色）・緊急地震速報（EEW）をリアルタイム表示。60秒ごと自動更新',
    sources: [jmaQuakeEpicenterSource, jmaQuakeStationSource, jmaQuakeEewSource],
    layers: [jmaQuakeStationLayer, jmaQuakeStationLabelLayer, jmaQuakeEewLayer, jmaQuakeEpicenterLayer, jmaQuakeEpicenterLabelLayer],
    attribution: '<a href="https://www.jma.go.jp/bosai/map.html#contents=quake" target="_blank">気象庁</a>',
    credit: '気象庁',
    ext: { name: 'extJmaQuake' },
  },
  {
    id: 'oh-homusyo-2026-layer-top',
    label: '🆕2026登記所地図',
    description:
      '法務省公開の登記所備付地図（2026年版）。筆界・地番・精度区分を表示。精度区分（甲一〜乙三）を色分け。SIMA・DXF・Shapefile・GeoJSON・KML・CSV出力対応。土地家屋調査士の実務に有用',
    sources: [homusyo2026Source, homusyo2026LowZoomSource],
    layers: [
      homusyo2026LowZoomFill,
      homusyo2026LowZoomLine,
      homusyo2026Fill,
      homusyo2026Coloring,
      homusyo2026Line,
      homusyo2026Label,
      homusyo2026Vertex,
    ],
    attribution: '法務省登記所備付地図データ',
    credit: '法務省登記所備付地図データ',
    ext: {
      name: 'extTokizyo',
      layerPrefix: 'oh-homusyo-2026',
      sourceId: 'homusyo-2026-source',
      highlightSuffix: '_tokizyo2026',
      dbVersion: '2026',
    },
  },
  {
    id: 'oh-homusyo-2026-kijyunten-top',
    label: '🆕2026登記所地図-基準点',
    sources: [homusyo2026KijyuntenSource],
    layers: [homusyo2026LayerKijyunten, homusyo2026LayerKijyuntenLabel],
    attribution:
      '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
    credit: '法務省登記所備付地図データ',
    ext: { name: 'extKijyunten' },
  },
  {
    id: 'oh-homusyo-2026-zukaku-top',
    label: '🆕2026登記所地図-図郭',
    sources: [homusyo2026ZukakuSource],
    layers: [homusyo2026LayerZukaku, homusyo2026LayerZukakuLine, homusyo2026LayerZukakuLabel],
    attribution:
      '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
    credit: '法務省登記所備付地図データ',
  },
  {
    id: 'oh-hypsometric',
    label: '🆕海陸段彩図',
    sources: [gsjTerrainSource],
    layers: [hypsometricLayer],
    attribution: '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank">産総研シームレス標高タイル</a>',
    credit: '産総研シームレス標高タイル',
    ext: { name: 'extHypsometric' },
  },
  {
    id: 'oh-n13-road',
    label: '🆕国土数値情報 道路',
    sources: [
      {
        id: 'oh-n13-road-major-source',
        obj: {
          type: 'vector',
          url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/n13/n13_road_major.pmtiles',
          minzoom: 4,
          maxzoom: 14,
        },
      },
      {
        id: 'oh-n13-road-minor-source',
        obj: {
          type: 'vector',
          url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/n13/n13_road_minor.pmtiles',
          minzoom: 10,
          maxzoom: 14,
        },
      },
    ],
    layers: [
      // --- Major（高速+国道）: z4〜 ---
      {
        id: 'oh-n13-road-major-layer',
        source: 'oh-n13-road-major-source',
        'source-layer': 'road',
        type: 'line',
        minzoom: 4,
        maxzoom: 24,
        filter: ['!=', ['get', 'N13_006'], '1'], // 3m未満以外（実線）
        paint: {
          'line-color': ['match', ['get', 'N13_003'], '4', '#e53935', '1', '#1565c0', '#9e9e9e'],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4,
            ['match', ['get', 'N13_006'], '5', 1.2, '4', 1, '3', 0.8, '2', 0.5, 0.3],
            8,
            ['match', ['get', 'N13_006'], '5', 3, '4', 2.5, '3', 2, '2', 1.5, 1],
            14,
            ['match', ['get', 'N13_006'], '5', 14, '4', 10, '3', 6, '2', 3.5, 2],
            18,
            ['match', ['get', 'N13_006'], '5', 30, '4', 22, '3', 14, '2', 8, 4],
          ],
          'line-opacity': 0.8,
        },
      },
      {
        id: 'oh-n13-road-major-narrow-layer',
        source: 'oh-n13-road-major-source',
        'source-layer': 'road',
        type: 'line',
        minzoom: 4,
        maxzoom: 24,
        filter: ['==', ['get', 'N13_006'], '1'], // 3m未満のみ（破線）
        paint: {
          'line-color': ['match', ['get', 'N13_003'], '4', '#e53935', '1', '#1565c0', '#9e9e9e'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.3, 8, 0.7, 14, 1.5],
          'line-opacity': 0.7,
          'line-dasharray': [4, 3],
        },
      },
      {
        id: 'oh-n13-road-minor-layer',
        source: 'oh-n13-road-minor-source',
        'source-layer': 'road',
        type: 'line',
        minzoom: 10,
        maxzoom: 24,
        filter: ['!=', ['get', 'N13_006'], '1'], // 3m未満以外（実線）
        paint: {
          'line-color': ['match', ['get', 'N13_003'], '2', '#2e7d32', '3', '#757575', '5', '#8d6e63', '#9e9e9e'],
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10,
            ['match', ['get', 'N13_006'], '5', 1.5, '4', 1.2, '3', 0.8, '2', 0.5, 0.3],
            14,
            ['match', ['get', 'N13_006'], '5', 14, '4', 10, '3', 6, '2', 3.5, 2],
            18,
            ['match', ['get', 'N13_006'], '5', 30, '4', 22, '3', 14, '2', 8, 4],
          ],
          'line-opacity': 0.8,
        },
      },
      {
        id: 'oh-n13-road-minor-narrow-layer',
        source: 'oh-n13-road-minor-source',
        'source-layer': 'road',
        type: 'line',
        minzoom: 10,
        maxzoom: 24,
        filter: ['==', ['get', 'N13_006'], '1'], // 3m未満のみ（破線）
        paint: {
          'line-color': ['match', ['get', 'N13_003'], '2', '#2e7d32', '3', '#757575', '5', '#8d6e63', '#9e9e9e'],
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 14, 1.5],
          'line-opacity': 0.7,
          'line-dasharray': [4, 3],
        },
      },
      {
        id: 'oh-n13-road-minor-label',
        source: 'oh-n13-road-minor-source',
        'source-layer': 'road',
        type: 'symbol',
        minzoom: 14,
        maxzoom: 24,
        layout: {
          'symbol-placement': 'line',
          'text-field': [
            'match',
            ['get', 'N13_006'],
            '1',
            '3m未満',
            '2',
            '3-5.5m',
            '3',
            '5.5-13m',
            '4',
            '13-19.5m',
            '5',
            '19.5m以上',
            '',
          ],
          'text-size': ['interpolate', ['linear'], ['zoom'], 14, 13, 18, 18],
          'text-font': ['Noto Sans CJK JP Regular'],
          'text-anchor': 'center',
          'symbol-spacing': 250,
          'text-max-angle': 30,
        },
        paint: { 'text-color': '#333', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.5 },
      },
    ],
    attribution:
      '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N13-2024.html" target="_blank">国土数値情報（道路）</a>',
    credit: '国土数値情報（道路）',
    ext: { name: 'extN13Road' },
  },

  // {
  //     id: 'oh-elevation-mesh-3d',
  //     label: '🆕標高・傾斜度4次メッシュ3D',
  //     source: elevationMeshSource,
  //     layers: [elevationMeshFill, elevationMeshHeight],
  //     attribution:
  //         '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-c.html" target="_blank">国土数値情報</a>',
  // },
  // {
  //     id: 'oh-mountain',
  //     label: '🆕日本の主な山岳',
  //     source: mountainSource,
  //     layers: [mountainLayer, mountainLabelLayer],
  //     attribution: '<a href="https://maps.gsi.go.jp/3d/mountain/mountain.html" target="_blank">国土地理院</a>',
  //     ext: { name: 'extMountain' },
  // },
  // {
  //     id: 'oh-densho',
  //     label: '🆕自然災害伝承碑',
  //     source: denshoSource,
  //     layers: [denshoLayer, denshoLabelLayer],
  //     attribution: '<a href="https://www.gsi.go.jp/bousaichiri/denshouhi_download.html" target="_blank">国土地理院</a>',
  //     ext: { name: 'extDensho' },
  // },
  // 遺跡等に移動
  // {
  //     id: 'oh-bunkazai',
  //     label: '🆕都道府県指定文化財',
  //     source: bunkazaiSource,
  //     layers: [bunkazaiLayer, bunkazaiLabelLayer],
  //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報</a>',
  //     ext: { name: 'extBunkazai' },
  // },
  // {
  //     id: 'oh-gsi-pp-full',
  //     label: '🆕📷 GSI単写真',
  //     description: '国土地理院の航空写真（単写真）。過去の空中写真。戦前〜現代',
  //     source: gsiPpFullSource,
  //     layers: [gsiPpFullLayer],
  //     attribution: '<a href="https://www.gsi.go.jp/" target="_blank">国土地理院</a>',
  //     legend:
  //         '<div class="layer-legend">' +
  //         '<ul class="layer-legend-list">' +
  //         '<li><span style="background:#1976D2;"></span>国土地理院</li>' +
  //         '<li><span style="background:#4CAF50;"></span>米軍</li>' +
  //         '<li><span style="background:#795548;"></span>陸軍</li>' +
  //         '</ul>' +
  //         '</div>',
  //     ext: { name: 'extGsiPp' },
  // },
  // AI OSM検索はツールボックスから呼び出すため、レイヤーからは削除
  // {
  //   id: 'oh-overpass-ai',
  //   label: "<span style='color: red'>NEW</span>AI OSM検索",
  //   source: overpassSource,
  //   layers: [overpassPolygon, overpassPolygonLine, overpassLine, overpassPoint],
  //   attribution:
  //     '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  //   ext: { name: 'extOsmAi' },
  // },
  //   {
  //       id: 'oh-shinrin',
  //       label: '🆕森林地域',
  //       source: shinrinSource,
  //       layers: [shinrinLayer, shinrinLayerLine],
  //       attribution:
  //           '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A13-2015.html" target="_blank">国土数値情報</a>' +
  //           '<div class="legend-scale">' +
  //           '<ul class="legend-labels">' +
  //           '<li><span style="background:rgba(34, 139, 34, 0.7);"></span>森林地域</li>' +
  //           '<li><span style="background:rgba(0, 100, 0, 0.7);"></span>国有林</li>' +
  //           '<li><span style="background:rgba(107, 142, 35, 0.7);"></span>地域森林計画対象民有林</li>' +
  //           '<li><span style="background:rgba(46, 139, 87, 0.7);"></span>保安林</li>' +
  //           '</ul>' +
  //           '</div>',
  //   },
  //   {
  //       id: 'oh-choju',
  //       label: '🆕鳥獣保護区',
  //       source: chojuSource,
  //       layers: [chojuLayer, chojuLayerLine],
  //       attribution:
  //           '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A15.html" target="_blank">国土数値情報</a>' +
  //           '<div class="legend-scale">' +
  //           '<ul class="legend-labels">' +
  //           '<li><span style="background:rgba(255, 165, 0, 0.7);"></span>鳥獣保護区</li>' +
  //           '<li><span style="background:rgba(255, 69, 0, 0.7);"></span>特別保護地区</li>' +
  //           '<li><span style="background:rgba(255, 215, 0, 0.7);"></span>休猟区</li>' +
  //           '<li><span style="background:rgba(255, 140, 0, 0.7);"></span>特例休猟区</li>' +
  //           '</ul>' +
  //           '</div>',
  //   },
  //   {
  //       id: 'oh-keisatsu',
  //       label: '<span style="color:orange;">NEW</span>警察署',
  //       source: keisatsuSource,
  //       layers: [keisatsuPolygonLayer, keisatsuPolygonLineLayer, keisatsuPointLayer, keisatsuPointLayerLabel],
  //       attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P18.html" target="_blank">国土数値情報</a>',
  //   },
  //   {
  //       id: 'oh-yubinkyoku',
  //       label: '<span style="color:orange;">NEW</span>郵便局',
  //       source: yubinkyokuSource,
  //       layers: [yubinkyokuPointLayer, yubinkyokuPointLayerLabel],
  //       attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P30.html" target="_blank">国土数値情報</a>',
  //   },
  //   {
  //       id: 'oh-shobosho',
  //       label: '<span style="color:orange;">NEW</span>消防署',
  //       source: shoboshoSource,
  //       layers: [shoboshoPolygonLayer, shoboshoPolygonLineLayer, shoboshoPointLayer, shoboshoPointLayerLabel],
  //       attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P17.html" target="_blank">国土数値情報</a>',
  //   },
  //   {
  //       id: 'oh-population-pyramid',
  //       label: '<span style="color:orange;">NEW</span>人口ピラミッド（市区町村別）',
  //       source: populationPyramidSource,
  //       layers: [populationPyramidPolygonLayer, populationPyramidHighlightLayer, populationPyramidLineLayer, populationPyramidLabelLayer],
  //       attribution: '<a href="https://hanishina.net/maps/historydata.html" target="_blank">hanishina.net</a> / <a href="https://dashboard.e-stat.go.jp/" target="_blank">統計ダッシュボード</a>',
  //   },
  // {
  //     id: 'oh-gsi-optimal-bvmap',
  //     label: '<span style="color:orange;">NEW</span>最適化ベクトルタイル',
  //     sources: bvmapSources,
  //     layers: bvmapLayers,
  //     attribution: '<a href="https://github.com/gsi-cyberjapan/optimal_bvmap" target="_blank">国土地理院 最適化ベクトルタイル</a>',
  //     ext: { name: 'extGsiBvmap' },
  // },
  // {
  //     id: 'oh-gyosei-history',
  //     label: '<span style="color:orange;">NEW</span>行政界履歴（1920〜）',
  //     source: gyoseiHistorySource,
  //     layers: [gyoseiHistoryLayerQuery, gyoseiHistoryLayerPolygon, gyoseiHistoryLayerLine, gyoseiHistoryLayerLabel],
  //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2025.html" target="_blank">国土数値情報</a> / <a href="https://hanishina.net/maps/historydata.html" target="_blank">hanishina.net</a>',
  //     ext: { name: 'ext-gyosei-history' },
  // },
  // {
  //     id: 'oh-school',
  //     label: '<span style="color:pink;">NEW</span>学校データ',
  //     source: schoolSource,
  //     layers: [schoolLayer, schoolLayerLabel],
  //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P29-2023.html" target="_blank">国土数値情報</a>',
  //     ext: { name: 'extSchool' },
  // },
  // {
  //     id: 'oh-tadankai',
  //     label: '<span style="color:orange;">NEW</span>多段階浸水想定',
  //     source: tadankaiSource,
  //     layers: [tadankaiLayerPolygon, tadankaiLayerLine],
  //     ext: { name: 'ext-tadankai' },
  // },
  // {
  //     id: 'oh-kozui',
  //     label: '<span style="color:orange;">NEW</span>洪水浸水想定区域（河川単位）',
  //     source: kozuiSource,
  //     layers: [kozuiLayer, kozuiLayerLine],
  //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
  //     ext: { name: 'extKozui' },
  // },
  // {
  //     id: 'oh-kozui-3d',
  //     label: '<span style="color:orange;">NEW</span>洪水浸水想定区域（河川単位）3D',
  //     source: kozuiSource,
  //     layers: [kozui3dLayer],
  //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
  //     ext: { name: 'extKozui3d' },
  // },
  // {
  //     id: 'oh-bakumatsu',
  //     label: '<span style="color:orange;">NEW</span>幕末期近世の村',
  //     source: bakumatsuSource,
  //     layers: [bakumatsuLayer, bakumatsuLayerLine, bakumatsuLayerLabel],
  //     attribution:
  //         '出典：<a href="https://github.com/yaoyue00085856/Kyudaka_agrivillage" target="_blank">幕末期近世村領域データ</a>',
  //     ext: { name: 'extBakumatsu' },
  // },
  // {
  //     id: 'oh-koaza',
  //     label: '<span style="color:orange;">NEW</span>関東小字地図',
  //     sources: [koazaSource, muraSource, muraCenterSource],
  //     layers: [muraCenterLabel, koazaLayer, koazaLayerLine, koazaLayerLabel, muraLayer, muraLayerLine],
  //     attribution: '<a href="https://koaza.net/" target="_blank">関東小字地図</a>',
  //     ext: { name: 'extKoaza' },
  // },

  // {
  //   id: 'oh-ezosansen',
  //   label: '<span style="color:red;">NEW</span>東西蝦夷山川地理取調図',
  //   sources: [ezosansenSource],
  //   layers: [ezosansenLayer],
  //   attribution: '<a href="https://github.com/koukita/touzaiezo" target="_blank">喜多氏のgithub</a>',
  // },
  // {
  //   id: 'oh-ezosansen2',
  //   label: '<span style="color:red;">NEW</span>東西蝦夷山川地理取調図2',
  //   sources: [ezosansen2Source],
  //   layers: [ezosansen2Layer],
  //   attribution: '<a href="https://github.com/koukita/touzaiezo" target="_blank">喜多氏のgithub</a>',
  // },
  // {
  //   id: 'oh-rain-radar',
  //   label: '<span style="color:red;">NEW</span>雨雲レーダー',
  //   sources: [rainRadarSource],
  //   layers: [rainRadarLayer],
  //   attribution: '<a href="https://www.jma.go.jp/bosai/nowc/" target="_blank">気象庁</a>',
  //   ext: { name: 'extRainRadar' },
  // },
  // {
  //   id: 'oh-himawari',
  //   label: '<span style="color:red;">NEW</span>ひまわり衛星画像',
  //   sources: [himawariSource],
  //   layers: [himawariLayer],
  //   attribution: '<a href="https://www.jma.go.jp/bosai/map.html#contents=himawari" target="_blank">気象庁</a>',
  //   ext: { name: 'extHimawari' },
  // },
  // {
  //   id: 'oh-kikukuru',
  //   label: '<span style="color:red;">NEW</span>キキクル（危険度分布）',
  //   sources: [kikukuruDummySource],
  //   layers: [kikukuruDummyLayer],
  //   attribution: '<a href="https://www.jma.go.jp/bosai/risk/" target="_blank">気象庁</a>',
  //   ext: { name: 'extKikukuru' },
  // },
  // {
  //     id: 'oh-iseki-p32',
  //     label: '<span style="color:red;">NEW</span>都道府県指定文化財',
  //     source: isekiP32Source,
  //     layers: [isekiP32Layer, isekiP32LayerHeatmap, isekiP32LayerLabel],
  //     attribution:
  //         '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報（都道府県指定文化財データ）</a>',
  // },
  // {
  //     id: 'oh-city-s25',
  //     label: '<span style="color:red;">NEW</span>S25市町村',
  //     source: cityS25Source,
  //     layers: [cityS25Layer, cityS25LayerLine, cityS25LayerLabel],
  //     ext: { name: 'ext-city-s25' },
  // },
  // 天気フォルダ
  {
    id: 'tenki',
    label: '天気',
    nodes: [
      {
        id: 'oh-amedas-folder',
        label: '️AMeDAS（気象観測）',
        description:
          '気象庁AMeDAS約1300地点のリアルタイム観測データ。気温・降水量・風速・湿度・気圧を色分け表示。10分ごと自動更新。クリックで詳細情報',
        sources: [amedasSource],
        layers: [amedasLayer, amedasLabelLayer, amedasWindArrowLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/amedas/" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extAmedas' },
      },
      {
        id: 'oh-rain-radar-folder',
        label: '雨雲レーダー',
        sources: [rainRadarSource],
        layers: [rainRadarLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/nowc/" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extRainRadar' },
      },
      {
        id: 'oh-himawari-folder',
        label: 'ひまわり衛星画像',
        sources: [himawariSource],
        layers: [himawariLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/map.html#contents=himawari" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extHimawari' },
      },
      {
        id: 'oh-kikukuru-folder',
        label: 'キキクル（危険度分布）',
        description:
          '気象庁が10分ごとに更新するリアルタイム危険度情報。土砂キキクル・浸水キキクル・洪水キキクルの3種類。5段階危険度（注意〜極めて危険）で表示。1km〜250mメッシュ精度',
        sources: [kikukuruSource],
        layers: [kikukuruLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/risk/" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extKikukuru' },
      },
      {
        id: 'oh-aircraft-folder',
        label: '✈️航空機トラッキング',
        description:
          'OpenSky Networkのリアルタイムデータ。表示範囲内の航空機位置・高度・進行方向を表示。高度に応じた色分け。クリックで詳細情報',
        sources: [aircraftSource],
        layers: [aircraftLayer, aircraftLabelLayer],
        attribution: '<a href="https://opensky-network.org/" target="_blank">OpenSky Network</a>',
        credit: 'OpenSky Network',
        ext: { name: 'extAircraft' },
      },
      // OpenWeatherMap 天気タイル（世界全域・リアルタイム）
      {
        id: 'oh-owm-precipitation',
        label: '🌧️降水量（世界）',
        description: 'OpenWeatherMapのリアルタイム降水量マップ。世界全域をカバー。3時間ごと更新',
        sources: [owmPrecipitationSource],
        layers: [owmPrecipitationLayer],
        attribution: '<a href="https://openweathermap.org/" target="_blank">OpenWeatherMap</a>',
        credit: 'OpenWeatherMap',
        ext: { name: 'extOwm' },
      },
      {
        id: 'oh-owm-clouds',
        label: '☁️雲量（世界）',
        description: 'OpenWeatherMapのリアルタイム雲量分布マップ。世界全域をカバー',
        sources: [owmCloudsSource],
        layers: [owmCloudsLayer],
        attribution: '<a href="https://openweathermap.org/" target="_blank">OpenWeatherMap</a>',
        credit: 'OpenWeatherMap',
        ext: { name: 'extOwm' },
      },
      {
        id: 'oh-owm-temp',
        label: '🌡️気温（世界）',
        description: 'OpenWeatherMapのリアルタイム気温分布マップ。世界全域をカバー',
        sources: [owmTempSource],
        layers: [owmTempLayer],
        attribution: '<a href="https://openweathermap.org/" target="_blank">OpenWeatherMap</a>',
        credit: 'OpenWeatherMap',
        ext: { name: 'extOwm' },
      },
      {
        id: 'oh-owm-wind',
        label: '💨風速（世界）',
        description: 'OpenWeatherMapのリアルタイム風速分布マップ。世界全域をカバー',
        sources: [owmWindSource],
        layers: [owmWindLayer],
        attribution: '<a href="https://openweathermap.org/" target="_blank">OpenWeatherMap</a>',
        credit: 'OpenWeatherMap',
        ext: { name: 'extOwm' },
      },
      {
        id: 'oh-owm-pressure',
        label: '🔵気圧（世界）',
        description: 'OpenWeatherMapのリアルタイム気圧分布マップ。世界全域をカバー',
        sources: [owmPressureSource],
        layers: [owmPressureLayer],
        attribution: '<a href="https://openweathermap.org/" target="_blank">OpenWeatherMap</a>',
        credit: 'OpenWeatherMap',
        ext: { name: 'extOwm' },
      },
    ],
  },
  {
    id: 'drone',
    label: 'ドローン',
    nodes: [
      {
        id: 'oh-drone-zone',
        label: 'ドローン飛行禁止区域',
        source: droneZoneSource,
        layers: [droneZoneYellowFillLayer, droneZoneYellowLineLayer, droneZoneRedFillLayer, droneZoneRedLineLayer],
        attribution:
          '<a href="https://www.npa.go.jp/bureau/security/kogatamujinki/index.html" target="_blank">警察庁</a><br>飛行禁止場所<br>・対象施設の敷地・区域の上空（レッドゾーン）<br>・周囲おおむね300mの上空（イエローゾーン）',
        credit:
          '警察庁 飛行禁止場所 ・対象施設の敷地・区域の上空（レッドゾーン） ・周囲おおむね300mの上空（イエローゾーン）',
      },
      {
        id: 'oh-drone-plan',
        label: '🛸無人航空機 飛行計画',
        description: 'DIPS2.0飛行計画データ。2024年7月〜2025年6月の約195万件。飛行目的別の色分け・フィルタリング対応',
        source: dronePlanSource,
        layers: [dronePlanFillLayer, dronePlanLineLayer],
        attribution:
          '<a href="https://www.geospatial.jp/ckan/dataset/links-mujinkoukuukihikoukeikaku-2025_" target="_blank">G空間情報センター</a><br>国土交通省 DIPS2.0 無人航空機飛行計画データ（2025年度）<br>公共データ利用規約（第1.0版）',
        credit: '国土交通省 DIPS2.0 無人航空機飛行計画データ',
        ext: { name: 'extDronePlan' },
      },
    ],
  },
  {
    id: 'ksj',
    label: '生活インフラ',
    nodes: [
      {
        id: 'oh-keisatsu',
        label: '警察署',
        source: keisatsuSource,
        layers: [keisatsuPolygonLayer, keisatsuPolygonLineLayer, keisatsuPointLayer, keisatsuPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P18.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-yubinkyoku',
        label: '郵便局',
        source: yubinkyokuSource,
        layers: [yubinkyokuPointLayer, yubinkyokuPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P30.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-yubinku',
        label: '郵便番号境界',
        source: yubinkuSource,
        layers: [yubinkuFillLayer, yubinkuLineLayer, yubinkuLabelLayer],
        attribution:
          '<a href="https://hanishina.net/maps/yubindata.html" target="_blank">郵便番号境界データ（hanishina.net）</a>',
        credit: '郵便番号境界データ（hanishina.net）',
        ext: { name: 'extYubinku' },
      },
      {
        id: 'oh-shobosho',
        label: '消防署',
        source: shoboshoSource,
        layers: [shoboshoPolygonLayer, shoboshoPolygonLineLayer, shoboshoPointLayer, shoboshoPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P17.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-gas-station',
        label: '燃料給油所',
        source: gasStationSource,
        layers: [gasStationPointLayer, gasStationPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P07-2016.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-city-park',
        label: '都市公園',
        source: cityParkSource,
        layers: [cityParkPointLayer, cityParkPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P13.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-industrial-land',
        label: '工業用地',
        source: industrialLandSource,
        layers: [
          industrialComplexLayer,
          industrialComplexLineLayer,
          industrialIsolatedLayer,
          industrialIsolatedLineLayer,
        ],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L05.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-gyoko',
        label: '漁港',
        source: gyokoSource,
        layers: [gyokoLineLayer, gyokoPointLayer, gyokoPointLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-C09.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-kaso-h12',
        label: '過疎地域(H12)',
        source: kasoH12Source,
        layers: [kasoH12Layer, kasoH12LineLayer, kasoH12LabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A17-2017.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-shinko-sanson',
        label: '振興山村',
        source: shinkoSansonSource,
        layers: [shinkoSansonLayer, shinkoSansonLineLayer, shinkoSansonLabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A24-2016.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tokutei-nosan',
        label: '特定農山村地域',
        source: tokuteiNosanSource,
        layers: [tokuteiNosanLayer, tokuteiNosanLineLayer, tokuteiNosanLabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A25-2016.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'gousetsu-chitai',
        label: '豪雪地帯',
        source: gousetsuChitaiSource,
        nodes: [
          {
            id: 'oh-gousetsu-chitai',
            label: '豪雪地帯（ポリゴン）',
            source: gousetsuChitaiSource,
            layers: [gousetsuChitaiLayer, gousetsuChitaiLineLayer, gousetsuChitaiLabelLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A22-m.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
          },
          {
            id: 'oh-gousetsu-point',
            label: '気象観測点（最深積雪）',
            source: gousetsuChitaiSource,
            layers: [gousetsuPointLayer, gousetsuPointLabelLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A22-m.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
          },
        ],
      },
      {
        id: 'oh-heliport',
        label: 'ヘリポート',
        source: heliportSource,
        layers: [heliportLayer, heliportLabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N11.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'josuidou',
        label: '上水道',
        nodes: [
          {
            id: 'oh-josuidou-area',
            label: '給水区域',
            source: josuidouAreaSource,
            layers: [josuidouAreaLayer, josuidouAreaLineLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P21.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
          },
          {
            id: 'oh-josuidou-point',
            label: '浄水場',
            source: josuidouPointSource,
            layers: [josuidouPointLayer, josuidouPointLabelLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P21.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
          },
        ],
      },
    ],
  },
  {
    id: 'hikkai',
    label: '筆界調査データベース',
    nodes: [
      {
        id: 'oh-homusyo-2026-layer',
        label: '2026登記所地図',
        description:
          '法務省公開の登記所備付地図（2026年版）。筆界・地番・精度区分を表示。精度区分（甲一〜乙三）を色分け。SIMA・DXF・Shapefile・GeoJSON・KML・CSV出力対応。土地家屋調査士の実務に有用',
        sources: [homusyo2026Source, homusyo2026LowZoomSource],
        layers: [
          homusyo2026LowZoomFill,
          homusyo2026LowZoomLine,
          homusyo2026Fill,
          homusyo2026Line,
          homusyo2026Label,
          homusyo2026Vertex,
        ],
        attribution: '法務省登記所備付地図データ',
        credit: '法務省登記所備付地図データ',
        ext: {
          name: 'extTokizyo',
          layerPrefix: 'oh-homusyo-2026',
          sourceId: 'homusyo-2026-source',
          highlightSuffix: '_tokizyo2026',
          dbVersion: '2026',
        },
      },
      {
        id: 'oh-homusyo-2026-kijyunten',
        label: '2026登記所地図-基準点',
        sources: [homusyo2026KijyuntenSource],
        layers: [homusyo2026LayerKijyunten, homusyo2026LayerKijyuntenLabel],
        attribution:
          '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
        credit: '法務省登記所備付地図データ',
        ext: { name: 'extKijyunten' },
      },
      {
        id: 'oh-homusyo-2026-zukaku',
        label: '2026登記所地図-図郭',
        sources: [homusyo2026ZukakuSource],
        layers: [homusyo2026LayerZukaku, homusyo2026LayerZukakuLine, homusyo2026LayerZukakuLabel],
        attribution:
          '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
        credit: '法務省登記所備付地図データ',
      },
      {
        id: 'oh-homusyo-2025-layer',
        label: '2025登記所地図',
        description:
          '法務省公開の登記所備付地図。筆界・地番・精度区分を表示。精度区分（甲一〜乙三）を色分け。SIMA・DXF・Shapefile・GeoJSON・KML・CSV出力対応。土地家屋調査士の実務に有用',
        sources: [homusyo2025Source, homusyo2025LowZoomSource],
        layers: [
          homusyo2025LowZoomFill,
          homusyo2025LowZoomLine,
          homusyo2025Fill,
          homusyo2025Line,
          homusyo2025Label,
          homusyo2025Vertex,
        ],
        attribution: '法務省登記所備付地図データ',
        credit: '法務省登記所備付地図データ',
        ext: { name: 'extTokizyo' },
      },
      {
        id: 'oh-homusyo-2025-kijyunten',
        label: '2025登記所地図-基準点',
        sources: [homusyo2025KijyuntenSource],
        layers: [homusyo2025LayerKijyunten, homusyo2025LayerKijyuntenLabel],
        attribution:
          '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
        credit: '法務省登記所備付地図データ',
        ext: { name: 'extKijyunten' },
      },
      {
        id: 'oh-homusyo-2025-zukaku',
        label: '2025登記所地図-図郭',
        sources: [homusyo2025ZukakuSource],
        layers: [homusyo2025LayerZukaku, homusyo2025LayerZukakuLine, homusyo2025LayerZukakuLabel],
        attribution:
          '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
        credit: '法務省登記所備付地図データ',
      },
      // {
      //   id: 'oh-homusyo-2025-zukaku',
      //   label: '🧪2026登記所地図-図郭テスト',
      //   description: 'テストデータ: 宮崎市生目台 52図郭',
      //   sources: [homusyo2025TestZukakuSource],
      //   layers: [homusyo2025TestZukakuFill, homusyo2025TestZukakuLine, homusyo2025TestZukakuLabel],
      //   attribution: '法務省登記所備付地図データ（XML再構築）',
      // },
      {
        id: 'oh-chibanzu-all2',
        label: '⭐️全国地番図公開マップ️',
        description:
          '有志の開示請求で取得したデータや自治体オープンデータを集約した地番図。コメント機能でディスカッション可能。市民参加型の筆界データベース',
        sources: [cityGeojsonSource, ...chibanzuSources, ...publicSources],
        layers: [cityGeojsonPolygonLayer, cityGeojsonLineLayer, cityGeojsonLabelLayer, cityGeojsonLabelLayer2],
        ext: { name: 'ext-chibanzu' },
      },
      {
        id: 'oh-syochiiki2',
        label: '2020 町丁・字等別境界',
        source: syochiikiSource,
        layers: [syochiikiLayer, syochiikLayerLine2, syochiikiLayerLabel2],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/stat-search/files?page=1&toukei=00200521&tstat=000001136464&cycle=0&tclass1=000001136472" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
        ext: { name: 'ext-syochiiki2' },
      },
      {
        id: 'oh-fude',
        label: '農地の区画情報(筆ポリゴン)',
        source: fudeSource,
        layers: [fudeLayer, fudeLine],
        attribution:
          '<a href="https://www.maff.go.jp/j/tokei/porigon/" target="_blank">農地の区画情報（筆ポリゴン）のデータ提供・利用</a>',
        credit: '農地の区画情報（筆ポリゴン）のデータ提供・利用',
      },
      // {
      //     id: 'oh-zeni',
      //     label: "善意の基準局",
      //     sources: [zeniSource,zeniCircleSource],
      //     layers:[zeniCircleLayer,zeniCenterPointLayer,zeniCircleLayerLine],
      //     attribution: '<a href="https://github.com/Bolero-fk/ZeniKijunkyokuChecker" target="_blank">ZeniKijunkyokuChecker</a><br>' +
      //         '<a href="https://rtk.silentsystem.jp/" target="_blank">善意の基準局掲示板</a>',
      //     ext: {name:'extZeni'}
      // },
      {
        id: 'oh-minden',
        label: '民間等電子基準点',
        sources: [mindenSource],
        layers: [mindenCircleLayer, mindenCenterPointLayer],
        attribution:
          '<a href="https://sakura.3ku.jp/gnss/private_gnss-based_control_station/minden/" target="_blank">桜町測量 日々是精進</a>',
        credit: '桜町測量 日々是精進',
        ext: { name: 'extMinden' },
      },
      {
        id: 'oh-ntrip',
        label: 'その他RTK基準局',
        sources: [ntripSource],
        layers: [ntripCircleLayer, ntripCenterPointLayer],
        attribution:
          '<a href="http://ntrip1.bizstation.jp:2101/" target="_blank">Link1</a> ' +
          '<a href="http://geortk.jp:2101/" target="_blank">Link2</a><br>',
        credit: 'Link1  +  Link2',
        ext: { name: 'extNtrip' },
      },
      {
        id: 'oh-nogyochiiki',
        label: 'H27農業地域',
        source: nogyochiikiSource,
        layers: [nogyochiikiLayer, nogyochiikiLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A12.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        // ext: {name:'extTokijyo'}
      },
      {
        id: 'oh-kizyunten',
        label: '基本基準点',
        description:
          '国土地理院の三角点・電子基準点・水準点の位置。測量時の既知点として活用。測量・土地家屋調査の基礎データ',
        source: kizyuntenSource,
        layers: [kizyuntenPoint, kizyuntenPointLabel, kizyuntenNameLabel],
        attribution: '<a href="" target="_blank"></a>',
        ext: { name: 'extKhonkijyunten' },
      },
      {
        id: 'oh-toshikan',
        label: '都市部官民基準点',
        source: toshikanSource,
        layers: [toshikanLayer, toshikanLayerLabel, toshikanLabelLayer],
        attribution:
          '<a href="https://gaikuchosa.mlit.go.jp/gaiku/system/download_kanmin.phtml" target="_blank">都市部官民基準点</a>',
        credit: '都市部官民基準点',
        ext: { name: 'extToshikan' },
      },
      {
        id: 'oh-gaiku',
        label: '街区基準点',
        source: gaikuSource,
        layers: [gaikuLayer, gaikuLayerLabel, gaikuLabelLayer],
        attribution:
          '<a href="https://gaikuchosa.mlit.go.jp/gaiku/system/download.phtml" target="_blank">街区基準点</a>',
        credit: '街区基準点',
        ext: { name: 'extGaiku' },
      },
      {
        id: 'oh-saga-kijyunten',
        label: '佐賀県基準点',
        source: sagakijyuntenSource,
        layers: [sagakijyuntenPointLayer, sagakijyuntenLabelLayer],
        attribution:
          '<a href="http://www.sagakoushoku.com/toukikijyunten/" target="_blank">佐賀県公共嘱託登記土地家屋調査士協会</a>',
        credit: '佐賀県公共嘱託登記土地家屋調査士協会',
        ext: { name: 'extSaga' },
      },
      {
        id: 'oh-gsi-jusho',
        label: '住居表示住所',
        source: gsiJushoSource,
        layers: [gsiJushoCircle, gsiJushoLabel],
        attribution:
          '<a href="https://saigai.gsi.go.jp/jusho/download/index.html" target="_blank">国土地理院（住居表示住所）</a>',
        credit: '国土地理院（住居表示住所）',
      },
      {
        id: 'citychibanzu',
        label: '市町村地番図',
        nodes: [
          // {
          //     id: 'oh-chibanzu-all',
          //     label: "全国地番図（オープンデータ）",
          //     sources: chibanzuSources,
          //     layers: chibanzuLayers0,
          //     ext: {name:'ext-chibanzu'}
          // },
          // {
          //     id: 'oh-chibanzu-all2',
          //     label: '全国地番図公開マップ',
          //     sources: [cityGeojsonSource,...chibanzuSources,...publicSources],
          //     layers: [...chibanzuLayers1,...publicLayers0,cityGeojsonPolygonLayer,cityGeojsonLineLayer,cityGeojsonLabelLayer],
          //     // sources: [cityPmtilesSource,...chibanzuSources,...publicSources],
          //     // layers: [...chibanzuLayers1,...publicLayers0,cityPmtilesPolygonLayer,cityPmtilesLineLayer,cityPmtilesLabelLayer],
          //     ext: {name:'ext-chibanzu'}
          // },
          ...chibanzuLayers2,
          ...publicLayers,
        ],
      },
      {
        id: 'oh-chiseki-v2',
        label: '地籍調査進捗率（R6年度末）',
        sources: [chisekiV2DummySource],
        layers: [chisekiV2DummyLayer],
        attribution:
          '<a href="https://www.chiseki.go.jp/situation/status/index.html" target="_blank">国土交通省 地籍調査Webサイト</a>（令和6年度末時点）',
        credit: '国土交通省 地籍調査Webサイト （令和6年度末時点）',
        ext: { name: 'extChisekiV2' },
      },
    ],
  },

  // ==================== 国土数値情報 ====================
  {
    id: 'kokudo-suuchi',
    label: '⭐️国土数値情報',
    nodes: [
      {
        id: 'kokudo-mizu-tochi',
        label: '1. 国土（水・土地）',
        nodes: [
          {
            id: 'kk-suiiki',
            label: '水域',
            nodes: [
              {
                id: 'oh-kk-dam',
                label: 'ダム（W01）',
                source: kkDamSource,
                layers: [kkDamCircle, kkDamLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W01.html" target="_blank">国土数値情報（ダム）</a>',
                credit: '国土数値情報（ダム）',
              },
              {
                id: 'oh-kk-kosho',
                label: '湖沼（W09）',
                source: kkKoshoSource,
                layers: [kkKoshoFill, kkKoshoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W09-v2_0.html" target="_blank">国土数値情報（湖沼）</a>',
                credit: '国土数値情報（湖沼）',
              },
              {
                id: 'oh-kk-kasen',
                label: '河川（W05）',
                source: kkKasenSource,
                layers: [kkKasenLine, kkKasenLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W05.html" target="_blank">国土数値情報（河川）</a>',
                credit: '国土数値情報（河川）',
              },
              {
                id: 'oh-kk-kaigan',
                label: '海岸線（C23）',
                source: kkKaiganSource,
                layers: [kkKaiganLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-C23.html" target="_blank">国土数値情報（海岸線）</a>',
                credit: '国土数値情報（海岸線）',
              },
              {
                id: 'oh-kk-kaigan-hozen',
                label: '海岸保全施設（P23）',
                source: kkKaiganHozenSource,
                layers: [kkKaiganHozenCircle, kkKaiganHozenLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P23.html" target="_blank">国土数値情報（海岸保全施設）</a>',
                credit: '国土数値情報（海岸保全施設）',
              },
              {
                id: 'oh-ryuiki',
                label: '流域メッシュ（W07）',
                source: ryuikiSource2,
                layers: [ryuikiLayer2, ryuikiLayerLine2],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W07.html" target="_blank">国土数値情報（流域メッシュ）</a>',
                credit: '国土数値情報（流域メッシュ）',
              },
            ],
          },
          {
            id: 'kk-chikei',
            label: '地形',
            nodes: [
              {
                id: 'oh-mountain',
                label: '日本の主な山岳（国土地理院）',
                source: mountainSource,
                layers: [mountainLayer, mountainLabelLayer],
                attribution:
                  '<a href="https://maps.gsi.go.jp/3d/mountain/mountain.html" target="_blank">国土地理院</a>',
                credit: '国土地理院',
                ext: { name: 'extMountain' },
              },
              {
                id: 'oh-teiichi',
                label: '低位地帯（G08）',
                source: teiichiSource,
                layers: [teiichiLayerPolygon, teiichiLayerLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G08.html" target="_blank">国土数値情報（低位地帯）</a>',
                credit: '国土数値情報（低位地帯）',
              },
              {
                id: 'oh-zosei',
                label: '大規模盛土造成地（A54）',
                source: zoseiSource,
                layers: [zoseiLayer, zoseiLayerLine, zoseiLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A54-2023.html" target="_blank">国土数値情報（大規模盛土造成地）</a>',
                credit: '国土数値情報（大規模盛土造成地）',
              },
              {
                id: 'oh-elevation3-mesh',
                label: '標高・傾斜度3次メッシュ1km（G04-a）',
                source: elevation3MeshSource,
                layers: [elevation3MeshFill, elevation3MeshLine, elevation3MeshLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-a.html" target="_blank">国土数値情報（標高・傾斜度3次メッシュ）</a>',
                credit: '国土数値情報（標高・傾斜度3次メッシュ）',
              },
              {
                id: 'oh-elevation-mesh',
                label: '標高・傾斜度4次メッシュ500m（G04-c）',
                source: elevationMeshSource,
                layers: [elevationMeshFill, elevationMeshLine, elevationMeshLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-c.html" target="_blank">国土数値情報（標高・傾斜度4次メッシュ）</a>',
                credit: '国土数値情報（標高・傾斜度4次メッシュ）',
              },
              {
                id: 'oh-kk-hyoko5-mesh',
                label: '標高・傾斜度5次メッシュ250m（G04-d）（調整中）',
                source: kkHyoko5MeshSource,
                layers: [kkHyoko5MeshFill, kkHyoko5MeshLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-d.html" target="_blank">国土数値情報（標高・傾斜度5次メッシュ）</a>',
                credit: '国土数値情報（標高・傾斜度5次メッシュ）',
              },
              {
                id: 'oh-kk-heinenchi-mesh',
                label: '平年値メッシュ1km（G02）',
                source: kkHeinenchiMeshSource,
                layers: [kkHeinenchiMeshFill],
                ext: { name: 'extHeinenchi' },
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G02-v3_0.html" target="_blank">国土数値情報（平年値メッシュ）</a>',
                credit: '国土数値情報（平年値メッシュ）',
              },
            ],
          },
          {
            id: 'kk-tochi-riyo',
            label: '土地利用',
            nodes: [
              {
                id: 'oh-kk-nogyo',
                label: '農業地域（A12）',
                source: kkNogyoSource,
                layers: [kkNogyoFill, kkNogyoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A12.html" target="_blank">国土数値情報（農業地域）</a>',
                credit: '国土数値情報（農業地域）',
              },
              {
                id: 'oh-kk-shinrin',
                label: '森林地域（A13）',
                source: kkShinrinSource,
                layers: [kkShinrinFill, kkShinrinLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A13.html" target="_blank">国土数値情報（森林地域）</a>',
                credit: '国土数値情報（森林地域）',
              },
              {
                id: 'oh-kk-kokuyurin',
                label: '国有林野（A45）',
                source: kkKokuyurinSource,
                layers: [kkKokuyurinFill, kkKokuyurinLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A45.html" target="_blank">国土数値情報（国有林野）</a>',
                credit: '国土数値情報（国有林野）',
              },
              {
                id: 'oh-industrial-land',
                label: '工業用地（L05）',
                source: industrialLandSource,
                layers: [
                  industrialComplexLayer,
                  industrialComplexLineLayer,
                  industrialIsolatedLayer,
                  industrialIsolatedLineLayer,
                ],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L05.html" target="_blank">国土数値情報（工業用地）</a>',
                credit: '国土数値情報（工業用地）',
              },
              {
                id: 'oh-kk-tochiriyo3-mesh',
                label: '土地利用3次メッシュ1km（L03-a）',
                source: kkTochiriyo3MeshSource,
                layers: [kkTochiriyo3MeshFill],
                ext: { name: 'extTochiriyo3' },
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-a-2021.html" target="_blank">国土数値情報（土地利用3次メッシュ）</a>',
                credit: '国土数値情報（土地利用3次メッシュ）',
              },
              {
                id: 'oh-tochiriyo100',
                label: '土地利用細分メッシュ100m（L03-b）',
                source: tochiriyo100Source,
                layers: [tochiriyo100Layer],
                attribution:
                  "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報（土地利用細分メッシュ）</a>",
                credit: '国土数値情報（土地利用細分メッシュ）',
                ext: { name: 'extTochiriyo' },
              },
              {
                id: 'oh-tochiriyo-raster',
                label: '土地利用細分メッシュ100mラスタ（L03-b）',
                source: tochiriyoRasterSource,
                layers: [tochiriyoRasterLayer],
                attribution:
                  "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報（土地利用細分メッシュ・ラスタ版）</a>",
                credit: '国土数値情報（土地利用細分メッシュ・ラスタ版）',
              },
              {
                id: 'oh-blend-tochiriyo-hillshade',
                label: '土地利用×陰影（L03-b）',
                sources: [blendBaseTochiriyoSource, blendTochiriyoHillshadeSource],
                layers: [blendBaseTochiriyoLayer, blendTochiriyoHillshadeLayer],
                attribution:
                  "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報 土地利用細分メッシュ</a>・国土地理院 陰影起伏図",
                credit: '国土数値情報 土地利用細分メッシュ ・国土地理院 陰影起伏図',
                ext: { name: 'extBlendHillshade' },
              },
              {
                id: 'oh-kk-tochiriyo-toshi-mesh',
                label: '都市地域土地利用細分メッシュ100m（L03-b-u）',
                source: kkTochiriyoToshiMeshSource,
                layers: [kkTochiriyoToshiMeshFill],
                ext: { name: 'extTochiriyoToshi' },
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b-u-2021.html" target="_blank">国土数値情報（都市地域土地利用細分メッシュ）</a>',
                credit: '国土数値情報（都市地域土地利用細分メッシュ）',
              },
              {
                id: 'oh-kk-tochiriyo-shosai-mesh',
                label: '土地利用詳細メッシュ50m（L03-b-c）（調整中）',
                source: kkTochiriyoShosaiMeshSource,
                layers: [kkTochiriyoShosaiMeshFill, kkTochiriyoShosaiMeshLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b-c-2021.html" target="_blank">国土数値情報（土地利用詳細メッシュ）</a>',
                credit: '国土数値情報（土地利用詳細メッシュ）',
              },
            ],
          },
          {
            id: 'kk-chika',
            label: '地価',
            nodes: [
              {
                id: 'oh-kk-chika-koji',
                label: '地価公示（L01）',
                description:
                  '国土交通省が毎年1月1日時点で評価する公示価格。地価推移グラフで過去の変動を視覚化できる。不動産取引の指標。国土交通省土地鑑定委員会',
                source: kkChikaKojiSource,
                layers: [kkChikaKojiCircle, kkChikaKojiLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-v3_0.html" target="_blank">国土数値情報（地価公示）</a>',
                credit: '国土数値情報（地価公示）',
              },
              {
                id: 'oh-kk-chika-chosa',
                label: '地価調査（L02）',
                description:
                  '都道府県が毎年7月1日時点で評価する基準地価格。地価公示と半年ずれで、半年ごとの地価動向を把握できる。各都道府県が実施',
                source: kkChikaChosaSource,
                layers: [kkChikaChosaCircle, kkChikaChosaLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L02-v3_0.html" target="_blank">国土数値情報（地価調査）</a>',
                credit: '国土数値情報（地価調査）',
              },
            ],
          },
        ],
      },
      {
        id: 'kokudo-seisaku-kuiki',
        label: '2. 政策区域',
        nodes: [
          {
            id: 'kk-gyosei-chiiki',
            label: '行政地域',
            nodes: [
              {
                id: 'oh-kk-gyosei',
                label: '行政区域（N03）',
                source: kkGyoseiSource,
                layers: [kkGyoseiFill, kkGyoseiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2025.html" target="_blank">国土数値情報（行政区域）</a>',
                credit: '国土数値情報（行政区域）',
              },
              {
                id: 'oh-kk-did',
                label: 'DID人口集中地区（A16）',
                source: kkDidSource,
                layers: [kkDidFill, kkDidLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報（DID人口集中地区）</a>',
                credit: '国土数値情報（DID人口集中地区）',
              },
              {
                id: 'oh-did-timeline',
                label: 'DID人口集中地区（時系列）',
                source: didTimelineSource,
                layers: didTimelineLayers,
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報（DID人口集中地区）</a>',
                credit: '国土数値情報（DID人口集中地区）',
                ext: { name: 'extDidTimeline' },
              },
              {
                id: 'oh-kk-shogakkoku',
                label: '小学校区（A27）',
                source: kkShogakkokuSource,
                layers: [kkShogakkokuFill, kkShogakkokuLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A27-v2_1.html" target="_blank">国土数値情報（小学校区）</a>',
                credit: '国土数値情報（小学校区）',
              },
              {
                id: 'oh-kk-chugakkoku',
                label: '中学校区（A32）',
                source: kkChugakkokuSource,
                layers: [kkChugakkokuFill, kkChugakkokuLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A32.html" target="_blank">国土数値情報（中学校区）</a>',
                credit: '国土数値情報（中学校区）',
              },
              {
                id: 'oh-kk-iryouken',
                label: '医療圏（A38）',
                source: kkIryoukenSource,
                layers: [kkIryoukenFill, kkIryoukenLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A38.html" target="_blank">国土数値情報（医療圏）</a>',
                credit: '国土数値情報（医療圏）',
              },
              {
                id: 'oh-kk-keikan-keikaku',
                label: '景観計画区域（A35a）',
                source: kkKeikanKeikakuSource,
                layers: [kkKeikanKeikakuFill, kkKeikanKeikakuLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A35a.html" target="_blank">国土数値情報（景観計画区域）</a>',
                credit: '国土数値情報（景観計画区域）',
              },
              {
                id: 'oh-kk-keikan-chiku',
                label: '景観地区・準景観地区（A35b）',
                source: kkKeikanChikuSource,
                layers: [kkKeikanChikuFill, kkKeikanChikuLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A35b.html" target="_blank">国土数値情報（景観地区・準景観地区）</a>',
                credit: '国土数値情報（景観地区・準景観地区）',
              },
              {
                id: 'oh-kk-keikan-kenzobutsu',
                label: '景観重要建造物・樹木（A35c）',
                source: kkKeikanKenzobutsuSource,
                layers: [kkKeikanKenzobutsuCircle, kkKeikanKenzobutsuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A35c.html" target="_blank">国土数値情報（景観重要建造物・樹木）</a>',
                credit: '国土数値情報（景観重要建造物・樹木）',
              },
              {
                id: 'oh-kk-dentou-hozon',
                label: '伝統的建造物群保存地区（A43）',
                source: kkDentouHozonSource,
                layers: [kkDentouHozonFill, kkDentouHozonLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A43.html" target="_blank">国土数値情報（伝統的建造物群保存地区）</a>',
                credit: '国土数値情報（伝統的建造物群保存地区）',
              },
              {
                id: 'oh-kk-rekishi-fuuchi',
                label: '歴史的風致維持向上計画の重点地区（A44）',
                source: kkRekishiFuuchiSource,
                layers: [kkRekishiFuuchiFill, kkRekishiFuuchiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A44.html" target="_blank">国土数値情報（歴史的風致維持向上計画）</a>',
                credit: '国土数値情報（歴史的風致維持向上計画）',
              },
              {
                id: 'oh-gyosei-history',
                label: '行政界履歴 1920〜（N03）',
                source: gyoseiHistorySource,
                layers: [
                  gyoseiHistoryLayerQuery,
                  gyoseiHistoryLayerPolygon,
                  gyoseiHistoryLayerLine,
                  gyoseiHistoryLayerLabel,
                ],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2025.html" target="_blank">国土数値情報（行政区域）</a> / <a href="https://hanishina.net/maps/historydata.html" target="_blank">hanishina.net</a>',
                credit: '国土数値情報（行政区域） / hanishina.net',
                ext: { name: 'ext-gyosei-history' },
              },
              {
                id: 'oh-moritokisei',
                label: '宅地造成等工事規制区域・特定盛土等規制区域（A56）',
                source: moritokiseiSource,
                layers: [moritokiseiLayer, moritokiseiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A56-2025.html" target="_blank">国土数値情報（宅地造成等工事規制区域・特定盛土等規制区域）</a>' +
                  '<div class="legend-scale">' +
                  '<ul class="legend-labels">' +
                  '<li><span style="background:rgba(229,57,53,0.55);"></span>宅地造成等工事規制区域</li>' +
                  '<li><span style="background:rgba(30,136,229,0.55);"></span>特定盛土等規制区域</li>' +
                  '</ul></div>',
                credit: '国土数値情報（宅地造成等工事規制区域・特定盛土等規制区域）',
              },
            ],
          },
          {
            id: 'kk-toshi-keikaku',
            label: '都市計画決定情報',
            nodes: [
              {
                id: 'oh-youto2024',
                label: '用途地域（A29）',
                description:
                  '都市計画法に基づく土地利用の規制区分。住居系・商業系・工業系の13種類。建ぺい率・容積率を含む。不動産調査に必須。国交省都市計画決定GISデータ',
                sources: [youto2024Source],
                layers: [youto2024Layer, youto2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokei2024',
                label: '都市計画区域（A09）',
                description:
                  '市街化区域・市街化調整区域・非線引き区域の区分を表示。都市計画法に基づく。不動産調査で建築可否の確認に使う。国交省都市計画決定GISデータ',
                sources: [tokei2024Source],
                layers: [tokei2024Layer, tokei2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-senbiki2024',
                label: '区域区分（A55）',
                sources: [senbiki2024Source],
                layers: [senbiki2024Layer, senbiki2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-ritteki2024',
                label: '立地適正化計画（A50）',
                sources: [ritteki2024Source],
                layers: [ritteki2024Layer, ritteki2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-douro2024',
                label: '都市計画道路（A55）',
                sources: [douro2024Source],
                layers: [douro2024Layer, douro2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-kouen2024',
                label: '公園・緑地（P13）',
                sources: [kouen2024Source],
                layers: [kouen2024Layer, kouen2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-bouka2024',
                label: '防火地域（A55）',
                sources: [bouka2024Source],
                layers: [bouka2024Layer, bouka2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-chikukei2024',
                label: '地区計画（A55）',
                sources: [chikukei2024Source],
                layers: [chikukei2024Layer, chikukei2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tochiku2024',
                label: '土地区画整理事業（A55）',
                sources: [tochiku2024Source],
                layers: [tochiku2024Layer, tochiku2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tkbt2024',
                label: '特別用途地区（A55）',
                sources: [tkbt2024Source],
                layers: [tkbt2024Layer, tkbt2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-koudori2024',
                label: '高度利用地区（A55）',
                sources: [koudori2024Source],
                layers: [koudori2024Layer, koudori2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-koudoti2024',
                label: '高度地区（A55）',
                sources: [koudoti2024Source],
                layers: [koudoti2024Layer, koudoti2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-fuuchichiku2024',
                label: '風致地区（A55）',
                sources: [fuuchichiku2024Source],
                layers: [fuuchichiku2024Layer, fuuchichiku2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokuteiyouto2024',
                label: '特定用途制限地域（A55）',
                sources: [tokuteiyouto2024Source],
                layers: [tokuteiyouto2024Layer, tokuteiyouto2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-jyuntoshi2024',
                label: '準都市計画区域（A55）',
                sources: [jyuntoshi2024Source],
                layers: [jyuntoshi2024Layer, jyuntoshi2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-toshisaisei2024',
                label: '都市再生特別地区（A55）',
                sources: [toshisaisei2024Source],
                layers: [toshisaisei2024Layer, toshisaisei2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokuryoku2024',
                label: '特別緑地保全地区（A55）',
                sources: [tokuryoku2024Source],
                layers: [tokuryoku2024Layer, tokuryoku2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-rekifuu2024',
                label: '歴史的風土保存地区（A42）',
                sources: [rekifuu2024Source],
                layers: [rekifuu2024Layer, rekifuu2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokuteiyuudou2024',
                label: '特定用途誘導地区（A55）',
                sources: [tokuteiyuudou2024Source],
                layers: [tokuteiyuudou2024Layer, tokuteiyuudou2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-soubou2024',
                label: '航空機騒音障害防止地区（A55）',
                sources: [soubou2024Source],
                layers: [soubou2024Layer, soubou2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokuteibouka2024',
                label: '特定防災街区整備地区（A55）',
                sources: [tokuteibouka2024Source],
                layers: [tokuteibouka2024Layer, tokuteibouka2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-tokureiyouseki2024',
                label: '特例容積率適用地区（A55）',
                sources: [tokureiyouseki2024Source],
                layers: [tokureiyouseki2024Layer, tokureiyouseki2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-ryokukachiiki2024',
                label: '緑化地域（A55）',
                sources: [ryokukachiiki2024Source],
                layers: [ryokukachiiki2024Layer, ryokukachiiki2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-kyojyuchosei2024',
                label: '居住調整地域（A55）',
                sources: [kyojyuchosei2024Source],
                layers: [kyojyuchosei2024Layer, kyojyuchosei2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-kousoujyukyo2024',
                label: '高層住居誘導地区（A55）',
                sources: [kousoujyukyo2024Source],
                layers: [kousoujyukyo2024Layer, kousoujyukyo2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
              {
                id: 'oh-fukkousaiseikyoten2024',
                label: '復興再生拠点（A55）',
                sources: [fukkousaiseikyoten2024Source],
                layers: [fukkousaiseikyoten2024Layer, fukkousaiseikyoten2024LayerLine],
                attribution:
                  '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
                credit: '都市計画決定GISデータ（2024）',
              },
            ],
          },
          {
            id: 'kk-daitoshi-jouken',
            label: '大都市圏・条件不利地域',
            nodes: [
              {
                id: 'oh-kk-sandai-toshiken',
                label: '三大都市圏計画区域（A03）',
                source: kkSandaiToshikenSource,
                layers: [kkSandaiToshikenFill, kkSandaiToshikenLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A03.html" target="_blank">国土数値情報（三大都市圏計画区域）</a>',
                credit: '国土数値情報（三大都市圏計画区域）',
              },
              {
                id: 'oh-kk-kaso-chiiki',
                label: '過疎地域（A17）',
                source: kkKasoChiikiSource,
                layers: [kkKasoChiikiFill, kkKasoChiikiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A17-2017.html" target="_blank">国土数値情報（過疎地域）</a>',
                credit: '国土数値情報（過疎地域）',
              },
              {
                id: 'oh-kk-shinko-sanson',
                label: '振興山村（A24）',
                source: kkShinkoSansonSource,
                layers: [kkShinkoSansonFill, kkShinkoSansonLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A24-2016.html" target="_blank">国土数値情報（振興山村）</a>',
                credit: '国土数値情報（振興山村）',
              },
              {
                id: 'oh-kk-tokutei-nosan',
                label: '特定農山村地域（A25）',
                source: kkTokuteiNosanSource,
                layers: [kkTokuteiNosanFill, kkTokuteiNosanLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A25-2016.html" target="_blank">国土数値情報（特定農山村地域）</a>',
                credit: '国土数値情報（特定農山村地域）',
              },
              {
                id: 'oh-kk-rito-shinko',
                label: '離島振興対策実施地域（A19）',
                source: kkRitoShinkoSource,
                layers: [kkRitoShinkoFill, kkRitoShinkoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A19-2017.html" target="_blank">国土数値情報（離島振興対策実施地域）</a>',
                credit: '国土数値情報（離島振興対策実施地域）',
              },
              {
                id: 'oh-kk-rito-tokei',
                label: '離島振興統計（A19s・H22）',
                source: kkRitoTokeiSource,
                layers: [kkRitoTokeiFill, kkRitoTokeiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A19s.html" target="_blank">国土数値情報（離島振興統計）</a>',
                credit: '国土数値情報（離島振興統計）',
              },
              {
                id: 'oh-kk-amami-tokei',
                label: '奄美群島統計（A20s・H22）',
                source: kkAmamiTokeiSource,
                layers: [kkAmamiTokeiFill, kkAmamiTokeiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A20s.html" target="_blank">国土数値情報（奄美群島統計）</a>',
                credit: '国土数値情報（奄美群島統計）',
              },
              {
                id: 'oh-kk-ogasawara-tokei',
                label: '小笠原諸島統計（A21s・H22）',
                source: kkOgasawaraTokeiSource,
                layers: [kkOgasawaraTokeiFill, kkOgasawaraTokeiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A21s.html" target="_blank">国土数値情報（小笠原諸島統計）</a>',
                credit: '国土数値情報（小笠原諸島統計）',
              },
              {
                id: 'oh-kk-hanto-shinko',
                label: '半島振興対策実施地域（A18）',
                source: kkHantoShinkoSource,
                layers: [kkHantoShinkoFill, kkHantoShinkoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A18-2016.html" target="_blank">国土数値情報（半島振興対策実施地域）</a>',
                credit: '国土数値情報（半島振興対策実施地域）',
              },
              {
                id: 'oh-kk-hanto-tokei',
                label: '半島振興統計（A18s-a・H22）',
                source: kkHantoTokeiSource,
                layers: [kkHantoTokeiFill, kkHantoTokeiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A18s-a.html" target="_blank">国土数値情報（半島振興統計）</a>',
                credit: '国土数値情報（半島振興統計）',
              },
              {
                id: 'oh-kk-gosetsu-chitai',
                label: '豪雪地帯（A22）',
                source: kkGosetsuChitaiSource,
                layers: [kkGosetsuChitaiFill, kkGosetsuChitaiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A22-2016.html" target="_blank">国土数値情報（豪雪地帯）</a>',
                credit: '国土数値情報（豪雪地帯）',
              },
              {
                id: 'oh-gousetsu-point',
                label: '気象観測点・最深積雪（A22-m）',
                source: gousetsuChitaiSource,
                layers: [gousetsuPointLayer, gousetsuPointLabelLayer],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A22-m.html" target="_blank">国土数値情報（豪雪地帯・気象観測）</a>',
                credit: '国土数値情報（豪雪地帯・気象観測）',
              },
              {
                id: 'oh-kk-gosetsu-tokei',
                label: '豪雪地帯統計（A22s・H22）',
                source: kkGosetsuTokeiSource,
                layers: [kkGosetsuTokeiFill, kkGosetsuTokeiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A22s.html" target="_blank">国土数値情報（豪雪地帯統計）</a>',
                credit: '国土数値情報（豪雪地帯統計）',
              },
              {
                id: 'oh-kk-tokushu-dojo',
                label: '特殊土壌地帯（A23）',
                source: kkTokushuDojoSource,
                layers: [kkTokushuDojoFill, kkTokushuDojoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A23-2016.html" target="_blank">国土数値情報（特殊土壌地帯）</a>',
                credit: '国土数値情報（特殊土壌地帯）',
              },
              {
                id: 'oh-kk-hanto-douro',
                label: '半島循環道路（A37）',
                source: kkHantoDouroSource,
                layers: [kkHantoDouroLine2],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A37.html" target="_blank">国土数値情報（半島循環道路）</a>',
                credit: '国土数値情報（半島循環道路）',
              },
              {
                id: 'oh-kk-misshu-shigaichi',
                label: '密集市街地（A39）',
                source: kkMisshuShigaichiSource,
                layers: [kkMisshuShigaichiFill, kkMisshuShigaichiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A39-2015.html" target="_blank">国土数値情報（密集市街地）</a>',
                credit: '国土数値情報（密集市街地）',
              },
            ],
          },
          {
            id: 'kk-saigai-bousai',
            label: '災害・防災',
            nodes: [
              {
                id: 'oh-kk-hinan-shisetsu',
                label: '避難施設（P20）',
                source: kkHinanShisetsuSource,
                layers: [kkHinanShisetsuCircle, kkHinanShisetsuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P20.html" target="_blank">国土数値情報（避難施設）</a>',
                credit: '国土数値情報（避難施設）',
              },
              {
                id: 'oh-kk-tatsumaki',
                label: '竜巻等の突風（A30b）',
                source: kkTatsumakiSource,
                layers: [kkTatsumakiCircle, kkTatsumakiLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A30b.html" target="_blank">国土数値情報（竜巻等の突風）</a>',
                credit: '国土数値情報（竜巻等の突風）',
              },
              {
                id: 'oh-kk-dosha-saigai',
                label: '土砂災害警戒区域（A33）',
                description:
                  '土砂災害防止法（2001年）に基づき各都道府県が指定。警戒区域・特別警戒区域、土石流危険渓流、急傾斜地崩壊危険箇所、地すべり危険箇所の4カテゴリ。災害種別ごとに個別オン/オフ可能',
                source: kkDoshaSaigaiSource,
                layers: [kkDoshaSaigaiFill, kkDoshaSaigaiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A33-2024.html" target="_blank">国土数値情報（土砂災害警戒区域）</a>',
                credit: '国土数値情報（土砂災害警戒区域）',
              },
              {
                id: 'oh-kk-jisuberi',
                label: '地すべり防止区域（A46）',
                source: kkJisuberiSource,
                layers: [kkJisuberiFill, kkJisuberiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A46-2021.html" target="_blank">国土数値情報（地すべり防止区域）</a>',
                credit: '国土数値情報（地すべり防止区域）',
              },
              {
                id: 'oh-kk-kyukeisha',
                label: '急傾斜地崩壊危険区域（A47）',
                source: kkKyukeishaSource,
                layers: [kkKyukeishaFill, kkKyukeishaLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A47-2021.html" target="_blank">国土数値情報（急傾斜地崩壊危険区域）</a>',
                credit: '国土数値情報（急傾斜地崩壊危険区域）',
              },
              {
                id: 'oh-kk-saigai-kiken',
                label: '災害危険区域（A48）',
                source: kkSaigaiKikenSource,
                layers: [kkSaigaiKikenFill, kkSaigaiKikenLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A48-2021.html" target="_blank">国土数値情報（災害危険区域）</a>',
                credit: '国土数値情報（災害危険区域）',
              },
              {
                id: 'oh-kk-sabo-shiteichi',
                label: '砂防指定地（A52）',
                source: kkSaboShiteichiSource,
                layers: [kkSaboShiteichiFill, kkSaboShiteichiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A52-2023.html" target="_blank">国土数値情報（砂防指定地）</a>',
                credit: '国土数値情報（砂防指定地）',
              },
              {
                id: 'oh-kk-tsunami-shinsui',
                label: '津波浸水想定（A40）',
                description:
                  '津波発生時の浸水想定区域。3D立体表示で建物と浸水高さを比較可能。南海トラフ地震（今後30年以内に70〜80%の確率）の津波想定データも収録。国土数値情報',
                source: kkTsunamiShinsuiSource,
                layers: [kkTsunamiShinsuiFill, kkTsunamiShinsuiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A40-2024.html" target="_blank">国土数値情報（津波浸水想定）</a>',
                credit: '国土数値情報（津波浸水想定）',
              },
              {
                id: 'oh-kk-takashio-shinsui',
                label: '高潮浸水想定区域（A49）',
                source: kkTakashioShinsuiSource,
                layers: [kkTakashioShinsuiFill, kkTakashioShinsuiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A49-2024.html" target="_blank">国土数値情報（高潮浸水想定区域）</a>',
                credit: '国土数値情報（高潮浸水想定区域）',
              },
              {
                id: 'oh-kk-usui-shinsui',
                label: '雨水出水浸水想定（A51）',
                source: kkUsuiShinsuiSource,
                layers: [kkUsuiShinsuiFill, kkUsuiShinsuiLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A51-2024.html" target="_blank">国土数値情報（雨水出水浸水想定）</a>',
                credit: '国土数値情報（雨水出水浸水想定）',
              },
              {
                id: 'oh-kk-kozui-shinsui',
                label: '洪水浸水想定区域（A31a）',
                description:
                  '河川氾濫時の浸水位置・深さのシミュレーション。水防法に基づき国交省が作成。想定最大規模・計画規模の2種類。河川別フィルタ・3D立体表示・暖色/寒色モード切り替え可能',
                source: kkKozuiShinsuiSource,
                layers: [kkKozuiShinsuiFill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a-2024.html" target="_blank">国土数値情報（洪水浸水想定区域）</a>',
                credit: '国土数値情報（洪水浸水想定区域）',
              },
              {
                id: 'oh-kk-kozui-mesh-10',
                label: '洪水メッシュ 計画規模（A31b）',
                source: kkKozuiMesh10Source,
                layers: [kkKozuiMesh10Fill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31b-2024.html" target="_blank">国土数値情報（洪水浸水想定区域1次メッシュ）</a>',
                credit: '国土数値情報（洪水浸水想定区域1次メッシュ）',
              },
              {
                id: 'oh-kk-kozui-mesh-20',
                label: '洪水メッシュ 想定最大規模（A31b）',
                source: kkKozuiMesh20Source,
                layers: [kkKozuiMesh20Fill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31b-2024.html" target="_blank">国土数値情報（洪水浸水想定区域1次メッシュ）</a>',
                credit: '国土数値情報（洪水浸水想定区域1次メッシュ）',
              },
              {
                id: 'oh-kk-kozui-mesh-30',
                label: '洪水メッシュ 浸水継続時間（A31b）',
                source: kkKozuiMesh30Source,
                layers: [kkKozuiMesh30Fill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31b-2024.html" target="_blank">国土数値情報（洪水浸水想定区域1次メッシュ）</a>',
                credit: '国土数値情報（洪水浸水想定区域1次メッシュ）',
              },
              {
                id: 'oh-kk-kozui-mesh-41',
                label: '洪水メッシュ 家屋倒壊・氾濫流（A31b）',
                source: kkKozuiMesh41Source,
                layers: [kkKozuiMesh41Fill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31b-2024.html" target="_blank">国土数値情報（洪水浸水想定区域1次メッシュ）</a>',
                credit: '国土数値情報（洪水浸水想定区域1次メッシュ）',
              },
              {
                id: 'oh-kk-kozui-mesh-42',
                label: '洪水メッシュ 家屋倒壊・河岸浸食（A31b）',
                source: kkKozuiMesh42Source,
                layers: [kkKozuiMesh42Fill],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31b-2024.html" target="_blank">国土数値情報（洪水浸水想定区域1次メッシュ）</a>',
                credit: '国土数値情報（洪水浸水想定区域1次メッシュ）',
              },
              {
                id: 'oh-kk-dosha-nadare-mesh',
                label: '土砂災害・雪崩メッシュ5km（A30a5）（調整中）',
                source: kkDoshaNadareMeshSource,
                layers: [kkDoshaNadareMeshFill, kkDoshaNadareMeshLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A30a5.html" target="_blank">国土数値情報（土砂災害・雪崩メッシュ）</a>',
                credit: '国土数値情報（土砂災害・雪崩メッシュ）',
              },
              {
                id: 'oh-tadankai',
                label: '多段階浸水想定（A53）',
                description:
                  '複数の降雨シナリオ（100年/200年頻度等）による段階的な浸水想定。最悪ケースだけでなく現実的なリスク把握が可能。国交省作成。降雨パターンの切り替え表示',
                source: tadankaiSource,
                layers: [tadankaiLayerPolygon, tadankaiLayerLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A53.html" target="_blank">国土数値情報（多段階浸水想定）</a>',
                credit: '国土数値情報（多段階浸水想定）',
                ext: { name: 'ext-tadankai' },
              },
            ],
          },
        ],
      },
      {
        id: 'kokudo-chiiki',
        label: '3. 地域',
        nodes: [
          {
            id: 'kk-shisetsu',
            label: '施設',
            nodes: [
              {
                id: 'oh-kk-kuni-kikan',
                label: '国・都道府県の機関（P28）',
                source: kkKuniKikanSource,
                layers: [kkKuniKikanCircle, kkKuniKikanLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P28-2022.html" target="_blank">国土数値情報（国・都道府県の機関）</a>',
                credit: '国土数値情報（国・都道府県の機関）',
              },
              {
                id: 'oh-kk-yakuba',
                label: '市町村役場等（P05）',
                source: kkYakubaSource,
                layers: [kkYakubaCircle, kkYakubaLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P05-2022.html" target="_blank">国土数値情報（市町村役場等）</a>',
                credit: '国土数値情報（市町村役場等）',
              },
              {
                id: 'oh-kk-shikuchoson-yakuba',
                label: '市区町村役場（P34）',
                source: kkShikuchosonYakubaSource,
                layers: [kkShikuchosonYakubaCircle, kkShikuchosonYakubaLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P34.html" target="_blank">国土数値情報（市区町村役場）</a>',
                credit: '国土数値情報（市区町村役場）',
              },
              {
                id: 'oh-kk-kokyou-shisetsu',
                label: '公共施設（P02）',
                source: kkKokyouShisetsuSource,
                layers: [kkKokyouShisetsuCircle, kkKokyouShisetsuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P02-2006.html" target="_blank">国土数値情報（公共施設）</a>',
                credit: '国土数値情報（公共施設）',
              },
              {
                id: 'oh-kk-gesuidou',
                label: '下水道関連施設（P22）',
                source: kkGesuidouSource,
                layers: [kkGesuidouCircle, kkGesuidouLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P22.html" target="_blank">国土数値情報（下水道関連施設）</a>',
                credit: '国土数値情報（下水道関連施設）',
              },
              {
                id: 'oh-kk-haikibutsu',
                label: '廃棄物処理施設（P15）',
                source: kkHaikibutsuSource,
                layers: [kkHaikibutsuCircle, kkHaikibutsuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P15.html" target="_blank">国土数値情報（廃棄物処理施設）</a>',
                credit: '国土数値情報（廃棄物処理施設）',
              },
              {
                id: 'oh-kk-hatsuden',
                label: '発電施設（P03）',
                source: kkHatsudenSource,
                layers: [kkHatsudenCircle, kkHatsudenLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P03.html" target="_blank">国土数値情報（発電施設）</a>',
                credit: '国土数値情報（発電施設）',
              },
              {
                id: 'oh-kk-newtown',
                label: 'ニュータウン（P26）',
                source: kkNewtownSource,
                layers: [kkNewtownCircle, kkNewtownLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P26.html" target="_blank">国土数値情報（ニュータウン）</a>',
                credit: '国土数値情報（ニュータウン）',
              },
              {
                id: 'oh-kk-kenkyu-kikan',
                label: '研究機関（P16）',
                source: kkKenkyuKikanSource,
                layers: [kkKenkyuKikanCircle, kkKenkyuKikanLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P16.html" target="_blank">国土数値情報（研究機関）</a>',
                credit: '国土数値情報（研究機関）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P16_002',
                  layers: ['oh-kk-kenkyu-kikan', 'oh-kk-kenkyu-kikan-label'],
                  categories: [
                    { code: '1', label: '国立', color: '#1565C0' },
                    { code: '2', label: '公立', color: '#2E7D32' },
                    { code: '3', label: '民営', color: '#E65100' },
                    { code: '4', label: '独法', color: '#7B1FA2' },
                  ],
                },
              },
              {
                id: 'oh-kk-jiba-sangyo',
                label: '地場産業関連施設（P24）',
                source: kkJibaSangyoSource,
                layers: [kkJibaSangyoCircle, kkJibaSangyoLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P24.html" target="_blank">国土数値情報（地場産業関連施設）</a>',
                credit: '国土数値情報（地場産業関連施設）',
              },
              {
                id: 'oh-kk-butsuryu-kyoten',
                label: '物流拠点（P31）',
                source: kkButsuryuKyotenSource,
                layers: [kkButsuryuKyotenCircle, kkButsuryuKyotenLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P31.html" target="_blank">国土数値情報（物流拠点）</a>',
                credit: '国土数値情報（物流拠点）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P31_002',
                  layers: ['oh-kk-butsuryu-kyoten', 'oh-kk-butsuryu-kyoten-label'],
                  categories: [
                    { code: '1', label: 'トラックターミナル', color: '#D32F2F' },
                    { code: '2', label: '鉄道貨物駅', color: '#FF8F00' },
                    { code: '3', label: '港湾', color: '#795548' },
                    { code: '4', label: '空港', color: '#1565C0' },
                    { code: '5', label: '倉庫', color: '#2E7D32' },
                    { code: '6', label: '配送センター', color: '#0097A7' },
                    { code: '7', label: 'その他', color: '#78909C' },
                  ],
                },
              },
              {
                id: 'oh-kk-shukyaku',
                label: '集客施設（P33）',
                source: kkShukyakuSource,
                layers: [kkShukyakuCircle, kkShukyakuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P33.html" target="_blank">国土数値情報（集客施設）</a>',
                credit: '国土数値情報（集客施設）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P33_004',
                  layers: ['oh-kk-shukyaku', 'oh-kk-shukyaku-label'],
                  categories: [
                    { code: '1', label: '劇場・ホール', color: '#D81B60' },
                    { code: '2', label: 'スポーツ施設', color: '#1565C0' },
                    { code: '3', label: '遊興施設', color: '#E65100' },
                    { code: '4', label: '商業施設', color: '#2E7D32' },
                    { code: '5', label: '観光施設', color: '#FF8F00' },
                    { code: '6', label: 'その他', color: '#78909C' },
                  ],
                },
              },
              {
                id: 'oh-kk-fukushi',
                label: '福祉施設（P14）',
                source: kkFukushiSource,
                layers: [kkFukushiCircle, kkFukushiLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P14-2023.html" target="_blank">国土数値情報（福祉施設）</a>',
                credit: '国土数値情報（福祉施設）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P14_005',
                  layers: ['oh-kk-fukushi', 'oh-kk-fukushi-label'],
                  categories: [
                    { code: '01', label: '老人福祉', color: '#E91E63' },
                    { code: '02', label: '障害者支援', color: '#9C27B0' },
                    { code: '03', label: '児童福祉', color: '#3F51B5' },
                    { code: '04', label: '母子福祉', color: '#2196F3' },
                    { code: '05', label: 'その他社会福祉', color: '#FF9800' },
                    { code: '06', label: '保護施設', color: '#F06292' },
                  ],
                },
              },
              {
                id: 'oh-kk-bunka-shisetsu',
                label: '文化施設（P27）',
                source: kkBunkaShisetsuSource,
                layers: [kkBunkaShisetsuCircle, kkBunkaShisetsuLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P27.html" target="_blank">国土数値情報（文化施設）</a>',
                credit: '国土数値情報（文化施設）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P27_002',
                  layers: ['oh-kk-bunka-shisetsu', 'oh-kk-bunka-shisetsu-label'],
                  categories: [
                    { code: '1', label: '図書館', color: '#7B1FA2' },
                    { code: '2', label: '博物館', color: '#4A148C' },
                    { code: '3', label: '劇場・音楽堂', color: '#E91E63' },
                    { code: '4', label: '文化会館', color: '#2E7D32' },
                    { code: '5', label: '生涯学習', color: '#0277BD' },
                    { code: '6', label: 'その他', color: '#FF6F00' },
                  ],
                },
              },
              {
                id: 'oh-keisatsu',
                label: '警察署（P18）',
                source: keisatsuSource,
                layers: [keisatsuPolygonLayer, keisatsuPolygonLineLayer, keisatsuPointLayer, keisatsuPointLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P18.html" target="_blank">国土数値情報（警察署）</a>',
                credit: '国土数値情報（警察署）',
              },
              {
                id: 'oh-shobosho',
                label: '消防署（P17）',
                source: shoboshoSource,
                layers: [shoboshoPolygonLayer, shoboshoPolygonLineLayer, shoboshoPointLayer, shoboshoPointLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P17.html" target="_blank">国土数値情報（消防署）</a>',
                credit: '国土数値情報（消防署）',
              },
              {
                id: 'oh-yubinkyoku',
                label: '郵便局（P30）',
                source: yubinkyokuSource,
                layers: [yubinkyokuPointLayer, yubinkyokuPointLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P30.html" target="_blank">国土数値情報（郵便局）</a>',
                credit: '国土数値情報（郵便局）',
              },
              {
                id: 'oh-gas-station',
                label: '燃料給油所（P07）',
                source: gasStationSource,
                layers: [gasStationPointLayer, gasStationPointLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P07-2016.html" target="_blank">国土数値情報（燃料給油所）</a>',
                credit: '国土数値情報（燃料給油所）',
              },
              {
                id: 'oh-iryokikan',
                label: '医療機関（P04）',
                source: iryokikanSource,
                layers: [iryokikanLayer, iryokikanLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P04-2020.html" target="_blank">国土数値情報（医療機関）</a>',
                credit: '国土数値情報（医療機関）',
              },
              {
                id: 'oh-school',
                label: '学校データ（P29）',
                source: schoolSource,
                layers: [schoolLayer, schoolLayerLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P29-2023.html" target="_blank">国土数値情報（学校）</a>',
                credit: '国土数値情報（学校）',
              },
              {
                id: 'kk-josuidou',
                label: '上水道（P21）',
                nodes: [
                  {
                    id: 'oh-josuidou-area',
                    label: '給水区域',
                    source: josuidouAreaSource,
                    layers: [josuidouAreaLayer, josuidouAreaLineLayer],
                    attribution:
                      '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P21.html" target="_blank">国土数値情報（上水道）</a>',
                    credit: '国土数値情報（上水道）',
                  },
                  {
                    id: 'oh-josuidou-point',
                    label: '浄水場',
                    source: josuidouPointSource,
                    layers: [josuidouPointLayer, josuidouPointLabelLayer],
                    attribution:
                      '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P21.html" target="_blank">国土数値情報（上水道）</a>',
                    credit: '国土数値情報（上水道）',
                  },
                ],
              },
              {
                id: 'oh-syukuhaku',
                label: '宿泊容量メッシュ1km（P09）',
                sources: [syukuhakuSource],
                layers: [syukuhakuLayer, syukuhakuLayerLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P09.html" target="_blank">国土数値情報（宿泊容量メッシュ）</a>',
                credit: '国土数値情報（宿泊容量メッシュ）',
              },
            ],
          },
          {
            id: 'kk-chiiki-shigen-kanko',
            label: '地域資源・観光',
            nodes: [
              {
                id: 'oh-kk-bunkazai',
                label: '都道府県指定文化財（P32）',
                source: kkBunkazaiSource,
                layers: [kkBunkazaiCircle, kkBunkazaiLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報（都道府県指定文化財）</a>',
                credit: '国土数値情報（都道府県指定文化財）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P32_004',
                  layers: ['oh-kk-bunkazai', 'oh-kk-bunkazai-label'],
                  categories: [
                    { code: '1', label: '有形文化財', color: '#FF6F00' },
                    { code: '2', label: '無形文化財', color: '#AB47BC' },
                    { code: '3', label: '民俗文化財', color: '#8D6E63' },
                    { code: '4', label: '記念物', color: '#2E7D32' },
                    { code: '5', label: '文化的景観', color: '#0097A7' },
                    { code: '6', label: '伝統的建造物群', color: '#D81B60' },
                    { code: '7', label: 'その他', color: '#546E7A' },
                  ],
                },
              },
              {
                id: 'oh-kk-sekai-bunka-isan',
                label: '世界文化遺産（A34）',
                source: kkSekaiBunkaIsanSource,
                layers: [kkSekaiBunkaIsanFill, kkSekaiBunkaIsanLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A34-2022.html" target="_blank">国土数値情報（世界文化遺産）</a>',
                credit: '国土数値情報（世界文化遺産）',
              },
              {
                id: 'oh-kk-sekai-shizen-isan',
                label: '世界自然遺産（A28）',
                source: kkSekaiShizenIsanSource,
                layers: [kkSekaiShizenIsanFill, kkSekaiShizenIsanLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A28.html" target="_blank">国土数値情報（世界自然遺産）</a>',
                credit: '国土数値情報（世界自然遺産）',
              },
              {
                id: 'oh-kk-chiiki-shigen',
                label: '地域資源（P19）',
                source: kkChiikiShigenSource,
                layers: [kkChiikiShigenCircle, kkChiikiShigenLabel],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P19.html" target="_blank">国土数値情報（地域資源）</a>',
                credit: '国土数値情報（地域資源）',
                ext: {
                  name: 'extKkChipFilter',
                  prop: 'P19_004',
                  layers: ['oh-kk-chiiki-shigen', 'oh-kk-chiiki-shigen-label'],
                  categories: [
                    { code: '1', label: '自然', color: '#D32F2F' },
                    { code: '2', label: '歴史・文化', color: '#795548' },
                    { code: '3', label: '芸能', color: '#9E9E9E' },
                    { code: '4', label: '祭り', color: '#0277BD' },
                    { code: '5', label: '工芸品', color: '#2E7D32' },
                    { code: '6', label: '食品', color: '#00695C' },
                    { code: '7', label: '技術', color: '#1565C0' },
                    { code: '8', label: '観光施設', color: '#FF6F00' },
                    { code: '9', label: '産業', color: '#7B1FA2' },
                    { code: '10', label: '人材', color: '#F06292' },
                  ],
                },
              },
              {
                id: 'oh-kanko',
                label: '観光資源（P12）',
                sources: [kankoSource],
                layers: [kankoLayer, kankoLayerLine, kankoLine, kankoPoint],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P12-2014.html" target="_blank">国土数値情報（観光資源）</a>',
                credit: '国土数値情報（観光資源）',
              },
            ],
          },
          {
            id: 'kk-hogo-hozen',
            label: '保護保全',
            nodes: [
              {
                id: 'oh-kk-shizen-koen',
                label: '自然公園地域（A10）',
                source: kkShizenKoenSource,
                layers: [kkShizenKoenFill, kkShizenKoenLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A10-2015.html" target="_blank">国土数値情報（自然公園地域）</a>',
                credit: '国土数値情報（自然公園地域）',
              },
              {
                id: 'oh-kk-shizen-hozen',
                label: '自然保全地域（A11）',
                source: kkShizenHozenSource,
                layers: [kkShizenHozenFill, kkShizenHozenLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A11.html" target="_blank">国土数値情報（自然保全地域）</a>',
                credit: '国土数値情報（自然保全地域）',
              },
              {
                id: 'oh-kk-chouju-hogo',
                label: '鳥獣保護区（A15）',
                source: kkChoujuHogoSource,
                layers: [kkChoujuHogoFill, kkChoujuHogoLine],
                attribution:
                  '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A15.html" target="_blank">国土数値情報（鳥獣保護区）</a>',
                credit: '国土数値情報（鳥獣保護区）',
              },
            ],
          },
        ],
      },
      {
        id: 'kokudo-kotsu',
        label: '4. 交通',
        nodes: [
          {
            id: 'oh-n13-road',
            label: '道路（N13）',
            sources: [
              {
                id: 'oh-n13-road-major-source',
                obj: {
                  type: 'vector',
                  url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/n13/n13_road_major.pmtiles',
                  minzoom: 4,
                  maxzoom: 14,
                },
              },
              {
                id: 'oh-n13-road-minor-source',
                obj: {
                  type: 'vector',
                  url: 'pmtiles://https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/n13/n13_road_minor.pmtiles',
                  minzoom: 10,
                  maxzoom: 14,
                },
              },
            ],
            layers: [
              // --- Major（高速+国道）: z4〜 ---
              {
                id: 'oh-n13-road-major-layer',
                source: 'oh-n13-road-major-source',
                'source-layer': 'road',
                type: 'line',
                minzoom: 4,
                maxzoom: 24,
                filter: ['!=', ['get', 'N13_006'], '1'],
                paint: {
                  'line-color': ['match', ['get', 'N13_003'], '4', '#e53935', '1', '#1565c0', '#9e9e9e'],
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    4,
                    ['match', ['get', 'N13_006'], '5', 1.2, '4', 1, '3', 0.8, '2', 0.5, 0.3],
                    8,
                    ['match', ['get', 'N13_006'], '5', 3, '4', 2.5, '3', 2, '2', 1.5, 1],
                    14,
                    ['match', ['get', 'N13_006'], '5', 14, '4', 10, '3', 6, '2', 3.5, 2],
                    18,
                    ['match', ['get', 'N13_006'], '5', 30, '4', 22, '3', 14, '2', 8, 4],
                  ],
                  'line-opacity': 0.8,
                },
              },
              {
                id: 'oh-n13-road-major-narrow-layer',
                source: 'oh-n13-road-major-source',
                'source-layer': 'road',
                type: 'line',
                minzoom: 4,
                maxzoom: 24,
                filter: ['==', ['get', 'N13_006'], '1'],
                paint: {
                  'line-color': ['match', ['get', 'N13_003'], '4', '#e53935', '1', '#1565c0', '#9e9e9e'],
                  'line-width': ['interpolate', ['linear'], ['zoom'], 4, 0.3, 8, 0.7, 14, 1.5],
                  'line-opacity': 0.7,
                  'line-dasharray': [4, 3],
                },
              },
              {
                id: 'oh-n13-road-major-label',
                source: 'oh-n13-road-major-source',
                'source-layer': 'road',
                type: 'symbol',
                minzoom: 14,
                maxzoom: 24,
                layout: {
                  'symbol-placement': 'line',
                  'text-field': [
                    'match',
                    ['get', 'N13_006'],
                    '1',
                    '3m未満',
                    '2',
                    '3-5.5m',
                    '3',
                    '5.5-13m',
                    '4',
                    '13-19.5m',
                    '5',
                    '19.5m以上',
                    '',
                  ],
                  'text-size': ['interpolate', ['linear'], ['zoom'], 14, 13, 18, 18],
                  'text-font': ['Noto Sans CJK JP Regular'],
                  'text-anchor': 'center',
                  'symbol-spacing': 250,
                  'text-max-angle': 30,
                },
                paint: { 'text-color': '#333', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.5 },
              },
              // --- Minor（都道府県道+市区町村道+その他）: z10〜 ---
              {
                id: 'oh-n13-road-minor-layer',
                source: 'oh-n13-road-minor-source',
                'source-layer': 'road',
                type: 'line',
                minzoom: 10,
                maxzoom: 24,
                filter: ['!=', ['get', 'N13_006'], '1'],
                paint: {
                  'line-color': [
                    'match',
                    ['get', 'N13_003'],
                    '2',
                    '#2e7d32',
                    '3',
                    '#757575',
                    '5',
                    '#8d6e63',
                    '#9e9e9e',
                  ],
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    10,
                    ['match', ['get', 'N13_006'], '5', 1.5, '4', 1.2, '3', 0.8, '2', 0.5, 0.3],
                    14,
                    ['match', ['get', 'N13_006'], '5', 14, '4', 10, '3', 6, '2', 3.5, 2],
                    18,
                    ['match', ['get', 'N13_006'], '5', 30, '4', 22, '3', 14, '2', 8, 4],
                  ],
                  'line-opacity': 0.8,
                },
              },
              {
                id: 'oh-n13-road-minor-narrow-layer',
                source: 'oh-n13-road-minor-source',
                'source-layer': 'road',
                type: 'line',
                minzoom: 10,
                maxzoom: 24,
                filter: ['==', ['get', 'N13_006'], '1'],
                paint: {
                  'line-color': [
                    'match',
                    ['get', 'N13_003'],
                    '2',
                    '#2e7d32',
                    '3',
                    '#757575',
                    '5',
                    '#8d6e63',
                    '#9e9e9e',
                  ],
                  'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.3, 14, 1.5],
                  'line-opacity': 0.7,
                  'line-dasharray': [4, 3],
                },
              },
              {
                id: 'oh-n13-road-minor-label',
                source: 'oh-n13-road-minor-source',
                'source-layer': 'road',
                type: 'symbol',
                minzoom: 14,
                maxzoom: 24,
                layout: {
                  'symbol-placement': 'line',
                  'text-field': [
                    'match',
                    ['get', 'N13_006'],
                    '1',
                    '3m未満',
                    '2',
                    '3-5.5m',
                    '3',
                    '5.5-13m',
                    '4',
                    '13-19.5m',
                    '5',
                    '19.5m以上',
                    '',
                  ],
                  'text-size': ['interpolate', ['linear'], ['zoom'], 14, 13, 18, 18],
                  'text-font': ['Noto Sans CJK JP Regular'],
                  'text-anchor': 'center',
                  'symbol-spacing': 250,
                  'text-max-angle': 30,
                },
                paint: { 'text-color': '#333', 'text-halo-color': 'rgba(255,255,255,0.9)', 'text-halo-width': 1.5 },
              },
            ],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N13-2024.html" target="_blank">国土数値情報（道路）</a>',
            credit: '国土数値情報（道路）',
            ext: { name: 'extN13Road' },
          },
          {
            id: 'oh-kk-kinkyu-yuso',
            label: '緊急輸送道路（N10）',
            source: kkKinkyuYusoSource,
            layers: [kkKinkyuYusoLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N10-2024.html" target="_blank">国土数値情報（緊急輸送道路）</a>',
            credit: '国土数値情報（緊急輸送道路）',
          },
          {
            id: 'oh-kk-butsuryu-douro',
            label: '重要物流道路（N12）',
            source: kkButsuryuDouroSource,
            layers: [kkButsuryuDouroLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N12-2021.html" target="_blank">国土数値情報（重要物流道路）</a>',
            credit: '国土数値情報（重要物流道路）',
          },
          {
            id: 'oh-kk-kosoku-bus-tei',
            label: '高速バス停留所（P36）',
            source: kkKosokuBusTeiSource,
            layers: [kkKosokuBusTeiCircle, kkKosokuBusTeiLabel],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P36-2023.html" target="_blank">国土数値情報（高速バス停留所）</a>',
            credit: '国土数値情報（高速バス停留所）',
          },
          {
            id: 'oh-kk-kuukou',
            label: '空港（C28）',
            source: kkKuukouSource,
            layers: [kkKuukouFill, kkKuukouLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-C28-2021.html" target="_blank">国土数値情報（空港）</a>',
            credit: '国土数値情報（空港）',
          },
          {
            id: 'oh-kk-kowan',
            label: '港湾（C02）',
            source: kkKowanSource,
            layers: [kkKowanCircle, kkKowanLabel],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-C02-2014.html" target="_blank">国土数値情報（港湾）</a>',
            credit: '国土数値情報（港湾）',
            ext: {
              name: 'extKkChipFilter',
              prop: 'C02_002',
              layers: ['oh-kk-kowan', 'oh-kk-kowan-label'],
              categories: [
                { code: '1', label: '国際戦略港湾', color: '#D32F2F' },
                { code: '2', label: '国際拠点港湾', color: '#E65100' },
                { code: '3', label: '重要港湾', color: '#1565C0' },
                { code: '4', label: '地方港湾', color: '#42A5F5' },
                { code: '5', label: '56条港湾', color: '#78909C' },
              ],
            },
          },
          {
            id: 'oh-kk-teiki-kouro',
            label: '定期旅客航路（N09）',
            source: kkTeikiKouroSource,
            layers: [kkTeikiKouroLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N09.html" target="_blank">国土数値情報（定期旅客航路）</a>',
            credit: '国土数値情報（定期旅客航路）',
          },
          {
            id: 'oh-kk-kowan-ryutsu',
            label: '港湾間流通量（S10a）',
            source: kkKowanRyutsuSource,
            layers: [kkKowanRyutsuLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S10a-2016.html" target="_blank">国土数値情報（港湾間流通量）</a>',
            credit: '国土数値情報（港湾間流通量）',
          },
          {
            id: 'oh-kk-kuukou-ryutsu',
            label: '空港間流通量（S10b）',
            source: kkKuukouRyutsuSource,
            layers: [kkKuukouRyutsuLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S10b-2014.html" target="_blank">国土数値情報（空港間流通量）</a>',
            credit: '国土数値情報（空港間流通量）',
          },
          {
            id: 'oh-kk-kamotsu-ryokaku',
            label: '貨物旅客地域流動量（S05-d）',
            source: kkKamotsuRyokakuSource,
            layers: [kkKamotsuRyokakuLine2],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S05-d-2018.html" target="_blank">国土数値情報（貨物旅客地域流動量）</a>',
            credit: '国土数値情報（貨物旅客地域流動量）',
          },
          {
            id: 'oh-kk-eki-joko-pt',
            label: '駅別乗降客数PT（S05-c）',
            source: kkEkiJokoPtSource,
            layers: [kkEkiJokoPtCircle, kkEkiJokoPtLabel],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S05-c.html" target="_blank">国土数値情報（駅別乗降客数・パーソントリップ）</a>',
            credit: '国土数値情報（駅別乗降客数・パーソントリップ）',
          },
          {
            id: 'oh-kk-douro-mitsudo-mesh',
            label: '道路密度・道路延長メッシュ1km（N04）',
            source: kkDouroMitsudoMeshSource,
            layers: [kkDouroMitsudoMeshFill, kkDouroMitsudoMeshLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N04.html" target="_blank">国土数値情報（道路密度・道路延長メッシュ）</a>',
            credit: '国土数値情報（道路密度・道路延長メッシュ）',
          },
          {
            id: 'oh-tetsudo',
            label: '鉄道（N05）',
            source: tetsudoSource,
            layers: [tetsudoLayerRed, tetsudoLayerBlue, tetsudoLayerPointRed, tetsudoLayerPointBlue, tetsudoLayerLabel],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N05.html" target="_blank">国土数値情報（鉄道）</a>',
            credit: '国土数値情報（鉄道）',
            ext: { name: 'extTetsudo', parameters: [] },
          },
          {
            id: 'oh-bus',
            label: 'バスルートと停留所（N07）',
            sources: [busSource, busteiSource],
            layers: [busLayer, busteiLayer, busteiLayerLabel],
            attribution:
              "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N07-v2_0.html' target='_blank'>国土数値情報（バスルート）</a>",
            credit: '国土数値情報（バスルート）',
            ext: { name: 'extBus', parameters: [] },
          },
          {
            id: 'oh-heliport',
            label: 'ヘリポート（N11）',
            source: heliportSource,
            layers: [heliportLayer, heliportLabelLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N11.html" target="_blank">国土数値情報（ヘリポート）</a>',
            credit: '国土数値情報（ヘリポート）',
          },
          {
            id: 'oh-gyoko',
            label: '漁港（C09）',
            source: gyokoSource,
            layers: [gyokoLineLayer, gyokoPointLayer, gyokoPointLayerLabel],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-C09.html" target="_blank">国土数値情報（漁港）</a>',
            credit: '国土数値情報（漁港）',
          },
          {
            id: 'oh-ekibetsukyaku',
            label: '駅別乗降客数（S12）',
            sources: [ekibetsukyakuSource],
            layers: [ekibetsukyakuLine, ekibetsukyakuLabel],
            attribution:
              "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報（駅別乗降客数）</a>",
            credit: '国土数値情報（駅別乗降客数）',
          },
          {
            id: 'oh-ekibetsukyaku-3d',
            label: '駅別乗降客数３D（S12）',
            sources: [ekibetsukyakuSource3d, tetsudo2Source],
            layers: [tetsudoLine, ekibetsukyakuHeight],
            ext: { name: 'extKyakusu' },
            attribution:
              "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報（駅別乗降客数）</a>" +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:hsl(240, 100%, 50%);"></span>0 - 99人</li>' +
              '<li><span style="background:hsl(230, 100%, 55%);"></span>100 - 299人</li>' +
              '<li><span style="background:hsl(220, 100%, 60%);"></span>300 - 499人</li>' +
              '<li><span style="background:hsl(210, 100%, 65%);"></span>500 - 999人</li>' +
              '<li><span style="background:hsl(200, 100%, 70%);"></span>1000 - 4999人</li>' +
              '<li><span style="background:hsl(180, 100%, 50%);"></span>5000 - 19999人</li>' +
              '<li><span style="background:hsl(150, 100%, 50%);"></span>20000 - 99999人</li>' +
              '<li><span style="background:hsl(120, 100%, 50%);"></span>100000 - 499999人</li>' +
              '<li><span style="background:hsl(60, 100%, 50%);"></span>500000 - 1199999人</li>' +
              '<li><span style="background:hsl(0, 100%, 50%);"></span>1200000人+</li>' +
              '</ul>' +
              '</div>',
            credit:
              '国土数値情報（駅別乗降客数） " +   +   +  0 - 99人  +  100 - 299人  +  300 - 499人  +  500 - 999人  +  1000 - 4999人  +  5000 - 19999人  +  20000 - 99999人  +  100000 - 499999人  +  500000 - 1199999人  +  1200000人+  +   +',
          },
        ],
      },
      {
        id: 'kokudo-tokei',
        label: '5. 各種統計',
        nodes: [
          {
            id: 'oh-r02suikei250m',
            label: 'R02-250mメッシュ別将来推計人口',
            source: r02suikei250mSource,
            layers: [r02suikei250mLayer, r02suikei250mLayerLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh250r6.html" target="_blank">国土数値情報（将来推計人口250mメッシュ）</a>' +
              '<div class="legend-scale"><ul class="legend-labels">' +
              '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
              '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
              '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
              '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
              '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
              '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li></ul></div>',
            credit:
              '国土数値情報（将来推計人口250mメッシュ）  +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加',
            info: true,
          },
          {
            id: 'oh-r02suikei500m',
            label: 'R02-500mメッシュ別将来推計人口',
            source: r02suikei500mSource,
            layers: [r02suikei500mLayer, r02suikei500mLayerLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh500r6.html" target="_blank">国土数値情報（将来推計人口500mメッシュ）</a>' +
              '<div class="legend-scale"><ul class="legend-labels">' +
              '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
              '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
              '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
              '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
              '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
              '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li></ul></div>',
            credit:
              '国土数値情報（将来推計人口500mメッシュ）  +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加',
            info: true,
          },
          {
            id: 'oh-r02suikei1km',
            label: 'R02-1kmメッシュ別将来推計人口',
            source: r02suikei1kmSource,
            layers: [r02suikei1kmLayer, r02suikei1kmLayerLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000r6.html" target="_blank">国土数値情報（将来推計人口1kmメッシュ）</a>' +
              '<div class="legend-scale"><ul class="legend-labels">' +
              '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
              '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
              '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
              '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
              '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
              '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li></ul></div>',
            credit:
              '国土数値情報（将来推計人口1kmメッシュ）  +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加',
            info: true,
          },
          // {
          //   id: 'oh-suikei500m',
          //   label: '500mメッシュ別将来推計人口（H30）',
          //   source: suikei500mSource,
          //   layers: [suikei500mLayer, suikei500mLayerLine],
          //   attribution:
          //     '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh500h30.html" target="_blank">国土数値情報（将来推計人口500mメッシュH30）</a>' +
          //     '<div class="legend-scale"><ul class="legend-labels">' +
          //     '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          //     '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          //     '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          //     '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          //     '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          //     '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li></ul></div>',
          //   info: true,
          // },
          // {
          //   id: 'oh-suikei1km',
          //   label: '1kmメッシュ別将来推計人口（H30）',
          //   source: suikei1kmSource,
          //   layers: [suikei1kmLayer, suikei1kmLayerLine],
          //   attribution:
          //     '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000h30.html" target="_blank">国土数値情報（将来推計人口1kmメッシュH30）</a>' +
          //     '<div class="legend-scale"><ul class="legend-labels">' +
          //     '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          //     '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          //     '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          //     '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          //     '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          //     '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li></ul></div>',
          //   info: true,
          // },
        ],
      },
    ],
  },

  {
    id: 'reinfolib',
    label: '⭐️不動産情報ライブラリ',
    nodes: [
      {
        id: 'oh-reinfolib-toshikeikaku',
        label: '都市計画区域/区域区分',
        source: reinfolibToshikeikakuSource,
        layers: [reinfolibToshikeikakuLayer, reinfolibToshikeikakuLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi2" target="_blank">不動産情報ライブラリ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(173, 216, 230, 0.5);"></span>都市計画区域</li>' +
          '<li><span style="background:rgba(255, 200, 150, 0.5);"></span>市街化区域</li>' +
          '<li><span style="background:rgba(150, 220, 150, 0.5);"></span>市街化調整区域</li>' +
          '</ul>' +
          '</div>',
        credit: '不動産情報ライブラリ  +   +   +  都市計画区域  +  市街化区域  +  市街化調整区域  +   +',
      },
      {
        id: 'oh-reinfolib-yotochiiki',
        label: '用途地域',
        source: reinfolibYotochiikiSource,
        layers: [reinfolibYotochiikiLayer, reinfolibYotochiikiLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi3" target="_blank">不動産情報ライブラリ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(0, 180, 0, 0.6);"></span>第1種低層住居専用地域</li>' +
          '<li><span style="background:rgba(100, 200, 100, 0.6);"></span>第1種中高層住居専用地域</li>' +
          '<li><span style="background:rgba(200, 240, 200, 0.6);"></span>第1種住居地域</li>' +
          '<li><span style="background:rgba(255, 230, 180, 0.6);"></span>準住居地域</li>' +
          '<li><span style="background:rgba(255, 180, 180, 0.6);"></span>近隣商業地域</li>' +
          '<li><span style="background:rgba(255, 100, 100, 0.6);"></span>商業地域</li>' +
          '<li><span style="background:rgba(200, 150, 255, 0.6);"></span>準工業地域</li>' +
          '<li><span style="background:rgba(150, 150, 200, 0.6);"></span>工業地域</li>' +
          '<li><span style="background:rgba(100, 100, 180, 0.6);"></span>工業専用地域</li>' +
          '</ul>' +
          '</div>',
        credit:
          '不動産情報ライブラリ  +   +   +  第1種低層住居専用地域  +  第1種中高層住居専用地域  +  第1種住居地域  +  準住居地域  +  近隣商業地域  +  商業地域  +  準工業地域  +  工業地域  +  工業専用地域  +   +',
      },
      {
        id: 'oh-fj-ricchitekiseika',
        label: '立地適正化計画',
        source: fjRicchitekiseiksSourse,
        layers: [fjRicchitekiseikaLayer, fjRicchitekiseikaLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi4" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-shogakkoku',
        label: '小学校区',
        source: reinfolibShogakkokuSource,
        layers: [reinfolibShogakkokuLayer, reinfolibShogakkokuLayerLine, reinfolibShogakkokuLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi5" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-chugakkoku',
        label: '中学校区',
        source: reinfolibChugakkokuSource,
        layers: [reinfolibChugakkokuLayer, reinfolibChugakkokuLayerLine, reinfolibChugakkokuLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi6" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-gakko',
        label: '学校（施設）',
        source: reinfolibShogakkoSource,
        layers: [reinfolibShogakkoLayer, reinfolibShogakkoLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi7" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-hoikuen',
        label: '保育園・幼稚園等',
        source: reinfolibHoikuenSource,
        layers: [reinfolibHoikuenLayer, reinfolibHoikuenLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi8" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-iryo',
        label: '医療施設',
        source: reinfolibIryoSource,
        layers: [reinfolibIryoLayer, reinfolibIryoLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi11" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-fukushi',
        label: '福祉施設',
        source: reinfolibFukushiJidoSource,
        layers: [reinfolibFukushiJidoLayer, reinfolibFukushiJidoLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi12" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-jinkou-suikei',
        label: '将来推計人口250mメッシュ',
        source: reinfolibJinkouSuikeiSource,
        layers: [reinfolibJinkouSuikeiLayer, reinfolibJinkouSuikeiLayerLine],
        ext: { name: 'extSuikeiSlider' },
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>' +
          ' 国土数値情報 将来推計人口（250mメッシュ）',
        credit: '不動産情報ライブラリ  +  国土数値情報 将来推計人口（250mメッシュ）',
      },
      {
        id: 'oh-reinfolib-bokachiiki',
        label: '防火地域/準防火地域',
        source: reinfolibBokachiikiSource,
        layers: [reinfolibBokachiikiLayer, reinfolibBokachiikiLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi17" target="_blank">不動産情報ライブラリ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(255, 0, 0, 0.5);"></span>防火地域</li>' +
          '<li><span style="background:rgba(255, 165, 0, 0.5);"></span>準防火地域</li>' +
          '</ul>' +
          '</div>',
        credit: '不動産情報ライブラリ  +   +   +  防火地域  +  準防火地域  +   +',
      },
      {
        id: 'oh-reinfolib-eki-jokokyaku',
        label: '駅別乗降客数',
        source: reinfolibEkiJokokyakuSource,
        layers: [reinfolibEkiJokokyakuLayer, reinfolibEkiJokokyakuLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-saigai-kiken',
        label: '災害危険区域',
        source: reinfolibSaigaiKikenSource,
        layers: [reinfolibSaigaiKikenLayer, reinfolibSaigaiKikenLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-toshokan',
        label: '図書館',
        source: reinfolibToshokanSource,
        layers: [reinfolibToshokanLayer, reinfolibToshokanLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-yakuba',
        label: '市区町村役場及び集会施設等',
        source: reinfolibYakubaSource,
        layers: [reinfolibYakubaLayer, reinfolibYakubaLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-shizen-koen',
        label: '自然公園地域',
        source: reinfolibShizenKoenSource,
        layers: [reinfolibShizenKoenLayer, reinfolibShizenKoenLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-morido',
        label: '大規模盛土造成地マップ',
        source: reinfolibMoridoSource,
        layers: [reinfolibMoridoLayer, reinfolibMoridoLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-chiku-keikaku',
        label: '地区計画',
        source: reinfolibChikuKeikakuSource,
        layers: [reinfolibChikuKeikakuLayer, reinfolibChikuKeikakuLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-kodo-riyo',
        label: '高度利用地区',
        source: reinfolibKodoRiyoSource,
        layers: [reinfolibKodoRiyoLayer, reinfolibKodoRiyoLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi19" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-dosya',
        label: '土砂災害警戒区域',
        source: reinfolibDosyaSource,
        layers: [reinfolibDosyaLayer, reinfolibDosyaLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi20" target="_blank">不動産情報ライブラリ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(255, 255, 0, 0.6);"></span>土砂災害警戒区域</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.6);"></span>土砂災害特別警戒区域</li>' +
          '</ul>' +
          '</div>',
        credit: '不動産情報ライブラリ  +   +   +  土砂災害警戒区域  +  土砂災害特別警戒区域  +   +',
      },
      {
        id: 'oh-reinfolib-tsunami',
        label: '津波浸水想定',
        source: reinfolibTsunamiSource,
        layers: [reinfolibTsunamiLayer, reinfolibTsunamiLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi21" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-kozui',
        label: '洪水浸水想定区域',
        source: reinfolibKozuiSource,
        layers: [reinfolibKozuiLayer],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi22" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-takashio',
        label: '高潮浸水想定区域',
        source: reinfolibTakashioSource,
        layers: [reinfolibTakashioLayer, reinfolibTakashioLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi24" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-fj-ekijyoka',
        label: '液状化発生傾向',
        source: fjEkijyokaSourse,
        layers: [fjEkijyokatLayer, fjEkijyokatLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi26" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-jisuberi',
        label: '地すべり防止区域',
        source: reinfolibJisuberiSource,
        layers: [reinfolibJisuberiLayer, reinfolibJisuberiLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi28" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-kyukeisha',
        label: '急傾斜地崩壊危険区域',
        source: reinfolibKyukeishaSource,
        layers: [reinfolibKyukeishaLayer, reinfolibKyukeishaLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi29" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-toshikeikaku-doro',
        label: '都市計画道路',
        source: reinfolibToshikeikakuDoroSource,
        layers: [reinfolibToshikeikakuDoroLayer],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-hinanbasho',
        label: '指定緊急避難場所',
        source: reinfolibHinanbashoSource,
        layers: [reinfolibHinanbashoLayer, reinfolibHinanbashoLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-did',
        label: '人口集中地区',
        source: reinfolibDidSource,
        layers: [reinfolibDidLayer, reinfolibDidLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-torihiki',
        label: '不動産取引価格情報',
        source: reinfolibTorihikiSource,
        layers: [reinfolibTorihikiLayer, reinfolibTorihikiLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi34" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-chikakoji',
        label: '地価公示',
        source: reinfolibChikaKojiSource,
        layers: [reinfolibChikaKojiLayer, reinfolibChikaKojiLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi35" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-reinfolib-chikachosa',
        label: '都道府県地価調査',
        source: reinfolibChikaChosaSource,
        layers: [reinfolibChikaChosaLayer, reinfolibChikaChosaLayerLabel],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/#titleApi36" target="_blank">不動産情報ライブラリ</a>',
        credit: '不動産情報ライブラリ',
      },
      {
        id: 'oh-saigai-rireki',
        label: '災害履歴',
        source: saigaiRirekiSource,
        layers: [saigaiRirekiLayer, saigaiRirekiLayerLine],
        attribution:
          '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/xst001/" target="_blank">不動産情報ライブラリ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(30, 144, 255);"></span>浸水域等</li>' +
          '<li><span style="background:rgb(0, 0, 139);"></span>堤防決壊箇所等</li>' +
          '<li><span style="background:rgb(135, 206, 250);"></span>高潮浸水域等</li>' +
          '<li><span style="background:rgb(75, 0, 130);"></span>高潮破堤箇所等</li>' +
          '<li><span style="background:rgb(139, 69, 19);"></span>がけ崩れ等</li>' +
          '<li><span style="background:rgb(210, 105, 30);"></span>地すべり等</li>' +
          '<li><span style="background:rgb(184, 134, 11);"></span>河道閉塞箇所等</li>' +
          '<li><span style="background:rgb(178, 34, 34);"></span>土石流等</li>' +
          '<li><span style="background:rgb(255, 215, 0);"></span>液状化</li>' +
          '<li><span style="background:rgb(255, 140, 0);"></span>地震土砂災害</li>' +
          '<li><span style="background:rgb(0, 206, 209);"></span>津波高</li>' +
          '<li><span style="background:rgb(32, 178, 170);"></span>津波浸水域</li>' +
          '</ul>' +
          '</div>',
        credit:
          '不動産情報ライブラリ  +   +   +  浸水域等  +  堤防決壊箇所等  +  高潮浸水域等  +  高潮破堤箇所等  +  がけ崩れ等  +  地すべり等  +  河道閉塞箇所等  +  土石流等  +  液状化  +  地震土砂災害  +  津波高  +  津波浸水域  +   +',
      },
    ],
  },
  {
    id: 'fudosan',
    label: '不動産情報',
    nodes: [
      {
        id: 'oh-koji',
        label: '公示価格（R06）',
        source: kojiSource,
        layers: [kojiLayerPoint, kojiLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-2024.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-koji-3d',
        label: '公示価格（R06）3D',
        source: kojiSource,
        layers: [kojilayerheight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-2024.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(225, 49, 33);"></span>1000万円以上</li>' +
          '<li><span style="background:rgb(225, 61, 35);"></span>100万円ー1000万円</li>' +
          '<li><span style="background:rgb(226, 84, 39);"></span>75万円ー100万円</li>' +
          '<li><span style="background:rgb(231, 145, 52);"></span>50万円ー75万円</li>' +
          '<li><span style="background:rgb(235, 178, 61);"></span>25万円ー50万円</li>' +
          '<li><span style="background:rgb(239, 211, 71);"></span>10万円ー25万円</li>' +
          '<li><span style="background:rgb(245, 245, 81);"></span>10万円未満</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  1000万円以上  +  100万円ー1000万円  +  75万円ー100万円  +  50万円ー75万円  +  25万円ー50万円  +  10万円ー25万円  +  10万円未満  +   +',
        info: true,
      },
      {
        id: 'oh-chika',
        label: '都道府県地価調査（R06）3D',
        source: chikaSource3d,
        layers: [chikalayerheight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L02-2024.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(225, 49, 33);"></span>1000万円以上</li>' +
          '<li><span style="background:rgb(225, 61, 35);"></span>100万円ー1000万円</li>' +
          '<li><span style="background:rgb(226, 84, 39);"></span>75万円ー100万円</li>' +
          '<li><span style="background:rgb(231, 145, 52);"></span>50万円ー75万円</li>' +
          '<li><span style="background:rgb(235, 178, 61);"></span>25万円ー50万円</li>' +
          '<li><span style="background:rgb(239, 211, 71);"></span>10万円ー25万円</li>' +
          '<li><span style="background:rgb(245, 245, 81);"></span>10万円未満</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  1000万円以上  +  100万円ー1000万円  +  75万円ー100万円  +  50万円ー75万円  +  25万円ー50万円  +  10万円ー25万円  +  10万円未満  +   +',
        info: true,
      },
      {
        id: 'citychibanzu',
        label: '市町村地番図',
        nodes: [...chibanzuLayers2],
      },
      {
        id: 'hazard-population',
        label: 'ハザードと人口',
        nodes: [
          {
            id: 'oh-hazard-pop-flood',
            label: '浸水域内の人口（洪水・想定最大規模）',
            source: kozuiSaidaiSource,
            layers: [kozuiSaidaiLayer],
            ext: { name: 'extHazardPopulation', values: ['flood'] },
            attribution: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
            credit: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
          },
          {
            id: 'oh-hazard-pop-tsunami',
            label: '浸水域内の人口（津波）',
            source: tsunamiSource,
            layers: [tsunamiLayer],
            ext: { name: 'extHazardPopulation', values: ['tsunami'] },
            attribution: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
            credit: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
          },
          {
            id: 'oh-hazard-pop-sediment',
            label: '警戒区域内の人口（土砂災害）',
            source: hazpopDosyaSource,
            layers: [hazpopDosyaFill, hazpopDosyaLine],
            ext: { name: 'extHazardPopulation', values: ['sediment'] },
            attribution: '人口: 国勢調査125mメッシュ / 警戒区域: 不動産情報ライブラリ',
            credit: '人口: 国勢調査125mメッシュ / 警戒区域: 不動産情報ライブラリ',
          },
        ],
      },
      {
        id: 'hazard',
        label: 'ハザードマップ等',
        nodes: [
          // {
          //   id: 'hinanbasyo',
          //   label: '指定緊急避難場所',
          //   description:
          //     '災害対策基本法第49条の4に基づき各市区町村が指定、国土地理院が整備。洪水・津波・土石流・内水氾濫・高潮・地震・大規模火災・噴火の災害種別ごとにフィルタリング可能',
          //   nodes: [
          //     {
          //       id: 'oh-hinanjyo-kozui',
          //       label: '指定緊急避難場所(洪水)',
          //       source: hinanjyoKozuiSource,
          //       layers: [hinanjyoKozuiLayer, hinanjyoKozuiLayerLabel],
          //     },
          //     {
          //       id: 'oh-tsunami-kozui',
          //       label: '指定緊急避難場所(津波)',
          //       source: hinanjyoTsunamiSource,
          //       layers: [hinanjyoTsunamiLayer, hinanjyoTsunamiLayerLabel],
          //     },
          //     {
          //       id: 'oh-hinanjyo-dosekiryu',
          //       label: '指定緊急避難場所(崖崩れ等)',
          //       source: hinanjyoDosekiryuSource,
          //       layers: [hinanjyoDosekiryuLayer, hinanjyoDosekiryuLayerLabel],
          //       attribution: '指定緊急避難場所(崖崩れ、土石流及び地滑り)',
          //     },
          //   ],
          // },
          {
            id: 'oh-kozui-saidai',
            label: '洪水浸水想定（想定最大規模）',
            source: kozuiSaidaiSource,
            layers: [kozuiSaidaiLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
              '<img src="' +
              require('@/assets/legend/shinsui_legend3.png') +
              '">',
            credit: 'ハザードマップポータルサイト  +',
          },
          {
            id: 'oh-keikaku-saidai',
            label: '洪水浸水想定（計画規模）',
            source: kozuikeikskuSource,
            layers: [kozuiKeikskuLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
              '<img src="' +
              require('@/assets/legend/shinsui_legend3.png') +
              '">',
            credit: 'ハザードマップポータルサイト  +',
          },
          {
            id: 'oh-tsunami',
            label: '津波浸水想定',
            source: tsunamiSource,
            layers: [tsunamiLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
              '<img src="' +
              require('@/assets/legend/shinsui_legend3.png') +
              '">',
            credit: 'ハザードマップポータルサイト  +',
          },
          {
            id: 'oh-kozui',
            label: '洪水浸水想定区域（河川単位）',
            source: kozuiSource,
            layers: [kozuiLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
            ext: { name: 'extKozui' },
          },
          {
            id: 'oh-kozui-3d',
            label: '洪水浸水想定区域（河川単位）3D',
            source: kozuiSource,
            layers: [kozui3dLayer],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
            ext: { name: 'extKozui3d' },
          },
          {
            id: 'oh-tadankai',
            label: '多段階浸水想定',
            source: tadankaiSource,
            layers: [tadankaiLayerPolygon, tadankaiLayerLine],
            ext: { name: 'ext-tadankai' },
          },
          {
            id: 'oh-keizoku',
            label: '浸水継続時間（想定最大規模）',
            source: keizokuSource,
            layers: [keizokuLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-kaokutoukai-hanran',
            label: '家屋倒壊等氾濫想定区域（氾濫流）',
            source: kaokutoukaiHanranSource,
            layers: [kaokutoukaiHanranLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-hightide',
            label: '高潮浸水想定区域',
            source: hightideSource,
            layers: [hightideLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
              '<img src="' +
              require('@/assets/legend/shinsui_legend3.png') +
              '">',
            credit: 'ハザードマップポータルサイト  +',
          },
          {
            id: 'oh-naisui',
            label: '内水浸水想定区域',
            source: naisuiSource,
            layers: [naisuiLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
              '<img src="' +
              require('@/assets/legend/shinsui_legend3.png') +
              '">',
            credit: 'ハザードマップポータルサイト  +',
          },
          {
            id: 'oh-nadare',
            label: '雪崩危険箇所',
            source: nadareSource,
            layers: [nadareLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-tameike',
            label: 'ため池決壊による浸水想定',
            source: tameikeSource,
            layers: [tameikeLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-dosya',
            label: '土砂災害警戒区域',
            source: dosyaSource,
            layers: [dosyaLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-dosekiryu',
            label: '土石流危険渓流',
            source: dosekiryuSource,
            layers: [dosekiryuLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-kyukeisya',
            label: '急傾斜地崩壊危険箇所',
            source: kyukeisyaSource,
            layers: [kyukeisyaLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-jisuberi',
            label: '地すべり危険箇所',
            source: jisuberiSource,
            layers: [jisuberiLayer],
            attribution:
              '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
            credit: 'ハザードマップポータルサイト',
          },
          {
            id: 'oh-teiichi',
            label: '低位地帯',
            source: teiichiSource,
            layers: [teiichiLayerPolygon, teiichiLayerLine],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G08-2015.html" target="_blank">国土数値情報</a>' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgba(255, 255, 200, 0.7);"></span>浸水深 0m</li>' +
              '<li><span style="background:rgba(255, 230, 150, 0.7);"></span>浸水深 2m</li>' +
              '<li><span style="background:rgba(255, 180, 100, 0.7);"></span>浸水深 5m</li>' +
              '<li><span style="background:rgba(255, 100, 100, 0.8);"></span>浸水深 10m</li>' +
              '<li><span style="background:rgba(180, 50, 150, 0.8);"></span>浸水深 20m</li>' +
              '</ul>' +
              '</div>',
            credit:
              '国土数値情報  +   +   +  浸水深 0m  +  浸水深 2m  +  浸水深 5m  +  浸水深 10m  +  浸水深 20m  +   +',
          },
          {
            id: 'oh-kansui',
            label: '道路冠水想定箇所（アンダーパス等',
            source: kansuiSource,
            layers: [kansuiLayer, kansuiLayerLabel],
          },
          {
            id: 'oh-tsunami-height',
            label: '津波浸水想定3D',
            source: tsunamiSource2,
            layers: [tsunamiLayerHeight],
            attribution:
              '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A40-2023.html" target="_blank">国土数値情報</a>',
            credit: '国土数値情報',
          },
          {
            id: 'oh-nantora-height',
            label: '宮崎県南海トラフ津波浸水想定3D',
            source: nantoraSource,
            layers: [nantoraLayerHeight],
          },
          // --- 宮崎県防災ハイブリッドレイヤー（ラスター＋MVT）---
          {
            id: 'oh-hyuganada-totatsu',
            label: '日向灘地震 津波到達時間（宮崎県）',
            sources: [hyuganadaTotatsuRasterSource, hyuganadaTotatsuMvtSource],
            layers: [hyuganadaTotatsuRasterLayer, hyuganadaTotatsuMvtLayer, hyuganadaTotatsuMvtLine],
            attribution:
              '<a href="https://data.bodik.jp/dataset/450006_1086" target="_blank">津波浸水開始時間（日向灘地震）</a>' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgb(192,21,27);"></span>～15分</li>' +
              '<li><span style="background:rgb(241,74,55);"></span>15～20分</li>' +
              '<li><span style="background:rgb(251,124,92);"></span>20～25分</li>' +
              '<li><span style="background:rgb(252,179,152);"></span>25～30分</li>' +
              '<li><span style="background:rgb(254,227,214);"></span>30分～</li>' +
              '</ul>' +
              '</div>',
            credit:
              '津波浸水開始時間（日向灘地震）  +   +   +  ～15分  +  15～20分  +  20～25分  +  25～30分  +  30分～  +   +',
          },
          {
            id: 'oh-hyuganada-tsunami',
            label: '日向灘地震 津波浸水深（宮崎県）',
            sources: [hyuganadaTsunamiRasterSource, hyuganadaTsunamiMvtSource],
            layers: [hyuganadaTsunamiRasterLayer, hyuganadaTsunamiMvtLayer, hyuganadaTsunamiMvtLine],
            attribution:
              '<a href="https://data.bodik.jp/dataset/450006_1085" target="_blank">津波浸水深（日向灘R2）</a>' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgb(255,255,179);"></span>0.3m未満</li>' +
              '<li><span style="background:rgb(247,245,169);"></span>0.3～0.5m</li>' +
              '<li><span style="background:rgb(248,225,166);"></span>0.5～1.0m</li>' +
              '<li><span style="background:rgb(255,216,192);"></span>1.0～3.0m</li>' +
              '<li><span style="background:rgb(255,183,183);"></span>3.0～5.0m</li>' +
              '<li><span style="background:rgb(255,145,145);"></span>5.0～10.0m</li>' +
              '<li><span style="background:rgb(242,133,201);"></span>10.0～20.0m</li>' +
              '<li><span style="background:rgb(220,122,220);"></span>20.0m以上</li>' +
              '</ul>' +
              '</div>',
            credit:
              '津波浸水深（日向灘R2）  +   +   +  0.3m未満  +  0.3～0.5m  +  0.5～1.0m  +  1.0～3.0m  +  3.0～5.0m  +  5.0～10.0m  +  10.0～20.0m  +  20.0m以上  +   +',
          },
          {
            id: 'oh-hyuganada-shindo',
            label: '日向灘地震 震度分布（宮崎県）',
            sources: [hyuganadaShindoRasterSource, hyuganadaShindoMvtSource],
            layers: [hyuganadaShindoRasterLayer, hyuganadaShindoMvtLayer, hyuganadaShindoMvtLine],
            attribution:
              '<a href="https://data.bodik.jp/dataset/450006_1087" target="_blank">震度分布（日向灘地震）</a>' +
              '<br>震度は「最大JMA」を使用。' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgb(211,235,249);"></span>震度3</li>' +
              '<li><span style="background:rgb(117,251,253);"></span>震度4</li>' +
              '<li><span style="background:rgb(0,0,245);"></span>震度5弱</li>' +
              '<li><span style="background:rgb(117,251,76);"></span>震度5強</li>' +
              '<li><span style="background:rgb(255,255,84);"></span>震度6弱</li>' +
              '<li><span style="background:rgb(239,135,51);"></span>震度6強</li>' +
              '<li><span style="background:rgb(188,39,27);"></span>震度7</li>' +
              '</ul>' +
              '</div>',
            credit:
              '震度分布（日向灘地震）  +  震度は「最大JMA」を使用。 +   +   +  震度3  +  震度4  +  震度5弱  +  震度5強  +  震度6弱  +  震度6強  +  震度7  +   +',
          },
          {
            id: 'oh-nantora-shindo',
            label: '南海トラフ 震度分布（宮崎県）',
            sources: [nantoraShindoRasterSource, nantoraShindoMvtSource],
            layers: [nantoraShindoRasterLayer, nantoraShindoMvtLayer, nantoraShindoMvtLine],
            attribution:
              '<a href="https://data.bodik.jp/dataset/450006_1083" target="_blank">地震液状化想定（南トラH25）</a>' +
              '<br>震度は「JMA独自」を使用。' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgb(211,235,249);"></span>震度3</li>' +
              '<li><span style="background:rgb(117,251,253);"></span>震度4</li>' +
              '<li><span style="background:rgb(0,0,245);"></span>震度5弱</li>' +
              '<li><span style="background:rgb(117,251,76);"></span>震度5強</li>' +
              '<li><span style="background:rgb(255,255,84);"></span>震度6弱</li>' +
              '<li><span style="background:rgb(239,135,51);"></span>震度6強</li>' +
              '<li><span style="background:rgb(188,39,27);"></span>震度7</li>' +
              '</ul>' +
              '</div>',
            credit:
              '地震液状化想定（南トラH25）  +  震度は「JMA独自」を使用。 +   +   +  震度3  +  震度4  +  震度5弱  +  震度5強  +  震度6弱  +  震度6強  +  震度7  +   +',
          },
          {
            id: 'oh-nantora-ekijyoka',
            label: '南海トラフ 液状化（宮崎県）',
            sources: [nantoraEkijyokaRasterSource, nantoraShindoMvtSource],
            layers: [nantoraEkijyokaRasterLayer, nantoraEkijyokaMvtLayer, nantoraEkijyokaMvtLine],
            attribution:
              '<a href="https://data.bodik.jp/dataset/450006_1083" target="_blank">地震液状化想定（南トラH25）</a>' +
              '<br>液状化可能性は「PL独自」を使用。' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgb(192,192,192);"></span>なし（PL=0）</li>' +
              '<li><span style="background:rgb(116,249,75);"></span>小（PL≦5）</li>' +
              '<li><span style="background:rgb(255,255,84);"></span>中（5＜PL≦15）</li>' +
              '<li><span style="background:rgb(234,51,35);"></span>大（PL＞15）</li>' +
              '</ul>' +
              '</div>',
            credit:
              '地震液状化想定（南トラH25）  +  液状化可能性は「PL独自」を使用。 +   +   +  なし（PL=0）  +  小（PL≦5）  +  中（5＜PL≦15）  +  大（PL＞15）  +   +',
          },
          {
            id: 'oh-hokkaidotsunami-height',
            label: '北海道津波浸水想定3D',
            source: hokkaidotsunamiSource,
            layers: [hokkaidotsunamiLayerHeight],
          },
          {
            id: 'oh-tokyojishin',
            label: '地震に関する危険度一覧(東京都)',
            source: tokyojishinSource,
            layers: [tokyojishinLayerSogo, tokyojishinLayerLine, tokyojishinLayerLabel],
          },
          {
            id: 'oh-tokyojishin3d',
            label: '地震に関する危険度一覧(東京都)3D',
            source: tokyojishinSource,
            layers: [tokyojishinLayerSogo, tokyojishinLayerLine, tokyojishinLayerLabel, tokyojishinheightSogo],
          },
          // {
          //     id: 'oh-tokyojishin',
          //     label: "地震に関する危険度一覧(東京都)",
          //     source: tokyojishinSource,
          //     layers: [tokyojishinLayer,tokyojishinLayerLine,tokyojishinLayerLabel,tokyojishinheight],
          // },
          // {
          //   id: 'oh-densyohi',
          //   label: '災害伝承碑',
          //   source: densyohiSource,
          //   layers: [densyohiLayer, densyohiLayerLabel],
          // },
          {
            id: 'oh-densho',
            label: '自然災害伝承碑',
            description:
              '過去の津波・洪水・土砂災害などの教訓を伝える石碑・モニュメント。全国2000基以上（2025年時点）。国土地理院が市区町村の申請に基づき掲載。災害種別ごとの色分け・フィルタリング・テキスト検索が可能',
            source: denshoSource,
            layers: [denshoLayer, denshoLabelLayer],
            attribution:
              '<a href="https://www.gsi.go.jp/bousaichiri/denshouhi_download.html" target="_blank">国土地理院</a>',
            credit: '国土地理院',
            ext: { name: 'extDensho' },
          },
          // 遺跡等に移動
          // {
          //   id: 'oh-bunkazai',
          //   label: '都道府県指定文化財',
          //   source: bunkazaiSource,
          //   layers: [bunkazaiLayer, bunkazaiLabelLayer],
          //   attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報</a>',
          //   ext: { name: 'extBunkazai' },
          // },
          {
            id: 'oh-fukuokakenhazard',
            label: '福岡県土砂災害',
            source: fukuokakenHazardSource,
            layers: [fukuokakenHazardLayer, fukuokakenHazardLayerLine],
            attribution:
              '<a href="https://ckan.open-governmentdata.org/dataset/401000_dosyasaigaikeikaikuikitoudata" target="_blank">福岡県　土砂災害警戒区域等のshapeデータ</a>',
            credit: '福岡県 土砂災害警戒区域等のshapeデータ',
          },
        ],
      },
      {
        id: 'oh-fude',
        label: '農地の区画情報(筆ポリゴン)',
        source: fudeSource,
        layers: [fudeLayer, fudeLine],
        attribution:
          '<a href="https://www.maff.go.jp/j/tokei/porigon/" target="_blank">農地の区画情報（筆ポリゴン）のデータ提供・利用</a>',
        credit: '農地の区画情報（筆ポリゴン）のデータ提供・利用',
      },
      {
        id: 'oh-nogyochiiki',
        label: 'H27農業地域',
        source: nogyochiikiSource,
        layers: [nogyochiikiLayer, nogyochiikiLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A12.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        // ext: {name:'extTokijyo'}
      },
      {
        id: 'oh-syochiiki',
        label: '2020国勢調査小地域人口ピラミッド',
        source: syochiikiSource,
        layers: [syochiikiLayer, syochiikLayerLine, syochiikiLayerLabel],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/stat-search/files?page=1&toukei=00200521&tstat=000001136464&cycle=0&tclass1=000001136472" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
        ext: { name: 'extSyochiiki' },
      },
      {
        id: 'oh-did',
        label: 'DID人口集中地区',
        source: didSource,
        layers: [didLayer, didLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-did-timeline',
        label: 'DID人口集中地区（時系列）',
        source: didTimelineSource,
        layers: didTimelineLayers,
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extDidTimeline' },
      },
      {
        id: 'oh-bus',
        label: 'R04バスルートと停留所',
        sources: [busSource, busteiSource],
        layers: [busLayer, busteiLayer, busteiLayerLabel],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N07-v2_0.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
        ext: { name: 'extBus', parameters: [] },
      },
      {
        id: 'oh-tetsudo',
        label: '鉄道（廃線は赤色）',
        source: tetsudoSource,
        layers: [tetsudoLayerRed, tetsudoLayerBlue, tetsudoLayerPointRed, tetsudoLayerPointBlue, tetsudoLayerLabel],
        ext: { name: 'extTetsudo', parameters: [] },
      },
      {
        id: 'oh-ekibetsukyaku',
        label: '駅別乗降客数',
        sources: [ekibetsukyakuSource],
        layers: [ekibetsukyakuLine, ekibetsukyakuLabel],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
      },
      {
        id: 'oh-ekibetsukyaku-3d',
        label: '駅別乗降客数３D',
        sources: [ekibetsukyakuSource3d, tetsudo2Source],
        layers: [tetsudoLine, ekibetsukyakuHeight],
        ext: { name: 'extKyakusu' },
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報</a>" +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:hsl(240, 100%, 50%);"></span>0 - 99人</li>' +
          '<li><span style="background:hsl(230, 100%, 55%);"></span>100 - 299人</li>' +
          '<li><span style="background:hsl(220, 100%, 60%);"></span>300 - 499人</li>' +
          '<li><span style="background:hsl(210, 100%, 65%);"></span>500 - 999人</li>' +
          '<li><span style="background:hsl(200, 100%, 70%);"></span>1000 - 4999人</li>' +
          '<li><span style="background:hsl(180, 100%, 50%);"></span>5000 - 19999人</li>' +
          '<li><span style="background:hsl(150, 100%, 50%);"></span>20000 - 99999人</li>' +
          '<li><span style="background:hsl(120, 100%, 50%);"></span>100000 - 499999人</li>' +
          '<li><span style="background:hsl(60, 100%, 50%);"></span>500000 - 1199999人</li>' +
          '<li><span style="background:hsl(0, 100%, 50%);"></span>1200000人+</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報 " +   +   +  0 - 99人  +  100 - 299人  +  300 - 499人  +  500 - 999人  +  1000 - 4999人  +  5000 - 19999人  +  20000 - 99999人  +  100000 - 499999人  +  500000 - 1199999人  +  1200000人+  +   +',
        // info:true
      },
      {
        id: 'oh-yochien',
        label: '幼稚園・保育園等（R0２）',
        source: yochienSource,
        layers: [yochienLayer, yochienLayerLabel],
      },
      {
        id: 'oh-syogakkoR05',
        label: '小学校（R05）',
        source: syogakkoR05Source,
        layers: [syogakkoR05Layer, syogakkoR05LayerLine, syogakkoR05LayerLabel, syogakkoR05LayerPoint],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A27-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-cyugakuR05',
        label: '中学校（R05）',
        source: cyugakuR05Source,
        layers: [cyugakuR05Layer, cyugakuR05LayerLine, cyugakuR05LayerLabel, cyugakuR05LayerPoint],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A32-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-iryokikan',
        label: '医療機関（R02）',
        source: iryokikanSource,
        layers: [iryokikanLayer, iryokikanLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P04-2020.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-city-h12',
        label: 'H12市町村',
        source: cityH12Source,
        layers: [cityH12Layer, cityH12LayerLine, cityH12LayerLabel],
        ext: { name: 'ext-city-h12' },
      },
      {
        id: 'oh-city-r05',
        label: 'R05市町村',
        source: cityR05Source,
        layers: [cityR05Layer, cityR05LayerLine, cityR05LayerLabel],
        ext: { name: 'ext-city-r05' },
      },
      {
        id: 'oh-yotochiikiP',
        label: '用途地域',
        source: yotochiikiPSource,
        layers: [yotochiikiPLayer, yotochiikiPLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tokei',
        label: '都市計画区域',
        source: tokeiSource,
        layers: [tokeiLayer, tokeiLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-kuikikubun',
        label: '市街化調整区域',
        source: kuikikubunSource,
        layers: [kuikikubunLayer, kuikikubunLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tkbt',
        label: '特別用途地区',
        source: tkbtSource,
        layers: [tkbtLayer, tkbtLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-inei',
        label: '陰影起伏図',
        source: ineiSource,
        layers: [ineiLayer],
      },
      {
        id: 'oh-jinkochikei',
        label: '人工地形',
        source: jinkochikeiSource,
        layers: [jinkochikeiLayer],
        attribution:
          '<a href="https://github.com/gsi-cyberjapan/experimental_landformclassification" target="_blank">国土地理院ベクトルタイル提供実験（地形分類）</a>',
        credit: '国土地理院ベクトルタイル提供実験（地形分類）',
      },
      {
        id: 'oh-teii',
        label: '低位地帯',
        source: teiiSource,
        layers: [teiiLayer],
        attribution:
          '<div style="width: 200px;"><a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G08-2015.html" target="_blank">国土数値情報</a><br>' +
          '本データは、周辺部よりも標高が低く、排水が困難である地帯（低位地帯）を整備したものである。<br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '"></div>',
        credit:
          '国土数値情報  + 本データは、周辺部よりも標高が低く、排水が困難である地帯（低位地帯）を整備したものである。  +',
        info: true,
      },
      {
        id: 'oh-kosyo',
        label: '湖沼',
        source: kosyoSource,
        layers: [kosyoLayer, kosyoLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W09-2005.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-danso',
        label: '都市圏活断層図',
        source: dansoSource,
        layers: [dansoLayer],
        attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html#afm" target="_blank">地理院タイル</a>',
        credit: '地理院タイル',
      },
      {
        id: 'oh-jshis-fault',
        label: '主要活断層帯（J-SHIS）',
        sources: [jshisFaultSource],
        layers: [jshisFaultFillLayer, jshisFaultLineLayer, jshisFaultLabelLayer],
        attribution: '<a href="https://www.j-shis.bosai.go.jp/" target="_blank">J-SHIS 地震ハザードステーション</a>',
        credit: 'J-SHIS 地震ハザードステーション',
        ext: { name: 'extJshisFault' },
      },
      {
        id: 'oh-jma-quake',
        label: '<span style="color:red">NEW </span>地震情報（気象庁）',
        description:
          '気象庁の公式一次ソースから取得する最新地震情報。震源・各観測点の震度（JMA震度階級色）・緊急地震速報（EEW）をリアルタイム表示。60秒ごと自動更新',
        sources: [jmaQuakeEpicenterSource, jmaQuakeStationSource, jmaQuakeEewSource],
        layers: [jmaQuakeStationLayer, jmaQuakeStationLabelLayer, jmaQuakeEewLayer, jmaQuakeEpicenterLayer, jmaQuakeEpicenterLabelLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/map.html#contents=quake" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extJmaQuake' },
      },
      // {
      //   id: 'oh-overture-buildings',
      //   label: "<span style='color: red'>NEW</span>Overture Maps 建物",
      //   sources: [overtureBuildsSource],
      //   layers: [overtureBuildsFillLayer, overtureBuildsLineLayer],
      //   attribution: '<a href="https://overturemaps.org/" target="_blank">Overture Maps Foundation</a> (ODbL)',
      // },
      {
        id: 'oh-kyusekki',
        label: '全国旧石器遺跡',
        source: kyusekkiSource,
        layers: [kyusekkiLayer, kyusekkiLayerHeatmap],
      },
    ],
  },
  {
    id: 'gsi',
    label: (isGsiNew ? '<span style="color: red;">NEW </span>' : '') + '️地理院タイル',
    nodes: convertTileJson,
  },
  {
    id: 'tochibunrui',
    label: '土地分類基本調査（土地履歴調査）',
    nodes: tochibunruiNodes,
  },
  {
    id: 1,
    label: '基本地図',
    nodes: [
      {
        id: 'OSM',
        label: 'OSM',
        nodes: [
          {
            id: 'protomaps-folder',
            label: 'Protomaps',
            nodes: [
              {
                id: 'oh-vector-layer-protomaps-light',
                label: 'Protomaps Light',
                sources: protomapsSources,
                layers: protomapsLightLayers,
                attribution:
                  '<a href="https://protomaps.com" target="_blank">Protomaps</a> / © <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a>',
                credit: 'Protomaps / © OpenStreetMap',
              },
              {
                id: 'oh-vector-layer-protomaps-dark',
                label: 'Protomaps Dark',
                sources: protomapsSources,
                layers: protomapsDarkLayers,
                attribution:
                  '<a href="https://protomaps.com" target="_blank">Protomaps</a> / © <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a>',
                credit: 'Protomaps / © OpenStreetMap',
              },
              {
                id: 'oh-vector-layer-protomaps-grayscale',
                label: 'Protomaps Grayscale',
                sources: protomapsSources,
                layers: protomapsGrayscaleLayers,
                attribution:
                  '<a href="https://protomaps.com" target="_blank">Protomaps</a> / © <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a>',
                credit: 'Protomaps / © OpenStreetMap',
              },
              {
                id: 'oh-vector-layer-protomaps-black',
                label: 'Protomaps Black',
                sources: protomapsSources,
                layers: protomapsBlackLayers,
                attribution:
                  '<a href="https://protomaps.com" target="_blank">Protomaps</a> / © <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a>',
                credit: 'Protomaps / © OpenStreetMap',
              },
              {
                id: 'oh-vector-layer-protomaps-white',
                label: 'Protomaps White',
                sources: protomapsSources,
                layers: protomapsWhiteLayers,
                attribution:
                  '<a href="https://protomaps.com" target="_blank">Protomaps</a> / © <a href="https://osm.org/copyright" target="_blank">OpenStreetMap</a>',
                credit: 'Protomaps / © OpenStreetMap',
              },
            ],
          },
          {
            id: 'oh-vector-layer-osm-bright',
            label: 'OSMベクター',
            sources: osmBrightSources,
            layers: osmBrightLayers,
            attribution:
              '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
            credit: '© OpenStreetMap contributors',
          },
          {
            id: 'oh-vector-layer-osm-bright-label-only',
            label: 'OSMベクターラベルのみ',
            sources: osmBrightLabelOnlySources,
            layers: osmBrightLabelOnlyLayers,
            attribution:
              '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
            credit: '© OpenStreetMap contributors',
            ext: { name: 'extLabelOnly' },
          },
          {
            id: 'oh-vector-layer-osm-3d',
            label: 'OSMベクター3D',
            sources: osm3dSources,
            layers: osm3dLayers,
            attribution:
              '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
            credit: '© OpenStreetMap contributors',
          },
          {
            id: 'oh-osm',
            label: 'OSMラスター',
            source: osmSource,
            layers: [osmLayer],
            attribution:
              '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
            credit: '© OpenStreetMap contributors',
          },
          // {
          //   id: 'oh-overpass',
          //   label: 'OSM overpass',
          //   source: overpassSource,
          //   layers: [overpassPolygon, overpassPolygonLine, overpassLine, overpassPoint],
          //   attribution:
          //     '© <a href="https://wiki.openstreetmap.org/wiki/Japan/Community_Overpass_API" target="_blank">OpenStreetMap</a> contributors<br>' +
          //     '<a href="https://wiki.openstreetmap.org/wiki/JA:How_to_map_a" target="_blank">How to map a</a>',
          //   ext: { name: 'extOSM' },
          // },
          // oh-overpass-ai はトップレベルに移動
          // {
          //     id: 'oh-vector-layer-osm-toner',
          //     label: "OSMベクター・トナー",
          //     sources: osmTonerSources,
          //     layers: osmTonerLayers,
          //     attribution: '© <a href="https://wiki.openstreetmap.org/wiki/Japan/OSMFJ_Tileserver" target="_blank">OpenStreetMap</a> contributors',
          // },
        ],
      },
      {
        id: 'oh-vector-fx-basic',
        label: '地理院ベクター',
        sources: fxBasicSources,
        layers: fxBasicLayers,
        // ext: {name:'extChiriin'}
      },
      {
        id: 'oh-vector-mono',
        label: '地理院ベクター・モノクロ',
        sources: monoSources,
        layers: monoLayers,
        // ext: { name: 'extChiriinMono' },
      },
      {
        id: 'oh-vector-fx-dark',
        label: '地理院ベクター・ダーク',
        sources: fxDarkSources,
        layers: fxDarkLayers,
        // ext: {name:'extChiriinDark'}
      },
      {
        id: 'oh-vector-maniawase-light',
        label: '地理院ベクター・軽量',
        sources: maniawaseLightSources,
        layers: maniawaseLightLayers,
        attribution:
          '<a href="https://maps.gsi.go.jp/vector/" target="_blank">地理院地図Vector</a> / スタイル: <a href="https://cat-in-136.github.io/2026/02/design-and-implementation-of-gsi-style-maniawase.html" target="_blank">cat-in-136</a>',
        credit: '地理院地図Vector / スタイル: cat-in-136',
      },
      {
        id: 'oh-vector-maniawase-dark',
        label: '地理院ベクター・軽量ダーク',
        sources: maniawaseDarkSources,
        layers: maniawaseDarkLayers,
        attribution:
          '<a href="https://maps.gsi.go.jp/vector/" target="_blank">地理院地図Vector</a> / スタイル: <a href="https://cat-in-136.github.io/2026/02/design-and-implementation-of-gsi-style-maniawase.html" target="_blank">cat-in-136</a>',
        credit: '地理院地図Vector / スタイル: cat-in-136',
      },
      {
        id: 'oh-vector-maniawase-custom',
        label: '地理院ベクター・軽量カスタム',
        sources: maniawaseCustomSources,
        layers: maniawaseCustomLayers,
        ext: { name: 'extManiawaseCustom' },
        attribution:
          '<a href="https://maps.gsi.go.jp/vector/" target="_blank">地理院地図Vector</a> / スタイル: <a href="https://cat-in-136.github.io/2026/02/design-and-implementation-of-gsi-style-maniawase.html" target="_blank">cat-in-136</a>',
        credit: '地理院地図Vector / スタイル: cat-in-136',
      },
      {
        id: 'oh-gsi-optimal-bvmap',
        label: '最適化ベクトルタイル',
        sources: bvmapSources,
        layers: bvmapLayers,
        attribution:
          '<a href="https://github.com/gsi-cyberjapan/optimal_bvmap" target="_blank">国土地理院 最適化ベクトルタイル</a>',
        credit: '国土地理院 最適化ベクトルタイル',
        ext: { name: 'extGsiBvmap' },
      },
      {
        id: 'oh-stdLayer',
        label: '地理院標準地図',
        source: stdSource,
        layers: [stdLayer],
      },
      {
        id: 'oh-pale-layer',
        label: '地理院淡色地図',
        source: paleSource,
        layers: [paleLayer],
      },
      {
        id: 'oh-mapbox-streets',
        label: 'Mapbox Streets',
        source: mapboxStreetsSource,
        layers: [mapboxStreetsLayer],
        attribution: '&copy; Mapbox &copy; OpenStreetMap',
        credit: '© Mapbox © OpenStreetMap',
      },
      {
        id: 'oh-plateauPmtiles',
        label: 'PLATEAU建物',
        source: plateauPmtilesSource,
        layers: [plateauPmtilesLayer],
        ext: { name: 'extPlateau' },
      },
      {
        id: 'oh-plateau-tokyo23ku',
        label: 'PLATEAU建物東京都23区',
        source: plateauTokyo23kuSource,
        layers: [plateauTokyo23kuLayer],
        attribution: '<a href="https://github.com/indigo-lab/plateau-lod2-mvt?tab=readme-ov-file">plateau-lod2-mvt</a>',
        credit: 'plateau-lod2-mvt',
        ext: { name: 'extPlateauTokyo23ku' },
      },
      // PLATEAU建物3Dは deck.gl の Tile3DLayer が Draco圧縮b3dmを正しく描画できないため無効化
      // {
      //   id: 'oh-plateau-3dtiles',
      //   label: 'PLATEAU建物3D',
      //   sources: [],
      //   layers: [],
      //   attribution: '<a href="https://www.mlit.go.jp/plateau/" target="_blank">PLATEAU</a> 3D Tiles',
      //   ext: { name: 'extPlateau3DTiles' },
      // },
    ],
  },
  {
    id: 'pointcloud-folder',
    label: '点群データ',
    nodes: [
      {
        id: 'oh-pc-vs-hamamatsu-wide',
        label: '浜松市 中心地（LP点群）',
        description: 'VIRTUAL SHIZUOKA レーザー点群。静岡県浜松市中心部の3D Tiles形式点群データ',
        sources: [],
        layers: [],
        attribution:
          '出典: <a href="https://www.geospatial.jp/ckan/dataset/virtual-shizuoka-mw" target="_blank" rel="noopener">VIRTUAL SHIZUOKA（静岡県）</a> CC BY 4.0',
        credit: '出典: VIRTUAL SHIZUOKA（静岡県） CC BY 4.0',
        ext: {
          name: 'extPointcloud',
          pcType: '3dtiles',
          tilesetUrl: 'https://kenzkenz4.xsrv.jp/vs-pointcloud/hamamatsu-wide/tileset.json',
          center: [137.735, 34.704],
          zoom: 13,
          // geoidHeight: 0（デフォルト。py3dtiles変換データは補正不要）
        },
      },
      {
        id: 'oh-pc-aist-copc',
        label: 'AIST 3DDB 点群（COPC）',
        description: '産総研 3次元地質地盤図 COPC点群ストリーミング。東京都23区・兵庫県のデータが利用可能',
        sources: [],
        layers: [],
        attribution:
          '出典: <a href="https://www.digiarc.aist.go.jp/team/gsvrt/information/tdv-copc-data/" target="_blank" rel="noopener">産総研 3DDB COPC</a>',
        credit: '出典: 産総研 3DDB COPC',
        ext: {
          name: 'extPointcloud',
          pcType: 'copc',
        },
      },
      // {
      //   id: 'oh-pc-expo2025',
      //   label: '大阪万博 点群（PLATEAU）',
      //   description: 'PLATEAU 2025年度 大阪・関西万博エリアのレーザー点群データ（3D Tiles形式）',
      //   sources: [],
      //   layers: [],
      //   attribution: '出典: <a href="https://www.geospatial.jp/ckan/dataset/plateau-27999-osaka-shi-2025" target="_blank" rel="noopener">PLATEAU 大阪市</a>',
      //   ext: {
      //     name: 'extPointcloud',
      //     pcType: '3dtiles',
      //     tilesetUrl: 'http://localhost:9998/tileset.json',
      //     center: [135.383, 34.653],
      //     zoom: 15,
      //   },
      // },
    ],
  },
  {
    id: 'syashin',
    label: '航空写真',
    nodes: [
      {
        id: 'oh-mapbox-satellite',
        label: '高解像度衛星写真（Mapbox）',
        source: mapboxSatelliteSource,
        layers: [mapboxSatelliteLayer],
        attribution: '&copy; Mapbox &copy; Maxar',
        credit: '© Mapbox © Maxar',
      },
      {
        id: 'oh-mapbox-satellite-streets',
        label: '衛星写真+地名（Mapbox）',
        description: '高解像度衛星写真に道路名・地名ラベルを重ねたスタイル。衛星写真でも地名が読める',
        source: mapboxSatelliteStreetsSource,
        layers: [mapboxSatelliteStreetsLayer],
        attribution: '&copy; Mapbox &copy; Maxar',
        credit: '© Mapbox © Maxar',
      },
      {
        id: 'sentinel-2',
        label: 'Sentinel-2 衛星画像',
        nodes: [
          {
            id: 'oh-sentinel-true-color',
            label: '真色合成（自然色）',
            source: sentinelTrueColorSource,
            layers: [sentinelTrueColorLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: 'Sentinel-2 衛星の真色合成画像。10m解像度、3〜5日更新。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-false-color',
            label: 'フォルスカラー（植生）',
            source: sentinelFalseColorSource,
            layers: [sentinelFalseColorLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '近赤外バンドを使った疑似カラー合成。植生が赤く表示される。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-ndvi',
            label: 'NDVI（植生指数）',
            source: sentinelNdviSource,
            layers: [sentinelNdviLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '正規化植生指数。緑が濃いほど植生が豊か。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-moisture-index',
            label: '水分指数',
            source: sentinelMoistureSource,
            layers: [sentinelMoistureLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '植生や土壌の水分量を示す指数。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-ndwi',
            label: 'NDWI（水域指数）',
            source: sentinelNdwiSource,
            layers: [sentinelNdwiLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '水域を検出する指数。洪水検出に有用。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-swir',
            label: 'SWIR（短波赤外）',
            source: sentinelSwirSource,
            layers: [sentinelSwirLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '短波赤外バンド合成。雲・雪・氷の識別に有用。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-agriculture',
            label: '農業',
            source: sentinelAgricultureSource,
            layers: [sentinelAgricultureLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '農地の状態を可視化するバンド合成。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-geology',
            label: '地質',
            source: sentinelGeologySource,
            layers: [sentinelGeologyLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '地質構造を可視化するバンド合成。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-nbr-raw',
            label: 'NBR（焼損面積指数）',
            source: sentinelNbrSource,
            layers: [sentinelNbrLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '火災跡地を検出する指数。防災・林野管理に有用。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-false-color-urban',
            label: '都市フォルスカラー',
            source: sentinelUrbanSource,
            layers: [sentinelUrbanLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '都市域を強調するフォルスカラー合成。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-bathymetric',
            label: '水深',
            source: sentinelBathymetricSource,
            layers: [sentinelBathymetricLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: '浅い水域の水深を推定する可視化。',
            ext: { name: 'extSentinel' },
          },
          {
            id: 'oh-sentinel-evi',
            label: 'EVI（拡張植生指数）',
            source: sentinelEviSource,
            layers: [sentinelEviLayer],
            attribution:
              '&copy; <a href="https://dataspace.copernicus.eu/" target="_blank">Copernicus</a> / ESA Sentinel-2',
            credit: '© Copernicus / ESA Sentinel-2',
            description: 'NDVIを改良した拡張植生指数。高密度植生でも飽和しにくい。',
            ext: { name: 'extSentinel' },
          },
        ],
      },
      {
        id: 'oh-seamlessphoto',
        label: '最新写真',
        source: seamlessphotoSource,
        layers: [seamlessphotoLayer],
      },
      {
        id: 'virtual-shiazuok',
        label: 'VIRTUAL SHIZUOKA 静岡県',
        nodes: [
          {
            id: 'oh-virtual-shiazuoka-zenbu',
            label: 'VIRTUAL SHIZUOKA 静岡県 全部',
            sources: [
              shizuokaOrthoSource,
              shizuokaOrthoTobuSource,
              shizuokaOrthoIzuseibuSource,
              shizuokaOrthoFujiSource,
              shizuokaOrthoAlpusSource,
            ],
            layers: [
              shizuokaOrthoLayer,
              shizuokaOrthoTobuLayer,
              shizuokaOrthoIzuseibuLayer,
              shizuokaOrthoFujiLayer,
              shizuokaOrthoAlpusLayer,
            ],
          },
          {
            id: 'oh-virtual-shiazuoka-tobu',
            label: 'VIRTUAL SHIZUOKA 静岡県 中・西部',
            source: shizuokaOrthoSource,
            layers: [shizuokaOrthoLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/virtual-shizuoka-mw" target="_blank">VIRTUAL SHIZUOKA 静岡県 中・西部 点群データ</a>',
            credit: 'VIRTUAL SHIZUOKA 静岡県 中・西部 点群データ',
          },
          {
            id: 'oh-virtual-shiazuoka-tobu2',
            label: 'VIRTUAL SHIZUOKA 静岡県 東部',
            source: shizuokaOrthoTobuSource,
            layers: [shizuokaOrthoTobuLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/virtual-shizuoka-mw" target="_blank">VIRTUAL SHIZUOKA 静岡県 中・西部 点群データ</a>',
            credit: 'VIRTUAL SHIZUOKA 静岡県 中・西部 点群データ',
          },
          {
            id: 'oh-virtual-shiazuoka-izuseibu',
            label: 'VIRTUAL SHIZUOKA 静岡県 伊豆西部',
            source: shizuokaOrthoIzuseibuSource,
            layers: [shizuokaOrthoIzuseibuLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/shizuoka-2020-pointcloud" target="_blank">VIRTUAL SHIZUOKA 静岡県 伊豆西部 点群データ</a>',
            credit: 'VIRTUAL SHIZUOKA 静岡県 伊豆西部 点群データ',
          },
          {
            id: 'oh-virtual-shiazuoka-fuji',
            label: 'VIRTUAL SHIZUOKA 静岡県 富士',
            source: shizuokaOrthoFujiSource,
            layers: [shizuokaOrthoFujiLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/shizuoka-2021-pointcloud" target="_blank">VIRTUAL SHIZUOKA 静岡県 富士山および静岡東部 点群データ</a>',
            credit: 'VIRTUAL SHIZUOKA 静岡県 富士山および静岡東部 点群データ',
          },
          {
            id: 'oh-virtual-shiazuoka-alpus',
            label: 'VIRTUAL SHIZUOKA 静岡県 北部',
            source: shizuokaOrthoAlpusSource,
            layers: [shizuokaOrthoAlpusLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/shizuoka-2022-pointcloud" target="_blank">VIRTUAL SHIZUOKA 静岡県 北部（南アルプス） 点群データ</a>',
            credit: 'VIRTUAL SHIZUOKA 静岡県 北部（南アルプス） 点群データ',
          },
        ],
      },
      {
        id: 'oh-sp87',
        label: '1987~90年航空写真(一部)',
        source: sp87Source,
        layers: [sp87Layer],
        // ext: {name:'ext-sp87'}
      },
      {
        id: 'oh-sp84',
        label: '1984~86年航空写真(一部)',
        source: sp84Source,
        layers: [sp84Layer],
        // ext: {name:'ext-sp84'}
      },
      {
        id: 'oh-sp79',
        label: '1979~83年航空写真(一部)',
        source: sp79Source,
        layers: [sp79Layer],
        // ext: {name:'ext-sp79'}
      },
      {
        id: 'oh-sp74',
        label: '1974~78年航空写真(全国)',
        source: sp74Source,
        layers: [sp74Layer],
        // ext: {name:'ext-sp74'}
      },
      {
        id: 'oh-sp61',
        label: '1961~64年航空写真(一部)',
        source: sp61Source,
        layers: [sp61Layer],
        // ext: {name:'ext-sp61'}
      },
      {
        id: 'oh-sp45',
        label: '1945~50年航空写真(一部)',
        source: sp45Source,
        layers: [sp45Layer],
        // ext: {name:'ext-sp45'}
      },
      {
        id: 'oh-sp36',
        label: '1936~42年航空写真(一部)',
        source: sp36Source,
        layers: [sp36Layer],
        // ext: {name:'ext-sp36'}
      },
      {
        id: 'oh-sp28',
        label: '1928年航空写真(大阪府)',
        source: sp28Source,
        layers: [sp28Layer],
        // ext: {name:'ext-sp28'}
      },
      {
        id: 'oh-kanagawa-syashin',
        label: '神奈川航空写真',
        source: kanagawaSyashinSource,
        layers: [kanagawaSyashinLayer],
      },
      {
        id: 'oh-yokohama-syashin',
        label: '横浜北部、川崎航空写真',
        source: yokohamaSyashinSource,
        layers: [yokohamaSyashinLayer],
      },
      {
        id: 'oh-miyazaki-syashin',
        label: '宮崎県航空写真',
        source: miyazakiSyashinSource,
        layers: [miyazakiSyashinLayer],
      },
      {
        id: 'oh-yamanashi-ortho',
        label: '山梨県航空写真',
        source: yamanashiOrthoSource,
        layers: [yamanashiOrthoLayer],
        attribution: '山梨県航空写真画像 CC-BY 4.0',
        credit: '山梨県航空写真画像 CC-BY 4.0',
      },
      {
        id: 'oh-tokyo-ortho',
        label: '東京都23区航空写真',
        source: tokyoOrthoSource,
        layers: [tokyoOrthoLayer],
        attribution: '東京都デジタルツイン3Dビューア CC-BY 4.0',
        credit: '東京都デジタルツイン3Dビューア CC-BY 4.0',
      },
      {
        id: 'oh-dronebird',
        label: 'DRONEBIRD',
        source: dronebirdSource,
        layers: [dronebirdLayer],
      },
      {
        id: 'oh-muroran-ortho',
        label: '室蘭市航空写真',
        source: muroranOrthoSource,
        layers: [muroranOrthoLayer],
        attribution:
          '<a href="https://murorancity-opendata-muroran.hub.arcgis.com/" target="_blank">むろらんオープンデータライブラリ</a>',
        credit: 'むろらんオープンデータライブラリ',
      },
      {
        id: 'oh-itoigawa-ortho',
        label: '糸魚川市航空写真',
        source: itoigawaOrthoSource,
        layers: [itoigawaOrthoLayer],
        attribution: '<a href="https://wiki.openstreetmap.org/wiki/Itoigawa_ortho" target="_blank">Itoigawa ortho</a>',
        credit: 'Itoigawa ortho',
      },
      {
        id: 'oh-nerima-ortho',
        label: '練馬区航空写真',
        source: nerimaOrthoSource,
        layers: [nerimaOrthoLayer],
        attribution: '<a href="https://wiki.openstreetmap.org/wiki/Nerima_ortho" target="_blank">Nerima ortho</a>',
        credit: 'Nerima ortho',
      },
      {
        id: 'oh-fukaya-ortho',
        label: '深谷市航空写真',
        source: fukayaOrthoSource,
        layers: [fukayaOrthoLayer],
        attribution: '<a href="https://wiki.openstreetmap.org/wiki/Fukaya_ortho" target="_blank">Fukaya ortho</a>',
        credit: 'Fukaya ortho',
      },
      {
        id: 'oh-atsugi-ortho',
        label: '厚木市航空写真',
        source: atsugiOrthoSource,
        layers: [atsugiOrthoLayer],
        attribution: '<a href="https://wiki.openstreetmap.org/wiki/Atsugi_ortho" target="_blank">Atsugi ortho</a>',
        credit: 'Atsugi ortho',
      },
      {
        id: 'oh-kakegawa-ortho',
        label: '掛川市航空写真',
        source: kakegawaOrthoSource,
        layers: [kakegawaOrthoLayer],
        attribution:
          '<a href="https://www.city.kakegawa.shizuoka.jp/gyosei/docs/452145.html" target="_blank">令和4年度掛川市航空写真</a>',
        credit: '令和4年度掛川市航空写真',
      },
      {
        id: 'oh-handa-ortho',
        label: '半田市航空写真',
        source: handaOrthoSource,
        layers: [handaOrthoLayer],
        attribution:
          '<a href="https://www.city.handa.lg.jp/opendata/1005557/1005561/1004329.html" target="_blank">半田市オープンデータ</a>',
        credit: '半田市オープンデータ',
      },
      {
        id: 'oh-tondabayashi-ortho',
        label: '富田林市航空写真',
        source: tondabayashiOrthoSource,
        layers: [tondabayashiOrthoLayer],
        attribution: '<a href="https://www.city.tondabayashi.lg.jp/" target="_blank">富田林市</a>',
        credit: '富田林市',
      },
      {
        id: 'oh-kagoshima-ortho',
        label: '鹿児島市航空写真',
        source: kagoshimaOrthoSource,
        layers: [kagoshimaOrthoLayer],
        attribution: '<a href="https://www.city.kagoshima.lg.jp/" target="_blank">鹿児島市オープンデータ</a>',
        credit: '鹿児島市オープンデータ',
      },
      {
        id: 'oh-plateau-ortho',
        label: 'PLATEAUオルソ',
        source: plateauOrthoSource,
        layers: [plateauOrthoLayer],
        attribution:
          '<a href="https://github.com/Project-PLATEAU/plateau-streaming-tutorial/blob/main/ortho/plateau-ortho-streaming.md" target="_blank">PLATEAU</a>',
        credit: 'PLATEAU',
      },
      {
        id: 'osmfj-ortho',
        label: 'OSMFJ航空写真',
        nodes: [
          {
            id: 'oh-osmfj-kyoto',
            label: '京都府航空写真',
            source: osmfjKyotoSource,
            layers: [osmfjKyotoLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-osaka',
            label: '大阪市航空写真2023',
            source: osmfjOsakaSource,
            layers: [osmfjOsakaLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-himeji',
            label: '姫路市航空写真',
            source: osmfjHimejiSource,
            layers: [osmfjHimejiLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-kochi',
            label: '高知市航空写真',
            source: osmfjKochiSource,
            layers: [osmfjKochiLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-nagahama',
            label: '長浜市航空写真',
            source: osmfjNagahamaSource,
            layers: [osmfjNagahamaLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-nagaoka',
            label: '長岡市航空写真',
            source: osmfjNagaokaSource,
            layers: [osmfjNagaokaLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-asago',
            label: '朝来市航空写真',
            source: osmfjAsagoSource,
            layers: [osmfjAsagoLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
          {
            id: 'oh-osmfj-handa',
            label: '半田市航空写真(OSMFJ)',
            source: osmfjHandaSource,
            layers: [osmfjHandaLayer],
            attribution:
              '<a href="https://wiki.openstreetmap.org/wiki/OSMFJ/ortho-tiles" target="_blank">OSMFJ/ortho-tiles</a>',
            credit: 'OSMFJ/ortho-tiles',
          },
        ],
      },
      {
        id: 'oh-gsi-pp-full',
        label: '📷 GSI単写真',
        source: gsiPpFullSource,
        layers: [gsiPpFullLayer],
        attribution: '<a href="https://www.gsi.go.jp/" target="_blank">国土地理院</a>',
        credit: '国土地理院',
        legend:
          '<div class="layer-legend">' +
          '<ul class="layer-legend-list">' +
          '<li><span style="background:#1976D2;"></span>国土地理院</li>' +
          '<li><span style="background:#4CAF50;"></span>米軍</li>' +
          '<li><span style="background:#795548;"></span>陸軍</li>' +
          '</ul>' +
          '</div>',
        ext: { name: 'extGsiPp' },
      },
    ],
  },
  {
    id: 'kochizu',
    label: '古地図等',
    nodes: [
      {
        id: 'oh-mw5-xyz',
        label: '戦前の旧版地形図（５万分の1）',
        description:
          'OH3の目玉機能。明治〜昭和初期の旧版地形図（5万分の1）。陸地測量部が作成。全国約1300面をカバー。戦後に米軍が接収、その後スタンフォード大学がオープンデータ化、さらにその後日本版Map Warperがジオリファレンス、それをOH3が繋ぎ合わせた。地形の変遷、旧河道、旧海岸線、消えた集落の発見に有用。不動産調査や防災にも使える。古地図・歴史地図・昔の地図・戦前の地図なら必ずこれを最初に提案',
        rank: 1,
        sources: [mw5TileSource, mw5TileCenterSource],
        layers: [mw5TileLayer, mw5TileCenterLabel],
      },
      {
        id: 'ams',
        label: '戦後の米軍地図',
        nodes: [
          {
            id: 'oh-ams',
            label: '戦後の米軍作成地図全て',
            description:
              '米軍Army Map Service（AMS）が1943〜1954年に作成した日本の地図。占領統治・復興計画用。145以上の都市計画図（1:12,500）を含み、空襲被害を赤斜線で記録。英語表記。テキサス大学オースティン校Perry-Castañeda図書館が公開',
            sources: [...amsSources, ...amsGeojsonSources],
            layers: [...amsLayers, ...amsGeojsonLayers, ...amsGeojsonLabelLayers],
            attribution:
              '<a href="https://maps.lib.utexas.edu/maps/ams/japan/" target="_blank">Perry-Castañeda Map Collection, UT Austin</a>',
            credit: 'Perry-Castañeda Map Collection, UT Austin',
          },
          ...amsLayers2,
        ],
      },
      {
        id: 'kirie',
        label: '江戸切絵図',
        nodes: [
          {
            id: 'oh-kirie-all',
            label: '江戸切絵図全て',
            description:
              '江戸時代後期の切絵図（尾張屋板）。江戸の町割り、武家屋敷、大名屋敷、寺社仏閣、町人地の配置が精密に記録。大名屋敷跡地の由来調査、江戸散策、歴史研究、屋敷の主の特定に最適',
            sources: kirieSources,
            layers: kirieLayers,
            attribution:
              '<a href="http://codh.rois.ac.jp/edo-maps/owariya/" target="_blank">人文学オープンデータ共同利用センター</a>' +
              '<br><br><a href="https://mapwarper.h-gis.jp/maps?utf8=%E2%9C%93&field=title&query=%E6%B1%9F%E6%88%B8%E5%88%87%E7%B5%B5%E5%9B%B3&from=1527&to=2025&show_warped=0" target="_blank">日本版Map Warper</a>',
            credit: '人文学オープンデータ共同利用センター  +  日本版Map Warper',
          },
          ...kirieLayers2,
        ],
      },
      konjyakuMap,
      kotizuTreeNode,
      {
        id: 'oh-tokyokasai',
        label: '東京市火災動態地図（大正12年）',
        description:
          '関東大震災（1923年9月1日）直後の東京市における火災延焼の時系列動態地図。出火点から延焼範囲が時刻ごとに広がる様子を確認できる。震災研究、防災史、都市計画の歴史資料',
        sources: tokyokasaiSources,
        layers: tokyokasaiLayers,
        position: [139.775, 35.689],
        z: 13,
        attribution: '出典：<a href="https://mapwarper.h-gis.jp/layers/34" target="_blank">日本版MapWarper</a>',
        credit: '日本版MapWarper',
      },
      {
        id: 'oh-jinsoku',
        label: '迅速測図',
        description:
          '明治14〜19年にフランス式で作成された彩色地図。関東平野が中心エリア。色鮮やかな手書き彩色で明治初期の田・畑・林・集落・街道といった土地利用が詳細に記録されている。近代化前の原風景がわかる貴重な資料',
        sources: [jinsokuSource],
        layers: [jinsokuLayer],
        // ext: {name:'ext-jinsoku'}
      },
      {
        id: 'oh-jissoku',
        label: '北海道実測切図',
        description:
          '明治20年代に作成された北海道の実測地形図。開拓期の北海道の地形・集落・街道・地名を詳細に記録。北海道の開拓史研究、旧集落の発掘、地名由来調査に活用',
        sources: [jissokuSource, jissokuMatsumaeSource],
        layers: [jissokuLayer, jissokuMatsumaeLayer],
      },
      {
        id: 'oh-ezosansen',
        label: '東西蝦夷山川地理取調図',
        description:
          '松浦武四郎が幕末〜明治初期に作成した蝦夷地（北海道）の全域図。アイヌの地名・集落・山川を詳細に記録。アイヌ文化研究、北海道の地名由来調査、松浦武四郎研究に',
        sources: [ezosansenSource],
        layers: [ezosansenLayer],
        attribution: '<a href="https://github.com/koukita/touzaiezo" target="_blank">喜多氏のgithub</a>',
        credit: '喜多氏のgithub',
      },
      {
        id: 'oh-ezosansen2',
        label: '東西蝦夷山川地理取調図2',
        description: '松浦武四郎作成の東西蝦夷山川地理取調図の別バージョン。初版と詳細が異なる箇所の比較研究に',
        sources: [ezosansen2Source],
        layers: [ezosansen2Layer],
        attribution: '<a href="https://github.com/koukita/touzaiezo" target="_blank">喜多氏のgithub</a>',
        credit: '喜多氏のgithub',
      },
      {
        id: 'oh-gokaido',
        label: '五街道',
        description:
          '江戸時代の主要街道（東海道・中山道・日光街道・奥州街道・甲州街道）のルート。宿場町・一里塚・峠・渡しの歴史的位置を確認できる。歴史散策、旧街道のウォーキング・サイクリング、歴史研究に',
        source: gokaidoSource,
        layers: [gokaidoLayer, gokaidoLayerLabel],
        attribution: '<a href="https://github.com/japan-road-jp/Gokaodo-Map/tree/main" target="_blank">Gokaodo-Map</a>',
        credit: 'Gokaodo-Map',
      },
    ],
  },
  {
    id: 'citygis',
    label: '都市計画決定GISデータ',
    nodes: [
      // === 2024年版（新データ）===
      {
        id: 'oh-youto2024',
        label: '用途地域(2024)',
        sources: [youto2024Source],
        layers: [youto2024Layer, youto2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokei2024',
        label: '都市計画区域(2024)',
        sources: [tokei2024Source],
        layers: [tokei2024Layer, tokei2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-senbiki2024',
        label: '区域区分(2024)',
        sources: [senbiki2024Source],
        layers: [senbiki2024Layer, senbiki2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-ritteki2024',
        label: '立地適正化計画(2024)',
        sources: [ritteki2024Source],
        layers: [ritteki2024Layer, ritteki2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-douro2024',
        label: '都市計画道路(2024)',
        sources: [douro2024Source],
        layers: [douro2024Layer, douro2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-kouen2024',
        label: '公園・緑地(2024)',
        sources: [kouen2024Source],
        layers: [kouen2024Layer, kouen2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-bouka2024',
        label: '防火地域(2024)',
        sources: [bouka2024Source],
        layers: [bouka2024Layer, bouka2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-chikukei2024',
        label: '地区計画(2024)',
        sources: [chikukei2024Source],
        layers: [chikukei2024Layer, chikukei2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tochiku2024',
        label: '土地区画整理事業(2024)',
        sources: [tochiku2024Source],
        layers: [tochiku2024Layer, tochiku2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tkbt2024',
        label: '特別用途地区(2024)',
        sources: [tkbt2024Source],
        layers: [tkbt2024Layer, tkbt2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-koudori2024',
        label: '高度利用地区(2024)',
        sources: [koudori2024Source],
        layers: [koudori2024Layer, koudori2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-koudoti2024',
        label: '高度地区(2024)',
        sources: [koudoti2024Source],
        layers: [koudoti2024Layer, koudoti2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-fuuchichiku2024',
        label: '風致地区(2024)',
        sources: [fuuchichiku2024Source],
        layers: [fuuchichiku2024Layer, fuuchichiku2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokuteiyouto2024',
        label: '特定用途制限地域(2024)',
        sources: [tokuteiyouto2024Source],
        layers: [tokuteiyouto2024Layer, tokuteiyouto2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-jyuntoshi2024',
        label: '準都市計画区域(2024)',
        sources: [jyuntoshi2024Source],
        layers: [jyuntoshi2024Layer, jyuntoshi2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-toshisaisei2024',
        label: '都市再生特別地区(2024)',
        sources: [toshisaisei2024Source],
        layers: [toshisaisei2024Layer, toshisaisei2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokuryoku2024',
        label: '特別緑地保全地区(2024)',
        sources: [tokuryoku2024Source],
        layers: [tokuryoku2024Layer, tokuryoku2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-rekifuu2024',
        label: '歴史的風土保存地区(2024)',
        sources: [rekifuu2024Source],
        layers: [rekifuu2024Layer, rekifuu2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokuteiyuudou2024',
        label: '特定用途誘導地区(2024)',
        sources: [tokuteiyuudou2024Source],
        layers: [tokuteiyuudou2024Layer, tokuteiyuudou2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-soubou2024',
        label: '航空機騒音障害防止地区(2024)',
        sources: [soubou2024Source],
        layers: [soubou2024Layer, soubou2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokuteibouka2024',
        label: '特定防災街区整備地区(2024)',
        sources: [tokuteibouka2024Source],
        layers: [tokuteibouka2024Layer, tokuteibouka2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-tokureiyouseki2024',
        label: '特例容積率適用地区(2024)',
        sources: [tokureiyouseki2024Source],
        layers: [tokureiyouseki2024Layer, tokureiyouseki2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-ryokukachiiki2024',
        label: '緑化地域(2024)',
        sources: [ryokukachiiki2024Source],
        layers: [ryokukachiiki2024Layer, ryokukachiiki2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-kyojyuchosei2024',
        label: '居住調整地域(2024)',
        sources: [kyojyuchosei2024Source],
        layers: [kyojyuchosei2024Layer, kyojyuchosei2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-kousoujyukyo2024',
        label: '高層住居誘導地区(2024)',
        sources: [kousoujyukyo2024Source],
        layers: [kousoujyukyo2024Layer, kousoujyukyo2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      {
        id: 'oh-fukkousaiseikyoten2024',
        label: '復興再生拠点(2024)',
        sources: [fukkousaiseikyoten2024Source],
        layers: [fukkousaiseikyoten2024Layer, fukkousaiseikyoten2024LayerLine],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ（2024）</a>',
        credit: '都市計画決定GISデータ（2024）',
      },
      /* === 旧データ（コメントアウト）===
      {
        id: 'oh-tochikukaku',
        label: 'R07土地区画整理事業',
        sources: [tochikukakuseiriSource],
        layers: [tochikukakuseiriLayer, tochikukakuseiriLineLayer],
        attribution:
          '<a href="https://www.mlit.go.jp/toshi/tosiko/toshi_tosiko_tk_000087.html" target="_blank">都市計画決定GISデータ</a>',
        credit: '都市計画決定GISデータ',
      },
      {
        id: 'oh-yotochiikiP',
        label: '用途地域(旧)',
        source: yotochiikiPSource,
        layers: [yotochiikiPLayer, yotochiikiPLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tokei',
        label: '都市計画区域(旧)',
        source: tokeiSource,
        layers: [tokeiLayer, tokeiLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-kuikikubun',
        label: '市街化調整区域(旧)',
        source: kuikikubunSource,
        layers: [kuikikubunLayer, kuikikubunLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tkbt',
        label: '特別用途地区(旧)',
        source: tkbtSource,
        layers: [tkbtLayer, tkbtLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A55-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      */
    ],
  },
  {
    id: 'mapillary',
    label: 'mapillary',
    nodes: [
      {
        id: 'oh-mapillary',
        label: '️mapillary①-基本',
        sources: [mapillarySource],
        layers: [mapillarySequences, mapillaryImages],
        attribution: 'メニューからmapillaryをオンにしてください。<br>© Mapillary',
        credit: '© Mapillary',
      },
      {
        id: 'oh-mapillary-2',
        label: 'mapillary②-オブジェクト',
        sources: [mapillarySource2],
        layers: [mapillaryLabels2, mapillaryIcon2],
        attribution: '© Mapillary',
        credit: '© Mapillary',
      },
      {
        id: 'oh-mapillary-3',
        label: 'mapillary③-交通標識',
        sources: [mapillarySource3],
        layers: [mapillaryImages3, mapillaryIcon3],
        attribution: '© Mapillary',
        credit: '© Mapillary',
      },
    ],
  },
  {
    id: 'suikei-jinkou-folder',
    label: '将来推計人口',
    nodes: [
      // {
      //   id: 'oh-reinfolib-jinkou-suikei',
      //   label: '将来推計人口250mメッシュ（不動産情報ライブラリ版）',
      //   source: reinfolibJinkouSuikeiSource,
      //   layers: [reinfolibJinkouSuikeiLayer, reinfolibJinkouSuikeiLayerLine],
      //   ext: { name: 'extSuikeiSlider' },
      //   attribution:
      //     '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual/" target="_blank">不動産情報ライブラリ</a>' +
      //     ' 国土数値情報 将来推計人口（250mメッシュ）',
      // },
      {
        id: 'oh-suikei-r6-250m',
        label: '将来推計人口250mメッシュ（R6）',
        source: suikeiR6_250mSource,
        layers: [suikeiR6_250mLayer, suikeiR6_250mLayerLine],
        ext: { name: 'extSuikeiSlider', fillLayerId: 'oh-suikei-r6-250m-layer', ageGroups: 'r6' },
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh250r6.html" target="_blank">国土数値情報</a>' +
          ' 250mメッシュ別将来推計人口（R6国政局推計）',
        credit: '国土数値情報  +  250mメッシュ別将来推計人口（R6国政局推計）',
      },
      {
        id: 'oh-suikei-r6-1km',
        label: '将来推計人口1kmメッシュ（R6）',
        source: suikeiR6_1kmSource,
        layers: [suikeiR6_1kmLayer, suikeiR6_1kmLayerLine],
        ext: { name: 'extSuikeiSlider', fillLayerId: 'oh-suikei-r6-1km-layer', ageGroups: 'r6' },
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000h30.html" target="_blank">国土数値情報</a>' +
          ' 1kmメッシュ別将来推計人口（R6国政局推計）',
        credit: '国土数値情報  +  1kmメッシュ別将来推計人口（R6国政局推計）',
      },
    ],
  },
  {
    id: 'tokei',
    label: '統計',
    nodes: [
      {
        id: 'oh-school-choropleth',
        label: '📚都道府県主題図（授業用）',
        sources: [schoolChoroplethSource],
        layers: [schoolChoroplethFillLayer, schoolChoroplethLineLayer, schoolChoroplethLabelLayer, schoolChoroplethExtrusionLayer],
        ext: { name: 'extSchoolChoropleth' },
        attribution:
          '<a href="https://dashboard.e-stat.go.jp/" target="_blank">統計ダッシュボード</a> / <a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-v3_1.html" target="_blank">国土数値情報（行政区域）</a>',
        credit: '統計ダッシュボード / 国土数値情報（行政区域）',
      },
      {
        id: 'oh-choropleth',
        label: '📊統計コロプレスマップ',
        sources: [cityGeojsonSource],
        layers: [choroplethFillLayer, choroplethLineLayer, choroplethLabelLayer],
        ext: { name: 'extChoropleth' },
        attribution:
          '<a href="https://dashboard.e-stat.go.jp/" target="_blank">統計ダッシュボード</a> / <a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-v3_1.html" target="_blank">国土数値情報（行政区域）</a>',
        credit: '統計ダッシュボード / 国土数値情報（行政区域）',
      },
      {
        id: 'oh-income-choropleth',
        label: '課税所得（市区町村別）',
        sources: [cityGeojsonSource],
        layers: [incomeChoroplethFillLayer, incomeChoroplethLineLayer, incomeChoroplethLabelLayer],
        ext: { name: 'extIncomeChoropleth' },
        attribution:
          '<a href="https://dashboard.e-stat.go.jp/" target="_blank">統計ダッシュボード</a>（市町村税課税状況等の調）/ <a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-v3_1.html" target="_blank">国土数値情報（行政区域）</a>',
        credit: '統計ダッシュボード （市町村税課税状況等の調）/ 国土数値情報（行政区域）',
      },
      {
        id: 'oh-keizai-census',
        label: '2021経済センサス500mメッシュ',
        source: keizaiCensusSource,
        layers: [keizaiCensusLayer, keizaiCensusLineLayer, keizaiCensusLabelLayer],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200553" target="_blank">総務省・経済産業省「令和3年経済センサス-活動調査」</a>',
        credit: '出典： 総務省・経済産業省「令和3年経済センサス-活動調査」',
        ext: { name: 'extKeizaiCensus' },
      },
      {
        id: 'oh-jinko-ido',
        label: '2020就業・通学・交通手段500mメッシュ',
        source: jinkoIdoSource,
        layers: [jinkoIdoLayer, jinkoIdoLineLayer, jinkoIdoLabelLayer],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521" target="_blank">総務省「令和2年国勢調査」</a>',
        credit: '出典： 総務省「令和2年国勢調査」',
        ext: { name: 'extJinkoIdo' },
      },
      {
        id: 'oh-jutaku-mesh',
        label: '2020住宅の所有・建て方500mメッシュ',
        source: jutakuMeshSource,
        layers: [jutakuMeshLayer, jutakuMeshLineLayer, jutakuMeshLabelLayer],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521" target="_blank">総務省「令和2年国勢調査」</a>',
        credit: '出典： 総務省「令和2年国勢調査」',
        ext: { name: 'extJutakuMesh' },
      },
      {
        id: 'oh-setai-mesh',
        label: '2020世帯の種類・世帯人員250mメッシュ',
        source: setaiMeshSource,
        layers: [setaiMeshLayer, setaiMeshLineLayer, setaiMeshLabelLayer],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521" target="_blank">総務省「令和2年国勢調査」</a>',
        credit: '出典： 総務省「令和2年国勢調査」',
        ext: { name: 'extSetaiMesh' },
      },
      {
        id: 'oh-uriage-mesh',
        label: '2021経済センサス売上・規模1kmメッシュ',
        source: uriageMeshSource,
        layers: [uriageMeshLayer, uriageMeshLineLayer, uriageMeshLabelLayer],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200553" target="_blank">総務省・経済産業省「令和3年経済センサス-活動調査」</a>',
        credit: '出典： 総務省・経済産業省「令和3年経済センサス-活動調査」',
        ext: { name: 'extUriageMesh' },
      },
      // {
      //   id: 'oh-price-heatmap',
      //   label: '📈不動産価格ヒートマップ（取引/成約）',
      //   sources: [],
      //   layers: [],
      //   attribution: '<a href="https://www.reinfolib.mlit.go.jp/help/apiManual" target="_blank">不動産情報ライブラリ</a>',
      //   ext: { name: 'ext-price-heatmap' },
      // },
      {
        id: 'oh-syochiiki',
        label: '2020国勢調査小地域人口ピラミッド',
        source: syochiikiSource,
        layers: [syochiikiLayer, syochiikLayerLine, syochiikiLayerLabel],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/stat-search/files?page=1&toukei=00200521&tstat=000001136464&cycle=0&tclass1=000001136472" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
        ext: { name: 'extSyochiiki' },
      },
      {
        id: 'oh-population-pyramid',
        label: '人口ピラミッド（市区町村別）',
        source: populationPyramidSource,
        layers: [
          populationPyramidPolygonLayer,
          populationPyramidHighlightLayer,
          populationPyramidLineLayer,
          populationPyramidLabelLayer,
        ],
        attribution:
          '<a href="https://hanishina.net/maps/historydata.html" target="_blank">hanishina.net</a> / <a href="https://dashboard.e-stat.go.jp/" target="_blank">統計ダッシュボード</a>',
        credit: 'hanishina.net / 統計ダッシュボード',
      },
      {
        id: 'oh-syochiiki-2',
        label: '2020国勢調査小地域人口密度3D',
        source: syochiikiSource,
        layers: [syochiikiLayer, syochiikiLayerHeight],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/stat-search/files?page=1&toukei=00200521&tstat=000001136464&cycle=0&tclass1=000001136472" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
      },
      // 100mメッシュは特殊データのため一時的に非表示
      // {
      //   id: 'oh-m100m',
      //   label: '100mメッシュ人口(2020)',
      //   source: m100mSource,
      //   layers: [m100mLayer, m100mLayerLine, m100mLayerLabel],
      //   attribution: '出典：<a href="https://gtfs-gis.jp/teikyo/" target="_blank">地域分析に有用なデータの提供</a>',
      // },
      // {
      //   id: 'oh-m100m-3d',
      //   label: '100mメッシュ人口3D(2020)',
      //   source: m100mSource,
      //   layers: [m100mLayer, m100mLayerLine, m100mLayerLabel, m100mLayerHeight],
      //   attribution: '出典：<a href="https://gtfs-gis.jp/teikyo/" target="_blank">地域分析に有用なデータの提供</a>',
      // },
      {
        id: 'oh-m125m',
        label: '125mメッシュ人口(2020)',
        source: m125mSource,
        layers: [m125mLayer, m125mLayerLine, m125mLayerLabel],
        ext: { name: 'extMeshPopulation', values: ['125m'] },
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=E&serveyId=E002005112020&statsId=T001225&datum=2000&by_prefecture_flg=1" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
      },
      {
        id: 'oh-m125m-3d',
        label: '125mメッシュ人口3D(2020)',
        source: m125mSource,
        layers: [m125mLayer, m125mLayerLine, m125mLayerLabel, m125mLayerHeight],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=E&serveyId=E002005112020&statsId=T001225&datum=2000&by_prefecture_flg=1" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
      },
      {
        id: 'oh-m125m-aging',
        label: '125mメッシュ高齢化率(2020)',
        source: m125mSource,
        layers: [m125mAgingLayer, m125mLayerLine, m125mAgingLayerLabel],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=E&serveyId=E002005112020&statsId=T001225&datum=2000&by_prefecture_flg=1" target="_blank">e-Stat</a>' +
          '<div class="legend-scale"><ul class="legend-labels">' +
          '<li><span style="background:#2166ac;"></span>〜15%</li>' +
          '<li><span style="background:#67a9cf;"></span>〜20%</li>' +
          '<li><span style="background:#d1e5f0;"></span>〜25%</li>' +
          '<li><span style="background:#fddbc7;"></span>〜30%</li>' +
          '<li><span style="background:#ef8a62;"></span>〜35%</li>' +
          '<li><span style="background:#b2182b;"></span>40%〜</li>' +
          '</ul></div>',
        credit: '出典： e-Stat  +   +  〜15%  +  〜20%  +  〜25%  +  〜30%  +  〜35%  +  40%〜  +',
      },
      {
        id: 'oh-m125m-aging-3d',
        label: '125mメッシュ高齢化率3D(2020)',
        source: m125mSource,
        layers: [m125mAgingLayer, m125mLayerLine, m125mAgingLayerLabel, m125mAgingLayerHeight],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=E&serveyId=E002005112020&statsId=T001225&datum=2000&by_prefecture_flg=1" target="_blank">e-Stat</a>' +
          '<div class="legend-scale"><ul class="legend-labels">' +
          '<li><span style="background:#2166ac;"></span>〜15%</li>' +
          '<li><span style="background:#67a9cf;"></span>〜20%</li>' +
          '<li><span style="background:#d1e5f0;"></span>〜25%</li>' +
          '<li><span style="background:#fddbc7;"></span>〜30%</li>' +
          '<li><span style="background:#ef8a62;"></span>〜35%</li>' +
          '<li><span style="background:#b2182b;"></span>40%〜</li>' +
          '</ul></div>',
        credit: '出典： e-Stat  +   +  〜15%  +  〜20%  +  〜25%  +  〜30%  +  〜35%  +  40%〜  +',
      },
      {
        id: 'oh-m250m',
        label: '250mメッシュ人口(2020)',
        source: m250mSource,
        layers: [m250mLayer, m250mLayerLine, m250mLayerLabel],
        ext: { name: 'extMeshPopulation', values: ['250m'] },
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=Q&serveyId=Q002005112020&statsId=T001142" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
      },
      {
        id: 'oh-m250m-3d',
        label: '250mメッシュ人口3D(2020)',
        source: m250mSource,
        layers: [m250mLayer, m250mLayerLine, m250mLayerLabel, m250mLayerHeight],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=1&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=Q&serveyId=Q002005112020&statsId=T001142" target="_blank">e-Stat</a>',
        credit: '出典： e-Stat',
      },
      {
        id: 'oh-m500m',
        label: '500mメッシュ人口(2020)',
        source: m500mSource,
        layers: [m500mLayer, m500mLayerLine, m500mLayerLabel],
        ext: { name: 'extMeshPopulation', values: ['500m'] },
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=8&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=H&serveyId=H002005112020&statsId=T001141&datum=2011" target="_blank">e-Stsat</a>',
        credit: '出典： e-Stsat',
      },
      {
        id: 'oh-m500m-3d',
        label: '500mメッシュ人口3D(2020)',
        source: m500mSource,
        layers: [m500mLayer, m500mLayerLine, m500mLayerLabel, m500mLayerHeight],
        attribution:
          '出典：<a href="https://www.e-stat.go.jp/gis/statmap-search?page=8&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=H&serveyId=H002005112020&statsId=T001141&datum=2011" target="_blank">e-Stsat</a>',
        credit: '出典： e-Stsat',
      },
      {
        id: 'oh-m1km',
        label: '1kmメッシュ人口(2020)',
        source: m1kmSource,
        layers: [m1kmLayer, m1kmLayerLine, m1kmLayerLabel],
        ext: { name: 'extMeshPopulation', values: ['1km'] },
        attribution:
          '<a href="https://www.e-stat.go.jp/gis/statmap-search?page=8&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=S&serveyId=S002005112020&statsId=T001100&prefCode=01%2C02%2C03%2C04%2C05%2C06%2C07%2C08%2C09%2C10%2C11%2C12%2C13%2C14%2C15%2C16%2C17%2C18%2C19%2C20%2C21%2C22%2C23%2C24%2C25%2C26%2C27%2C28%2C29%2C30%2C31%2C32%2C33%2C34%2C35%2C36%2C37%2C38%2C39%2C40%2C41%2C42%2C43%2C44%2C45%2C46%2C47&datum=2000" target="_blank">e-Stat</a>',
        credit: 'e-Stat',
      },
      {
        id: 'oh-m1km-3d',
        label: '1kmメッシュ人口3D(2020)',
        source: m1kmSource,
        layers: [m1kmLayer, m1kmLayerLine, m1kmLayerLabel, m1kmLayerHeight],
        attribution:
          '<a href="https://www.e-stat.go.jp/gis/statmap-search?page=8&type=1&toukeiCode=00200521&toukeiYear=2020&aggregateUnit=S&serveyId=S002005112020&statsId=T001100&prefCode=01%2C02%2C03%2C04%2C05%2C06%2C07%2C08%2C09%2C10%2C11%2C12%2C13%2C14%2C15%2C16%2C17%2C18%2C19%2C20%2C21%2C22%2C23%2C24%2C25%2C26%2C27%2C28%2C29%2C30%2C31%2C32%2C33%2C34%2C35%2C36%2C37%2C38%2C39%2C40%2C41%2C42%2C43%2C44%2C45%2C46%2C47&datum=2000" target="_blank">e-Stat</a>',
        credit: 'e-Stat',
      },
      {
        id: 'oh-chiriin250m',
        label: '地理院版250mメッシュ人口(2020)',
        source: chiriin250mSource,
        layers: [chiriin250mLayer, chiriin250mLayerLine],
        attribution:
          '<a href="" target="_blank"></a>' +
          '<img width="200px" src="' +
          require('@/assets/legend/kokusei_population.png') +
          '">',
        credit: '+',
        info: true,
      },
      {
        id: 'oh-chiriin250m-3D',
        label: '地理院版250mメッシュ人口3D(2020)',
        source: chiriin250mSource,
        layers: [chiriin250mLayerHeight],
        attribution:
          '<a href="" target="_blank"></a>' +
          '<img width="200px" src="' +
          require('@/assets/legend/kokusei_population.png') +
          '">',
        credit: '+',
        info: true,
      },
      {
        id: 'oh-r02suikei250m',
        label: 'R02-250mメッシュ別将来推計人口',
        source: r02suikei250mSource,
        layers: [r02suikei250mLayer, r02suikei250mLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh250r6.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加  +   +',
        info: true,
      },
      {
        id: 'oh-r02suikei500m',
        label: 'R02-500mメッシュ別将来推計人口',
        source: r02suikei500mSource,
        layers: [r02suikei500mLayer, r02suikei500mLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh500r6.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加  +   +',
        info: true,
      },
      {
        id: 'oh-r02suikei1km',
        label: 'R02-1kmメッシュ別将来推計人口',
        source: r02suikei1kmSource,
        layers: [r02suikei1kmLayer, r02suikei1kmLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000r6.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加  +   +',
        info: true,
      },
      {
        id: 'oh-suikei500m',
        label: '500mメッシュ別将来推計人口',
        source: suikei500mSource,
        layers: [suikei500mLayer, suikei500mLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh500h30.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加  +   +',
        info: true,
      },
      {
        id: 'oh-suikei1km',
        label: '1kmメッシュ別将来推計人口',
        source: suikei1kmSource,
        layers: [suikei1kmLayer, suikei1kmLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000h30.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
          '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
          '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
          '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
          '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
          '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  無居住化  +  50%以上減少  +  30%以上50%未満減少  +  0%以上30%未満減少  +  増加  +  さらに増加  +   +',
        info: true,
      },
      // {
      //     id: 'oh-suikei1km-full',
      //     label: "1kmメッシュ別将来推計人口（フル）",
      //     source: suikei1kmFullSource,
      //     layers: [suikei1kmFullLayer,suikei1kmFullLayerLine],
      //     attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-mesh1000h30.html" target="_blank">国土数値情報</a>' +
      //         '<div class="legend-scale">' +
      //         '<ul class="legend-labels">' +
      //         '<li><span style="background:rgba(196, 253, 187, 0.8);"></span>無居住化</li>' +
      //         '<li><span style="background:rgba(140, 252, 114, 0.8);"></span>50%以上減少</li>' +
      //         '<li><span style="background:rgba(97, 197, 250, 0.8);"></span>30%以上50%未満減少</li>' +
      //         '<li><span style="background:rgba(89, 119, 246, 0.8);"></span>0%以上30%未満減少</li>' +
      //         '<li><span style="background:rgba(184, 38, 25, 0.8);"></span>増加</li>' +
      //         '<li><span style="background:rgba(255, 0, 0, 0.8);"></span>さらに増加</li>' +
      //         '</ul>' +
      //         '</div>',
      //     info: true
      // },
      {
        id: 'oh-did',
        label: 'DID人口集中地区',
        source: didSource,
        layers: [didLayer, didLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-did-timeline',
        label: 'DID人口集中地区（時系列）',
        source: didTimelineSource,
        layers: didTimelineLayers,
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A16-v2_3.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extDidTimeline' },
      },
      {
        id: 'oh-heatmap',
        label: '📊閲覧エリアヒートマップ',
        sources: [],
        layers: [],
        attribution: 'OH3 閲覧統計データ',
        credit: 'OH3 閲覧統計データ',
        ext: { name: 'ext-heatmap' },
      },
    ],
  },
  {
    id: 'doro',
    label: '鉄道、道路等',
    nodes: [
      {
        id: 'oh-q-kyoryo',
        label: '全国橋梁地図',
        source: qKyouryoSource,
        layers: [qKyoryoLayer, qKyoryoLayerLabel],
      },
      {
        id: 'oh-q-tunnel',
        label: '全国トンネル地図',
        source: qTunnelSource,
        layers: [qTunnelLayer, qTunnelLayerLabel],
      },
      {
        id: 'oh-bus',
        label: 'R04バスルートと停留所',
        sources: [busSource, busteiSource],
        layers: [busLayer, busteiLayer, busteiLayerLabel],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N07-v2_0.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
        ext: { name: 'extBus', parameters: [] },
      },
      {
        id: 'oh-tetsudo',
        label: '鉄道（廃線は赤色）',
        source: tetsudoSource,
        layers: [tetsudoLayerRed, tetsudoLayerBlue, tetsudoLayerPointRed, tetsudoLayerPointBlue, tetsudoLayerLabel],
        ext: { name: 'extTetsudo', parameters: [] },
      },
      {
        id: 'oh-tetsudojikeiretsu',
        label: '鉄道時系列',
        source: tetsudojikeiretsuSource,
        layers: [tetsudojikeiretsuLayerBlue, tetsudojikeiretsuLayerPoint],
        ext: { name: 'extTetsudojikeiretsu', parameters: [] },
      },
      {
        id: 'oh-ekibetsukyaku',
        label: '駅別乗降客数',
        sources: [ekibetsukyakuSource],
        layers: [ekibetsukyakuLine, ekibetsukyakuLabel],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
      },
      {
        id: 'oh-ekibetsukyaku-3d',
        label: '駅別乗降客数３D',
        sources: [ekibetsukyakuSource3d, tetsudo2Source],
        layers: [tetsudoLine, ekibetsukyakuHeight],
        ext: { name: 'extKyakusu' },
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-S12-2022.html' target='_blank'>国土数値情報</a>" +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:hsl(240, 100%, 50%);"></span>0 - 99人</li>' +
          '<li><span style="background:hsl(230, 100%, 55%);"></span>100 - 299人</li>' +
          '<li><span style="background:hsl(220, 100%, 60%);"></span>300 - 499人</li>' +
          '<li><span style="background:hsl(210, 100%, 65%);"></span>500 - 999人</li>' +
          '<li><span style="background:hsl(200, 100%, 70%);"></span>1000 - 4999人</li>' +
          '<li><span style="background:hsl(180, 100%, 50%);"></span>5000 - 19999人</li>' +
          '<li><span style="background:hsl(150, 100%, 50%);"></span>20000 - 99999人</li>' +
          '<li><span style="background:hsl(120, 100%, 50%);"></span>100000 - 499999人</li>' +
          '<li><span style="background:hsl(60, 100%, 50%);"></span>500000 - 1199999人</li>' +
          '<li><span style="background:hsl(0, 100%, 50%);"></span>1200000人+</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報 " +   +   +  0 - 99人  +  100 - 299人  +  300 - 499人  +  500 - 999人  +  1000 - 4999人  +  5000 - 19999人  +  20000 - 99999人  +  100000 - 499999人  +  500000 - 1199999人  +  1200000人+  +   +',
        // info:true
      },
      {
        id: 'oh-highway',
        label: '高速道路時系列',
        source: highwaySource,
        layers: [highwayLayerGreen, highwayLayerRed],
        ext: { name: 'extHighway', parameters: [] },
      },
      {
        id: 'oh-kokuarea',
        label: '空港等の周辺空域',
        source: kokuareaSource,
        layers: [kokuareaLayer, kokuareaLayerLine],
      },
      {
        id: 'oh-michinoeki',
        label: '道の駅',
        source: michinoekiSource,
        layers: [michinoekiLayer, michinoekiLayerLabel],
      },
    ],
  },
  {
    id: 'skosodate',
    label: '子育て',
    nodes: [
      {
        id: 'oh-school-child',
        label: '学校データ',
        source: schoolSource,
        layers: [schoolLayer, schoolLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P29-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extSchool' },
      },
      {
        id: 'oh-yochien',
        label: '幼稚園・保育園等（R0２）',
        source: yochienSource,
        layers: [yochienLayer, yochienLayerLabel],
      },
      {
        id: 'oh-syogakkoR05',
        label: '小学校（R05）',
        source: syogakkoR05Source,
        layers: [syogakkoR05Layer, syogakkoR05LayerLine, syogakkoR05LayerLabel, syogakkoR05LayerPoint],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A27-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-cyugakuR05',
        label: '中学校（R05）',
        source: cyugakuR05Source,
        layers: [cyugakuR05Layer, cyugakuR05LayerLine, cyugakuR05LayerLabel, cyugakuR05LayerPoint],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A32-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-iryokikan',
        label: '医療機関（R02）',
        source: iryokikanSource,
        layers: [iryokikanLayer, iryokikanLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P04-2020.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
    ],
  },
  {
    id: 'coloring-map',
    label: '塗り絵マップ',
    nodes: [
      {
        id: 'oh-pref-coloring',
        label: '都道府県版',
        source: prefColoringSource,
        layers: [prefColoringFillLayer, prefColoringLineLayer, prefColoringLabelLayer],
        ext: { name: 'extColoring' },
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-v3_1.html" target="_blank">国土数値情報（行政区域）</a>',
        credit: '国土数値情報（行政区域）',
      },
      {
        id: 'oh-city-coloring',
        label: '市区町村版',
        sources: [cityGeojsonSource],
        layers: [cityColoringFillLayer, cityColoringLineLayer, cityColoringLabelLayer],
        ext: { name: 'extColoring' },
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-v3_1.html" target="_blank">国土数値情報（行政区域）</a>',
        credit: '国土数値情報（行政区域）',
      },
    ],
  },
  {
    id: 'city',
    label: '市町村',
    nodes: [
      {
        id: 'oh-gyosei-history',
        label: '行政界履歴（1920〜）',
        source: gyoseiHistorySource,
        layers: [gyoseiHistoryLayerQuery, gyoseiHistoryLayerPolygon, gyoseiHistoryLayerLine, gyoseiHistoryLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2025.html" target="_blank">国土数値情報</a> / <a href="https://hanishina.net/maps/historydata.html" target="_blank">hanishina.net</a>',
        credit: '国土数値情報 / hanishina.net',
        ext: { name: 'ext-gyosei-history' },
      },
      {
        id: 'oh-city-gun',
        label: '明治中期の郡',
        source: cityGunSource,
        layers: [cityGunLayer, cityGunLayerLine, cityGunLayerLabel],
        attribution: '<a href="https://booth.pm/ja/items/3053727" target="_blank">郡地図研究会</a>',
        credit: '郡地図研究会',
        ext: { name: 'ext-city-gun' },
      },
      {
        id: 'oh-city-t09',
        label: 'T09市町村',
        source: cityT09Source,
        layers: [cityT09Layer, cityT09LayerLine, cityT09LayerLabel],
        ext: { name: 'ext-city-t09' },
      },
      {
        id: 'oh-city-s25',
        label: 'S25市町村',
        source: cityS25Source,
        layers: [cityS25Layer, cityS25LayerLine, cityS25LayerLabel],
        ext: { name: 'ext-city-s25' },
      },
      {
        id: 'oh-city-h12',
        label: 'H12市町村',
        source: cityH12Source,
        layers: [cityH12Layer, cityH12LayerLine, cityH12LayerLabel],
        ext: { name: 'ext-city-h12' },
      },
      {
        id: 'oh-city-r05',
        label: 'R05市町村',
        source: cityR05Source,
        layers: [cityR05Layer, cityR05LayerLine, cityR05LayerLabel],
        ext: { name: 'ext-city-r05' },
      },
    ],
  },
  {
    id: 'kanko',
    label: '観光',
    nodes: [
      {
        id: 'oh-kanko',
        label: '観光資源',
        sources: [kankoSource],
        layers: [kankoLayer, kankoLayerLine, kankoLine, kankoPoint],
        attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P12-2014.html">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-syukuhaku',
        label: '宿泊容量メッシュ1km',
        sources: [syukuhakuSource],
        layers: [syukuhakuLayer, syukuhakuLayerLine],
        attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P09.html">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-syukuhaku',
        label: '宿泊容量メッシュ1km3D',
        sources: [syukuhakuSource],
        layers: [syukuhakuLayerHeight],
        attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P09.html">国土数値情報</a>',
        credit: '国土数値情報',
      },
    ],
  },
  {
    id: 'bakumatsu',
    label: '幕末期近世の村',
    description:
      '幕末期の約7万の村を表示。村域・石高（生産力）・藩の割り当てを可視化。6種の色分けモード（標準・藩別・旧国別・都道府県別・郡別・石高別）、石高を3D高さで表現する機能あり。相給（複数藩の重複支配）関係も表示。国文学研究資料館のデータをGIS化。GitHub: yaoyue00085856氏作成',
    nodes: [
      {
        id: 'oh-bakumatsu',
        label: '幕末期近世の村',
        source: bakumatsuSource,
        layers: [bakumatsuLayer, bakumatsuLayerLine, bakumatsuLayerLabel],
        attribution:
          '出典：<a href="https://github.com/yaoyue00085856/Kyudaka_agrivillage" target="_blank">幕末期近世村領域データ</a>',
        credit: '出典： 幕末期近世村領域データ',
        ext: { name: 'extBakumatsu' },
      },
      {
        id: 'oh-bakumatsu-kokudaka-height',
        label: '幕末期近世の村（石高/面積）3D',
        source: bakumatsuSource,
        layers: [bakumatsuLayerHeight],
        attribution:
          '出典：<a href="https://github.com/yaoyue00085856/Kyudaka_agrivillage" target="_blank">幕末期近世村領域データ</a>',
        credit: '出典： 幕末期近世村領域データ',
        ext: { name: 'extBakumatsu3d' },
      },
      {
        id: 'oh-bakumatsu-poin',
        label: '幕末期近世の村（ポイント）',
        source: bakumatsuPointSource,
        layers: [bakumatsuPointLayer],
      },
    ],
  },
  // {
  //   id: 'koaza',
  //   label: '関東小字地図',
  //   description:
  //     '消えゆく小字（こあざ）地名を地図上に復元。江戸〜明治期の小字は地形・歴史・土地利用を反映した名前で、土地の記憶を伝える。東京・神奈川など関東圏をカバー。小字名・旧村名・郡名で検索可能。@koaza_net氏作成',
  //   nodes: [
  //     {
  //       id: 'oh-koaza',
  //       label: '関東小字地図',
  //       sources: [koazaSource, muraSource, muraCenterSource],
  //       layers: [muraCenterLabel, koazaLayer, koazaLayerLine, koazaLayerLabel, muraLayer, muraLayerLine],
  //       attribution: '<a href="https://koaza.net/" target="_blank">関東小字地図</a>',
  //       credit: '関東小字地図',
  //       ext: { name: 'extKoaza' },
  //     },
  //   ],
  // },
  {
    id: 'blend-series',
    label: '🔀合成シリーズ',
    nodes: [
      {
        id: 'oh-blend-photo-hillshade',
        label: '🔀航空写真×陰影',
        sources: [blendBasePhotoSource, blendPhotoHillshadeSource],
        layers: [blendBasePhotoLayer, blendPhotoHillshadeLayer],
        attribution: '国土地理院 全国最新写真（シームレス）・陰影起伏図',
        credit: '国土地理院 全国最新写真（シームレス）・陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-pale-hillshade',
        label: '🔀淡色地図×陰影',
        sources: [blendBasePaleSource, blendPaleHillshadeSource],
        layers: [blendBasePaleLayer, blendPaleHillshadeLayer],
        attribution: '国土地理院 淡色地図・陰影起伏図',
        credit: '国土地理院 淡色地図・陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-std-hillshade',
        label: '🔀標準地図×陰影',
        sources: [blendBaseStdSource, blendStdHillshadeSource],
        layers: [blendBaseStdLayer, blendStdHillshadeLayer],
        attribution: '国土地理院 標準地図・陰影起伏図',
        credit: '国土地理院 標準地図・陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-pale-hillshade-relief',
        label: '🔀淡色×陰影×標高',
        source: blendPaleHillshadeReliefSource,
        layers: [blendPaleHillshadeReliefLayer],
        attribution: '国土地理院 淡色地図・陰影起伏図・色別標高図',
        credit: '国土地理院 淡色地図・陰影起伏図・色別標高図',
      },
      {
        id: 'oh-blend-hillshade-relief',
        label: '🔀陰影×標高',
        source: blendHillshadeReliefSource,
        layers: [blendHillshadeReliefLayer],
        attribution: '国土地理院 陰影起伏図・色別標高図',
        credit: '国土地理院 陰影起伏図・色別標高図',
      },
      {
        id: 'oh-blend-tochiriyo-hillshade',
        label: '🔀土地利用×陰影',
        sources: [blendBaseTochiriyoSource, blendTochiriyoHillshadeSource],
        layers: [blendBaseTochiriyoLayer, blendTochiriyoHillshadeLayer],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報 土地利用細分メッシュ</a>・国土地理院 陰影起伏図",
        credit: '国土数値情報 土地利用細分メッシュ ・国土地理院 陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-geology-hillshade',
        label: '🔀地質図×陰影',
        sources: [blendBaseGeologySource, blendGeologyHillshadeSource],
        layers: [blendBaseGeologyLayer, blendGeologyHillshadeLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/" target="_blank">20万分の1日本シームレス地質図</a>・国土地理院 陰影起伏図',
        credit: '20万分の1日本シームレス地質図 ・国土地理院 陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-mw5-hillshade',
        label: '🔀旧版地形図×陰影',
        sources: [blendBaseMw5Source, blendMw5HillshadeSource, blendMw5CenterSource],
        layers: [blendBaseMw5Layer, blendMw5HillshadeLayer, blendMw5CenterLabel],
        attribution: '戦前旧版地形図（５万分の1）・国土地理院 陰影起伏図',
        credit: '戦前旧版地形図（５万分の1）・国土地理院 陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-dojo-hillshade',
        label: '🔀土壌図×陰影',
        sources: [blendBaseDojoSource, blendDojoHillshadeSource],
        layers: [blendBaseDojoLayer, blendDojoHillshadeLayer],
        attribution:
          '<a href="https://soil-inventory.rad.naro.go.jp/" target="_blank">農研機構 日本土壌インベントリー</a>・国土地理院 陰影起伏図',
        credit: '農研機構 日本土壌インベントリー ・国土地理院 陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-ecoris67-hillshade',
        label: '🔀第6-7回植生図×陰影',
        sources: [blendBaseEcoris67Source, blendEcoris67HillshadeSource],
        layers: [blendBaseEcoris67Layer, blendEcoris67HillshadeLayer],
        attribution: "<a href='https://map.ecoris.info/' target='_blank'>エコリス地図タイル</a>・国土地理院 陰影起伏図",
        credit: 'エコリス地図タイル・国土地理院 陰影起伏図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-mw5-photo',
        label: '🔀旧版地形図×航空写真',
        sources: [blendBaseMw5Source2, blendMw5PhotoSource, blendMw5CenterSource],
        layers: [blendBaseMw5Layer2, blendMw5PhotoLayer, blendMw5CenterLabel],
        attribution: '戦前旧版地形図（５万分の1）・国土地理院 全国最新写真（シームレス）',
        credit: '戦前旧版地形図（５万分の1）・国土地理院 全国最新写真（シームレス）',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-mw5-tochiriyo',
        label: '🔀旧版地形図×土地利用',
        sources: [blendBaseMw5Source3, blendMw5TochiriyoSource, blendMw5CenterSource, blendMw5TochiriyoVecSource],
        layers: [blendBaseMw5Layer3, blendMw5TochiriyoLayer, blendMw5CenterLabel, blendMw5TochiriyoVecLayer],
        attribution:
          "戦前旧版地形図（５万分の1）・<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報 土地利用細分メッシュ</a>",
        credit: '戦前旧版地形図（５万分の1）・ 国土数値情報 土地利用細分メッシュ',
        ext: { name: 'extTochiriyo' },
      },
      {
        id: 'oh-blend-mw5-relief',
        label: '🔀旧版地形図×色別標高図',
        sources: [blendBaseMw5Source4, blendMw5ReliefSource, blendMw5CenterSource],
        layers: [blendBaseMw5Layer4, blendMw5ReliefLayer, blendMw5CenterLabel],
        attribution: '戦前旧版地形図（５万分の1）・国土地理院 色別標高図',
        credit: '戦前旧版地形図（５万分の1）・国土地理院 色別標高図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-photo-relief',
        label: '🔀航空写真×色別標高図',
        sources: [blendBasePhotoSource2, blendPhotoReliefSource],
        layers: [blendBasePhotoLayer2, blendPhotoReliefLayer],
        attribution: '国土地理院 全国最新写真（シームレス）・色別標高図',
        credit: '国土地理院 全国最新写真（シームレス）・色別標高図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-photo-geology',
        label: '🔀航空写真×地質図',
        sources: [blendBasePhotoSource3, blendPhotoGeologySource],
        layers: [blendBasePhotoLayer3, blendPhotoGeologyLayer],
        attribution:
          '国土地理院 全国最新写真（シームレス）・<a href="https://gbank.gsj.jp/seamless/" target="_blank">20万分の1日本シームレス地質図</a>',
        credit: '国土地理院 全国最新写真（シームレス）・ 20万分の1日本シームレス地質図',
        ext: { name: 'extBlendHillshade' },
      },
      {
        id: 'oh-blend-relief-hillshade-geology',
        label: '🔀色別標高図×陰影×地質図',
        source: blendReliefHillshadeGeologySource,
        layers: [blendReliefHillshadeGeologyLayer],
        attribution:
          '国土地理院 色別標高図・陰影起伏図・<a href="https://gbank.gsj.jp/seamless/" target="_blank">20万分の1日本シームレス地質図</a>',
        credit: '国土地理院 色別標高図・陰影起伏図・ 20万分の1日本シームレス地質図',
      },
    ],
  },
  {
    id: 2,
    label: '自然、立体図等',
    nodes: [
      {
        id: 'cs2',
        label: 'CS立体図',
        nodes: [
          {
            id: 'oh-cs-all2',
            label: 'CS立体図全部',
            sources: csSources,
            layers: csLayersSelected,
          },
          ...csLayers2,
        ],
      },
      {
        id: 'sekisyoku',
        label: '赤色立体地図',
        nodes: [
          {
            id: 'oh-sekisyoku-layer-all',
            label: '赤色立体地図全部',
            sources: [
              toyamaSekisyokuSource,
              kanagawakenyokohamaSekisyokuSource,
              kanagawakenseibuSekisyokuSource,
              sagamiharaSekisyokuSource,
              kanagawaSekisyokuSource,
              tamaSekisyokuSource,
              tokyo23SekisyokuSource,
              tosyo01SekisyokuSource,
              tosyo02SekisyokuSource,
              tosyo03SekisyokuSource,
              tosyo04SekisyokuSource,
              tosyo05SekisyokuSource,
              tosyo06SekisyokuSource,
              kochiSekisyokuSource,
            ],
            layers: [
              toyamaSekisyokuLayer,
              kanagawakenyokohamaSekisyokuLayer,
              kanagawakenseibuSekisyokuLayer,
              sagamiharaSekisyokuLayer,
              kanagawaSekisyokuLayer,
              tamaSekisyokuLayer,
              tokyo23SekisyokuLayer,
              tosyo02SekisyokuLayer,
              tosyo03SekisyokuLayer,
              tosyo04SekisyokuLayer,
              tosyo05SekisyokuLayer,
              tosyo06SekisyokuLayer,
              tosyo01SekisyokuLayer,
              kochiSekisyokuLayer,
            ],
          },
          {
            id: 'oh-kanagawa-sekisyoku-layer',
            label: '神奈川県県央部県赤色立体地図',
            source: kanagawaSekisyokuSource,
            layers: [kanagawaSekisyokuLayer],
            bounds: boundsSort([139.09766500655184, 35.624094342992194, 139.46201634577034, 35.315414497020726]),
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/kanagawa-2020-1-pointcloud" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          {
            id: 'oh-sagamihara-sekisyoku-layer',
            label: '神奈川県相模原市赤色立体地図',
            source: sagamiharaSekisyokuSource,
            layers: [sagamiharaSekisyokuLayer],
            bounds: boundsSort([139.04848994843064, 35.68660597218647, 139.34747849259207, 35.468439520826834]),
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/kanagawa-2020-2-pointcloud" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          {
            id: 'oh-yokophama-sekisyoku-layer',
            label: '神奈川県横浜南部赤色立体地図',
            source: kanagawakenyokohamaSekisyokuSource,
            layers: [kanagawakenyokohamaSekisyokuLayer],
            bounds: boundsSort([139.1460674051824, 35.47387830775729, 139.77576139218593, 35.09494646110994]),
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/kanagawa-2021-pointcloud" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          {
            id: 'oh-kanagawakenseibu-sekisyoku-layer',
            label: '神奈川県西部赤色立体地図',
            source: kanagawakenseibuSekisyokuSource,
            layers: [kanagawakenseibuSekisyokuLayer],
            bounds: boundsSort([138.9251684590787, 35.52398114822313, 139.25907181711648, 35.10840903088935]),
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/kanagawa-2019-pointcloud" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          {
            id: 'oh-tosyo-sekisyoku-layer',
            label: '東京都赤色立体地図',
            sources: [
              tamaSekisyokuSource,
              tokyo23SekisyokuSource,
              tosyo01SekisyokuSource,
              tosyo02SekisyokuSource,
              tosyo03SekisyokuSource,
              tosyo04SekisyokuSource,
              tosyo05SekisyokuSource,
              tosyo06SekisyokuSource,
            ],
            layers: [
              tamaSekisyokuLayer,
              tokyo23SekisyokuLayer,
              tosyo02SekisyokuLayer,
              tosyo03SekisyokuLayer,
              tosyo04SekisyokuLayer,
              tosyo05SekisyokuLayer,
              tosyo06SekisyokuLayer,
              tosyo01SekisyokuLayer,
            ],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/tokyopc-23ku-2024" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          // {
          //     id: 'oh-aichiSekisyokuLayer',
          //     label: "愛知県赤色立体地図",
          //     source: aichiSekisyokuSource,
          //     layers: [aichiSekisyokuLayer]
          // },
          {
            id: 'oh-kochi-sekisyoku-layer',
            label: '高知県赤色立体地図',
            sources: [kochiSekisyokuSource],
            layers: [kochiSekisyokuLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/sekisyoku" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
          {
            id: 'oh-toyama-sekisyoku-layer',
            label: '富山県赤色立体地図',
            sources: [toyamaSekisyokuSource],
            layers: [toyamaSekisyokuLayer],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/rrimap" target="_blank">G空間情報センター</a>',
            credit: 'G空間情報センター',
          },
        ],
      },
      // {
      //   id: 'oh-mpi-rrim-1mdem',
      //   label: '1mDEM赤色立体地図',
      //   nodes: [
      //     {
      //       id: 'oh-rrim-zenkoku',
      //       label: '全国',
      //       sources: [],
      //       layers: [],
      //       attribution:
      //         '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/gsi_dem1m_all_v3.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-mpi-rrim-1mdem-shikoku',
      //       label: '四国',
      //       sources: [],
      //       layers: [],
      //       attribution:
      //         '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/shikoku_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-mpi-rrim-1mdem-kyushu',
      //       label: '九州・沖縄',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/kyushu_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-mpi-rrim-1mdem-chugoku',
      //       label: '中国',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/chugoku_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-mpi-rrim-1mdem-kinki',
      //       label: '近畿',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/kinki_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-rrim-chubu',
      //       label: '中部',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/chubu_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-rrim-hokkaido',
      //       label: '北海道',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/hokkaido_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-rrim-tohoku',
      //       label: '東北',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/tohoku_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-rrim-kanto',
      //       label: '関東',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/kanto_dem1m.pmtiles',
      //       },
      //     },
      //     {
      //       id: 'oh-rrim-hokuriku',
      //       label: '北陸',
      //       sources: [],
      //       layers: [],
      //       attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //       rrim: {
      //         pmtilesUrl: 'https://kenzkenz.net/dem/1m/hokuriku_dem1m.pmtiles',
      //       },
      //     },
      //   ],
      // },
      // {
      //   id: 'oh-rrim-simple-zenkoku',
      //   label: '1mDEM赤色立体地図',
      //   sources: [],
      //   layers: [],
      //   attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
      //   credit: '基盤地図情報ダウンロードサービス',
      //   ext: { name: 'ext-rrim-simple', pmtilesUrl: 'https://kenzkenz.net/dem/1m/gsi_dem1m_all_v3.pmtiles' },
      // },
      {
        id: 'oh-cs-raster-zenkoku',
        label: '1mDEM CS立体図',
        source: {
          id: 'oh-cs-raster-zenkoku-source',
          obj: {
            type: 'raster',
            tiles: [
              `superres://{z}/{x}/{y}.webp?pmtiles=${encodeURIComponent('https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/dem1m/cs.pmtiles')}&maxzoom=17`,
            ],
            tileSize: 256,
            maxzoom: 19,
          },
        },
        layers: [
          {
            id: 'oh-cs-raster-zenkoku-layer',
            type: 'raster',
            source: 'oh-cs-raster-zenkoku-source',
          },
        ],
        attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
        credit: '基盤地図情報ダウンロードサービス',
      },
      {
        id: 'oh-rrim-raster-zenkoku',
        label: '1mDEM赤色立体地図ラスター',
        source: {
          id: 'oh-rrim-raster-zenkoku-source',
          obj: {
            type: 'raster',
            tiles: [
              `superres://{z}/{x}/{y}.webp?pmtiles=${encodeURIComponent('https://pub-4f328e8270694eb48f7294c7fa63d628.r2.dev/pmtiles/dem1m/v18.pmtiles')}&maxzoom=17`,
            ],
            tileSize: 256,
            maxzoom: 19,
          },
        },
        layers: [
          {
            id: 'oh-rrim-raster-zenkoku-layer',
            type: 'raster',
            source: 'oh-rrim-raster-zenkoku-source',
          },
        ],
        attribution: '<a href="https://service.gsi.go.jp/kiban/" target="_blank">基盤地図情報ダウンロードサービス</a>',
        credit: '基盤地図情報ダウンロードサービス',
      },
      {
        id: 'kako-no-chikei-2',
        label: '過去の地形',
        nodes: [
          {
            id: 'oh-kako-ku6910y-rrim-2',
            label: '1969福岡市赤色立体地図',
            source: {
              id: 'oh-kako-ku6910y-rrim-2-source',
              obj: {
                type: 'raster',
                url: 'pmtiles://https://kenzkenz.net/dem/kako/KU6910Y_rrim_z15.pmtiles',
                tileSize: 256,
                maxzoom: 15,
              },
            },
            layers: [
              {
                id: 'oh-kako-ku6910y-rrim-2-layer',
                type: 'raster',
                source: 'oh-kako-ku6910y-rrim-2-source',
              },
            ],
            bounds: boundsSort([131.378, 32.068, 131.847, 33.085]),
            attribution: '国土地理院 過去の地形データ',
            credit: '国土地理院 過去の地形データ',
          },
        ],
      },
      {
        id: 'fg-bitikei',
        label: '微地形表現図マップタイル',
        description:
          '森林情報オープンデータ（https://forestgeo.info/）提供。@forestgeoinfo氏作成。※遺跡立体図（永惠氏）とは別プロジェクト。各県のCS立体図・赤色立体地図・陰影起伏図などの高精度地形データをタイル化して配信',
        nodes: [
          {
            id: 'oh-fg-miyagi-cs',
            label: '宮城県CS立体図',
            source: fgMiyagiCsSource,
            layers: [fgMiyagiCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-miyagi-topo',
            label: '宮城県地形強調図',
            source: fgMiyagiTopoSource,
            layers: [fgMiyagiTopoLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-tochigi-cs',
            label: '栃木県CS立体図',
            source: fgTochigiCsSource,
            layers: [fgTochigiCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-saitama-cs',
            label: '埼玉県CS立体図',
            source: fgSaitamaCsSource,
            layers: [fgSaitamaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-tokyo-cs',
            label: '東京都CS立体図（多摩）',
            source: fgTokyoCsSource,
            layers: [fgTokyoCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-tokyo-inyouzu',
            label: '東京都陰陽図（多摩）',
            source: fgTokyoInyouzuSource,
            layers: [fgTokyoInyouzuLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-kanagawa-cs',
            label: '神奈川県CS立体図',
            source: fgKanagawaCsSource,
            layers: [fgKanagawaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-kanagawa-rrim',
            label: '神奈川県赤色立体地図',
            source: fgKanagawaRrimSource,
            layers: [fgKanagawaRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-niigata-cs',
            label: '新潟県CS立体図（長岡）',
            source: fgNiigataCsSource,
            layers: [fgNiigataCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-niigata-inyouzu',
            label: '新潟県陰陽図（長岡）',
            source: fgNiigataInyouzuSource,
            layers: [fgNiigataInyouzuLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-toyama-rrim',
            label: '富山県赤色立体地図',
            source: fgToyamaRrimSource,
            layers: [fgToyamaRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-ishikawa-cs',
            label: '石川県CS立体図（能登）',
            source: fgIshikawaCsSource,
            layers: [fgIshikawaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-ishikawa-rrim',
            label: '石川県赤色立体地図（能登）',
            source: fgIshikawaRrimSource,
            layers: [fgIshikawaRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-ishikawa-inyouzu',
            label: '石川県陰陽図（能登）',
            source: fgIshikawaInyouzuSource,
            layers: [fgIshikawaInyouzuLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-yamanashi-cs',
            label: '山梨県CS立体図',
            source: fgYamanashiCsSource,
            layers: [fgYamanashiCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-yamanashi-inyouzu',
            label: '山梨県陰陽図',
            source: fgYamanashiInyouzuSource,
            layers: [fgYamanashiInyouzuLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-nagano-cs',
            label: '長野県CS立体図',
            source: fgNaganoCsSource,
            layers: [fgNaganoCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-gifu-cs',
            label: '岐阜県CS立体図',
            source: fgGifuCsSource,
            layers: [fgGifuCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-shizuoka-cs',
            label: '静岡県CS立体図',
            source: fgShizuokaCsSource,
            layers: [fgShizuokaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-shiga-cs',
            label: '滋賀県CS立体図',
            source: fgShigaCsSource,
            layers: [fgShigaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-kyoto-rrim',
            label: '京都府赤色立体地図',
            source: fgKyotoRrimSource,
            layers: [fgKyotoRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-osaka-cs',
            label: '大阪府CS立体図',
            source: fgOsakaCsSource,
            layers: [fgOsakaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-hyogo-cs',
            label: '兵庫県CS立体図',
            source: fgHyogoCsSource,
            layers: [fgHyogoCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-tottori-cs',
            label: '鳥取県CS立体図',
            source: fgTottoriCsSource,
            layers: [fgTottoriCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-okayama-cs',
            label: '岡山県CS立体図',
            source: fgOkayamaCsSource,
            layers: [fgOkayamaCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-ehime-cs',
            label: '愛媛県CS立体図',
            source: fgEhimeCsSource,
            layers: [fgEhimeCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-ehime-rrim',
            label: '愛媛県赤色立体地図',
            source: fgEhimeRrimSource,
            layers: [fgEhimeRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-kochi-cs',
            label: '高知県CS立体図',
            source: fgKochiCsSource,
            layers: [fgKochiCsLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
          {
            id: 'oh-fg-nagasaki-rrim',
            label: '長崎県赤色立体地図/地形起伏図',
            source: fgNagasakiRrimSource,
            layers: [fgNagasakiRrimLayer],
            attribution: '<a href="https://forestgeo.info/" target="_blank">森林情報オープンデータ</a> CC-BY 4.0',
            credit: '森林情報オープンデータ CC-BY 4.0',
          },
        ],
      },
      {
        id: 'geoland-gifu',
        label: 'ジオランドぎふ',
        nodes: [
          {
            id: 'oh-wms-gifu-detail',
            label: '詳細地質図',
            source: wmsGifuSource,
            layers: [wmsGifuLayer],
            attribution: '<a href="https://geo-gifu.org/geoland/" target="_blank">ジオランドぎふ</a>',
            credit: 'ジオランドぎふ',
          },
          {
            id: 'oh-wms-gifu-fault',
            label: '断層',
            source: wmsGifuFaultSource,
            layers: [wmsGifuFaultLayer],
            attribution: '<a href="https://geo-gifu.org/geoland/" target="_blank">ジオランドぎふ</a>',
            credit: 'ジオランドぎふ',
          },
          {
            id: 'oh-wms-gifu-active-fault',
            label: '活断層',
            source: wmsGifuActiveFaultSource,
            layers: [wmsGifuActiveFaultLayer],
            attribution: '<a href="https://geo-gifu.org/geoland/" target="_blank">ジオランドぎふ</a>',
            credit: 'ジオランドぎふ',
          },
        ],
      },
      {
        id: 'oh-tokyoGreenPmtiles',
        label: '東京都緑のオープンデータ',
        source: tokyoGreenPmtilesSource,
        layers: [tokyoGreenPmtilesLayer],
        ext: { name: 'extGreen' },
        popup: {
          layers: ['oh-tokyoGreenPmtiles'],
          template: `
            <div style="max-width: 300px;">
              <h4 style="margin: 0 0 8px 0; color: #228B22;">
                {{#名称}}{{名称}}{{/名称}}
                {{^名称}}{{#公園名}}{{公園名}}{{/公園名}}{{/名称}}
                {{^名称}}{{^公園名}}緑地{{/公園名}}{{/名称}}
              </h4>
              <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
                {{_category_label}}
              </div>
              {{#区市町村}}<div><b>区市町村:</b> {{区市町村}}</div>{{/区市町村}}
              {{#位置}}<div><b>位置:</b> {{位置}}</div>{{/位置}}
              {{#面積m2}}<div><b>面積:</b> {{面積m2}}㎡</div>{{/面積m2}}
              {{#開園年月日}}<div><b>開園:</b> {{開園年月日}}</div>{{/開園年月日}}
              {{#設置等根拠}}<div><b>根拠:</b> {{設置等根拠}}</div>{{/設置等根拠}}
              {{#所管}}<div><b>所管:</b> {{所管}}</div>{{/所管}}
              {{#備考}}<div style="font-size: 11px; color: #888;"><b>備考:</b> {{備考}}</div>{{/備考}}
            </div>
          `,
        },
      },
      {
        id: 'oh-elevation3-mesh',
        label: '標高・傾斜度3次メッシュ',
        source: elevation3MeshSource,
        layers: [elevation3MeshFill, elevation3MeshLine, elevation3MeshLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-a.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-elevation3-mesh-3d',
        label: '標高・傾斜度3次メッシュ3D',
        source: elevation3MeshSource,
        layers: [elevation3MeshFill, elevation3MeshHeight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-a.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-elevation-mesh',
        label: '標高・傾斜度4次メッシュ',
        source: elevationMeshSource,
        layers: [elevationMeshFill, elevationMeshLine, elevationMeshLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-c.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-elevation-mesh-3d',
        label: '標高・傾斜度4次メッシュ3D',
        source: elevationMeshSource,
        layers: [elevationMeshFill, elevationMeshHeight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G04-c.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-sea-level',
        label: '海面上昇シミュレーション',
        sources: [seaLevelSource],
        layers: [seaLevellLayer],
        attribution:
          "<a href='https://maps.gsi.go.jp/development/ichiran.html#dem' target='_blank'>国土地理院 標高タイル</a>",
        credit: '国土地理院 標高タイル',
        ext: { name: 'extSeaLevel' },
      },
      {
        id: 'oh-hypsometric',
        label: '海陸段彩図',
        sources: [gsjTerrainSource],
        layers: [hypsometricLayer],
        attribution: '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extHypsometric' },
      },
      {
        id: 'oh-gebco-seafloor',
        label: 'GEBCO海底地形',
        sources: [gebcoTerrainSource],
        layers: [gebcoSeafloorLayer],
        attribution: '<a href="https://tiles.gsj.jp/tiles/elev2/#h_gebco" target="_blank">GEBCO Grid</a>',
        credit: 'GEBCO Grid',
      },
      {
        id: 'oh-gebco-land',
        label: 'GEBCO陸地形',
        sources: [gebcoTerrainSource],
        layers: [gebcoLandLayer],
        attribution: '<a href="https://tiles.gsj.jp/tiles/elev2/#h_gebco" target="_blank">GEBCO Grid</a>',
        credit: 'GEBCO Grid',
      },
      {
        id: 'oh-gebco-landsea',
        label: 'GEBCO陸海地形',
        sources: [gebcoTerrainSource],
        layers: [gebcoLandSeaLayer],
        attribution: '<a href="https://tiles.gsj.jp/tiles/elev2/#h_gebco" target="_blank">GEBCO Grid</a>',
        credit: 'GEBCO Grid',
      },
      {
        id: 'oh-terrain22',
        label: '地形分類図',
        sources: [terrain22_41Source],
        layers: [terrain22_41Layer, terrain22_41LayerLine],
        attribution: '<a href="https://gisstar.gsi.go.jp/terrain2021/" target="_blank">地形分類データ</a>',
        credit: '地形分類データ',
      },
      // 世界の赤色立体地図（一時無効化。復活時はコメント解除）
      // {
      //   id: 'oh-mpi-rrim',
      //   label: '世界の赤色立体地図',
      //   sources: [],
      //   layers: [],
      //   attribution:
      //     '<a href="https://tiles.gsj.jp/tiles/elev/tiles.html#h_land" target="_blank">産総研 シームレス標高タイルサービス（陸域統合DEM）</a>',
      //   rrim: {},
      // },

      // {
      //     id: 'cd',
      //     label: "CS立体図",
      //     nodes: [
      //         {
      //             id: 'oh-cs-all',
      //             label: "CS立体図全部",
      //             sources: [csNotoSource,csTochigiSource,csNaganoSource,csGifuSource,csOsakaSource,csHyogoSource,csShizuokaSource,
      //                 csHiroshimaSource,csOkayamaSource,csFukushimaSource,csEhimeSource,csKochiSource,csKumamotoSource,csKanagawaSource,
      //                 tokyo23CsSource],
      //
      //             layers: [csNotoLayer,csTochigiLayer,csNaganoLayer,csGifuLayer,csOsakaLayer,csHyogoLayer,csShizuokaLayer,
      //                 csHiroshimaLayer,csOkayamaLayer,csFukushimaLayer,csEhimeLayer,csKochiLayer,csKumamotoLayer,csKanagawaLayer,
      //                 tokyo23CsLayer]
      //         },
      //         {
      //             id: 'oh-csNotoLayer',
      //             label: "能登CS立体図",
      //             source: csNotoSource,
      //             layers: [csNotoLayer]
      //         },
      //         {
      //             id: 'oh-cs-tochigi-layer',
      //             label: "栃木県CS立体図",
      //             source: csTochigiSource,
      //             layers: [csTochigiLayer],
      //             attribution:'<a href="https://www.geospatial.jp/ckan/dataset/csmap_tochigi" target="_blank">G空間情報センター</a>'
      //         },
      //         {
      //             id: 'oh-cs-nagano-layer',
      //             label: "長野県CS立体図",
      //             source: csNaganoSource,
      //             layers: [csNaganoLayer],
      //             attribution:'<a href="https://www.geospatial.jp/ckan/dataset/nagano-csmap" target="_blank">G空間情報センター</a>'
      //         },
      //         {
      //             id: 'oh-csGifuLayer',
      //             label: "岐阜県CS立体図",
      //             source: csGifuSource,
      //             layers: [csGifuLayer]
      //         },
      //         {
      //             id: 'oh-csOsakaLayer',
      //             label: "大阪府CS立体図",
      //             source: csOsakaSource,
      //             layers: [csOsakaLayer]
      //         },
      //         {
      //             id: 'oh-csHyogoLayer',
      //             label: "兵庫県CS立体図",
      //             source: csHyogoSource,
      //             layers: [csHyogoLayer]
      //         },
      //         {
      //             id: 'oh-csShizuokaLayer',
      //             label: "静岡県CS立体図",
      //             source: csShizuokaSource,
      //             layers: [csShizuokaLayer]
      //         },
      //         {
      //             id: 'oh-cs-hiroshima-layer',
      //             label: "広島県CS立体図",
      //             source: csHiroshimaSource,
      //             layers: [csHiroshimaLayer],
      //             attribution:'<a href="https://github.com/shi-works/aist-dem-with-cs-on-maplibre-gl-js" target="_blank">aist-dem-with-cs-on-maplibre-gl-js</a>'
      //         },
      //         {
      //             id: 'oh-cs-okayama-layer',
      //             label: "岡山県CS立体図",
      //             source: csOkayamaSource,
      //             layers: [csOkayamaLayer],
      //             attribution:'<a href="https://www2.ffpri.go.jp/soilmap/index.html" target="_blank">森林総研・森林土壌デジタルマップ</a>'
      //         },
      //         {
      //             id: 'oh-cs-fukushima-layer',
      //             label: "福島県CS立体図",
      //             source: csFukushimaSource,
      //             layers: [csFukushimaLayer],
      //             attribution:'<a href="https://www2.ffpri.go.jp/soilmap/index.html" target="_blank">森林総研・森林土壌デジタルマップ</a>'
      //         },
      //         {
      //             id: 'oh-cs-ehime-layer',
      //             label: "愛媛県CS立体図",
      //             source: csEhimeSource,
      //             layers: [csEhimeLayer],
      //             attribution:'<a href="https://www2.ffpri.go.jp/soilmap/index.html" target="_blank">森林総研・森林土壌デジタルマップ</a>'
      //         },
      //         {
      //             id: 'oh-cs-kochi-layer',
      //             label: "高知県CS立体図",
      //             source: csKochiSource,
      //             layers: [csKochiLayer],
      //             attribution:'<a href="https://www.geospatial.jp/ckan/dataset/csmap_kochi" target="_blank">G空間情報センター</a>'
      //         },
      //         {
      //             id: 'oh-cs-kumamoto-layer',
      //             label: "熊本県・大分県CS立体図",
      //             source: csKumamotoSource,
      //             layers: [csKumamotoLayer],
      //             attribution:'<a href="https://www2.ffpri.go.jp/soilmap/index.html" target="_blank">森林総研・森林土壌デジタルマップ</a>'
      //         },
      //         {
      //             id: 'oh-cs-kanagawa-layer',
      //             label: "神奈川県CS立体図",
      //             source: csKanagawaSource,
      //             layers: [csKanagawaLayer]
      //         },
      //         {
      //             id: 'oh-tokyo23-cs-layer',
      //             label: "東京都23区CS立体図",
      //             source: tokyo23CsSource,
      //             layers: [tokyo23CsLayer]
      //         },
      //         {
      //             id: 'oh-cs-yokohama-layer',
      //             label: "横浜北部、川崎CS立体図",
      //             source: csYokohamSource,
      //             layers: [csYokohamaLayer]
      //         },
      //     ]
      // },
      {
        id: 'oh-shitchi',
        label: '第6-7回植生図',
        source: ecoris67Source,
        layers: [ecoris67Layer],
        attribution:
          "<a href='https://map.ecoris.info/' target='_blank'>エコリス地図タイル</a>" +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:#fdf1ce;"></span>高山帯自然植生</li>' +
          '<li><span style="background:#997f60;"></span>コケモモートウヒクラス域 自然植生</li>' +
          '<li><span style="background:#a58f74;"></span>〃 代償植生</li>' +
          '<li><span style="background:#178017;"></span>ブナクラス域 自然植生</li>' +
          '<li><span style="background:#5b9700;"></span>〃 代償植生</li>' +
          '<li><span style="background:#003300;"></span>ヤブツバキクラス域 自然植生</li>' +
          '<li><span style="background:#004a00;"></span>〃 代償植生</li>' +
          '<li><span style="background:#ffff00;"></span>河川・湿原・沼沢地・砂丘植生</li>' +
          '<li><span style="background:#697720;"></span>植林地</li>' +
          '<li><span style="background:#8cd27d;"></span>耕作地</li>' +
          '<li><span style="background:#868585;"></span>市街地</li>' +
          '<li><span style="background:#99ffff;"></span>開放水域</li>' +
          '<li><span style="background:#cccc20;"></span>竹林</li>' +
          '<li><span style="background:#69ff00;"></span>牧草地・ゴルフ場・芝地</li>' +
          '<li><span style="background:#999662;"></span>水田以外の耕作地</li>' +
          '</ul>' +
          '</div>',
        credit:
          'エコリス地図タイル " +   +   +  高山帯自然植生  +  コケモモートウヒクラス域 自然植生  +  〃 代償植生  +  ブナクラス域 自然植生  +  〃 代償植生  +  ヤブツバキクラス域 自然植生  +  〃 代償植生  +  河川・湿原・沼沢地・砂丘植生  +  植林地  +  耕作地  +  市街地  +  開放水域  +  竹林  +  牧草地・ゴルフ場・芝地  +  水田以外の耕作地  +   +',
      },
      {
        id: 'oh-shitchi',
        label: '明治期の低湿地',
        source: shitchiSource,
        layers: [shitchiLayer],
      },
      {
        id: 'oh-shikibetsu',
        label: '色別標高図',
        source: shikibetsuSource,
        layers: [shikibetsuLayer],
      },
      {
        id: 'oh-inei',
        label: '陰影起伏図',
        source: ineiSource,
        layers: [ineiLayer],
      },
      {
        id: 'oh-shizen-chikei',
        label: '自然地形（白黒）',
        source: shizenChikeiSource,
        layers: [shizenChikeiLayer],
      },
      {
        id: 'oh-jinko-chikei',
        label: '人工改変地形（白黒）',
        source: jinkoChikeiSource,
        layers: [jinkoChikeiLayer],
      },
      {
        id: 'oh-dojo-inventory',
        label: '日本土壌インベントリー',
        source: dojoSource,
        layers: [dojoLayer],
        attribution:
          '<a href="https://soil-inventory.rad.naro.go.jp/" target="_blank">農研機構 日本土壌インベントリー</a>',
        credit: '農研機構 日本土壌インベントリー',
      },
      // {
      //   id: 'oh-kawadak',
      //   label: '川だけ地形地図',
      //   source: kawadakeSource,
      //   layers: [kawddakeLayer],
      //   attribution:
      //     "日本列島に流れる川と湖沼、土地の起伏です。<br>海部の透過表現はOH3の動的処理によるものです。<br><a href='https://www.gridscapes.net/#AllRiversAllLakesTopography' target='_blank'>川だけ地形地図</a>",
      // },
      // {
      //     id: 'oh-ryuiki',
      //     label: "川と流域地図",
      //     source: ryuikiSource,
      //     layers: [ryuikiLayer],
      //     attribution: "<a href='https://tiles.dammaps.jp/ryuiki/' target='_blank'>川と流域地図</a>",
      // },
      {
        id: 'oh-bunsuirei',
        label: '⭐️河川と分水嶺',
        sources: [bunsuireiSource, kasenSource],
        layers: [bunsuireiLayer, bunsuireiLayerLine, kasenLayer, kasenLayerLabel],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W07.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
        ext: { name: 'extBunsuirei' },
      },
      {
        id: 'oh-kasen',
        label: '河川',
        source: kasenSource,
        layers: [kasenLayer, kasenLayerLabel],
      },
      {
        id: 'oh-ryuiki',
        label: '流域（分水嶺）',
        source: ryuikiSource2,
        layers: [ryuikiLayer2, ryuikiLayerLine2],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W07.html' target='_blank'>国土数値情報</a>",
        credit: '国土数値情報',
      },
      // {
      //     id: 'oh-chikeibunrui',
      //     label: "地形分類",
      //     source: chikeibunruiSource,
      //     layers: [chikeibunruiLayer]
      // },
      // {
      //     id: 'oh-chikeibunrui2',
      //     label: "地形分類テスト",
      //     source: chikeibunruiSource2,
      //     layers: [chikeibunruiLayer2,chikeibunruiLine2]
      // },
      {
        id: 'oh-shizenchikei',
        label: '自然地形',
        source: shizenchikeiSource,
        layers: [shizenchikeiLayer],
        attribution:
          '<a href="https://github.com/gsi-cyberjapan/experimental_landformclassification" target="_blank">国土地理院ベクトルタイル提供実験（地形分類）</a>',
        credit: '国土地理院ベクトルタイル提供実験（地形分類）',
      },
      {
        id: 'oh-jinkochikei',
        label: '人工地形',
        source: jinkochikeiSource,
        layers: [jinkochikeiLayer],
        attribution:
          '<a href="https://github.com/gsi-cyberjapan/experimental_landformclassification" target="_blank">国土地理院ベクトルタイル提供実験（地形分類）</a>',
        credit: '国土地理院ベクトルタイル提供実験（地形分類）',
      },
      {
        id: 'oh-teii',
        label: '低位地帯',
        source: teiiSource,
        layers: [teiiLayer],
        attribution:
          '<div style="width: 200px;"><a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G08-2015.html" target="_blank">国土数値情報</a><br>' +
          '本データは、周辺部よりも標高が低く、排水が困難である地帯（低位地帯）を整備したものである。<br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '"></div>',
        credit:
          '国土数値情報  + 本データは、周辺部よりも標高が低く、排水が困難である地帯（低位地帯）を整備したものである。  +',
        info: true,
      },
      {
        id: 'oh-kosyo',
        label: '湖沼',
        source: kosyoSource,
        layers: [kosyoLayer, kosyoLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W09-2005.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-dam',
        label: 'ダム',
        source: damSource,
        layers: [damLayer, damLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-W01.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-seamless',
        label: 'シームレス地質図',
        source: seamlessSource,
        layers: [seamlessLayer],
        attribution: '<a href="https://gbank.gsj.jp/seamless/" target="_blank">20万分の1日本シームレス地質図</a>',
        credit: '20万分の1日本シームレス地質図',
      },
      {
        id: 'oh-gsj-geology',
        label: '産総研 5万分の1地質図',
        sources: [gsjGeologyDummySource],
        layers: [gsjGeologyDummyLayer],
        attribution: '<a href="https://www.gsj.jp/license/license.html" target="_blank">産総研地質調査総合センター</a>',
        credit: '産総研地質調査総合センター',
        ext: { name: 'ext-gsj-geology' },
      },
      {
        id: 'oh-kaiyochishitsu',
        label: '海底地質図',
        source: kaiyochishitsuource,
        layers: [kaiyochishitsuLayer],
        attribution: "<a href='https://gbank.gsj.jp/geonavi/' target='_blank'>産総研 地質図Navi</a>",
        credit: '産総研 地質図Navi',
      },
      {
        id: 'oh-msil-wrecks',
        label: '沈船',
        source: msilWrecksSource,
        layers: [msilWrecksLayer],
        attribution: "<a href='https://www.msil.go.jp/' target='_blank'>海洋状況表示システム</a>",
        credit: '海洋状況表示システム',
        popup: {
          layers: ['oh-msil-wrecks'],
          template: `
            <div style="max-width: 280px;">
              <h4 style="margin: 0 0 6px 0; color: #e53935;">⚓ 沈船</h4>
              {{#objectid}}<div><b>ID:</b> {{objectid}}</div>{{/objectid}}
              <div style="font-size: 11px; color: #888; margin-top: 4px;">出典: 海洋状況表示システム（電子海図）</div>
            </div>
          `,
        },
      },
      {
        id: 'oh-msil-coral',
        label: '海底底質（さんご）',
        source: msilCoralSource,
        layers: [msilCoralLayer],
        attribution: "<a href='https://www.msil.go.jp/' target='_blank'>海洋状況表示システム</a>",
        credit: '海洋状況表示システム',
        popup: {
          layers: ['oh-msil-coral'],
          template: `
            <div style="max-width: 280px;">
              <h4 style="margin: 0 0 6px 0; color: #ff7043;">🪸 さんご（底質）</h4>
              {{#objectid}}<div><b>ID:</b> {{objectid}}</div>{{/objectid}}
              <div style="font-size: 11px; color: #888; margin-top: 4px;">出典: 海洋状況表示システム（電子海図）</div>
            </div>
          `,
        },
      },
      {
        id: 'oh-msil-mud',
        label: '海底底質（泥・粘土）',
        source: msilMudSource,
        layers: [msilMudLayer],
        attribution: "<a href='https://www.msil.go.jp/' target='_blank'>海洋状況表示システム</a>",
        credit: '海洋状況表示システム',
        popup: {
          layers: ['oh-msil-mud'],
          template: `
            <div style="max-width: 280px;">
              <h4 style="margin: 0 0 6px 0; color: #8d6e63;">🟤 泥・粘土（底質）</h4>
              {{#objectid}}<div><b>ID:</b> {{objectid}}</div>{{/objectid}}
              <div style="font-size: 11px; color: #888; margin-top: 4px;">出典: 海洋状況表示システム（電子海図）</div>
            </div>
          `,
        },
      },
      {
        id: 'oh-kazan',
        label: '火山土地条件図',
        source: kazanSource,
        layers: [kazanLayer],
        attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html#vlcd" target="_blank">地理院タイル</a>',
        credit: '地理院タイル',
      },
      {
        id: 'oh-hifuku',
        label: '植生被覆率',
        source: hifukuSource,
        layers: [hifukuLayer, hifukuLine],
        attribution:
          '<a href="https://zenodo.org/records/5553516" target="_blank">日本全国の町内各地区の植生被覆率</a>',
        credit: '日本全国の町内各地区の植生被覆率',
      },
      {
        id: 'oh-veg2024',
        label: '現存植生図2024',
        sources: [veg2024RasterSource, veg2024Source],
        layers: [veg2024RasterLayer, veg2024FillLayer, veg2024LineLayer],
        ext: { name: 'extVeg2024' },
        attribution:
          '<a href="https://www.geospatial.jp/ckan/dataset/biodic_veg2024" target="_blank">環境省生物多様性センター 現存植生図2024</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:#1a5e28;"></span>自然林</li>' +
          '<li><span style="background:#2e7d3a;"></span>二次林(自然林近)</li>' +
          '<li><span style="background:#4e9450;"></span>二次林</li>' +
          '<li><span style="background:#6aaa5c;"></span>植林地</li>' +
          '<li><span style="background:#7cc464;"></span>二次草原(高)</li>' +
          '<li><span style="background:#a8d88c;"></span>二次草原(低)</li>' +
          '<li><span style="background:#c4d45a;"></span>農耕地(樹園地)</li>' +
          '<li><span style="background:#e8d87c;"></span>農耕地(水田畑)</li>' +
          '<li><span style="background:#b0b0b0;"></span>市街地等</li>' +
          '<li><span style="background:#9b6eb5;"></span>自然草原</li>' +
          '<li><span style="background:#a0845c;"></span>自然裸地</li>' +
          '<li><span style="background:#6ca0d4;"></span>開放水域</li>' +
          '</ul></div>',
        credit:
          '環境省生物多様性センター 現存植生図2024  +   +   +  自然林  +  二次林(自然林近)  +  二次林  +  植林地  +  二次草原(高)  +  二次草原(低)  +  農耕地(樹園地)  +  農耕地(水田畑)  +  市街地等  +  自然草原  +  自然裸地  +  開放水域  +',
      },
      {
        id: 'oh-kuma',
        label: '2019 クマ類全国分布メッシュ',
        source: kumaSource,
        layers: [kumaFillLayer, kumaLineLayer],
        attribution: '<a href="https://geoportal.env.go.jp/" target="_blank">環境ジオポータル</a>',
        credit: '環境ジオポータル',
      },
      {
        id: 'oh-geopark',
        label: 'ジオパーク',
        source: geoparkSource,
        layers: [geoparkLayer, geoparkLine],
        ext: { name: 'extGeopark' },
        attribution:
          '<a href="https://tosashimizu-geo.jp/leaflet/geopark/geoparkarea.html" target="_blank">ジオパークエリア</a><br>' +
          '土井恵治氏　土佐清水ジオパーク推進協議会事務局',
        credit: 'ジオパークエリア  + 土井恵治氏 土佐清水ジオパーク推進協議会事務局',
      },
      {
        id: 'oh-shizenisan',
        label: '世界自然遺産',
        source: shizenisanSource,
        layers: [shizenisanLayer, shizenisanLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A28.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-bunkaisan',
        label: '世界文化遺産',
        source: bunkaisanSource,
        layers: [
          bunkaisanLayerA34a,
          bunkaisanLineA34a,
          bunkaisanLayerA34f,
          bunkaisanLineA34f,
          bunkaisanLineA34c,
          bunkaisanPointA34b,
          bunkaisanPointA34d,
          bunkaisanPointA34e,
          bunkaisanPointA34g,
        ],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A34-2022.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-kokuritsukoen',
        label: '国立公園',
        source: kokuritsukoenSource,
        layers: [kokuritsukoenLayer, kokuritsukoenLine],
        attribution:
          '<a href="http://gis.biodic.go.jp/webgis/sc-026.html?kind=nps" target="_blank">生物多様性センター</a>',
        credit: '生物多様性センター',
      },
      {
        id: 'oh-shizenkoen',
        label: '自然公園',
        source: shizenkoenSource,
        layers: [shizenkoenLayer, shizenkoenLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A10-2015.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-danso',
        label: '都市圏活断層図',
        source: dansoSource,
        layers: [dansoLayer],
        attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html#afm" target="_blank">地理院タイル</a>',
        credit: '地理院タイル',
      },
      {
        id: 'oh-jshis-fault',
        label: '主要活断層帯（J-SHIS）',
        sources: [jshisFaultSource],
        layers: [jshisFaultFillLayer, jshisFaultLineLayer, jshisFaultLabelLayer],
        attribution: '<a href="https://www.j-shis.bosai.go.jp/" target="_blank">J-SHIS 地震ハザードステーション</a>',
        credit: 'J-SHIS 地震ハザードステーション',
        ext: { name: 'extJshisFault' },
      },
      {
        id: 'oh-jma-quake',
        label: '<span style="color:red">NEW </span>地震情報（気象庁）',
        description:
          '気象庁の公式一次ソースから取得する最新地震情報。震源・各観測点の震度（JMA震度階級色）・緊急地震速報（EEW）をリアルタイム表示。60秒ごと自動更新',
        sources: [jmaQuakeEpicenterSource, jmaQuakeStationSource, jmaQuakeEewSource],
        layers: [jmaQuakeStationLayer, jmaQuakeStationLabelLayer, jmaQuakeEewLayer, jmaQuakeEpicenterLayer, jmaQuakeEpicenterLabelLayer],
        attribution: '<a href="https://www.jma.go.jp/bosai/map.html#contents=quake" target="_blank">気象庁</a>',
        credit: '気象庁',
        ext: { name: 'extJmaQuake' },
      },
      // {
      //   id: 'oh-overture-buildings',
      //   label: "<span style='color: red'>NEW</span>Overture Maps 建物",
      //   sources: [overtureBuildsSource],
      //   layers: [overtureBuildsFillLayer, overtureBuildsLineLayer],
      //   attribution: '<a href="https://overturemaps.org/" target="_blank">Overture Maps Foundation</a> (ODbL)',
      // },
      {
        id: 'oh-kokuyurin',
        label: '国有林野データ',
        source: kokuyurinSource,
        layers: [kokuyurinLayer, kokuyurinLayerLine, kokuyurinLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A45.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-shinrin',
        label: '森林地域',
        source: shinrinSource,
        layers: [shinrinLayer, shinrinLayerLine],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A13-2015.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgba(34, 139, 34, 0.7);"></span>森林地域</li>' +
          '<li><span style="background:rgba(0, 100, 0, 0.7);"></span>国有林</li>' +
          '<li><span style="background:rgba(107, 142, 35, 0.7);"></span>地域森林計画対象民有林</li>' +
          '<li><span style="background:rgba(46, 139, 87, 0.7);"></span>保安林</li>' +
          '</ul>' +
          '</div>',
        credit: '国土数値情報  +   +   +  森林地域  +  国有林  +  地域森林計画対象民有林  +  保安林  +   +',
      },
      {
        id: 'oh-shinrinkeikaku-vector',
        label: '森林計画対象森林',
        source: shinrinkeikakuVectorSource,
        layers: [shinrinkeikakuKokuyurinLayer, shinrinkeikakuMinyurinLayer, shinrinkeikakuLabelLayer],
        attribution:
          '<a href="https://www.geospatial.jp/ckan/dataset/layer" target="_blank">林野庁 森林計画対象森林レイヤ</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:#004d00;"></span>国有林</li>' +
          '<li><span style="background:#00e600;"></span>民有林</li>' +
          '</ul>' +
          '</div>',
        credit: '林野庁 森林計画対象森林レイヤ  +   +   +  国有林  +  民有林  +   +',
      },
      {
        id: 'pref-forest-folder',
        label: '県別 森林計画図',
        nodes: [
          {
            id: 'oh-hokkaido-forest',
            label: '北海道 森林計画図',
            description: '北海道の森林計画図。林班・小班の区画を表示',
            source: prefForest.hokkaido.source,
            layers: [prefForest.hokkaido.fill, prefForest.hokkaido.line, prefForest.hokkaido.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/hokkaido-shinrinkeikakuzu" target="_blank">北海道 森林計画図</a> CC BY',
            credit: '北海道 森林計画図 CC BY',
          },
          {
            id: 'oh-iwate-forest',
            label: '岩手県 森林計画図',
            description: '岩手県の森林計画図。林班・小班の区画を表示',
            source: prefForest.iwate.source,
            layers: [prefForest.iwate.fill, prefForest.iwate.line, prefForest.iwate.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/03000-032" target="_blank">岩手県 森林計画図</a> CC BY',
            credit: '岩手県 森林計画図 CC BY',
          },
          {
            id: 'oh-niigata-forest',
            label: '新潟県 森林計画図',
            description: '新潟県の森林計画図。林班・小班の区画を表示',
            source: prefForest.niigata.source,
            layers: [prefForest.niigata.fill, prefForest.niigata.line, prefForest.niigata.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/15000-002" target="_blank">新潟県 森林計画図</a> CC BY',
            credit: '新潟県 森林計画図 CC BY',
          },
          {
            id: 'oh-gunma-forest',
            label: '群馬県 森林計画図',
            description: '群馬県35市町村の森林計画図。林班・小班の区画を表示',
            source: prefForest.gunma.source,
            layers: [prefForest.gunma.fill, prefForest.gunma.line, prefForest.gunma.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/10000-101" target="_blank">群馬県 森林計画図</a> CC BY',
            credit: '群馬県 森林計画図 CC BY',
          },
          {
            id: 'oh-ibaraki-shinrin',
            label: '茨城県森林計画図',
            description: '茨城県内43市町村の森林計画図（林班・小班）。林種による色分け表示',
            source: ibarakiShinrinSource,
            layers: [
              ibarakiShinrinRinpanLayer,
              ibarakiShinrinRinpanLineLayer,
              ibarakiShinrinRinpanLabelLayer,
              ibarakiShinrinShohanLayer,
              ibarakiShinrinShohanLineLayer,
            ],
            attribution:
              '<a href="https://www.ibaraki-opendata.jp/" target="_blank">茨城オープンデータポータル</a>（CC BY 4.0）' +
              '<div class="legend-scale">' +
              '<ul class="legend-labels">' +
              '<li><span style="background:rgba(139, 69, 19, 0.7);"></span>人工林</li>' +
              '<li><span style="background:rgba(34, 139, 34, 0.7);"></span>天然林</li>' +
              '<li><span style="background:rgba(107, 142, 35, 0.7);"></span>竹林</li>' +
              '<li><span style="background:rgba(169, 169, 169, 0.7);"></span>無立木地</li>' +
              '</ul>' +
              '</div>',
            credit:
              '茨城オープンデータポータル （CC BY 4.0） +   +   +  人工林  +  天然林  +  竹林  +  無立木地  +   +',
          },
          {
            id: 'oh-tochigi-forest',
            label: '栃木県 森林計画図',
            description: '栃木県の森林計画図。小班レベルの区画を表示',
            source: prefForest.tochigi.source,
            layers: [prefForest.tochigi.fill, prefForest.tochigi.line, prefForest.tochigi.label],
            attribution:
              '<a href="https://tochigi-dataeye.pref.tochigi.lg.jp/" target="_blank">栃木県 森林計画図</a> CC BY',
            credit: '栃木県 森林計画図 CC BY',
          },
          {
            id: 'oh-nagano-forest',
            label: '長野県 森林計画図',
            description: '長野県の森林計画図。林班・小班の区画を表示',
            source: prefForest.nagano.source,
            layers: [prefForest.nagano.fill, prefForest.nagano.line, prefForest.nagano.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/20000-010" target="_blank">長野県 森林計画図</a> CC BY',
            credit: '長野県 森林計画図 CC BY',
          },
          {
            id: 'oh-shizuoka-forest',
            label: '静岡県 森林計画図',
            description: '静岡県の森林計画図。林班・小班の区画を表示',
            source: prefForest.shizuoka.source,
            layers: [prefForest.shizuoka.fill, prefForest.shizuoka.line, prefForest.shizuoka.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/22000-015" target="_blank">静岡県 森林計画図</a> CC BY',
            credit: '静岡県 森林計画図 CC BY',
          },
          {
            id: 'oh-wakayama-forest',
            label: '和歌山県 森林計画図',
            description: '和歌山県の森林計画図。林班・小班の区画を表示',
            source: prefForest.wakayama.source,
            layers: [prefForest.wakayama.fill, prefForest.wakayama.line, prefForest.wakayama.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/30000-001" target="_blank">和歌山県 森林計画図</a> CC BY',
            credit: '和歌山県 森林計画図 CC BY',
          },
          {
            id: 'oh-ehime-forest',
            label: '愛媛県 森林計画図',
            description: '愛媛県の森林計画図。林班レベルの区画を表示',
            source: prefForest.ehime.source,
            layers: [prefForest.ehime.fill, prefForest.ehime.line, prefForest.ehime.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/38000-007" target="_blank">愛媛県 森林計画図</a> CC BY',
            credit: '愛媛県 森林計画図 CC BY',
          },
          {
            id: 'oh-yamaguchi-forest',
            label: '山口県 森林計画図',
            description: '山口県の森林計画図。林班・小班の区画を表示',
            source: prefForest.yamaguchi.source,
            layers: [prefForest.yamaguchi.fill, prefForest.yamaguchi.line, prefForest.yamaguchi.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/35000-001" target="_blank">山口県 森林計画図</a> CC BY',
            credit: '山口県 森林計画図 CC BY',
          },
          {
            id: 'oh-fukuokakenshinrin',
            label: '福岡県森林計画区域',
            source: fukuokakenshinrinSource,
            layers: [fukuokakenshinrinLayer, fukuokakenshinrinLayerLine],
            attribution:
              '<a href="https://www.pref.fukuoka.lg.jp/contents/forest-opendata.html" target="_blank">ふくおか森林オープンデータ</a>',
            credit: 'ふくおか森林オープンデータ',
          },
          {
            id: 'oh-kumamoto-forest',
            label: '熊本県 森林計画図',
            description: '熊本県の森林計画図。林班・小班の区画を表示',
            source: prefForest.kumamoto.source,
            layers: [prefForest.kumamoto.fill, prefForest.kumamoto.line, prefForest.kumamoto.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/43000-019" target="_blank">熊本県 森林計画図</a> CC BY',
            credit: '熊本県 森林計画図 CC BY',
          },
          {
            id: 'oh-nagasaki-forest',
            label: '長崎県 森林計画図',
            description: '長崎県の森林計画図。林班・小班の区画を表示',
            source: prefForest.nagasaki.source,
            layers: [prefForest.nagasaki.fill, prefForest.nagasaki.line, prefForest.nagasaki.label],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/42000-011" target="_blank">長崎県 森林計画図</a> CC BY',
            credit: '長崎県 森林計画図 CC BY',
          },
        ],
      },
      {
        id: 'oh-fr-mesh-national',
        label: '森林資源メッシュ（20m）',
        description: '全国森林資源メッシュ（20m）。樹種・樹高・立木密度・材積の色分け表示',
        source: frMeshNationalSource,
        sources: [frMeshNationalRasterSource],
        layers: [frMeshNationalRasterLayer, frMeshNationalFillLayer, frMeshNationalLineLayer],
        ext: { name: 'extShinrinMesh' },
        attribution:
          '<a href="https://www.geospatial.jp/ckan/dataset/mesh_tile" target="_blank">林野庁 全国森林資源メッシュ</a>',
        credit: '林野庁 全国森林資源メッシュ',
      },
      // 県別PMTiles（全国版に統合したため無効化）
      // {
      //   id: 'fr-mesh20m-folder',
      //   label: '森林資源メッシュ（20m）',
      //   nodes: [
      //     {
      //       id: 'oh-fr-mesh20m-tochigi',
      //       label: '栃木県森林資源メッシュ（20m）',
      //       source: frMesh20mSources[0],
      //       sources: [frMesh20mRasterSources[0]],
      //       layers: [frMesh20mRasterLayers[0], frMesh20mFillLayers[0], frMesh20mLineLayers[0]],
      //       ext: { name: 'extShinrinMesh' },
      //       attribution:
      //         '<a href="https://www.geospatial.jp/ckan/dataset/fr_mesh20m_tochigi" target="_blank">林野庁 全国森林資源メッシュ</a>',
      //     },
      //     {
      //       id: 'oh-fr-mesh20m-hyogo',
      //       label: '兵庫県森林資源メッシュ（20m）',
      //       source: frMesh20mSources[1],
      //       sources: [frMesh20mRasterSources[1]],
      //       layers: [frMesh20mRasterLayers[1], frMesh20mFillLayers[1], frMesh20mLineLayers[1]],
      //       ext: { name: 'extShinrinMesh' },
      //       attribution:
      //         '<a href="https://www.geospatial.jp/ckan/dataset/fr_mesh20m_hyogo" target="_blank">林野庁 全国森林資源メッシュ</a>',
      //     },
      //     {
      //       id: 'oh-fr-mesh20m-kochi',
      //       label: '高知県森林資源メッシュ（20m）',
      //       source: frMesh20mSources[2],
      //       sources: [frMesh20mRasterSources[2]],
      //       layers: [frMesh20mRasterLayers[2], frMesh20mFillLayers[2], frMesh20mLineLayers[2]],
      //       ext: { name: 'extShinrinMesh' },
      //       attribution:
      //         '<a href="https://www.geospatial.jp/ckan/dataset/fr_mesh20m_kochi" target="_blank">林野庁 全国森林資源メッシュ</a>',
      //     },
      //     {
      //       id: 'oh-fr-mesh20m-tottori',
      //       label: '鳥取県森林資源メッシュ（20m）',
      //       source: frMesh20mSources[3],
      //       sources: [frMesh20mRasterSources[3]],
      //       layers: [frMesh20mRasterLayers[3], frMesh20mFillLayers[3], frMesh20mLineLayers[3]],
      //       ext: { name: 'extShinrinMesh' },
      //       attribution:
      //         '<a href="https://www.geospatial.jp/ckan/dataset/fr_mesh20m_tottori" target="_blank">林野庁 全国森林資源メッシュ</a>',
      //     },
      //   ],
      // },
    ],
  },
  {
    id: 'iseki',
    label: '遺跡等',
    nodes: [
      {
        id: 'oh-kyusekki',
        label: '全国旧石器遺跡',
        source: kyusekkiSource,
        layers: [kyusekkiLayer, kyusekkiLayerHeatmap],
      },
      {
        id: 'oh-bunkazai',
        label: '都道府県指定文化財',
        source: bunkazaiSource,
        layers: [bunkazaiLayer, bunkazaiLabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extBunkazai' },
      },
      // {
      //   id: 'oh-iseki-p32',
      //   label: '都道府県指定文化財（旧）',
      //   source: isekiP32Source,
      //   layers: [isekiP32Layer, isekiP32LayerHeatmap, isekiP32LayerLabel],
      //   attribution:
      //     '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報（都道府県指定文化財データ）</a>',
      // },
      {
        id: 'isekirittaitizu',
        label: '兵庫県遺跡立体地図',
        description:
          '遺跡立体図は高精度DEMを用いた独自の地形表現手法。永惠裕和氏（兵庫県立考古博物館・@zukarohiegana）が作成。重要：赤色立体地図・CS立体図・陰影起伏図とは別物。遺跡立体図の独自手法は「比高（高さの違い）を色の違いで、傾斜の緩急を明暗で表現」する点。陰影や等高線は使わない。城跡の縄張り・堀切・土塁、古墳、寺社跡などの微地形が浮かび上がる',
        nodes: [
          {
            id: 'oh-hyogo-iseki-all',
            label: '兵庫県遺跡立体地図全て',
            sources: [...isekiSources, ...isekiGeojsonSources],
            layers: [...isekiLayers, ...isekiGeojsonLayers, ...isekiGeojsonLabelLayers],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/2021-2022-hyogo-shiseki" target="_blank">兵庫県遺跡立体図</a>',
            credit: '兵庫県遺跡立体図',
          },
          ...isekiLayersMarge,
        ],
      },
      {
        id: 'isekirittaitizu2',
        label: '全国遺跡立体図',
        description:
          '遺跡立体図は高精度DEMを用いた独自の地形表現手法。永惠裕和氏（兵庫県立考古博物館・@zukarohiegana）が作成。重要：赤色立体地図・CS立体図・陰影起伏図とは別物。遺跡立体図の独自手法は「比高（高さの違い）を色の違いで、傾斜の緩急を明暗で表現」する点。陰影や等高線は使わない。城跡の縄張り・堀切・土塁、古墳、寺社跡などの微地形が浮かび上がる。奈良文化財研究所提供',
        nodes: [
          {
            id: 'oh-zenkoku-iseki-all',
            label: '全国遺跡立体図全て',
            sources: [...isekiSources2, ...isekiGeojsonSources2],
            layers: [...isekiLayers22, ...isekiGeojsonLayers2, ...isekiGeojsonLabelLayers2],
            attribution:
              '<a href="https://www.geospatial.jp/ckan/dataset/iseki" target="_blank">全国遺跡立体図（奈良文化財研究所）</a>',
            credit: '全国遺跡立体図（奈良文化財研究所）',
          },
          ...isekiLayers222,
        ],
      },
      // --- OH2から移植: 全国規模の文化財 ---
      {
        id: 'oh-bunkazaidb',
        label: '国指定文化財等データベース',
        source: bunkazaiDbSource,
        layers: [bunkazaiDbLayer, bunkazaiDbLabelLayer],
        attribution:
          '<a href="https://kunishitei.bunka.go.jp/bsys/index" target="_blank">国指定文化財等データベース</a>',
        credit: '国指定文化財等データベース',
        popup: {
          layers: ['oh-bunkazaidb'],
          template: `
            <div style="max-width: 300px;">
              <h4 style="margin: 0 0 8px 0; color: #2E7D32;">{{名称}}</h4>
              {{#種類}}<div><b>種類:</b> {{種類}}</div>{{/種類}}
              {{#都道府県}}<div><b>都道府県:</b> {{都道府県}}</div>{{/都道府県}}
              {{#所在地}}<div><b>所在地:</b> {{所在地}}</div>{{/所在地}}
            </div>`,
        },
      },
      {
        id: 'oh-zenkoku-bunkazai',
        label: '全国の文化財データ（S50）',
        source: zenkokuBunkazaiSource,
        layers: [zenkokuBunkazaiLayer, zenkokuBunkazaiLabelLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gmlold/datalist/gmlold_KsjTmplt-P01.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        popup: {
          layers: ['oh-zenkoku-bunkazai'],
          template: `
            <div style="max-width: 300px;">
              <h4 style="margin: 0 0 8px 0; color: #FF8C00;">{{text}}</h4>
            </div>`,
        },
      },
      {
        id: 'oh-yayoi-iseki',
        label: '全国縄文・弥生集落遺跡',
        source: yayoiIsekiSource,
        layers: [yayoiIsekiLayer, yayoiIsekiLabelLayer],
        popup: {
          layers: ['oh-yayoi-iseki'],
          template: `
            <div style="max-width: 300px;">
              <h4 style="margin: 0 0 8px 0; color: #8B4513;">{{名称}}</h4>
              {{#時代}}<div><b>時代:</b> {{時代}}</div>{{/時代}}
              {{#種類}}<div><b>種類:</b> {{種類}}</div>{{/種類}}
              {{#都道府県}}<div><b>都道府県:</b> {{都道府県}}</div>{{/都道府県}}
            </div>`,
        },
      },
      // --- OH2から移植: 各県・市の埋蔵文化財 ---
      {
        id: 'maibun-chiiki',
        label: '各県・市の埋蔵文化財',
        nodes: [
          {
            id: 'oh-hokkaido-maibun-fill',
            label: '北海道埋蔵文化財包蔵地',
            source: hokkaidoMaibunSource,
            layers: [hokkaidoMaibunFill, hokkaidoMaibunLine, hokkaidoMaibunLabel],
            attribution:
              '<a href="https://www.harp.lg.jp/opendata/dataset/1245.html" target="_blank">北海道埋蔵文化財包蔵地</a>',
            credit: '北海道埋蔵文化財包蔵地',
            popup: {
              layers: ['oh-hokkaido-maibun-fill', 'oh-hokkaido-maibun-line'],
              template: `
                <div style="max-width: 300px;">
                  <h4 style="margin: 0 0 8px 0; color: #D32F2F;">{{SiteName}}</h4>
                  {{#種別}}<div><b>種別:</b> {{種別}}</div>{{/種別}}
                  {{#時代}}<div><b>時代:</b> {{時代}}</div>{{/時代}}
                  {{#所在地}}<div><b>所在地:</b> {{所在地}}</div>{{/所在地}}
                </div>`,
            },
          },
          {
            id: 'oh-iwate-iseki-fill',
            label: '岩手県遺跡地図',
            source: iwateIsekiSource,
            layers: [iwateIsekiFill, iwateIsekiLine, iwateIsekiLabel],
            attribution:
              '<a href="https://www.pref.iwate.jp/kyouikubunka/bunka/bunkazai/1006815.html" target="_blank">岩手県の埋蔵文化財</a>',
            credit: '岩手県の埋蔵文化財',
          },
          {
            id: 'oh-miyagi-iseki-fill',
            label: '宮城県遺跡地図',
            source: miyagiIsekiSource,
            layers: [miyagiIsekiFill, miyagiIsekiLine, miyagiIsekiLabel],
            attribution:
              '<a href="https://www.pref.miyagi.jp/site/maizou/bunkazaimap.html" target="_blank">宮城県遺跡地図</a>',
            credit: '宮城県遺跡地図',
          },
          {
            id: 'oh-toyama-iseki-fill',
            label: '富山県埋蔵文化財',
            source: toyamaIsekiSource,
            layers: [toyamaIsekiFill, toyamaIsekiLine, toyamaIsekiLabel],
            attribution:
              '<a href="https://opendata.pref.toyama.jp/dataset/gis-maibun/resource/7031917f-42c7-41d9-9f78-41ef72a10adc" target="_blank">富山県埋蔵文化財（2021.4.1時点）</a>',
            credit: '富山県埋蔵文化財（2021.4.1時点）',
          },
          {
            id: 'oh-tokyo-bunkazai',
            label: '東京都文化財',
            source: tokyoBunkazaiSource,
            layers: [tokyoBunkazaiLayer, tokyoBunkazaiLabelLayer],
            attribution:
              '<a href="https://catalog.data.metro.tokyo.lg.jp/dataset/t000021d0000000017" target="_blank">TOKYO OPENDATA</a>',
            credit: 'TOKYO OPENDATA',
            popup: {
              layers: ['oh-tokyo-bunkazai'],
              template: `
                <div style="max-width: 300px;">
                  <h4 style="margin: 0 0 8px 0; color: #2E7D32;">{{名称}}</h4>
                  {{#種別}}<div><b>種別:</b> {{種別}}</div>{{/種別}}
                  {{#所在地}}<div><b>所在地:</b> {{所在地}}</div>{{/所在地}}
                </div>`,
            },
          },
          {
            id: 'oh-setagaya-maibun-fill',
            label: '世田谷区埋蔵文化財',
            source: setagayaMaibunSource,
            layers: [setagayaMaibunFill, setagayaMaibunLine, setagayaMaibunLabel],
            attribution:
              '<a href="https://data-setagaya.opendata.arcgis.com/datasets/8124e82e2a3e4b15a5e4e59f0536c67c/explore" target="_blank">世田谷区埋蔵文化財（2019.3.1）</a>',
            credit: '世田谷区埋蔵文化財（2019.3.1）',
          },
          {
            id: 'oh-yokkaichi-iseki-fill',
            label: '四日市市遺跡',
            source: yokkaichiIsekiSource,
            layers: [yokkaichiIsekiFill, yokkaichiIsekiLine, yokkaichiIsekiLabel],
            attribution: '<a href="https://data.bodik.jp/dataset/242021_00062" target="_blank">四日市市遺跡情報</a>',
            credit: '四日市市遺跡情報',
          },
          {
            id: 'oh-okayama-maibun',
            label: '岡山県埋蔵文化財',
            source: okayamaMaibunSource,
            layers: [okayamaMaibunFill, okayamaMaibunLine, okayamaMaibunLabel],
            attribution:
              '<a href="https://www.okayama-opendata.jp/resources/10378" target="_blank">岡山県埋蔵文化財</a>',
            credit: '岡山県埋蔵文化財',
          },
          {
            id: 'oh-wakayama-maibun-fill',
            label: '和歌山県埋蔵文化財',
            source: wakayamaMaibunSource,
            layers: [wakayamaMaibunFill, wakayamaMaibunLine, wakayamaMaibunLabel],
            attribution:
              '<a href="https://data.bodik.jp/dataset/300004_500100_chiiki_minyurin/resource/610b1d02-e8e2-4828-8cff-30ae38f085fa" target="_blank">和歌山県埋蔵文化財</a>',
            credit: '和歌山県埋蔵文化財',
          },
          {
            id: 'oh-yamaguchi-iseki-fill',
            label: '山口県遺跡地図',
            source: yamaguchiIsekiSource,
            layers: [yamaguchiIsekiFill, yamaguchiIsekiLine, yamaguchiIsekiLabel],
            attribution:
              '<a href="https://yamaguchi-opendata.jp/ckan/sk/dataset/iseki-test/resource/412030be-5bb3-43b1-9787-728782b632be" target="_blank">山口県遺跡地図（2024.1.12）</a>',
            credit: '山口県遺跡地図（2024.1.12）',
          },
          {
            id: 'oh-kumamoto-iseki-fill',
            label: '熊本県遺跡',
            source: kumamotoIsekiSource,
            layers: [kumamotoIsekiFill, kumamotoIsekiLine, kumamotoIsekiCircle, kumamotoIsekiLabel],
            attribution:
              '<a href="https://www.pref.kumamoto.jp/soshiki/125/90282.html" target="_blank">熊本県遺跡地図</a>',
            credit: '熊本県遺跡地図',
            popup: {
              layers: ['oh-kumamoto-iseki-fill', 'oh-kumamoto-iseki', 'oh-kumamoto-iseki-line'],
              template: `
                <div style="max-width: 300px;">
                  <h4 style="margin: 0 0 8px 0; color: #1B5E20;">{{m_cont2}}</h4>
                </div>`,
            },
          },
          {
            id: 'oh-okinawa-iseki-fill',
            label: '沖縄県遺跡',
            source: okinawaIsekiSource,
            layers: [okinawaIsekiFill, okinawaIsekiLine, okinawaIsekiLabel],
            attribution: '<a href="http://gis.pref.okinawa.jp/OpenData/" target="_blank">沖縄県地図情報システム</a>',
            credit: '沖縄県地図情報システム',
          },
          {
            id: 'oh-tondabayashi-maibun-fill',
            label: '富田林市埋蔵文化財',
            source: tondabayashiMaibunSource,
            layers: [tondabayashiMaibunFill, tondabayashiMaibunLine, tondabayashiMaibunLabel],
            attribution:
              '<a href="https://www.city.tondabayashi.lg.jp/site/bunkazai/89886.html" target="_blank">周知の埋蔵文化財包蔵地に関する公開データ（2023.4）</a>',
            credit: '周知の埋蔵文化財包蔵地に関する公開データ（2023.4）',
          },
        ],
      },
    ],
  },
  {
    id: 'hazard-population',
    label: 'ハザードと人口',
    nodes: [
      {
        id: 'oh-hazard-pop-flood',
        label: '浸水域内の人口（洪水・想定最大規模）',
        source: kozuiSaidaiSource,
        layers: [kozuiSaidaiLayer],
        ext: { name: 'extHazardPopulation', values: ['flood'] },
        attribution: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
        credit: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
      },
      {
        id: 'oh-hazard-pop-tsunami',
        label: '浸水域内の人口（津波）',
        source: tsunamiSource,
        layers: [tsunamiLayer],
        ext: { name: 'extHazardPopulation', values: ['tsunami'] },
        attribution: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
        credit: '人口: 国勢調査125mメッシュ / 浸水域: 不動産情報ライブラリ',
      },
      {
        id: 'oh-hazard-pop-sediment',
        label: '警戒区域内の人口（土砂災害）',
        source: hazpopDosyaSource,
        layers: [hazpopDosyaFill, hazpopDosyaLine],
        ext: { name: 'extHazardPopulation', values: ['sediment'] },
        attribution: '人口: 国勢調査125mメッシュ / 警戒区域: 不動産情報ライブラリ',
        credit: '人口: 国勢調査125mメッシュ / 警戒区域: 不動産情報ライブラリ',
      },
    ],
  },
  {
    id: 'hazard',
    label: 'ハザードマップ等',
    nodes: [
      {
        id: 'oh-kk-hinan-basho',
        label: '指定緊急避難場所',
        source: kkHinanBashoSource,
        layers: [kkHinanBashoCircle, kkHinanBashoLabel],
        attribution:
          '<img src="./img/skhb-desc.png" style="max-width:300px;"><br><a href="https://hinanmap.gsi.go.jp/index.html" target="_blank">国土地理院　指定緊急避難場所データ</a>',
        credit: '国土地理院 指定緊急避難場所データ',
        description:
          '災害対策基本法第49条の4に基づき各市区町村が指定、国土地理院が整備。洪水・津波・土石流・内水氾濫・高潮・地震・大規模火災・噴火の災害種別ごと',
      },
      {
        id: 'oh-kk-hinan-jo',
        label: '指定避難所',
        source: kkHinanJoSource,
        layers: [kkHinanJoCircle, kkHinanJoLabel],
        attribution:
          '<img src="./img/hinan-desc.png" style="max-width:300px;"><br><a href="https://hinanmap.gsi.go.jp/index.html" target="_blank">国土地理院　指定避難所データ</a>',
        credit: '国土地理院 指定避難所データ',
        description:
          '災害対策基本法第49条の4に基づき各市区町村が指定、国土地理院が整備。洪水・津波・土石流・内水氾濫・高潮・地震・大規模火災・噴火の災害種別ごと',
      },
      // {
      //   id: 'hinanbasyo',
      //   label: '指定緊急避難場所',
      //   nodes: [
      //     {
      //       id: 'oh-hinanjyo-kozui',
      //       label: '指定緊急避難場所(洪水)',
      //       source: hinanjyoKozuiSource,
      //       layers: [hinanjyoKozuiLayer, hinanjyoKozuiLayerLabel],
      //     },
      //     {
      //       id: 'oh-tsunami-kozui',
      //       label: '指定緊急避難場所(津波)',
      //       source: hinanjyoTsunamiSource,
      //       layers: [hinanjyoTsunamiLayer, hinanjyoTsunamiLayerLabel],
      //     },
      //     {
      //       id: 'oh-hinanjyo-dosekiryu',
      //       label: '指定緊急避難場所(崖崩れ等)',
      //       source: hinanjyoDosekiryuSource,
      //       layers: [hinanjyoDosekiryuLayer, hinanjyoDosekiryuLayerLabel],
      //       attribution: '指定緊急避難場所(崖崩れ、土石流及び地滑り)',
      //     },
      //   ],
      // },
      {
        id: 'oh-kozui-saidai',
        label: '洪水浸水想定（想定最大規模）',
        source: kozuiSaidaiSource,
        layers: [kozuiSaidaiLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '">',
        credit: 'ハザードマップポータルサイト  +',
      },
      {
        id: 'oh-keikaku-saidai',
        label: '洪水浸水想定（計画規模）',
        source: kozuikeikskuSource,
        layers: [kozuiKeikskuLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '">',
        credit: 'ハザードマップポータルサイト  +',
      },
      {
        id: 'oh-tsunami',
        label: '津波浸水想定',
        source: tsunamiSource,
        layers: [tsunamiLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '">',
        credit: 'ハザードマップポータルサイト  +',
      },
      {
        id: 'oh-kozui',
        label: '洪水浸水想定区域（河川単位）',
        source: kozuiSource,
        layers: [kozuiLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extKozui' },
      },
      {
        id: 'oh-kozui-3d',
        label: '洪水浸水想定区域（河川単位）3D',
        source: kozuiSource,
        layers: [kozui3dLayer],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A31a.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
        ext: { name: 'extKozui3d' },
      },
      {
        id: 'oh-tadankai',
        label: '多段階浸水想定',
        source: tadankaiSource,
        layers: [tadankaiLayerPolygon, tadankaiLayerLine],
        ext: { name: 'ext-tadankai' },
      },
      {
        id: 'oh-keizoku',
        label: '浸水継続時間（想定最大規模）',
        source: keizokuSource,
        layers: [keizokuLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-kaokutoukai-hanran',
        label: '家屋倒壊等氾濫想定区域（氾濫流）',
        source: kaokutoukaiHanranSource,
        layers: [kaokutoukaiHanranLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-hightide',
        label: '高潮浸水想定区域',
        source: hightideSource,
        layers: [hightideLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '">',
        credit: 'ハザードマップポータルサイト  +',
      },
      {
        id: 'oh-naisui',
        label: '内水浸水想定区域',
        source: naisuiSource,
        layers: [naisuiLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a><br><br>' +
          '<img src="' +
          require('@/assets/legend/shinsui_legend3.png') +
          '">',
        credit: 'ハザードマップポータルサイト  +',
      },
      {
        id: 'oh-nadare',
        label: '雪崩危険箇所',
        source: nadareSource,
        layers: [nadareLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-tameike',
        label: 'ため池決壊による浸水想定',
        source: tameikeSource,
        layers: [tameikeLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-dosya',
        label: '土砂災害警戒区域',
        source: dosyaSource,
        layers: [dosyaLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-dosekiryu',
        label: '土石流危険渓流',
        source: dosekiryuSource,
        layers: [dosekiryuLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-kyukeisya',
        label: '急傾斜地崩壊危険箇所',
        source: kyukeisyaSource,
        layers: [kyukeisyaLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      {
        id: 'oh-jisuberi',
        label: '地すべり危険箇所',
        source: jisuberiSource,
        layers: [jisuberiLayer],
        attribution:
          '<a href="https://disaportal.gsi.go.jp/hazardmapportal/hazardmap/copyright/opendata.html" target="_blank">ハザードマップポータルサイト</a>',
        credit: 'ハザードマップポータルサイト',
      },
      // {
      //   id: 'oh-teiichi',
      //   label: '低位地帯２',
      //   source: teiichiSource,
      //   layers: [teiichiLayerPolygon, teiichiLayerLine],
      //   attribution:
      //     '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-G08-2015.html" target="_blank">国土数値情報</a>' +
      //     '<div class="legend-scale">' +
      //     '<ul class="legend-labels">' +
      //     '<li><span style="background:rgba(255, 255, 200, 0.7);"></span>浸水深 0m</li>' +
      //     '<li><span style="background:rgba(255, 230, 150, 0.7);"></span>浸水深 2m</li>' +
      //     '<li><span style="background:rgba(255, 180, 100, 0.7);"></span>浸水深 5m</li>' +
      //     '<li><span style="background:rgba(255, 100, 100, 0.8);"></span>浸水深 10m</li>' +
      //     '<li><span style="background:rgba(180, 50, 150, 0.8);"></span>浸水深 20m</li>' +
      //     '</ul>' +
      //     '</div>',
      // },
      {
        id: 'oh-kansui',
        label: '道路冠水想定箇所（アンダーパス等',
        source: kansuiSource,
        layers: [kansuiLayer, kansuiLayerLabel],
      },
      {
        id: 'oh-tsunami-height',
        label: '津波浸水想定3D',
        source: tsunamiSource2,
        layers: [tsunamiLayerHeight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A40-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-nantora-height',
        label: '宮崎県南海トラフ津波浸水想定3D',
        source: nantoraSource,
        layers: [nantoraLayerHeight],
      },
      // --- 宮崎県防災ハイブリッドレイヤー（ラスター＋MVT）---
      {
        id: 'oh-hyuganada-totatsu',
        label: '日向灘地震 津波到達時間（宮崎県）',
        sources: [hyuganadaTotatsuRasterSource, hyuganadaTotatsuMvtSource],
        layers: [hyuganadaTotatsuRasterLayer, hyuganadaTotatsuMvtLayer, hyuganadaTotatsuMvtLine],
        attribution:
          '<a href="https://data.bodik.jp/dataset/450006_1086" target="_blank">津波浸水開始時間（日向灘地震）</a>',
        credit: '津波浸水開始時間（日向灘地震）',
      },
      {
        id: 'oh-hyuganada-tsunami',
        label: '日向灘地震 津波浸水深（宮崎県）',
        sources: [hyuganadaTsunamiRasterSource, hyuganadaTsunamiMvtSource],
        layers: [hyuganadaTsunamiRasterLayer, hyuganadaTsunamiMvtLayer, hyuganadaTsunamiMvtLine],
        attribution: '<a href="https://data.bodik.jp/dataset/450006_1085" target="_blank">津波浸水深（日向灘R2）</a>',
        credit: '津波浸水深（日向灘R2）',
      },
      {
        id: 'oh-hyuganada-shindo',
        label: '日向灘地震 震度分布（宮崎県）',
        sources: [hyuganadaShindoRasterSource, hyuganadaShindoMvtSource],
        layers: [hyuganadaShindoRasterLayer, hyuganadaShindoMvtLayer, hyuganadaShindoMvtLine],
        attribution: '<a href="https://data.bodik.jp/dataset/450006_1087" target="_blank">震度分布（日向灘地震）</a>',
        credit: '震度分布（日向灘地震）',
      },
      {
        id: 'oh-nantora-shindo',
        label: '南海トラフ 震度分布（宮崎県）',
        sources: [nantoraShindoRasterSource, nantoraShindoMvtSource],
        layers: [nantoraShindoRasterLayer, nantoraShindoMvtLayer, nantoraShindoMvtLine],
        attribution:
          '<a href="https://data.bodik.jp/dataset/450006_1083" target="_blank">地震液状化想定（南トラH25）</a>',
        credit: '地震液状化想定（南トラH25）',
      },
      {
        id: 'oh-nantora-ekijyoka',
        label: '南海トラフ 液状化（宮崎県）',
        sources: [nantoraEkijyokaRasterSource, nantoraShindoMvtSource],
        layers: [nantoraEkijyokaRasterLayer, nantoraEkijyokaMvtLayer, nantoraEkijyokaMvtLine],
        attribution:
          '<a href="https://data.bodik.jp/dataset/450006_1083" target="_blank">地震液状化想定（南トラH25）</a>',
        credit: '地震液状化想定（南トラH25）',
      },
      {
        id: 'oh-hokkaidotsunami-height',
        label: '北海道津波浸水想定3D',
        source: hokkaidotsunamiSource,
        layers: [hokkaidotsunamiLayerHeight],
      },
      {
        id: 'oh-zosei',
        label: 'R05大規模盛土造成地',
        source: zoseiSource,
        layers: [zoseiLayer, zoseiLayerLine, zoseiLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-A54-2023.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-tokyojishin',
        label: '地震に関する危険度一覧(東京都)',
        source: tokyojishinSource,
        layers: [tokyojishinLayerSogo, tokyojishinLayerLine, tokyojishinLayerLabel],
      },
      {
        id: 'oh-tokyojishin3d',
        label: '地震に関する危険度一覧(東京都)3D',
        source: tokyojishinSource,
        layers: [tokyojishinLayerSogo, tokyojishinLayerLine, tokyojishinLayerLabel, tokyojishinheightSogo],
      },
      // {
      //     id: 'oh-tokyojishin',
      //     label: "地震に関する危険度一覧(東京都)",
      //     source: tokyojishinSource,
      //     layers: [tokyojishinLayer,tokyojishinLayerLine,tokyojishinLayerLabel,tokyojishinheight],
      // },
      // {
      //   id: 'oh-densyohi',
      //   label: '災害伝承碑',
      //   source: densyohiSource,
      //   layers: [densyohiLayer, densyohiLayerLabel],
      // },
      // {
      //   id: 'oh-densho',
      //   label: '自然災害伝承碑',
      //   source: denshoSource,
      //   layers: [denshoLayer, denshoLabelLayer],
      //   attribution: '<a href="https://www.gsi.go.jp/bousaichiri/denshouhi_download.html" target="_blank">国土地理院</a>',
      //   ext: { name: 'extDensho' },
      // },
      // 遺跡等に移動
      // {
      //   id: 'oh-bunkazai',
      //   label: '都道府県指定文化財',
      //   source: bunkazaiSource,
      //   layers: [bunkazaiLayer, bunkazaiLabelLayer],
      //   attribution: '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-P32.html" target="_blank">国土数値情報</a>',
      //   ext: { name: 'extBunkazai' },
      // },
      {
        id: 'oh-fukuokakenhazard',
        label: '福岡県土砂災害',
        source: fukuokakenHazardSource,
        layers: [fukuokakenHazardLayer, fukuokakenHazardLayerLine],
        attribution:
          '<a href="https://ckan.open-governmentdata.org/dataset/401000_dosyasaigaikeikaikuikitoudata" target="_blank">福岡県　土砂災害警戒区域等のshapeデータ</a>',
        credit: '福岡県 土砂災害警戒区域等のshapeデータ',
      },
    ],
  },
  {
    id: 'dem-folder',
    label: 'DEM',
    nodes: [
      {
        id: 'oh-dem-tint',
        label: 'プリセット色別標高図（DEM）',
        sources: [demTintSrc],
        layers: [demTintLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemTint' },
      },
      {
        id: 'oh-contour-2',
        label: '等高線（DEM）',
        source: contourSource,
        layers: [contourLayerMajor, contourLayerMinor, contourLayerLabel],
        attribution: '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
      },
      {
        id: 'oh-dem-slope',
        label: '傾斜角マップ（DEM）',
        sources: [demSlopeSrc],
        layers: [demSlopeLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemSlope' },
      },
      {
        id: 'oh-dem-aspect',
        label: '斜面方位マップ（DEM）',
        sources: [demAspectSrc],
        layers: [demAspectLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemAspect' },
      },
      {
        id: 'oh-dem-curvature',
        label: '曲率マップ（DEM）',
        sources: [demCurvatureSrc],
        layers: [demCurvatureLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemCurvature' },
      },
      {
        id: 'oh-dem-landform',
        label: '地形分類マップ（DEM）',
        sources: [demLandformSrc],
        layers: [demLandformLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemLandform' },
      },
      {
        id: 'oh-dem-flow',
        label: '流域解析（DEM）',
        sources: [demFlowSrc],
        layers: [demFlowLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemFlow' },
      },
      {
        id: 'oh-dem-hillshade',
        label: '陰影起伏図（DEM）',
        sources: [demHillshadeSrc],
        layers: [demHillshadeLayer],
        attribution:
          '<a href="https://gbank.gsj.jp/seamless/elev/" target="_blank" rel="noopener">産総研シームレス標高タイル</a>',
        credit: '産総研シームレス標高タイル',
        ext: { name: 'extDemHillshade' },
      },
    ],
  },
  // ────────────────────────────────────────
  // エコリス植生図
  // https://map.ecoris.info/
  // 環境省生物多様性センター植生調査GISデータを株式会社エコリスが作成・加工
  // ────────────────────────────────────────
  {
    id: 'ecoris',
    label: 'エコリス植生図',
    nodes: [
      // 第6-7回植生図（ベクター）
      {
        id: 'oh-ecoris-vege67v',
        label: '第6-7回植生図（ベクター）',
        source: {
          id: 'ecoris-vege67v-source',
          obj: {
            type: 'vector',
            url: 'pmtiles://https://map.ecoris.info/tiles/vege67vector.pmtiles',
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          // --- 17 fill レイヤー（shoku_c / dai_n / chu_n / hanrei_n でフィルタ）---
          {
            id: 'oh-ecoris-v67-kozan',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '1'],
            paint: { 'fill-color': '#FDF1CE', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-kokemomo',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '2'],
            paint: { 'fill-color': '#997F60', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-kokemomo-d',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '3'],
            paint: { 'fill-color': '#A58F74', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-buna',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '4'],
            paint: { 'fill-color': '#178017', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-buna-d',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '5'],
            paint: { 'fill-color': '#5B9700', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-yabutsubaki',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '6'],
            paint: { 'fill-color': '#003300', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-yabutsubaki-d',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '7'],
            paint: { 'fill-color': '#004A00', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-kawabe',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'shoku_c', '8'],
            paint: { 'fill-color': '#FFFF00', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-shokurin-other',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: [
              'all',
              ['==', 'dai_n', '植林地'],
              ['!=', 'chu_n', 'アカマツ植林'],
              ['!=', 'chu_n', 'スギ・ヒノキ・サワラ植林'],
            ],
            paint: { 'fill-color': '#D77214', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-akamatsu',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'chu_n', 'アカマツ植林'],
            paint: { 'fill-color': '#aa7720', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-sugihinoki',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'chu_n', 'スギ・ヒノキ・サワラ植林'],
            paint: { 'fill-color': '#697720', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-chikurin',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'dai_n', '竹林'],
            paint: { 'fill-color': '#CCCC20', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-bokusou',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'dai_n', '牧草地・ゴルフ場・芝地'],
            paint: { 'fill-color': '#69FF00', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-kousaku',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['all', ['==', 'dai_n', '耕作地'], ['!=', 'hanrei_n', '水田雑草群落']],
            paint: { 'fill-color': '#999662', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-suiden',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'hanrei_n', '水田雑草群落'],
            paint: { 'fill-color': '#8cd27d', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-shigaichi',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['all', ['==', 'shoku_c', '10'], ['!=', 'chu_n', '開放水域']],
            paint: { 'fill-color': '#868585', 'fill-opacity': 0.75 },
          },
          {
            id: 'oh-ecoris-v67-suiiki',
            type: 'fill',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            filter: ['==', 'chu_n', '開放水域'],
            paint: { 'fill-color': '#99FFFF', 'fill-opacity': 0.75 },
          },
          // --- ライン（z12以上でポリゴン境界線）---
          {
            id: 'oh-ecoris-v67-line',
            type: 'line',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            minzoom: 12,
            paint: { 'line-color': '#000000', 'line-width': 0.5 },
          },
          // --- ラベル（z14以上で凡例名表示）---
          {
            id: 'oh-ecoris-v67-label',
            type: 'symbol',
            source: 'ecoris-vege67v-source',
            'source-layer': 'vege67vector',
            minzoom: 14,
            layout: {
              'text-field': ['get', 'hanrei_n'],
              'text-size': 12,
              'text-font': ['Open Sans Regular'],
            },
            paint: {
              'text-color': '#000',
              'text-halo-color': '#fff',
              'text-halo-width': 0.8,
            },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを 株式会社エコリス が作成・加工）',
        ext: { name: 'extEcorisVege' },
      },
      {
        id: 'oh-ecoris-vege67hill',
        label: '第6-7回植生図（陰影起伏）',
        source: {
          id: 'ecoris-vege67hill-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/vege67hill/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-vege67hill',
            type: 'raster',
            source: 'ecoris-vege67hill-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-vege67',
        label: '第6-7回植生図',
        source: {
          id: 'ecoris-vege67-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/vege67/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-vege67',
            type: 'raster',
            source: 'ecoris-vege67-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit:
          'エコリス地図タイル（環境省生物多様性センター第6〜7回植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-vegehill',
        label: '第5回植生図（陰影起伏）',
        source: {
          id: 'ecoris-vegehill-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/vegehill/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-vegehill',
            type: 'raster',
            source: 'ecoris-vegehill-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター第5回植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター第5回植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-vege',
        label: '第5回植生図',
        source: {
          id: 'ecoris-vege-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/vege/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-vege',
            type: 'raster',
            source: 'ecoris-vege-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター第5回植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター第5回植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-tanbohill',
        label: '田んぼ（陰影起伏）',
        source: {
          id: 'ecoris-tanbohill-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/tanbohill/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-tanbohill',
            type: 'raster',
            source: 'ecoris-tanbohill-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-tanbo',
        label: '田んぼ',
        source: {
          id: 'ecoris-tanbo-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/tanbo/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-tanbo',
            type: 'raster',
            source: 'ecoris-tanbo-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-sugihinokihill',
        label: 'スギ・ヒノキ・サワラ植林（陰影起伏）',
        source: {
          id: 'ecoris-sugihinokihill-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/sugihinokihill/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-sugihinokihill',
            type: 'raster',
            source: 'ecoris-sugihinokihill-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-sugihinoki',
        label: 'スギ・ヒノキ・サワラ植林',
        source: {
          id: 'ecoris-sugihinoki-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/sugihinoki/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-sugihinoki',
            type: 'raster',
            source: 'ecoris-sugihinoki-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（環境省生物多様性センター植生調査GISデータを 株式会社エコリス が作成・加工）',
      },
      {
        id: 'oh-ecoris-hill',
        label: '陰影起伏',
        source: {
          id: 'ecoris-hill-source',
          obj: {
            type: 'raster',
            tiles: ['https://map.ecoris.info/tiles/hill/{z}/{x}/{y}.png'],
            tileSize: 256,
            minzoom: 5,
            maxzoom: 15,
          },
        },
        layers: [
          {
            id: 'oh-ecoris-hill',
            type: 'raster',
            source: 'ecoris-hill-source',
            paint: { 'raster-opacity': 0.8 },
          },
        ],
        attribution:
          'エコリス地図タイル（<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
        credit: 'エコリス地図タイル（ 株式会社エコリス が作成・加工）',
      },
      // 特定植物群落 — kubun大分類で色分け
      // kubunの先頭3文字で温度帯を判定し、大分類色を適用
      (() => {
        // 先頭3文字 → 大分類色マッピング
        const spColorExpr = [
          'case',
          // 高山
          [
            'any',
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '高山'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '雪田'],
          ],
          '#6FA8DC', // 淡い青
          // 亜寒帯
          ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '亜寒帯'],
          '#3D85C6', // 青
          // 冷温帯
          ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '冷温帯'],
          '#38761D', // 深緑
          // 暖温帯
          ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '暖温帯'],
          '#6AA84F', // 緑
          // 亜熱帯・亜温帯
          [
            'any',
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '亜熱帯'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '亜温帯'],
          ],
          '#93C47D', // ライムグリーン
          // 湿地・河辺・浮水
          [
            'any',
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '湿地'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '河辺'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '浮水'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '浮葉'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '流水'],
          ],
          '#45B5AA', // ターコイズ
          // 海浜
          ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '海浜'],
          '#E6B422', // 砂金色
          // 特殊環境（火山・岩上・蘚苔・シダ）
          [
            'any',
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '火山'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '岩上'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '蘚苔'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], '草本'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], 'シダ'],
          ],
          '#B07BAC', // プラム
          // 特殊樹林（マングローブ・タケ・ヤシ）
          [
            'any',
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 4], 'マングロ'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], 'タケ'],
            ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 2], 'ヤシ'],
          ],
          '#E69138', // オレンジ
          // 個体群
          ['==', ['slice', ['coalesce', ['get', 'kubun'], ''], 0, 3], '個体群'],
          '#E06666', // サーモンピンク
          // デフォルト（汎用・植生一般）
          '#999999',
        ]
        return {
          id: 'oh-ecoris-sp',
          label: '特定植物群落',
          source: {
            id: 'ecoris-sp-source',
            obj: {
              type: 'vector',
              url: 'pmtiles://https://map.ecoris.info/tiles/pmtiles/spPlantComm.pmtiles',
            },
          },
          layers: [
            {
              id: 'oh-ecoris-sp-area',
              type: 'fill',
              source: 'ecoris-sp-source',
              'source-layer': 'area',
              paint: {
                'fill-color': spColorExpr,
                'fill-opacity': 0.45,
              },
            },
            {
              id: 'oh-ecoris-sp-line',
              type: 'line',
              source: 'ecoris-sp-source',
              'source-layer': 'area',
              paint: {
                'line-color': spColorExpr,
                'line-width': 1.5,
                'line-opacity': 0.8,
              },
            },
            {
              id: 'oh-ecoris-sp-point',
              type: 'circle',
              source: 'ecoris-sp-source',
              'source-layer': 'point',
              paint: {
                'circle-color': spColorExpr,
                'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3, 10, 5, 15, 8],
                'circle-stroke-color': '#fff',
                'circle-stroke-width': 1,
              },
            },
          ],
          attribution:
            'エコリス地図タイル（環境省生物多様性センター特定植物群落調査GISデータを<a href="https://map.ecoris.info/" target="_blank">株式会社エコリス</a>が作成・加工）',
          credit:
            'エコリス地図タイル（環境省生物多様性センター特定植物群落調査GISデータを 株式会社エコリス が作成・加工）',
          ext: { name: 'extEcorisSp' },
        }
      })(),
    ],
  },
  {
    id: 'sonohoka',
    label: 'その他',
    nodes: [
      // {
      //   id: 'oh-chiseki',
      //   label: '地籍調査進捗（国交省）',
      //   sources: [chisekiDummySource],
      //   layers: [chisekiDummyLayer],
      //   attribution: '<a href="https://www.chiseki.go.jp/" target="_blank">国土交通省 地籍調査Webサイト</a>',
      //   ext: { name: 'extChiseki' },
      // },
      {
        id: 'oh-wikimedia-photos',
        label: 'Wikimedia写真',
        sources: [],
        layers: [],
        attribution: '<a href="https://commons.wikimedia.org/" target="_blank">Wikimedia Commons</a>',
        credit: 'Wikimedia Commons',
        ext: { name: 'extWikimediaPhotos' },
      },
      {
        id: 'oh-hikari',
        label: '夜のあかり',
        source: hikariSource,
        layers: [hikariLayer],
        attribution: 'NASA夜間光データ',
        credit: 'NASA夜間光データ',
      },
      // {
      //   id: 'oh-hikari-height',
      //   label: '夜のあかり3D',
      //   source: hikariSource,
      //   layers: [hikariLayerHeight],
      //   attribution: 'NASA夜間光データ',
      // },
      {
        id: 'oh-nrct-poi',
        label: '『日本歴史地名大系』施設・地点項目',
        source: nrctPoiSource,
        layers: [nrctPoiLayer, nrctPoiLabelLayer],
        attribution:
          '<a href="https://geoshape.ex.nii.ac.jp/nrct-poi/" target="_blank">『日本歴史地名大系』施設・地点項目（国立情報学研究所）</a>',
        credit: '『日本歴史地名大系』施設・地点項目（国立情報学研究所）',
        ext: { name: 'extNrctPoi' },
      },
      {
        id: 'oh-nrct-mct',
        label: '『日本歴史地名大系』地名項目',
        source: nrctMctSource,
        layers: [nrctMctLayer, nrctMctLabelLayer],
        attribution:
          '<a href="https://geoshape.ex.nii.ac.jp/nrct/" target="_blank">『日本歴史地名大系』地名項目（国立情報学研究所）</a>',
        credit: '『日本歴史地名大系』地名項目（国立情報学研究所）',
        ext: { name: 'extNrctMct' },
      },
      {
        id: 'oh-sekibutsu',
        label: 'みんなで石仏調査',
        source: sekibutsuSource,
        layers: [sekibutsuLayer],
        attribution: '<a href="https://map.sekibutsu.info/about" target="_blank">みんなで石仏調査</a>',
        credit: 'みんなで石仏調査',
        ext: { name: 'extSekibutsu' },
      },
      {
        id: 'oh-traffic-accident',
        label: '交通事故統計情報(2019年〜2022年)',
        source: trafficAccidentSource,
        layers: [trafficAccidentHeatmap, trafficAccidentCircle, trafficAccidentFatal],
        attribution:
          '<a href="https://github.com/sanskruthiya/ta-jp2022?tab=readme-ov-file" target="_blank">Geographic Record of Traffic Accident in Japan</a>',
        credit: 'Geographic Record of Traffic Accident in Japan',
        ext: { name: 'extTrafficAccident' },
      },
      {
        id: 'oh-tochiriyo100',
        label: '2021土地利用細分メッシュ(100mベクタ)',
        source: tochiriyo100Source,
        layers: [tochiriyo100Layer],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報 土地利用細分メッシュ</a>",
        credit: '国土数値情報 土地利用細分メッシュ',
        ext: { name: 'extTochiriyo' },
      },
      {
        id: 'oh-tochiriyo-raster',
        label: '2014土地利用細分メッシュ(100mラスタ)',
        source: tochiriyoRasterSource,
        layers: [tochiriyoRasterLayer],
        attribution:
          "<a href='https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L03-b_r.html' target='_blank'>国土数値情報 土地利用細分メッシュ</a>",
        credit: '国土数値情報 土地利用細分メッシュ',
      },
      {
        id: 'oh-tochiriyo',
        label: '東京都土地利用現況調査',
        source: tochiriyoSource,
        layers: [tochiriyoLayer, tochiriyoLayerLine],
        attribution:
          "<a href='https://catalog.data.metro.tokyo.lg.jp/dataset/t000008d2000000019' target='_blank'>土地利用現況調査GISデータ</a>",
        credit: '土地利用現況調査GISデータ',
        ext: { name: 'extTochiriyoTokyo' },
      },
      {
        id: 'oh-senkyoku',
        label: '選挙区（2022）',
        source: senkyokuSource,
        layers: [senkyokuLayer, senkyokuLayerLine, senkyokuLayerLabel],
      },
      {
        id: 'oh-r07koji-3d',
        label: '公示価格（R07）3D',
        source: r07kojiSource,
        layers: [r07kojilayerheight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-2025.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(225, 49, 33);"></span>1000万円以上</li>' +
          '<li><span style="background:rgb(225, 61, 35);"></span>100万円ー1000万円</li>' +
          '<li><span style="background:rgb(226, 84, 39);"></span>75万円ー100万円</li>' +
          '<li><span style="background:rgb(231, 145, 52);"></span>50万円ー75万円</li>' +
          '<li><span style="background:rgb(235, 178, 61);"></span>25万円ー50万円</li>' +
          '<li><span style="background:rgb(239, 211, 71);"></span>10万円ー25万円</li>' +
          '<li><span style="background:rgb(245, 245, 81);"></span>10万円未満</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  1000万円以上  +  100万円ー1000万円  +  75万円ー100万円  +  50万円ー75万円  +  25万円ー50万円  +  10万円ー25万円  +  10万円未満  +   +',
        info: true,
      },
      {
        id: 'oh-koji',
        label: '公示価格（R06）',
        source: kojiSource,
        layers: [kojiLayerPoint, kojiLayerLabel],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-2024.html" target="_blank">国土数値情報</a>',
        credit: '国土数値情報',
      },
      {
        id: 'oh-koji-3d',
        label: '公示価格（R06）3D',
        source: kojiSource,
        layers: [kojilayerheight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L01-2024.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(225, 49, 33);"></span>1000万円以上</li>' +
          '<li><span style="background:rgb(225, 61, 35);"></span>100万円ー1000万円</li>' +
          '<li><span style="background:rgb(226, 84, 39);"></span>75万円ー100万円</li>' +
          '<li><span style="background:rgb(231, 145, 52);"></span>50万円ー75万円</li>' +
          '<li><span style="background:rgb(235, 178, 61);"></span>25万円ー50万円</li>' +
          '<li><span style="background:rgb(239, 211, 71);"></span>10万円ー25万円</li>' +
          '<li><span style="background:rgb(245, 245, 81);"></span>10万円未満</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  1000万円以上  +  100万円ー1000万円  +  75万円ー100万円  +  50万円ー75万円  +  25万円ー50万円  +  10万円ー25万円  +  10万円未満  +   +',
        info: true,
      },
      {
        id: 'oh-chika',
        label: '都道府県地価調査（R06）3D',
        source: chikaSource3d,
        layers: [chikalayerheight],
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-L02-2024.html" target="_blank">国土数値情報</a>' +
          '<div class="legend-scale">' +
          '<ul class="legend-labels">' +
          '<li><span style="background:rgb(225, 49, 33);"></span>1000万円以上</li>' +
          '<li><span style="background:rgb(225, 61, 35);"></span>100万円ー1000万円</li>' +
          '<li><span style="background:rgb(226, 84, 39);"></span>75万円ー100万円</li>' +
          '<li><span style="background:rgb(231, 145, 52);"></span>50万円ー75万円</li>' +
          '<li><span style="background:rgb(235, 178, 61);"></span>25万円ー50万円</li>' +
          '<li><span style="background:rgb(239, 211, 71);"></span>10万円ー25万円</li>' +
          '<li><span style="background:rgb(245, 245, 81);"></span>10万円未満</li>' +
          '</ul>' +
          '</div>',
        credit:
          '国土数値情報  +   +   +  1000万円以上  +  100万円ー1000万円  +  75万円ー100万円  +  50万円ー75万円  +  25万円ー50万円  +  10万円ー25万円  +  10万円未満  +   +',
        info: true,
      },
      // 旧版（新版NRCT-POI/MCTに置き換え済み）
      // {
      //   id: 'oh-nihonrekishi',
      //   label: '日本歴史地名大系',
      //   source: nihonrekishiSource,
      //   layers: [nihonrekishiLayer, nihonrekishiLayerLabel],
      // },
    ],
  },
  // オフラインレイヤーグループはOfflineAreaManagerダイアログに移動
  // {
  //     id: 'offline',
  //     label: "オフラインレイヤー",
  //     nodes: [
  //         {
  //             id: 'oh-offline-pale-layer',
  //             label: "地理院淡色地図オフライン",
  //             source: paleSource,
  //             layers: [paleLayer]
  //         },
  //         {
  //             id: 'oh-homusyo-2025-layer-offline',
  //             label: "2025登記所地図オフライン",
  //             sources: [homusyo2025Source,homusyo2025DDissolvedSource,homusyo2025NinizahyoSource],
  //             layers: [homusyo2025Layer,homusyo2025LayerLine,homusyo2025LayerLabel,homusyo2025LayerVertex,homusyo2025DissolvedAllLayer,homusyo2025DissolvedLayer,homusyo2025NinizahyoCircle,homusyo2025NinizahyoSymbol],
  //             attribution: '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
  //             ext: {name:'extTokijyo2025'}
  //         }
  //     ]
  // },
  // {
  //   id: 'test',
  //   label: 'テスト',
  //   nodes: [
  //     // {
  //     //     id: 'oh-amx-a-fude',
  //     //     label: "2024登記所地図",
  //     //     sources: [amxSource,amx2024Source],
  //     //     layers: [amxLayerDaihyou,amx2024Layer,amx2024LayerLine,amx2024LayerVertex,amx2024LayerLabel],
  //     //     attribution: '<a href="https://front.geospatial.jp/moj-chizu-xml-readme/" target="_blank">法務省登記所備付地図データ</a>',
  //     //     ext: {name:'extTokijyo'}
  //     // },
  //
  //     {
  //       id: 'oh-t23kuLayer',
  //       label: '23区テスト',
  //       sources: [t23kuSource],
  //       layers: [t23kuLayer],
  //     },
  //     {
  //       id: 'oh-vector-layer-mono',
  //       label: '地理院ベクター・モノクロ',
  //       sources: monoSources,
  //       layers: monoLayers,
  //     },
  //     {
  //       id: 'oh-test2',
  //       label: 'test',
  //       sources: [fjEkijyokaSourse],
  //       layers: [fjEkijyokatLayer, fjEkijyokatLayerLine],
  //     },
  //     {
  //       id: 'oh-jinjya',
  //       label: '延喜式神名帳式内社(神社)',
  //       source: jinjyaSource,
  //       layers: [jinjyaLayer, jinjyaLayerLabel],
  //     },
  //     {
  //       id: 'oh-hikari',
  //       label: '夜のあかり',
  //       source: hikariSource,
  //       layers: [hikariLayer],
  //     },
  //     {
  //       id: 'oh-hikari-height',
  //       label: '夜のあかり3D',
  //       source: hikariSource,
  //       layers: [hikariLayerHeight],
  //     },
  //   ],
  // },
]

//ーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーー

// // ソース（raster）— demtint:// で dem_png を色別標高PNGへ
// const demTintSrc = {
//     id: 'oh-dem-tint-src',
//     obj: {
//         type: 'raster',
//         // level=0 をクエリで渡す。値を変えたい時は setTiles(...) で ?level= を更新
//         tiles: ['demtint://https://tiles.gsj.jp/tiles/elev/land/{z}/{y}/{x}.png?level=0'],
//         tileSize: 256,
//         scheme: 'xyz',
//         maxzoom: 22
//     }
// };
//
// // レイヤ（最近傍で拡大＝ボケなし）
// const demTintLayer = {
//     id: 'oh-dem-tint',
//     type: 'raster',
//     source: 'oh-dem-tint-src',
//     paint: {
//         'raster-resampling': 'nearest',   // ← 補間禁止（ドット強調）
//         'raster-fade-duration': 0,
//         'raster-opacity': 1
//     }
// };

// layers01.unshift({
//     id: 'oh-dem-tint',
//     label: '<span style="color: red">NEW</span>️プリセット色別標高図',
//     sources: [demTintSrc],
//     layers:  [demTintLayer],
//     attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener">地理院タイル（標高PNG）</a>',
//     ext: {name:'extDemTint'},
// });

//ーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーーー

// オフラインレイヤーはOfflineAreaManagerダイアログで制御するためフィルタリング不要
// if (store.state.isOffline) {
//     layers01 = layers01.filter(layer => layer.id === 'offline')
// }
/**
 * layers01ツリーを再帰的にフラット化し、全レイヤーのコンパクトなリストを返す
 * OH3全レイヤー開放モードで使用
 * @param {Array} tree - layers01またはそのサブツリー
 * @param {string} category - 親フォルダのラベル（カテゴリとして使用）
 * @returns {Array<{id: string, label: string, category: string}>}
 */
export function flattenAllLayers(tree = layers01, category = '', parentDesc = '', parentRank = 0) {
  const result = []
  for (const node of tree) {
    if (!node || !node.id) continue
    if (node.nodes || node.children) {
      // フォルダノード → カテゴリパスを積み上げ、description/rankを子に引き継ぐ
      const folderName = node.label?.replace(/<[^>]+>/g, '').trim() || ''
      const cat = category && folderName ? `${category} > ${folderName}` : (folderName || category)
      const desc = node.description || parentDesc
      const rank = node.rank || parentRank
      result.push(...flattenAllLayers(node.nodes || node.children, cat, desc, rank))
    } else if (node.source || node.layers || node.sources) {
      // リーフノード（実際のレイヤー）。自身のdescription/rankがなければ親から引き継ぐ
      const entry = {
        id: node.id,
        label: (node.label?.replace(/<[^>]+>/g, '').replace(/📷\s?/, '') || node.id).trim(),
        category: category,
        description: node.description || parentDesc,
      }
      const rank = node.rank || parentRank
      if (rank) entry.rank = rank
      result.push(entry)
    }
  }
  return result
}

let layers02 = JSON.parse(JSON.stringify(layers01))
let layers01Clone = [...layers01]
let layers02Clone = [...layers02]
export const layers = reactive({
  map01: layers01Clone,
  map02: layers02Clone,
})
waitForStateValue(() => store.state.currentGroupName).then((val) => {
  nextTick(() => {
    const currentGroupName = toRef(store.state, 'currentGroupName')
    document.querySelectorAll('.group-layer').forEach((groupLayer) => {
      if (store.state.currentGroupName) {
        groupLayer.innerHTML = `⭐<span style="color: red">️${escapeHtml(store.state.currentGroupName)}</span>`
      } else {
        groupLayer.innerHTML = ``
      }
    })
    watch(currentGroupName, (newVal) => {
      if (newVal) {
        layers01Clone = layers01
        layers02Clone = layers02
      } else {
        layers01Clone = layers01.filter((l) => l.id !== 'oh-gloup-layer')
        layers02Clone = layers02.filter((l) => l.id !== 'oh-gloup-layer')
      }
      // 中身を置き換える方法
      layers.map01.splice(0, layers01Clone.length, ...layers01Clone)
      layers.map02.splice(0, layers02Clone.length, ...layers02Clone)
      nextTick(() => {
        document.querySelectorAll('.group-layer').forEach((groupLayer) => {
          if (newVal) {
            groupLayer.innerHTML = `⭐<span style="color: red">️${escapeHtml(newVal)}</span>`
          } else {
            groupLayer.innerHTML = ``
          }
        })
      })
    })
  })
  const selectedLayers = computed(() => store.state.selectedLayers)
  watch(
    selectedLayers,
    (newVal) => {
      nextTick(() => {
        document.querySelectorAll('.group-layer').forEach((groupLayer) => {
          if (store.state.currentGroupName) {
            groupLayer.innerHTML = `⭐<span style="color: red">️${escapeHtml(store.state.currentGroupName)}</span>`
          } else {
            groupLayer.innerHTML = ``
          }
        })
      })
    },
    { deep: true }
  )
})
